/**
 * Generated by orval 🍺
 * Do not edit manually.
 * CDF Template API
 * OpenAPI-first contract for the CDF template.
 * All protected workplace requests require an X-Tenant-Slug header identifying
 * an entity currently granted to the caller. The API validates the grant and
 * derives ownership from that entity; identifiers from another entity are
 * rejected. Browser requests use the selected entity URL for this header.
 * User-specific search, favourites, teams, files, notifications and AI history
 * are also isolated by the selected entity.
 *
 * Workflow:
 *   1. Edit this spec FIRST for any API change.
 *   2. Run `pnpm gen:api` to regenerate the React Query client
 *      (lib/api-client-react) and Zod schemas (lib/api-zod).
 *   3. Implement/adjust the Fastify routes in artifacts/api-server
 *      to match, validating with the generated Zod schemas.
 *
 * OpenAPI spec version: 0.1.0
 */
import {
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import { customFetch } from '../mutator';
/**
 * Existing roles owned by this organisation; apply the same assignment limits as individual members.
 * @minItems 1
 * @maxItems 100
 */
export type OrganisationGroupRoleIds = string[];

export type OrganisationGroupAccessStatusState = typeof OrganisationGroupAccessStatusState[keyof typeof OrganisationGroupAccessStatusState];


export const OrganisationGroupAccessStatusState = {
  ready: 'ready',
  setup_required: 'setup_required',
  unavailable: 'unavailable',
  directory_mismatch: 'directory_mismatch',
} as const;

/**
 * Administrator-only readiness probes group reads and user membership permissions in the configured sign-in directory. Missing configuration or denied consent requires setup; outages are retryable. No credentials or directory enumeration for normal members.
 */
export interface OrganisationGroupAccessStatus {
  state: OrganisationGroupAccessStatusState;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  message: string;
  retryable: boolean;
  directoryId?: string;
  transitiveMembership: true;
}

export type OrganisationDirectoryGroupKind = typeof OrganisationDirectoryGroupKind[keyof typeof OrganisationDirectoryGroupKind];


export const OrganisationDirectoryGroupKind = {
  security: 'security',
  microsoft365: 'microsoft365',
} as const;

/**
 * Server-discovered security or Microsoft 365 group in the configured sign-in directory. Hidden membership is marked unverifiable and never grants access; display names are never authority.
 */
export interface OrganisationDirectoryGroup {
  id: string;
  directoryId: string;
  /**
     * @minLength 1
     * @maxLength 256
     */
  displayName: string;
  kind: OrganisationDirectoryGroupKind;
  membershipVerifiable: boolean;
  /** @maxLength 1000 */
  unavailableReason?: string;
}

export interface OrganisationDirectoryGroupPage {
  /** @maxItems 100 */
  items: OrganisationDirectoryGroup[];
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  hasMore: boolean;
  /**
     * @minLength 1
     * @maxLength 2048
     */
  nextCursor?: string;
}

/**
 * Server owns the directory and real group lookup. Client group names, membership and directory overrides are rejected.
 */
export interface CreateOrganisationGroupBinding {
  groupId: string;
  roleIds: OrganisationGroupRoleIds;
}

export interface UpdateOrganisationGroupBinding {
  /**
     * @minimum 1
     * @maximum 2147483647
     */
  revision: number;
  roleIds: OrganisationGroupRoleIds;
  enabled: boolean;
}

/**
 * Organisation-only configuration. Never grants parent/platform authority or proves current user membership.
 */
export interface OrganisationGroupBinding {
  id: string;
  organizationId: string;
  directoryId: string;
  groupId: string;
  /**
     * @minLength 1
     * @maxLength 256
     */
  displayName: string;
  roleIds: OrganisationGroupRoleIds;
  enabled: boolean;
  /**
     * @minimum 1
     * @maximum 2147483647
     */
  revision: number;
  createdAt: string;
  updatedAt: string;
}

export interface OrganisationGroupBindingPage {
  /** @maxItems 100 */
  items: OrganisationGroupBinding[];
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  hasMore: boolean;
  /**
     * @minLength 1
     * @maxLength 2048
     */
  nextCursor?: string;
}

export type OrganisationGroupAccessSourceVerification = typeof OrganisationGroupAccessSourceVerification[keyof typeof OrganisationGroupAccessSourceVerification];


export const OrganisationGroupAccessSourceVerification = {
  verified: 'verified',
  not_member: 'not_member',
  unavailable: 'unavailable',
  disabled: 'disabled',
} as const;

/**
 * Request-current verification only. Failed checks contribute no roles; names/details visible to administrators only.
 */
export interface OrganisationGroupAccessSource {
  bindingId: string;
  groupId: string;
  /**
     * @minLength 1
     * @maxLength 256
     */
  displayName: string;
  roleIds: OrganisationGroupRoleIds;
  verification: OrganisationGroupAccessSourceVerification;
}

/**
 * Individual grants and group contributions stay separate. Suspension denies both. Group membership is never restored as individual access.
 */
export interface OrganisationMemberAccessSources {
  memberId: string;
  individualAccess: boolean;
  /** @maxItems 100 */
  individualRoleIds: string[];
  suspended: boolean;
  /** @maxItems 100 */
  groups: OrganisationGroupAccessSource[];
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  hasMore: boolean;
  /**
     * @minLength 1
     * @maxLength 2048
     */
  nextCursor?: string;
}

/**
 * @pattern ^[a-f0-9]{64}$
 */
export type TenantBackupDigest = string;

export interface TenantBackupStart {
  idempotencyKey: string;
}

export interface TenantBackupReadiness {
  exportEnabled: false;
  restoreEnabled: false;
  coverageRevision: TenantBackupDigest;
  /**
     * @maxItems 1000
     * @items.maxLength 1000
     */
  blockers: string[];
}

export type TenantBackupIdentityNamespace = typeof TenantBackupIdentityNamespace[keyof typeof TenantBackupIdentityNamespace];


export const TenantBackupIdentityNamespace = {
  app_users: 'app_users',
  users: 'users',
} as const;

/**
 * Minimum shared identity reference; existing immutable ID plus tid/oid must agree. Legacy users resolve by exact existing ID. No email matching or user creation.
 */
export interface TenantBackupIdentity {
  namespace: TenantBackupIdentityNamespace;
  id: string;
  entraTenantId?: string;
  entraObjectId?: string;
}

export type TenantBackupEntryKind = typeof TenantBackupEntryKind[keyof typeof TenantBackupEntryKind];


export const TenantBackupEntryKind = {
  rows: 'rows',
  binary: 'binary',
} as const;

/**
 * Every covered table has one NDJSON entry including empty tables. Binary entries bind row key/field and metadata to content-addressed bytes.
 */
export interface TenantBackupEntry {
  /**
     * @maxLength 200
     * @pattern ^(tables/[a-z_]+\.ndjson|binaries/[a-f0-9]{64})$
     */
  path: string;
  kind: TenantBackupEntryKind;
  /**
     * @maxLength 100
     * @pattern ^[a-z_]+$
     */
  table: string;
  /**
     * @minimum 0
     * @maximum 1000000
     */
  recordCount: number;
  /**
     * @minimum 0
     * @maximum 1073741824
     */
  byteSize: number;
  sha256: TenantBackupDigest;
  /** @maxLength 500 */
  recordKey?: string;
  /** @maxLength 100 */
  field?: string;
  /** @maxLength 200 */
  contentType?: string;
}

export type TenantBackupExclusionDisposition = typeof TenantBackupExclusionDisposition[keyof typeof TenantBackupExclusionDisposition];


export const TenantBackupExclusionDisposition = {
  shared_reference: 'shared_reference',
  secret: 'secret',
  transient: 'transient',
  platform: 'platform',
  generated: 'generated',
} as const;

export interface TenantBackupExclusion {
  /** @maxLength 100 */
  table: string;
  /**
     * @maxItems 200
     * @items.maxLength 100
     */
  fields: string[];
  /** @maxLength 1000 */
  reason: string;
  disposition: TenantBackupExclusionDisposition;
}

export type TenantBackupManifestFormat = typeof TenantBackupManifestFormat[keyof typeof TenantBackupManifestFormat];


export const TenantBackupManifestFormat = {
  'commons-tenant-backup': 'commons-tenant-backup',
} as const;

export type TenantBackupManifestVersion = typeof TenantBackupManifestVersion[keyof typeof TenantBackupManifestVersion];


export const TenantBackupManifestVersion = {
  NUMBER_1: 1,
} as const;

/**
 * manifest.json UTF-8; table lines use canonical JSON with sorted keys, stable PK order, UTC timestamps, exact decimals and binary references. Hash uncompressed bytes; archive digest is independent and bound by server receipt. Checksums never confer authority. Reject unknown properties/tables/fields and missing entries.
 */
export interface TenantBackupManifest {
  format: TenantBackupManifestFormat;
  version: TenantBackupManifestVersion;
  archiveId: string;
  tenantId: string;
  /**
     * @minItems 1
     * @maxItems 10000
     */
  entityIds: string[];
  createdAt: string;
  /** @maxLength 100 */
  applicationVersion: string;
  schemaRevision: TenantBackupDigest;
  coverageRevision: TenantBackupDigest;
  snapshotRevision: TenantBackupDigest;
  /** @maxItems 100000 */
  identities: TenantBackupIdentity[];
  /** @maxItems 100000 */
  entries: TenantBackupEntry[];
  /** @maxItems 1000 */
  exclusions: TenantBackupExclusion[];
  /**
     * @maxItems 100000
     * @items.maxLength 1000
     */
  externalReconciliation: string[];
}

export type TenantBackupOperationKind = typeof TenantBackupOperationKind[keyof typeof TenantBackupOperationKind];


export const TenantBackupOperationKind = {
  export: 'export',
  import: 'import',
} as const;

export type TenantBackupOperationState = typeof TenantBackupOperationState[keyof typeof TenantBackupOperationState];


export const TenantBackupOperationState = {
  staging: 'staging',
  validating: 'validating',
  ready: 'ready',
  reviewed: 'reviewed',
  committing: 'committing',
  succeeded: 'succeeded',
  failed: 'failed',
  cancelled: 'cancelled',
  expired: 'expired',
} as const;

export interface TenantBackupOperation {
  id: string;
  tenantId: string;
  actorId: string;
  kind: TenantBackupOperationKind;
  state: TenantBackupOperationState;
  createdAt: string;
  expiresAt: string;
  archiveDigest?: TenantBackupDigest;
  manifest?: TenantBackupManifest;
  /**
     * @maxItems 1000
     * @items.maxLength 1000
     */
  blockers: string[];
  /** @maxLength 100 */
  errorCode?: string;
  restoreAuditId?: string;
}

/**
 * Durable server-owned non-bearer receipt; client fields cannot replace stored bindings. Single-use review expires within 15 minutes.
 */
export interface TenantBackupReceipt {
  id: string;
  operationId: string;
  archiveId: string;
  tenantId: string;
  actorId: string;
  archiveDigest: TenantBackupDigest;
  dataRevision: TenantBackupDigest;
  authorityRevision: TenantBackupDigest;
  coverageRevision: TenantBackupDigest;
  expiresAt: string;
}

export interface TenantBackupPreview {
  receipt: TenantBackupReceipt;
  /** @minimum 0 */
  additions: number;
  /** @minimum 0 */
  updates: number;
  /** @minimum 0 */
  removals: number;
  /** @minimum 0 */
  preservedAuditCount: number;
  /**
     * @maxItems 1000
     * @items.maxLength 1000
     */
  accessEffects: string[];
  /**
     * @maxItems 1000
     * @items.maxLength 1000
     */
  externalReconciliation: string[];
  /**
     * @maxItems 1000
     * @items.maxLength 1000
     */
  blockers: string[];
  /** @maxLength 200 */
  confirmationPhrase: string;
}

export type TenantBackupChangeAction = typeof TenantBackupChangeAction[keyof typeof TenantBackupChangeAction];


export const TenantBackupChangeAction = {
  add: 'add',
  update: 'update',
  remove: 'remove',
  preserve_audit: 'preserve_audit',
  reissue: 'reissue',
  reconcile_external: 'reconcile_external',
} as const;

/**
 * Redacted review summary; never include secret values or entire personal records.
 */
export interface TenantBackupChange {
  /** @maxLength 100 */
  table: string;
  /** @maxLength 500 */
  recordKey: string;
  action: TenantBackupChangeAction;
  /** @maxLength 1000 */
  effect: string;
}

export interface TenantBackupChangesPage {
  /** @maxItems 100 */
  items: TenantBackupChange[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
  receiptId: string;
}

/**
 * Compare every supplied value with the durable server receipt. Confirmation expires with that receipt; a committed identical retry does not repeat mutations.
 */
export interface TenantBackupConfirmation {
  receiptId: string;
  archiveDigest: TenantBackupDigest;
  dataRevision: TenantBackupDigest;
  authorityRevision: TenantBackupDigest;
  /** Stable confirmation key; retain for status recovery and identical retry */
  idempotencyKey: string;
  /**
     * Must exactly equal RESTORE followed by the original tenant UUID
     * @minLength 1
     * @maxLength 200
     */
  confirmationPhrase: string;
}

export type PolicyAdmissionDetailsMissingRulesItem = {
  ruleId: string;
  /** @minimum 1 */
  version: number;
  title: string;
};

/**
 * Current missing requirements for the booking beneficiary. Acceptance must be submitted by that person; this response grants no access and is not a write token.
 */
export interface PolicyAdmissionDetails {
  spaceId: string;
  beneficiaryUserId: string;
  revision: string;
  /** @nullable */
  legacySiteId?: string | null;
  /** @maxItems 1000 */
  missingRules: PolicyAdmissionDetailsMissingRulesItem[];
}

export interface PolicyRuleContent {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 20000
     */
  body: string;
  requiresIndividualAcknowledgement: boolean;
  /**
     * @minimum 1
     * @maximum 3650
     * @nullable
     */
  validityDays: number | null;
}

export type PolicyRule = PolicyRuleContent & {
  id: string;
  tenantId: string;
  /** @minimum 1 */
  version: number;
  active: boolean;
};

export interface PolicyCataloguePage {
  items: PolicyRule[];
  total: number;
  limit: number;
  offset: number;
  canManageCatalogue: boolean;
}

export interface SavePolicyCatalogueRule {
  /**
     * @minLength 1
     * @maxLength 200
     */
  title: string;
  /**
     * @minLength 1
     * @maxLength 20000
     */
  body: string;
  requiresIndividualAcknowledgement: boolean;
  /**
     * @minimum 1
     * @maximum 3650
     * @nullable
     */
  validityDays: number | null;
  ruleId?: string;
  /** @minimum 1 */
  expectedVersion?: number;
  expectedActive?: boolean;
  active: boolean;
}

export type PolicyScope = typeof PolicyScope[keyof typeof PolicyScope];


export const PolicyScope = {
  site: 'site',
  building: 'building',
  floor: 'floor',
  zone: 'zone',
  space_type: 'space_type',
} as const;

export type PolicyScopeRuleSourcesItem = {
  scope: PolicyScope;
  scopeId: string;
  name: string;
  inherited: boolean;
};

export interface PolicyScopeRule {
  rule: PolicyRule;
  sources: PolicyScopeRuleSourcesItem[];
}

export interface PolicyScopeRulesPage {
  items: PolicyScopeRule[];
  total: number;
  limit: number;
  offset: number;
  /**
     * @minLength 64
     * @maxLength 64
     */
  revision: string;
  canManageAssignments: boolean;
}

export interface SetPolicyScopeRule {
  scope: PolicyScope;
  scopeId: string;
  ruleId: string;
  active: boolean;
  /**
     * @minLength 64
     * @maxLength 64
     */
  revision: string;
}

export interface SiteInductionItem {
  /**
     * @minLength 1
     * @maxLength 64
     */
  id: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  label: string;
  /** @maxLength 1000 */
  detail?: string;
}

export interface SiteArrivalSafety {
  /** @maxItems 50 */
  arrivalItems: SiteInductionItem[];
  /** @maxLength 4000 */
  safetyGuidance: string;
  /**
     * @minLength 64
     * @maxLength 64
     */
  revision: string;
}

export interface PolicyAssignment {
  id: string;
  tenantId: string;
  entityId: string;
  ruleId: string;
  scope: PolicyScope;
  scopeId: string;
}

export type PolicyAcceptanceEvidenceMethod = typeof PolicyAcceptanceEvidenceMethod[keyof typeof PolicyAcceptanceEvidenceMethod];


export const PolicyAcceptanceEvidenceMethod = {
  individual: 'individual',
  shared: 'shared',
} as const;

export type PolicyAcceptanceEvidenceSource = typeof PolicyAcceptanceEvidenceSource[keyof typeof PolicyAcceptanceEvidenceSource];


export const PolicyAcceptanceEvidenceSource = {
  user: 'user',
  legacy_exact: 'legacy_exact',
} as const;

export interface PolicyAcceptanceEvidence {
  id: string;
  tenantId: string;
  userId: string;
  ruleId: string;
  /** @minimum 1 */
  version: number;
  acceptedAt: string;
  /** @nullable */
  expiresAt: string | null;
  method: PolicyAcceptanceEvidenceMethod;
  checkedRuleIds: string[];
  batchId: string;
  source: PolicyAcceptanceEvidenceSource;
}

export type ApplicablePolicyRule = PolicyRule & ({
  assignments: PolicyAssignment[];
  acceptance: PolicyAcceptanceEvidence | null;
  needsAcceptance: boolean;
});

export interface PolicyRequirements {
  tenantId: string;
  entityId: string;
  spaceId: string;
  revision: string;
  rules: ApplicablePolicyRule[];
  complete: boolean;
}

export type AcceptPolicyRequirementsRulesItem = {
  ruleId: string;
  /** @minimum 1 */
  version: number;
};

export interface AcceptPolicyRequirements {
  spaceId: string;
  /**
     * @minLength 64
     * @maxLength 64
     */
  revision: string;
  /** @maxItems 1000 */
  rules: AcceptPolicyRequirementsRulesItem[];
  /** @maxItems 1000 */
  checkedRuleIds: string[];
}

export type EmailTemplateType = typeof EmailTemplateType[keyof typeof EmailTemplateType];


export const EmailTemplateType = {
  booking_confirmed: 'booking_confirmed',
  booking_modified: 'booking_modified',
  booking_cancelled: 'booking_cancelled',
  booking_auto_released: 'booking_auto_released',
  booking_reminder: 'booking_reminder',
  check_in_prompt: 'check_in_prompt',
  auto_release_warning: 'auto_release_warning',
  resource_unavailable: 'resource_unavailable',
  visitor_arrival: 'visitor_arrival',
  visitor_approval: 'visitor_approval',
} as const;

export interface EmailTemplateContent {
  /**
     * @minLength 1
     * @maxLength 200
     */
  subject: string;
  /**
     * @minLength 1
     * @maxLength 10000
     */
  body: string;
}

export interface EmailTemplateField {
  path: string;
  label: string;
  sample: string;
}

export interface EmailTemplateOverridePermissions {
  booking_confirmed?: boolean;
  booking_modified?: boolean;
  booking_cancelled?: boolean;
  booking_auto_released?: boolean;
  booking_reminder?: boolean;
  check_in_prompt?: boolean;
  auto_release_warning?: boolean;
  resource_unavailable?: boolean;
  visitor_arrival?: boolean;
  visitor_approval?: boolean;
}

export interface EmailTemplatePermissions {
  allowEntityOverrides: EmailTemplateOverridePermissions;
}

export type EmailTemplateSource = typeof EmailTemplateSource[keyof typeof EmailTemplateSource];


export const EmailTemplateSource = {
  platform: 'platform',
  tenant: 'tenant',
  entity: 'entity',
} as const;

export interface EmailTemplate {
  type: EmailTemplateType;
  label: string;
  subject: string;
  body: string;
  source: EmailTemplateSource;
  builtIn: boolean;
  locked: boolean;
  hasOverride: boolean;
  allowEntityOverride: boolean;
  allowedFields: EmailTemplateField[];
  /** @nullable */
  updatedBy: string | null;
  /** @nullable */
  updatedByName: string | null;
  /** @nullable */
  updatedAt: string | null;
}

export type EmailTemplateListScope = typeof EmailTemplateListScope[keyof typeof EmailTemplateListScope];


export const EmailTemplateListScope = {
  platform: 'platform',
  tenant: 'tenant',
  entity: 'entity',
} as const;

export interface EmailTemplateList {
  scope: EmailTemplateListScope;
  /** @nullable */
  tenantId: string | null;
  /** @nullable */
  entityId: string | null;
  allowEntityOverrides: EmailTemplateOverridePermissions;
  /** @maxItems 10 */
  items: EmailTemplate[];
}

export interface EmailTemplatePreviewRequest {
  type: EmailTemplateType;
  /**
     * @minLength 1
     * @maxLength 200
     */
  subject: string;
  /**
     * @minLength 1
     * @maxLength 10000
     */
  body: string;
}

export interface EmailTemplatePreview {
  subject: string;
  body: string;
  html: string;
}

/**
 * @nullable
 */
export type CustomerPolicyValuesBookingNameAudience = typeof CustomerPolicyValuesBookingNameAudience[keyof typeof CustomerPolicyValuesBookingNameAudience] | null;


export const CustomerPolicyValuesBookingNameAudience = {
  private: 'private',
  authorised_roles: 'authorised_roles',
  entity_colleagues: 'entity_colleagues',
} as const;

/**
 * @nullable
 */
export type CustomerPolicyValuesDeskLocationAudience = typeof CustomerPolicyValuesDeskLocationAudience[keyof typeof CustomerPolicyValuesDeskLocationAudience] | null;


export const CustomerPolicyValuesDeskLocationAudience = {
  private: 'private',
  authorised_roles: 'authorised_roles',
  entity_colleagues: 'entity_colleagues',
} as const;

export interface CustomerPolicyValues {
  /** @nullable */
  lightPrimary?: string | null;
  /** @nullable */
  darkPrimary?: string | null;
  /** @nullable */
  bookingNameAudience?: CustomerPolicyValuesBookingNameAudience;
  /** @nullable */
  deskLocationAudience?: CustomerPolicyValuesDeskLocationAudience;
}

export type HomeWebpartType = typeof HomeWebpartType[keyof typeof HomeWebpartType];


export const HomeWebpartType = {
  welcome: 'welcome',
  'upcoming-bookings': 'upcoming-bookings',
  suggestions: 'suggestions',
  'more-ways': 'more-ways',
  booking: 'booking',
  'rich-html': 'rich-html',
  'site-tiles': 'site-tiles',
} as const;

export type HomeWebpartModule = typeof HomeWebpartModule[keyof typeof HomeWebpartModule];


export const HomeWebpartModule = {
  booking: 'booking',
  workplace_planning: 'workplace_planning',
  visitors: 'visitors',
  staffed_services: 'staffed_services',
  maps_rooms: 'maps_rooms',
  reporting: 'reporting',
  ai_tools: 'ai_tools',
} as const;

export interface HomeSiteTile {
  /**
     * @minLength 1
     * @maxLength 64
     * @pattern ^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$
     */
  id: string;
  /**
     * @minLength 1
     * @maxLength 100
     */
  label: string;
  /** @maxLength 400 */
  description: string;
  /**
     * @minLength 1
     * @maxLength 80
     */
  actionLabel: string;
  /**
     * Entity-owned site, or null for an explicitly unmapped office.
     * @nullable
     */
  siteId: string | null;
  /**
     * Optional entity-owned booking type; requires a site.
     * @nullable
     */
  spaceTypeId: string | null;
  /** Keep this action unavailable until a real booking type is selected. */
  requireBookingType?: boolean;
}

export type HomeSiteTilesHeadingTone = typeof HomeSiteTilesHeadingTone[keyof typeof HomeSiteTilesHeadingTone];


export const HomeSiteTilesHeadingTone = {
  default: 'default',
  accent: 'accent',
} as const;

export type HomeSiteTilesActionTone = typeof HomeSiteTilesActionTone[keyof typeof HomeSiteTilesActionTone];


export const HomeSiteTilesActionTone = {
  default: 'default',
  positive: 'positive',
} as const;

export interface HomeSiteTiles {
  /** @maxLength 80 */
  eyebrow: string;
  /** @maxLength 160 */
  title: string;
  /** @maxLength 800 */
  description: string;
  headingTone: HomeSiteTilesHeadingTone;
  actionTone: HomeSiteTilesActionTone;
  /**
     * @minItems 1
     * @maxItems 12
     */
  cards: HomeSiteTile[];
}

/**
 * @nullable
 */
export type HomeSiteTileTargetUnavailableReason = typeof HomeSiteTileTargetUnavailableReason[keyof typeof HomeSiteTileTargetUnavailableReason] | null;


export const HomeSiteTileTargetUnavailableReason = {
  unmapped: 'unmapped',
  missing: 'missing',
  type: 'type',
  inactive: 'inactive',
  catalogue: 'catalogue',
  module: 'module',
  access: 'access',
} as const;

/**
 * Current entity-scoped admission; no foreign site metadata or booking availability counts. Targets with no eligible site never link to unfiltered booking.
 */
export interface HomeSiteTileTarget {
  instanceId: string;
  cardId: string;
  /** @nullable */
  siteId: string | null;
  /** @nullable */
  spaceTypeId: string | null;
  /**
     * Actual scoped booking type code, only when the target is available.
     * @nullable
     */
  spaceTypeCode: string | null;
  available: boolean;
  /** @nullable */
  unavailableReason: HomeSiteTileTargetUnavailableReason;
}

/**
 * Rich HTML only; scoped semantic heading colour, never arbitrary CSS.
 */
export type HomeWebpartInstanceHeadingTone = typeof HomeWebpartInstanceHeadingTone[keyof typeof HomeWebpartInstanceHeadingTone];


export const HomeWebpartInstanceHeadingTone = {
  default: 'default',
  accent: 'accent',
} as const;

/**
 * Rich HTML only; bounded regular card or centred page introduction.
 */
export type HomeWebpartInstancePresentation = typeof HomeWebpartInstancePresentation[keyof typeof HomeWebpartInstancePresentation];


export const HomeWebpartInstancePresentation = {
  default: 'default',
  masthead: 'masthead',
} as const;

export interface HomeWebpartInstance {
  /**
     * @minLength 1
     * @maxLength 64
     * @pattern ^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$
     */
  id: string;
  type: HomeWebpartType;
  visible: boolean;
  /**
     * Rich HTML only; sanitised on save and read.
     * @maxLength 16000
     */
  html?: string;
  /** Rich HTML only; scoped semantic heading colour, never arbitrary CSS. */
  headingTone?: HomeWebpartInstanceHeadingTone;
  /** Rich HTML only; bounded regular card or centred page introduction. */
  presentation?: HomeWebpartInstancePresentation;
  siteTiles?: HomeSiteTiles;
}

export interface HomeWebpartCell {
  /** @maxItems 60 */
  instances: HomeWebpartInstance[];
}

export type HomeWebpartRowColumns = typeof HomeWebpartRowColumns[keyof typeof HomeWebpartRowColumns];


export const HomeWebpartRowColumns = {
  NUMBER_1: 1,
  NUMBER_2: 2,
} as const;

export interface HomeWebpartRow {
  /**
     * @minLength 1
     * @maxLength 64
     * @pattern ^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$
     */
  id: string;
  columns: HomeWebpartRowColumns;
  /**
     * @minItems 1
     * @maxItems 2
     */
  cells: HomeWebpartCell[];
}

export type HomeWebpartLayoutVersion = typeof HomeWebpartLayoutVersion[keyof typeof HomeWebpartLayoutVersion];


export const HomeWebpartLayoutVersion = {
  NUMBER_1: 1,
} as const;

/**
 * Version 1. Cell count equals columns; unique IDs; singleton built-ins; up to 30 rows, 60 instances, 20 rich HTML instances and 128000 UTF-8 bytes. Empty pages are valid.
 */
export interface HomeWebpartLayout {
  version: HomeWebpartLayoutVersion;
  /** @maxItems 30 */
  rows: HomeWebpartRow[];
}

export type NullableHomeWebpartLayout = HomeWebpartLayout | null;

export interface HomeWebpartDefinition {
  type: HomeWebpartType;
  /**
     * @minLength 1
     * @maxLength 80
     */
  name: string;
  /** @maxLength 400 */
  description: string;
  enabled: boolean;
  /** @maxItems 7 */
  moduleKeys: HomeWebpartModule[];
}

/**
 * @nullable
 */
export type HomeWebpartCatalogueEntryUnavailableReason = typeof HomeWebpartCatalogueEntryUnavailableReason[keyof typeof HomeWebpartCatalogueEntryUnavailableReason] | null;


export const HomeWebpartCatalogueEntryUnavailableReason = {
  catalogue: 'catalogue',
  module: 'module',
  access: 'access',
} as const;

export type HomeWebpartCatalogueEntry = HomeWebpartDefinition & {
  singleton: boolean;
  requiredModuleKeys: HomeWebpartModule[];
  available: boolean;
  /** @nullable */
  unavailableReason: HomeWebpartCatalogueEntryUnavailableReason;
};

export type HomeWebpartCatalogueVersion = typeof HomeWebpartCatalogueVersion[keyof typeof HomeWebpartCatalogueVersion];


export const HomeWebpartCatalogueVersion = {
  NUMBER_1: 1,
} as const;

export interface HomeWebpartCatalogue {
  version: HomeWebpartCatalogueVersion;
  /**
     * @minLength 64
     * @maxLength 64
     */
  revision: string;
  /**
     * @minItems 7
     * @maxItems 7
     */
  entries: HomeWebpartCatalogueEntry[];
  registeredModules: HomeWebpartModule[];
}

export interface SaveHomeWebpartCatalogue {
  /**
     * @minLength 64
     * @maxLength 64
     * @pattern ^[a-f0-9]{64}$
     */
  expectedRevision: string;
  /**
     * @minItems 7
     * @maxItems 7
     */
  entries: HomeWebpartDefinition[];
}

export interface SaveHomeWebpartPage {
  /**
     * @minLength 64
     * @maxLength 64
     * @pattern ^[a-f0-9]{64}$
     */
  expectedRevision: string;
  layout: NullableHomeWebpartLayout | null;
}

export type HomeWebpartPageSource = typeof HomeWebpartPageSource[keyof typeof HomeWebpartPageSource];


export const HomeWebpartPageSource = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
  builtIn: 'builtIn',
} as const;

/**
 * Managers receive retained unavailable instances and saved/inherited layouts; other viewers receive admitted published instances only. Null save resets inheritance. Sanitised IDs report modified input.
 */
export interface HomeWebpartPage {
  layout: HomeWebpartLayout;
  savedLayout: NullableHomeWebpartLayout | null;
  inheritedLayout: HomeWebpartLayout;
  source: HomeWebpartPageSource;
  /**
     * @minLength 64
     * @maxLength 64
     */
  revision: string;
  canManage: boolean;
  allowEntityOverride: boolean;
  catalogue: HomeWebpartCatalogue;
  sanitisedInstanceIds: string[];
  /** @maxItems 120 */
  siteTileTargets?: HomeSiteTileTarget[];
}

export type HomeLayoutSectionId = typeof HomeLayoutSectionId[keyof typeof HomeLayoutSectionId];


export const HomeLayoutSectionId = {
  welcome: 'welcome',
  'upcoming-bookings': 'upcoming-bookings',
  suggestions: 'suggestions',
  'more-ways': 'more-ways',
  booking: 'booking',
} as const;

export interface HomeLayoutSection {
  id: HomeLayoutSectionId;
  visible: boolean;
}

/**
 * Each current Home section ID must appear exactly once; order and visible control its presentation.
 */
export interface HomeLayout {
  /**
     * @minItems 5
     * @maxItems 5
     */
  sections: HomeLayoutSection[];
}

/**
 * @nullable
 */
export type NullableHomeLayout = {
  /**
     * @minItems 5
     * @maxItems 5
     */
  sections: HomeLayoutSection[];
} | null;

export type PlatformHomeLayoutSource = typeof PlatformHomeLayoutSource[keyof typeof PlatformHomeLayoutSource];


export const PlatformHomeLayoutSource = {
  platform: 'platform',
  builtIn: 'builtIn',
} as const;

export interface PlatformHomeLayout {
  layout: HomeLayout;
  source: PlatformHomeLayoutSource;
}

export type CustomerHomeLayoutSource = typeof CustomerHomeLayoutSource[keyof typeof CustomerHomeLayoutSource];


export const CustomerHomeLayoutSource = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
  builtIn: 'builtIn',
} as const;

export interface CustomerHomeLayout {
  effective: HomeLayout;
  source: CustomerHomeLayoutSource;
  platformValue: NullableHomeLayout | null;
  tenantValue: NullableHomeLayout | null;
  entityValue: NullableHomeLayout | null;
  allowEntityOverride: boolean;
}

export type NavigationLayoutGroupItemsItem = typeof NavigationLayoutGroupItemsItem[keyof typeof NavigationLayoutGroupItemsItem];


export const NavigationLayoutGroupItemsItem = {
  home: 'home',
  book: 'book',
  'my-bookings': 'my-bookings',
  'work-plan': 'work-plan',
  'my-visitors': 'my-visitors',
  assistant: 'assistant',
  teams: 'teams',
  colleagues: 'colleagues',
  'manager-attendance': 'manager-attendance',
  'floor-map': 'floor-map',
  resources: 'resources',
  'staffed-services': 'staffed-services',
} as const;

export interface NavigationLayoutGroup {
  /**
     * @minLength 1
     * @maxLength 40
     * @pattern ^[a-z][a-z0-9-]{0,39}$
     */
  id: string;
  /**
     * @minLength 1
     * @maxLength 40
     */
  label: string;
  defaultCollapsed: boolean;
  items: NavigationLayoutGroupItemsItem[];
}

export interface NavigationLayout {
  enabled: boolean;
  /** @maxItems 10 */
  groups: NavigationLayoutGroup[];
}

/**
 * @nullable
 */
export type NullableNavigationLayout = {
  enabled: boolean;
  /** @maxItems 10 */
  groups: NavigationLayoutGroup[];
} | null;

export type PlatformNavigationLayoutSource = typeof PlatformNavigationLayoutSource[keyof typeof PlatformNavigationLayoutSource];


export const PlatformNavigationLayoutSource = {
  platform: 'platform',
  builtIn: 'builtIn',
} as const;

export interface PlatformNavigationLayout {
  layout: NavigationLayout;
  source: PlatformNavigationLayoutSource;
}

export type CustomerNavigationLayoutSource = typeof CustomerNavigationLayoutSource[keyof typeof CustomerNavigationLayoutSource];


export const CustomerNavigationLayoutSource = {
  entity: 'entity',
  tenant: 'tenant',
  platform: 'platform',
  builtIn: 'builtIn',
} as const;

export interface CustomerNavigationLayout {
  effective: NavigationLayout;
  source: CustomerNavigationLayoutSource;
  platformValue: NullableNavigationLayout | null;
  tenantValue: NullableNavigationLayout | null;
  entityValue: NullableNavigationLayout | null;
  allowEntityOverride: boolean;
}

export interface CustomerOverridePermissions {
  lightPrimary?: boolean;
  darkPrimary?: boolean;
  bookingNameAudience?: boolean;
  deskLocationAudience?: boolean;
  logo?: boolean;
  skinColors?: boolean;
  skinTypography?: boolean;
  skinLogo?: boolean;
  skinDecorations?: boolean;
  navigationLayout?: boolean;
  homeLayout?: boolean;
}

export type CustomerSettingsPatchScope = typeof CustomerSettingsPatchScope[keyof typeof CustomerSettingsPatchScope];


export const CustomerSettingsPatchScope = {
  tenant: 'tenant',
  entity: 'entity',
} as const;

/**
 * Semantic colours for one colour theme.
 */
export interface CustomerSkinTheme {
  /** @nullable */
  primary?: string | null;
  /** @nullable */
  onPrimary?: string | null;
  /** @nullable */
  headerBackground?: string | null;
  /** @nullable */
  headerForeground?: string | null;
  /** @nullable */
  accent?: string | null;
}

/**
 * Named supporting colours that decorative elements can reference.
 */
export interface CustomerSkinPalette {
  /** @nullable */
  primary?: string | null;
  /** @nullable */
  secondary?: string | null;
  /** @nullable */
  accent?: string | null;
  /** @nullable */
  muted?: string | null;
  /** @nullable */
  subtle?: string | null;
  /** @nullable */
  neutral?: string | null;
}

/**
 * @nullable
 */
export type CustomerSkinTypographyFont = typeof CustomerSkinTypographyFont[keyof typeof CustomerSkinTypographyFont] | null;


export const CustomerSkinTypographyFont = {
  app: 'app',
  lato: 'lato',
} as const;

export type CustomerSkinTypographyWeightsItem = typeof CustomerSkinTypographyWeightsItem[keyof typeof CustomerSkinTypographyWeightsItem];


export const CustomerSkinTypographyWeightsItem = {
  regular: 'regular',
  bold: 'bold',
  heavy: 'heavy',
  italic: 'italic',
} as const;

/**
 * @nullable
 */
export type CustomerSkinTypographyFallback = typeof CustomerSkinTypographyFallback[keyof typeof CustomerSkinTypographyFallback] | null;


export const CustomerSkinTypographyFallback = {
  calibri: 'calibri',
  system: 'system',
} as const;

/**
 * Approved typeface, supported weights and controlled fallback stack.
 */
export interface CustomerSkinTypography {
  /** @nullable */
  font?: CustomerSkinTypographyFont;
  /**
     * @minItems 1
     * @maxItems 4
     * @nullable
     */
  weights?: CustomerSkinTypographyWeightsItem[] | null;
  /** @nullable */
  fallback?: CustomerSkinTypographyFallback;
}

/**
 * @nullable
 */
export type CustomerSkinLogoOnDark = typeof CustomerSkinLogoOnDark[keyof typeof CustomerSkinLogoOnDark] | null;


export const CustomerSkinLogoOnDark = {
  'as-uploaded': 'as-uploaded',
  'light-plate': 'light-plate',
} as const;

/**
 * Logo presentation bounds; logo content stays in the existing scoped logo storage.
 */
export interface CustomerSkinLogo {
  /**
     * @minimum 24
     * @maximum 640
     * @nullable
     */
  minWidth?: number | null;
  /**
     * @minimum 0
     * @maximum 160
     * @nullable
     */
  clearSpace?: number | null;
  /** @nullable */
  onDark?: CustomerSkinLogoOnDark;
}

export type CustomerSkinCirclesColorsItem = typeof CustomerSkinCirclesColorsItem[keyof typeof CustomerSkinCirclesColorsItem];


export const CustomerSkinCirclesColorsItem = {
  primary: 'primary',
  secondary: 'secondary',
  accent: 'accent',
  muted: 'muted',
  subtle: 'subtle',
  neutral: 'neutral',
} as const;

/**
 * @nullable
 */
export type CustomerSkinCirclesPlacement = typeof CustomerSkinCirclesPlacement[keyof typeof CustomerSkinCirclesPlacement] | null;


export const CustomerSkinCirclesPlacement = {
  home: 'home',
  none: 'none',
} as const;

/**
 * Overlapping-circle decoration, bounded to safe measurements.
 */
export interface CustomerSkinCircles {
  /** @nullable */
  enabled?: boolean | null;
  /**
     * @minItems 1
     * @maxItems 4
     * @nullable
     */
  colors?: CustomerSkinCirclesColorsItem[] | null;
  /**
     * @minimum 0
     * @maximum 24
     * @nullable
     */
  topStroke?: number | null;
  /**
     * @minimum 0
     * @maximum 24
     * @nullable
     */
  bottomStroke?: number | null;
  /**
     * @minimum 0
     * @maximum 100
     * @nullable
     */
  overlap?: number | null;
  /**
     * @minimum 0
     * @maximum 80
     * @nullable
     */
  crop?: number | null;
  /** @nullable */
  placement?: CustomerSkinCirclesPlacement;
}

/**
 * @nullable
 */
export type CustomerSkinHighlightPlacement = typeof CustomerSkinHighlightPlacement[keyof typeof CustomerSkinHighlightPlacement] | null;


export const CustomerSkinHighlightPlacement = {
  'main-title': 'main-title',
  none: 'none',
} as const;

/**
 * Short accent line under main page titles.
 */
export interface CustomerSkinHighlight {
  /** @nullable */
  enabled?: boolean | null;
  /** @nullable */
  color?: string | null;
  /**
     * @minimum 0
     * @maximum 12
     * @nullable
     */
  thickness?: number | null;
  /**
     * @minimum 0
     * @maximum 480
     * @nullable
     */
  width?: number | null;
  /** @nullable */
  placement?: CustomerSkinHighlightPlacement;
}

/**
 * Decorative elements and their supported placement.
 */
export interface CustomerSkinDecorations {
  /** @nullable */
  enabled?: boolean | null;
  circles?: CustomerSkinCircles;
  highlight?: CustomerSkinHighlight;
}

/**
 * Stored skin configuration. Omitted or null fields are unset; entity values inherit tenant defaults unless overridden with permission.
 */
export interface CustomerSkinValues {
  /**
     * @minimum 1
     * @maximum 1
     */
  version?: number;
  light?: CustomerSkinTheme;
  dark?: CustomerSkinTheme;
  palette?: CustomerSkinPalette;
  typography?: CustomerSkinTypography;
  logo?: CustomerSkinLogo;
  decorations?: CustomerSkinDecorations;
}

export interface CustomerSettingsPatch {
  scope: CustomerSettingsPatchScope;
  values?: CustomerPolicyValues;
  allowEntityOverrides?: CustomerOverridePermissions;
  skin?: CustomerSkinValues;
  navigationLayout?: NullableNavigationLayout | null;
  homeLayout?: NullableHomeLayout | null;
}

export type CustomerLogoScope = typeof CustomerLogoScope[keyof typeof CustomerLogoScope];


export const CustomerLogoScope = {
  tenant: 'tenant',
  entity: 'entity',
} as const;

export interface CustomerLogo {
  dataUrl: string;
  altText: string;
  scope: CustomerLogoScope;
}

export type CustomerSettingsForeground = {
  /** @nullable */
  lightPrimary?: string | null;
  /** @nullable */
  darkPrimary?: string | null;
};

export interface CustomerSkinEffectiveDecorations {
  /** @nullable */
  enabled?: boolean | null;
  circles: CustomerSkinCircles;
  highlight: CustomerSkinHighlight;
}

export interface CustomerSkinEffective {
  /**
     * @minimum 1
     * @maximum 1
     */
  version: number;
  light: CustomerSkinTheme;
  dark: CustomerSkinTheme;
  palette: CustomerSkinPalette;
  typography: CustomerSkinTypography;
  logo: CustomerSkinLogo;
  decorations: CustomerSkinEffectiveDecorations;
}

export interface CustomerSkinOverridePermissions {
  skinColors?: boolean;
  skinTypography?: boolean;
  skinLogo?: boolean;
  skinDecorations?: boolean;
}

export interface CustomerSettingsSkin {
  /**
     * @minimum 1
     * @maximum 1
     */
  version: number;
  effective: CustomerSkinEffective;
  tenantValues: CustomerSkinValues;
  entityValues: CustomerSkinValues;
  allowEntityOverrides: CustomerSkinOverridePermissions;
}

export interface CustomerSettings {
  tenantId: string;
  entityId: string;
  effective: CustomerPolicyValues;
  navigationLayout: CustomerNavigationLayout;
  homeLayout: CustomerHomeLayout;
  foreground: CustomerSettingsForeground;
  tenantValues: CustomerPolicyValues;
  entityValues: CustomerPolicyValues;
  allowEntityOverrides: CustomerOverridePermissions;
  canManageTenant: boolean;
  canManageEntity: boolean;
  logo: CustomerLogo | null;
  skin: CustomerSettingsSkin;
}

export interface WorkplaceOptionValues {
  /** @nullable */
  siteCoordinatesDisabled?: boolean | null;
  /** @nullable */
  australianTimezoneList?: boolean | null;
  /**
     * Tenant-only default for new sites; unset uses Australia/Sydney.
     * @minLength 1
     * @maxLength 64
     * @nullable
     */
  defaultSiteTimezone?: string | null;
  /**
     * Legacy field; Book always uses the one-screen layout.
     * @deprecated
     * @nullable
     */
  oneScreenBook?: boolean | null;
}

export interface WorkplaceOverridePermissions {
  siteCoordinatesDisabled?: boolean;
  australianTimezoneList?: boolean;
  /**
     * Legacy field; the Book layout is shared across entities.
     * @deprecated
     */
  oneScreenBook?: boolean;
}

export interface WorkplaceEditableValues {
  /** @nullable */
  siteCoordinatesDisabled?: boolean | null;
  /** @nullable */
  australianTimezoneList?: boolean | null;
  /**
     * Tenant scope only. Null clears the default.
     * @minLength 1
     * @maxLength 64
     * @nullable
     */
  defaultSiteTimezone?: string | null;
}

export interface WorkplaceEditableOverrides {
  siteCoordinatesDisabled?: boolean;
  australianTimezoneList?: boolean;
}

export type WorkplaceOptionsPatchScope = typeof WorkplaceOptionsPatchScope[keyof typeof WorkplaceOptionsPatchScope];


export const WorkplaceOptionsPatchScope = {
  tenant: 'tenant',
  entity: 'entity',
} as const;

export interface WorkplaceOptionsPatch {
  scope: WorkplaceOptionsPatchScope;
  values?: WorkplaceEditableValues;
  allowEntityOverrides?: WorkplaceEditableOverrides;
}

export interface WorkplaceOptionsSettings {
  tenantId: string;
  entityId: string;
  platformDefaults: WorkplaceOptionValues;
  effective: WorkplaceOptionValues;
  tenantValues: WorkplaceOptionValues;
  entityValues: WorkplaceOptionValues;
  allowEntityOverrides: WorkplaceOverridePermissions;
  platformAdmin?: boolean;
  canManageTenant: boolean;
  canManageEntity: boolean;
}

export type HealthStatus = typeof HealthStatus[keyof typeof HealthStatus];


export const HealthStatus = {
  ok: 'ok',
} as const;

export type HealthDatabase = typeof HealthDatabase[keyof typeof HealthDatabase];


export const HealthDatabase = {
  connected: 'connected',
  not_configured: 'not_configured',
} as const;

export type HealthPlugins = {
  database: boolean;
  storage: boolean;
  ai: boolean;
  auth: boolean;
};

export interface Health {
  status: HealthStatus;
  uptimeSeconds: number;
  timestamp: string;
  database: HealthDatabase;
  plugins: HealthPlugins;
}

export type PluginStatusValue = typeof PluginStatusValue[keyof typeof PluginStatusValue];


export const PluginStatusValue = {
  ready: 'ready',
  disabled: 'disabled',
  misconfigured: 'misconfigured',
} as const;

export type PublicPluginStatusValue = typeof PublicPluginStatusValue[keyof typeof PublicPluginStatusValue];


export const PublicPluginStatusValue = {
  ready: 'ready',
  disabled: 'disabled',
} as const;

export interface PublicPlugin {
  enabled: boolean;
  status: PublicPluginStatusValue;
}

export type DatabasePluginCapabilities = {
  persistentStorage: boolean;
  appUsers: boolean;
};

export interface DatabasePlugin {
  enabled: boolean;
  status: PluginStatusValue;
  reason?: string;
  capabilities: DatabasePluginCapabilities;
}

export type StoragePluginCapabilities = {
  fileUpload: boolean;
  fileDownload: boolean;
};

export interface StoragePlugin {
  enabled: boolean;
  status: PluginStatusValue;
  reason?: string;
  capabilities: StoragePluginCapabilities;
}

export type AiPluginCapabilities = {
  llm: boolean;
  embeddings: boolean;
  semanticSearch: boolean;
};

export interface AiPlugin {
  enabled: boolean;
  status: PluginStatusValue;
  reason?: string;
  capabilities: AiPluginCapabilities;
}

/**
 * Public MSAL client configuration (no secrets).
 */
export interface AuthClientConfig {
  clientId: string;
  authority: string;
  redirectUri: string;
  postLoginRedirect: string;
  scopes: string[];
}

export interface PublicAuthPlugin {
  enabled: boolean;
  status: PublicPluginStatusValue;
  config?: AuthClientConfig;
}

export type AuthPluginProvider = typeof AuthPluginProvider[keyof typeof AuthPluginProvider];


export const AuthPluginProvider = {
  microsoft_entra_workforce: 'microsoft_entra_workforce',
} as const;

export type AuthPluginCapabilities = {
  login: boolean;
  protectedRoutes: boolean;
  persistentUsers: boolean;
};

export interface AuthPlugin {
  enabled: boolean;
  status: PluginStatusValue;
  reason?: string;
  provider?: AuthPluginProvider;
  mode?: string;
  capabilities: AuthPluginCapabilities;
  config?: AuthClientConfig;
}

export type NotificationsPluginCapabilities = {
  inApp: boolean;
  email: boolean;
};

export type NotificationsPluginEmailMode = typeof NotificationsPluginEmailMode[keyof typeof NotificationsPluginEmailMode];


export const NotificationsPluginEmailMode = {
  acs: 'acs',
  graph: 'graph',
  cdf_relay: 'cdf_relay',
  in_app_only: 'in_app_only',
} as const;

export interface NotificationsPlugin {
  enabled: boolean;
  status: PluginStatusValue;
  reason?: string;
  capabilities: NotificationsPluginCapabilities;
  emailMode?: NotificationsPluginEmailMode;
  /**
     * Masked sender domain only (never the connection string).
     * @nullable
     */
  senderHint?: string | null;
}

export interface PluginsStatus {
  database: PublicPlugin;
  storage: PublicPlugin;
  ai: PublicPlugin;
  auth: PublicAuthPlugin;
  notifications: PublicPlugin;
}

export interface PluginsStatusDetail {
  database: DatabasePlugin;
  storage: StoragePlugin;
  ai: AiPlugin;
  auth: AuthPlugin;
  notifications: NotificationsPlugin;
}

/**
 * UI AppRole derived from product role assignments (it_admin/space_admin → admin; employee/team_assistant/visitor_host → editor).
 */
export type CurrentUserRole = typeof CurrentUserRole[keyof typeof CurrentUserRole];


export const CurrentUserRole = {
  viewer: 'viewer',
  editor: 'editor',
  admin: 'admin',
} as const;

export type CurrentUserProductRolesItem = typeof CurrentUserProductRolesItem[keyof typeof CurrentUserProductRolesItem];


export const CurrentUserProductRolesItem = {
  employee: 'employee',
  team_assistant: 'team_assistant',
  space_admin: 'space_admin',
  it_admin: 'it_admin',
  visitor_host: 'visitor_host',
} as const;

export type CurrentUserGroupAccessState = typeof CurrentUserGroupAccessState[keyof typeof CurrentUserGroupAccessState];


export const CurrentUserGroupAccessState = {
  verified: 'verified',
  unavailable: 'unavailable',
} as const;

/**
 * Current server verification outcome; no directory enumeration. Unavailable checks withhold group access while preserving independent access.
 */
export type CurrentUserGroupAccess = {
  state: CurrentUserGroupAccessState;
  /** @maxLength 500 */
  message: string;
  retryable: boolean;
};

export type TenantMembershipStatus = typeof TenantMembershipStatus[keyof typeof TenantMembershipStatus];


export const TenantMembershipStatus = {
  active: 'active',
  suspended: 'suspended',
  archived: 'archived',
} as const;

export interface TenantRoleSummary {
  id: string;
  key: string;
  name: string;
  isSystem: boolean;
}

export interface TenantMembership {
  /** A current independent individual source grants access. */
  individualAccess?: boolean;
  /**
     * Currently verified organisation bindings; these IDs cannot be submitted as membership proof.
     * @maxItems 100
     */
  groupBindingIds?: string[];
  id: string;
  /** Preferred address, using the current alias when present. */
  slug: string;
  baseSlug: string;
  /** @nullable */
  alias: string | null;
  name: string;
  tenantId: string;
  tenantName: string;
  status: TenantMembershipStatus;
  roles: TenantRoleSummary[];
  /** Effective permission keys from the member's roles plus any active elevation or override overlays. Contains '*' when an override session grants full tenant-admin permissions. */
  permissions: string[];
}

export interface ActiveElevation {
  id: string;
  organizationId: string;
  /** @nullable */
  roleId: string | null;
  /** @nullable */
  permissionKeys: string[] | null;
  reason: string;
  expiresAt: string;
}

export interface ActiveOverride {
  id: string;
  organizationId: string;
  reason: string;
  startedAt: string;
  expiresAt: string;
}

export interface CurrentUser {
  /** @nullable */
  appUserId?: string | null;
  entraObjectId: string;
  entraTenantId: string;
  /** @nullable */
  email?: string | null;
  displayName: string;
  /** True when the user is synced to the app_users table. */
  persistent: boolean;
  /** UI AppRole derived from product role assignments (it_admin/space_admin → admin; employee/team_assistant/visitor_host → editor). */
  role: CurrentUserRole;
  /** Product persona role codes assigned in the database. */
  productRoles: CurrentUserProductRolesItem[];
  /** Effective capability codes from product roles plus explicit user_capabilities grants (e.g. booking:self, inventory:manage). */
  capabilities: string[];
  /** True when the user has a platform_admins row (platform-plane access). */
  isPlatformAdmin: boolean;
  /** Organisation choices from current individual and verified Microsoft group sources. Override sessions never manufacture organisation membership. */
  tenants: TenantMembership[];
  /** Current server verification outcome; no directory enumeration. Unavailable checks withhold group access while preserving independent access. */
  groupAccess?: CurrentUserGroupAccess;
  /** Approved, non-expired JIT elevation grants. */
  activeElevations: ActiveElevation[];
  /** Active (non-ended, non-expired) break-glass override sessions. */
  activeOverrides: ActiveOverride[];
}

export type ProductRoleCode = typeof ProductRoleCode[keyof typeof ProductRoleCode];


export const ProductRoleCode = {
  employee: 'employee',
  team_assistant: 'team_assistant',
  space_admin: 'space_admin',
  it_admin: 'it_admin',
  visitor_host: 'visitor_host',
} as const;

export interface AdminUser {
  userId: string;
  displayName: string;
  /** @nullable */
  firstName?: string | null;
  /** @nullable */
  lastName?: string | null;
  /** @nullable */
  email: string | null;
  /** @nullable */
  lastLoginAt: string | null;
  roles: ProductRoleCode[];
}

export interface AdminUserList {
  items: AdminUser[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export interface AdminUserRoleAssignment {
  userId: string;
  roleCode: ProductRoleCode;
  roles: ProductRoleCode[];
  created: boolean;
}

export type ManagerScopeMode = typeof ManagerScopeMode[keyof typeof ManagerScopeMode];


export const ManagerScopeMode = {
  directory: 'directory',
  local: 'local',
} as const;

export interface ManagerReport {
  userId: string;
  displayName: string;
  /** @nullable */
  email: string | null;
}

export interface ManagerReportPage {
  items: ManagerReport[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type ManagerScopeResponseStatus = typeof ManagerScopeResponseStatus[keyof typeof ManagerScopeResponseStatus];


export const ManagerScopeResponseStatus = {
  ready: 'ready',
  unavailable: 'unavailable',
} as const;

export type ManagerScopeResponseReason = typeof ManagerScopeResponseReason[keyof typeof ManagerScopeResponseReason];


export const ManagerScopeResponseReason = {
  setup_required: 'setup_required',
  consent_required: 'consent_required',
  tenant_mismatch: 'tenant_mismatch',
  directory_error: 'directory_error',
  incomplete_directory: 'incomplete_directory',
  scope_changed: 'scope_changed',
} as const;

export interface ManagerScopeResponse {
  status: ManagerScopeResponseStatus;
  source: ManagerScopeMode;
  reason?: ManagerScopeResponseReason;
  checkedAt: string;
  reports: ManagerReportPage;
}

export type ManagerScopeStatusStatus = typeof ManagerScopeStatusStatus[keyof typeof ManagerScopeStatusStatus];


export const ManagerScopeStatusStatus = {
  ready: 'ready',
  unavailable: 'unavailable',
} as const;

export type ManagerScopeStatusReason = typeof ManagerScopeStatusReason[keyof typeof ManagerScopeStatusReason];


export const ManagerScopeStatusReason = {
  setup_required: 'setup_required',
  consent_required: 'consent_required',
  tenant_mismatch: 'tenant_mismatch',
  directory_error: 'directory_error',
  incomplete_directory: 'incomplete_directory',
  scope_changed: 'scope_changed',
} as const;

export interface ManagerScopeStatus {
  status: ManagerScopeStatusStatus;
  reason?: ManagerScopeStatusReason;
  checkedAt: string;
}

export interface AdminManagerScope {
  manager: ManagerReport;
  mode: ManagerScopeMode;
  /** @nullable */
  updatedAt: string | null;
  resolution: ManagerScopeStatus;
  localReports: ManagerReportPage;
}

export interface ManagerScopeModeRequest {
  mode: ManagerScopeMode;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
}

export interface ManagerScopeModeResult {
  managerUserId: string;
  mode: ManagerScopeMode;
}

export interface ManagerReportRequest {
  reportUserId: string;
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
}

export interface ManagerScopeReasonRequest {
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
}

export type AdminCapabilityProbeRole = typeof AdminCapabilityProbeRole[keyof typeof AdminCapabilityProbeRole];


export const AdminCapabilityProbeRole = {
  viewer: 'viewer',
  editor: 'editor',
  admin: 'admin',
} as const;

export interface AdminCapabilityProbe {
  ok: boolean;
  capability: string;
  role: AdminCapabilityProbeRole;
  /** @nullable */
  appUserId: string | null;
}

export interface EntityProfile {
  description: string;
  websiteUrl: string;
  industry: string;
  locations: string;
  contacts: string;
  strategicGoals: string;
  constraints: string;
  notes: string;
  enrichedSummary: string;
  requirementSimilarityScope: string;
  logoIdentity: string;
}

export interface EntityProfileUpdate {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  profile?: EntityProfile;
}

export type EntityDetailStatus = typeof EntityDetailStatus[keyof typeof EntityDetailStatus];


export const EntityDetailStatus = {
  active: 'active',
  suspended: 'suspended',
  archived: 'archived',
} as const;

export type EntityDetailParentStatus = typeof EntityDetailParentStatus[keyof typeof EntityDetailParentStatus];


export const EntityDetailParentStatus = {
  active: 'active',
  suspended: 'suspended',
  archived: 'archived',
} as const;

export interface EntityDetail {
  id: string;
  tenantId: string;
  name: string;
  /** Preferred address using the current alias or GUID base. */
  slug: string;
  baseSlug: string;
  /** @nullable */
  alias: string | null;
  status: EntityDetailStatus;
  parentStatus?: EntityDetailParentStatus;
  profile: EntityProfile;
  createdAt: string;
  updatedAt: string;
}

export interface EntityPage {
  items: EntityDetail[];
  limit: number;
  offset: number;
  hasMore: boolean;
}

export type TenantDetailStatus = typeof TenantDetailStatus[keyof typeof TenantDetailStatus];


export const TenantDetailStatus = {
  active: 'active',
  suspended: 'suspended',
  archived: 'archived',
} as const;

/**
 * @nullable
 */
export type TenantDetailSettings = { [key: string]: unknown } | null;

export interface TenantDetail {
  id: string;
  name: string;
  /** Preferred address using the current alias or GUID base. */
  slug: string;
  baseSlug: string;
  /** @nullable */
  alias: string | null;
  status: TenantDetailStatus;
  /** @nullable */
  settings: TenantDetailSettings;
  /** @nullable */
  createdBy: string | null;
  /** @nullable */
  suspendedAt: string | null;
  /** @nullable */
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export type PlatformTenantListItem = TenantDetail & {
  /** @minimum 0 */
  entityCount: number;
};

export type TenantDeletionPreviewStatus = typeof TenantDeletionPreviewStatus[keyof typeof TenantDeletionPreviewStatus];


export const TenantDeletionPreviewStatus = {
  active: 'active',
  suspended: 'suspended',
  archived: 'archived',
} as const;

export type TenantDeletionPreviewRecordCountsItem = {
  table: string;
  /** @minimum 0 */
  count: number;
};

export interface TenantDeletionPreview {
  tenantId: string;
  tenantName: string;
  status: TenantDeletionPreviewStatus;
  /** @minimum 0 */
  entityCount: number;
  recordCounts: TenantDeletionPreviewRecordCountsItem[];
  unclassifiedTables: string[];
  blockers: string[];
  deletionEnabled: false;
}

export interface PlatformAdmin {
  userId: string;
  /** @nullable */
  email: string | null;
  displayName: string;
  /** @nullable */
  assignedBy: string | null;
  assignedAt: string;
}

export interface MemberCandidate {
  userId: string;
  displayName: string;
  email: string;
}

export interface MemberCandidatePage {
  items: MemberCandidate[];
  limit: number;
  offset: number;
  hasMore: boolean;
}

/**
 * Stored reference provenance, not proof of current access. Use administrator access-sources for current verification.
 */
export type TenantMemberAccessOrigin = typeof TenantMemberAccessOrigin[keyof typeof TenantMemberAccessOrigin];


export const TenantMemberAccessOrigin = {
  individual: 'individual',
  group: 'group',
} as const;

export interface TenantMember {
  /** Stored reference provenance, not proof of current access. Use administrator access-sources for current verification. */
  accessOrigin?: TenantMemberAccessOrigin;
  memberId: string;
  userId: string;
  /** @nullable */
  email: string | null;
  displayName: string;
  roles: TenantRoleSummary[];
  joinedAt: string;
}

export type TenantInviteStatus = typeof TenantInviteStatus[keyof typeof TenantInviteStatus];


export const TenantInviteStatus = {
  pending: 'pending',
  accepted: 'accepted',
  revoked: 'revoked',
  expired: 'expired',
} as const;

export interface TenantInvite {
  id: string;
  organizationId: string;
  email: string;
  roleId: string;
  roleName: string;
  status: TenantInviteStatus;
  /** @nullable */
  invitedBy: string | null;
  /** @nullable */
  invitedByName: string | null;
  /** @nullable */
  acceptedBy: string | null;
  /** @nullable */
  acceptedAt: string | null;
  expiresAt: string;
  /** @nullable */
  revokedAt: string | null;
  createdAt: string;
}

export interface TenantRoleDetail {
  id: string;
  organizationId: string;
  key: string;
  name: string;
  /** @nullable */
  description: string | null;
  isSystem: boolean;
  permissions: string[];
  memberCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface RoleDeleteResult {
  removedAssignments: number;
}

export type ElevationRequestStatus = typeof ElevationRequestStatus[keyof typeof ElevationRequestStatus];


export const ElevationRequestStatus = {
  pending: 'pending',
  approved: 'approved',
  denied: 'denied',
  expired: 'expired',
} as const;

export interface ElevationRequest {
  id: string;
  requesterId: string;
  /** @nullable */
  requesterEmail?: string | null;
  requesterDisplayName: string;
  organizationId: string;
  /** @nullable */
  roleId: string | null;
  /** @nullable */
  roleName: string | null;
  /** @nullable */
  permissionKeys: string[] | null;
  requestedDurationHours: number;
  reason: string;
  status: ElevationRequestStatus;
  /** @nullable */
  approvedBy: string | null;
  /** @nullable */
  approvedAt: string | null;
  /** @nullable */
  expiresAt: string | null;
  /** @nullable */
  deniedReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ElevationRequestList {
  items: ElevationRequest[];
  limit: number;
  offset: number;
}

/**
 * @nullable
 */
export type OverrideSessionMetadata = { [key: string]: unknown } | null;

export interface OverrideSession {
  id: string;
  platformAdminId: string;
  organizationId: string;
  tenantName: string;
  tenantSlug: string;
  reason: string;
  startedAt: string;
  expiresAt: string;
  /** @nullable */
  endedAt: string | null;
  /** @nullable */
  endedBy: string | null;
  /** @nullable */
  metadata: OverrideSessionMetadata;
  createdAt: string;
}

export interface OverrideSessionList {
  items: OverrideSession[];
  limit: number;
  offset: number;
}

export type GovernanceReadinessAudit = {
  retentionEnabled: boolean;
  custodyConfigured: boolean;
  minimumAgeDays: number;
  blockers: string[];
};

export type GovernanceReadinessTransfer = {
  enabled: boolean;
  registeredTableCount: number;
  missingAdapters: string[];
  blockers: string[];
};

export interface GovernanceReadiness {
  audit: GovernanceReadinessAudit;
  transfer: GovernanceReadinessTransfer;
}

export type TenancyAuditEventPlane = typeof TenancyAuditEventPlane[keyof typeof TenancyAuditEventPlane];


export const TenancyAuditEventPlane = {
  platform: 'platform',
  tenant: 'tenant',
} as const;

/**
 * @nullable
 */
export type TenancyAuditEventMetadata = { [key: string]: unknown } | null;

export interface TenancyAuditEvent {
  id: string;
  at: string;
  /** @nullable */
  actorUserId: string | null;
  /** @nullable */
  tenantId: string | null;
  /** @nullable */
  organizationId: string | null;
  plane: TenancyAuditEventPlane;
  action: string;
  targetType: string;
  /** @nullable */
  targetId: string | null;
  /** @nullable */
  metadata: TenancyAuditEventMetadata;
  /** @nullable */
  requestId: string | null;
  /** @nullable */
  correlationId: string | null;
}

export interface TenancyAuditEventList {
  items: TenancyAuditEvent[];
  limit: number;
  offset: number;
}

export type FeatureKey = typeof FeatureKey[keyof typeof FeatureKey];


export const FeatureKey = {
  booking: 'booking',
  workplace_planning: 'workplace_planning',
  visitors: 'visitors',
  staffed_services: 'staffed_services',
  maps_rooms: 'maps_rooms',
  reporting: 'reporting',
  ai_tools: 'ai_tools',
} as const;

export interface FeatureEnabledUpdate {
  enabled: boolean;
}

export type EntityFeatureUpdateOverride = typeof EntityFeatureUpdateOverride[keyof typeof EntityFeatureUpdateOverride];


export const EntityFeatureUpdateOverride = {
  off: 'off',
  inherit: 'inherit',
} as const;

export interface EntityFeatureUpdate {
  override: EntityFeatureUpdateOverride;
}

export interface FeatureSetting {
  moduleKey: FeatureKey;
  enabled: boolean;
}

export interface FeatureSettingsList {
  items: FeatureSetting[];
}

export type EffectiveFeatureEntityOverride = typeof EffectiveFeatureEntityOverride[keyof typeof EffectiveFeatureEntityOverride];


export const EffectiveFeatureEntityOverride = {
  off: 'off',
  inherit: 'inherit',
} as const;

/**
 * @nullable
 */
export type EffectiveFeatureBlockedBy = typeof EffectiveFeatureBlockedBy[keyof typeof EffectiveFeatureBlockedBy] | null;


export const EffectiveFeatureBlockedBy = {
  platform: 'platform',
  tenant: 'tenant',
  entity: 'entity',
} as const;

export interface EffectiveFeature {
  moduleKey: FeatureKey;
  platformEnabled: boolean;
  tenantEnabled: boolean;
  entityOverride: EffectiveFeatureEntityOverride;
  inheritedEnabled: boolean;
  effectiveEnabled: boolean;
  /** @nullable */
  blockedBy: EffectiveFeatureBlockedBy;
  ready: boolean;
}

export interface EffectiveFeaturesList {
  items: EffectiveFeature[];
}

export interface DirectoryPerson {
  id: string;
  displayName: string;
  mail: string;
}

/**
 * How results were obtained — delegated OBO, app-only fallback, or unavailable
 */
export type DirectorySearchResultSource = typeof DirectorySearchResultSource[keyof typeof DirectorySearchResultSource];


export const DirectorySearchResultSource = {
  graph_delegated: 'graph_delegated',
  graph_app: 'graph_app',
  unavailable: 'unavailable',
} as const;

export interface DirectorySearchResult {
  /** false when Graph is not configured or consent is missing — UI falls back to manual email entry */
  available: boolean;
  items: DirectoryPerson[];
  /** How results were obtained — delegated OBO, app-only fallback, or unavailable */
  source: DirectorySearchResultSource;
}

export interface DemoSeedCounts {
  /** @minimum 0 */
  sites: number;
  /** @minimum 0 */
  buildings: number;
  /** @minimum 0 */
  floors: number;
  /** @minimum 0 */
  zones: number;
  /** @minimum 0 */
  spaces: number;
  /** @minimum 0 */
  amenityLinks?: number;
}

export type DemoSeedResultCreated = {
  /** @minimum 0 */
  sites: number;
  /** @minimum 0 */
  buildings: number;
  /** @minimum 0 */
  floors: number;
  /** @minimum 0 */
  zones: number;
  /** @minimum 0 */
  spaces: number;
  /** @minimum 0 */
  amenityLinks: number;
};

export type DemoSeedResultCatalog = {
  /** @minimum 0 */
  spaceTypesInserted: number;
  /** @minimum 0 */
  amenitiesInserted: number;
};

export type DemoSeedResultDemoUsers = {
  /** @minimum 0 */
  created: number;
  /** @minimum 0 */
  skipped: number;
};

export type DemoSeedResultBookings = {
  /** @minimum 0 */
  created: number;
  /** @minimum 0 */
  skipped: number;
  /** @minimum 0 */
  conflicts: number;
};

export type DemoSeedResultCleanup = {
  /** @minimum 0 */
  bookings: number;
  /** @minimum 0 */
  spaces: number;
  /** @minimum 0 */
  sites: number;
};

export interface DemoSeedResult {
  siteId: string;
  created: DemoSeedResultCreated;
  skipped: DemoSeedCounts;
  catalog: DemoSeedResultCatalog;
  demoUsers: DemoSeedResultDemoUsers;
  bookings: DemoSeedResultBookings;
  cleanup: DemoSeedResultCleanup;
  message: string;
}

export type IntegrationConnectorStatus = typeof IntegrationConnectorStatus[keyof typeof IntegrationConnectorStatus];


export const IntegrationConnectorStatus = {
  ready: 'ready',
  disconnected: 'disconnected',
  degraded: 'degraded',
  disabled: 'disabled',
  not_configured: 'not_configured',
} as const;

export type IntegrationsStatusGraphMode = typeof IntegrationsStatusGraphMode[keyof typeof IntegrationsStatusGraphMode];


export const IntegrationsStatusGraphMode = {
  mock: 'mock',
  live: 'live',
  none: 'none',
} as const;

export type IntegrationsStatusGraph = {
  status: IntegrationConnectorStatus;
  connected: boolean;
  mode: IntegrationsStatusGraphMode;
  message: string;
  envConfigured?: boolean;
  forceMock?: boolean;
  liveSelected?: boolean;
  /** @nullable */
  lastError?: string | null;
  /** @nullable */
  lastCheckedAt?: string | null;
  /** @nullable */
  lastFallbackAt?: string | null;
  /** @nullable */
  lastFallbackError?: string | null;
  consentSteps?: string[];
  /** @nullable */
  tenantIdMasked?: string | null;
  /** @nullable */
  clientIdMasked?: string | null;
};

export type IntegrationsStatusLlm = {
  status: IntegrationConnectorStatus;
  message: string;
};

export type IntegrationsStatusDatabase = {
  status: IntegrationConnectorStatus;
  message: string;
};

export type IntegrationsStatusAuth = {
  status: IntegrationConnectorStatus;
  message: string;
};

export type IntegrationsStatusRoomAvailabilitySource = typeof IntegrationsStatusRoomAvailabilitySource[keyof typeof IntegrationsStatusRoomAvailabilitySource];


export const IntegrationsStatusRoomAvailabilitySource = {
  app: 'app',
  app_fallback: 'app_fallback',
  exchange: 'exchange',
} as const;

export type IntegrationsStatusRoomAvailability = {
  source: IntegrationsStatusRoomAvailabilitySource;
  /** @nullable */
  banner: string | null;
};

export interface IntegrationsStatus {
  graph: IntegrationsStatusGraph;
  llm: IntegrationsStatusLlm;
  database: IntegrationsStatusDatabase;
  auth: IntegrationsStatusAuth;
  roomAvailability: IntegrationsStatusRoomAvailability;
}

export type GraphConnectionTestResultMode = typeof GraphConnectionTestResultMode[keyof typeof GraphConnectionTestResultMode];


export const GraphConnectionTestResultMode = {
  mock: 'mock',
  live: 'live',
  force_mock: 'force_mock',
  none: 'none',
} as const;

export interface GraphConnectionTestResult {
  ok: boolean;
  mode: GraphConnectionTestResultMode;
  message: string;
  consentSteps: string[];
}

export interface GraphRoom {
  id: string;
  /** @nullable */
  displayName?: string | null;
  /** @nullable */
  emailAddress?: string | null;
  /** @nullable */
  capacity?: number | null;
  /** @nullable */
  building?: string | null;
  /** @nullable */
  floorLabel?: string | null;
  tags?: string[];
}

export interface GraphRoomListResponse {
  items: GraphRoom[];
  total: number;
  matchingHint: string;
}

export interface ChatRequest {
  /**
     * @minLength 1
     * @maxLength 4000
     */
  message: string;
  conversationId?: string;
}

export interface ChatResponse {
  reply: string;
  conversationId: string;
}

export interface AssistantChatRequest {
  /**
     * @minLength 1
     * @maxLength 4000
     */
  message: string;
  conversationId?: string;
  /** Optional book-on-behalf target (requires booking:on_behalf) */
  bookForUserId?: string;
}

export type AssistantProposedActionType = typeof AssistantProposedActionType[keyof typeof AssistantProposedActionType];


export const AssistantProposedActionType = {
  create_booking: 'create_booking',
  create_booking_series: 'create_booking_series',
  cancel_booking: 'cancel_booking',
  set_work_plan: 'set_work_plan',
  set_timed_plan: 'set_timed_plan',
  decide_location_needs: 'decide_location_needs',
  admin_action: 'admin_action',
} as const;

export type AssistantProposedActionPayload = { [key: string]: unknown };

export type InventoryLifecycleAction = typeof InventoryLifecycleAction[keyof typeof InventoryLifecycleAction];


export const InventoryLifecycleAction = {
  deactivate: 'deactivate',
  retire: 'retire',
  reopen: 'reopen',
  remove_schedule: 'remove_schedule',
} as const;

export type InventoryLevel = typeof InventoryLevel[keyof typeof InventoryLevel];


export const InventoryLevel = {
  site: 'site',
  building: 'building',
  floor: 'floor',
  zone: 'zone',
  space: 'space',
} as const;

export interface InventoryLifecycleIdentity {
  id: string;
  level: InventoryLevel;
  name: string;
  code: string;
  path: string[];
}

export type LocationStatus = typeof LocationStatus[keyof typeof LocationStatus];


export const LocationStatus = {
  active: 'active',
  inactive: 'inactive',
  retired: 'retired',
} as const;

export type InventoryLifecycleRestrictionReason = typeof InventoryLifecycleRestrictionReason[keyof typeof InventoryLifecycleRestrictionReason];


export const InventoryLifecycleRestrictionReason = {
  lifecycle: 'lifecycle',
  booking_mode: 'booking_mode',
} as const;

export interface InventoryLifecycleRestriction {
  source: InventoryLifecycleIdentity;
  status: LocationStatus;
  effectiveAt: string;
  inherited: boolean;
  reason?: InventoryLifecycleRestrictionReason;
  /** @nullable */
  transitionId: string | null;
}

export interface InventoryLifecycleState {
  item: InventoryLifecycleIdentity;
  timezone: string;
  /** @minimum 0 */
  revision: number;
  ownStatus: LocationStatus;
  effectiveStatus: LocationStatus;
  restrictions: InventoryLifecycleRestriction[];
  pendingClosures: InventoryLifecycleRestriction[];
}

export type InventoryLifecycleChildEffect = typeof InventoryLifecycleChildEffect[keyof typeof InventoryLifecycleChildEffect];


export const InventoryLifecycleChildEffect = {
  newly_unavailable: 'newly_unavailable',
  already_unavailable: 'already_unavailable',
  restored: 'restored',
  remains_unavailable: 'remains_unavailable',
  unchanged: 'unchanged',
} as const;

/**
 * Complete reviewed booking counts for a space, omitted for parent locations or unavailable booking impact
 */
export type InventoryLifecycleChildBookingImpact = {
  /** @minimum 0 */
  cancellationCount: number;
  /** @minimum 0 */
  underwayCount: number;
};

export interface InventoryLifecycleChild {
  item: InventoryLifecycleIdentity;
  before: InventoryLifecycleState;
  after: InventoryLifecycleState;
  effect: InventoryLifecycleChildEffect;
  /** Complete reviewed booking counts for a space, omitted for parent locations or unavailable booking impact */
  bookingImpact?: InventoryLifecycleChildBookingImpact;
}

export interface InventoryLifecycleChildren {
  items: InventoryLifecycleChild[];
  total: number;
  limit: number;
  offset: number;
}

export type InventoryLifecycleBookingEffect = typeof InventoryLifecycleBookingEffect[keyof typeof InventoryLifecycleBookingEffect];


export const InventoryLifecycleBookingEffect = {
  cancelled_on_confirmation: 'cancelled_on_confirmation',
  underway_preserved: 'underway_preserved',
} as const;

export interface InventoryLifecycleBooking {
  id: string;
  space: InventoryLifecycleIdentity;
  start: string;
  end: string;
  status: string;
  effect: InventoryLifecycleBookingEffect;
}

export interface InventoryLifecycleBookings {
  items: InventoryLifecycleBooking[];
  total: number;
  limit: number;
  offset: number;
}

export type InventoryLifecyclePreviewCounts = {[key: string]: number};

export type InventoryLifecyclePreviewBookingImpact = {
  available: boolean;
  /** @nullable */
  cancellationCount: number | null;
  /** @nullable */
  underwayCount: number | null;
  /** @nullable */
  revision: string | null;
  bookings?: InventoryLifecycleBookings;
};

export interface InventoryLifecyclePreview {
  previewId: string;
  impactRevision: string;
  expiresAt: string;
  action: InventoryLifecycleAction;
  effectiveAt: string;
  timezone: string;
  selectedBefore: InventoryLifecycleState;
  selectedAfter: InventoryLifecycleState;
  counts: InventoryLifecyclePreviewCounts;
  children: InventoryLifecycleChildren;
  bookingImpact: InventoryLifecyclePreviewBookingImpact;
  confirmationAvailable: boolean;
  explanation: string;
}

export interface AssistantProposedAction {
  type: AssistantProposedActionType;
  summary: string;
  payload: AssistantProposedActionPayload;
  confirmationToken: string;
  lifecycleReview?: InventoryLifecyclePreview;
}

export interface AssistantSelectableOption {
  /** Stable option id (usually a space UUID) */
  id: string;
  /** Short button label shown in the chat UI */
  label: string;
  /** Message sent on behalf of the user when the option is clicked */
  message: string;
}

export interface AssistantScreenHint {
  /** Stable screen id (see the guidance registry) */
  id: string;
  label: string;
  description?: string;
  /** Fully resolved in-app path (tenant-prefixed by the server) */
  path: string;
}

export type AssistantInputFieldKind = typeof AssistantInputFieldKind[keyof typeof AssistantInputFieldKind];


export const AssistantInputFieldKind = {
  date: 'date',
  time: 'time',
  duration: 'duration',
  text: 'text',
} as const;

export interface AssistantInputField {
  id: string;
  label: string;
  kind: AssistantInputFieldKind;
}

export interface AssistantChatResponse {
  conversationId: string;
  reply: string;
  proposedActions: AssistantProposedAction[];
  /** Clickable choices derived from tool results (e.g. available desks). The UI renders these as chips under the assistant reply. */
  selectableOptions: AssistantSelectableOption[];
  /** In-app screen links the assistant suggests (e.g. open the Floor Map or Reports screen). The UI renders these as buttons. */
  screenHints?: AssistantScreenHint[];
  /** Optional controls to help the user answer this assistant question. */
  requestedFields?: AssistantInputField[];
}

export interface AssistantConfirmRequest {
  /**
     * @minLength 16
     * @maxLength 256
     */
  overlapAcknowledgement?: string;
  /**
     * @minLength 16
     * @maxLength 128
     */
  confirmationToken: string;
}

export type AssistantConfirmResponseAction = typeof AssistantConfirmResponseAction[keyof typeof AssistantConfirmResponseAction];


export const AssistantConfirmResponseAction = {
  create_booking: 'create_booking',
  create_booking_series: 'create_booking_series',
  cancel_booking: 'cancel_booking',
  create_itinerary: 'create_itinerary',
  set_work_plan: 'set_work_plan',
  set_timed_plan: 'set_timed_plan',
  decide_location_needs: 'decide_location_needs',
  admin_action: 'admin_action',
} as const;

export type AssistantConfirmResponseWorkPlanDaysItemStatus = typeof AssistantConfirmResponseWorkPlanDaysItemStatus[keyof typeof AssistantConfirmResponseWorkPlanDaysItemStatus];


export const AssistantConfirmResponseWorkPlanDaysItemStatus = {
  office: 'office',
  remote: 'remote',
  off: 'off',
  unknown: 'unknown',
} as const;

export type AssistantConfirmResponseWorkPlanDaysItem = {
  date: string;
  status: AssistantConfirmResponseWorkPlanDaysItemStatus;
  /** @nullable */
  buildingId?: string | null;
  /** @nullable */
  buildingName?: string | null;
};

export type AssistantConfirmResponseAdminResultResult = { [key: string]: unknown };

/**
 * Result of a confirmed administration action
 */
export type AssistantConfirmResponseAdminResult = {
  tool?: string;
  summary?: string;
  result?: AssistantConfirmResponseAdminResultResult;
};

export type BookingStatus = typeof BookingStatus[keyof typeof BookingStatus];


export const BookingStatus = {
  confirmed: 'confirmed',
  pending_check_in: 'pending_check_in',
  checked_in: 'checked_in',
  cancelled: 'cancelled',
  auto_released: 'auto_released',
  pending_approval: 'pending_approval',
  pending_sync: 'pending_sync',
} as const;

export type BookingSource = typeof BookingSource[keyof typeof BookingSource];


export const BookingSource = {
  ui: 'ui',
  ai: 'ai',
  import: 'import',
} as const;

/**
 * Calendar outcome for Exchange rooms; null for app-managed resources.
 * @nullable
 */
export type BookingExchangeSyncState = typeof BookingExchangeSyncState[keyof typeof BookingExchangeSyncState] | null;


export const BookingExchangeSyncState = {
  pending: 'pending',
  awaiting_response: 'awaiting_response',
  accepted: 'accepted',
  declined: 'declined',
  failed: 'failed',
  app_fallback: 'app_fallback',
  cancelled: 'cancelled',
  legacy_unverified: 'legacy_unverified',
} as const;

/**
 * @nullable
 */
export type BookingExchangeSyncAction = typeof BookingExchangeSyncAction[keyof typeof BookingExchangeSyncAction] | null;


export const BookingExchangeSyncAction = {
  create: 'create',
  update: 'update',
  cancel: 'cancel',
  poll: 'poll',
} as const;

export interface BookingVehicle {
  /** @nullable */
  vehicleId?: string | null;
  plate: string;
  /** @nullable */
  permitId?: string | null;
  /** @nullable */
  sizeClass?: string | null;
}

export interface Booking {
  id: string;
  spaceId: string;
  /** @nullable */
  layoutId?: string | null;
  /**
     * Immutable selected layout name snapshot.
     * @nullable
     */
  layoutName?: string | null;
  /**
     * Immutable selected layout capacity snapshot.
     * @minimum 1
     * @nullable
     */
  layoutCapacity?: number | null;
  /** @nullable */
  itineraryId?: string | null;
  /** @nullable */
  seriesId?: string | null;
  bookedByUserId: string;
  bookedForUserId: string;
  start: string;
  end: string;
  status: BookingStatus;
  /** @nullable */
  title?: string | null;
  /** @nullable */
  notes?: string | null;
  source: BookingSource;
  checkInRequired: boolean;
  /** @nullable */
  checkInBy?: string | null;
  /** @nullable */
  exchangeEventId?: string | null;
  /**
     * Calendar outcome for Exchange rooms; null for app-managed resources.
     * @nullable
     */
  exchangeSyncState?: BookingExchangeSyncState;
  /** @nullable */
  exchangeSyncAction?: BookingExchangeSyncAction;
  /** @nullable */
  exchangeSyncError?: string | null;
  /** @nullable */
  clientRequestId?: string | null;
  /** @nullable */
  cancelledAt?: string | null;
  createdAt: string;
  updatedAt: string;
  vehicle?: BookingVehicle | null;
}

/**
 * daily_workdays = Mon–Fri; weekly = selected weekdays (0=Sun … 6=Sat).
 */
export type RecurrenceFrequency = typeof RecurrenceFrequency[keyof typeof RecurrenceFrequency];


export const RecurrenceFrequency = {
  daily_workdays: 'daily_workdays',
  weekly: 'weekly',
} as const;

export interface SkippedOccurrence {
  start: string;
  end: string;
  reason: string;
}

export interface BookingSeriesCreateResponse {
  seriesId: string;
  frequency: RecurrenceFrequency;
  /**
     * @nullable
     * @items.minimum 0
     * @items.maximum 6
     */
  weekdays?: number[] | null;
  until: string;
  bookings: Booking[];
  skipped: SkippedOccurrence[];
}

export interface StopRequirements {
  /**
     * @maxLength 64
     * @nullable
     */
  typeCode: string | null;
  /** @maxItems 20 */
  amenityIds: string[];
  /**
     * @minimum 1
     * @maximum 1000
     * @nullable
     */
  minCapacity: number | null;
}

export interface TimedPlanBookingLink {
  id: string;
  bookingId: string;
}

export interface TimedPlanStop {
  id: string;
  /** @minimum 0 */
  position: number;
  siteId: string;
  /** @nullable */
  buildingId: string | null;
  startAt: string;
  endAt: string;
  requirements: StopRequirements;
  bookingLinks: TimedPlanBookingLink[];
}

export type TimedPlanAdjustmentAction = typeof TimedPlanAdjustmentAction[keyof typeof TimedPlanAdjustmentAction];


export const TimedPlanAdjustmentAction = {
  replace: 'replace',
  cancel: 'cancel',
} as const;

export type TimedPlanAdjustmentState = typeof TimedPlanAdjustmentState[keyof typeof TimedPlanAdjustmentState];


export const TimedPlanAdjustmentState = {
  pending: 'pending',
  awaiting_calendar: 'awaiting_calendar',
  succeeded: 'succeeded',
  unresolved: 'unresolved',
} as const;

export interface TimedPlanAdjustment {
  id: string;
  planRevision: number;
  /** @nullable */
  stopId: string | null;
  originalBookingId: string;
  /** @nullable */
  replacementBookingId: string | null;
  action: TimedPlanAdjustmentAction;
  state: TimedPlanAdjustmentState;
  /** @nullable */
  reason: string | null;
  attempts: number;
  /** @nullable */
  nextAttemptAt: string | null;
}

export interface TimedPlan {
  id: string;
  date: string;
  timezone: string;
  revision: number;
  stops: TimedPlanStop[];
  adjustments: TimedPlanAdjustment[];
  updatedAt: string;
}

export type LocationNeedFeatureKind = typeof LocationNeedFeatureKind[keyof typeof LocationNeedFeatureKind];


export const LocationNeedFeatureKind = {
  space_type: 'space_type',
  amenity: 'amenity',
  tag: 'tag',
} as const;

export type LocationNeedStrength = typeof LocationNeedStrength[keyof typeof LocationNeedStrength];


export const LocationNeedStrength = {
  required: 'required',
  preferred: 'preferred',
} as const;

export type LocationNeedDecisionProperty = typeof LocationNeedDecisionProperty[keyof typeof LocationNeedDecisionProperty];


export const LocationNeedDecisionProperty = {
  pending: 'pending',
  approved: 'approved',
  dismissed: 'dismissed',
} as const;

export type LocationNeedSource = typeof LocationNeedSource[keyof typeof LocationNeedSource];


export const LocationNeedSource = {
  manual: 'manual',
  inferred: 'inferred',
} as const;

export interface LocationNeed {
  id: string;
  siteId: string;
  featureKind: LocationNeedFeatureKind;
  featureCode: string;
  strength: LocationNeedStrength;
  decision: LocationNeedDecisionProperty;
  source: LocationNeedSource;
  /** @nullable */
  evidence: string | null;
  updatedAt: string;
}

export interface AssistantConfirmResponse {
  ok: boolean;
  action: AssistantConfirmResponseAction;
  booking?: Booking;
  series?: BookingSeriesCreateResponse;
  /** @nullable */
  cancelledId?: string | null;
  /** Days written when action is set_work_plan */
  workPlanDays?: AssistantConfirmResponseWorkPlanDaysItem[];
  timedPlan?: TimedPlan;
  locationNeeds?: LocationNeed[];
  /** Result of a confirmed administration action */
  adminResult?: AssistantConfirmResponseAdminResult;
}

export interface StoredFile {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
  /** @nullable */
  ownerDisplayName?: string | null;
}

export interface FileList {
  files: StoredFile[];
  /** @minimum 0 */
  total: number;
}

export interface DashboardTopBuilding {
  buildingId: string;
  buildingCode: string;
  buildingName: string;
  siteName: string;
  /** @minimum 0 */
  spaceCount: number;
  /** @minimum 0 */
  bookedMinutes: number;
  /** @minimum 0 */
  availableMinutes: number;
  /**
     * @minimum 0
     * @nullable
     */
  utilisationPct: number | null;
  /** @minimum 0 */
  recordedMinutes: number;
  /** @minimum 0 */
  estimatedMinutes: number;
  /** @minimum 0 */
  missingScheduleSpaceCount: number;
}

export type DashboardBlackoutScopeKind = typeof DashboardBlackoutScopeKind[keyof typeof DashboardBlackoutScopeKind];


export const DashboardBlackoutScopeKind = {
  space: 'space',
  zone: 'zone',
  floor: 'floor',
  building: 'building',
} as const;

export interface DashboardBlackout {
  id: string;
  scopeKind: DashboardBlackoutScopeKind;
  scopeId: string;
  /** @nullable */
  scopeLabel?: string | null;
  /**
     * Site timezone for this disclosed blackout.
     * @nullable
     */
  timezone?: string | null;
  reason: string;
  startAt: string;
  endAt: string;
}

export interface DashboardStats {
  /** @minimum 0 */
  siteCount: number;
  /** @minimum 0 */
  buildingCount: number;
  /** @minimum 0 */
  spaceCount: number;
  /** @minimum 0 */
  todayBookings: number;
  /** @minimum 0 */
  todayCheckedIn: number;
  /**
     * Percent of today's bookings that are checked in (0–100)
     * @minimum 0
     */
  todayCheckInRate: number;
  /**
     * App users with lastLoginAt in the last 7 days
     * @minimum 0
     */
  activeUsersThisWeek: number;
  /**
     * Distinct users with presence status=office today (UTC)
     * @minimum 0
     */
  declaredOfficeToday: number;
  /**
     * Visitors scheduled for today (expected + checked in/out)
     * @minimum 0
     */
  todayVisitorsExpected: number;
  /**
     * Visitors currently checked in
     * @minimum 0
     */
  todayVisitorsOnSite: number;
  topBuildings: DashboardTopBuilding[];
  upcomingBlackouts: DashboardBlackout[];
  generatedAt: string;
}

export interface TimeseriesPoint {
  date: string;
  /**
     * Bookings that started on this UTC day
     * @minimum 0
     */
  bookings: number;
  /**
     * Check-in events on this UTC day
     * @minimum 0
     */
  checkIns: number;
  /**
     * Users declared office on this calendar day
     * @minimum 0
     */
  declaredOffice: number;
}

export interface Timeseries {
  days: number;
  points: TimeseriesPoint[];
}

export interface BookingOverlapWindow {
  spaceId: string;
  spaceName: string;
  spaceTypeId: string;
  spaceTypeName: string;
  start: string;
  end: string;
  timezone: string;
  bookingId?: string;
  /** @minimum 0 */
  proposedIndex?: number;
  status?: string;
}

export interface BookingOverlapDetail {
  proposed: BookingOverlapWindow;
  overlapping: BookingOverlapWindow;
}

export interface BookingOverlapWarning {
  bookedForUserId: string;
  beneficiaryName: string;
  /** @minItems 1 */
  overlaps: BookingOverlapDetail[];
  /**
     * @minLength 16
     * @maxLength 256
     */
  acknowledgement: string;
}

/**
 * BOOKING_OVERLAP_CONFIRMATION_REQUIRED (409) acknowledges only different-space same-type beneficiary overlaps; BOOKING_CONFLICT remains blocking.
 */
export interface Error {
  error: string;
  code?: string;
  overlapWarning?: BookingOverlapWarning;
  policyRequirements?: PolicyAdmissionDetails;
}

export interface NotificationChannelPrefs {
  inApp: boolean;
  email: boolean;
}

export interface NotificationPreferenceMap {
  confirmations: NotificationChannelPrefs;
  reminders: NotificationChannelPrefs;
  check_in: NotificationChannelPrefs;
  cancellations: NotificationChannelPrefs;
  modifications: NotificationChannelPrefs;
  visitors: NotificationChannelPrefs;
}

export type NotificationChannelStatusEmailStatus = typeof NotificationChannelStatusEmailStatus[keyof typeof NotificationChannelStatusEmailStatus];


export const NotificationChannelStatusEmailStatus = {
  ready: 'ready',
  disabled: 'disabled',
} as const;

export type NotificationChannelStatusEmailMode = typeof NotificationChannelStatusEmailMode[keyof typeof NotificationChannelStatusEmailMode];


export const NotificationChannelStatusEmailMode = {
  acs: 'acs',
  graph: 'graph',
  cdf_relay: 'cdf_relay',
  in_app_only: 'in_app_only',
} as const;

export interface NotificationChannelStatus {
  emailEnabled: boolean;
  emailStatus: NotificationChannelStatusEmailStatus;
  emailMode: NotificationChannelStatusEmailMode;
  /** @nullable */
  senderHint?: string | null;
  message: string;
}

/**
 * @nullable
 */
export type NotificationItemPayload = { [key: string]: unknown } | null;

export interface NotificationItem {
  id: string;
  type: string;
  category: string;
  title: string;
  /** @nullable */
  body?: string | null;
  /** @nullable */
  href?: string | null;
  /** @nullable */
  payload?: NotificationItemPayload;
  /** @nullable */
  emailStatus?: string | null;
  /** @nullable */
  readAt?: string | null;
  createdAt: string;
}

export interface NotificationListResponse {
  items: NotificationItem[];
  /** @minimum 0 */
  unreadCount: number;
}

export interface NotificationUnreadCount {
  /** @minimum 0 */
  unreadCount: number;
}

export interface NotificationPreferencesResponse {
  preferences: NotificationPreferenceMap;
  /** @nullable */
  updatedAt?: string | null;
  channels: NotificationChannelStatus;
}

export type UpdateNotificationPreferencesRequestPreferences = {[key: string]: {
  inApp?: boolean;
  email?: boolean;
}};

export interface UpdateNotificationPreferencesRequest {
  preferences?: UpdateNotificationPreferencesRequestPreferences;
}

export interface InventoryLifecycleRequest {
  action: InventoryLifecycleAction;
  /** Explicit UTC offset required; immediate actions use the reviewed instant */
  effectiveAt?: string;
  /**
     * Optional site wall-clock assertion; rejected if offset and timezone disagree, including DST gaps
     * @pattern ^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$
     */
  localEffectiveTime?: string;
  transitionId?: string;
}

export interface InventoryLifecycleConfirmation {
  previewId: string;
  /**
     * @minLength 64
     * @maxLength 64
     */
  impactRevision: string;
  idempotencyKey: string;
}

/**
 * @nullable
 */
export type InventoryLifecycleTransitionCause = typeof InventoryLifecycleTransitionCause[keyof typeof InventoryLifecycleTransitionCause] | null;


export const InventoryLifecycleTransitionCause = {
  floor_plan_pin_removed: 'floor_plan_pin_removed',
  floor_plan_removed: 'floor_plan_removed',
} as const;

export interface InventoryLifecycleTransition {
  id: string;
  status: LocationStatus;
  effectiveAt: string;
  confirmedAt: string;
  /** @nullable */
  removedAt: string | null;
  revision: number;
  hierarchyPath: string[];
  /** @nullable */
  cause?: InventoryLifecycleTransitionCause;
}

export interface InventoryLifecycleHistory {
  items: InventoryLifecycleTransition[];
  total: number;
}

export type SpaceStatus = typeof SpaceStatus[keyof typeof SpaceStatus];


export const SpaceStatus = {
  active: 'active',
  inactive: 'inactive',
  retired: 'retired',
} as const;

export type SpaceMode = typeof SpaceMode[keyof typeof SpaceMode];


export const SpaceMode = {
  reservable: 'reservable',
  drop_in: 'drop_in',
  assigned: 'assigned',
  unavailable: 'unavailable',
} as const;

export type AvailabilitySot = typeof AvailabilitySot[keyof typeof AvailabilitySot];


export const AvailabilitySot = {
  exchange: 'exchange',
  app: 'app',
} as const;

export type PolicyScopeKind = typeof PolicyScopeKind[keyof typeof PolicyScopeKind];


export const PolicyScopeKind = {
  global: 'global',
  space_type: 'space_type',
  site: 'site',
  building: 'building',
  floor: 'floor',
  zone: 'zone',
  space: 'space',
} as const;

export type BlackoutScopeKind = typeof BlackoutScopeKind[keyof typeof BlackoutScopeKind];


export const BlackoutScopeKind = {
  space: 'space',
  zone: 'zone',
  floor: 'floor',
  building: 'building',
} as const;

export interface CredentialType {
  id: string;
  code: string;
  displayName: string;
  /** @nullable */
  description: string | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CredentialTypeList {
  items: CredentialType[];
  total: number;
}

export type UserCredentialStatus = typeof UserCredentialStatus[keyof typeof UserCredentialStatus];


export const UserCredentialStatus = {
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
  revoked: 'revoked',
} as const;

export interface UserCredential {
  id: string;
  userId: string;
  credentialTypeId: string;
  credentialTypeCode: string;
  credentialTypeName: string;
  status: UserCredentialStatus;
  isEffectivelyApproved: boolean;
  referenceNumber: string;
  /** @nullable */
  expiresAt: string | null;
  documentContentType: string;
  documentFileName: string;
  documentByteSize: number;
  /** @nullable */
  reviewerNote: string | null;
  /** @nullable */
  reviewedByUserId: string | null;
  /** @nullable */
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserCredentialList {
  items: UserCredential[];
  total: number;
}

export interface CreateCredentialTypeRequest {
  /**
     * @minLength 1
     * @maxLength 64
     */
  code: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  displayName: string;
  /**
     * @maxLength 2000
     * @nullable
     */
  description?: string | null;
  enabled?: boolean;
}

export interface UpdateCredentialTypeRequest {
  /**
     * @minLength 1
     * @maxLength 200
     */
  displayName?: string;
  /**
     * @maxLength 2000
     * @nullable
     */
  description?: string | null;
  enabled?: boolean;
}

export interface SubmitCredentialRequest {
  credentialTypeId: string;
  /**
     * @minLength 1
     * @maxLength 120
     */
  referenceNumber: string;
  /** @nullable */
  expiresAt?: string | null;
  document: Blob | File;
}

export type ReviewCredentialRequestDecision = typeof ReviewCredentialRequestDecision[keyof typeof ReviewCredentialRequestDecision];


export const ReviewCredentialRequestDecision = {
  approved: 'approved',
  rejected: 'rejected',
} as const;

export interface ReviewCredentialRequest {
  decision: ReviewCredentialRequestDecision;
  /**
     * @maxLength 2000
     * @nullable
     */
  reviewerNote?: string | null;
}

export type BookingRulesOfficeEligibilityAdvanceClassificationsItem = typeof BookingRulesOfficeEligibilityAdvanceClassificationsItem[keyof typeof BookingRulesOfficeEligibilityAdvanceClassificationsItem];


export const BookingRulesOfficeEligibilityAdvanceClassificationsItem = {
  EL2: 'EL2',
  SES: 'SES',
  Conciliator: 'Conciliator',
} as const;

export type BookingRulesOfficeEligibility = {
  /**
     * @minItems 1
     * @maxItems 3
     */
  advanceClassifications: BookingRulesOfficeEligibilityAdvanceClassificationsItem[];
  /**
     * @minimum 1
     * @maximum 168
     */
  generalAccessLeadHours: number;
};

export interface BookingRules {
  /** @minimum 1 */
  minDurationMinutes: number;
  /** @minimum 1 */
  maxDurationMinutes: number;
  /** @minimum 0 */
  advanceWindowDays: number;
  officeEligibility?: BookingRulesOfficeEligibility;
  /** @minimum 0 */
  minLeadMinutes?: number;
  allowRecurrence: boolean;
  /**
     * Max days from the series anchor start to the last occurrence (inclusive). Default 90 when unset.
     * @minimum 1
     * @maximum 365
     */
  maxRecurrenceHorizonDays?: number;
  checkInRequired: boolean;
  /** @nullable */
  autoReleaseGraceMinutes: number | null;
  requireApproval: boolean;
  requireVehicle: boolean;
  requirePermit: boolean;
  /** @minimum 1 */
  maxActiveBookingsPerUser?: number;
  businessDayBooking: boolean;
  /** Credential type ids that the booker must hold as approved and unexpired before a booking is allowed under this policy scope. */
  requiredCredentialTypeIds?: string[];
  /**
     * Application user IDs explicitly allowed to book this space. This field is valid only on a space-scoped booking policy. Empty or omitted means no individual restriction; malformed, duplicate, or over-limit entries are rejected.
     * @maxItems 100
     */
  allowedAppUserIds?: string[];
  /**
     * Stable Entra security-group object IDs allowed to book this space. Membership is checked live at booking time; group names and membership snapshots are not authorization data. This field is valid only on a space-scoped booking policy. Empty or omitted means no group restriction; malformed, duplicate, or over-limit entries are rejected.
     * @maxItems 100
     */
  allowedEntraGroupIds?: string[];
}

export interface OpeningInterval {
  /** @pattern ^([01][0-9]|2[0-3]):[0-5][0-9]$ */
  start: string;
  /** @pattern ^(([01][0-9]|2[0-3]):[0-5][0-9]|24:00)$ */
  end: string;
}

export interface WeeklyOpeningDay {
  /**
     * 0=Sunday in the site timezone
     * @minimum 0
     * @maximum 6
     */
  weekday: number;
  intervals: OpeningInterval[];
}

export interface OpeningException {
  date: string;
  intervals: OpeningInterval[];
}

export interface PutSiteOpeningHours {
  /** @maxItems 7 */
  weekly: WeeklyOpeningDay[];
  /** @maxItems 366 */
  exceptions: OpeningException[];
  /**
     * @minLength 3
     * @maxLength 500
     */
  reason: string;
}

export interface SiteOpeningHours {
  siteId: string;
  timezone: string;
  configured: boolean;
  weekly: WeeklyOpeningDay[];
  exceptions: OpeningException[];
  /** @nullable */
  updatedAt: string | null;
}

/**
 * @nullable
 */
export type SiteMetadata = { [key: string]: unknown } | null;

export interface Site {
  lifecycle?: InventoryLifecycleState;
  id: string;
  code: string;
  name: string;
  timezone: string;
  /** @nullable */
  address?: string | null;
  /**
     * WGS84 latitude
     * @minimum -90
     * @maximum 90
     * @nullable
     */
  latitude?: number | null;
  /**
     * WGS84 longitude
     * @minimum -180
     * @maximum 180
     * @nullable
     */
  longitude?: number | null;
  status: LocationStatus;
  /** Whether the Site map is shown. Defaults to true. */
  siteMapEnabled: boolean;
  /** @nullable */
  metadata?: SiteMetadata;
  createdAt: string;
  updatedAt: string;
}

/**
 * @nullable
 */
export type CreateSiteMetadata = { [key: string]: unknown } | null;

export interface CreateSite {
  /**
     * @minLength 1
     * @maxLength 64
     */
  code: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * IANA timezone for the site; defaults to Australia/Sydney when omitted.
     * @minLength 1
     * @maxLength 64
     */
  timezone?: string;
  /**
     * @maxLength 500
     * @nullable
     */
  address?: string | null;
  /**
     * @minimum -90
     * @maximum 90
     * @nullable
     */
  latitude?: number | null;
  /**
     * @minimum -180
     * @maximum 180
     * @nullable
     */
  longitude?: number | null;
  status?: LocationStatus;
  /** Defaults to true when omitted. */
  siteMapEnabled?: boolean;
  /** @nullable */
  metadata?: CreateSiteMetadata;
}

/**
 * @nullable
 */
export type UpdateSiteMetadata = { [key: string]: unknown } | null;

export interface UpdateSite {
  /**
     * @minLength 1
     * @maxLength 64
     */
  code?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @minLength 1
     * @maxLength 64
     */
  timezone?: string;
  /**
     * @maxLength 500
     * @nullable
     */
  address?: string | null;
  /**
     * @minimum -90
     * @maximum 90
     * @nullable
     */
  latitude?: number | null;
  /**
     * @minimum -180
     * @maximum 180
     * @nullable
     */
  longitude?: number | null;
  status?: LocationStatus;
  siteMapEnabled?: boolean;
  /** @nullable */
  metadata?: UpdateSiteMetadata;
}

export interface SiteListResponse {
  items: Site[];
  /** @minimum 0 */
  total: number;
}

export interface AustralianTimeZoneOption {
  /** IANA zone identifier, for example Australia/Adelaide */
  value: string;
  /** Friendly label, for example Adelaide (South Australia) */
  label: string;
}

export interface BookingOptions {
  /** Always true. Book uses Where, When and What on one screen for every entity. */
  oneScreenBook: boolean;
}

export interface SiteFormOptions {
  /** Hide site latitude/longitude and refuse stored site coordinates. */
  siteCoordinatesDisabled: boolean;
  /** Restrict the timezone dropdown to Australian timezones. */
  australianTimezoneList: boolean;
  /** Tenant default for new sites, or Australia/Sydney when unset or outside the entity's permitted list. */
  defaultSiteTimezone: string;
  /** IANA timezone dropdown choices; restricted to Australian zones when configured. */
  timezones: AustralianTimeZoneOption[];
}

/**
 * @nullable
 */
export type BuildingExternalRef = { [key: string]: unknown } | null;

export interface Building {
  lifecycle?: InventoryLifecycleState;
  id: string;
  siteId: string;
  code: string;
  name: string;
  /**
     * Building pin latitude (preferred for directions)
     * @minimum -90
     * @maximum 90
     * @nullable
     */
  latitude?: number | null;
  /**
     * Building pin longitude
     * @minimum -180
     * @maximum 180
     * @nullable
     */
  longitude?: number | null;
  status: LocationStatus;
  /** @nullable */
  externalRef?: BuildingExternalRef;
  createdAt: string;
  updatedAt: string;
}

/**
 * @nullable
 */
export type CreateBuildingExternalRef = { [key: string]: unknown } | null;

export interface CreateBuilding {
  siteId: string;
  /**
     * @minLength 1
     * @maxLength 64
     */
  code: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @minimum -90
     * @maximum 90
     * @nullable
     */
  latitude?: number | null;
  /**
     * @minimum -180
     * @maximum 180
     * @nullable
     */
  longitude?: number | null;
  status?: LocationStatus;
  /** @nullable */
  externalRef?: CreateBuildingExternalRef;
}

/**
 * @nullable
 */
export type UpdateBuildingExternalRef = { [key: string]: unknown } | null;

export interface UpdateBuilding {
  siteId?: string;
  /**
     * @minLength 1
     * @maxLength 64
     */
  code?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @minimum -90
     * @maximum 90
     * @nullable
     */
  latitude?: number | null;
  /**
     * @minimum -180
     * @maximum 180
     * @nullable
     */
  longitude?: number | null;
  status?: LocationStatus;
  /** @nullable */
  externalRef?: UpdateBuildingExternalRef;
}

export interface BuildingListResponse {
  items: Building[];
  /** @minimum 0 */
  total: number;
}

export interface Floor {
  lifecycle?: InventoryLifecycleState;
  id: string;
  buildingId: string;
  levelIndex: number;
  name: string;
  code: string;
  status: LocationStatus;
  /** @nullable */
  mapAssetUrl?: string | null;
  hasFloorPlan: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFloor {
  buildingId: string;
  levelIndex: number;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @minLength 1
     * @maxLength 64
     */
  code: string;
  status?: LocationStatus;
  /**
     * @maxLength 1000
     * @nullable
     */
  mapAssetUrl?: string | null;
}

export interface UpdateFloor {
  buildingId?: string;
  levelIndex?: number;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @minLength 1
     * @maxLength 64
     */
  code?: string;
  status?: LocationStatus;
  /**
     * @maxLength 1000
     * @nullable
     */
  mapAssetUrl?: string | null;
}

export interface FloorListResponse {
  items: Floor[];
  /** @minimum 0 */
  total: number;
}

export type HarbourImportResultFloorIds = {[key: string]: string};

export type HarbourImportResultWorkAreaIds = {[key: string]: string};

export type HarbourImportResultCounts = {
  /** @minimum 0 */
  floors: number;
  /** @minimum 0 */
  rooms: number;
  /** @minimum 0 */
  workAreas: number;
  /** @minimum 0 */
  desks: number;
};

export type HarbourImportResultMappingsItemKind = typeof HarbourImportResultMappingsItemKind[keyof typeof HarbourImportResultMappingsItemKind];


export const HarbourImportResultMappingsItemKind = {
  room: 'room',
  desk: 'desk',
} as const;

export type HarbourImportResultMappingsItem = {
  sourceId: string;
  spaceId: string;
  floorSourceId: string;
  kind: HarbourImportResultMappingsItemKind;
};

export interface HarbourImportResult {
  siteId: string;
  buildingId: string;
  floorIds: HarbourImportResultFloorIds;
  workAreaIds: HarbourImportResultWorkAreaIds;
  counts: HarbourImportResultCounts;
  /** @minimum 0 */
  created: number;
  /** @minimum 0 */
  skipped: number;
  mappings: HarbourImportResultMappingsItem[];
}

export type HarbourLocationPreviewStatus = typeof HarbourLocationPreviewStatus[keyof typeof HarbourLocationPreviewStatus];


export const HarbourLocationPreviewStatus = {
  missing: 'missing',
  conflict: 'conflict',
  needs_update: 'needs_update',
  current: 'current',
} as const;

export type HarbourLocationPreviewCurrent = {
  /** @nullable */
  address: string | null;
  /** @nullable */
  siteLatitude: number | null;
  /** @nullable */
  siteLongitude: number | null;
  /** @nullable */
  buildingLatitude: number | null;
  /** @nullable */
  buildingLongitude: number | null;
};

export type HarbourLocationPreviewTarget = {
  address: string;
  /**
     * @minimum -90
     * @maximum 90
     */
  latitude: number;
  /**
     * @minimum -180
     * @maximum 180
     */
  longitude: number;
  source: string;
  sourceRecordId: string;
};

export interface HarbourLocationPreview {
  status: HarbourLocationPreviewStatus;
  /** @nullable */
  reason: string | null;
  /** @nullable */
  siteId: string | null;
  /** @nullable */
  buildingId: string | null;
  current: HarbourLocationPreviewCurrent;
  target: HarbourLocationPreviewTarget;
}

export interface HarbourDemoRefreshInput {
  /** Australia/Melbourne date; defaults to today */
  date?: string;
  /** Client-generated idempotency key */
  requestId: string;
}

export type HarbourDemoResultScenariosItemStatus = typeof HarbourDemoResultScenariosItemStatus[keyof typeof HarbourDemoResultScenariosItemStatus];


export const HarbourDemoResultScenariosItemStatus = {
  confirmed: 'confirmed',
  pending_check_in: 'pending_check_in',
  checked_in: 'checked_in',
  cancelled: 'cancelled',
  auto_released: 'auto_released',
  pending_approval: 'pending_approval',
  pending_sync: 'pending_sync',
} as const;

export type HarbourDemoResultScenariosItem = {
  key: string;
  spaceId: string;
  bookingId: string;
  status: HarbourDemoResultScenariosItemStatus;
  start: string;
  end: string;
};

export interface HarbourDemoResult {
  siteId: string;
  /** @nullable */
  date: string | null;
  timezone: string;
  /** @nullable */
  lastRefreshedAt: string | null;
  replayed: boolean;
  scenarios: HarbourDemoResultScenariosItem[];
}

export interface BrisbaneImportResult {
  siteId: string;
  floorId: string;
  /** @minimum 0 */
  created: number;
  /** @minimum 0 */
  skipped: number;
}

export interface GeometryPoint {
  /**
     * @minimum 0
     * @maximum 1000
     */
  x: number;
  /**
     * @minimum 0
     * @maximum 1000
     */
  y: number;
}

export type FloorGeometryFeatureKind = typeof FloorGeometryFeatureKind[keyof typeof FloorGeometryFeatureKind];


export const FloorGeometryFeatureKind = {
  room: 'room',
  chamber: 'chamber',
  office: 'office',
  workstation: 'workstation',
  context: 'context',
  sample: 'sample',
} as const;

export type FloorGeometryFeatureProvenance = typeof FloorGeometryFeatureProvenance[keyof typeof FloorGeometryFeatureProvenance];


export const FloorGeometryFeatureProvenance = {
  existing: 'existing',
  proposed: 'proposed',
  ambiguous: 'ambiguous',
  sample: 'sample',
  context: 'context',
} as const;

export type FloorGeometryFeatureConfidence = typeof FloorGeometryFeatureConfidence[keyof typeof FloorGeometryFeatureConfidence];


export const FloorGeometryFeatureConfidence = {
  visual: 'visual',
  approximate: 'approximate',
  unlocated: 'unlocated',
} as const;

export interface FloorGeometryFeature {
  /**
     * @minLength 1
     * @maxLength 64
     */
  code: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  label: string;
  kind: FloorGeometryFeatureKind;
  provenance: FloorGeometryFeatureProvenance;
  confidence: FloorGeometryFeatureConfidence;
  /** @nullable */
  spaceId?: string | null;
  /**
     * @minItems 3
     * @maxItems 24
     */
  polygon?: GeometryPoint[];
  pin?: GeometryPoint | null;
  /**
     * @minimum 0
     * @maximum 10
     */
  heightMeters: number;
}

export type FloorGeometryDocumentSchemaVersion = typeof FloorGeometryDocumentSchemaVersion[keyof typeof FloorGeometryDocumentSchemaVersion];


export const FloorGeometryDocumentSchemaVersion = {
  NUMBER_1: 1,
} as const;

export interface FloorGeometryDocument {
  schemaVersion: FloorGeometryDocumentSchemaVersion;
  /**
     * @minLength 1
     * @maxLength 255
     */
  sourceName: string;
  sourceDate: string;
  /**
     * @minimum 1
     * @maximum 20000
     */
  imageWidth: number;
  /**
     * @minimum 1
     * @maximum 20000
     */
  imageHeight: number;
  illustrative: boolean;
  /** @maxItems 500 */
  features: FloorGeometryFeature[];
}

export interface FloorGeometry {
  floorId: string;
  /** @minimum 1 */
  revision: number;
  document: FloorGeometryDocument;
  updatedAt: string;
}

export interface UpdateFloorGeometry {
  /** @minimum 0 */
  expectedRevision: number;
  document: FloorGeometryDocument;
}

export type TwinAppStatusState = typeof TwinAppStatusState[keyof typeof TwinAppStatusState];


export const TwinAppStatusState = {
  busy: 'busy',
  free: 'free',
  unavailable: 'unavailable',
  unknown: 'unknown',
} as const;

export type TwinAppStatusSource = typeof TwinAppStatusSource[keyof typeof TwinAppStatusSource];


export const TwinAppStatusSource = {
  app: 'app',
} as const;

export type TwinAppStatusFreshness = typeof TwinAppStatusFreshness[keyof typeof TwinAppStatusFreshness];


export const TwinAppStatusFreshness = {
  fresh: 'fresh',
  stale: 'stale',
  unavailable: 'unavailable',
} as const;

export interface TwinAppStatus {
  state: TwinAppStatusState;
  /** @minimum 0 */
  activeBookingCount: number;
  /** @minimum 0 */
  checkedInCount: number;
  source: TwinAppStatusSource;
  /** @nullable */
  observedAt: string | null;
  freshness: TwinAppStatusFreshness;
  /** @nullable */
  errorCode: string | null;
}

export type TwinCalendarStatusState = typeof TwinCalendarStatusState[keyof typeof TwinCalendarStatusState];


export const TwinCalendarStatusState = {
  busy: 'busy',
  free: 'free',
  unknown: 'unknown',
  not_applicable: 'not_applicable',
} as const;

export type TwinCalendarStatusSource = typeof TwinCalendarStatusSource[keyof typeof TwinCalendarStatusSource];


export const TwinCalendarStatusSource = {
  microsoft_365: 'microsoft_365',
  none: 'none',
} as const;

export type TwinCalendarStatusFreshness = typeof TwinCalendarStatusFreshness[keyof typeof TwinCalendarStatusFreshness];


export const TwinCalendarStatusFreshness = {
  fresh: 'fresh',
  stale: 'stale',
  unavailable: 'unavailable',
} as const;

export interface TwinCalendarStatus {
  state: TwinCalendarStatusState;
  source: TwinCalendarStatusSource;
  /**
     * Administrator-linked room mailbox
     * @nullable
     */
  mailboxUpn: string | null;
  /** @nullable */
  observedAt: string | null;
  freshness: TwinCalendarStatusFreshness;
  /** @nullable */
  errorCode: string | null;
}

export type TwinOccupancyStatusState = typeof TwinOccupancyStatusState[keyof typeof TwinOccupancyStatusState];


export const TwinOccupancyStatusState = {
  occupied: 'occupied',
  vacant: 'vacant',
  unknown: 'unknown',
} as const;

export type TwinOccupancyStatusSource = typeof TwinOccupancyStatusSource[keyof typeof TwinOccupancyStatusSource];


export const TwinOccupancyStatusSource = {
  simulated_sensor: 'simulated_sensor',
} as const;

export type TwinOccupancyStatusFreshness = typeof TwinOccupancyStatusFreshness[keyof typeof TwinOccupancyStatusFreshness];


export const TwinOccupancyStatusFreshness = {
  fresh: 'fresh',
  stale: 'stale',
  offline: 'offline',
} as const;

export interface TwinOccupancyStatus {
  state: TwinOccupancyStatusState;
  source: TwinOccupancyStatusSource;
  simulated: true;
  observedAt: string;
  /** Current instant, independent of the booking window */
  appliesAt: string;
  freshness: TwinOccupancyStatusFreshness;
  /** @nullable */
  errorCode: string | null;
}

/**
 * Overlay summary only; booking policy and write-time conflict checks remain authoritative.
 */
export type TwinSpaceStatusDisplayStatus = typeof TwinSpaceStatusDisplayStatus[keyof typeof TwinSpaceStatusDisplayStatus];


export const TwinSpaceStatusDisplayStatus = {
  busy: 'busy',
  unavailable: 'unavailable',
  unknown: 'unknown',
  no_known_conflict: 'no_known_conflict',
} as const;

export interface TwinSpaceStatus {
  spaceId: string;
  app: TwinAppStatus;
  calendar: TwinCalendarStatus;
  occupancy: TwinOccupancyStatus;
  /** Overlay summary only; booking policy and write-time conflict checks remain authoritative. */
  displayStatus: TwinSpaceStatusDisplayStatus;
}

export interface FloorTwinStatusResponse {
  floorId: string;
  start: string;
  end: string;
  generatedAt: string;
  items: TwinSpaceStatus[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
}

export type SetFloorTwinSensorScenariosAssignmentsItemScenario = typeof SetFloorTwinSensorScenariosAssignmentsItemScenario[keyof typeof SetFloorTwinSensorScenariosAssignmentsItemScenario];


export const SetFloorTwinSensorScenariosAssignmentsItemScenario = {
  occupied: 'occupied',
  vacant: 'vacant',
  stale: 'stale',
  offline: 'offline',
} as const;

export type SetFloorTwinSensorScenariosAssignmentsItem = {
  spaceId: string;
  scenario: SetFloorTwinSensorScenariosAssignmentsItemScenario;
};

export interface SetFloorTwinSensorScenarios {
  /** @maxItems 100 */
  assignments: SetFloorTwinSensorScenariosAssignmentsItem[];
}

export interface FloorTwinSensorScenarioResult {
  floorId: string;
  /**
     * @minimum 0
     * @maximum 100
     */
  assignedCount: number;
  simulated: true;
}

export interface FloorPlanMeta {
  floorId: string;
  contentType: string;
  /** @minimum 0 */
  byteSize: number;
  /** @minimum 1 */
  width: number;
  /** @minimum 1 */
  height: number;
  /** @nullable */
  fileName: string | null;
  updatedAt: string;
  hasPlan: true;
}

export interface Zone {
  lifecycle?: InventoryLifecycleState;
  id: string;
  floorId: string;
  code: string;
  name: string;
  /** @nullable */
  zoneKind?: string | null;
  teamAffinityTags: string[];
  status: LocationStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CreateZone {
  floorId: string;
  /**
     * @minLength 1
     * @maxLength 64
     */
  code: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @maxLength 64
     * @nullable
     */
  zoneKind?: string | null;
  teamAffinityTags?: string[];
  status?: LocationStatus;
}

export interface UpdateZone {
  floorId?: string;
  /**
     * @minLength 1
     * @maxLength 64
     */
  code?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @maxLength 64
     * @nullable
     */
  zoneKind?: string | null;
  teamAffinityTags?: string[];
  status?: LocationStatus;
}

export interface ZoneListResponse {
  items: Zone[];
  /** @minimum 0 */
  total: number;
}

export type StaffedServiceTeamListItemsItem = {
  id: string;
  name: string;
};

export interface StaffedServiceTeamList {
  items: StaffedServiceTeamListItemsItem[];
  /** @minimum 0 */
  total: number;
}

export interface StaffedService {
  id: string;
  name: string;
  /** @nullable */
  description?: string | null;
  siteId: string;
  responsibleWorkgroupId: string;
  active: boolean;
  createdAt: string;
}

export interface StaffedServiceInput {
  /**
     * @minLength 1
     * @maxLength 120
     */
  name: string;
  /**
     * @maxLength 1000
     * @nullable
     */
  description?: string | null;
  siteId: string;
  responsibleWorkgroupId: string;
}

export interface StaffedServicePatch {
  /**
     * @minLength 1
     * @maxLength 120
     */
  name?: string;
  /**
     * @maxLength 1000
     * @nullable
     */
  description?: string | null;
  responsibleWorkgroupId?: string;
  active?: boolean;
}

export interface StaffedServiceList {
  items: StaffedService[];
  /** @minimum 0 */
  total: number;
}

export type StaffedServiceRequestStatus = typeof StaffedServiceRequestStatus[keyof typeof StaffedServiceRequestStatus];


export const StaffedServiceRequestStatus = {
  requested: 'requested',
  confirmed: 'confirmed',
  declined: 'declined',
  cancelled: 'cancelled',
  completed: 'completed',
} as const;

export interface StaffedServiceRequest {
  id: string;
  serviceId: string;
  serviceName: string;
  siteId: string;
  responsibleWorkgroupId: string;
  requesterUserId: string;
  start: string;
  end: string;
  location: string;
  details: string;
  status: StaffedServiceRequestStatus;
  /** @nullable */
  assignedUserId: string | null;
  /** @nullable */
  declineReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface StaffedServiceRequestInput {
  serviceId: string;
  /** Must be on a 15-minute mark. */
  start: string;
  /** Must be on a 15-minute mark. */
  end: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  location: string;
  /**
     * @minLength 1
     * @maxLength 2000
     */
  details: string;
  clientRequestId: string;
}

export interface StaffedServiceRequestList {
  items: StaffedServiceRequest[];
  /** @minimum 0 */
  total: number;
}

export type StaffedServiceTransitionAction = typeof StaffedServiceTransitionAction[keyof typeof StaffedServiceTransitionAction];


export const StaffedServiceTransitionAction = {
  assign: 'assign',
  confirm: 'confirm',
  decline: 'decline',
  cancel: 'cancel',
  complete: 'complete',
} as const;

export interface StaffedServiceTransition {
  action: StaffedServiceTransitionAction;
  assignedUserId?: string;
  /**
     * @minLength 1
     * @maxLength 1000
     */
  reason?: string;
}

/**
 * @nullable
 */
export type SpaceTypeUiSchema = { [key: string]: unknown } | null;

export interface SpaceType {
  id: string;
  code: string;
  displayName: string;
  /** @nullable */
  description?: string | null;
  sotStrategy: AvailabilitySot;
  defaultRules: BookingRules;
  /** @nullable */
  uiSchema?: SpaceTypeUiSchema;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSpaceType {
  /**
     * @minLength 1
     * @maxLength 64
     */
  code: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  displayName: string;
  /**
     * @maxLength 2000
     * @nullable
     */
  description?: string | null;
  sotStrategy?: AvailabilitySot;
  defaultRules?: BookingRules;
}

export type UpdateSpaceTypeDefaultRules = { [key: string]: unknown };

/**
 * @nullable
 */
export type UpdateSpaceTypeUiSchema = { [key: string]: unknown } | null;

export interface UpdateSpaceType {
  /**
     * @minLength 1
     * @maxLength 200
     */
  displayName?: string;
  /**
     * @maxLength 2000
     * @nullable
     */
  description?: string | null;
  sotStrategy?: AvailabilitySot;
  defaultRules?: UpdateSpaceTypeDefaultRules;
  /** @nullable */
  uiSchema?: UpdateSpaceTypeUiSchema;
}

export interface SpaceTypeListResponse {
  items: SpaceType[];
  /** @minimum 0 */
  total: number;
}

export interface Amenity {
  id: string;
  code: string;
  displayName: string;
  /** @nullable */
  category?: string | null;
  createdAt: string;
}

export interface CreateAmenity {
  /**
     * @minLength 1
     * @maxLength 64
     */
  code: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  displayName: string;
  /**
     * @maxLength 64
     * @nullable
     */
  category?: string | null;
}

export interface UpdateAmenity {
  /**
     * @minLength 1
     * @maxLength 200
     */
  displayName?: string;
  /**
     * @maxLength 64
     * @nullable
     */
  category?: string | null;
}

export interface AmenityListResponse {
  items: Amenity[];
  /** @minimum 0 */
  total: number;
}

export interface SpaceLayout {
  id: string;
  spaceId: string;
  name: string;
  /** @minimum 1 */
  capacity: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSpaceLayout {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @minimum 1
     * @maximum 2147483647
     */
  capacity: number;
  active?: boolean;
}

export interface UpdateSpaceLayout {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @minimum 1
     * @maximum 2147483647
     */
  capacity?: number;
  active?: boolean;
}

export interface SpaceLayoutListResponse {
  items: SpaceLayout[];
  /** @minimum 0 */
  total: number;
}

/**
 * @nullable
 */
export type SpaceAttributes = { [key: string]: unknown } | null;

/**
 * @nullable
 */
export type SpaceExternalRef = { [key: string]: unknown } | null;

export interface Space {
  /** True if this physical space has any layout records, including retired layouts. */
  layoutRequired?: boolean;
  layouts?: SpaceLayout[];
  lifecycle?: InventoryLifecycleState;
  id: string;
  zoneId: string;
  spaceTypeId: string;
  code: string;
  name: string;
  /** @minimum 1 */
  capacity: number;
  mode: SpaceMode;
  status: SpaceStatus;
  /** @nullable */
  attributes?: SpaceAttributes;
  tags: string[];
  /**
     * Floor-plan marker X (0–1000 normalized; schematic grid values remain small integers)
     * @minimum 0
     * @maximum 1000
     * @nullable
     */
  mapX?: number | null;
  /**
     * Floor-plan marker Y (0–1000 normalized; schematic grid values remain small integers)
     * @minimum 0
     * @maximum 1000
     * @nullable
     */
  mapY?: number | null;
  /** Align this space to nearby placed spaces of the same type (desks and hot desks share one group) when editing its position */
  mapSnapEnabled?: boolean;
  sotOverride?: AvailabilitySot | null;
  /** @nullable */
  externalRef?: SpaceExternalRef;
  /** @nullable */
  assignedUserId?: string | null;
  /** @nullable */
  imageFileId?: string | null;
  /** @nullable */
  retiredAt?: string | null;
  amenityIds?: string[];
  createdAt: string;
  updatedAt: string;
}

/**
 * @nullable
 */
export type CreateSpaceAttributes = { [key: string]: unknown } | null;

/**
 * @nullable
 */
export type CreateSpaceExternalRef = { [key: string]: unknown } | null;

export interface CreateSpace {
  zoneId: string;
  spaceTypeId: string;
  /**
     * @minLength 1
     * @maxLength 64
     */
  code: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /** @minimum 1 */
  capacity?: number;
  mode?: SpaceMode;
  status?: SpaceStatus;
  /** @nullable */
  attributes?: CreateSpaceAttributes;
  tags?: string[];
  /**
     * @minimum 0
     * @maximum 1000
     * @nullable
     */
  mapX?: number | null;
  /**
     * @minimum 0
     * @maximum 1000
     * @nullable
     */
  mapY?: number | null;
  /** Align this space to nearby placed spaces of the same type (desks and hot desks share one group) when editing its position */
  mapSnapEnabled?: boolean;
  sotOverride?: AvailabilitySot | null;
  /** @nullable */
  externalRef?: CreateSpaceExternalRef;
  amenityIds?: string[];
}

/**
 * @nullable
 */
export type UpdateSpaceAttributes = { [key: string]: unknown } | null;

/**
 * @nullable
 */
export type UpdateSpaceExternalRef = { [key: string]: unknown } | null;

export interface UpdateSpace {
  zoneId?: string;
  spaceTypeId?: string;
  /**
     * @minLength 1
     * @maxLength 64
     */
  code?: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /** @minimum 1 */
  capacity?: number;
  mode?: SpaceMode;
  /** @nullable */
  assignedUserId?: string | null;
  status?: SpaceStatus;
  /** @nullable */
  attributes?: UpdateSpaceAttributes;
  tags?: string[];
  /**
     * @minimum 0
     * @maximum 1000
     * @nullable
     */
  mapX?: number | null;
  /**
     * @minimum 0
     * @maximum 1000
     * @nullable
     */
  mapY?: number | null;
  /** Align this space to nearby placed spaces of the same type (desks and hot desks share one group) when editing its position */
  mapSnapEnabled?: boolean;
  sotOverride?: AvailabilitySot | null;
  /** @nullable */
  externalRef?: UpdateSpaceExternalRef;
  amenityIds?: string[];
}

export interface SpaceListResponse {
  items: Space[];
  /** @minimum 0 */
  total: number;
}

export interface BookingPolicy {
  id: string;
  name: string;
  scopeKind: PolicyScopeKind;
  /** @nullable */
  scopeId?: string | null;
  /** @nullable */
  spaceTypeId?: string | null;
  /**
     * When set, the policy applies only if the space carries every listed tag (case-insensitive AND match). Empty/null means no tag constraint.
     * @maxItems 20
     * @nullable
     * @items.minLength 1
     * @items.maxLength 64
     */
  tagFilter?: string[] | null;
  priority: number;
  rules: BookingRules;
  enabled: boolean;
  /** @nullable */
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBookingPolicy {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  scopeKind: PolicyScopeKind;
  /** @nullable */
  scopeId?: string | null;
  /** @nullable */
  spaceTypeId?: string | null;
  /**
     * @maxItems 20
     * @nullable
     * @items.minLength 1
     * @items.maxLength 64
     */
  tagFilter?: string[] | null;
  priority?: number;
  rules: BookingRules;
  enabled?: boolean;
}

export interface UpdateBookingPolicy {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  scopeKind?: PolicyScopeKind;
  /** @nullable */
  scopeId?: string | null;
  /** @nullable */
  spaceTypeId?: string | null;
  /**
     * @maxItems 20
     * @nullable
     * @items.minLength 1
     * @items.maxLength 64
     */
  tagFilter?: string[] | null;
  priority?: number;
  rules?: BookingRules;
  enabled?: boolean;
}

export interface BookingPolicyListResponse {
  items: BookingPolicy[];
  /** @minimum 0 */
  total: number;
}

export interface Blackout {
  id: string;
  /**
     * Site IANA zone for this authorized blackout scope.
     * @nullable
     */
  timezone?: string | null;
  scopeKind: BlackoutScopeKind;
  scopeId: string;
  startAt: string;
  endAt: string;
  reason: string;
  /** @nullable */
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBlackout {
  scopeKind: BlackoutScopeKind;
  scopeId: string;
  startAt: string;
  endAt: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  reason: string;
}

export interface UpdateBlackout {
  scopeKind?: BlackoutScopeKind;
  scopeId?: string;
  startAt?: string;
  endAt?: string;
  /**
     * @minLength 1
     * @maxLength 500
     */
  reason?: string;
}

export interface BlackoutListResponse {
  items: Blackout[];
  /** @minimum 0 */
  total: number;
}

export interface EffectivePolicy {
  spaceId: string;
  /** @nullable */
  policyId?: string | null;
  /** @nullable */
  policyName?: string | null;
  scopeKind: string;
  rules: BookingRules;
}

export interface EvaluatePolicyRequest {
  spaceId: string;
  start: string;
  end: string;
  hasVehicle?: boolean;
  hasPermit?: boolean;
  now?: string;
}

export interface EvaluatePolicyResponse {
  allowed: boolean;
  spaceId: string;
  /** @nullable */
  policyId?: string | null;
  /** @nullable */
  policyName?: string | null;
  scopeKind: string;
  rules: BookingRules;
}

export interface BookingListResponse {
  items: Booking[];
  /**
     * Total matching bookings in the entity for the current filters.
     * @minimum 0
     */
  total: number;
}

export interface VehicleInput {
  vehicleId?: string;
  /**
     * @minLength 1
     * @maxLength 32
     */
  plate?: string;
  /**
     * @maxLength 100
     * @nullable
     */
  permitId?: string | null;
  /**
     * @maxLength 40
     * @nullable
     */
  sizeClass?: string | null;
  isEv?: boolean;
  /**
     * @maxLength 100
     * @nullable
     */
  label?: string | null;
}

export interface CreateBookingRequest {
  /**
     * @minLength 16
     * @maxLength 256
     */
  overlapAcknowledgement?: string;
  spaceId: string;
  layoutId?: string;
  start: string;
  end: string;
  /**
     * @maxLength 200
     * @nullable
     */
  title?: string | null;
  /**
     * @maxLength 2000
     * @nullable
     */
  notes?: string | null;
  bookedForUserId?: string;
  /** @nullable */
  itineraryId?: string | null;
  vehicle?: VehicleInput | null;
  /**
     * @maxLength 100
     * @nullable
     */
  clientRequestId?: string | null;
  source?: BookingSource;
}

export interface BookingRecurrence {
  frequency: RecurrenceFrequency;
  /**
     * Required when frequency is weekly. Site-local weekday 0=Sun … 6=Sat (resolved via the space's site IANA timezone, not the client's browser-local getDay).
     * @maxItems 7
     * @nullable
     * @items.minimum 0
     * @items.maximum 6
     */
  weekdays?: number[] | null;
  /** Inclusive last candidate start for the series. */
  until: string;
}

export interface CreateBookingSeriesRequest {
  /**
     * @minLength 16
     * @maxLength 256
     */
  overlapAcknowledgement?: string;
  spaceId: string;
  layoutId?: string;
  start: string;
  end: string;
  /**
     * @maxLength 200
     * @nullable
     */
  title?: string | null;
  /**
     * @maxLength 2000
     * @nullable
     */
  notes?: string | null;
  bookedForUserId?: string;
  vehicle?: VehicleInput | null;
  /**
     * @maxLength 100
     * @nullable
     */
  clientRequestId?: string | null;
  source?: BookingSource;
  recurrence: BookingRecurrence;
}

export type BookingSeriesPreviewResponsePlannedItem = {
  start: string;
  end: string;
};

export interface BookingSeriesPreviewResponse {
  frequency: RecurrenceFrequency;
  /**
     * @nullable
     * @items.minimum 0
     * @items.maximum 6
     */
  weekdays?: number[] | null;
  until: string;
  planned: BookingSeriesPreviewResponsePlannedItem[];
  skipped: SkippedOccurrence[];
  /** @minimum 1 */
  horizonDays: number;
}

/**
 * one = cancel this occurrence only; series = cancel this and future active occurrences in the same series.
 */
export type CancelBookingRequestScope = typeof CancelBookingRequestScope[keyof typeof CancelBookingRequestScope];


export const CancelBookingRequestScope = {
  one: 'one',
  series: 'series',
} as const;

export interface CancelBookingRequest {
  /** one = cancel this occurrence only; series = cancel this and future active occurrences in the same series. */
  scope?: CancelBookingRequestScope;
}

export type CheckInBookingRequestMethod = typeof CheckInBookingRequestMethod[keyof typeof CheckInBookingRequestMethod];


export const CheckInBookingRequestMethod = {
  web: 'web',
  qr: 'qr',
  admin: 'admin',
} as const;

export interface CheckInBookingRequest {
  method?: CheckInBookingRequestMethod;
  /**
     * Every configured arrival check must be acknowledged for each check-in.
     * @maxItems 50
     * @items.minLength 1
     * @items.maxLength 64
     */
  arrivalItemIds?: string[];
}

export type CheckOutBookingRequestMethod = typeof CheckOutBookingRequestMethod[keyof typeof CheckOutBookingRequestMethod];


export const CheckOutBookingRequestMethod = {
  web: 'web',
  qr: 'qr',
  admin: 'admin',
} as const;

export interface CheckOutBookingRequest {
  method?: CheckOutBookingRequestMethod;
}

/**
 * @nullable
 */
export type AuditEventBefore = { [key: string]: unknown } | null;

/**
 * @nullable
 */
export type AuditEventAfter = { [key: string]: unknown } | null;

export interface AuditEvent {
  id: string;
  at: string;
  /** @nullable */
  actorUserId?: string | null;
  /** @nullable */
  actorName?: string | null;
  action: string;
  entityType: string;
  entityId: string;
  summary: string;
  /** @nullable */
  before?: AuditEventBefore;
  /** @nullable */
  after?: AuditEventAfter;
  /** @nullable */
  requestId?: string | null;
  /** @nullable */
  correlationId?: string | null;
}

export interface AuditEventListResponse {
  items: AuditEvent[];
  total: number;
}

export interface DirectoryUser {
  id: string;
  /** @nullable */
  displayName?: string | null;
  email: string;
  /** @nullable */
  department?: string | null;
}

export interface DirectoryUserListResponse {
  items: DirectoryUser[];
  total: number;
}

export interface AutoReleaseResult {
  /** @minimum 0 */
  released: number;
  bookingIds: string[];
}

/**
 * forward applies title/notes/time delta to this occurrence and all later active occurrences in the same series.
 */
export type UpdateBookingRequestSeriesScope = typeof UpdateBookingRequestSeriesScope[keyof typeof UpdateBookingRequestSeriesScope];


export const UpdateBookingRequestSeriesScope = {
  one: 'one',
  forward: 'forward',
} as const;

export interface UpdateBookingRequest {
  layoutId?: string;
  start?: string;
  end?: string;
  /**
     * @maxLength 200
     * @nullable
     */
  title?: string | null;
  /**
     * @maxLength 2000
     * @nullable
     */
  notes?: string | null;
  /** forward applies title/notes/time delta to this occurrence and all later active occurrences in the same series. */
  seriesScope?: UpdateBookingRequestSeriesScope;
}

export type CreateItineraryRequestLegsItem = {
  spaceId: string;
  layoutId?: string;
  start: string;
  end: string;
  /**
     * @maxLength 200
     * @nullable
     */
  title?: string | null;
  vehicle?: VehicleInput | null;
};

export interface CreateItineraryRequest {
  /**
     * @minLength 16
     * @maxLength 256
     */
  overlapAcknowledgement?: string;
  /**
     * @maxLength 200
     * @nullable
     */
  label?: string | null;
  /** @nullable */
  dayDate?: string | null;
  /**
     * @maxLength 100
     * @nullable
     */
  clientRequestId?: string | null;
  source?: BookingSource;
  /**
     * @minItems 1
     * @maxItems 20
     */
  legs: CreateItineraryRequestLegsItem[];
}

export interface Itinerary {
  id: string;
  ownerUserId: string;
  /** @nullable */
  label?: string | null;
  /** @nullable */
  dayDate?: string | null;
  createdAt: string;
  bookings: Booking[];
}

export interface Vehicle {
  id: string;
  userId: string;
  plate: string;
  /** @nullable */
  permitId?: string | null;
  /** @nullable */
  sizeClass?: string | null;
  isEv: boolean;
  /** @nullable */
  label?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateVehicleRequest {
  /**
     * @minLength 1
     * @maxLength 32
     */
  plate: string;
  /**
     * @maxLength 100
     * @nullable
     */
  permitId?: string | null;
  /**
     * @maxLength 40
     * @nullable
     */
  sizeClass?: string | null;
  isEv?: boolean;
  /**
     * @maxLength 100
     * @nullable
     */
  label?: string | null;
}

export interface VehicleListResponse {
  items: Vehicle[];
  /** @minimum 0 */
  total: number;
}

export interface AvailabilitySpace {
  id: string;
  code: string;
  name: string;
  capacity: number;
  layoutRequired?: boolean;
  layouts?: SpaceLayout[];
  mode: string;
  zoneId: string;
  zoneName: string;
  floorId: string;
  floorName: string;
  buildingId: string;
  buildingName: string;
  siteId: string;
  siteName: string;
  spaceTypeId: string;
  spaceTypeCode: string;
  spaceTypeName: string;
  availabilitySot: string;
  tags: string[];
  accessibility: boolean;
  /**
     * Floor-plan grid column (0-based)
     * @nullable
     */
  mapX?: number | null;
  /**
     * Floor-plan grid row (0-based)
     * @nullable
     */
  mapY?: number | null;
}

/**
 * Active booking that makes a space unavailable for the search window.
 * Used by the floor map to enable walk-up takeover after missed check-in.
 */
export interface AvailabilityBlockingBooking {
  id: string;
  status: BookingStatus;
  checkInRequired: boolean;
  /** @nullable */
  checkInBy?: string | null;
  /** Original booking end (takeover books until this instant) */
  end?: string;
  /** True only when the signed-in viewer owns the blocking booking */
  isOwnBooking?: boolean;
}

export type AvailabilityResultReasonsItem = typeof AvailabilityResultReasonsItem[keyof typeof AvailabilityResultReasonsItem];


export const AvailabilityResultReasonsItem = {
  AVAILABLE: 'AVAILABLE',
  CONFLICT: 'CONFLICT',
  POLICY_VIOLATION: 'POLICY_VIOLATION',
  BLACKOUT: 'BLACKOUT',
  UNAVAILABLE_MODE: 'UNAVAILABLE_MODE',
  RETIRED: 'RETIRED',
  TYPE_FILTER: 'TYPE_FILTER',
} as const;

export type AvailabilityResultAvailabilitySource = typeof AvailabilityResultAvailabilitySource[keyof typeof AvailabilityResultAvailabilitySource];


export const AvailabilityResultAvailabilitySource = {
  app: 'app',
  app_fallback: 'app_fallback',
  exchange: 'exchange',
} as const;

export interface AvailabilityResult {
  space: AvailabilitySpace;
  available: boolean;
  reasons: AvailabilityResultReasonsItem[];
  /** @nullable */
  reasonDetail?: string | null;
  policyScopeKind: string;
  availabilitySource: AvailabilityResultAvailabilitySource;
  /** Present when an app booking makes the space unavailable */
  blockingBooking?: AvailabilityBlockingBooking | null;
}

/**
 * Exchange/Graph readiness for meeting-room SoT (FR-EU-017)
 */
export type AvailabilitySearchResponseGraphStatus = typeof AvailabilitySearchResponseGraphStatus[keyof typeof AvailabilitySearchResponseGraphStatus];


export const AvailabilitySearchResponseGraphStatus = {
  connected: 'connected',
  disconnected: 'disconnected',
  degraded: 'degraded',
} as const;

export interface AvailabilitySearchResponse {
  items: AvailabilityResult[];
  /** @minimum 0 */
  total: number;
  start: string;
  end: string;
  /** Exchange/Graph readiness for meeting-room SoT (FR-EU-017) */
  graphStatus: AvailabilitySearchResponseGraphStatus;
  /**
     * User-facing degradation copy when rooms are on app_fallback
     * @nullable
     */
  graphBanner?: string | null;
}

export interface UtilisationReportBucket {
  siteId: string;
  siteCode: string;
  siteName: string;
  buildingId: string;
  buildingCode: string;
  buildingName: string;
  floorId: string;
  floorName: string;
  typeCode: string;
  typeName: string;
  /** @minimum 0 */
  spaceCount: number;
  /** @minimum 0 */
  capacityUnits: number;
  /** @minimum 0 */
  bookedMinutes: number;
  /** @minimum 0 */
  availableMinutes: number;
  /**
     * @minimum 0
     * @nullable
     */
  utilisationPct: number | null;
  /** @minimum 0 */
  checkedInMinutes: number;
  /** @minimum 0 */
  checkedInPct: number;
  /** @minimum 0 */
  recordedMinutes: number;
  /** @minimum 0 */
  estimatedMinutes: number;
  /** @minimum 0 */
  missingScheduleSpaceCount: number;
  /** @minimum 0 */
  closedSpaceCount: number;
}

/**
 * @nullable
 */
export type UtilisationReportFiltersGroupBy = typeof UtilisationReportFiltersGroupBy[keyof typeof UtilisationReportFiltersGroupBy] | null;


export const UtilisationReportFiltersGroupBy = {
  site: 'site',
  building: 'building',
  floor: 'floor',
  type: 'type',
} as const;

export type UtilisationReportFilters = {
  /** @nullable */
  siteId: string | null;
  /** @nullable */
  buildingId: string | null;
  /** @nullable */
  typeCode: string | null;
  /** @nullable */
  groupBy: UtilisationReportFiltersGroupBy;
};

export type UtilisationReportSummary = {
  spaceCount: number;
  capacityUnits: number;
  bookedMinutes: number;
  availableMinutes: number;
  /** @nullable */
  utilisationPct: number | null;
  /** @minimum 0 */
  checkedInMinutes: number;
  /** @minimum 0 */
  checkedInPct: number;
  /** @minimum 0 */
  recordedMinutes: number;
  /** @minimum 0 */
  estimatedMinutes: number;
  /** @minimum 0 */
  missingScheduleSpaceCount: number;
  /** @minimum 0 */
  closedSpaceCount: number;
};

export interface UtilisationReport {
  from: string;
  to: string;
  filters: UtilisationReportFilters;
  summary: UtilisationReportSummary;
  items: UtilisationReportBucket[];
}

export interface UtilisationTimeseriesItem {
  day: string;
  /** @nullable */
  siteId: string | null;
  siteCode: string;
  siteName: string;
  /** @minimum 0 */
  bookedMinutes: number;
  /** @minimum 0 */
  availableMinutes: number;
  /**
     * @minimum 0
     * @nullable
     */
  utilisationPct: number | null;
  /** @minimum 0 */
  recordedMinutes: number;
  /** @minimum 0 */
  estimatedMinutes: number;
  /** @minimum 0 */
  missingScheduleSpaceCount: number;
  /** @minimum 0 */
  closedSpaceCount: number;
}

export type UtilisationTimeseriesPeriod = typeof UtilisationTimeseriesPeriod[keyof typeof UtilisationTimeseriesPeriod];


export const UtilisationTimeseriesPeriod = {
  day: 'day',
  month: 'month',
  quarter: 'quarter',
} as const;

export type UtilisationTimeseriesScope = typeof UtilisationTimeseriesScope[keyof typeof UtilisationTimeseriesScope];


export const UtilisationTimeseriesScope = {
  site: 'site',
  national: 'national',
} as const;

export type UtilisationTimeseriesFilters = {
  /** @nullable */
  siteId: string | null;
  /** @nullable */
  buildingId: string | null;
  /** @nullable */
  typeCode: string | null;
};

export type UtilisationTimeseriesSummary = {
  /** @minimum 0 */
  dayCount: number;
  /** @minimum 0 */
  siteCount: number;
};

export interface UtilisationTimeseries {
  from: string;
  to: string;
  period: UtilisationTimeseriesPeriod;
  scope: UtilisationTimeseriesScope;
  filters: UtilisationTimeseriesFilters;
  summary: UtilisationTimeseriesSummary;
  items: UtilisationTimeseriesItem[];
}

export interface NoShowReportItem {
  bookingId: string;
  spaceId: string;
  spaceCode: string;
  spaceName: string;
  siteId: string;
  siteName: string;
  buildingId: string;
  buildingName: string;
  typeCode: string;
  typeName: string;
  startAt: string;
  endAt: string;
  /** @nullable */
  checkInBy: string | null;
  /** @nullable */
  releasedAt: string | null;
  bookedForUserId: string;
}

export type NoShowReportFilters = {
  /** @nullable */
  siteId: string | null;
  /** @nullable */
  buildingId: string | null;
  /** @nullable */
  typeCode: string | null;
};

export type NoShowReportSummaryByTypeItem = {
  typeCode: string;
  typeName: string;
  /** @minimum 0 */
  count: number;
};

export type NoShowReportSummary = {
  /** @minimum 0 */
  noShowCount: number;
  byType: NoShowReportSummaryByTypeItem[];
};

export interface NoShowReport {
  from: string;
  to: string;
  filters: NoShowReportFilters;
  summary: NoShowReportSummary;
  items: NoShowReportItem[];
}

export interface IntentVsActualReportItem {
  day: string;
  siteId: string;
  siteCode: string;
  siteName: string;
  buildingId: string;
  buildingCode: string;
  buildingName: string;
  /** @minimum 0 */
  declaredOfficeCount: number;
  /** @minimum 0 */
  bookedCount: number;
  /** @minimum 0 */
  checkedInCount: number;
  /** @minimum 0 */
  intentCount: number;
  /** @minimum 0 */
  attendanceRatio: number;
}

export type IntentVsActualReportFilters = {
  /** @nullable */
  siteId: string | null;
  /** @nullable */
  buildingId: string | null;
  /** @nullable */
  typeCode: string | null;
};

export type IntentVsActualReportSummary = {
  /** @minimum 0 */
  dayCount: number;
  /** @minimum 0 */
  buildingCount: number;
  /** @minimum 0 */
  declaredOfficeCount: number;
  /** @minimum 0 */
  bookedCount: number;
  /** @minimum 0 */
  checkedInCount: number;
  /** @minimum 0 */
  intentCount: number;
  /** @minimum 0 */
  attendanceRatio: number;
  usingPresenceSignal: boolean;
};

export interface IntentVsActualReport {
  from: string;
  to: string;
  filters: IntentVsActualReportFilters;
  summary: IntentVsActualReportSummary;
  items: IntentVsActualReportItem[];
}

/**
 * @nullable
 */
export type ManagerAttendanceBookingCheckInMethod = typeof ManagerAttendanceBookingCheckInMethod[keyof typeof ManagerAttendanceBookingCheckInMethod] | null;


export const ManagerAttendanceBookingCheckInMethod = {
  web: 'web',
  qr: 'qr',
  admin: 'admin',
} as const;

/**
 * @nullable
 */
export type ManagerAttendanceBookingCheckOutMethod = typeof ManagerAttendanceBookingCheckOutMethod[keyof typeof ManagerAttendanceBookingCheckOutMethod] | null;


export const ManagerAttendanceBookingCheckOutMethod = {
  web: 'web',
  qr: 'qr',
  admin: 'admin',
} as const;

export type ManagerAttendanceBookingDurationEvidence = typeof ManagerAttendanceBookingDurationEvidence[keyof typeof ManagerAttendanceBookingDurationEvidence];


export const ManagerAttendanceBookingDurationEvidence = {
  none: 'none',
  recorded_checkout: 'recorded_checkout',
  estimated_no_checkout: 'estimated_no_checkout',
} as const;

export interface ManagerAttendanceBooking {
  bookingId: string;
  status: string;
  startAt: string;
  endAt: string;
  /** @nullable */
  checkInAt: string | null;
  /** @nullable */
  checkInMethod: ManagerAttendanceBookingCheckInMethod;
  /** @nullable */
  checkOutAt: string | null;
  /** @nullable */
  checkOutMethod: ManagerAttendanceBookingCheckOutMethod;
  durationEvidence: ManagerAttendanceBookingDurationEvidence;
  /**
     * @minimum 0
     * @nullable
     */
  durationMinutes: number | null;
}

export type ManagerAttendanceDayPlanStatus = typeof ManagerAttendanceDayPlanStatus[keyof typeof ManagerAttendanceDayPlanStatus];


export const ManagerAttendanceDayPlanStatus = {
  office: 'office',
  remote: 'remote',
  off: 'off',
  unknown: 'unknown',
} as const;

/**
 * @nullable
 */
export type ManagerAttendanceDayPlanSource = typeof ManagerAttendanceDayPlanSource[keyof typeof ManagerAttendanceDayPlanSource] | null;


export const ManagerAttendanceDayPlanSource = {
  manual: 'manual',
  recurring: 'recurring',
} as const;

export type ManagerAttendanceDayArrivalMethodsItem = typeof ManagerAttendanceDayArrivalMethodsItem[keyof typeof ManagerAttendanceDayArrivalMethodsItem];


export const ManagerAttendanceDayArrivalMethodsItem = {
  web: 'web',
  qr: 'qr',
  admin: 'admin',
} as const;

export type ManagerAttendanceDayComparison = typeof ManagerAttendanceDayComparison[keyof typeof ManagerAttendanceDayComparison];


export const ManagerAttendanceDayComparison = {
  matched: 'matched',
  no_recorded_arrival: 'no_recorded_arrival',
  unexpected_arrival: 'unexpected_arrival',
  no_comparison: 'no_comparison',
} as const;

export interface ManagerAttendanceDay {
  staffId: string;
  staffName: string;
  /** @nullable */
  staffEmail: string | null;
  day: string;
  planStatus: ManagerAttendanceDayPlanStatus;
  /** @nullable */
  planSource: ManagerAttendanceDayPlanSource;
  /** @nullable */
  planBuildingId: string | null;
  /** @nullable */
  planBuildingName: string | null;
  planAtSite: boolean;
  /** @minimum 0 */
  bookingCount: number;
  /** @minimum 0 */
  cancelledCount: number;
  /** @minimum 0 */
  noShowCount: number;
  arrivalMethods: ManagerAttendanceDayArrivalMethodsItem[];
  comparison: ManagerAttendanceDayComparison;
  bookings: ManagerAttendanceBooking[];
}

export interface ManagerAttendanceReport {
  from: string;
  to: string;
  siteId: string;
  siteName: string;
  timezone: string;
  scopeSource: ManagerScopeMode;
  scopeCheckedAt: string;
  staff: ManagerReportPage;
  items: ManagerAttendanceDay[];
  definition: string;
}

export interface PeakReportCell {
  /**
     * 0 = Sunday … 6 = Saturday (UTC)
     * @minimum 0
     * @maximum 6
     */
  weekday: number;
  /**
     * Hour of day (UTC)
     * @minimum 0
     * @maximum 23
     */
  hour: number;
  /** @minimum 0 */
  bookedMinutes: number;
  /** @minimum 0 */
  bookingHits: number;
}

export type PeakReportFilters = {
  /** @nullable */
  siteId: string | null;
  /** @nullable */
  buildingId: string | null;
  /** @nullable */
  typeCode: string | null;
};

export type PeakReportSummary = {
  /** @minimum 0 */
  maxBookedMinutes: number;
  /** @minimum 0 */
  totalBookedMinutes: number;
  /** @minimum 0 */
  spaceCount: number;
};

export interface PeakReport {
  from: string;
  to: string;
  filters: PeakReportFilters;
  summary: PeakReportSummary;
  cells: PeakReportCell[];
}

export interface UnderutilisedReportItem {
  spaceId: string;
  spaceCode: string;
  spaceName: string;
  siteId: string;
  siteCode: string;
  siteName: string;
  buildingId: string;
  buildingCode: string;
  buildingName: string;
  typeCode: string;
  typeName: string;
  /** @minimum 1 */
  capacity: number;
  /** @minimum 0 */
  bookedMinutes: number;
  /** @minimum 0 */
  availableMinutes: number;
  /** @minimum 0 */
  occupancyPct: number;
  /** @minimum 0 */
  recordedMinutes: number;
  /** @minimum 0 */
  estimatedMinutes: number;
  /** @minimum 0 */
  bookingCount: number;
  /** @minimum 0 */
  noShowCount: number;
  /** @minimum 0 */
  neverCheckedInCount: number;
}

export type UnderutilisedReportFilters = {
  /** @nullable */
  siteId: string | null;
  /** @nullable */
  buildingId: string | null;
  /** @nullable */
  typeCode: string | null;
  /**
     * @minimum 0
     * @maximum 100
     */
  thresholdPct: number;
};

export type UnderutilisedReportSummary = {
  /** @minimum 0 */
  spaceCount: number;
  /** @minimum 0 */
  underutilisedCount: number;
  thresholdPct: number;
  /** @minimum 0 */
  avgOccupancyPct: number;
  /** @minimum 0 */
  missingScheduleSpaceCount: number;
};

export interface UnderutilisedReport {
  from: string;
  to: string;
  filters: UnderutilisedReportFilters;
  summary: UnderutilisedReportSummary;
  items: UnderutilisedReportItem[];
}

export interface SaveTimedPlanStop {
  id: string;
  siteId: string;
  /** @nullable */
  buildingId?: string | null;
  startAt: string;
  endAt: string;
  requirements: StopRequirements;
  /** @maxItems 10 */
  bookingIds: string[];
}

export interface TimedPlanList {
  items: TimedPlan[];
}

export interface SaveTimedPlanRequest {
  /** @minimum 0 */
  expectedRevision: number;
  /** @maxLength 100 */
  timezone: string;
  /** @maxItems 24 */
  stops: SaveTimedPlanStop[];
}

export type LocationContextSource = typeof LocationContextSource[keyof typeof LocationContextSource];


export const LocationContextSource = {
  manual: 'manual',
  device: 'device',
} as const;

export interface LocationContext {
  source: LocationContextSource;
  /** @nullable */
  label: string | null;
  /** @nullable */
  siteId: string | null;
  /** @nullable */
  buildingId: string | null;
  /**
     * @minimum -90
     * @maximum 90
     * @nullable
     */
  latitude: number | null;
  /**
     * @minimum -180
     * @maximum 180
     * @nullable
     */
  longitude: number | null;
  /**
     * @minimum 0
     * @nullable
     */
  accuracyMeters: number | null;
  observedAt: string;
  updatedAt: string;
}

export interface LocationContextResponse {
  context: LocationContext | null;
}

export type SaveLocationContextRequestSource = typeof SaveLocationContextRequestSource[keyof typeof SaveLocationContextRequestSource];


export const SaveLocationContextRequestSource = {
  manual: 'manual',
  device: 'device',
} as const;

export interface SaveLocationContextRequest {
  source: SaveLocationContextRequestSource;
  /**
     * @maxLength 160
     * @nullable
     */
  label?: string | null;
  /** @nullable */
  siteId?: string | null;
  /** @nullable */
  buildingId?: string | null;
  /**
     * @minimum -90
     * @maximum 90
     * @nullable
     */
  latitude?: number | null;
  /**
     * @minimum -180
     * @maximum 180
     * @nullable
     */
  longitude?: number | null;
  /**
     * @minimum 0
     * @nullable
     */
  accuracyMeters?: number | null;
  observedAt: string;
}

export interface LocationNeedList {
  items: LocationNeed[];
}

export interface SuggestLocationNeedsRequest {
  siteId: string;
}

export type LocationNeedDecisionDecision = typeof LocationNeedDecisionDecision[keyof typeof LocationNeedDecisionDecision];


export const LocationNeedDecisionDecision = {
  approved: 'approved',
  dismissed: 'dismissed',
} as const;

export type LocationNeedDecisionStrength = typeof LocationNeedDecisionStrength[keyof typeof LocationNeedDecisionStrength];


export const LocationNeedDecisionStrength = {
  required: 'required',
  preferred: 'preferred',
} as const;

export interface LocationNeedDecision {
  id: string;
  decision: LocationNeedDecisionDecision;
  strength: LocationNeedDecisionStrength;
}

export interface DecideLocationNeedsRequest {
  /**
     * @minItems 1
     * @maxItems 20
     */
  items: LocationNeedDecision[];
}

export type LocationRecommendationAvailabilitySource = typeof LocationRecommendationAvailabilitySource[keyof typeof LocationRecommendationAvailabilitySource];


export const LocationRecommendationAvailabilitySource = {
  app: 'app',
  app_fallback: 'app_fallback',
  exchange: 'exchange',
} as const;

export interface LocationRecommendation {
  space: AvailabilitySpace;
  score: number;
  reasons: string[];
  availabilitySource: LocationRecommendationAvailabilitySource;
}

export interface LocationRecommendationList {
  items: LocationRecommendation[];
  /** @minimum 0 */
  totalEligible: number;
  /** More inventory exists beyond the bounded eligibility scan */
  truncated: boolean;
  requiredNeeds: LocationNeed[];
  preferredNeeds: LocationNeed[];
  explanation: string;
}

export type CreateLocationNeedRequestFeatureKind = typeof CreateLocationNeedRequestFeatureKind[keyof typeof CreateLocationNeedRequestFeatureKind];


export const CreateLocationNeedRequestFeatureKind = {
  space_type: 'space_type',
  amenity: 'amenity',
  tag: 'tag',
} as const;

export type CreateLocationNeedRequestStrength = typeof CreateLocationNeedRequestStrength[keyof typeof CreateLocationNeedRequestStrength];


export const CreateLocationNeedRequestStrength = {
  required: 'required',
  preferred: 'preferred',
} as const;

export interface CreateLocationNeedRequest {
  siteId: string;
  featureKind: CreateLocationNeedRequestFeatureKind;
  /**
     * @minLength 1
     * @maxLength 64
     */
  featureCode: string;
  strength: CreateLocationNeedRequestStrength;
}

export type UpdateLocationNeedRequestDecision = typeof UpdateLocationNeedRequestDecision[keyof typeof UpdateLocationNeedRequestDecision];


export const UpdateLocationNeedRequestDecision = {
  approved: 'approved',
  dismissed: 'dismissed',
} as const;

export type UpdateLocationNeedRequestStrength = typeof UpdateLocationNeedRequestStrength[keyof typeof UpdateLocationNeedRequestStrength];


export const UpdateLocationNeedRequestStrength = {
  required: 'required',
  preferred: 'preferred',
} as const;

export interface UpdateLocationNeedRequest {
  decision: UpdateLocationNeedRequestDecision;
  strength: UpdateLocationNeedRequestStrength;
}

export type PresenceStatus = typeof PresenceStatus[keyof typeof PresenceStatus];


export const PresenceStatus = {
  office: 'office',
  remote: 'remote',
  off: 'off',
  unknown: 'unknown',
} as const;

/**
 * @nullable
 */
export type PresenceSource = typeof PresenceSource[keyof typeof PresenceSource] | null;


export const PresenceSource = {
  manual: 'manual',
  recurring: 'recurring',
} as const;

export type PresenceSharingLevel = typeof PresenceSharingLevel[keyof typeof PresenceSharingLevel];


export const PresenceSharingLevel = {
  everyone: 'everyone',
  workgroups: 'workgroups',
  selected: 'selected',
  private: 'private',
} as const;

export interface PresenceDay {
  /** @nullable */
  id: string | null;
  date: string;
  status: PresenceStatus;
  /** @nullable */
  buildingId: string | null;
  /** @nullable */
  buildingName: string | null;
  /** @nullable */
  siteName: string | null;
  source: PresenceSource | null;
}

export interface PresenceDefaultDay {
  /**
     * 0 = Sunday … 6 = Saturday
     * @minimum 0
     * @maximum 6
     */
  weekday: number;
  status: PresenceStatus;
  /** @nullable */
  buildingId: string | null;
  /** @nullable */
  buildingName: string | null;
  /** @nullable */
  siteName: string | null;
}

export type PresenceSettingsSelectedColleaguesItem = {
  userId: string;
  displayName: string;
};

export interface PresenceSettings {
  sharingLevel: PresenceSharingLevel;
  /** @maxItems 50 */
  selectedColleagueIds: string[];
  /** @maxItems 50 */
  selectedColleagues: PresenceSettingsSelectedColleaguesItem[];
  shareDeskLocation: boolean;
  shareTeamsAvailability: boolean;
  /** @nullable */
  updatedAt: string | null;
}

/**
 * @nullable
 */
export type StaffClassificationRecordValue = typeof StaffClassificationRecordValue[keyof typeof StaffClassificationRecordValue] | null;


export const StaffClassificationRecordValue = {
  EL2: 'EL2',
  SES: 'SES',
  Conciliator: 'Conciliator',
} as const;

export interface StaffClassificationRecord {
  /** @nullable */
  value: StaffClassificationRecordValue;
}

export interface WorkplaceZone {
  id: string;
  name: string;
  floorName: string;
  siteName: string;
}

export interface WorkplaceProfile {
  /** @nullable */
  defaultSiteId: string | null;
  assignedZoneIds: string[];
  assignedZones: WorkplaceZone[];
  /** @nullable */
  primaryZoneId: string | null;
  /** @nullable */
  primaryZoneFloorId: string | null;
  /** @nullable */
  primaryZoneSiteId: string | null;
  /** @nullable */
  updatedAt: string | null;
}

export interface UserSearchPrefs {
  /** @nullable */
  siteId: string | null;
  /** @nullable */
  buildingId: string | null;
  /**
     * Space type code (e.g. desk, room)
     * @nullable
     */
  typeCode: string | null;
  /**
     * Local wall-clock start as HH:MM (24h)
     * @nullable
     * @pattern ^([01]\d|2[0-3]):[0-5]\d$
     */
  startTimeOfDay: string | null;
  /**
     * @minimum 15
     * @maximum 1440
     * @nullable
     */
  durationMinutes: number | null;
  /** @nullable */
  updatedAt: string | null;
}

export interface UpdateUserSearchPrefs {
  /** @nullable */
  siteId?: string | null;
  /** @nullable */
  buildingId?: string | null;
  /**
     * @maxLength 64
     * @nullable
     */
  typeCode?: string | null;
  /**
     * Local wall-clock start as HH:MM (24h)
     * @nullable
     * @pattern ^([01]\d|2[0-3]):[0-5]\d$
     */
  startTimeOfDay?: string | null;
  /**
     * @minimum 15
     * @maximum 1440
     * @nullable
     */
  durationMinutes?: number | null;
}

export type FavouriteItemReasonsItem = typeof FavouriteItemReasonsItem[keyof typeof FavouriteItemReasonsItem];


export const FavouriteItemReasonsItem = {
  AVAILABLE: 'AVAILABLE',
  CONFLICT: 'CONFLICT',
  UNAVAILABLE_MODE: 'UNAVAILABLE_MODE',
  RETIRED: 'RETIRED',
  INACTIVE: 'INACTIVE',
} as const;

export interface FavouriteItem {
  spaceId: string;
  createdAt: string;
  space: AvailabilitySpace;
  /**
     * Present when start/end were provided on list. True when the space can be reserved for that window; false when blocked, inactive, or non-bookable.
     * @nullable
     */
  available?: boolean | null;
  /** Reason codes when available is false (or empty when free / no window) */
  reasons?: FavouriteItemReasonsItem[];
  /** @nullable */
  reasonDetail?: string | null;
}

export interface FavouriteListResponse {
  items: FavouriteItem[];
  /** @minimum 0 */
  total: number;
  /**
     * @minimum 1
     * @maximum 50
     */
  limit: number;
  /** @nullable */
  windowStart?: string | null;
  /** @nullable */
  windowEnd?: string | null;
}

export type BookingSuggestionReasonsItem = typeof BookingSuggestionReasonsItem[keyof typeof BookingSuggestionReasonsItem];


export const BookingSuggestionReasonsItem = {
  history: 'history',
  favourite: 'favourite',
  near_team: 'near_team',
} as const;

export interface BookingSuggestion {
  /** Stable id for dismiss (space:{uuid}|wd:{0-6}|h:{0-23}) */
  patternKey: string;
  space: AvailabilitySpace;
  /**
     * Local weekday of the habit (0=Sunday)
     * @minimum 0
     * @maximum 6
     */
  weekday: number;
  weekdayLabel: string;
  /**
     * @minimum 0
     * @maximum 23
     */
  startHour: number;
  /** Site-local HH:mm habit start (includes median minute) */
  typicalStartTime?: string;
  /**
     * @minimum 15
     * @maximum 1440
     */
  durationMinutes: number;
  /** @minimum 1 */
  occurrenceCount: number;
  /** Frequency + recency score used for ranking */
  score: number;
  nextStart: string;
  nextEnd: string;
  /** Human-readable habit line for UI cards */
  summary: string;
  /**
     * Next-slot availability when checkAvailability was true
     * @nullable
     */
  available?: boolean | null;
  /** @nullable */
  reasonDetail?: string | null;
  /** Space is starred by the caller */
  isFavourite: boolean;
  /** Teammates plan or booked nearby that day */
  nearTeam: boolean;
  /** Why this item ranked */
  reasons: BookingSuggestionReasonsItem[];
}

export interface BookingSuggestionListResponse {
  items: BookingSuggestion[];
  /** @minimum 1 */
  historyDays: number;
  /**
     * Minimum matching bookings required before a pattern is suggested
     * @minimum 1
     */
  minOccurrences: number;
  favouriteSpaceIds?: string[];
  nearTeamFloorIds?: string[];
  nearTeamBuildingIds?: string[];
}

export interface BookAgainItem {
  spaceId: string;
  space: AvailabilitySpace;
  /** @minimum 1 */
  bookingCount: number;
  lastBookedAt: string;
  lastStart: string;
  lastEnd: string;
  /** @minimum 15 */
  typicalDurationMinutes: number;
  /** @nullable */
  available?: boolean | null;
  reasons?: string[];
  /** @nullable */
  reasonDetail?: string | null;
  /** Space is starred by the caller */
  isFavourite: boolean;
}

export interface BookAgainListResponse {
  items: BookAgainItem[];
  /** @minimum 0 */
  total: number;
  /** @minimum 1 */
  limit: number;
  /** @nullable */
  windowStart?: string | null;
  /** @nullable */
  windowEnd?: string | null;
}

export interface PresenceWeekPlan {
  from: string;
  to: string;
  days: PresenceDay[];
  defaults: PresenceDefaultDay[];
  settings: PresenceSettings;
}

export type UpsertPresencePlanRequestEntriesItem = {
  date: string;
  status: PresenceStatus;
  /** @nullable */
  buildingId?: string | null;
};

export interface UpsertPresencePlanRequest {
  /**
     * @minItems 1
     * @maxItems 90
     */
  entries: UpsertPresencePlanRequestEntriesItem[];
}

export interface UpsertPresencePlanResponse {
  days: PresenceDay[];
}

export type SetPresenceDefaultsRequestDefaultsItem = {
  /**
     * @minimum 0
     * @maximum 6
     */
  weekday: number;
  status: PresenceStatus;
  /** @nullable */
  buildingId?: string | null;
};

export interface SetPresenceDefaultsRequest {
  /** @maxItems 7 */
  defaults: SetPresenceDefaultsRequestDefaultsItem[];
}

export interface SetPresenceDefaultsResponse {
  defaults: PresenceDefaultDay[];
}

export interface SetPresenceSettingsRequest {
  sharingLevel: PresenceSharingLevel;
  /** @maxItems 50 */
  selectedColleagueIds?: string[];
  shareDeskLocation?: boolean;
  shareTeamsAvailability?: boolean;
}

export interface UserPresenceWindow {
  userId: string;
  days: PresenceDay[];
  visible: boolean;
  sharingLevel: PresenceSharingLevel | null;
}

export interface UsersPresenceResponse {
  items: UserPresenceWindow[];
}

export interface ColleagueSearchItem {
  userId: string;
  /** @nullable */
  displayName: string | null;
  email: string;
  /** False when privacy hides presence from the viewer */
  presenceVisible: boolean;
  todayStatus: PresenceStatus | null;
  /** @nullable */
  todayBuildingId: string | null;
  /** @nullable */
  todayBuildingName: string | null;
  /** True when viewer and colleague are both office in the same building today */
  sameBuildingToday: boolean;
}

export interface ColleagueSearchResponse {
  date: string;
  items: ColleagueSearchItem[];
  /** @minimum 0 */
  total: number;
}

export interface SharedWorkgroupRef {
  id: string;
  name: string;
}

export interface VisibleColleagueDesk {
  userId: string;
  displayName: string;
  spaceId: string;
  spaceName: string;
  floorId: string;
  buildingName: string;
  siteName: string;
  /**
     * Site timezone for the disclosed booking, without exposing additional location details
     * @nullable
     */
  timezone?: string | null;
  startAt: string;
  endAt: string;
}

export interface ColleagueWeekResponse {
  deskBooking: VisibleColleagueDesk | null;
  /**
     * Entra user ID only when this viewer may request Teams presence
     * @nullable
     */
  teamsUserId: string | null;
  userId: string;
  /** @nullable */
  displayName: string | null;
  email: string;
  from: string;
  to: string;
  presenceVisible: boolean;
  days: PresenceDay[];
  sameBuildingToday: boolean;
  todayStatus: PresenceStatus | null;
  /** @nullable */
  todayBuildingId: string | null;
  /** @nullable */
  todayBuildingName: string | null;
  sharedWorkgroups: SharedWorkgroupRef[];
}

export interface VisibleColleagueDesksResponse {
  items: VisibleColleagueDesk[];
  /** @minimum 0 */
  total: number;
  at: string;
}

export interface NominationItem {
  colleagueUserId: string;
  displayName: string;
  email: string;
  createdAt: string;
}

export interface NominationListResponse {
  items: NominationItem[];
  /** @minimum 0 */
  total: number;
}

export interface NominationResult {
  colleagueUserId: string;
  nominated: boolean;
}

export interface NominationRemoveResult {
  colleagueUserId: string;
  removed: boolean;
}

export interface NominatedColleagueBooking {
  bookingId: string;
  spaceId: string;
  spaceName: string;
  spaceCode: string;
  buildingId: string;
  buildingName: string;
  siteId: string;
  siteName: string;
  /**
     * Site timezone for this disclosed booking
     * @nullable
     */
  timezone?: string | null;
  startAt: string;
  endAt: string;
  status: string;
}

export interface NominatedColleagueBookingsEntry {
  colleagueUserId: string;
  displayName: string;
  visible: boolean;
  bookings: NominatedColleagueBooking[];
}

export interface NominatedColleagueBookingsResponse {
  from: string;
  to: string;
  items: NominatedColleagueBookingsEntry[];
}

export type WorkgroupMemberRole = typeof WorkgroupMemberRole[keyof typeof WorkgroupMemberRole];


export const WorkgroupMemberRole = {
  owner: 'owner',
  member: 'member',
} as const;

export interface WorkgroupSummary {
  id: string;
  name: string;
  /** @nullable */
  description: string | null;
  ownerId: string;
  /** @minimum 0 */
  memberCount: number;
  myRole: WorkgroupMemberRole;
  createdAt: string;
  updatedAt: string;
}

export interface WorkgroupListResponse {
  items: WorkgroupSummary[];
  /** @minimum 0 */
  total: number;
}

export interface WorkgroupMember {
  userId: string;
  role: WorkgroupMemberRole;
  /** @nullable */
  displayName: string | null;
  email: string;
  joinedAt: string;
}

export interface WorkgroupGuidance {
  /**
     * 0 = Sunday … 6 = Saturday
     * @items.minimum 0
     * @items.maximum 6
     */
  weekdays: number[];
  /** @nullable */
  buildingId: string | null;
  /** @nullable */
  buildingName: string | null;
  /** @nullable */
  note: string | null;
  /** @nullable */
  updatedAt: string | null;
  /** @nullable */
  updatedByUserId: string | null;
}

export type WorkgroupDetail = WorkgroupSummary & ({
  members: WorkgroupMember[];
  guidance: WorkgroupGuidance | null;
});

export interface CreateWorkgroupRequest {
  /**
     * @minLength 1
     * @maxLength 120
     */
  name: string;
  /**
     * @maxLength 500
     * @nullable
     */
  description?: string | null;
}

export interface UpdateWorkgroupRequest {
  /**
     * @minLength 1
     * @maxLength 120
     */
  name?: string;
  /**
     * @maxLength 500
     * @nullable
     */
  description?: string | null;
}

export interface AddWorkgroupMemberRequest {
  userId: string;
}

export interface DeleteWorkgroupResponse {
  deleted: true;
}

export interface LeftWorkgroupResponse {
  left: true;
}

export interface SetWorkgroupGuidanceRequest {
  /**
     * @maxItems 7
     * @items.minimum 0
     * @items.maximum 6
     */
  weekdays: number[];
  /** @nullable */
  buildingId?: string | null;
  /**
     * @maxLength 1000
     * @nullable
     */
  note?: string | null;
}

export type WorkgroupWeekMember = WorkgroupMember & {
  presence: UserPresenceWindow;
};

export interface WorkgroupWeekResponse {
  workgroupId: string;
  from: string;
  to: string;
  guidance: WorkgroupGuidance | null;
  members: WorkgroupWeekMember[];
}

export type TeamBookingMemberRole = typeof TeamBookingMemberRole[keyof typeof TeamBookingMemberRole];


export const TeamBookingMemberRole = {
  owner: 'owner',
  member: 'member',
} as const;

export interface TeamBooking {
  bookingId: string;
  spaceId: string;
  spaceName: string;
  spaceCode: string;
  buildingId: string;
  buildingName: string;
  siteId: string;
  siteName: string;
  /**
     * Site timezone for this disclosed booking
     * @nullable
     */
  timezone?: string | null;
  startAt: string;
  endAt: string;
  status: string;
}

export interface TeamBookingMember {
  userId: string;
  displayName: string;
  email: string;
  role: TeamBookingMemberRole;
  visible: boolean;
  bookings: TeamBooking[];
}

export interface TeamBookingsResponse {
  workgroupId: string;
  from: string;
  to: string;
  members: TeamBookingMember[];
}

export interface SiteInductionChecklist {
  siteId: string;
  siteName: string;
  /** @minimum 1 */
  version: number;
  items: SiteInductionItem[];
  active: boolean;
  updatedAt: string;
  /**
     * @minimum 1
     * @maximum 3650
     * @nullable
     */
  validityDays: number | null;
  /** @maxItems 50 */
  arrivalItems: SiteInductionItem[];
  /** @maxLength 4000 */
  safetyGuidance: string;
}

export interface PutSiteSafetyConfig {
  /**
     * @minItems 1
     * @maxItems 50
     */
  items: SiteInductionItem[];
  /**
     * @minimum 1
     * @maximum 3650
     * @nullable
     */
  validityDays: number | null;
  /** @maxItems 50 */
  arrivalItems: SiteInductionItem[];
  /** @maxLength 4000 */
  safetyGuidance: string;
}

export interface UserSiteInductionStatus {
  siteId: string;
  siteName: string;
  /**
     * @minimum 1
     * @nullable
     */
  currentVersion: number | null;
  hasActiveChecklist: boolean;
  /**
     * @minimum 1
     * @nullable
     */
  acknowledgedVersion: number | null;
  /** @nullable */
  completedAt: string | null;
  /** @nullable */
  expiresAt: string | null;
  /** True when the site has no active checklist, or the user has acknowledged the current version. */
  isComplete: boolean;
}

export interface UserSiteInductionListResponse {
  items: UserSiteInductionStatus[];
}

export interface CompleteSiteInductionRequest {
  siteId: string;
  /** @minimum 1 */
  version: number;
  /**
     * @minItems 1
     * @maxItems 50
     * @items.minLength 1
     * @items.maxLength 64
     */
  itemIds: string[];
}

export type VisitorBookingSettingsMode = typeof VisitorBookingSettingsMode[keyof typeof VisitorBookingSettingsMode];


export const VisitorBookingSettingsMode = {
  host_only: 'host_only',
  self_service: 'self_service',
  both: 'both',
} as const;

export interface VisitorBookingSettings {
  mode: VisitorBookingSettingsMode;
}

export type VisitorBookingApprovalStatus = typeof VisitorBookingApprovalStatus[keyof typeof VisitorBookingApprovalStatus];


export const VisitorBookingApprovalStatus = {
  pending: 'pending',
  approved: 'approved',
  revoked: 'revoked',
} as const;

export interface VisitorLinkedBooking {
  id: string;
  spaceId: string;
  /** @nullable */
  layoutId?: string | null;
  /** @nullable */
  layoutName?: string | null;
  /**
     * @minimum 1
     * @nullable
     */
  layoutCapacity?: number | null;
  start: string;
  end: string;
  status: string;
  /** @nullable */
  retryId?: string | null;
  calendarPending: boolean;
}

export interface VisitorBookingApproval {
  id: string;
  visitId: string;
  organizationId: string;
  guestUserId: string;
  status: VisitorBookingApprovalStatus;
  /** @nullable */
  siteId?: string | null;
  /**
     * Timezone of the approved site, disclosed only with this approval.
     * @nullable
     */
  timezone?: string | null;
  /** @nullable */
  spaceTypeId?: string | null;
  /** @nullable */
  validFrom?: string | null;
  /** @nullable */
  validUntil?: string | null;
  /** @nullable */
  decidedAt?: string | null;
  createdAt: string;
  linkedBooking?: VisitorLinkedBooking | null;
  visitorName?: string;
  guestName?: string;
  /** @nullable */
  guestEmail?: string | null;
  expectedStart?: string;
  /** @nullable */
  expectedEnd?: string | null;
}

export type VisitorCancelResultStatus = typeof VisitorCancelResultStatus[keyof typeof VisitorCancelResultStatus];


export const VisitorCancelResultStatus = {
  cancelled: 'cancelled',
  already_cancelled: 'already_cancelled',
} as const;

export interface VisitorCancelResult {
  visitId: string;
  status: VisitorCancelResultStatus;
}

export interface VisitorApprovalList {
  items: VisitorBookingApproval[];
  /** @minimum 0 */
  total: number;
}

export interface VisitorApprovalRequest {
  visitId: string;
}

export type VisitorApprovalDecisionDecision = typeof VisitorApprovalDecisionDecision[keyof typeof VisitorApprovalDecisionDecision];


export const VisitorApprovalDecisionDecision = {
  approve: 'approve',
  revoke: 'revoke',
} as const;

export interface VisitorApprovalDecision {
  decision: VisitorApprovalDecisionDecision;
  siteId?: string;
  spaceTypeId?: string;
  /** Must be on a 15-minute mark when approving. */
  validFrom?: string;
  /** Must be on a 15-minute mark when approving. */
  validUntil?: string;
}

export interface VisitorSelfBookRequest {
  /**
     * @minLength 16
     * @maxLength 256
     */
  overlapAcknowledgement?: string;
  spaceId: string;
  layoutId?: string;
  start: string;
  end: string;
  clientRequestId: string;
}

export type VisitorSelfBookingResultOutcome = typeof VisitorSelfBookingResultOutcome[keyof typeof VisitorSelfBookingResultOutcome];


export const VisitorSelfBookingResultOutcome = {
  confirmed: 'confirmed',
  calendar_pending: 'calendar_pending',
  app_only: 'app_only',
} as const;

export interface VisitorSelfBookingResult {
  booking: Booking;
  visitId: string;
  outcome: VisitorSelfBookingResultOutcome;
}

export type VisitorVisitStatus = typeof VisitorVisitStatus[keyof typeof VisitorVisitStatus];


export const VisitorVisitStatus = {
  expected: 'expected',
  checked_in: 'checked_in',
  checked_out: 'checked_out',
  cancelled: 'cancelled',
  no_show: 'no_show',
} as const;

export interface Visit {
  id: string;
  /** @nullable */
  visitorId?: string | null;
  hostUserId: string;
  /** @nullable */
  hostDisplayName?: string | null;
  /** @nullable */
  hostEmail?: string | null;
  /** @nullable */
  bookingId?: string | null;
  /** @nullable */
  siteId?: string | null;
  /** @nullable */
  siteName?: string | null;
  /**
     * Timezone of the visit site; absent for visits without a site.
     * @nullable
     */
  timezone?: string | null;
  /** @nullable */
  buildingId?: string | null;
  /** @nullable */
  buildingName?: string | null;
  visitorName: string;
  /** @nullable */
  visitorEmail?: string | null;
  /** @nullable */
  visitorCompany?: string | null;
  expectedStart: string;
  /** @nullable */
  expectedEnd?: string | null;
  status: VisitorVisitStatus;
  /** @nullable */
  notes?: string | null;
  /** @nullable */
  checkedInAt?: string | null;
  /** @nullable */
  checkedOutAt?: string | null;
  /** @nullable */
  checkedInByUserId?: string | null;
  /** @nullable */
  cancelledAt?: string | null;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
  /** @nullable */
  bookingTitle?: string | null;
  /** @nullable */
  spaceName?: string | null;
}

export interface VisitListResponse {
  items: Visit[];
  /** @minimum 0 */
  total: number;
}

/**
 * Only the selected visit snapshot changes; guest access and reusable identity are preserved. Omitted fields remain unchanged.
 */
export interface UpdateVisitRequest {
  /**
     * @minLength 1
     * @maxLength 200
     * @pattern \S
     */
  visitorName?: string;
  /**
     * @maxLength 200
     * @nullable
     */
  visitorEmail?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  visitorCompany?: string | null;
  /**
     * @maxLength 1000
     * @nullable
     */
  notes?: string | null;
}

export interface CreateVisitRequest {
  /**
     * @minLength 1
     * @maxLength 200
     */
  visitorName: string;
  /**
     * @maxLength 200
     * @nullable
     */
  visitorEmail?: string | null;
  /**
     * @maxLength 200
     * @nullable
     */
  visitorCompany?: string | null;
  /**
     * Invitations must use a 15-minute mark; walk-up visits may use the actual arrival time.
     * @nullable
     */
  expectedStart?: string | null;
  /**
     * Optional for invitations; when supplied it must use a 15-minute mark.
     * @nullable
     */
  expectedEnd?: string | null;
  /** @nullable */
  bookingId?: string | null;
  /** @nullable */
  siteId?: string | null;
  /** @nullable */
  buildingId?: string | null;
  /** @nullable */
  hostUserId?: string | null;
  /**
     * @maxLength 1000
     * @nullable
     */
  notes?: string | null;
}

export type InsightsOverviewWindow = {
  from: string;
  to: string;
};

export type InsightsOverviewFilters = {
  /** @nullable */
  siteId?: string | null;
  /** @nullable */
  buildingId?: string | null;
  /** @nullable */
  typeCode?: string | null;
};

export type InsightsOverviewSummary = {
  /** @nullable */
  overallUtilisationPct: number | null;
  totalSpaces: number;
  totalBookedMinutes: number;
  totalAvailableMinutes: number;
  noShowCount: number;
  underuseFlagCount: number;
  overbookFlagCount: number;
};

export type InsightsOverviewUtilisationByTypeItem = {
  typeCode: string;
  typeName: string;
  /** @nullable */
  utilisationPct: number | null;
  spaceCount: number;
  bookedMinutes: number;
  availableMinutes: number;
};

export type InsightsOverviewUtilisationTrendItem = {
  day: string;
  /** @nullable */
  utilisationPct: number | null;
};

export type InsightsOverviewUnderuseFlagsItem = {
  buildingName: string;
  typeCode: string;
  typeName: string;
  occupancyPct: number;
  capacity: number;
  spaceName: string;
};

export type InsightsOverviewOverbookFlagsItem = {
  buildingName: string;
  day: string;
  bookedCount: number;
  checkedInCount: number;
  declaredOfficeCount: number;
  attendanceRatio: number;
};

export type InsightsOverviewNoShowHotspotsItem = {
  typeCode: string;
  typeName: string;
  count: number;
};

export type DemandForecastWeeksItemWeekdayBreakdownItem = {
  weekday: string;
  projectedBookings: number;
};

export type DemandForecastWeeksItem = {
  weekStart: string;
  projectedBookings: number;
  weekdayBreakdown: DemandForecastWeeksItemWeekdayBreakdownItem[];
};

export interface DemandForecast {
  method: string;
  historyWeeks: number;
  weeks: DemandForecastWeeksItem[];
}

export interface InsightsOverview {
  window: InsightsOverviewWindow;
  filters: InsightsOverviewFilters;
  summary: InsightsOverviewSummary;
  utilisationByType: InsightsOverviewUtilisationByTypeItem[];
  utilisationTrend: InsightsOverviewUtilisationTrendItem[];
  underuseFlags: InsightsOverviewUnderuseFlagsItem[];
  overbookFlags: InsightsOverviewOverbookFlagsItem[];
  noShowHotspots: InsightsOverviewNoShowHotspotsItem[];
  forecast: DemandForecast;
}

export interface WeeklySummaryResult {
  summary: string;
  generatedAt: string;
  model: string;
  degraded: boolean;
}

export interface InventoryImportCounts {
  /** @minimum 0 */
  additions: number;
  /** @minimum 0 */
  updates: number;
  /** @minimum 0 */
  unchanged: number;
  /** @minimum 0 */
  blocked: number;
}

export type InventoryImportRowRaw = {[key: string]: string};

export type InventoryImportRowAction = typeof InventoryImportRowAction[keyof typeof InventoryImportRowAction];


export const InventoryImportRowAction = {
  add: 'add',
  update: 'update',
  unchanged: 'unchanged',
  blocked: 'blocked',
} as const;

export interface InventoryImportRow {
  /**
     * One-based data row number after the header
     * @minimum 1
     */
  rowIndex: number;
  raw: InventoryImportRowRaw;
  valid: boolean;
  action: InventoryImportRowAction;
  /** @nullable */
  spaceId?: string | null;
  changedFields: string[];
  errors: string[];
}

export interface InventoryImportPreview {
  /** @minimum 0 */
  totalRows: number;
  /** @minimum 0 */
  validRows: number;
  /** @minimum 0 */
  errorRows: number;
  summary: InventoryImportCounts;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
  /** @maxItems 100 */
  rows: InventoryImportRow[];
}

export type InventoryImportOutcomeAction = typeof InventoryImportOutcomeAction[keyof typeof InventoryImportOutcomeAction];


export const InventoryImportOutcomeAction = {
  created: 'created',
  updated: 'updated',
  unchanged: 'unchanged',
  blocked: 'blocked',
} as const;

export interface InventoryImportOutcome {
  /** @minimum 1 */
  rowIndex: number;
  action: InventoryImportOutcomeAction;
  /** @nullable */
  spaceId?: string | null;
  errors: string[];
}

export interface InventoryImportCommitResult {
  /** @minimum 0 */
  created: number;
  /** @minimum 0 */
  updated: number;
  /**
     * Unchanged plus blocked rows
     * @minimum 0
     */
  skipped: number;
  /** @minimum 0 */
  unchanged: number;
  /** @minimum 0 */
  blocked: number;
  /** @minimum 0 */
  totalRows: number;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /** @minimum 0 */
  offset: number;
  reportId: string;
  expiresAt: string;
  /** @maxItems 100 */
  errors: string[];
  /** @maxItems 100 */
  rows: InventoryImportOutcome[];
}

export type LimitParameter = number;

export type OffsetParameter = number;

export type ReportFromParameter = string;

export type ReportToParameter = string;

export type ReportSiteIdParameter = string;

export type ReportBuildingIdParameter = string;

export type ReportTypeCodeParameter = string;

/**
 * Opaque delegated Microsoft Graph credential from the signed-in caller. Authorization remains an independently validated custom-API bearer. The server verifies Graph /me and /organization against API oid/tid before any group read or check of a server-enrolled subject. Requires delegated User.Read, User.ReadBasic.All and administrator-consented GroupMember.Read.All. Never persist, log or include in URLs. Missing or rejected credentials grant no group access; valid individual access remains available.
 */
export type GroupGraphTokenParameter = string;

/**
 * Opaque delegated Microsoft Graph access token from the signed-in manager's existing browser session. Only a downstream Graph credential; Authorization must still contain the independently validated custom-API bearer. The server checks Graph /me and /organization against API oid/tid before reading reports. Never persist, log or include in URLs. Missing access makes directory mode unavailable; local assignments remain usable.
 */
export type ManagerGraphTokenParameter = string;

export type ReportFormatParameter = typeof ReportFormatParameter[keyof typeof ReportFormatParameter];


export const ReportFormatParameter = {
  json: 'json',
  csv: 'csv',
} as const;

export type EmailTemplateScopeQueryParameter = typeof EmailTemplateScopeQueryParameter[keyof typeof EmailTemplateScopeQueryParameter];


export const EmailTemplateScopeQueryParameter = {
  tenant: 'tenant',
  entity: 'entity',
} as const;

export type UploadTenantBackupBody = {
  archive: Blob | File;
  idempotencyKey: string;
};

export type FindTenantBackupRequestParams = {
kind: FindTenantBackupRequestKind;
};

export type FindTenantBackupRequestKind = typeof FindTenantBackupRequestKind[keyof typeof FindTenantBackupRequestKind];


export const FindTenantBackupRequestKind = {
  export: 'export',
  import: 'import',
} as const;

export type ListTenantBackupChangesParams = {
receiptId: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000000
 */
offset?: number;
};

export type ListPolicyCatalogueParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListPolicyRuleVersionsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type ListPolicyScopeRulesParams = {
scope: PolicyScope;
scopeId: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type SetPolicyScopeRule200 = {
  saved: boolean;
};

export type GetMyPolicyRequirementsParams = {
spaceId: string;
};

export type ListInventoryLifecycleHistoryParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListInventoryLifecyclePreviewChildrenParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListInventoryLifecyclePreviewBookingsParams = {
/**
 * Filter the reviewed snapshot to one space before pagination
 */
spaceId?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListStaffedServicesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
includeInactive?: boolean;
};

export type ListStaffedServiceTeamsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListMyStaffedServiceRequestsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type ListTeamStaffedServiceRequestsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: LimitParameter;
/**
 * @minimum 0
 */
offset?: OffsetParameter;
};

export type GetDashboardTimeseriesParams = {
/**
 * @minimum 7
 * @maximum 90
 */
days?: number;
};

export type ListAdminAuditEventsParams = {
/**
 * @maxLength 200
 */
q?: string;
/**
 * @maxLength 120
 */
action?: string;
/**
 * @maxLength 60
 */
entityType?: string;
actorUserId?: string;
from?: string;
to?: string;
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListAdminUsersParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
};

export type AssignAdminUserRoleBody = {
  roleCode: ProductRoleCode;
};

export type GetMyManagerScopeParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetAdminManagerScopeParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListGraphRoomsParams = {
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
};

export type ListSitesParams = {
status?: ListSitesStatus;
includeInactive?: ListSitesIncludeInactive;
};

export type ListSitesStatus = typeof ListSitesStatus[keyof typeof ListSitesStatus];


export const ListSitesStatus = {
  active: 'active',
  inactive: 'inactive',
} as const;

export type ListSitesIncludeInactive = typeof ListSitesIncludeInactive[keyof typeof ListSitesIncludeInactive];


export const ListSitesIncludeInactive = {
  true: 'true',
  false: 'false',
} as const;

export type ListMySiteInductionsParams = {
/**
 * When set, return status for this site only
 */
siteId?: string;
};

export type ListBuildingsParams = {
siteId?: string;
status?: ListBuildingsStatus;
includeInactive?: ListBuildingsIncludeInactive;
};

export type ListBuildingsStatus = typeof ListBuildingsStatus[keyof typeof ListBuildingsStatus];


export const ListBuildingsStatus = {
  active: 'active',
  inactive: 'inactive',
} as const;

export type ListBuildingsIncludeInactive = typeof ListBuildingsIncludeInactive[keyof typeof ListBuildingsIncludeInactive];


export const ListBuildingsIncludeInactive = {
  true: 'true',
  false: 'false',
} as const;

export type ListFloorsParams = {
buildingId?: string;
status?: ListFloorsStatus;
includeInactive?: ListFloorsIncludeInactive;
};

export type ListFloorsStatus = typeof ListFloorsStatus[keyof typeof ListFloorsStatus];


export const ListFloorsStatus = {
  active: 'active',
  inactive: 'inactive',
} as const;

export type ListFloorsIncludeInactive = typeof ListFloorsIncludeInactive[keyof typeof ListFloorsIncludeInactive];


export const ListFloorsIncludeInactive = {
  true: 'true',
  false: 'false',
} as const;

export type ListZonesParams = {
floorId?: string;
status?: ListZonesStatus;
includeInactive?: ListZonesIncludeInactive;
};

export type ListZonesStatus = typeof ListZonesStatus[keyof typeof ListZonesStatus];


export const ListZonesStatus = {
  active: 'active',
  inactive: 'inactive',
} as const;

export type ListZonesIncludeInactive = typeof ListZonesIncludeInactive[keyof typeof ListZonesIncludeInactive];


export const ListZonesIncludeInactive = {
  true: 'true',
  false: 'false',
} as const;

export type ListSpaceLayoutsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListSpacesParams = {
siteId?: string;
buildingId?: string;
floorId?: string;
zoneId?: string;
spaceTypeId?: string;
status?: ListSpacesStatus;
includeRetired?: ListSpacesIncludeRetired;
includeInactive?: ListSpacesIncludeInactive;
/**
 * @maxLength 120
 */
q?: string;
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListSpacesStatus = typeof ListSpacesStatus[keyof typeof ListSpacesStatus];


export const ListSpacesStatus = {
  active: 'active',
  inactive: 'inactive',
  retired: 'retired',
} as const;

export type ListSpacesIncludeRetired = typeof ListSpacesIncludeRetired[keyof typeof ListSpacesIncludeRetired];


export const ListSpacesIncludeRetired = {
  true: 'true',
  false: 'false',
} as const;

export type ListSpacesIncludeInactive = typeof ListSpacesIncludeInactive[keyof typeof ListSpacesIncludeInactive];


export const ListSpacesIncludeInactive = {
  true: 'true',
  false: 'false',
} as const;

export type ListBookingPoliciesParams = {
scopeKind?: PolicyScopeKind;
scopeId?: string;
spaceTypeId?: string;
enabled?: ListBookingPoliciesEnabled;
};

export type ListBookingPoliciesEnabled = typeof ListBookingPoliciesEnabled[keyof typeof ListBookingPoliciesEnabled];


export const ListBookingPoliciesEnabled = {
  true: 'true',
  false: 'false',
} as const;

export type ListBlackoutsParams = {
scopeKind?: BlackoutScopeKind;
scopeId?: string;
from?: string;
to?: string;
};

export type UploadFileBody = {
  file: Blob | File;
};

export type SearchAvailabilityParams = {
start: string;
end: string;
siteId?: string;
buildingId?: string;
floorId?: string;
zoneId?: string;
/**
 * Comma-separated space type codes
 */
typeCodes?: string;
/**
 * Comma-separated amenity codes (all required)
 */
amenityCodes?: string;
/**
 * Comma-separated space tags (all required, case-insensitive). Quiet/focus rooms use tag `quiet` (Exchange Set-Place Focus/Quiet model).
 */
tags?: string;
/**
 * @minimum 1
 */
minCapacity?: number;
accessibilityOnly?: SearchAvailabilityAccessibilityOnly;
bookForUserId?: string;
includeUnavailable?: SearchAvailabilityIncludeUnavailable;
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
hasVehicle?: SearchAvailabilityHasVehicle;
hasPermit?: SearchAvailabilityHasPermit;
};

export type SearchAvailabilityAccessibilityOnly = typeof SearchAvailabilityAccessibilityOnly[keyof typeof SearchAvailabilityAccessibilityOnly];


export const SearchAvailabilityAccessibilityOnly = {
  true: true,
  false: false,
} as const;

export type SearchAvailabilityIncludeUnavailable = typeof SearchAvailabilityIncludeUnavailable[keyof typeof SearchAvailabilityIncludeUnavailable];


export const SearchAvailabilityIncludeUnavailable = {
  true: true,
  false: false,
} as const;

export type SearchAvailabilityHasVehicle = typeof SearchAvailabilityHasVehicle[keyof typeof SearchAvailabilityHasVehicle];


export const SearchAvailabilityHasVehicle = {
  true: true,
  false: false,
} as const;

export type SearchAvailabilityHasPermit = typeof SearchAvailabilityHasPermit[keyof typeof SearchAvailabilityHasPermit];


export const SearchAvailabilityHasPermit = {
  true: true,
  false: false,
} as const;

export type ListBookingsParams = {
spaceId?: string;
status?: BookingStatus;
from?: string;
to?: string;
mine?: ListBookingsMine;
/**
 * Page size for the bounded result set (default 200).
 * @minimum 1
 * @maximum 200
 */
limit?: number;
/**
 * Number of bookings to skip, for paging through entity history.
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
};

export type ListBookingsMine = typeof ListBookingsMine[keyof typeof ListBookingsMine];


export const ListBookingsMine = {
  true: true,
  false: false,
} as const;

export type TakeOverBookingBody = {
  layoutId?: string;
};

export type ListDirectoryUsersParams = {
q?: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
};

export type GetUtilisationReportParams = {
from: ReportFromParameter;
to: ReportToParameter;
siteId?: ReportSiteIdParameter;
buildingId?: ReportBuildingIdParameter;
typeCode?: ReportTypeCodeParameter;
/**
 * Response format. csv returns a downloadable attachment of the row data.
 */
format?: ReportFormatParameter;
/**
 * Collapse results to the requested granularity.
 */
groupBy?: GetUtilisationReportGroupBy;
};

export type GetUtilisationReportGroupBy = typeof GetUtilisationReportGroupBy[keyof typeof GetUtilisationReportGroupBy];


export const GetUtilisationReportGroupBy = {
  site: 'site',
  building: 'building',
  floor: 'floor',
  type: 'type',
} as const;

export type GetUtilisationTimeseriesParams = {
from: ReportFromParameter;
to: ReportToParameter;
siteId?: ReportSiteIdParameter;
buildingId?: ReportBuildingIdParameter;
typeCode?: ReportTypeCodeParameter;
/**
 * Response format. csv returns a downloadable attachment of the row data.
 */
format?: ReportFormatParameter;
/**
 * UTC calendar period; every site's denominator uses its local opening schedule.
 */
period?: GetUtilisationTimeseriesPeriod;
/**
 * National values sum eligible durations before division.
 */
scope?: GetUtilisationTimeseriesScope;
};

export type GetUtilisationTimeseriesPeriod = typeof GetUtilisationTimeseriesPeriod[keyof typeof GetUtilisationTimeseriesPeriod];


export const GetUtilisationTimeseriesPeriod = {
  day: 'day',
  month: 'month',
  quarter: 'quarter',
} as const;

export type GetUtilisationTimeseriesScope = typeof GetUtilisationTimeseriesScope[keyof typeof GetUtilisationTimeseriesScope];


export const GetUtilisationTimeseriesScope = {
  site: 'site',
  national: 'national',
} as const;

export type GetNoShowReportParams = {
from: ReportFromParameter;
to: ReportToParameter;
siteId?: ReportSiteIdParameter;
buildingId?: ReportBuildingIdParameter;
typeCode?: ReportTypeCodeParameter;
/**
 * Response format. csv returns a downloadable attachment of the row data.
 */
format?: ReportFormatParameter;
};

export type GetIntentVsActualReportParams = {
from: ReportFromParameter;
to: ReportToParameter;
siteId?: ReportSiteIdParameter;
buildingId?: ReportBuildingIdParameter;
typeCode?: ReportTypeCodeParameter;
/**
 * Response format. csv returns a downloadable attachment of the row data.
 */
format?: ReportFormatParameter;
};

export type GetManagerAttendanceReportParams = {
siteId: string;
from: string;
to: string;
staffId?: string;
/**
 * @minimum 1
 * @maximum 25
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
/**
 * Response format. csv returns a downloadable attachment of the row data.
 */
format?: ReportFormatParameter;
};

export type GetPeakReportParams = {
from: ReportFromParameter;
to: ReportToParameter;
siteId?: ReportSiteIdParameter;
buildingId?: ReportBuildingIdParameter;
typeCode?: ReportTypeCodeParameter;
/**
 * Response format. csv returns a downloadable attachment of the row data.
 */
format?: ReportFormatParameter;
};

export type GetUnderutilisedReportParams = {
from: ReportFromParameter;
to: ReportToParameter;
siteId?: ReportSiteIdParameter;
buildingId?: ReportBuildingIdParameter;
typeCode?: ReportTypeCodeParameter;
/**
 * Response format. csv returns a downloadable attachment of the row data.
 */
format?: ReportFormatParameter;
/**
 * Occupancy % below which a space is considered underutilised
 * @minimum 0
 * @maximum 100
 */
thresholdPct?: number;
};

export type ListAuditEventsParams = {
entityType?: string;
entityId?: string;
actorUserId?: string;
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
};

export type GetMyPresencePlanParams = {
/**
 * Inclusive start date (YYYY-MM-DD)
 */
from: string;
/**
 * Inclusive end date (YYYY-MM-DD)
 */
to: string;
};

export type ListMyTimedPlansParams = {
from: string;
to: string;
};

export type ListMyLocationNeedsParams = {
siteId?: string;
};

export type PutMyWorkplaceProfileBody = {
  /** @nullable */
  defaultSiteId: string | null;
};

export type SearchWorkplaceZonesParams = {
/**
 * @maxLength 100
 */
q?: string;
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type SearchWorkplaceZones200 = {
  items: WorkplaceZone[];
  total: number;
};

export type PutUserWorkplaceProfileBody = {
  /** @maxItems 20 */
  assignedZoneIds: string[];
  /** @nullable */
  primaryZoneId: string | null;
};

export type ListMyNotificationsParams = {
unreadOnly?: boolean;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
};

export type MarkAllMyNotificationsRead200 = {
  ok: boolean;
};

export type ListHostVisitorApprovalsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListMyVisitorApprovalsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type SearchVisitorAvailabilityParams = {
start: string;
end: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetVisitorPolicyRequirementsParams = {
spaceId: string;
};

export type GetVisitorInduction200 = {
  checklist: SiteInductionChecklist;
  status: UserSiteInductionStatus;
};

export type ListVisitsParams = {
mine?: boolean;
today?: boolean;
bookingId?: string;
hostUserId?: string;
/**
 * Comma-separated visitor visit statuses
 */
status?: string;
from?: string;
to?: string;
/**
 * Search visitor name, email, or company
 * @maxLength 100
 */
q?: string;
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
format?: ListVisitsFormat;
};

export type ListVisitsFormat = typeof ListVisitsFormat[keyof typeof ListVisitsFormat];


export const ListVisitsFormat = {
  json: 'json',
  csv: 'csv',
} as const;

export type ListAdminVisitsParams = {
today?: boolean;
bookingId?: string;
hostUserId?: string;
status?: string;
from?: string;
to?: string;
/**
 * @maxLength 100
 */
q?: string;
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
format?: ListAdminVisitsFormat;
};

export type ListAdminVisitsFormat = typeof ListAdminVisitsFormat[keyof typeof ListAdminVisitsFormat];


export const ListAdminVisitsFormat = {
  json: 'json',
  csv: 'csv',
} as const;

export type ListMyFavouritesParams = {
/**
 * Window start (ISO-8601). When both start and end are set, items include available + reasons.
 */
start?: string;
/**
 * Window end (ISO-8601, exclusive).
 */
end?: string;
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
};

export type ListMyBookAgainParams = {
/**
 * Window start (ISO-8601). When both start and end are set, items include available + reasons.
 */
start?: string;
/**
 * Window end (ISO-8601, exclusive).
 */
end?: string;
/**
 * @minimum 1
 * @maximum 20
 */
limit?: number;
};

export type ListMyBookingSuggestionsParams = {
/**
 * @minimum 1
 * @maximum 20
 */
limit?: number;
/**
 * When true, annotates each next-slot with availability.
 */
checkAvailability?: boolean;
/**
 * Optional search window start to bias ranking toward matching weekday/hour.
 */
forStart?: string;
forEnd?: string;
};

export type GetWorkgroupWeekParams = {
from: string;
to: string;
};

export type GetWorkgroupBookingsParams = {
from: string;
to: string;
};

export type GetUsersPresenceParams = {
/**
 * Comma-separated user UUIDs (max 50)
 */
userIds: string;
from: string;
to: string;
};

export type SearchColleaguesParams = {
/**
 * Name or email search text
 * @minLength 1
 * @maxLength 120
 */
q: string;
/**
 * Day used for today status and same-building (defaults to local today)
 */
date?: string;
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
};

export type GetColleagueWeekParams = {
/**
 * Week start (YYYY-MM-DD). Defaults to Monday of the date week.
 */
from?: string;
/**
 * Week end (YYYY-MM-DD). Defaults to Sunday of the date week.
 */
to?: string;
/**
 * Day used for today fields / same-building (defaults to local today)
 */
date?: string;
};

export type ListVisibleColleagueDesksParams = {
at: string;
floorId?: string;
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type AddNominationBody = {
  colleagueUserId: string;
};

export type GetNominatedColleagueBookingsParams = {
/**
 * Window start (YYYY-MM-DD). Defaults to local today.
 */
from?: string;
/**
 * Window end (YYYY-MM-DD). Defaults to 6 days after from.
 */
to?: string;
};

export type ListFloorBookingsParams = {
from: string;
to: string;
spaceId?: string;
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListFloorBookings200ItemsItem = {
  spaceId: string;
  spaceName: string;
  start: string;
  end: string;
  status: string;
  /** @nullable */
  bookerName: string | null;
};

export type ListFloorBookings200 = {
  items: ListFloorBookings200ItemsItem[];
  limit: number;
  offset: number;
};

export type UpsertFloorPlanBody = {
  /** PNG, JPEG, or SVG up to 5 MB */
  file: Blob | File;
  /**
     * Natural image width in pixels
     * @minimum 1
     * @maximum 20000
     */
  width?: number;
  /**
     * Natural image height in pixels
     * @minimum 1
     * @maximum 20000
     */
  height?: number;
};

export type DeleteFloorPlan200 = {
  ok: boolean;
};

export type GetFloorTwinStatusParams = {
start: string;
end: string;
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetInsightsOverviewParams = {
from?: string;
to?: string;
siteId?: string;
buildingId?: string;
typeCode?: string;
};

export type GenerateWeeklySummaryParams = {
from?: string;
to?: string;
siteId?: string;
buildingId?: string;
typeCode?: string;
};

export type PreviewInventoryImportBody = {
  /**
     * @minLength 1
     * @maxLength 2000000
     */
  csv: string;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit?: number;
  /** @minimum 0 */
  offset?: number;
};

export type CommitInventoryImportBody = {
  /**
     * @minLength 1
     * @maxLength 2000000
     */
  csv: string;
  /**
     * @minimum 1
     * @maximum 100
     */
  limit?: number;
  /** @minimum 0 */
  offset?: number;
};

export type GetInventoryImportResultParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ExportBookingsCsvParams = {
from: string;
to: string;
siteId?: string;
buildingId?: string;
typeCode?: string;
};

export type GetApiPlatformAdminTenantsParams = {
status?: GetApiPlatformAdminTenantsStatus;
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetApiPlatformAdminTenantsStatus = typeof GetApiPlatformAdminTenantsStatus[keyof typeof GetApiPlatformAdminTenantsStatus];


export const GetApiPlatformAdminTenantsStatus = {
  active: 'active',
  suspended: 'suspended',
  archived: 'archived',
} as const;

export type GetApiPlatformAdminTenants200 = {
  items: PlatformTenantListItem[];
  limit: number;
  offset: number;
  hasMore: boolean;
};

export type PostApiPlatformAdminTenantsBodySettings = { [key: string]: unknown };

export type PostApiPlatformAdminTenantsBody = {
  /** @maxLength 200 */
  name: string;
  /**
     * Optional initial friendly alias. The GUID base address is generated automatically.
     * @maxLength 100
     */
  slug?: string;
  settings?: PostApiPlatformAdminTenantsBodySettings;
  initialOwnerEmail: string;
};

export type PatchApiPlatformAdminTenantsTenantIdBody = {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
};

export type PatchApiPlatformAdminTenantsTenantIdSlugBody = {
  /**
     * @minLength 1
     * @maxLength 100
     * @pattern ^[a-z0-9](?:[a-z0-9-]{0,98}[a-z0-9])?$
     */
  slug: string;
};

export type PatchApiPlatformAdminTenantsTenantIdIdentityBody = {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * @minLength 1
     * @maxLength 100
     * @pattern ^[a-z0-9](?:[a-z0-9-]{0,98}[a-z0-9])?$
     */
  slug: string;
  expectedTenantUpdatedAt: string;
  expectedEntityUpdatedAt: string;
};

export type GetHomeWebpartOptionsParams = {
scope?: GetHomeWebpartOptionsScope;
kind: GetHomeWebpartOptionsKind;
/**
 * @maxLength 100
 */
search?: string;
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 100000
 */
offset?: number;
};

export type GetHomeWebpartOptionsScope = typeof GetHomeWebpartOptionsScope[keyof typeof GetHomeWebpartOptionsScope];


export const GetHomeWebpartOptionsScope = {
  tenant: 'tenant',
  entity: 'entity',
} as const;

export type GetHomeWebpartOptionsKind = typeof GetHomeWebpartOptionsKind[keyof typeof GetHomeWebpartOptionsKind];


export const GetHomeWebpartOptionsKind = {
  site: 'site',
  type: 'type',
} as const;

export type GetHomeWebpartOptions200ItemsItemStatus = typeof GetHomeWebpartOptions200ItemsItemStatus[keyof typeof GetHomeWebpartOptions200ItemsItemStatus];


export const GetHomeWebpartOptions200ItemsItemStatus = {
  active: 'active',
  inactive: 'inactive',
} as const;

export type GetHomeWebpartOptions200ItemsItem = {
  id: string;
  label: string;
  code: string;
  status: GetHomeWebpartOptions200ItemsItemStatus;
};

export type GetHomeWebpartOptions200 = {
  /** @maxItems 50 */
  items: GetHomeWebpartOptions200ItemsItem[];
  /** @minimum 0 */
  total: number;
};

export type GetApiTenantsTenantSlugHomeWebpartsParams = {
scope?: GetApiTenantsTenantSlugHomeWebpartsScope;
};

export type GetApiTenantsTenantSlugHomeWebpartsScope = typeof GetApiTenantsTenantSlugHomeWebpartsScope[keyof typeof GetApiTenantsTenantSlugHomeWebpartsScope];


export const GetApiTenantsTenantSlugHomeWebpartsScope = {
  tenant: 'tenant',
  entity: 'entity',
} as const;

export type PutApiTenantsTenantSlugHomeWebpartsParams = {
scope?: PutApiTenantsTenantSlugHomeWebpartsScope;
};

export type PutApiTenantsTenantSlugHomeWebpartsScope = typeof PutApiTenantsTenantSlugHomeWebpartsScope[keyof typeof PutApiTenantsTenantSlugHomeWebpartsScope];


export const PutApiTenantsTenantSlugHomeWebpartsScope = {
  tenant: 'tenant',
  entity: 'entity',
} as const;

/**
 * @nullable
 */
export type GetApiPlatformAdminTenantsTenantIdSettings200Settings = { [key: string]: unknown } | null;

export type GetApiPlatformAdminTenantsTenantIdSettings200 = {
  /** @nullable */
  settings: GetApiPlatformAdminTenantsTenantIdSettings200Settings;
};

export type PatchApiPlatformAdminTenantsTenantIdSettingsBodySettings = { [key: string]: unknown };

export type PatchApiPlatformAdminTenantsTenantIdSettingsBody = {
  settings: PatchApiPlatformAdminTenantsTenantIdSettingsBodySettings;
};

export type PatchApiPlatformAdminTenantsTenantIdSettings200Settings = { [key: string]: unknown };

export type PatchApiPlatformAdminTenantsTenantIdSettings200 = {
  settings: PatchApiPlatformAdminTenantsTenantIdSettings200Settings;
};

export type GetApiPlatformAdminTenantsTenantIdEntitiesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasBody = {
  /**
     * @minLength 1
     * @maxLength 100
     * @nullable
     * @pattern ^[a-z0-9](?:[a-z0-9-]{0,98}[a-z0-9])?$
     */
  alias: string | null;
};

export type GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRolesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles200 = {
  items: TenantRoleDetail[];
  limit: number;
  offset: number;
  hasMore: boolean;
};

export type GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidatesParams = {
/**
 * @maxLength 120
 */
search?: string;
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
};

export type PostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersBody = {
  /** @maxLength 320 */
  email: string;
  /** Selected registered account; must still match the email at save time */
  userId?: string;
  /**
     * @minItems 1
     * @maxItems 100
     */
  roleIds: string[];
};

export type GetApiPlatformAdminAdmins200 = {
  items: PlatformAdmin[];
};

export type PostApiPlatformAdminAdminsBody = {
  email: string;
  /** Tenant-local Entra object ID returned by administrator-initiated directory lookup */
  objectId?: string;
  /**
     * Directory display name; required with objectId
     * @minLength 1
     * @maxLength 200
     */
  displayName?: string;
};

export type PostApiInvitesAcceptBody = {
  /** Single-use invite token. */
  token: string;
};

export type PostApiInvitesAccept200 = {
  accepted: boolean;
  organizationId: string;
  slug: string;
  name: string;
};

export type GetApiTenantsTenantSlugEntitiesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type PostApiTenantsTenantSlugEntitiesBody = {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /**
     * Optional friendly alias; GUID base is generated.
     * @minLength 1
     * @maxLength 100
     */
  slug?: string;
};

export type PostApiTenantsTenantSlugEntityLifecycleBodyAction = typeof PostApiTenantsTenantSlugEntityLifecycleBodyAction[keyof typeof PostApiTenantsTenantSlugEntityLifecycleBodyAction];


export const PostApiTenantsTenantSlugEntityLifecycleBodyAction = {
  suspend: 'suspend',
  restore: 'restore',
} as const;

export type PostApiTenantsTenantSlugEntityLifecycleBody = {
  action: PostApiTenantsTenantSlugEntityLifecycleBodyAction;
  /**
     * @minLength 10
     * @maxLength 2000
     */
  reason: string;
};

export type GetApiTenantsTenantSlugMemberCandidatesParams = {
/**
 * @maxLength 120
 */
search?: string;
/**
 * @minimum 1
 * @maximum 50
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
};

export type SearchOrganisationDirectoryGroupsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minLength 1
 * @maxLength 2048
 */
cursor?: string;
/**
 * @minLength 2
 * @maxLength 100
 */
search: string;
};

export type ListOrganisationGroupRolesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 * @maximum 10000
 */
offset?: number;
};

export type ListOrganisationGroupRoles200 = {
  /** @maxItems 100 */
  items: TenantRoleDetail[];
  /**
     * @minimum 1
     * @maximum 100
     */
  limit: number;
  /**
     * @minimum 0
     * @maximum 10000
     */
  offset: number;
  hasMore: boolean;
};

export type ListOrganisationGroupBindingsParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minLength 1
 * @maxLength 2048
 */
cursor?: string;
};

export type RemoveOrganisationGroupBindingParams = {
/**
 * @minimum 1
 * @maximum 2147483647
 */
revision: number;
};

export type GetOrganisationMemberAccessSourcesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minLength 1
 * @maxLength 2048
 */
cursor?: string;
};

export type GetApiTenantsTenantSlugMembersParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetApiTenantsTenantSlugMembers200 = {
  items: TenantMember[];
  limit: number;
  offset: number;
  hasMore: boolean;
};

export type PostApiTenantsTenantSlugMembersBody = {
  /** @maxLength 320 */
  email: string;
  /** Selected registered account; must still match the email at save time */
  userId?: string;
  /**
     * @minItems 1
     * @maxItems 100
     */
  roleIds: string[];
};

export type PatchApiTenantsTenantSlugMembersMemberIdBody = {
  /** @minItems 1 */
  roleIds: string[];
};

export type GetApiTenantsTenantSlugInvitesParams = {
/**
 * @minimum 1
 * @maximum 100
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetApiTenantsTenantSlugInvites200 = {
  items: TenantInvite[];
  limit: number;
  offset: number;
  hasMore: boolean;
};

export type PostApiTenantsTenantSlugInvitesBody = {
  email: string;
  roleId: string;
};

export type PostApiTenantsTenantSlugInvites201 = {
  invite: TenantInvite;
  /** Single-use invite token to share with the invitee. */
  token: string;
};

export type GetApiTenantsTenantSlugRoles200 = {
  items: TenantRoleDetail[];
};

export type PostApiTenantsTenantSlugRolesBody = {
  /**
     * @minLength 1
     * @maxLength 64
     */
  key: string;
  /**
     * @minLength 1
     * @maxLength 200
     */
  name: string;
  /** @maxLength 2000 */
  description?: string;
  permissionKeys?: string[];
};

/**
 * @nullable
 */
export type GetApiTenantsTenantSlugSettings200Settings = { [key: string]: unknown } | null;

export type GetApiTenantsTenantSlugSettings200 = {
  /** @nullable */
  settings: GetApiTenantsTenantSlugSettings200Settings;
};

export type PatchApiTenantsTenantSlugSettingsBodySettings = { [key: string]: unknown };

export type PatchApiTenantsTenantSlugSettingsBody = {
  settings: PatchApiTenantsTenantSlugSettingsBodySettings;
};

export type PatchApiTenantsTenantSlugSettings200Settings = { [key: string]: unknown };

export type PatchApiTenantsTenantSlugSettings200 = {
  settings: PatchApiTenantsTenantSlugSettings200Settings;
};

export type PutApiTenantsTenantSlugCustomerLogoBodyScope = typeof PutApiTenantsTenantSlugCustomerLogoBodyScope[keyof typeof PutApiTenantsTenantSlugCustomerLogoBodyScope];


export const PutApiTenantsTenantSlugCustomerLogoBodyScope = {
  tenant: 'tenant',
  entity: 'entity',
} as const;

export type PutApiTenantsTenantSlugCustomerLogoBodyContentType = typeof PutApiTenantsTenantSlugCustomerLogoBodyContentType[keyof typeof PutApiTenantsTenantSlugCustomerLogoBodyContentType];


export const PutApiTenantsTenantSlugCustomerLogoBodyContentType = {
  'image/png': 'image/png',
} as const;

export type PutApiTenantsTenantSlugCustomerLogoBody = {
  scope: PutApiTenantsTenantSlugCustomerLogoBodyScope;
  contentType: PutApiTenantsTenantSlugCustomerLogoBodyContentType;
  /** @maxLength 350000 */
  imageBase64: string;
  /**
     * @minLength 1
     * @maxLength 120
     */
  altText: string;
};

export type DeleteApiTenantsTenantSlugCustomerLogoParams = {
scope: DeleteApiTenantsTenantSlugCustomerLogoScope;
};

export type DeleteApiTenantsTenantSlugCustomerLogoScope = typeof DeleteApiTenantsTenantSlugCustomerLogoScope[keyof typeof DeleteApiTenantsTenantSlugCustomerLogoScope];


export const DeleteApiTenantsTenantSlugCustomerLogoScope = {
  tenant: 'tenant',
  entity: 'entity',
} as const;

export type GetApiTenantsTenantSlugAuditParams = {
action?: string;
actorUserId?: string;
targetType?: string;
targetId?: string;
from?: string;
to?: string;
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetApiTenantsTenantSlugAudit200 = {
  items: TenancyAuditEvent[];
  limit: number;
  offset: number;
};

export type GetApiTenantsTenantSlugDirectorySearchParams = {
/**
 * Search string (minimum 2 characters, maximum 100)
 * @minLength 2
 * @maxLength 100
 */
q: string;
/**
 * @minimum 1
 * @maximum 25
 */
limit?: number;
};

export type PatchApiTenantsTenantSlugRolesRoleIdBody = {
  /**
     * @minLength 1
     * @maxLength 200
     */
  name?: string;
  /**
     * @maxLength 2000
     * @nullable
     */
  description?: string | null;
  permissionKeys?: string[];
};

export type GetApiTenantsTenantSlugElevationsParams = {
status?: GetApiTenantsTenantSlugElevationsStatus;
requesterId?: string;
pendingApprovals?: boolean;
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type GetApiTenantsTenantSlugElevationsStatus = typeof GetApiTenantsTenantSlugElevationsStatus[keyof typeof GetApiTenantsTenantSlugElevationsStatus];


export const GetApiTenantsTenantSlugElevationsStatus = {
  pending: 'pending',
  approved: 'approved',
  denied: 'denied',
  expired: 'expired',
} as const;

export type PostApiTenantsTenantSlugElevationsBody = {
  roleId?: string;
  permissionKeys?: string[];
  /**
     * @minLength 10
     * @maxLength 2000
     */
  reason: string;
  /**
     * @minimum 1
     * @maximum 8
     */
  durationHours: number;
};

export type PostApiTenantsTenantSlugElevationsElevationIdResolveBody = {
  approve: boolean;
  /**
     * @minimum 1
     * @maximum 8
     */
  durationHours?: number;
  /** @maxLength 2000 */
  deniedReason?: string;
};

export type PostApiPlatformAdminOverrideBody = {
  tenantId: string;
  /**
     * @minLength 10
     * @maxLength 2000
     */
  reason: string;
  /**
     * @minimum 1
     * @maximum 4
     */
  durationHours: number;
};

export type GetApiPlatformAdminOverrideParams = {
tenantId?: string;
activeOnly?: boolean;
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type PostApiPlatformAdminOverrideEndBody = {
  tenantId?: string;
  overrideId?: string;
};

export type GetApiPlatformAdminAuditParams = {
action?: string;
actorUserId?: string;
organizationId?: string;
targetType?: string;
targetId?: string;
from?: string;
to?: string;
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListCredentialTypesParams = {
enabledOnly?: ListCredentialTypesEnabledOnly;
};

export type ListCredentialTypesEnabledOnly = typeof ListCredentialTypesEnabledOnly[keyof typeof ListCredentialTypesEnabledOnly];


export const ListCredentialTypesEnabledOnly = {
  true: 'true',
  false: 'false',
} as const;

export type ListMyCredentialsParams = {
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListCredentialReviewsParams = {
status?: ListCredentialReviewsStatus;
/**
 * @minimum 1
 * @maximum 200
 */
limit?: number;
/**
 * @minimum 0
 */
offset?: number;
};

export type ListCredentialReviewsStatus = typeof ListCredentialReviewsStatus[keyof typeof ListCredentialReviewsStatus];


export const ListCredentialReviewsStatus = {
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
  revoked: 'revoked',
} as const;

export type ListTenantEmailTemplatesParams = {
scope?: EmailTemplateScopeQueryParameter;
};

export type SaveTenantEmailTemplateParams = {
scope?: EmailTemplateScopeQueryParameter;
};

export type ResetTenantEmailTemplateParams = {
scope?: EmailTemplateScopeQueryParameter;
};

export type UpdateEmailTemplatePermissionsParams = {
/**
 * Permissions belong to the tenant. Entity scope is refused.
 */
scope?: UpdateEmailTemplatePermissionsScope;
};

export type UpdateEmailTemplatePermissionsScope = typeof UpdateEmailTemplatePermissionsScope[keyof typeof UpdateEmailTemplatePermissionsScope];


export const UpdateEmailTemplatePermissionsScope = {
  tenant: 'tenant',
  entity: 'entity',
} as const;

export type PreviewTenantEmailTemplateParams = {
scope?: EmailTemplateScopeQueryParameter;
};

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getGetTenantBackupReadinessUrl = (tenantId: string,) => {




  return `/api/tenants/${tenantId}/backups/readiness`
}

/**
 * Read-only backup readiness. All backup paths are contract-only and unmounted in the foundation.
 */
export const getTenantBackupReadiness = async (tenantId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenantBackupReadiness> => {

  return customFetch<TenantBackupReadiness>(getGetTenantBackupReadinessUrl(tenantId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenantBackupReadinessQueryKey = (tenantId: string,) => {
    return [
    `/api/tenants/${tenantId}/backups/readiness`
    ] as const;
    }


export const getGetTenantBackupReadinessQueryOptions = <TData = Awaited<ReturnType<typeof getTenantBackupReadiness>>, TError = void>(tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTenantBackupReadiness>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenantBackupReadinessQueryKey(tenantId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenantBackupReadiness>>> = ({ signal }) => getTenantBackupReadiness(tenantId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantId !== null && tenantId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenantBackupReadiness>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetTenantBackupReadinessQueryResult = NonNullable<Awaited<ReturnType<typeof getTenantBackupReadiness>>>
export type GetTenantBackupReadinessQueryError = void


export function useGetTenantBackupReadiness<TData = Awaited<ReturnType<typeof getTenantBackupReadiness>>, TError = void>(
 tenantId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTenantBackupReadiness>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTenantBackupReadiness>>,
          TError,
          Awaited<ReturnType<typeof getTenantBackupReadiness>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTenantBackupReadiness<TData = Awaited<ReturnType<typeof getTenantBackupReadiness>>, TError = void>(
 tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTenantBackupReadiness>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTenantBackupReadiness>>,
          TError,
          Awaited<ReturnType<typeof getTenantBackupReadiness>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTenantBackupReadiness<TData = Awaited<ReturnType<typeof getTenantBackupReadiness>>, TError = void>(
 tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTenantBackupReadiness>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetTenantBackupReadiness<TData = Awaited<ReturnType<typeof getTenantBackupReadiness>>, TError = void>(
 tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTenantBackupReadiness>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetTenantBackupReadinessQueryOptions(tenantId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateTenantBackupExportUrl = (tenantId: string,) => {




  return `/api/tenants/${tenantId}/backups/exports`
}

/**
 * Actor-bound idempotent export. Capture all reviewed families in one repeatable-read snapshot, reconcile canonical rows and content-addressed bytes, then atomically publish private chunks. Execution remains gated until the remaining safety gates pass.
 */
export const createTenantBackupExport = async (tenantId: string,
    tenantBackupStart: TenantBackupStart, options?: Parameters<typeof customFetch>[1]): Promise<TenantBackupOperation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenantBackupOperation>(getCreateTenantBackupExportUrl(tenantId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenantBackupStart)
  }
);}





export const getCreateTenantBackupExportMutationKey = () => ['createTenantBackupExport'] as const;

export const getCreateTenantBackupExportMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenantBackupExport>>, TError,CreateTenantBackupExportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createTenantBackupExport>>, TError,CreateTenantBackupExportMutationVariables, TContext> => {

const mutationKey = getCreateTenantBackupExportMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createTenantBackupExport>>, CreateTenantBackupExportMutationVariables> = (props) => {
          const {tenantId,data} = props ?? {};

          return  createTenantBackupExport(tenantId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateTenantBackupExportMutationResult = NonNullable<Awaited<ReturnType<typeof createTenantBackupExport>>>
    export type CreateTenantBackupExportMutationBody = TenantBackupStart
    export type CreateTenantBackupExportMutationError = void
    export type CreateTenantBackupExportMutationVariables = {tenantId: string;data: TenantBackupStart}

    export const useCreateTenantBackupExport = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createTenantBackupExport>>, TError,CreateTenantBackupExportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createTenantBackupExport>>,
        TError,
        CreateTenantBackupExportMutationVariables,
        TContext
      > => {
      return useMutation(getCreateTenantBackupExportMutationOptions(options), queryClient);
    }

export const getUploadTenantBackupUrl = (tenantId: string,) => {




  return `/api/tenants/${tenantId}/backups/imports`
}

/**
 * Private bounded version-1 ZIP32 upload using stored entries only. Reject compression, encryption, descriptors, links, unsafe paths, duplicates, unknown entries and noncanonical JSON before private staging. Maximum archive 256 MiB; authenticate and authorize before multipart parsing. The idempotency key must identify the same archive digest. No business writes.
 */
export const uploadTenantBackup = async (tenantId: string,
    uploadTenantBackupBody: UploadTenantBackupBody, options?: Parameters<typeof customFetch>[1]): Promise<TenantBackupOperation> => {
    const formData = new FormData();
formData.append(`archive`, uploadTenantBackupBody.archive);
formData.append(`idempotencyKey`, uploadTenantBackupBody.idempotencyKey);

  return customFetch<TenantBackupOperation>(getUploadTenantBackupUrl(tenantId),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}





export const getUploadTenantBackupMutationKey = () => ['uploadTenantBackup'] as const;

export const getUploadTenantBackupMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadTenantBackup>>, TError,UploadTenantBackupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadTenantBackup>>, TError,UploadTenantBackupMutationVariables, TContext> => {

const mutationKey = getUploadTenantBackupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadTenantBackup>>, UploadTenantBackupMutationVariables> = (props) => {
          const {tenantId,data} = props ?? {};

          return  uploadTenantBackup(tenantId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadTenantBackupMutationResult = NonNullable<Awaited<ReturnType<typeof uploadTenantBackup>>>
    export type UploadTenantBackupMutationBody = UploadTenantBackupBody
    export type UploadTenantBackupMutationError = void
    export type UploadTenantBackupMutationVariables = {tenantId: string;data: UploadTenantBackupBody}

    export const useUploadTenantBackup = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadTenantBackup>>, TError,UploadTenantBackupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof uploadTenantBackup>>,
        TError,
        UploadTenantBackupMutationVariables,
        TContext
      > => {
      return useMutation(getUploadTenantBackupMutationOptions(options), queryClient);
    }

export const getFindTenantBackupRequestUrl = (tenantId: string,
    requestKey: string,
    params: FindTenantBackupRequestParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantId}/backups/requests/${requestKey}?${stringifiedParams}` : `/api/tenants/${tenantId}/backups/requests/${requestKey}`
}

/**
 * Read-only recovery after a lost export or upload response. Look up the exact original actor, tenant, kind and request key without reserving, uploading, capturing or replaying anything. A missing operation is not permission to retry automatically.
 */
export const findTenantBackupRequest = async (tenantId: string,
    requestKey: string,
    params: FindTenantBackupRequestParams, options?: Parameters<typeof customFetch>[1]): Promise<TenantBackupOperation> => {

  return customFetch<TenantBackupOperation>(getFindTenantBackupRequestUrl(tenantId,requestKey,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getFindTenantBackupRequestQueryKey = (tenantId: string,
    requestKey: string,
    params?: FindTenantBackupRequestParams,) => {
    return [
    `/api/tenants/${tenantId}/backups/requests/${requestKey}`, ...(params ? [params] : [])
    ] as const;
    }


export const getFindTenantBackupRequestQueryOptions = <TData = Awaited<ReturnType<typeof findTenantBackupRequest>>, TError = void>(tenantId: string,
    requestKey: string,
    params: FindTenantBackupRequestParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof findTenantBackupRequest>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getFindTenantBackupRequestQueryKey(tenantId,requestKey,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof findTenantBackupRequest>>> = ({ signal }) => findTenantBackupRequest(tenantId,requestKey,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantId !== null && tenantId !== undefined && requestKey !== null && requestKey !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof findTenantBackupRequest>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type FindTenantBackupRequestQueryResult = NonNullable<Awaited<ReturnType<typeof findTenantBackupRequest>>>
export type FindTenantBackupRequestQueryError = void


export function useFindTenantBackupRequest<TData = Awaited<ReturnType<typeof findTenantBackupRequest>>, TError = void>(
 tenantId: string,
    requestKey: string,
    params: FindTenantBackupRequestParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof findTenantBackupRequest>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof findTenantBackupRequest>>,
          TError,
          Awaited<ReturnType<typeof findTenantBackupRequest>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useFindTenantBackupRequest<TData = Awaited<ReturnType<typeof findTenantBackupRequest>>, TError = void>(
 tenantId: string,
    requestKey: string,
    params: FindTenantBackupRequestParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof findTenantBackupRequest>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof findTenantBackupRequest>>,
          TError,
          Awaited<ReturnType<typeof findTenantBackupRequest>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useFindTenantBackupRequest<TData = Awaited<ReturnType<typeof findTenantBackupRequest>>, TError = void>(
 tenantId: string,
    requestKey: string,
    params: FindTenantBackupRequestParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof findTenantBackupRequest>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useFindTenantBackupRequest<TData = Awaited<ReturnType<typeof findTenantBackupRequest>>, TError = void>(
 tenantId: string,
    requestKey: string,
    params: FindTenantBackupRequestParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof findTenantBackupRequest>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getFindTenantBackupRequestQueryOptions(tenantId,requestKey,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetTenantBackupOperationUrl = (tenantId: string,
    operationId: string,) => {




  return `/api/tenants/${tenantId}/backups/operations/${operationId}`
}

/**
 * Actor-bound authenticated uncertain-outcome recovery; a status read never retries a write.
 */
export const getTenantBackupOperation = async (tenantId: string,
    operationId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenantBackupOperation> => {

  return customFetch<TenantBackupOperation>(getGetTenantBackupOperationUrl(tenantId,operationId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetTenantBackupOperationQueryKey = (tenantId: string,
    operationId: string,) => {
    return [
    `/api/tenants/${tenantId}/backups/operations/${operationId}`
    ] as const;
    }


export const getGetTenantBackupOperationQueryOptions = <TData = Awaited<ReturnType<typeof getTenantBackupOperation>>, TError = void>(tenantId: string,
    operationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTenantBackupOperation>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetTenantBackupOperationQueryKey(tenantId,operationId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getTenantBackupOperation>>> = ({ signal }) => getTenantBackupOperation(tenantId,operationId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantId !== null && tenantId !== undefined && operationId !== null && operationId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getTenantBackupOperation>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetTenantBackupOperationQueryResult = NonNullable<Awaited<ReturnType<typeof getTenantBackupOperation>>>
export type GetTenantBackupOperationQueryError = void


export function useGetTenantBackupOperation<TData = Awaited<ReturnType<typeof getTenantBackupOperation>>, TError = void>(
 tenantId: string,
    operationId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTenantBackupOperation>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTenantBackupOperation>>,
          TError,
          Awaited<ReturnType<typeof getTenantBackupOperation>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTenantBackupOperation<TData = Awaited<ReturnType<typeof getTenantBackupOperation>>, TError = void>(
 tenantId: string,
    operationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTenantBackupOperation>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTenantBackupOperation>>,
          TError,
          Awaited<ReturnType<typeof getTenantBackupOperation>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetTenantBackupOperation<TData = Awaited<ReturnType<typeof getTenantBackupOperation>>, TError = void>(
 tenantId: string,
    operationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTenantBackupOperation>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetTenantBackupOperation<TData = Awaited<ReturnType<typeof getTenantBackupOperation>>, TError = void>(
 tenantId: string,
    operationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getTenantBackupOperation>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetTenantBackupOperationQueryOptions(tenantId,operationId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCancelTenantBackupOperationUrl = (tenantId: string,
    operationId: string,) => {




  return `/api/tenants/${tenantId}/backups/operations/${operationId}`
}

/**
 * Cancel uncommitted work and expire private staged bytes; never cancel or replay a committed restore.
 */
export const cancelTenantBackupOperation = async (tenantId: string,
    operationId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenantBackupOperation> => {

  return customFetch<TenantBackupOperation>(getCancelTenantBackupOperationUrl(tenantId,operationId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getCancelTenantBackupOperationMutationKey = () => ['cancelTenantBackupOperation'] as const;

export const getCancelTenantBackupOperationMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelTenantBackupOperation>>, TError,CancelTenantBackupOperationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelTenantBackupOperation>>, TError,CancelTenantBackupOperationMutationVariables, TContext> => {

const mutationKey = getCancelTenantBackupOperationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelTenantBackupOperation>>, CancelTenantBackupOperationMutationVariables> = (props) => {
          const {tenantId,operationId} = props ?? {};

          return  cancelTenantBackupOperation(tenantId,operationId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelTenantBackupOperationMutationResult = NonNullable<Awaited<ReturnType<typeof cancelTenantBackupOperation>>>

    export type CancelTenantBackupOperationMutationError = void
    export type CancelTenantBackupOperationMutationVariables = {tenantId: string;operationId: string}

    export const useCancelTenantBackupOperation = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelTenantBackupOperation>>, TError,CancelTenantBackupOperationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof cancelTenantBackupOperation>>,
        TError,
        CancelTenantBackupOperationMutationVariables,
        TContext
      > => {
      return useMutation(getCancelTenantBackupOperationMutationOptions(options), queryClient);
    }

export const getDownloadTenantBackupArchiveUrl = (tenantId: string,
    operationId: string,) => {




  return `/api/tenants/${tenantId}/backups/operations/${operationId}/archive`
}

/**
 * Fresh original-parent administrator authority and exact actor ownership are required. Verify contiguous chunks, total size and archive digest before streaming. Expired or cancelled bytes are unavailable; no public URL or bearer capability.
 */
export const downloadTenantBackupArchive = async (tenantId: string,
    operationId: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadTenantBackupArchiveUrl(tenantId,operationId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadTenantBackupArchiveQueryKey = (tenantId: string,
    operationId: string,) => {
    return [
    `/api/tenants/${tenantId}/backups/operations/${operationId}/archive`
    ] as const;
    }


export const getDownloadTenantBackupArchiveQueryOptions = <TData = Awaited<ReturnType<typeof downloadTenantBackupArchive>>, TError = void>(tenantId: string,
    operationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadTenantBackupArchive>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadTenantBackupArchiveQueryKey(tenantId,operationId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadTenantBackupArchive>>> = ({ signal }) => downloadTenantBackupArchive(tenantId,operationId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantId !== null && tenantId !== undefined && operationId !== null && operationId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadTenantBackupArchive>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type DownloadTenantBackupArchiveQueryResult = NonNullable<Awaited<ReturnType<typeof downloadTenantBackupArchive>>>
export type DownloadTenantBackupArchiveQueryError = void


export function useDownloadTenantBackupArchive<TData = Awaited<ReturnType<typeof downloadTenantBackupArchive>>, TError = void>(
 tenantId: string,
    operationId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadTenantBackupArchive>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadTenantBackupArchive>>,
          TError,
          Awaited<ReturnType<typeof downloadTenantBackupArchive>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useDownloadTenantBackupArchive<TData = Awaited<ReturnType<typeof downloadTenantBackupArchive>>, TError = void>(
 tenantId: string,
    operationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadTenantBackupArchive>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadTenantBackupArchive>>,
          TError,
          Awaited<ReturnType<typeof downloadTenantBackupArchive>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useDownloadTenantBackupArchive<TData = Awaited<ReturnType<typeof downloadTenantBackupArchive>>, TError = void>(
 tenantId: string,
    operationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadTenantBackupArchive>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useDownloadTenantBackupArchive<TData = Awaited<ReturnType<typeof downloadTenantBackupArchive>>, TError = void>(
 tenantId: string,
    operationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadTenantBackupArchive>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getDownloadTenantBackupArchiveQueryOptions(tenantId,operationId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPreviewTenantBackupRestoreUrl = (tenantId: string,
    operationId: string,) => {




  return `/api/tenants/${tenantId}/backups/operations/${operationId}/preview`
}

/**
 * Validate the original-tenant archive against one current read-only snapshot. Return redacted replacement, audit retention, capability and external reconciliation impacts plus blockers. Store a 15-minute actor/tenant/archive/data/authority-bound receipt; this action never restores business records.
 */
export const previewTenantBackupRestore = async (tenantId: string,
    operationId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenantBackupPreview> => {

  return customFetch<TenantBackupPreview>(getPreviewTenantBackupRestoreUrl(tenantId,operationId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPreviewTenantBackupRestoreMutationKey = () => ['previewTenantBackupRestore'] as const;

export const getPreviewTenantBackupRestoreMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewTenantBackupRestore>>, TError,PreviewTenantBackupRestoreMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewTenantBackupRestore>>, TError,PreviewTenantBackupRestoreMutationVariables, TContext> => {

const mutationKey = getPreviewTenantBackupRestoreMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewTenantBackupRestore>>, PreviewTenantBackupRestoreMutationVariables> = (props) => {
          const {tenantId,operationId} = props ?? {};

          return  previewTenantBackupRestore(tenantId,operationId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewTenantBackupRestoreMutationResult = NonNullable<Awaited<ReturnType<typeof previewTenantBackupRestore>>>

    export type PreviewTenantBackupRestoreMutationError = void
    export type PreviewTenantBackupRestoreMutationVariables = {tenantId: string;operationId: string}

    export const usePreviewTenantBackupRestore = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewTenantBackupRestore>>, TError,PreviewTenantBackupRestoreMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof previewTenantBackupRestore>>,
        TError,
        PreviewTenantBackupRestoreMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewTenantBackupRestoreMutationOptions(options), queryClient);
    }

export const getListTenantBackupChangesUrl = (tenantId: string,
    operationId: string,
    params: ListTenantBackupChangesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantId}/backups/operations/${operationId}/changes?${stringifiedParams}` : `/api/tenants/${tenantId}/backups/operations/${operationId}/changes`
}

/**
 * Recompute the redacted change page against a read-only current snapshot and reject changed data, authority, receipt, archive or expiry. Paging never refreshes the receipt or replays a write.
 */
export const listTenantBackupChanges = async (tenantId: string,
    operationId: string,
    params: ListTenantBackupChangesParams, options?: Parameters<typeof customFetch>[1]): Promise<TenantBackupChangesPage> => {

  return customFetch<TenantBackupChangesPage>(getListTenantBackupChangesUrl(tenantId,operationId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenantBackupChangesQueryKey = (tenantId: string,
    operationId: string,
    params?: ListTenantBackupChangesParams,) => {
    return [
    `/api/tenants/${tenantId}/backups/operations/${operationId}/changes`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenantBackupChangesQueryOptions = <TData = Awaited<ReturnType<typeof listTenantBackupChanges>>, TError = void>(tenantId: string,
    operationId: string,
    params: ListTenantBackupChangesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTenantBackupChanges>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenantBackupChangesQueryKey(tenantId,operationId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenantBackupChanges>>> = ({ signal }) => listTenantBackupChanges(tenantId,operationId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantId !== null && tenantId !== undefined && operationId !== null && operationId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenantBackupChanges>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListTenantBackupChangesQueryResult = NonNullable<Awaited<ReturnType<typeof listTenantBackupChanges>>>
export type ListTenantBackupChangesQueryError = void


export function useListTenantBackupChanges<TData = Awaited<ReturnType<typeof listTenantBackupChanges>>, TError = void>(
 tenantId: string,
    operationId: string,
    params: ListTenantBackupChangesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTenantBackupChanges>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTenantBackupChanges>>,
          TError,
          Awaited<ReturnType<typeof listTenantBackupChanges>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTenantBackupChanges<TData = Awaited<ReturnType<typeof listTenantBackupChanges>>, TError = void>(
 tenantId: string,
    operationId: string,
    params: ListTenantBackupChangesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTenantBackupChanges>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTenantBackupChanges>>,
          TError,
          Awaited<ReturnType<typeof listTenantBackupChanges>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTenantBackupChanges<TData = Awaited<ReturnType<typeof listTenantBackupChanges>>, TError = void>(
 tenantId: string,
    operationId: string,
    params: ListTenantBackupChangesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTenantBackupChanges>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListTenantBackupChanges<TData = Awaited<ReturnType<typeof listTenantBackupChanges>>, TError = void>(
 tenantId: string,
    operationId: string,
    params: ListTenantBackupChangesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTenantBackupChanges>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListTenantBackupChangesQueryOptions(tenantId,operationId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getConfirmTenantBackupRestoreUrl = (tenantId: string,
    operationId: string,) => {




  return `/api/tenants/${tenantId}/backups/operations/${operationId}/confirm`
}

/**
 * Same-original-tenant atomic replacement only. Reauthorize and revalidate stored receipt, archive and revisions after a bounded application write barrier and database locks. Commit business replacement, retained audit and restore evidence with durable success together. Repeating the identical confirmation key and binding returns the committed result; altered bindings conflict. On an uncertain response, read operation status before retrying. Blob-backed restoration is blocked without safe publication; never replay providers or notifications.
 */
export const confirmTenantBackupRestore = async (tenantId: string,
    operationId: string,
    tenantBackupConfirmation: TenantBackupConfirmation, options?: Parameters<typeof customFetch>[1]): Promise<TenantBackupOperation> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenantBackupOperation>(getConfirmTenantBackupRestoreUrl(tenantId,operationId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(tenantBackupConfirmation)
  }
);}





export const getConfirmTenantBackupRestoreMutationKey = () => ['confirmTenantBackupRestore'] as const;

export const getConfirmTenantBackupRestoreMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmTenantBackupRestore>>, TError,ConfirmTenantBackupRestoreMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmTenantBackupRestore>>, TError,ConfirmTenantBackupRestoreMutationVariables, TContext> => {

const mutationKey = getConfirmTenantBackupRestoreMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmTenantBackupRestore>>, ConfirmTenantBackupRestoreMutationVariables> = (props) => {
          const {tenantId,operationId,data} = props ?? {};

          return  confirmTenantBackupRestore(tenantId,operationId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmTenantBackupRestoreMutationResult = NonNullable<Awaited<ReturnType<typeof confirmTenantBackupRestore>>>
    export type ConfirmTenantBackupRestoreMutationBody = TenantBackupConfirmation
    export type ConfirmTenantBackupRestoreMutationError = void
    export type ConfirmTenantBackupRestoreMutationVariables = {tenantId: string;operationId: string;data: TenantBackupConfirmation}

    export const useConfirmTenantBackupRestore = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmTenantBackupRestore>>, TError,ConfirmTenantBackupRestoreMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof confirmTenantBackupRestore>>,
        TError,
        ConfirmTenantBackupRestoreMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmTenantBackupRestoreMutationOptions(options), queryClient);
    }

export const getListPolicyCatalogueUrl = (params?: ListPolicyCatalogueParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/policies-and-standards/catalogue?${stringifiedParams}` : `/api/admin/policies-and-standards/catalogue`
}

export const listPolicyCatalogue = async (params?: ListPolicyCatalogueParams, options?: Parameters<typeof customFetch>[1]): Promise<PolicyCataloguePage> => {

  return customFetch<PolicyCataloguePage>(getListPolicyCatalogueUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPolicyCatalogueQueryKey = (params?: ListPolicyCatalogueParams,) => {
    return [
    `/api/admin/policies-and-standards/catalogue`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPolicyCatalogueQueryOptions = <TData = Awaited<ReturnType<typeof listPolicyCatalogue>>, TError = unknown>(params?: ListPolicyCatalogueParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPolicyCatalogue>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPolicyCatalogueQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPolicyCatalogue>>> = ({ signal }) => listPolicyCatalogue(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPolicyCatalogue>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListPolicyCatalogueQueryResult = NonNullable<Awaited<ReturnType<typeof listPolicyCatalogue>>>
export type ListPolicyCatalogueQueryError = unknown


export function useListPolicyCatalogue<TData = Awaited<ReturnType<typeof listPolicyCatalogue>>, TError = unknown>(
 params: undefined |  ListPolicyCatalogueParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPolicyCatalogue>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPolicyCatalogue>>,
          TError,
          Awaited<ReturnType<typeof listPolicyCatalogue>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListPolicyCatalogue<TData = Awaited<ReturnType<typeof listPolicyCatalogue>>, TError = unknown>(
 params?: ListPolicyCatalogueParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPolicyCatalogue>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPolicyCatalogue>>,
          TError,
          Awaited<ReturnType<typeof listPolicyCatalogue>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListPolicyCatalogue<TData = Awaited<ReturnType<typeof listPolicyCatalogue>>, TError = unknown>(
 params?: ListPolicyCatalogueParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPolicyCatalogue>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListPolicyCatalogue<TData = Awaited<ReturnType<typeof listPolicyCatalogue>>, TError = unknown>(
 params?: ListPolicyCatalogueParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPolicyCatalogue>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListPolicyCatalogueQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSavePolicyCatalogueRuleUrl = () => {




  return `/api/admin/policies-and-standards/catalogue`
}

export const savePolicyCatalogueRule = async (savePolicyCatalogueRuleBody: SavePolicyCatalogueRule, options?: Parameters<typeof customFetch>[1]): Promise<PolicyRule> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PolicyRule>(getSavePolicyCatalogueRuleUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(savePolicyCatalogueRuleBody)
  }
);}





export const getSavePolicyCatalogueRuleMutationKey = () => ['savePolicyCatalogueRule'] as const;

export const getSavePolicyCatalogueRuleMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof savePolicyCatalogueRule>>, TError,SavePolicyCatalogueRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof savePolicyCatalogueRule>>, TError,SavePolicyCatalogueRuleMutationVariables, TContext> => {

const mutationKey = getSavePolicyCatalogueRuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof savePolicyCatalogueRule>>, SavePolicyCatalogueRuleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  savePolicyCatalogueRule(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SavePolicyCatalogueRuleMutationResult = NonNullable<Awaited<ReturnType<typeof savePolicyCatalogueRule>>>
    export type SavePolicyCatalogueRuleMutationBody = SavePolicyCatalogueRule
    export type SavePolicyCatalogueRuleMutationError = void
    export type SavePolicyCatalogueRuleMutationVariables = {data: SavePolicyCatalogueRule}

    export const useSavePolicyCatalogueRule = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof savePolicyCatalogueRule>>, TError,SavePolicyCatalogueRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof savePolicyCatalogueRule>>,
        TError,
        SavePolicyCatalogueRuleMutationVariables,
        TContext
      > => {
      return useMutation(getSavePolicyCatalogueRuleMutationOptions(options), queryClient);
    }

export const getListPolicyRuleVersionsUrl = (id: string,
    params?: ListPolicyRuleVersionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/policies-and-standards/catalogue/${id}/versions?${stringifiedParams}` : `/api/admin/policies-and-standards/catalogue/${id}/versions`
}

export const listPolicyRuleVersions = async (id: string,
    params?: ListPolicyRuleVersionsParams, options?: Parameters<typeof customFetch>[1]): Promise<PolicyCataloguePage> => {

  return customFetch<PolicyCataloguePage>(getListPolicyRuleVersionsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPolicyRuleVersionsQueryKey = (id: string,
    params?: ListPolicyRuleVersionsParams,) => {
    return [
    `/api/admin/policies-and-standards/catalogue/${id}/versions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPolicyRuleVersionsQueryOptions = <TData = Awaited<ReturnType<typeof listPolicyRuleVersions>>, TError = unknown>(id: string,
    params?: ListPolicyRuleVersionsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPolicyRuleVersions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPolicyRuleVersionsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPolicyRuleVersions>>> = ({ signal }) => listPolicyRuleVersions(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPolicyRuleVersions>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListPolicyRuleVersionsQueryResult = NonNullable<Awaited<ReturnType<typeof listPolicyRuleVersions>>>
export type ListPolicyRuleVersionsQueryError = unknown


export function useListPolicyRuleVersions<TData = Awaited<ReturnType<typeof listPolicyRuleVersions>>, TError = unknown>(
 id: string,
    params: undefined |  ListPolicyRuleVersionsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPolicyRuleVersions>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPolicyRuleVersions>>,
          TError,
          Awaited<ReturnType<typeof listPolicyRuleVersions>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListPolicyRuleVersions<TData = Awaited<ReturnType<typeof listPolicyRuleVersions>>, TError = unknown>(
 id: string,
    params?: ListPolicyRuleVersionsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPolicyRuleVersions>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPolicyRuleVersions>>,
          TError,
          Awaited<ReturnType<typeof listPolicyRuleVersions>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListPolicyRuleVersions<TData = Awaited<ReturnType<typeof listPolicyRuleVersions>>, TError = unknown>(
 id: string,
    params?: ListPolicyRuleVersionsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPolicyRuleVersions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListPolicyRuleVersions<TData = Awaited<ReturnType<typeof listPolicyRuleVersions>>, TError = unknown>(
 id: string,
    params?: ListPolicyRuleVersionsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPolicyRuleVersions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListPolicyRuleVersionsQueryOptions(id,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListPolicyScopeRulesUrl = (params: ListPolicyScopeRulesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/policies-and-standards/assignments?${stringifiedParams}` : `/api/admin/policies-and-standards/assignments`
}

export const listPolicyScopeRules = async (params: ListPolicyScopeRulesParams, options?: Parameters<typeof customFetch>[1]): Promise<PolicyScopeRulesPage> => {

  return customFetch<PolicyScopeRulesPage>(getListPolicyScopeRulesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPolicyScopeRulesQueryKey = (params?: ListPolicyScopeRulesParams,) => {
    return [
    `/api/admin/policies-and-standards/assignments`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPolicyScopeRulesQueryOptions = <TData = Awaited<ReturnType<typeof listPolicyScopeRules>>, TError = unknown>(params: ListPolicyScopeRulesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPolicyScopeRules>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPolicyScopeRulesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPolicyScopeRules>>> = ({ signal }) => listPolicyScopeRules(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPolicyScopeRules>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListPolicyScopeRulesQueryResult = NonNullable<Awaited<ReturnType<typeof listPolicyScopeRules>>>
export type ListPolicyScopeRulesQueryError = unknown


export function useListPolicyScopeRules<TData = Awaited<ReturnType<typeof listPolicyScopeRules>>, TError = unknown>(
 params: ListPolicyScopeRulesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPolicyScopeRules>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPolicyScopeRules>>,
          TError,
          Awaited<ReturnType<typeof listPolicyScopeRules>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListPolicyScopeRules<TData = Awaited<ReturnType<typeof listPolicyScopeRules>>, TError = unknown>(
 params: ListPolicyScopeRulesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPolicyScopeRules>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPolicyScopeRules>>,
          TError,
          Awaited<ReturnType<typeof listPolicyScopeRules>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListPolicyScopeRules<TData = Awaited<ReturnType<typeof listPolicyScopeRules>>, TError = unknown>(
 params: ListPolicyScopeRulesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPolicyScopeRules>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListPolicyScopeRules<TData = Awaited<ReturnType<typeof listPolicyScopeRules>>, TError = unknown>(
 params: ListPolicyScopeRulesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPolicyScopeRules>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListPolicyScopeRulesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSetPolicyScopeRuleUrl = () => {




  return `/api/admin/policies-and-standards/assignments`
}

export const setPolicyScopeRule = async (setPolicyScopeRuleBody: SetPolicyScopeRule, options?: Parameters<typeof customFetch>[1]): Promise<SetPolicyScopeRule200> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SetPolicyScopeRule200>(getSetPolicyScopeRuleUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(setPolicyScopeRuleBody)
  }
);}





export const getSetPolicyScopeRuleMutationKey = () => ['setPolicyScopeRule'] as const;

export const getSetPolicyScopeRuleMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setPolicyScopeRule>>, TError,SetPolicyScopeRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setPolicyScopeRule>>, TError,SetPolicyScopeRuleMutationVariables, TContext> => {

const mutationKey = getSetPolicyScopeRuleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setPolicyScopeRule>>, SetPolicyScopeRuleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  setPolicyScopeRule(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetPolicyScopeRuleMutationResult = NonNullable<Awaited<ReturnType<typeof setPolicyScopeRule>>>
    export type SetPolicyScopeRuleMutationBody = SetPolicyScopeRule
    export type SetPolicyScopeRuleMutationError = void
    export type SetPolicyScopeRuleMutationVariables = {data: SetPolicyScopeRule}

    export const useSetPolicyScopeRule = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setPolicyScopeRule>>, TError,SetPolicyScopeRuleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setPolicyScopeRule>>,
        TError,
        SetPolicyScopeRuleMutationVariables,
        TContext
      > => {
      return useMutation(getSetPolicyScopeRuleMutationOptions(options), queryClient);
    }

export const getGetSiteArrivalSafetyUrl = (id: string,) => {




  return `/api/admin/sites/${id}/arrival-safety`
}

export const getSiteArrivalSafety = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SiteArrivalSafety> => {

  return customFetch<SiteArrivalSafety>(getGetSiteArrivalSafetyUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSiteArrivalSafetyQueryKey = (id: string,) => {
    return [
    `/api/admin/sites/${id}/arrival-safety`
    ] as const;
    }


export const getGetSiteArrivalSafetyQueryOptions = <TData = Awaited<ReturnType<typeof getSiteArrivalSafety>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteArrivalSafety>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSiteArrivalSafetyQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSiteArrivalSafety>>> = ({ signal }) => getSiteArrivalSafety(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSiteArrivalSafety>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetSiteArrivalSafetyQueryResult = NonNullable<Awaited<ReturnType<typeof getSiteArrivalSafety>>>
export type GetSiteArrivalSafetyQueryError = unknown


export function useGetSiteArrivalSafety<TData = Awaited<ReturnType<typeof getSiteArrivalSafety>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteArrivalSafety>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSiteArrivalSafety>>,
          TError,
          Awaited<ReturnType<typeof getSiteArrivalSafety>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSiteArrivalSafety<TData = Awaited<ReturnType<typeof getSiteArrivalSafety>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteArrivalSafety>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSiteArrivalSafety>>,
          TError,
          Awaited<ReturnType<typeof getSiteArrivalSafety>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSiteArrivalSafety<TData = Awaited<ReturnType<typeof getSiteArrivalSafety>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteArrivalSafety>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetSiteArrivalSafety<TData = Awaited<ReturnType<typeof getSiteArrivalSafety>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteArrivalSafety>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetSiteArrivalSafetyQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSaveSiteArrivalSafetyUrl = (id: string,) => {




  return `/api/admin/sites/${id}/arrival-safety`
}

export const saveSiteArrivalSafety = async (id: string,
    siteArrivalSafety: SiteArrivalSafety, options?: Parameters<typeof customFetch>[1]): Promise<SiteArrivalSafety> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SiteArrivalSafety>(getSaveSiteArrivalSafetyUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(siteArrivalSafety)
  }
);}





export const getSaveSiteArrivalSafetyMutationKey = () => ['saveSiteArrivalSafety'] as const;

export const getSaveSiteArrivalSafetyMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveSiteArrivalSafety>>, TError,SaveSiteArrivalSafetyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof saveSiteArrivalSafety>>, TError,SaveSiteArrivalSafetyMutationVariables, TContext> => {

const mutationKey = getSaveSiteArrivalSafetyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof saveSiteArrivalSafety>>, SaveSiteArrivalSafetyMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  saveSiteArrivalSafety(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveSiteArrivalSafetyMutationResult = NonNullable<Awaited<ReturnType<typeof saveSiteArrivalSafety>>>
    export type SaveSiteArrivalSafetyMutationBody = SiteArrivalSafety
    export type SaveSiteArrivalSafetyMutationError = void
    export type SaveSiteArrivalSafetyMutationVariables = {id: string;data: SiteArrivalSafety}

    export const useSaveSiteArrivalSafety = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveSiteArrivalSafety>>, TError,SaveSiteArrivalSafetyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof saveSiteArrivalSafety>>,
        TError,
        SaveSiteArrivalSafetyMutationVariables,
        TContext
      > => {
      return useMutation(getSaveSiteArrivalSafetyMutationOptions(options), queryClient);
    }

export const getGetMyPolicyRequirementsUrl = (params: GetMyPolicyRequirementsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/me/policies-and-standards?${stringifiedParams}` : `/api/me/policies-and-standards`
}

/**
 * @summary Resolve current cumulative Policies and standards for a space
 */
export const getMyPolicyRequirements = async (params: GetMyPolicyRequirementsParams, options?: Parameters<typeof customFetch>[1]): Promise<PolicyRequirements> => {

  return customFetch<PolicyRequirements>(getGetMyPolicyRequirementsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMyPolicyRequirementsQueryKey = (params?: GetMyPolicyRequirementsParams,) => {
    return [
    `/api/me/policies-and-standards`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetMyPolicyRequirementsQueryOptions = <TData = Awaited<ReturnType<typeof getMyPolicyRequirements>>, TError = void>(params: GetMyPolicyRequirementsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyPolicyRequirements>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMyPolicyRequirementsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMyPolicyRequirements>>> = ({ signal }) => getMyPolicyRequirements(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMyPolicyRequirements>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMyPolicyRequirementsQueryResult = NonNullable<Awaited<ReturnType<typeof getMyPolicyRequirements>>>
export type GetMyPolicyRequirementsQueryError = void


export function useGetMyPolicyRequirements<TData = Awaited<ReturnType<typeof getMyPolicyRequirements>>, TError = void>(
 params: GetMyPolicyRequirementsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyPolicyRequirements>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyPolicyRequirements>>,
          TError,
          Awaited<ReturnType<typeof getMyPolicyRequirements>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyPolicyRequirements<TData = Awaited<ReturnType<typeof getMyPolicyRequirements>>, TError = void>(
 params: GetMyPolicyRequirementsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyPolicyRequirements>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyPolicyRequirements>>,
          TError,
          Awaited<ReturnType<typeof getMyPolicyRequirements>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyPolicyRequirements<TData = Awaited<ReturnType<typeof getMyPolicyRequirements>>, TError = void>(
 params: GetMyPolicyRequirementsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyPolicyRequirements>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Resolve current cumulative Policies and standards for a space
 */

export function useGetMyPolicyRequirements<TData = Awaited<ReturnType<typeof getMyPolicyRequirements>>, TError = void>(
 params: GetMyPolicyRequirementsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyPolicyRequirements>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMyPolicyRequirementsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getAcceptMyPolicyRequirementsUrl = () => {




  return `/api/me/policies-and-standards`
}

/**
 * @summary Accept the exact current set as the authenticated caller
 */
export const acceptMyPolicyRequirements = async (acceptPolicyRequirements: AcceptPolicyRequirements, options?: Parameters<typeof customFetch>[1]): Promise<PolicyRequirements> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PolicyRequirements>(getAcceptMyPolicyRequirementsUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(acceptPolicyRequirements)
  }
);}





export const getAcceptMyPolicyRequirementsMutationKey = () => ['acceptMyPolicyRequirements'] as const;

export const getAcceptMyPolicyRequirementsMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof acceptMyPolicyRequirements>>, TError,AcceptMyPolicyRequirementsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof acceptMyPolicyRequirements>>, TError,AcceptMyPolicyRequirementsMutationVariables, TContext> => {

const mutationKey = getAcceptMyPolicyRequirementsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof acceptMyPolicyRequirements>>, AcceptMyPolicyRequirementsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  acceptMyPolicyRequirements(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AcceptMyPolicyRequirementsMutationResult = NonNullable<Awaited<ReturnType<typeof acceptMyPolicyRequirements>>>
    export type AcceptMyPolicyRequirementsMutationBody = AcceptPolicyRequirements
    export type AcceptMyPolicyRequirementsMutationError = void
    export type AcceptMyPolicyRequirementsMutationVariables = {data: AcceptPolicyRequirements}

    /**
 * @summary Accept the exact current set as the authenticated caller
 */
export const useAcceptMyPolicyRequirements = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof acceptMyPolicyRequirements>>, TError,AcceptMyPolicyRequirementsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof acceptMyPolicyRequirements>>,
        TError,
        AcceptMyPolicyRequirementsMutationVariables,
        TContext
      > => {
      return useMutation(getAcceptMyPolicyRequirementsMutationOptions(options), queryClient);
    }

export const getGetInventoryLifecycleUrl = (level: InventoryLevel,
    id: string,) => {




  return `/api/inventory/${level}/${id}/lifecycle`
}

/**
 * @summary Read own, inherited and pending lifecycle restrictions
 */
export const getInventoryLifecycle = async (level: InventoryLevel,
    id: string, options?: Parameters<typeof customFetch>[1]): Promise<InventoryLifecycleState> => {

  return customFetch<InventoryLifecycleState>(getGetInventoryLifecycleUrl(level,id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetInventoryLifecycleQueryKey = (level: InventoryLevel,
    id: string,) => {
    return [
    `/api/inventory/${level}/${id}/lifecycle`
    ] as const;
    }


export const getGetInventoryLifecycleQueryOptions = <TData = Awaited<ReturnType<typeof getInventoryLifecycle>>, TError = void | Error>(level: InventoryLevel,
    id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getInventoryLifecycle>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetInventoryLifecycleQueryKey(level,id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getInventoryLifecycle>>> = ({ signal }) => getInventoryLifecycle(level,id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: level !== null && level !== undefined && id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getInventoryLifecycle>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetInventoryLifecycleQueryResult = NonNullable<Awaited<ReturnType<typeof getInventoryLifecycle>>>
export type GetInventoryLifecycleQueryError = void | Error


export function useGetInventoryLifecycle<TData = Awaited<ReturnType<typeof getInventoryLifecycle>>, TError = void | Error>(
 level: InventoryLevel,
    id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getInventoryLifecycle>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getInventoryLifecycle>>,
          TError,
          Awaited<ReturnType<typeof getInventoryLifecycle>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetInventoryLifecycle<TData = Awaited<ReturnType<typeof getInventoryLifecycle>>, TError = void | Error>(
 level: InventoryLevel,
    id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getInventoryLifecycle>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getInventoryLifecycle>>,
          TError,
          Awaited<ReturnType<typeof getInventoryLifecycle>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetInventoryLifecycle<TData = Awaited<ReturnType<typeof getInventoryLifecycle>>, TError = void | Error>(
 level: InventoryLevel,
    id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getInventoryLifecycle>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read own, inherited and pending lifecycle restrictions
 */

export function useGetInventoryLifecycle<TData = Awaited<ReturnType<typeof getInventoryLifecycle>>, TError = void | Error>(
 level: InventoryLevel,
    id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getInventoryLifecycle>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetInventoryLifecycleQueryOptions(level,id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPreviewInventoryLifecycleUrl = (level: InventoryLevel,
    id: string,) => {




  return `/api/inventory/${level}/${id}/lifecycle/preview`
}

/**
 * @summary Review a lifecycle action without changing inventory or bookings
 */
export const previewInventoryLifecycle = async (level: InventoryLevel,
    id: string,
    inventoryLifecycleRequest: InventoryLifecycleRequest, options?: Parameters<typeof customFetch>[1]): Promise<InventoryLifecyclePreview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<InventoryLifecyclePreview>(getPreviewInventoryLifecycleUrl(level,id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(inventoryLifecycleRequest)
  }
);}





export const getPreviewInventoryLifecycleMutationKey = () => ['previewInventoryLifecycle'] as const;

export const getPreviewInventoryLifecycleMutationOptions = <TError = void | Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewInventoryLifecycle>>, TError,PreviewInventoryLifecycleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewInventoryLifecycle>>, TError,PreviewInventoryLifecycleMutationVariables, TContext> => {

const mutationKey = getPreviewInventoryLifecycleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewInventoryLifecycle>>, PreviewInventoryLifecycleMutationVariables> = (props) => {
          const {level,id,data} = props ?? {};

          return  previewInventoryLifecycle(level,id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewInventoryLifecycleMutationResult = NonNullable<Awaited<ReturnType<typeof previewInventoryLifecycle>>>
    export type PreviewInventoryLifecycleMutationBody = InventoryLifecycleRequest
    export type PreviewInventoryLifecycleMutationError = void | Error
    export type PreviewInventoryLifecycleMutationVariables = {level: InventoryLevel;id: string;data: InventoryLifecycleRequest}

    /**
 * @summary Review a lifecycle action without changing inventory or bookings
 */
export const usePreviewInventoryLifecycle = <TError = void | Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewInventoryLifecycle>>, TError,PreviewInventoryLifecycleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof previewInventoryLifecycle>>,
        TError,
        PreviewInventoryLifecycleMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewInventoryLifecycleMutationOptions(options), queryClient);
    }

export const getConfirmInventoryLifecycleUrl = (level: InventoryLevel,
    id: string,) => {




  return `/api/inventory/${level}/${id}/lifecycle/confirm`
}

/**
 * @summary Confirm reviewed lifecycle and booking impact
 */
export const confirmInventoryLifecycle = async (level: InventoryLevel,
    id: string,
    inventoryLifecycleConfirmation: InventoryLifecycleConfirmation, options?: Parameters<typeof customFetch>[1]): Promise<InventoryLifecycleState> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<InventoryLifecycleState>(getConfirmInventoryLifecycleUrl(level,id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(inventoryLifecycleConfirmation)
  }
);}





export const getConfirmInventoryLifecycleMutationKey = () => ['confirmInventoryLifecycle'] as const;

export const getConfirmInventoryLifecycleMutationOptions = <TError = void | Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmInventoryLifecycle>>, TError,ConfirmInventoryLifecycleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmInventoryLifecycle>>, TError,ConfirmInventoryLifecycleMutationVariables, TContext> => {

const mutationKey = getConfirmInventoryLifecycleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmInventoryLifecycle>>, ConfirmInventoryLifecycleMutationVariables> = (props) => {
          const {level,id,data} = props ?? {};

          return  confirmInventoryLifecycle(level,id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmInventoryLifecycleMutationResult = NonNullable<Awaited<ReturnType<typeof confirmInventoryLifecycle>>>
    export type ConfirmInventoryLifecycleMutationBody = InventoryLifecycleConfirmation
    export type ConfirmInventoryLifecycleMutationError = void | Error
    export type ConfirmInventoryLifecycleMutationVariables = {level: InventoryLevel;id: string;data: InventoryLifecycleConfirmation}

    /**
 * @summary Confirm reviewed lifecycle and booking impact
 */
export const useConfirmInventoryLifecycle = <TError = void | Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmInventoryLifecycle>>, TError,ConfirmInventoryLifecycleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof confirmInventoryLifecycle>>,
        TError,
        ConfirmInventoryLifecycleMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmInventoryLifecycleMutationOptions(options), queryClient);
    }

export const getListInventoryLifecycleHistoryUrl = (level: InventoryLevel,
    id: string,
    params?: ListInventoryLifecycleHistoryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/inventory/${level}/${id}/lifecycle/history?${stringifiedParams}` : `/api/inventory/${level}/${id}/lifecycle/history`
}

/**
 * @summary Paginated durable lifecycle history including removed schedules
 */
export const listInventoryLifecycleHistory = async (level: InventoryLevel,
    id: string,
    params?: ListInventoryLifecycleHistoryParams, options?: Parameters<typeof customFetch>[1]): Promise<InventoryLifecycleHistory> => {

  return customFetch<InventoryLifecycleHistory>(getListInventoryLifecycleHistoryUrl(level,id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListInventoryLifecycleHistoryQueryKey = (level: InventoryLevel,
    id: string,
    params?: ListInventoryLifecycleHistoryParams,) => {
    return [
    `/api/inventory/${level}/${id}/lifecycle/history`, ...(params ? [params] : [])
    ] as const;
    }


export const getListInventoryLifecycleHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listInventoryLifecycleHistory>>, TError = void | Error>(level: InventoryLevel,
    id: string,
    params?: ListInventoryLifecycleHistoryParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listInventoryLifecycleHistory>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListInventoryLifecycleHistoryQueryKey(level,id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listInventoryLifecycleHistory>>> = ({ signal }) => listInventoryLifecycleHistory(level,id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: level !== null && level !== undefined && id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listInventoryLifecycleHistory>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListInventoryLifecycleHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listInventoryLifecycleHistory>>>
export type ListInventoryLifecycleHistoryQueryError = void | Error


export function useListInventoryLifecycleHistory<TData = Awaited<ReturnType<typeof listInventoryLifecycleHistory>>, TError = void | Error>(
 level: InventoryLevel,
    id: string,
    params: undefined |  ListInventoryLifecycleHistoryParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listInventoryLifecycleHistory>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listInventoryLifecycleHistory>>,
          TError,
          Awaited<ReturnType<typeof listInventoryLifecycleHistory>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListInventoryLifecycleHistory<TData = Awaited<ReturnType<typeof listInventoryLifecycleHistory>>, TError = void | Error>(
 level: InventoryLevel,
    id: string,
    params?: ListInventoryLifecycleHistoryParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listInventoryLifecycleHistory>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listInventoryLifecycleHistory>>,
          TError,
          Awaited<ReturnType<typeof listInventoryLifecycleHistory>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListInventoryLifecycleHistory<TData = Awaited<ReturnType<typeof listInventoryLifecycleHistory>>, TError = void | Error>(
 level: InventoryLevel,
    id: string,
    params?: ListInventoryLifecycleHistoryParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listInventoryLifecycleHistory>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Paginated durable lifecycle history including removed schedules
 */

export function useListInventoryLifecycleHistory<TData = Awaited<ReturnType<typeof listInventoryLifecycleHistory>>, TError = void | Error>(
 level: InventoryLevel,
    id: string,
    params?: ListInventoryLifecycleHistoryParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listInventoryLifecycleHistory>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListInventoryLifecycleHistoryQueryOptions(level,id,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListInventoryLifecyclePreviewChildrenUrl = (previewId: string,
    params?: ListInventoryLifecyclePreviewChildrenParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/inventory/lifecycle/previews/${previewId}/children?${stringifiedParams}` : `/api/inventory/lifecycle/previews/${previewId}/children`
}

/**
 * @summary Browse every reviewed descendant without truncating totals
 */
export const listInventoryLifecyclePreviewChildren = async (previewId: string,
    params?: ListInventoryLifecyclePreviewChildrenParams, options?: Parameters<typeof customFetch>[1]): Promise<InventoryLifecycleChildren> => {

  return customFetch<InventoryLifecycleChildren>(getListInventoryLifecyclePreviewChildrenUrl(previewId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListInventoryLifecyclePreviewChildrenQueryKey = (previewId: string,
    params?: ListInventoryLifecyclePreviewChildrenParams,) => {
    return [
    `/api/inventory/lifecycle/previews/${previewId}/children`, ...(params ? [params] : [])
    ] as const;
    }


export const getListInventoryLifecyclePreviewChildrenQueryOptions = <TData = Awaited<ReturnType<typeof listInventoryLifecyclePreviewChildren>>, TError = void | Error>(previewId: string,
    params?: ListInventoryLifecyclePreviewChildrenParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listInventoryLifecyclePreviewChildren>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListInventoryLifecyclePreviewChildrenQueryKey(previewId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listInventoryLifecyclePreviewChildren>>> = ({ signal }) => listInventoryLifecyclePreviewChildren(previewId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: previewId !== null && previewId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listInventoryLifecyclePreviewChildren>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListInventoryLifecyclePreviewChildrenQueryResult = NonNullable<Awaited<ReturnType<typeof listInventoryLifecyclePreviewChildren>>>
export type ListInventoryLifecyclePreviewChildrenQueryError = void | Error


export function useListInventoryLifecyclePreviewChildren<TData = Awaited<ReturnType<typeof listInventoryLifecyclePreviewChildren>>, TError = void | Error>(
 previewId: string,
    params: undefined |  ListInventoryLifecyclePreviewChildrenParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listInventoryLifecyclePreviewChildren>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listInventoryLifecyclePreviewChildren>>,
          TError,
          Awaited<ReturnType<typeof listInventoryLifecyclePreviewChildren>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListInventoryLifecyclePreviewChildren<TData = Awaited<ReturnType<typeof listInventoryLifecyclePreviewChildren>>, TError = void | Error>(
 previewId: string,
    params?: ListInventoryLifecyclePreviewChildrenParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listInventoryLifecyclePreviewChildren>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listInventoryLifecyclePreviewChildren>>,
          TError,
          Awaited<ReturnType<typeof listInventoryLifecyclePreviewChildren>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListInventoryLifecyclePreviewChildren<TData = Awaited<ReturnType<typeof listInventoryLifecyclePreviewChildren>>, TError = void | Error>(
 previewId: string,
    params?: ListInventoryLifecyclePreviewChildrenParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listInventoryLifecyclePreviewChildren>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Browse every reviewed descendant without truncating totals
 */

export function useListInventoryLifecyclePreviewChildren<TData = Awaited<ReturnType<typeof listInventoryLifecyclePreviewChildren>>, TError = void | Error>(
 previewId: string,
    params?: ListInventoryLifecyclePreviewChildrenParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listInventoryLifecyclePreviewChildren>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListInventoryLifecyclePreviewChildrenQueryOptions(previewId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListInventoryLifecyclePreviewBookingsUrl = (previewId: string,
    params?: ListInventoryLifecyclePreviewBookingsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/inventory/lifecycle/previews/${previewId}/bookings?${stringifiedParams}` : `/api/inventory/lifecycle/previews/${previewId}/bookings`
}

/**
 * @summary Browse reviewed bookings, optionally for one affected space
 */
export const listInventoryLifecyclePreviewBookings = async (previewId: string,
    params?: ListInventoryLifecyclePreviewBookingsParams, options?: Parameters<typeof customFetch>[1]): Promise<InventoryLifecycleBookings> => {

  return customFetch<InventoryLifecycleBookings>(getListInventoryLifecyclePreviewBookingsUrl(previewId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListInventoryLifecyclePreviewBookingsQueryKey = (previewId: string,
    params?: ListInventoryLifecyclePreviewBookingsParams,) => {
    return [
    `/api/inventory/lifecycle/previews/${previewId}/bookings`, ...(params ? [params] : [])
    ] as const;
    }


export const getListInventoryLifecyclePreviewBookingsQueryOptions = <TData = Awaited<ReturnType<typeof listInventoryLifecyclePreviewBookings>>, TError = void>(previewId: string,
    params?: ListInventoryLifecyclePreviewBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listInventoryLifecyclePreviewBookings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListInventoryLifecyclePreviewBookingsQueryKey(previewId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listInventoryLifecyclePreviewBookings>>> = ({ signal }) => listInventoryLifecyclePreviewBookings(previewId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: previewId !== null && previewId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listInventoryLifecyclePreviewBookings>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListInventoryLifecyclePreviewBookingsQueryResult = NonNullable<Awaited<ReturnType<typeof listInventoryLifecyclePreviewBookings>>>
export type ListInventoryLifecyclePreviewBookingsQueryError = void


export function useListInventoryLifecyclePreviewBookings<TData = Awaited<ReturnType<typeof listInventoryLifecyclePreviewBookings>>, TError = void>(
 previewId: string,
    params: undefined |  ListInventoryLifecyclePreviewBookingsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listInventoryLifecyclePreviewBookings>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listInventoryLifecyclePreviewBookings>>,
          TError,
          Awaited<ReturnType<typeof listInventoryLifecyclePreviewBookings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListInventoryLifecyclePreviewBookings<TData = Awaited<ReturnType<typeof listInventoryLifecyclePreviewBookings>>, TError = void>(
 previewId: string,
    params?: ListInventoryLifecyclePreviewBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listInventoryLifecyclePreviewBookings>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listInventoryLifecyclePreviewBookings>>,
          TError,
          Awaited<ReturnType<typeof listInventoryLifecyclePreviewBookings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListInventoryLifecyclePreviewBookings<TData = Awaited<ReturnType<typeof listInventoryLifecyclePreviewBookings>>, TError = void>(
 previewId: string,
    params?: ListInventoryLifecyclePreviewBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listInventoryLifecyclePreviewBookings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Browse reviewed bookings, optionally for one affected space
 */

export function useListInventoryLifecyclePreviewBookings<TData = Awaited<ReturnType<typeof listInventoryLifecyclePreviewBookings>>, TError = void>(
 previewId: string,
    params?: ListInventoryLifecyclePreviewBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listInventoryLifecyclePreviewBookings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListInventoryLifecyclePreviewBookingsQueryOptions(previewId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListStaffedServicesUrl = (params?: ListStaffedServicesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/staffed-services?${stringifiedParams}` : `/api/staffed-services`
}

/**
 * @summary List active staffed services; administrators may include inactive ones
 */
export const listStaffedServices = async (params?: ListStaffedServicesParams, options?: Parameters<typeof customFetch>[1]): Promise<StaffedServiceList> => {

  return customFetch<StaffedServiceList>(getListStaffedServicesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListStaffedServicesQueryKey = (params?: ListStaffedServicesParams,) => {
    return [
    `/api/staffed-services`, ...(params ? [params] : [])
    ] as const;
    }


export const getListStaffedServicesQueryOptions = <TData = Awaited<ReturnType<typeof listStaffedServices>>, TError = unknown>(params?: ListStaffedServicesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listStaffedServices>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListStaffedServicesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listStaffedServices>>> = ({ signal }) => listStaffedServices(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listStaffedServices>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListStaffedServicesQueryResult = NonNullable<Awaited<ReturnType<typeof listStaffedServices>>>
export type ListStaffedServicesQueryError = unknown


export function useListStaffedServices<TData = Awaited<ReturnType<typeof listStaffedServices>>, TError = unknown>(
 params: undefined |  ListStaffedServicesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listStaffedServices>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listStaffedServices>>,
          TError,
          Awaited<ReturnType<typeof listStaffedServices>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListStaffedServices<TData = Awaited<ReturnType<typeof listStaffedServices>>, TError = unknown>(
 params?: ListStaffedServicesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listStaffedServices>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listStaffedServices>>,
          TError,
          Awaited<ReturnType<typeof listStaffedServices>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListStaffedServices<TData = Awaited<ReturnType<typeof listStaffedServices>>, TError = unknown>(
 params?: ListStaffedServicesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listStaffedServices>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List active staffed services; administrators may include inactive ones
 */

export function useListStaffedServices<TData = Awaited<ReturnType<typeof listStaffedServices>>, TError = unknown>(
 params?: ListStaffedServicesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listStaffedServices>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListStaffedServicesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateStaffedServiceUrl = () => {




  return `/api/staffed-services`
}

/**
 * @summary Configure a service and responsible team (inventory administrator)
 */
export const createStaffedService = async (staffedServiceInput: StaffedServiceInput, options?: Parameters<typeof customFetch>[1]): Promise<StaffedService> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<StaffedService>(getCreateStaffedServiceUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(staffedServiceInput)
  }
);}





export const getCreateStaffedServiceMutationKey = () => ['createStaffedService'] as const;

export const getCreateStaffedServiceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createStaffedService>>, TError,CreateStaffedServiceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createStaffedService>>, TError,CreateStaffedServiceMutationVariables, TContext> => {

const mutationKey = getCreateStaffedServiceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createStaffedService>>, CreateStaffedServiceMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createStaffedService(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateStaffedServiceMutationResult = NonNullable<Awaited<ReturnType<typeof createStaffedService>>>
    export type CreateStaffedServiceMutationBody = StaffedServiceInput
    export type CreateStaffedServiceMutationError = unknown
    export type CreateStaffedServiceMutationVariables = {data: StaffedServiceInput}

    /**
 * @summary Configure a service and responsible team (inventory administrator)
 */
export const useCreateStaffedService = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createStaffedService>>, TError,CreateStaffedServiceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createStaffedService>>,
        TError,
        CreateStaffedServiceMutationVariables,
        TContext
      > => {
      return useMutation(getCreateStaffedServiceMutationOptions(options), queryClient);
    }

export const getUpdateStaffedServiceUrl = (id: string,) => {




  return `/api/staffed-services/${id}`
}

/**
 * @summary Update a staffed service (inventory administrator)
 */
export const updateStaffedService = async (id: string,
    staffedServicePatch: StaffedServicePatch, options?: Parameters<typeof customFetch>[1]): Promise<StaffedService> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<StaffedService>(getUpdateStaffedServiceUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(staffedServicePatch)
  }
);}





export const getUpdateStaffedServiceMutationKey = () => ['updateStaffedService'] as const;

export const getUpdateStaffedServiceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateStaffedService>>, TError,UpdateStaffedServiceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateStaffedService>>, TError,UpdateStaffedServiceMutationVariables, TContext> => {

const mutationKey = getUpdateStaffedServiceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateStaffedService>>, UpdateStaffedServiceMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateStaffedService(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateStaffedServiceMutationResult = NonNullable<Awaited<ReturnType<typeof updateStaffedService>>>
    export type UpdateStaffedServiceMutationBody = StaffedServicePatch
    export type UpdateStaffedServiceMutationError = unknown
    export type UpdateStaffedServiceMutationVariables = {id: string;data: StaffedServicePatch}

    /**
 * @summary Update a staffed service (inventory administrator)
 */
export const useUpdateStaffedService = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateStaffedService>>, TError,UpdateStaffedServiceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateStaffedService>>,
        TError,
        UpdateStaffedServiceMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateStaffedServiceMutationOptions(options), queryClient);
    }

export const getListStaffedServiceTeamsUrl = (params?: ListStaffedServiceTeamsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/staffed-services/teams?${stringifiedParams}` : `/api/staffed-services/teams`
}

/**
 * @summary List entity teams for service configuration (inventory administrator)
 */
export const listStaffedServiceTeams = async (params?: ListStaffedServiceTeamsParams, options?: Parameters<typeof customFetch>[1]): Promise<StaffedServiceTeamList> => {

  return customFetch<StaffedServiceTeamList>(getListStaffedServiceTeamsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListStaffedServiceTeamsQueryKey = (params?: ListStaffedServiceTeamsParams,) => {
    return [
    `/api/staffed-services/teams`, ...(params ? [params] : [])
    ] as const;
    }


export const getListStaffedServiceTeamsQueryOptions = <TData = Awaited<ReturnType<typeof listStaffedServiceTeams>>, TError = unknown>(params?: ListStaffedServiceTeamsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listStaffedServiceTeams>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListStaffedServiceTeamsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listStaffedServiceTeams>>> = ({ signal }) => listStaffedServiceTeams(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listStaffedServiceTeams>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListStaffedServiceTeamsQueryResult = NonNullable<Awaited<ReturnType<typeof listStaffedServiceTeams>>>
export type ListStaffedServiceTeamsQueryError = unknown


export function useListStaffedServiceTeams<TData = Awaited<ReturnType<typeof listStaffedServiceTeams>>, TError = unknown>(
 params: undefined |  ListStaffedServiceTeamsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listStaffedServiceTeams>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listStaffedServiceTeams>>,
          TError,
          Awaited<ReturnType<typeof listStaffedServiceTeams>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListStaffedServiceTeams<TData = Awaited<ReturnType<typeof listStaffedServiceTeams>>, TError = unknown>(
 params?: ListStaffedServiceTeamsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listStaffedServiceTeams>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listStaffedServiceTeams>>,
          TError,
          Awaited<ReturnType<typeof listStaffedServiceTeams>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListStaffedServiceTeams<TData = Awaited<ReturnType<typeof listStaffedServiceTeams>>, TError = unknown>(
 params?: ListStaffedServiceTeamsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listStaffedServiceTeams>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List entity teams for service configuration (inventory administrator)
 */

export function useListStaffedServiceTeams<TData = Awaited<ReturnType<typeof listStaffedServiceTeams>>, TError = unknown>(
 params?: ListStaffedServiceTeamsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listStaffedServiceTeams>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListStaffedServiceTeamsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListMyStaffedServiceRequestsUrl = (params?: ListMyStaffedServiceRequestsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/staffed-service-requests?${stringifiedParams}` : `/api/staffed-service-requests`
}

/**
 * @summary List my service requests
 */
export const listMyStaffedServiceRequests = async (params?: ListMyStaffedServiceRequestsParams, options?: Parameters<typeof customFetch>[1]): Promise<StaffedServiceRequestList> => {

  return customFetch<StaffedServiceRequestList>(getListMyStaffedServiceRequestsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMyStaffedServiceRequestsQueryKey = (params?: ListMyStaffedServiceRequestsParams,) => {
    return [
    `/api/staffed-service-requests`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMyStaffedServiceRequestsQueryOptions = <TData = Awaited<ReturnType<typeof listMyStaffedServiceRequests>>, TError = unknown>(params?: ListMyStaffedServiceRequestsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyStaffedServiceRequests>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMyStaffedServiceRequestsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyStaffedServiceRequests>>> = ({ signal }) => listMyStaffedServiceRequests(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMyStaffedServiceRequests>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMyStaffedServiceRequestsQueryResult = NonNullable<Awaited<ReturnType<typeof listMyStaffedServiceRequests>>>
export type ListMyStaffedServiceRequestsQueryError = unknown


export function useListMyStaffedServiceRequests<TData = Awaited<ReturnType<typeof listMyStaffedServiceRequests>>, TError = unknown>(
 params: undefined |  ListMyStaffedServiceRequestsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyStaffedServiceRequests>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyStaffedServiceRequests>>,
          TError,
          Awaited<ReturnType<typeof listMyStaffedServiceRequests>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyStaffedServiceRequests<TData = Awaited<ReturnType<typeof listMyStaffedServiceRequests>>, TError = unknown>(
 params?: ListMyStaffedServiceRequestsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyStaffedServiceRequests>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyStaffedServiceRequests>>,
          TError,
          Awaited<ReturnType<typeof listMyStaffedServiceRequests>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyStaffedServiceRequests<TData = Awaited<ReturnType<typeof listMyStaffedServiceRequests>>, TError = unknown>(
 params?: ListMyStaffedServiceRequestsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyStaffedServiceRequests>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List my service requests
 */

export function useListMyStaffedServiceRequests<TData = Awaited<ReturnType<typeof listMyStaffedServiceRequests>>, TError = unknown>(
 params?: ListMyStaffedServiceRequestsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyStaffedServiceRequests>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMyStaffedServiceRequestsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateStaffedServiceRequestUrl = () => {




  return `/api/staffed-service-requests`
}

/**
 * @summary Submit a pending staffed-service request
 */
export const createStaffedServiceRequest = async (staffedServiceRequestInput: StaffedServiceRequestInput, options?: Parameters<typeof customFetch>[1]): Promise<StaffedServiceRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<StaffedServiceRequest>(getCreateStaffedServiceRequestUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(staffedServiceRequestInput)
  }
);}





export const getCreateStaffedServiceRequestMutationKey = () => ['createStaffedServiceRequest'] as const;

export const getCreateStaffedServiceRequestMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createStaffedServiceRequest>>, TError,CreateStaffedServiceRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createStaffedServiceRequest>>, TError,CreateStaffedServiceRequestMutationVariables, TContext> => {

const mutationKey = getCreateStaffedServiceRequestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createStaffedServiceRequest>>, CreateStaffedServiceRequestMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createStaffedServiceRequest(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateStaffedServiceRequestMutationResult = NonNullable<Awaited<ReturnType<typeof createStaffedServiceRequest>>>
    export type CreateStaffedServiceRequestMutationBody = StaffedServiceRequestInput
    export type CreateStaffedServiceRequestMutationError = unknown
    export type CreateStaffedServiceRequestMutationVariables = {data: StaffedServiceRequestInput}

    /**
 * @summary Submit a pending staffed-service request
 */
export const useCreateStaffedServiceRequest = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createStaffedServiceRequest>>, TError,CreateStaffedServiceRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createStaffedServiceRequest>>,
        TError,
        CreateStaffedServiceRequestMutationVariables,
        TContext
      > => {
      return useMutation(getCreateStaffedServiceRequestMutationOptions(options), queryClient);
    }

export const getListTeamStaffedServiceRequestsUrl = (params?: ListTeamStaffedServiceRequestsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/staffed-service-requests/team?${stringifiedParams}` : `/api/staffed-service-requests/team`
}

/**
 * @summary List requests for my responsible teams
 */
export const listTeamStaffedServiceRequests = async (params?: ListTeamStaffedServiceRequestsParams, options?: Parameters<typeof customFetch>[1]): Promise<StaffedServiceRequestList> => {

  return customFetch<StaffedServiceRequestList>(getListTeamStaffedServiceRequestsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTeamStaffedServiceRequestsQueryKey = (params?: ListTeamStaffedServiceRequestsParams,) => {
    return [
    `/api/staffed-service-requests/team`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTeamStaffedServiceRequestsQueryOptions = <TData = Awaited<ReturnType<typeof listTeamStaffedServiceRequests>>, TError = unknown>(params?: ListTeamStaffedServiceRequestsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTeamStaffedServiceRequests>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTeamStaffedServiceRequestsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTeamStaffedServiceRequests>>> = ({ signal }) => listTeamStaffedServiceRequests(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTeamStaffedServiceRequests>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListTeamStaffedServiceRequestsQueryResult = NonNullable<Awaited<ReturnType<typeof listTeamStaffedServiceRequests>>>
export type ListTeamStaffedServiceRequestsQueryError = unknown


export function useListTeamStaffedServiceRequests<TData = Awaited<ReturnType<typeof listTeamStaffedServiceRequests>>, TError = unknown>(
 params: undefined |  ListTeamStaffedServiceRequestsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTeamStaffedServiceRequests>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTeamStaffedServiceRequests>>,
          TError,
          Awaited<ReturnType<typeof listTeamStaffedServiceRequests>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTeamStaffedServiceRequests<TData = Awaited<ReturnType<typeof listTeamStaffedServiceRequests>>, TError = unknown>(
 params?: ListTeamStaffedServiceRequestsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTeamStaffedServiceRequests>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTeamStaffedServiceRequests>>,
          TError,
          Awaited<ReturnType<typeof listTeamStaffedServiceRequests>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTeamStaffedServiceRequests<TData = Awaited<ReturnType<typeof listTeamStaffedServiceRequests>>, TError = unknown>(
 params?: ListTeamStaffedServiceRequestsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTeamStaffedServiceRequests>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List requests for my responsible teams
 */

export function useListTeamStaffedServiceRequests<TData = Awaited<ReturnType<typeof listTeamStaffedServiceRequests>>, TError = unknown>(
 params?: ListTeamStaffedServiceRequestsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTeamStaffedServiceRequests>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListTeamStaffedServiceRequestsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getTransitionStaffedServiceRequestUrl = (id: string,) => {




  return `/api/staffed-service-requests/${id}/transition`
}

/**
 * @summary Assign, confirm, decline, cancel or complete a service request
 */
export const transitionStaffedServiceRequest = async (id: string,
    staffedServiceTransition: StaffedServiceTransition, options?: Parameters<typeof customFetch>[1]): Promise<StaffedServiceRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<StaffedServiceRequest>(getTransitionStaffedServiceRequestUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(staffedServiceTransition)
  }
);}





export const getTransitionStaffedServiceRequestMutationKey = () => ['transitionStaffedServiceRequest'] as const;

export const getTransitionStaffedServiceRequestMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof transitionStaffedServiceRequest>>, TError,TransitionStaffedServiceRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof transitionStaffedServiceRequest>>, TError,TransitionStaffedServiceRequestMutationVariables, TContext> => {

const mutationKey = getTransitionStaffedServiceRequestMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof transitionStaffedServiceRequest>>, TransitionStaffedServiceRequestMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  transitionStaffedServiceRequest(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TransitionStaffedServiceRequestMutationResult = NonNullable<Awaited<ReturnType<typeof transitionStaffedServiceRequest>>>
    export type TransitionStaffedServiceRequestMutationBody = StaffedServiceTransition
    export type TransitionStaffedServiceRequestMutationError = unknown
    export type TransitionStaffedServiceRequestMutationVariables = {id: string;data: StaffedServiceTransition}

    /**
 * @summary Assign, confirm, decline, cancel or complete a service request
 */
export const useTransitionStaffedServiceRequest = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof transitionStaffedServiceRequest>>, TError,TransitionStaffedServiceRequestMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof transitionStaffedServiceRequest>>,
        TError,
        TransitionStaffedServiceRequestMutationVariables,
        TContext
      > => {
      return useMutation(getTransitionStaffedServiceRequestMutationOptions(options), queryClient);
    }

export const getGetHealthUrl = () => {




  return `/api/health`
}

/**
 * @summary Liveness probe
 */
export const getHealth = async ( options?: Parameters<typeof customFetch>[1]): Promise<Health> => {

  return customFetch<Health>(getGetHealthUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetHealthQueryKey = () => {
    return [
    `/api/health`
    ] as const;
    }


export const getGetHealthQueryOptions = <TData = Awaited<ReturnType<typeof getHealth>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetHealthQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getHealth>>> = ({ signal }) => getHealth({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetHealthQueryResult = NonNullable<Awaited<ReturnType<typeof getHealth>>>
export type GetHealthQueryError = unknown


export function useGetHealth<TData = Awaited<ReturnType<typeof getHealth>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHealth>>,
          TError,
          Awaited<ReturnType<typeof getHealth>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetHealth<TData = Awaited<ReturnType<typeof getHealth>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHealth>>,
          TError,
          Awaited<ReturnType<typeof getHealth>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetHealth<TData = Awaited<ReturnType<typeof getHealth>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Liveness probe
 */

export function useGetHealth<TData = Awaited<ReturnType<typeof getHealth>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHealth>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetHealthQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetDashboardStatsUrl = () => {




  return `/api/dashboard/stats`
}

/**
 * Real aggregates from inventory, bookings, check-ins, presence, and
 * blackouts. Returns zeros when the database plugin is off.
 * @summary Portfolio KPIs, top buildings, and upcoming blackouts
 */
export const getDashboardStats = async ( options?: Parameters<typeof customFetch>[1]): Promise<DashboardStats> => {

  return customFetch<DashboardStats>(getGetDashboardStatsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDashboardStatsQueryKey = () => {
    return [
    `/api/dashboard/stats`
    ] as const;
    }


export const getGetDashboardStatsQueryOptions = <TData = Awaited<ReturnType<typeof getDashboardStats>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardStats>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDashboardStatsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboardStats>>> = ({ signal }) => getDashboardStats({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDashboardStats>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetDashboardStatsQueryResult = NonNullable<Awaited<ReturnType<typeof getDashboardStats>>>
export type GetDashboardStatsQueryError = Error


export function useGetDashboardStats<TData = Awaited<ReturnType<typeof getDashboardStats>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardStats>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardStats>>,
          TError,
          Awaited<ReturnType<typeof getDashboardStats>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetDashboardStats<TData = Awaited<ReturnType<typeof getDashboardStats>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardStats>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardStats>>,
          TError,
          Awaited<ReturnType<typeof getDashboardStats>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetDashboardStats<TData = Awaited<ReturnType<typeof getDashboardStats>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardStats>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Portfolio KPIs, top buildings, and upcoming blackouts
 */

export function useGetDashboardStats<TData = Awaited<ReturnType<typeof getDashboardStats>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardStats>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetDashboardStatsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetDashboardTimeseriesUrl = (params?: GetDashboardTimeseriesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/dashboard/timeseries?${stringifiedParams}` : `/api/dashboard/timeseries`
}

/**
 * Real daily series for the selected window (UTC days). Declared-office
 * is 0 on days with no presence data. Returns zero series when the
 * database plugin is off.
 * @summary Daily bookings, check-ins, and declared-office series
 */
export const getDashboardTimeseries = async (params?: GetDashboardTimeseriesParams, options?: Parameters<typeof customFetch>[1]): Promise<Timeseries> => {

  return customFetch<Timeseries>(getGetDashboardTimeseriesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetDashboardTimeseriesQueryKey = (params?: GetDashboardTimeseriesParams,) => {
    return [
    `/api/dashboard/timeseries`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetDashboardTimeseriesQueryOptions = <TData = Awaited<ReturnType<typeof getDashboardTimeseries>>, TError = Error>(params?: GetDashboardTimeseriesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardTimeseries>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetDashboardTimeseriesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getDashboardTimeseries>>> = ({ signal }) => getDashboardTimeseries(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getDashboardTimeseries>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetDashboardTimeseriesQueryResult = NonNullable<Awaited<ReturnType<typeof getDashboardTimeseries>>>
export type GetDashboardTimeseriesQueryError = Error


export function useGetDashboardTimeseries<TData = Awaited<ReturnType<typeof getDashboardTimeseries>>, TError = Error>(
 params: undefined |  GetDashboardTimeseriesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardTimeseries>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardTimeseries>>,
          TError,
          Awaited<ReturnType<typeof getDashboardTimeseries>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetDashboardTimeseries<TData = Awaited<ReturnType<typeof getDashboardTimeseries>>, TError = Error>(
 params?: GetDashboardTimeseriesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardTimeseries>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getDashboardTimeseries>>,
          TError,
          Awaited<ReturnType<typeof getDashboardTimeseries>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetDashboardTimeseries<TData = Awaited<ReturnType<typeof getDashboardTimeseries>>, TError = Error>(
 params?: GetDashboardTimeseriesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardTimeseries>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Daily bookings, check-ins, and declared-office series
 */

export function useGetDashboardTimeseries<TData = Awaited<ReturnType<typeof getDashboardTimeseries>>, TError = Error>(
 params?: GetDashboardTimeseriesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getDashboardTimeseries>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetDashboardTimeseriesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPluginsStatusUrl = () => {




  return `/api/plugins/status`
}

/**
 * Public (unauthenticated) endpoint. Returns only readiness booleans and
 * the non-secret MSAL public client config required before sign-in. It
 * omits diagnostic reasons, capability details, and provider metadata.
 * @summary Public availability and auth bootstrap config for platform plugins
 */
export const getPluginsStatus = async ( options?: Parameters<typeof customFetch>[1]): Promise<PluginsStatus> => {

  return customFetch<PluginsStatus>(getGetPluginsStatusUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPluginsStatusQueryKey = () => {
    return [
    `/api/plugins/status`
    ] as const;
    }


export const getGetPluginsStatusQueryOptions = <TData = Awaited<ReturnType<typeof getPluginsStatus>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatus>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPluginsStatusQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPluginsStatus>>> = ({ signal }) => getPluginsStatus({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatus>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetPluginsStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getPluginsStatus>>>
export type GetPluginsStatusQueryError = unknown


export function useGetPluginsStatus<TData = Awaited<ReturnType<typeof getPluginsStatus>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatus>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPluginsStatus>>,
          TError,
          Awaited<ReturnType<typeof getPluginsStatus>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetPluginsStatus<TData = Awaited<ReturnType<typeof getPluginsStatus>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatus>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPluginsStatus>>,
          TError,
          Awaited<ReturnType<typeof getPluginsStatus>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetPluginsStatus<TData = Awaited<ReturnType<typeof getPluginsStatus>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatus>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Public availability and auth bootstrap config for platform plugins
 */

export function useGetPluginsStatus<TData = Awaited<ReturnType<typeof getPluginsStatus>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatus>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetPluginsStatusQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPluginsStatusDetailUrl = () => {




  return `/api/plugins/status/detail`
}

/**
 * Full plugin status for integration administrators.
 * @summary Detailed platform plugin diagnostics
 */
export const getPluginsStatusDetail = async ( options?: Parameters<typeof customFetch>[1]): Promise<PluginsStatusDetail> => {

  return customFetch<PluginsStatusDetail>(getGetPluginsStatusDetailUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPluginsStatusDetailQueryKey = () => {
    return [
    `/api/plugins/status/detail`
    ] as const;
    }


export const getGetPluginsStatusDetailQueryOptions = <TData = Awaited<ReturnType<typeof getPluginsStatusDetail>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatusDetail>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPluginsStatusDetailQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPluginsStatusDetail>>> = ({ signal }) => getPluginsStatusDetail({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatusDetail>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetPluginsStatusDetailQueryResult = NonNullable<Awaited<ReturnType<typeof getPluginsStatusDetail>>>
export type GetPluginsStatusDetailQueryError = Error


export function useGetPluginsStatusDetail<TData = Awaited<ReturnType<typeof getPluginsStatusDetail>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatusDetail>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPluginsStatusDetail>>,
          TError,
          Awaited<ReturnType<typeof getPluginsStatusDetail>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetPluginsStatusDetail<TData = Awaited<ReturnType<typeof getPluginsStatusDetail>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatusDetail>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPluginsStatusDetail>>,
          TError,
          Awaited<ReturnType<typeof getPluginsStatusDetail>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetPluginsStatusDetail<TData = Awaited<ReturnType<typeof getPluginsStatusDetail>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatusDetail>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Detailed platform plugin diagnostics
 */

export function useGetPluginsStatusDetail<TData = Awaited<ReturnType<typeof getPluginsStatusDetail>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPluginsStatusDetail>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetPluginsStatusDetailQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetCurrentUserUrl = () => {




  return `/api/auth/me`
}

/**
 * @summary Current authenticated user
 */
export const getCurrentUser = async ( options?: Parameters<typeof customFetch>[1]): Promise<CurrentUser> => {

  return customFetch<CurrentUser>(getGetCurrentUserUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetCurrentUserQueryKey = () => {
    return [
    `/api/auth/me`
    ] as const;
    }


export const getGetCurrentUserQueryOptions = <TData = Awaited<ReturnType<typeof getCurrentUser>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetCurrentUserQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getCurrentUser>>> = ({ signal }) => getCurrentUser({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetCurrentUserQueryResult = NonNullable<Awaited<ReturnType<typeof getCurrentUser>>>
export type GetCurrentUserQueryError = Error


export function useGetCurrentUser<TData = Awaited<ReturnType<typeof getCurrentUser>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCurrentUser>>,
          TError,
          Awaited<ReturnType<typeof getCurrentUser>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetCurrentUser<TData = Awaited<ReturnType<typeof getCurrentUser>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getCurrentUser>>,
          TError,
          Awaited<ReturnType<typeof getCurrentUser>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetCurrentUser<TData = Awaited<ReturnType<typeof getCurrentUser>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Current authenticated user
 */

export function useGetCurrentUser<TData = Awaited<ReturnType<typeof getCurrentUser>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getCurrentUser>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetCurrentUserQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetAdminInventoryHealthUrl = () => {




  return `/api/admin/inventory/health`
}

/**
 * Scaffold endpoint proving requireCapability(inventory:manage). Full inventory CRUD lands in S3.
 * @summary Inventory admin guard probe
 */
export const getAdminInventoryHealth = async ( options?: Parameters<typeof customFetch>[1]): Promise<AdminCapabilityProbe> => {

  return customFetch<AdminCapabilityProbe>(getGetAdminInventoryHealthUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAdminInventoryHealthQueryKey = () => {
    return [
    `/api/admin/inventory/health`
    ] as const;
    }


export const getGetAdminInventoryHealthQueryOptions = <TData = Awaited<ReturnType<typeof getAdminInventoryHealth>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdminInventoryHealth>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAdminInventoryHealthQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAdminInventoryHealth>>> = ({ signal }) => getAdminInventoryHealth({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAdminInventoryHealth>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetAdminInventoryHealthQueryResult = NonNullable<Awaited<ReturnType<typeof getAdminInventoryHealth>>>
export type GetAdminInventoryHealthQueryError = Error


export function useGetAdminInventoryHealth<TData = Awaited<ReturnType<typeof getAdminInventoryHealth>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdminInventoryHealth>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAdminInventoryHealth>>,
          TError,
          Awaited<ReturnType<typeof getAdminInventoryHealth>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAdminInventoryHealth<TData = Awaited<ReturnType<typeof getAdminInventoryHealth>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdminInventoryHealth>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAdminInventoryHealth>>,
          TError,
          Awaited<ReturnType<typeof getAdminInventoryHealth>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAdminInventoryHealth<TData = Awaited<ReturnType<typeof getAdminInventoryHealth>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdminInventoryHealth>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Inventory admin guard probe
 */

export function useGetAdminInventoryHealth<TData = Awaited<ReturnType<typeof getAdminInventoryHealth>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdminInventoryHealth>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetAdminInventoryHealthQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetAdminPoliciesHealthUrl = () => {




  return `/api/admin/policies/health`
}

/**
 * Scaffold endpoint proving requireCapability(policy:manage). Full policy engine lands in S4.
 * @summary Policy admin guard probe
 */
export const getAdminPoliciesHealth = async ( options?: Parameters<typeof customFetch>[1]): Promise<AdminCapabilityProbe> => {

  return customFetch<AdminCapabilityProbe>(getGetAdminPoliciesHealthUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAdminPoliciesHealthQueryKey = () => {
    return [
    `/api/admin/policies/health`
    ] as const;
    }


export const getGetAdminPoliciesHealthQueryOptions = <TData = Awaited<ReturnType<typeof getAdminPoliciesHealth>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdminPoliciesHealth>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAdminPoliciesHealthQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAdminPoliciesHealth>>> = ({ signal }) => getAdminPoliciesHealth({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAdminPoliciesHealth>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetAdminPoliciesHealthQueryResult = NonNullable<Awaited<ReturnType<typeof getAdminPoliciesHealth>>>
export type GetAdminPoliciesHealthQueryError = Error


export function useGetAdminPoliciesHealth<TData = Awaited<ReturnType<typeof getAdminPoliciesHealth>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdminPoliciesHealth>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAdminPoliciesHealth>>,
          TError,
          Awaited<ReturnType<typeof getAdminPoliciesHealth>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAdminPoliciesHealth<TData = Awaited<ReturnType<typeof getAdminPoliciesHealth>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdminPoliciesHealth>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAdminPoliciesHealth>>,
          TError,
          Awaited<ReturnType<typeof getAdminPoliciesHealth>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAdminPoliciesHealth<TData = Awaited<ReturnType<typeof getAdminPoliciesHealth>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdminPoliciesHealth>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Policy admin guard probe
 */

export function useGetAdminPoliciesHealth<TData = Awaited<ReturnType<typeof getAdminPoliciesHealth>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdminPoliciesHealth>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetAdminPoliciesHealthQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListAdminAuditEventsUrl = (params?: ListAdminAuditEventsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/audit-events?${stringifiedParams}` : `/api/admin/audit-events`
}

/**
 * Newest-first audit events for the selected entity, with text, action, actor and date filters.
 * @summary Search the entity audit history
 */
export const listAdminAuditEvents = async (params?: ListAdminAuditEventsParams, options?: Parameters<typeof customFetch>[1]): Promise<AuditEventListResponse> => {

  return customFetch<AuditEventListResponse>(getListAdminAuditEventsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAdminAuditEventsQueryKey = (params?: ListAdminAuditEventsParams,) => {
    return [
    `/api/admin/audit-events`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAdminAuditEventsQueryOptions = <TData = Awaited<ReturnType<typeof listAdminAuditEvents>>, TError = Error>(params?: ListAdminAuditEventsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAdminAuditEvents>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAdminAuditEventsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAdminAuditEvents>>> = ({ signal }) => listAdminAuditEvents(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAdminAuditEvents>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListAdminAuditEventsQueryResult = NonNullable<Awaited<ReturnType<typeof listAdminAuditEvents>>>
export type ListAdminAuditEventsQueryError = Error


export function useListAdminAuditEvents<TData = Awaited<ReturnType<typeof listAdminAuditEvents>>, TError = Error>(
 params: undefined |  ListAdminAuditEventsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAdminAuditEvents>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAdminAuditEvents>>,
          TError,
          Awaited<ReturnType<typeof listAdminAuditEvents>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListAdminAuditEvents<TData = Awaited<ReturnType<typeof listAdminAuditEvents>>, TError = Error>(
 params?: ListAdminAuditEventsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAdminAuditEvents>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAdminAuditEvents>>,
          TError,
          Awaited<ReturnType<typeof listAdminAuditEvents>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListAdminAuditEvents<TData = Awaited<ReturnType<typeof listAdminAuditEvents>>, TError = Error>(
 params?: ListAdminAuditEventsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAdminAuditEvents>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Search the entity audit history
 */

export function useListAdminAuditEvents<TData = Awaited<ReturnType<typeof listAdminAuditEvents>>, TError = Error>(
 params?: ListAdminAuditEventsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAdminAuditEvents>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListAdminAuditEventsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getImportHarbourExchangeUrl = () => {




  return `/api/admin/import-harbour-exchange`
}

/**
 * Idempotent administrator action scoped to the active default entity; preserves existing edits on repeat runs.
 * @summary Import Harbour Exchange source inventory for the default test entity
 */
export const importHarbourExchange = async ( options?: Parameters<typeof customFetch>[1]): Promise<HarbourImportResult> => {

  return customFetch<HarbourImportResult>(getImportHarbourExchangeUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getImportHarbourExchangeMutationKey = () => ['importHarbourExchange'] as const;

export const getImportHarbourExchangeMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importHarbourExchange>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof importHarbourExchange>>, TError,void, TContext> => {

const mutationKey = getImportHarbourExchangeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof importHarbourExchange>>, void> = () => {


          return  importHarbourExchange(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ImportHarbourExchangeMutationResult = NonNullable<Awaited<ReturnType<typeof importHarbourExchange>>>

    export type ImportHarbourExchangeMutationError = Error


    /**
 * @summary Import Harbour Exchange source inventory for the default test entity
 */
export const useImportHarbourExchange = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importHarbourExchange>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof importHarbourExchange>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getImportHarbourExchangeMutationOptions(options), queryClient);
    }

export const getGetHarbourLocationUrl = () => {




  return `/api/admin/harbour-exchange/location`
}

/**
 * @summary Preview the scoped Harbour Exchange deployment location correction
 */
export const getHarbourLocation = async ( options?: Parameters<typeof customFetch>[1]): Promise<HarbourLocationPreview> => {

  return customFetch<HarbourLocationPreview>(getGetHarbourLocationUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetHarbourLocationQueryKey = () => {
    return [
    `/api/admin/harbour-exchange/location`
    ] as const;
    }


export const getGetHarbourLocationQueryOptions = <TData = Awaited<ReturnType<typeof getHarbourLocation>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHarbourLocation>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetHarbourLocationQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getHarbourLocation>>> = ({ signal }) => getHarbourLocation({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getHarbourLocation>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetHarbourLocationQueryResult = NonNullable<Awaited<ReturnType<typeof getHarbourLocation>>>
export type GetHarbourLocationQueryError = Error


export function useGetHarbourLocation<TData = Awaited<ReturnType<typeof getHarbourLocation>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHarbourLocation>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHarbourLocation>>,
          TError,
          Awaited<ReturnType<typeof getHarbourLocation>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetHarbourLocation<TData = Awaited<ReturnType<typeof getHarbourLocation>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHarbourLocation>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHarbourLocation>>,
          TError,
          Awaited<ReturnType<typeof getHarbourLocation>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetHarbourLocation<TData = Awaited<ReturnType<typeof getHarbourLocation>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHarbourLocation>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Preview the scoped Harbour Exchange deployment location correction
 */

export function useGetHarbourLocation<TData = Awaited<ReturnType<typeof getHarbourLocation>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHarbourLocation>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetHarbourLocationQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getApplyHarbourLocationUrl = () => {




  return `/api/admin/harbour-exchange/location`
}

/**
 * Explicit, audited, idempotent administrator action; rejects unrelated source or administrator location edits.
 * @summary Apply the verified location to the existing Harbour site and building
 */
export const applyHarbourLocation = async ( options?: Parameters<typeof customFetch>[1]): Promise<HarbourLocationPreview> => {

  return customFetch<HarbourLocationPreview>(getApplyHarbourLocationUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getApplyHarbourLocationMutationKey = () => ['applyHarbourLocation'] as const;

export const getApplyHarbourLocationMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyHarbourLocation>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof applyHarbourLocation>>, TError,void, TContext> => {

const mutationKey = getApplyHarbourLocationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof applyHarbourLocation>>, void> = () => {


          return  applyHarbourLocation(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ApplyHarbourLocationMutationResult = NonNullable<Awaited<ReturnType<typeof applyHarbourLocation>>>

    export type ApplyHarbourLocationMutationError = Error


    /**
 * @summary Apply the verified location to the existing Harbour site and building
 */
export const useApplyHarbourLocation = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof applyHarbourLocation>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof applyHarbourLocation>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getApplyHarbourLocationMutationOptions(options), queryClient);
    }

export const getGetHarbourDemoUrl = () => {




  return `/api/admin/harbour-exchange/demo`
}

/**
 * @summary Read the default test entity's Harbour Exchange demonstration status
 */
export const getHarbourDemo = async ( options?: Parameters<typeof customFetch>[1]): Promise<HarbourDemoResult> => {

  return customFetch<HarbourDemoResult>(getGetHarbourDemoUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetHarbourDemoQueryKey = () => {
    return [
    `/api/admin/harbour-exchange/demo`
    ] as const;
    }


export const getGetHarbourDemoQueryOptions = <TData = Awaited<ReturnType<typeof getHarbourDemo>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHarbourDemo>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetHarbourDemoQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getHarbourDemo>>> = ({ signal }) => getHarbourDemo({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getHarbourDemo>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetHarbourDemoQueryResult = NonNullable<Awaited<ReturnType<typeof getHarbourDemo>>>
export type GetHarbourDemoQueryError = Error


export function useGetHarbourDemo<TData = Awaited<ReturnType<typeof getHarbourDemo>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHarbourDemo>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHarbourDemo>>,
          TError,
          Awaited<ReturnType<typeof getHarbourDemo>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetHarbourDemo<TData = Awaited<ReturnType<typeof getHarbourDemo>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHarbourDemo>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHarbourDemo>>,
          TError,
          Awaited<ReturnType<typeof getHarbourDemo>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetHarbourDemo<TData = Awaited<ReturnType<typeof getHarbourDemo>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHarbourDemo>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read the default test entity's Harbour Exchange demonstration status
 */

export function useGetHarbourDemo<TData = Awaited<ReturnType<typeof getHarbourDemo>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHarbourDemo>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetHarbourDemoQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRefreshHarbourDemoUrl = () => {




  return `/api/admin/harbour-exchange/demo`
}

/**
 * @summary Refresh managed Harbour Exchange bookings for a site-local date
 */
export const refreshHarbourDemo = async (harbourDemoRefreshInput: HarbourDemoRefreshInput, options?: Parameters<typeof customFetch>[1]): Promise<HarbourDemoResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<HarbourDemoResult>(getRefreshHarbourDemoUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(harbourDemoRefreshInput)
  }
);}





export const getRefreshHarbourDemoMutationKey = () => ['refreshHarbourDemo'] as const;

export const getRefreshHarbourDemoMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof refreshHarbourDemo>>, TError,RefreshHarbourDemoMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof refreshHarbourDemo>>, TError,RefreshHarbourDemoMutationVariables, TContext> => {

const mutationKey = getRefreshHarbourDemoMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof refreshHarbourDemo>>, RefreshHarbourDemoMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  refreshHarbourDemo(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RefreshHarbourDemoMutationResult = NonNullable<Awaited<ReturnType<typeof refreshHarbourDemo>>>
    export type RefreshHarbourDemoMutationBody = HarbourDemoRefreshInput
    export type RefreshHarbourDemoMutationError = Error
    export type RefreshHarbourDemoMutationVariables = {data: HarbourDemoRefreshInput}

    /**
 * @summary Refresh managed Harbour Exchange bookings for a site-local date
 */
export const useRefreshHarbourDemo = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof refreshHarbourDemo>>, TError,RefreshHarbourDemoMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof refreshHarbourDemo>>,
        TError,
        RefreshHarbourDemoMutationVariables,
        TContext
      > => {
      return useMutation(getRefreshHarbourDemoMutationOptions(options), queryClient);
    }

export const getImportBrisbaneLevel14Url = () => {




  return `/api/admin/import-brisbane`
}

/**
 * Idempotent administrator action; existing inventory and geometry edits are preserved.
 * @summary Import the reviewed Brisbane Level 14 plan and inventory
 */
export const importBrisbaneLevel14 = async ( options?: Parameters<typeof customFetch>[1]): Promise<BrisbaneImportResult> => {

  return customFetch<BrisbaneImportResult>(getImportBrisbaneLevel14Url(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getImportBrisbaneLevel14MutationKey = () => ['importBrisbaneLevel14'] as const;

export const getImportBrisbaneLevel14MutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importBrisbaneLevel14>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof importBrisbaneLevel14>>, TError,void, TContext> => {

const mutationKey = getImportBrisbaneLevel14MutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof importBrisbaneLevel14>>, void> = () => {


          return  importBrisbaneLevel14(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ImportBrisbaneLevel14MutationResult = NonNullable<Awaited<ReturnType<typeof importBrisbaneLevel14>>>

    export type ImportBrisbaneLevel14MutationError = Error


    /**
 * @summary Import the reviewed Brisbane Level 14 plan and inventory
 */
export const useImportBrisbaneLevel14 = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof importBrisbaneLevel14>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof importBrisbaneLevel14>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getImportBrisbaneLevel14MutationOptions(options), queryClient);
    }

export const getListAdminUsersUrl = (params?: ListAdminUsersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/users?${stringifiedParams}` : `/api/admin/users`
}

/**
 * Returns a paginated list of users in the active booking tenant and their product-role assignments. Requires integrations:manage.
 * @summary List tenant users and product roles
 */
export const listAdminUsers = async (params?: ListAdminUsersParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminUserList> => {

  return customFetch<AdminUserList>(getListAdminUsersUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAdminUsersQueryKey = (params?: ListAdminUsersParams,) => {
    return [
    `/api/admin/users`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAdminUsersQueryOptions = <TData = Awaited<ReturnType<typeof listAdminUsers>>, TError = Error>(params?: ListAdminUsersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAdminUsers>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAdminUsersQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAdminUsers>>> = ({ signal }) => listAdminUsers(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAdminUsers>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListAdminUsersQueryResult = NonNullable<Awaited<ReturnType<typeof listAdminUsers>>>
export type ListAdminUsersQueryError = Error


export function useListAdminUsers<TData = Awaited<ReturnType<typeof listAdminUsers>>, TError = Error>(
 params: undefined |  ListAdminUsersParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAdminUsers>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAdminUsers>>,
          TError,
          Awaited<ReturnType<typeof listAdminUsers>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListAdminUsers<TData = Awaited<ReturnType<typeof listAdminUsers>>, TError = Error>(
 params?: ListAdminUsersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAdminUsers>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAdminUsers>>,
          TError,
          Awaited<ReturnType<typeof listAdminUsers>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListAdminUsers<TData = Awaited<ReturnType<typeof listAdminUsers>>, TError = Error>(
 params?: ListAdminUsersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAdminUsers>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List tenant users and product roles
 */

export function useListAdminUsers<TData = Awaited<ReturnType<typeof listAdminUsers>>, TError = Error>(
 params?: ListAdminUsersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAdminUsers>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListAdminUsersQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getAssignAdminUserRoleUrl = (userId: string,) => {




  return `/api/admin/users/${userId}/roles`
}

/**
 * Assigns a validated product role, writes a tenant audit event, and invalidates cached identity grants. Assigning it_admin additionally requires the target principal to be in BOOKING_BOOTSTRAP_ADMIN_OIDS or BOOKING_BOOTSTRAP_ADMIN_EMAILS. Requires integrations:manage.
 * @summary Assign a product role to a tenant user
 */
export const assignAdminUserRole = async (userId: string,
    assignAdminUserRoleBody: AssignAdminUserRoleBody, options?: Parameters<typeof customFetch>[1]): Promise<AdminUserRoleAssignment> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AdminUserRoleAssignment>(getAssignAdminUserRoleUrl(userId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(assignAdminUserRoleBody)
  }
);}





export const getAssignAdminUserRoleMutationKey = () => ['assignAdminUserRole'] as const;

export const getAssignAdminUserRoleMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof assignAdminUserRole>>, TError,AssignAdminUserRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof assignAdminUserRole>>, TError,AssignAdminUserRoleMutationVariables, TContext> => {

const mutationKey = getAssignAdminUserRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof assignAdminUserRole>>, AssignAdminUserRoleMutationVariables> = (props) => {
          const {userId,data} = props ?? {};

          return  assignAdminUserRole(userId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AssignAdminUserRoleMutationResult = NonNullable<Awaited<ReturnType<typeof assignAdminUserRole>>>
    export type AssignAdminUserRoleMutationBody = AssignAdminUserRoleBody
    export type AssignAdminUserRoleMutationError = Error
    export type AssignAdminUserRoleMutationVariables = {userId: string;data: AssignAdminUserRoleBody}

    /**
 * @summary Assign a product role to a tenant user
 */
export const useAssignAdminUserRole = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof assignAdminUserRole>>, TError,AssignAdminUserRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof assignAdminUserRole>>,
        TError,
        AssignAdminUserRoleMutationVariables,
        TContext
      > => {
      return useMutation(getAssignAdminUserRoleMutationOptions(options), queryClient);
    }

export const getRemoveAdminUserRoleUrl = (userId: string,
    roleCode: ProductRoleCode,) => {




  return `/api/admin/users/${userId}/roles/${roleCode}`
}

/**
 * Removes a validated product role, writes a tenant audit event, and invalidates cached identity grants. A caller cannot remove their own final it_admin assignment. Requires integrations:manage.
 * @summary Remove a product role from a tenant user
 */
export const removeAdminUserRole = async (userId: string,
    roleCode: ProductRoleCode, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getRemoveAdminUserRoleUrl(userId,roleCode),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getRemoveAdminUserRoleMutationKey = () => ['removeAdminUserRole'] as const;

export const getRemoveAdminUserRoleMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeAdminUserRole>>, TError,RemoveAdminUserRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof removeAdminUserRole>>, TError,RemoveAdminUserRoleMutationVariables, TContext> => {

const mutationKey = getRemoveAdminUserRoleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof removeAdminUserRole>>, RemoveAdminUserRoleMutationVariables> = (props) => {
          const {userId,roleCode} = props ?? {};

          return  removeAdminUserRole(userId,roleCode,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RemoveAdminUserRoleMutationResult = NonNullable<Awaited<ReturnType<typeof removeAdminUserRole>>>

    export type RemoveAdminUserRoleMutationError = Error
    export type RemoveAdminUserRoleMutationVariables = {userId: string;roleCode: ProductRoleCode}

    /**
 * @summary Remove a product role from a tenant user
 */
export const useRemoveAdminUserRole = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeAdminUserRole>>, TError,RemoveAdminUserRoleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof removeAdminUserRole>>,
        TError,
        RemoveAdminUserRoleMutationVariables,
        TContext
      > => {
      return useMutation(getRemoveAdminUserRoleMutationOptions(options), queryClient);
    }

export const getGetMyManagerScopeUrl = (params?: GetMyManagerScopeParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/me/manager-scope?${stringifiedParams}` : `/api/me/manager-scope`
}

/**
 * Directory mode resolves fresh direct reports and filters them to current members of the active tenant. Local mode uses only audited assignments. An unavailable directory returns status=unavailable and no reports. Directory access uses the manager's browser-acquired delegated Graph credential, checked by Graph against the independently authenticated API actor and tenant.
 * @summary Resolve the signed-in manager's own direct reports
 */
export const getMyManagerScope = async (params?: GetMyManagerScopeParams, options?: Parameters<typeof customFetch>[1]): Promise<ManagerScopeResponse> => {

  return customFetch<ManagerScopeResponse>(getGetMyManagerScopeUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMyManagerScopeQueryKey = (params?: GetMyManagerScopeParams,) => {
    return [
    `/api/me/manager-scope`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetMyManagerScopeQueryOptions = <TData = Awaited<ReturnType<typeof getMyManagerScope>>, TError = Error>(params?: GetMyManagerScopeParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyManagerScope>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMyManagerScopeQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMyManagerScope>>> = ({ signal }) => getMyManagerScope(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMyManagerScope>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMyManagerScopeQueryResult = NonNullable<Awaited<ReturnType<typeof getMyManagerScope>>>
export type GetMyManagerScopeQueryError = Error


export function useGetMyManagerScope<TData = Awaited<ReturnType<typeof getMyManagerScope>>, TError = Error>(
 params: undefined |  GetMyManagerScopeParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyManagerScope>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyManagerScope>>,
          TError,
          Awaited<ReturnType<typeof getMyManagerScope>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyManagerScope<TData = Awaited<ReturnType<typeof getMyManagerScope>>, TError = Error>(
 params?: GetMyManagerScopeParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyManagerScope>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyManagerScope>>,
          TError,
          Awaited<ReturnType<typeof getMyManagerScope>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyManagerScope<TData = Awaited<ReturnType<typeof getMyManagerScope>>, TError = Error>(
 params?: GetMyManagerScopeParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyManagerScope>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Resolve the signed-in manager's own direct reports
 */

export function useGetMyManagerScope<TData = Awaited<ReturnType<typeof getMyManagerScope>>, TError = Error>(
 params?: GetMyManagerScopeParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyManagerScope>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMyManagerScopeQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetAdminManagerScopeUrl = (managerUserId: string,
    params?: GetAdminManagerScopeParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/managers/${managerUserId}/scope?${stringifiedParams}` : `/api/admin/managers/${managerUserId}/scope`
}

/**
 * @summary Review a tenant manager's source mode and local assignments
 */
export const getAdminManagerScope = async (managerUserId: string,
    params?: GetAdminManagerScopeParams, options?: Parameters<typeof customFetch>[1]): Promise<AdminManagerScope> => {

  return customFetch<AdminManagerScope>(getGetAdminManagerScopeUrl(managerUserId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAdminManagerScopeQueryKey = (managerUserId: string,
    params?: GetAdminManagerScopeParams,) => {
    return [
    `/api/admin/managers/${managerUserId}/scope`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetAdminManagerScopeQueryOptions = <TData = Awaited<ReturnType<typeof getAdminManagerScope>>, TError = Error>(managerUserId: string,
    params?: GetAdminManagerScopeParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdminManagerScope>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAdminManagerScopeQueryKey(managerUserId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAdminManagerScope>>> = ({ signal }) => getAdminManagerScope(managerUserId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: managerUserId !== null && managerUserId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAdminManagerScope>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetAdminManagerScopeQueryResult = NonNullable<Awaited<ReturnType<typeof getAdminManagerScope>>>
export type GetAdminManagerScopeQueryError = Error


export function useGetAdminManagerScope<TData = Awaited<ReturnType<typeof getAdminManagerScope>>, TError = Error>(
 managerUserId: string,
    params: undefined |  GetAdminManagerScopeParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdminManagerScope>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAdminManagerScope>>,
          TError,
          Awaited<ReturnType<typeof getAdminManagerScope>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAdminManagerScope<TData = Awaited<ReturnType<typeof getAdminManagerScope>>, TError = Error>(
 managerUserId: string,
    params?: GetAdminManagerScopeParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdminManagerScope>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAdminManagerScope>>,
          TError,
          Awaited<ReturnType<typeof getAdminManagerScope>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAdminManagerScope<TData = Awaited<ReturnType<typeof getAdminManagerScope>>, TError = Error>(
 managerUserId: string,
    params?: GetAdminManagerScopeParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdminManagerScope>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Review a tenant manager's source mode and local assignments
 */

export function useGetAdminManagerScope<TData = Awaited<ReturnType<typeof getAdminManagerScope>>, TError = Error>(
 managerUserId: string,
    params?: GetAdminManagerScopeParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAdminManagerScope>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetAdminManagerScopeQueryOptions(managerUserId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSetAdminManagerScopeModeUrl = (managerUserId: string,) => {




  return `/api/admin/managers/${managerUserId}/scope`
}

/**
 * @summary Explicitly select directory or local replacement scope
 */
export const setAdminManagerScopeMode = async (managerUserId: string,
    managerScopeModeRequest: ManagerScopeModeRequest, options?: Parameters<typeof customFetch>[1]): Promise<ManagerScopeModeResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ManagerScopeModeResult>(getSetAdminManagerScopeModeUrl(managerUserId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(managerScopeModeRequest)
  }
);}





export const getSetAdminManagerScopeModeMutationKey = () => ['setAdminManagerScopeMode'] as const;

export const getSetAdminManagerScopeModeMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setAdminManagerScopeMode>>, TError,SetAdminManagerScopeModeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setAdminManagerScopeMode>>, TError,SetAdminManagerScopeModeMutationVariables, TContext> => {

const mutationKey = getSetAdminManagerScopeModeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setAdminManagerScopeMode>>, SetAdminManagerScopeModeMutationVariables> = (props) => {
          const {managerUserId,data} = props ?? {};

          return  setAdminManagerScopeMode(managerUserId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetAdminManagerScopeModeMutationResult = NonNullable<Awaited<ReturnType<typeof setAdminManagerScopeMode>>>
    export type SetAdminManagerScopeModeMutationBody = ManagerScopeModeRequest
    export type SetAdminManagerScopeModeMutationError = Error
    export type SetAdminManagerScopeModeMutationVariables = {managerUserId: string;data: ManagerScopeModeRequest}

    /**
 * @summary Explicitly select directory or local replacement scope
 */
export const useSetAdminManagerScopeMode = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setAdminManagerScopeMode>>, TError,SetAdminManagerScopeModeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setAdminManagerScopeMode>>,
        TError,
        SetAdminManagerScopeModeMutationVariables,
        TContext
      > => {
      return useMutation(getSetAdminManagerScopeModeMutationOptions(options), queryClient);
    }

export const getAddAdminManagerReportUrl = (managerUserId: string,) => {




  return `/api/admin/managers/${managerUserId}/reports`
}

/**
 * @summary Add an audited local direct-report assignment
 */
export const addAdminManagerReport = async (managerUserId: string,
    managerReportRequest: ManagerReportRequest, options?: Parameters<typeof customFetch>[1]): Promise<ManagerReport> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ManagerReport>(getAddAdminManagerReportUrl(managerUserId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(managerReportRequest)
  }
);}





export const getAddAdminManagerReportMutationKey = () => ['addAdminManagerReport'] as const;

export const getAddAdminManagerReportMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addAdminManagerReport>>, TError,AddAdminManagerReportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof addAdminManagerReport>>, TError,AddAdminManagerReportMutationVariables, TContext> => {

const mutationKey = getAddAdminManagerReportMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof addAdminManagerReport>>, AddAdminManagerReportMutationVariables> = (props) => {
          const {managerUserId,data} = props ?? {};

          return  addAdminManagerReport(managerUserId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AddAdminManagerReportMutationResult = NonNullable<Awaited<ReturnType<typeof addAdminManagerReport>>>
    export type AddAdminManagerReportMutationBody = ManagerReportRequest
    export type AddAdminManagerReportMutationError = Error
    export type AddAdminManagerReportMutationVariables = {managerUserId: string;data: ManagerReportRequest}

    /**
 * @summary Add an audited local direct-report assignment
 */
export const useAddAdminManagerReport = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addAdminManagerReport>>, TError,AddAdminManagerReportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof addAdminManagerReport>>,
        TError,
        AddAdminManagerReportMutationVariables,
        TContext
      > => {
      return useMutation(getAddAdminManagerReportMutationOptions(options), queryClient);
    }

export const getRemoveAdminManagerReportUrl = (managerUserId: string,
    reportUserId: string,) => {




  return `/api/admin/managers/${managerUserId}/reports/${reportUserId}`
}

/**
 * @summary Remove an audited local direct-report assignment
 */
export const removeAdminManagerReport = async (managerUserId: string,
    reportUserId: string,
    managerScopeReasonRequest: ManagerScopeReasonRequest, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<void>(getRemoveAdminManagerReportUrl(managerUserId,reportUserId),
  {
    ...options,
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(managerScopeReasonRequest)
  }
);}





export const getRemoveAdminManagerReportMutationKey = () => ['removeAdminManagerReport'] as const;

export const getRemoveAdminManagerReportMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeAdminManagerReport>>, TError,RemoveAdminManagerReportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof removeAdminManagerReport>>, TError,RemoveAdminManagerReportMutationVariables, TContext> => {

const mutationKey = getRemoveAdminManagerReportMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof removeAdminManagerReport>>, RemoveAdminManagerReportMutationVariables> = (props) => {
          const {managerUserId,reportUserId,data} = props ?? {};

          return  removeAdminManagerReport(managerUserId,reportUserId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RemoveAdminManagerReportMutationResult = NonNullable<Awaited<ReturnType<typeof removeAdminManagerReport>>>
    export type RemoveAdminManagerReportMutationBody = ManagerScopeReasonRequest
    export type RemoveAdminManagerReportMutationError = Error
    export type RemoveAdminManagerReportMutationVariables = {managerUserId: string;reportUserId: string;data: ManagerScopeReasonRequest}

    /**
 * @summary Remove an audited local direct-report assignment
 */
export const useRemoveAdminManagerReport = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeAdminManagerReport>>, TError,RemoveAdminManagerReportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof removeAdminManagerReport>>,
        TError,
        RemoveAdminManagerReportMutationVariables,
        TContext
      > => {
      return useMutation(getRemoveAdminManagerReportMutationOptions(options), queryClient);
    }

export const getGetIntegrationsStatusUrl = () => {




  return `/api/integrations/status`
}

/**
 * Honest degradation signals for room search banners and admin connector cards (FR-AD-050, FR-DG-001–004, FR-EU-017). No secrets returned.
 * @summary Connector readiness (Graph, LLM, DB, auth)
 */
export const getIntegrationsStatus = async ( options?: Parameters<typeof customFetch>[1]): Promise<IntegrationsStatus> => {

  return customFetch<IntegrationsStatus>(getGetIntegrationsStatusUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetIntegrationsStatusQueryKey = () => {
    return [
    `/api/integrations/status`
    ] as const;
    }


export const getGetIntegrationsStatusQueryOptions = <TData = Awaited<ReturnType<typeof getIntegrationsStatus>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getIntegrationsStatus>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetIntegrationsStatusQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getIntegrationsStatus>>> = ({ signal }) => getIntegrationsStatus({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getIntegrationsStatus>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetIntegrationsStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getIntegrationsStatus>>>
export type GetIntegrationsStatusQueryError = Error


export function useGetIntegrationsStatus<TData = Awaited<ReturnType<typeof getIntegrationsStatus>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getIntegrationsStatus>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getIntegrationsStatus>>,
          TError,
          Awaited<ReturnType<typeof getIntegrationsStatus>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetIntegrationsStatus<TData = Awaited<ReturnType<typeof getIntegrationsStatus>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getIntegrationsStatus>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getIntegrationsStatus>>,
          TError,
          Awaited<ReturnType<typeof getIntegrationsStatus>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetIntegrationsStatus<TData = Awaited<ReturnType<typeof getIntegrationsStatus>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getIntegrationsStatus>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Connector readiness (Graph, LLM, DB, auth)
 */

export function useGetIntegrationsStatus<TData = Awaited<ReturnType<typeof getIntegrationsStatus>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getIntegrationsStatus>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetIntegrationsStatusQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getTestGraphConnectionUrl = () => {




  return `/api/integrations/graph/test-connection`
}

/**
 * Live re-probe of app-only Graph credentials and Places access. Requires integrations:manage. Never returns secrets.
 * @summary Probe Microsoft Graph room connector (admin)
 */
export const testGraphConnection = async ( options?: Parameters<typeof customFetch>[1]): Promise<GraphConnectionTestResult> => {

  return customFetch<GraphConnectionTestResult>(getTestGraphConnectionUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getTestGraphConnectionMutationKey = () => ['testGraphConnection'] as const;

export const getTestGraphConnectionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof testGraphConnection>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof testGraphConnection>>, TError,void, TContext> => {

const mutationKey = getTestGraphConnectionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof testGraphConnection>>, void> = () => {


          return  testGraphConnection(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TestGraphConnectionMutationResult = NonNullable<Awaited<ReturnType<typeof testGraphConnection>>>

    export type TestGraphConnectionMutationError = Error


    /**
 * @summary Probe Microsoft Graph room connector (admin)
 */
export const useTestGraphConnection = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof testGraphConnection>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof testGraphConnection>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getTestGraphConnectionMutationOptions(options), queryClient);
    }

export const getListGraphRoomsUrl = (params?: ListGraphRoomsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/integrations/graph/rooms?${stringifiedParams}` : `/api/integrations/graph/rooms`
}

/**
 * Places/microsoft.graph.room cages for inventory matching by mailbox UPN. Live Graph only. Requires integrations:manage.
 * @summary List Exchange room places from Graph (admin discovery)
 */
export const listGraphRooms = async (params?: ListGraphRoomsParams, options?: Parameters<typeof customFetch>[1]): Promise<GraphRoomListResponse> => {

  return customFetch<GraphRoomListResponse>(getListGraphRoomsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListGraphRoomsQueryKey = (params?: ListGraphRoomsParams,) => {
    return [
    `/api/integrations/graph/rooms`, ...(params ? [params] : [])
    ] as const;
    }


export const getListGraphRoomsQueryOptions = <TData = Awaited<ReturnType<typeof listGraphRooms>>, TError = Error>(params?: ListGraphRoomsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGraphRooms>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListGraphRoomsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listGraphRooms>>> = ({ signal }) => listGraphRooms(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listGraphRooms>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListGraphRoomsQueryResult = NonNullable<Awaited<ReturnType<typeof listGraphRooms>>>
export type ListGraphRoomsQueryError = Error


export function useListGraphRooms<TData = Awaited<ReturnType<typeof listGraphRooms>>, TError = Error>(
 params: undefined |  ListGraphRoomsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGraphRooms>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listGraphRooms>>,
          TError,
          Awaited<ReturnType<typeof listGraphRooms>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListGraphRooms<TData = Awaited<ReturnType<typeof listGraphRooms>>, TError = Error>(
 params?: ListGraphRoomsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGraphRooms>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listGraphRooms>>,
          TError,
          Awaited<ReturnType<typeof listGraphRooms>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListGraphRooms<TData = Awaited<ReturnType<typeof listGraphRooms>>, TError = Error>(
 params?: ListGraphRoomsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGraphRooms>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Exchange room places from Graph (admin discovery)
 */

export function useListGraphRooms<TData = Awaited<ReturnType<typeof listGraphRooms>>, TError = Error>(
 params?: ListGraphRoomsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGraphRooms>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListGraphRoomsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetSiteFormOptionsUrl = () => {




  return `/api/inventory/site-form-options`
}

/**
 * Per-entity site setup rules. `siteCoordinatesDisabled` hides and refuses site latitude/longitude; `australianTimezoneList` restricts the site timezone to the Australian zones returned in `timezones`.
 * @summary Site setup options for the selected entity
 */
export const getSiteFormOptions = async ( options?: Parameters<typeof customFetch>[1]): Promise<SiteFormOptions> => {

  return customFetch<SiteFormOptions>(getGetSiteFormOptionsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSiteFormOptionsQueryKey = () => {
    return [
    `/api/inventory/site-form-options`
    ] as const;
    }


export const getGetSiteFormOptionsQueryOptions = <TData = Awaited<ReturnType<typeof getSiteFormOptions>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteFormOptions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSiteFormOptionsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSiteFormOptions>>> = ({ signal }) => getSiteFormOptions({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSiteFormOptions>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetSiteFormOptionsQueryResult = NonNullable<Awaited<ReturnType<typeof getSiteFormOptions>>>
export type GetSiteFormOptionsQueryError = Error


export function useGetSiteFormOptions<TData = Awaited<ReturnType<typeof getSiteFormOptions>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteFormOptions>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSiteFormOptions>>,
          TError,
          Awaited<ReturnType<typeof getSiteFormOptions>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSiteFormOptions<TData = Awaited<ReturnType<typeof getSiteFormOptions>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteFormOptions>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSiteFormOptions>>,
          TError,
          Awaited<ReturnType<typeof getSiteFormOptions>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSiteFormOptions<TData = Awaited<ReturnType<typeof getSiteFormOptions>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteFormOptions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Site setup options for the selected entity
 */

export function useGetSiteFormOptions<TData = Awaited<ReturnType<typeof getSiteFormOptions>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteFormOptions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetSiteFormOptionsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetBookingOptionsUrl = () => {




  return `/api/bookings/options`
}

/**
 * The same one-screen Where, When and What layout is used for every entity. The legacy `oneScreenBook` response field is always true.
 * @summary Booking layout for the selected entity
 */
export const getBookingOptions = async ( options?: Parameters<typeof customFetch>[1]): Promise<BookingOptions> => {

  return customFetch<BookingOptions>(getGetBookingOptionsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetBookingOptionsQueryKey = () => {
    return [
    `/api/bookings/options`
    ] as const;
    }


export const getGetBookingOptionsQueryOptions = <TData = Awaited<ReturnType<typeof getBookingOptions>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBookingOptions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetBookingOptionsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getBookingOptions>>> = ({ signal }) => getBookingOptions({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getBookingOptions>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetBookingOptionsQueryResult = NonNullable<Awaited<ReturnType<typeof getBookingOptions>>>
export type GetBookingOptionsQueryError = Error


export function useGetBookingOptions<TData = Awaited<ReturnType<typeof getBookingOptions>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBookingOptions>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBookingOptions>>,
          TError,
          Awaited<ReturnType<typeof getBookingOptions>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetBookingOptions<TData = Awaited<ReturnType<typeof getBookingOptions>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBookingOptions>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBookingOptions>>,
          TError,
          Awaited<ReturnType<typeof getBookingOptions>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetBookingOptions<TData = Awaited<ReturnType<typeof getBookingOptions>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBookingOptions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Booking layout for the selected entity
 */

export function useGetBookingOptions<TData = Awaited<ReturnType<typeof getBookingOptions>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBookingOptions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetBookingOptionsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListSitesUrl = (params?: ListSitesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/sites?${stringifiedParams}` : `/api/sites`
}

/**
 * @summary List sites
 */
export const listSites = async (params?: ListSitesParams, options?: Parameters<typeof customFetch>[1]): Promise<SiteListResponse> => {

  return customFetch<SiteListResponse>(getListSitesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSitesQueryKey = (params?: ListSitesParams,) => {
    return [
    `/api/sites`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSitesQueryOptions = <TData = Awaited<ReturnType<typeof listSites>>, TError = Error>(params?: ListSitesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSites>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSitesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSites>>> = ({ signal }) => listSites(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSites>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListSitesQueryResult = NonNullable<Awaited<ReturnType<typeof listSites>>>
export type ListSitesQueryError = Error


export function useListSites<TData = Awaited<ReturnType<typeof listSites>>, TError = Error>(
 params: undefined |  ListSitesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSites>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSites>>,
          TError,
          Awaited<ReturnType<typeof listSites>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListSites<TData = Awaited<ReturnType<typeof listSites>>, TError = Error>(
 params?: ListSitesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSites>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSites>>,
          TError,
          Awaited<ReturnType<typeof listSites>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListSites<TData = Awaited<ReturnType<typeof listSites>>, TError = Error>(
 params?: ListSitesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSites>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List sites
 */

export function useListSites<TData = Awaited<ReturnType<typeof listSites>>, TError = Error>(
 params?: ListSitesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSites>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListSitesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateSiteUrl = () => {




  return `/api/sites`
}

/**
 * @summary Create site
 */
export const createSite = async (createSiteBody: CreateSite, options?: Parameters<typeof customFetch>[1]): Promise<Site> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Site>(getCreateSiteUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createSiteBody)
  }
);}





export const getCreateSiteMutationKey = () => ['createSite'] as const;

export const getCreateSiteMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSite>>, TError,CreateSiteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createSite>>, TError,CreateSiteMutationVariables, TContext> => {

const mutationKey = getCreateSiteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createSite>>, CreateSiteMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createSite(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateSiteMutationResult = NonNullable<Awaited<ReturnType<typeof createSite>>>
    export type CreateSiteMutationBody = CreateSite
    export type CreateSiteMutationError = Error
    export type CreateSiteMutationVariables = {data: CreateSite}

    /**
 * @summary Create site
 */
export const useCreateSite = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSite>>, TError,CreateSiteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createSite>>,
        TError,
        CreateSiteMutationVariables,
        TContext
      > => {
      return useMutation(getCreateSiteMutationOptions(options), queryClient);
    }

export const getGetSiteUrl = (id: string,) => {




  return `/api/sites/${id}`
}

/**
 * @summary Get site
 */
export const getSite = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Site> => {

  return customFetch<Site>(getGetSiteUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSiteQueryKey = (id: string,) => {
    return [
    `/api/sites/${id}`
    ] as const;
    }


export const getGetSiteQueryOptions = <TData = Awaited<ReturnType<typeof getSite>>, TError = Error>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSite>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSiteQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSite>>> = ({ signal }) => getSite(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSite>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetSiteQueryResult = NonNullable<Awaited<ReturnType<typeof getSite>>>
export type GetSiteQueryError = Error


export function useGetSite<TData = Awaited<ReturnType<typeof getSite>>, TError = Error>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSite>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSite>>,
          TError,
          Awaited<ReturnType<typeof getSite>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSite<TData = Awaited<ReturnType<typeof getSite>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSite>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSite>>,
          TError,
          Awaited<ReturnType<typeof getSite>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSite<TData = Awaited<ReturnType<typeof getSite>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSite>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get site
 */

export function useGetSite<TData = Awaited<ReturnType<typeof getSite>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSite>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetSiteQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateSiteUrl = (id: string,) => {




  return `/api/sites/${id}`
}

/**
 * @summary Update site
 */
export const updateSite = async (id: string,
    updateSiteBody: UpdateSite, options?: Parameters<typeof customFetch>[1]): Promise<Site> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Site>(getUpdateSiteUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateSiteBody)
  }
);}





export const getUpdateSiteMutationKey = () => ['updateSite'] as const;

export const getUpdateSiteMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSite>>, TError,UpdateSiteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateSite>>, TError,UpdateSiteMutationVariables, TContext> => {

const mutationKey = getUpdateSiteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateSite>>, UpdateSiteMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateSite(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateSiteMutationResult = NonNullable<Awaited<ReturnType<typeof updateSite>>>
    export type UpdateSiteMutationBody = UpdateSite
    export type UpdateSiteMutationError = unknown
    export type UpdateSiteMutationVariables = {id: string;data: UpdateSite}

    /**
 * @summary Update site
 */
export const useUpdateSite = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSite>>, TError,UpdateSiteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateSite>>,
        TError,
        UpdateSiteMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateSiteMutationOptions(options), queryClient);
    }

export const getDeleteSiteUrl = (id: string,) => {




  return `/api/sites/${id}`
}

/**
 * Blocked while the site still has buildings or is referenced by visitor records.
 * @summary Delete a site
 */
export const deleteSite = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteSiteUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteSiteMutationKey = () => ['deleteSite'] as const;

export const getDeleteSiteMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteSite>>, TError,DeleteSiteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteSite>>, TError,DeleteSiteMutationVariables, TContext> => {

const mutationKey = getDeleteSiteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteSite>>, DeleteSiteMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteSite(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteSiteMutationResult = NonNullable<Awaited<ReturnType<typeof deleteSite>>>

    export type DeleteSiteMutationError = Error
    export type DeleteSiteMutationVariables = {id: string}

    /**
 * @summary Delete a site
 */
export const useDeleteSite = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteSite>>, TError,DeleteSiteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteSite>>,
        TError,
        DeleteSiteMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteSiteMutationOptions(options), queryClient);
    }

export const getGetSiteOpeningHoursUrl = (id: string,) => {




  return `/api/sites/${id}/opening-hours`
}

/**
 * @summary Get configured site-local opening hours for utilisation
 */
export const getSiteOpeningHours = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SiteOpeningHours> => {

  return customFetch<SiteOpeningHours>(getGetSiteOpeningHoursUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSiteOpeningHoursQueryKey = (id: string,) => {
    return [
    `/api/sites/${id}/opening-hours`
    ] as const;
    }


export const getGetSiteOpeningHoursQueryOptions = <TData = Awaited<ReturnType<typeof getSiteOpeningHours>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteOpeningHours>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSiteOpeningHoursQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSiteOpeningHours>>> = ({ signal }) => getSiteOpeningHours(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSiteOpeningHours>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetSiteOpeningHoursQueryResult = NonNullable<Awaited<ReturnType<typeof getSiteOpeningHours>>>
export type GetSiteOpeningHoursQueryError = unknown


export function useGetSiteOpeningHours<TData = Awaited<ReturnType<typeof getSiteOpeningHours>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteOpeningHours>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSiteOpeningHours>>,
          TError,
          Awaited<ReturnType<typeof getSiteOpeningHours>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSiteOpeningHours<TData = Awaited<ReturnType<typeof getSiteOpeningHours>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteOpeningHours>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSiteOpeningHours>>,
          TError,
          Awaited<ReturnType<typeof getSiteOpeningHours>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSiteOpeningHours<TData = Awaited<ReturnType<typeof getSiteOpeningHours>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteOpeningHours>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get configured site-local opening hours for utilisation
 */

export function useGetSiteOpeningHours<TData = Awaited<ReturnType<typeof getSiteOpeningHours>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteOpeningHours>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetSiteOpeningHoursQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPutSiteOpeningHoursUrl = (id: string,) => {




  return `/api/sites/${id}/opening-hours`
}

/**
 * @summary Replace site-local weekly hours and dated overrides
 */
export const putSiteOpeningHours = async (id: string,
    putSiteOpeningHoursBody: PutSiteOpeningHours, options?: Parameters<typeof customFetch>[1]): Promise<SiteOpeningHours> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SiteOpeningHours>(getPutSiteOpeningHoursUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(putSiteOpeningHoursBody)
  }
);}





export const getPutSiteOpeningHoursMutationKey = () => ['putSiteOpeningHours'] as const;

export const getPutSiteOpeningHoursMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putSiteOpeningHours>>, TError,PutSiteOpeningHoursMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof putSiteOpeningHours>>, TError,PutSiteOpeningHoursMutationVariables, TContext> => {

const mutationKey = getPutSiteOpeningHoursMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof putSiteOpeningHours>>, PutSiteOpeningHoursMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  putSiteOpeningHours(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PutSiteOpeningHoursMutationResult = NonNullable<Awaited<ReturnType<typeof putSiteOpeningHours>>>
    export type PutSiteOpeningHoursMutationBody = PutSiteOpeningHours
    export type PutSiteOpeningHoursMutationError = Error
    export type PutSiteOpeningHoursMutationVariables = {id: string;data: PutSiteOpeningHours}

    /**
 * @summary Replace site-local weekly hours and dated overrides
 */
export const usePutSiteOpeningHours = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putSiteOpeningHours>>, TError,PutSiteOpeningHoursMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof putSiteOpeningHours>>,
        TError,
        PutSiteOpeningHoursMutationVariables,
        TContext
      > => {
      return useMutation(getPutSiteOpeningHoursMutationOptions(options), queryClient);
    }

export const getGetSiteInductionChecklistUrl = (id: string,) => {




  return `/api/sites/${id}/induction-checklist`
}

/**
 * @summary Get the active OH&S induction checklist for a site
 */
export const getSiteInductionChecklist = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SiteInductionChecklist> => {

  return customFetch<SiteInductionChecklist>(getGetSiteInductionChecklistUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSiteInductionChecklistQueryKey = (id: string,) => {
    return [
    `/api/sites/${id}/induction-checklist`
    ] as const;
    }


export const getGetSiteInductionChecklistQueryOptions = <TData = Awaited<ReturnType<typeof getSiteInductionChecklist>>, TError = Error>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteInductionChecklist>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSiteInductionChecklistQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSiteInductionChecklist>>> = ({ signal }) => getSiteInductionChecklist(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSiteInductionChecklist>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetSiteInductionChecklistQueryResult = NonNullable<Awaited<ReturnType<typeof getSiteInductionChecklist>>>
export type GetSiteInductionChecklistQueryError = Error


export function useGetSiteInductionChecklist<TData = Awaited<ReturnType<typeof getSiteInductionChecklist>>, TError = Error>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteInductionChecklist>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSiteInductionChecklist>>,
          TError,
          Awaited<ReturnType<typeof getSiteInductionChecklist>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSiteInductionChecklist<TData = Awaited<ReturnType<typeof getSiteInductionChecklist>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteInductionChecklist>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSiteInductionChecklist>>,
          TError,
          Awaited<ReturnType<typeof getSiteInductionChecklist>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSiteInductionChecklist<TData = Awaited<ReturnType<typeof getSiteInductionChecklist>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteInductionChecklist>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get the active OH&S induction checklist for a site
 */

export function useGetSiteInductionChecklist<TData = Awaited<ReturnType<typeof getSiteInductionChecklist>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSiteInductionChecklist>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetSiteInductionChecklistQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPutSiteSafetyConfigUrl = (id: string,) => {




  return `/api/admin/sites/${id}/safety-config`
}

/**
 * @summary Configure site induction validity, arrival checks and safety guidance
 */
export const putSiteSafetyConfig = async (id: string,
    putSiteSafetyConfigBody: PutSiteSafetyConfig, options?: Parameters<typeof customFetch>[1]): Promise<SiteInductionChecklist> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SiteInductionChecklist>(getPutSiteSafetyConfigUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(putSiteSafetyConfigBody)
  }
);}





export const getPutSiteSafetyConfigMutationKey = () => ['putSiteSafetyConfig'] as const;

export const getPutSiteSafetyConfigMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putSiteSafetyConfig>>, TError,PutSiteSafetyConfigMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof putSiteSafetyConfig>>, TError,PutSiteSafetyConfigMutationVariables, TContext> => {

const mutationKey = getPutSiteSafetyConfigMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof putSiteSafetyConfig>>, PutSiteSafetyConfigMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  putSiteSafetyConfig(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PutSiteSafetyConfigMutationResult = NonNullable<Awaited<ReturnType<typeof putSiteSafetyConfig>>>
    export type PutSiteSafetyConfigMutationBody = PutSiteSafetyConfig
    export type PutSiteSafetyConfigMutationError = Error
    export type PutSiteSafetyConfigMutationVariables = {id: string;data: PutSiteSafetyConfig}

    /**
 * @summary Configure site induction validity, arrival checks and safety guidance
 */
export const usePutSiteSafetyConfig = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putSiteSafetyConfig>>, TError,PutSiteSafetyConfigMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof putSiteSafetyConfig>>,
        TError,
        PutSiteSafetyConfigMutationVariables,
        TContext
      > => {
      return useMutation(getPutSiteSafetyConfigMutationOptions(options), queryClient);
    }

export const getListMySiteInductionsUrl = (params?: ListMySiteInductionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/me/site-inductions?${stringifiedParams}` : `/api/me/site-inductions`
}

/**
 * @summary List the signed-in user's site induction status
 */
export const listMySiteInductions = async (params?: ListMySiteInductionsParams, options?: Parameters<typeof customFetch>[1]): Promise<UserSiteInductionListResponse> => {

  return customFetch<UserSiteInductionListResponse>(getListMySiteInductionsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMySiteInductionsQueryKey = (params?: ListMySiteInductionsParams,) => {
    return [
    `/api/me/site-inductions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMySiteInductionsQueryOptions = <TData = Awaited<ReturnType<typeof listMySiteInductions>>, TError = Error>(params?: ListMySiteInductionsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMySiteInductions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMySiteInductionsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMySiteInductions>>> = ({ signal }) => listMySiteInductions(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMySiteInductions>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMySiteInductionsQueryResult = NonNullable<Awaited<ReturnType<typeof listMySiteInductions>>>
export type ListMySiteInductionsQueryError = Error


export function useListMySiteInductions<TData = Awaited<ReturnType<typeof listMySiteInductions>>, TError = Error>(
 params: undefined |  ListMySiteInductionsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMySiteInductions>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMySiteInductions>>,
          TError,
          Awaited<ReturnType<typeof listMySiteInductions>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMySiteInductions<TData = Awaited<ReturnType<typeof listMySiteInductions>>, TError = Error>(
 params?: ListMySiteInductionsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMySiteInductions>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMySiteInductions>>,
          TError,
          Awaited<ReturnType<typeof listMySiteInductions>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMySiteInductions<TData = Awaited<ReturnType<typeof listMySiteInductions>>, TError = Error>(
 params?: ListMySiteInductionsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMySiteInductions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List the signed-in user's site induction status
 */

export function useListMySiteInductions<TData = Awaited<ReturnType<typeof listMySiteInductions>>, TError = Error>(
 params?: ListMySiteInductionsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMySiteInductions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMySiteInductionsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCompleteMySiteInductionUrl = () => {




  return `/api/me/site-inductions`
}

/**
 * Idempotent for the same user/site/version. Requires every checklist item id to be acknowledged. Fails when the submitted version is not the active checklist version.
 * @summary Complete (or re-ack) the active OH&S checklist for a site
 */
export const completeMySiteInduction = async (completeSiteInductionRequest: CompleteSiteInductionRequest, options?: Parameters<typeof customFetch>[1]): Promise<UserSiteInductionStatus> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<UserSiteInductionStatus>(getCompleteMySiteInductionUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(completeSiteInductionRequest)
  }
);}





export const getCompleteMySiteInductionMutationKey = () => ['completeMySiteInduction'] as const;

export const getCompleteMySiteInductionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof completeMySiteInduction>>, TError,CompleteMySiteInductionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof completeMySiteInduction>>, TError,CompleteMySiteInductionMutationVariables, TContext> => {

const mutationKey = getCompleteMySiteInductionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof completeMySiteInduction>>, CompleteMySiteInductionMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  completeMySiteInduction(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CompleteMySiteInductionMutationResult = NonNullable<Awaited<ReturnType<typeof completeMySiteInduction>>>
    export type CompleteMySiteInductionMutationBody = CompleteSiteInductionRequest
    export type CompleteMySiteInductionMutationError = Error
    export type CompleteMySiteInductionMutationVariables = {data: CompleteSiteInductionRequest}

    /**
 * @summary Complete (or re-ack) the active OH&S checklist for a site
 */
export const useCompleteMySiteInduction = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof completeMySiteInduction>>, TError,CompleteMySiteInductionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof completeMySiteInduction>>,
        TError,
        CompleteMySiteInductionMutationVariables,
        TContext
      > => {
      return useMutation(getCompleteMySiteInductionMutationOptions(options), queryClient);
    }

export const getListBuildingsUrl = (params?: ListBuildingsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/buildings?${stringifiedParams}` : `/api/buildings`
}

/**
 * @summary List buildings
 */
export const listBuildings = async (params?: ListBuildingsParams, options?: Parameters<typeof customFetch>[1]): Promise<BuildingListResponse> => {

  return customFetch<BuildingListResponse>(getListBuildingsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListBuildingsQueryKey = (params?: ListBuildingsParams,) => {
    return [
    `/api/buildings`, ...(params ? [params] : [])
    ] as const;
    }


export const getListBuildingsQueryOptions = <TData = Awaited<ReturnType<typeof listBuildings>>, TError = unknown>(params?: ListBuildingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBuildings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListBuildingsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listBuildings>>> = ({ signal }) => listBuildings(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listBuildings>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListBuildingsQueryResult = NonNullable<Awaited<ReturnType<typeof listBuildings>>>
export type ListBuildingsQueryError = unknown


export function useListBuildings<TData = Awaited<ReturnType<typeof listBuildings>>, TError = unknown>(
 params: undefined |  ListBuildingsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBuildings>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listBuildings>>,
          TError,
          Awaited<ReturnType<typeof listBuildings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListBuildings<TData = Awaited<ReturnType<typeof listBuildings>>, TError = unknown>(
 params?: ListBuildingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBuildings>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listBuildings>>,
          TError,
          Awaited<ReturnType<typeof listBuildings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListBuildings<TData = Awaited<ReturnType<typeof listBuildings>>, TError = unknown>(
 params?: ListBuildingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBuildings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List buildings
 */

export function useListBuildings<TData = Awaited<ReturnType<typeof listBuildings>>, TError = unknown>(
 params?: ListBuildingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBuildings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListBuildingsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateBuildingUrl = () => {




  return `/api/buildings`
}

/**
 * @summary Create building
 */
export const createBuilding = async (createBuildingBody: CreateBuilding, options?: Parameters<typeof customFetch>[1]): Promise<Building> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Building>(getCreateBuildingUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createBuildingBody)
  }
);}





export const getCreateBuildingMutationKey = () => ['createBuilding'] as const;

export const getCreateBuildingMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBuilding>>, TError,CreateBuildingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createBuilding>>, TError,CreateBuildingMutationVariables, TContext> => {

const mutationKey = getCreateBuildingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createBuilding>>, CreateBuildingMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createBuilding(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateBuildingMutationResult = NonNullable<Awaited<ReturnType<typeof createBuilding>>>
    export type CreateBuildingMutationBody = CreateBuilding
    export type CreateBuildingMutationError = unknown
    export type CreateBuildingMutationVariables = {data: CreateBuilding}

    /**
 * @summary Create building
 */
export const useCreateBuilding = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBuilding>>, TError,CreateBuildingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createBuilding>>,
        TError,
        CreateBuildingMutationVariables,
        TContext
      > => {
      return useMutation(getCreateBuildingMutationOptions(options), queryClient);
    }

export const getGetBuildingUrl = (id: string,) => {




  return `/api/buildings/${id}`
}

export const getBuilding = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Building> => {

  return customFetch<Building>(getGetBuildingUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetBuildingQueryKey = (id: string,) => {
    return [
    `/api/buildings/${id}`
    ] as const;
    }


export const getGetBuildingQueryOptions = <TData = Awaited<ReturnType<typeof getBuilding>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBuilding>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetBuildingQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getBuilding>>> = ({ signal }) => getBuilding(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getBuilding>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetBuildingQueryResult = NonNullable<Awaited<ReturnType<typeof getBuilding>>>
export type GetBuildingQueryError = unknown


export function useGetBuilding<TData = Awaited<ReturnType<typeof getBuilding>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBuilding>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBuilding>>,
          TError,
          Awaited<ReturnType<typeof getBuilding>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetBuilding<TData = Awaited<ReturnType<typeof getBuilding>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBuilding>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBuilding>>,
          TError,
          Awaited<ReturnType<typeof getBuilding>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetBuilding<TData = Awaited<ReturnType<typeof getBuilding>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBuilding>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetBuilding<TData = Awaited<ReturnType<typeof getBuilding>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBuilding>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetBuildingQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateBuildingUrl = (id: string,) => {




  return `/api/buildings/${id}`
}

export const updateBuilding = async (id: string,
    updateBuildingBody: UpdateBuilding, options?: Parameters<typeof customFetch>[1]): Promise<Building> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Building>(getUpdateBuildingUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateBuildingBody)
  }
);}





export const getUpdateBuildingMutationKey = () => ['updateBuilding'] as const;

export const getUpdateBuildingMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateBuilding>>, TError,UpdateBuildingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateBuilding>>, TError,UpdateBuildingMutationVariables, TContext> => {

const mutationKey = getUpdateBuildingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateBuilding>>, UpdateBuildingMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateBuilding(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateBuildingMutationResult = NonNullable<Awaited<ReturnType<typeof updateBuilding>>>
    export type UpdateBuildingMutationBody = UpdateBuilding
    export type UpdateBuildingMutationError = unknown
    export type UpdateBuildingMutationVariables = {id: string;data: UpdateBuilding}

    export const useUpdateBuilding = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateBuilding>>, TError,UpdateBuildingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateBuilding>>,
        TError,
        UpdateBuildingMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateBuildingMutationOptions(options), queryClient);
    }

export const getDeleteBuildingUrl = (id: string,) => {




  return `/api/buildings/${id}`
}

/**
 * Blocked while the building still has floors.
 * @summary Delete a building
 */
export const deleteBuilding = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteBuildingUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteBuildingMutationKey = () => ['deleteBuilding'] as const;

export const getDeleteBuildingMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteBuilding>>, TError,DeleteBuildingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteBuilding>>, TError,DeleteBuildingMutationVariables, TContext> => {

const mutationKey = getDeleteBuildingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteBuilding>>, DeleteBuildingMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteBuilding(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteBuildingMutationResult = NonNullable<Awaited<ReturnType<typeof deleteBuilding>>>

    export type DeleteBuildingMutationError = Error
    export type DeleteBuildingMutationVariables = {id: string}

    /**
 * @summary Delete a building
 */
export const useDeleteBuilding = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteBuilding>>, TError,DeleteBuildingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteBuilding>>,
        TError,
        DeleteBuildingMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteBuildingMutationOptions(options), queryClient);
    }

export const getListFloorsUrl = (params?: ListFloorsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/floors?${stringifiedParams}` : `/api/floors`
}

export const listFloors = async (params?: ListFloorsParams, options?: Parameters<typeof customFetch>[1]): Promise<FloorListResponse> => {

  return customFetch<FloorListResponse>(getListFloorsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListFloorsQueryKey = (params?: ListFloorsParams,) => {
    return [
    `/api/floors`, ...(params ? [params] : [])
    ] as const;
    }


export const getListFloorsQueryOptions = <TData = Awaited<ReturnType<typeof listFloors>>, TError = unknown>(params?: ListFloorsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listFloors>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListFloorsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listFloors>>> = ({ signal }) => listFloors(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listFloors>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListFloorsQueryResult = NonNullable<Awaited<ReturnType<typeof listFloors>>>
export type ListFloorsQueryError = unknown


export function useListFloors<TData = Awaited<ReturnType<typeof listFloors>>, TError = unknown>(
 params: undefined |  ListFloorsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listFloors>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFloors>>,
          TError,
          Awaited<ReturnType<typeof listFloors>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListFloors<TData = Awaited<ReturnType<typeof listFloors>>, TError = unknown>(
 params?: ListFloorsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listFloors>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFloors>>,
          TError,
          Awaited<ReturnType<typeof listFloors>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListFloors<TData = Awaited<ReturnType<typeof listFloors>>, TError = unknown>(
 params?: ListFloorsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listFloors>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListFloors<TData = Awaited<ReturnType<typeof listFloors>>, TError = unknown>(
 params?: ListFloorsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listFloors>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListFloorsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateFloorUrl = () => {




  return `/api/floors`
}

export const createFloor = async (createFloorBody: CreateFloor, options?: Parameters<typeof customFetch>[1]): Promise<Floor> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Floor>(getCreateFloorUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createFloorBody)
  }
);}





export const getCreateFloorMutationKey = () => ['createFloor'] as const;

export const getCreateFloorMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createFloor>>, TError,CreateFloorMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createFloor>>, TError,CreateFloorMutationVariables, TContext> => {

const mutationKey = getCreateFloorMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createFloor>>, CreateFloorMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createFloor(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateFloorMutationResult = NonNullable<Awaited<ReturnType<typeof createFloor>>>
    export type CreateFloorMutationBody = CreateFloor
    export type CreateFloorMutationError = unknown
    export type CreateFloorMutationVariables = {data: CreateFloor}

    export const useCreateFloor = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createFloor>>, TError,CreateFloorMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createFloor>>,
        TError,
        CreateFloorMutationVariables,
        TContext
      > => {
      return useMutation(getCreateFloorMutationOptions(options), queryClient);
    }

export const getGetFloorUrl = (id: string,) => {




  return `/api/floors/${id}`
}

export const getFloor = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Floor> => {

  return customFetch<Floor>(getGetFloorUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetFloorQueryKey = (id: string,) => {
    return [
    `/api/floors/${id}`
    ] as const;
    }


export const getGetFloorQueryOptions = <TData = Awaited<ReturnType<typeof getFloor>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloor>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetFloorQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getFloor>>> = ({ signal }) => getFloor(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getFloor>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetFloorQueryResult = NonNullable<Awaited<ReturnType<typeof getFloor>>>
export type GetFloorQueryError = unknown


export function useGetFloor<TData = Awaited<ReturnType<typeof getFloor>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloor>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFloor>>,
          TError,
          Awaited<ReturnType<typeof getFloor>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetFloor<TData = Awaited<ReturnType<typeof getFloor>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloor>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFloor>>,
          TError,
          Awaited<ReturnType<typeof getFloor>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetFloor<TData = Awaited<ReturnType<typeof getFloor>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloor>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetFloor<TData = Awaited<ReturnType<typeof getFloor>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloor>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetFloorQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateFloorUrl = (id: string,) => {




  return `/api/floors/${id}`
}

export const updateFloor = async (id: string,
    updateFloorBody: UpdateFloor, options?: Parameters<typeof customFetch>[1]): Promise<Floor> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Floor>(getUpdateFloorUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateFloorBody)
  }
);}





export const getUpdateFloorMutationKey = () => ['updateFloor'] as const;

export const getUpdateFloorMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateFloor>>, TError,UpdateFloorMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateFloor>>, TError,UpdateFloorMutationVariables, TContext> => {

const mutationKey = getUpdateFloorMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateFloor>>, UpdateFloorMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateFloor(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateFloorMutationResult = NonNullable<Awaited<ReturnType<typeof updateFloor>>>
    export type UpdateFloorMutationBody = UpdateFloor
    export type UpdateFloorMutationError = unknown
    export type UpdateFloorMutationVariables = {id: string;data: UpdateFloor}

    export const useUpdateFloor = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateFloor>>, TError,UpdateFloorMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateFloor>>,
        TError,
        UpdateFloorMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateFloorMutationOptions(options), queryClient);
    }

export const getDeleteFloorUrl = (id: string,) => {




  return `/api/floors/${id}`
}

/**
 * Blocked while the floor still has zones. Floor plans and geometry cascade.
 * @summary Delete a floor
 */
export const deleteFloor = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteFloorUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteFloorMutationKey = () => ['deleteFloor'] as const;

export const getDeleteFloorMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteFloor>>, TError,DeleteFloorMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteFloor>>, TError,DeleteFloorMutationVariables, TContext> => {

const mutationKey = getDeleteFloorMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteFloor>>, DeleteFloorMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteFloor(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteFloorMutationResult = NonNullable<Awaited<ReturnType<typeof deleteFloor>>>

    export type DeleteFloorMutationError = Error
    export type DeleteFloorMutationVariables = {id: string}

    /**
 * @summary Delete a floor
 */
export const useDeleteFloor = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteFloor>>, TError,DeleteFloorMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteFloor>>,
        TError,
        DeleteFloorMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteFloorMutationOptions(options), queryClient);
    }

export const getListZonesUrl = (params?: ListZonesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/zones?${stringifiedParams}` : `/api/zones`
}

export const listZones = async (params?: ListZonesParams, options?: Parameters<typeof customFetch>[1]): Promise<ZoneListResponse> => {

  return customFetch<ZoneListResponse>(getListZonesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListZonesQueryKey = (params?: ListZonesParams,) => {
    return [
    `/api/zones`, ...(params ? [params] : [])
    ] as const;
    }


export const getListZonesQueryOptions = <TData = Awaited<ReturnType<typeof listZones>>, TError = unknown>(params?: ListZonesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listZones>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListZonesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listZones>>> = ({ signal }) => listZones(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listZones>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListZonesQueryResult = NonNullable<Awaited<ReturnType<typeof listZones>>>
export type ListZonesQueryError = unknown


export function useListZones<TData = Awaited<ReturnType<typeof listZones>>, TError = unknown>(
 params: undefined |  ListZonesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listZones>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listZones>>,
          TError,
          Awaited<ReturnType<typeof listZones>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListZones<TData = Awaited<ReturnType<typeof listZones>>, TError = unknown>(
 params?: ListZonesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listZones>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listZones>>,
          TError,
          Awaited<ReturnType<typeof listZones>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListZones<TData = Awaited<ReturnType<typeof listZones>>, TError = unknown>(
 params?: ListZonesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listZones>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListZones<TData = Awaited<ReturnType<typeof listZones>>, TError = unknown>(
 params?: ListZonesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listZones>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListZonesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateZoneUrl = () => {




  return `/api/zones`
}

export const createZone = async (createZoneBody: CreateZone, options?: Parameters<typeof customFetch>[1]): Promise<Zone> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Zone>(getCreateZoneUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createZoneBody)
  }
);}





export const getCreateZoneMutationKey = () => ['createZone'] as const;

export const getCreateZoneMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createZone>>, TError,CreateZoneMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createZone>>, TError,CreateZoneMutationVariables, TContext> => {

const mutationKey = getCreateZoneMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createZone>>, CreateZoneMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createZone(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateZoneMutationResult = NonNullable<Awaited<ReturnType<typeof createZone>>>
    export type CreateZoneMutationBody = CreateZone
    export type CreateZoneMutationError = unknown
    export type CreateZoneMutationVariables = {data: CreateZone}

    export const useCreateZone = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createZone>>, TError,CreateZoneMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createZone>>,
        TError,
        CreateZoneMutationVariables,
        TContext
      > => {
      return useMutation(getCreateZoneMutationOptions(options), queryClient);
    }

export const getGetZoneUrl = (id: string,) => {




  return `/api/zones/${id}`
}

export const getZone = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Zone> => {

  return customFetch<Zone>(getGetZoneUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetZoneQueryKey = (id: string,) => {
    return [
    `/api/zones/${id}`
    ] as const;
    }


export const getGetZoneQueryOptions = <TData = Awaited<ReturnType<typeof getZone>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getZone>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetZoneQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getZone>>> = ({ signal }) => getZone(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getZone>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetZoneQueryResult = NonNullable<Awaited<ReturnType<typeof getZone>>>
export type GetZoneQueryError = unknown


export function useGetZone<TData = Awaited<ReturnType<typeof getZone>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getZone>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getZone>>,
          TError,
          Awaited<ReturnType<typeof getZone>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetZone<TData = Awaited<ReturnType<typeof getZone>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getZone>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getZone>>,
          TError,
          Awaited<ReturnType<typeof getZone>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetZone<TData = Awaited<ReturnType<typeof getZone>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getZone>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetZone<TData = Awaited<ReturnType<typeof getZone>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getZone>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetZoneQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateZoneUrl = (id: string,) => {




  return `/api/zones/${id}`
}

export const updateZone = async (id: string,
    updateZoneBody: UpdateZone, options?: Parameters<typeof customFetch>[1]): Promise<Zone> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Zone>(getUpdateZoneUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateZoneBody)
  }
);}





export const getUpdateZoneMutationKey = () => ['updateZone'] as const;

export const getUpdateZoneMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateZone>>, TError,UpdateZoneMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateZone>>, TError,UpdateZoneMutationVariables, TContext> => {

const mutationKey = getUpdateZoneMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateZone>>, UpdateZoneMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateZone(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateZoneMutationResult = NonNullable<Awaited<ReturnType<typeof updateZone>>>
    export type UpdateZoneMutationBody = UpdateZone
    export type UpdateZoneMutationError = unknown
    export type UpdateZoneMutationVariables = {id: string;data: UpdateZone}

    export const useUpdateZone = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateZone>>, TError,UpdateZoneMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateZone>>,
        TError,
        UpdateZoneMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateZoneMutationOptions(options), queryClient);
    }

export const getDeleteZoneUrl = (id: string,) => {




  return `/api/zones/${id}`
}

/**
 * Blocked while the zone still has spaces.
 * @summary Delete a zone
 */
export const deleteZone = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteZoneUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteZoneMutationKey = () => ['deleteZone'] as const;

export const getDeleteZoneMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteZone>>, TError,DeleteZoneMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteZone>>, TError,DeleteZoneMutationVariables, TContext> => {

const mutationKey = getDeleteZoneMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteZone>>, DeleteZoneMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteZone(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteZoneMutationResult = NonNullable<Awaited<ReturnType<typeof deleteZone>>>

    export type DeleteZoneMutationError = Error
    export type DeleteZoneMutationVariables = {id: string}

    /**
 * @summary Delete a zone
 */
export const useDeleteZone = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteZone>>, TError,DeleteZoneMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteZone>>,
        TError,
        DeleteZoneMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteZoneMutationOptions(options), queryClient);
    }

export const getListSpaceTypesUrl = () => {




  return `/api/space-types`
}

/**
 * @summary List space types
 */
export const listSpaceTypes = async ( options?: Parameters<typeof customFetch>[1]): Promise<SpaceTypeListResponse> => {

  return customFetch<SpaceTypeListResponse>(getListSpaceTypesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSpaceTypesQueryKey = () => {
    return [
    `/api/space-types`
    ] as const;
    }


export const getListSpaceTypesQueryOptions = <TData = Awaited<ReturnType<typeof listSpaceTypes>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSpaceTypes>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSpaceTypesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSpaceTypes>>> = ({ signal }) => listSpaceTypes({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSpaceTypes>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListSpaceTypesQueryResult = NonNullable<Awaited<ReturnType<typeof listSpaceTypes>>>
export type ListSpaceTypesQueryError = unknown


export function useListSpaceTypes<TData = Awaited<ReturnType<typeof listSpaceTypes>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSpaceTypes>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSpaceTypes>>,
          TError,
          Awaited<ReturnType<typeof listSpaceTypes>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListSpaceTypes<TData = Awaited<ReturnType<typeof listSpaceTypes>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSpaceTypes>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSpaceTypes>>,
          TError,
          Awaited<ReturnType<typeof listSpaceTypes>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListSpaceTypes<TData = Awaited<ReturnType<typeof listSpaceTypes>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSpaceTypes>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List space types
 */

export function useListSpaceTypes<TData = Awaited<ReturnType<typeof listSpaceTypes>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSpaceTypes>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListSpaceTypesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateSpaceTypeUrl = () => {




  return `/api/space-types`
}

/**
 * @summary Create a custom space type
 */
export const createSpaceType = async (createSpaceTypeBody: CreateSpaceType, options?: Parameters<typeof customFetch>[1]): Promise<SpaceType> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SpaceType>(getCreateSpaceTypeUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createSpaceTypeBody)
  }
);}





export const getCreateSpaceTypeMutationKey = () => ['createSpaceType'] as const;

export const getCreateSpaceTypeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSpaceType>>, TError,CreateSpaceTypeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createSpaceType>>, TError,CreateSpaceTypeMutationVariables, TContext> => {

const mutationKey = getCreateSpaceTypeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createSpaceType>>, CreateSpaceTypeMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createSpaceType(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateSpaceTypeMutationResult = NonNullable<Awaited<ReturnType<typeof createSpaceType>>>
    export type CreateSpaceTypeMutationBody = CreateSpaceType
    export type CreateSpaceTypeMutationError = unknown
    export type CreateSpaceTypeMutationVariables = {data: CreateSpaceType}

    /**
 * @summary Create a custom space type
 */
export const useCreateSpaceType = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSpaceType>>, TError,CreateSpaceTypeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createSpaceType>>,
        TError,
        CreateSpaceTypeMutationVariables,
        TContext
      > => {
      return useMutation(getCreateSpaceTypeMutationOptions(options), queryClient);
    }

export const getGetSpaceTypeUrl = (id: string,) => {




  return `/api/space-types/${id}`
}

export const getSpaceType = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<SpaceType> => {

  return customFetch<SpaceType>(getGetSpaceTypeUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSpaceTypeQueryKey = (id: string,) => {
    return [
    `/api/space-types/${id}`
    ] as const;
    }


export const getGetSpaceTypeQueryOptions = <TData = Awaited<ReturnType<typeof getSpaceType>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSpaceType>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSpaceTypeQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSpaceType>>> = ({ signal }) => getSpaceType(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSpaceType>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetSpaceTypeQueryResult = NonNullable<Awaited<ReturnType<typeof getSpaceType>>>
export type GetSpaceTypeQueryError = unknown


export function useGetSpaceType<TData = Awaited<ReturnType<typeof getSpaceType>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSpaceType>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSpaceType>>,
          TError,
          Awaited<ReturnType<typeof getSpaceType>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSpaceType<TData = Awaited<ReturnType<typeof getSpaceType>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSpaceType>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSpaceType>>,
          TError,
          Awaited<ReturnType<typeof getSpaceType>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSpaceType<TData = Awaited<ReturnType<typeof getSpaceType>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSpaceType>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetSpaceType<TData = Awaited<ReturnType<typeof getSpaceType>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSpaceType>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetSpaceTypeQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateSpaceTypeUrl = (id: string,) => {




  return `/api/space-types/${id}`
}

export const updateSpaceType = async (id: string,
    updateSpaceTypeBody: UpdateSpaceType, options?: Parameters<typeof customFetch>[1]): Promise<SpaceType> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SpaceType>(getUpdateSpaceTypeUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateSpaceTypeBody)
  }
);}





export const getUpdateSpaceTypeMutationKey = () => ['updateSpaceType'] as const;

export const getUpdateSpaceTypeMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSpaceType>>, TError,UpdateSpaceTypeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateSpaceType>>, TError,UpdateSpaceTypeMutationVariables, TContext> => {

const mutationKey = getUpdateSpaceTypeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateSpaceType>>, UpdateSpaceTypeMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateSpaceType(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateSpaceTypeMutationResult = NonNullable<Awaited<ReturnType<typeof updateSpaceType>>>
    export type UpdateSpaceTypeMutationBody = UpdateSpaceType
    export type UpdateSpaceTypeMutationError = unknown
    export type UpdateSpaceTypeMutationVariables = {id: string;data: UpdateSpaceType}

    export const useUpdateSpaceType = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSpaceType>>, TError,UpdateSpaceTypeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateSpaceType>>,
        TError,
        UpdateSpaceTypeMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateSpaceTypeMutationOptions(options), queryClient);
    }

export const getDeleteSpaceTypeUrl = (id: string,) => {




  return `/api/space-types/${id}`
}

/**
 * Built-in types are protected; types used by spaces, policies or visitor records cannot be deleted.
 * @summary Delete a space type
 */
export const deleteSpaceType = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteSpaceTypeUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteSpaceTypeMutationKey = () => ['deleteSpaceType'] as const;

export const getDeleteSpaceTypeMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteSpaceType>>, TError,DeleteSpaceTypeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteSpaceType>>, TError,DeleteSpaceTypeMutationVariables, TContext> => {

const mutationKey = getDeleteSpaceTypeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteSpaceType>>, DeleteSpaceTypeMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteSpaceType(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteSpaceTypeMutationResult = NonNullable<Awaited<ReturnType<typeof deleteSpaceType>>>

    export type DeleteSpaceTypeMutationError = Error
    export type DeleteSpaceTypeMutationVariables = {id: string}

    /**
 * @summary Delete a space type
 */
export const useDeleteSpaceType = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteSpaceType>>, TError,DeleteSpaceTypeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteSpaceType>>,
        TError,
        DeleteSpaceTypeMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteSpaceTypeMutationOptions(options), queryClient);
    }

export const getListAmenitiesUrl = () => {




  return `/api/amenities`
}

export const listAmenities = async ( options?: Parameters<typeof customFetch>[1]): Promise<AmenityListResponse> => {

  return customFetch<AmenityListResponse>(getListAmenitiesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAmenitiesQueryKey = () => {
    return [
    `/api/amenities`
    ] as const;
    }


export const getListAmenitiesQueryOptions = <TData = Awaited<ReturnType<typeof listAmenities>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAmenities>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAmenitiesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAmenities>>> = ({ signal }) => listAmenities({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAmenities>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListAmenitiesQueryResult = NonNullable<Awaited<ReturnType<typeof listAmenities>>>
export type ListAmenitiesQueryError = unknown


export function useListAmenities<TData = Awaited<ReturnType<typeof listAmenities>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAmenities>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAmenities>>,
          TError,
          Awaited<ReturnType<typeof listAmenities>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListAmenities<TData = Awaited<ReturnType<typeof listAmenities>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAmenities>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAmenities>>,
          TError,
          Awaited<ReturnType<typeof listAmenities>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListAmenities<TData = Awaited<ReturnType<typeof listAmenities>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAmenities>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListAmenities<TData = Awaited<ReturnType<typeof listAmenities>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAmenities>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListAmenitiesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateAmenityUrl = () => {




  return `/api/amenities`
}

export const createAmenity = async (createAmenityBody: CreateAmenity, options?: Parameters<typeof customFetch>[1]): Promise<Amenity> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Amenity>(getCreateAmenityUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createAmenityBody)
  }
);}





export const getCreateAmenityMutationKey = () => ['createAmenity'] as const;

export const getCreateAmenityMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAmenity>>, TError,CreateAmenityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createAmenity>>, TError,CreateAmenityMutationVariables, TContext> => {

const mutationKey = getCreateAmenityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createAmenity>>, CreateAmenityMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createAmenity(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateAmenityMutationResult = NonNullable<Awaited<ReturnType<typeof createAmenity>>>
    export type CreateAmenityMutationBody = CreateAmenity
    export type CreateAmenityMutationError = unknown
    export type CreateAmenityMutationVariables = {data: CreateAmenity}

    export const useCreateAmenity = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createAmenity>>, TError,CreateAmenityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createAmenity>>,
        TError,
        CreateAmenityMutationVariables,
        TContext
      > => {
      return useMutation(getCreateAmenityMutationOptions(options), queryClient);
    }

export const getGetAmenityUrl = (id: string,) => {




  return `/api/amenities/${id}`
}

export const getAmenity = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Amenity> => {

  return customFetch<Amenity>(getGetAmenityUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetAmenityQueryKey = (id: string,) => {
    return [
    `/api/amenities/${id}`
    ] as const;
    }


export const getGetAmenityQueryOptions = <TData = Awaited<ReturnType<typeof getAmenity>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAmenity>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAmenityQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getAmenity>>> = ({ signal }) => getAmenity(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getAmenity>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetAmenityQueryResult = NonNullable<Awaited<ReturnType<typeof getAmenity>>>
export type GetAmenityQueryError = unknown


export function useGetAmenity<TData = Awaited<ReturnType<typeof getAmenity>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAmenity>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAmenity>>,
          TError,
          Awaited<ReturnType<typeof getAmenity>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAmenity<TData = Awaited<ReturnType<typeof getAmenity>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAmenity>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAmenity>>,
          TError,
          Awaited<ReturnType<typeof getAmenity>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAmenity<TData = Awaited<ReturnType<typeof getAmenity>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAmenity>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetAmenity<TData = Awaited<ReturnType<typeof getAmenity>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getAmenity>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetAmenityQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateAmenityUrl = (id: string,) => {




  return `/api/amenities/${id}`
}

export const updateAmenity = async (id: string,
    updateAmenityBody: UpdateAmenity, options?: Parameters<typeof customFetch>[1]): Promise<Amenity> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Amenity>(getUpdateAmenityUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateAmenityBody)
  }
);}





export const getUpdateAmenityMutationKey = () => ['updateAmenity'] as const;

export const getUpdateAmenityMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAmenity>>, TError,UpdateAmenityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateAmenity>>, TError,UpdateAmenityMutationVariables, TContext> => {

const mutationKey = getUpdateAmenityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateAmenity>>, UpdateAmenityMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateAmenity(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateAmenityMutationResult = NonNullable<Awaited<ReturnType<typeof updateAmenity>>>
    export type UpdateAmenityMutationBody = UpdateAmenity
    export type UpdateAmenityMutationError = unknown
    export type UpdateAmenityMutationVariables = {id: string;data: UpdateAmenity}

    export const useUpdateAmenity = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateAmenity>>, TError,UpdateAmenityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateAmenity>>,
        TError,
        UpdateAmenityMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateAmenityMutationOptions(options), queryClient);
    }

export const getDeleteAmenityUrl = (id: string,) => {




  return `/api/amenities/${id}`
}

/**
 * Deleting an amenity also removes it from every space that uses it.
 * @summary Delete an amenity
 */
export const deleteAmenity = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteAmenityUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteAmenityMutationKey = () => ['deleteAmenity'] as const;

export const getDeleteAmenityMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAmenity>>, TError,DeleteAmenityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteAmenity>>, TError,DeleteAmenityMutationVariables, TContext> => {

const mutationKey = getDeleteAmenityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteAmenity>>, DeleteAmenityMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteAmenity(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteAmenityMutationResult = NonNullable<Awaited<ReturnType<typeof deleteAmenity>>>

    export type DeleteAmenityMutationError = Error
    export type DeleteAmenityMutationVariables = {id: string}

    /**
 * @summary Delete an amenity
 */
export const useDeleteAmenity = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteAmenity>>, TError,DeleteAmenityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteAmenity>>,
        TError,
        DeleteAmenityMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteAmenityMutationOptions(options), queryClient);
    }

export const getListSpaceLayoutsUrl = (id: string,
    params?: ListSpaceLayoutsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/spaces/${id}/layouts?${stringifiedParams}` : `/api/spaces/${id}/layouts`
}

export const listSpaceLayouts = async (id: string,
    params?: ListSpaceLayoutsParams, options?: Parameters<typeof customFetch>[1]): Promise<SpaceLayoutListResponse> => {

  return customFetch<SpaceLayoutListResponse>(getListSpaceLayoutsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSpaceLayoutsQueryKey = (id: string,
    params?: ListSpaceLayoutsParams,) => {
    return [
    `/api/spaces/${id}/layouts`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSpaceLayoutsQueryOptions = <TData = Awaited<ReturnType<typeof listSpaceLayouts>>, TError = unknown>(id: string,
    params?: ListSpaceLayoutsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSpaceLayouts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSpaceLayoutsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSpaceLayouts>>> = ({ signal }) => listSpaceLayouts(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSpaceLayouts>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListSpaceLayoutsQueryResult = NonNullable<Awaited<ReturnType<typeof listSpaceLayouts>>>
export type ListSpaceLayoutsQueryError = unknown


export function useListSpaceLayouts<TData = Awaited<ReturnType<typeof listSpaceLayouts>>, TError = unknown>(
 id: string,
    params: undefined |  ListSpaceLayoutsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSpaceLayouts>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSpaceLayouts>>,
          TError,
          Awaited<ReturnType<typeof listSpaceLayouts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListSpaceLayouts<TData = Awaited<ReturnType<typeof listSpaceLayouts>>, TError = unknown>(
 id: string,
    params?: ListSpaceLayoutsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSpaceLayouts>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSpaceLayouts>>,
          TError,
          Awaited<ReturnType<typeof listSpaceLayouts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListSpaceLayouts<TData = Awaited<ReturnType<typeof listSpaceLayouts>>, TError = unknown>(
 id: string,
    params?: ListSpaceLayoutsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSpaceLayouts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListSpaceLayouts<TData = Awaited<ReturnType<typeof listSpaceLayouts>>, TError = unknown>(
 id: string,
    params?: ListSpaceLayoutsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSpaceLayouts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListSpaceLayoutsQueryOptions(id,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateSpaceLayoutUrl = (id: string,) => {




  return `/api/spaces/${id}/layouts`
}

export const createSpaceLayout = async (id: string,
    createSpaceLayoutBody: CreateSpaceLayout, options?: Parameters<typeof customFetch>[1]): Promise<SpaceLayout> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SpaceLayout>(getCreateSpaceLayoutUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createSpaceLayoutBody)
  }
);}





export const getCreateSpaceLayoutMutationKey = () => ['createSpaceLayout'] as const;

export const getCreateSpaceLayoutMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSpaceLayout>>, TError,CreateSpaceLayoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createSpaceLayout>>, TError,CreateSpaceLayoutMutationVariables, TContext> => {

const mutationKey = getCreateSpaceLayoutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createSpaceLayout>>, CreateSpaceLayoutMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  createSpaceLayout(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateSpaceLayoutMutationResult = NonNullable<Awaited<ReturnType<typeof createSpaceLayout>>>
    export type CreateSpaceLayoutMutationBody = CreateSpaceLayout
    export type CreateSpaceLayoutMutationError = unknown
    export type CreateSpaceLayoutMutationVariables = {id: string;data: CreateSpaceLayout}

    export const useCreateSpaceLayout = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSpaceLayout>>, TError,CreateSpaceLayoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createSpaceLayout>>,
        TError,
        CreateSpaceLayoutMutationVariables,
        TContext
      > => {
      return useMutation(getCreateSpaceLayoutMutationOptions(options), queryClient);
    }

export const getUpdateSpaceLayoutUrl = (id: string,
    layoutId: string,) => {




  return `/api/spaces/${id}/layouts/${layoutId}`
}

export const updateSpaceLayout = async (id: string,
    layoutId: string,
    updateSpaceLayoutBody: UpdateSpaceLayout, options?: Parameters<typeof customFetch>[1]): Promise<SpaceLayout> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SpaceLayout>(getUpdateSpaceLayoutUrl(id,layoutId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateSpaceLayoutBody)
  }
);}





export const getUpdateSpaceLayoutMutationKey = () => ['updateSpaceLayout'] as const;

export const getUpdateSpaceLayoutMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSpaceLayout>>, TError,UpdateSpaceLayoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateSpaceLayout>>, TError,UpdateSpaceLayoutMutationVariables, TContext> => {

const mutationKey = getUpdateSpaceLayoutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateSpaceLayout>>, UpdateSpaceLayoutMutationVariables> = (props) => {
          const {id,layoutId,data} = props ?? {};

          return  updateSpaceLayout(id,layoutId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateSpaceLayoutMutationResult = NonNullable<Awaited<ReturnType<typeof updateSpaceLayout>>>
    export type UpdateSpaceLayoutMutationBody = UpdateSpaceLayout
    export type UpdateSpaceLayoutMutationError = unknown
    export type UpdateSpaceLayoutMutationVariables = {id: string;layoutId: string;data: UpdateSpaceLayout}

    export const useUpdateSpaceLayout = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSpaceLayout>>, TError,UpdateSpaceLayoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateSpaceLayout>>,
        TError,
        UpdateSpaceLayoutMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateSpaceLayoutMutationOptions(options), queryClient);
    }

export const getListSpacesUrl = (params?: ListSpacesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/spaces?${stringifiedParams}` : `/api/spaces`
}

/**
 * @summary List spaces (retired hidden by default)
 */
export const listSpaces = async (params?: ListSpacesParams, options?: Parameters<typeof customFetch>[1]): Promise<SpaceListResponse> => {

  return customFetch<SpaceListResponse>(getListSpacesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListSpacesQueryKey = (params?: ListSpacesParams,) => {
    return [
    `/api/spaces`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSpacesQueryOptions = <TData = Awaited<ReturnType<typeof listSpaces>>, TError = unknown>(params?: ListSpacesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSpaces>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSpacesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSpaces>>> = ({ signal }) => listSpaces(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSpaces>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListSpacesQueryResult = NonNullable<Awaited<ReturnType<typeof listSpaces>>>
export type ListSpacesQueryError = unknown


export function useListSpaces<TData = Awaited<ReturnType<typeof listSpaces>>, TError = unknown>(
 params: undefined |  ListSpacesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSpaces>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSpaces>>,
          TError,
          Awaited<ReturnType<typeof listSpaces>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListSpaces<TData = Awaited<ReturnType<typeof listSpaces>>, TError = unknown>(
 params?: ListSpacesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSpaces>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSpaces>>,
          TError,
          Awaited<ReturnType<typeof listSpaces>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListSpaces<TData = Awaited<ReturnType<typeof listSpaces>>, TError = unknown>(
 params?: ListSpacesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSpaces>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List spaces (retired hidden by default)
 */

export function useListSpaces<TData = Awaited<ReturnType<typeof listSpaces>>, TError = unknown>(
 params?: ListSpacesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSpaces>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListSpacesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateSpaceUrl = () => {




  return `/api/spaces`
}

/**
 * @summary Create space
 */
export const createSpace = async (createSpaceBody: CreateSpace, options?: Parameters<typeof customFetch>[1]): Promise<Space> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Space>(getCreateSpaceUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createSpaceBody)
  }
);}





export const getCreateSpaceMutationKey = () => ['createSpace'] as const;

export const getCreateSpaceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSpace>>, TError,CreateSpaceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createSpace>>, TError,CreateSpaceMutationVariables, TContext> => {

const mutationKey = getCreateSpaceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createSpace>>, CreateSpaceMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createSpace(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateSpaceMutationResult = NonNullable<Awaited<ReturnType<typeof createSpace>>>
    export type CreateSpaceMutationBody = CreateSpace
    export type CreateSpaceMutationError = unknown
    export type CreateSpaceMutationVariables = {data: CreateSpace}

    /**
 * @summary Create space
 */
export const useCreateSpace = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSpace>>, TError,CreateSpaceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createSpace>>,
        TError,
        CreateSpaceMutationVariables,
        TContext
      > => {
      return useMutation(getCreateSpaceMutationOptions(options), queryClient);
    }

export const getGetSpaceUrl = (id: string,) => {




  return `/api/spaces/${id}`
}

export const getSpace = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Space> => {

  return customFetch<Space>(getGetSpaceUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSpaceQueryKey = (id: string,) => {
    return [
    `/api/spaces/${id}`
    ] as const;
    }


export const getGetSpaceQueryOptions = <TData = Awaited<ReturnType<typeof getSpace>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSpace>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSpaceQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSpace>>> = ({ signal }) => getSpace(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSpace>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetSpaceQueryResult = NonNullable<Awaited<ReturnType<typeof getSpace>>>
export type GetSpaceQueryError = unknown


export function useGetSpace<TData = Awaited<ReturnType<typeof getSpace>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSpace>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSpace>>,
          TError,
          Awaited<ReturnType<typeof getSpace>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSpace<TData = Awaited<ReturnType<typeof getSpace>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSpace>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSpace>>,
          TError,
          Awaited<ReturnType<typeof getSpace>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSpace<TData = Awaited<ReturnType<typeof getSpace>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSpace>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetSpace<TData = Awaited<ReturnType<typeof getSpace>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSpace>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetSpaceQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateSpaceUrl = (id: string,) => {




  return `/api/spaces/${id}`
}

export const updateSpace = async (id: string,
    updateSpaceBody: UpdateSpace, options?: Parameters<typeof customFetch>[1]): Promise<Space> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Space>(getUpdateSpaceUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateSpaceBody)
  }
);}





export const getUpdateSpaceMutationKey = () => ['updateSpace'] as const;

export const getUpdateSpaceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSpace>>, TError,UpdateSpaceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateSpace>>, TError,UpdateSpaceMutationVariables, TContext> => {

const mutationKey = getUpdateSpaceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateSpace>>, UpdateSpaceMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateSpace(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateSpaceMutationResult = NonNullable<Awaited<ReturnType<typeof updateSpace>>>
    export type UpdateSpaceMutationBody = UpdateSpace
    export type UpdateSpaceMutationError = unknown
    export type UpdateSpaceMutationVariables = {id: string;data: UpdateSpace}

    export const useUpdateSpace = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSpace>>, TError,UpdateSpaceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateSpace>>,
        TError,
        UpdateSpaceMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateSpaceMutationOptions(options), queryClient);
    }

export const getDeleteSpaceUrl = (id: string,) => {




  return `/api/spaces/${id}`
}

/**
 * Blocked when the space has booking history; retire it instead.
 * @summary Delete a space
 */
export const deleteSpace = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteSpaceUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteSpaceMutationKey = () => ['deleteSpace'] as const;

export const getDeleteSpaceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteSpace>>, TError,DeleteSpaceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteSpace>>, TError,DeleteSpaceMutationVariables, TContext> => {

const mutationKey = getDeleteSpaceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteSpace>>, DeleteSpaceMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteSpace(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteSpaceMutationResult = NonNullable<Awaited<ReturnType<typeof deleteSpace>>>

    export type DeleteSpaceMutationError = Error
    export type DeleteSpaceMutationVariables = {id: string}

    /**
 * @summary Delete a space
 */
export const useDeleteSpace = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteSpace>>, TError,DeleteSpaceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteSpace>>,
        TError,
        DeleteSpaceMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteSpaceMutationOptions(options), queryClient);
    }

export const getRetireSpaceUrl = (id: string,) => {




  return `/api/spaces/${id}/retire`
}

/**
 * @summary Soft-retire a space
 */
export const retireSpace = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Space> => {

  return customFetch<Space>(getRetireSpaceUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRetireSpaceMutationKey = () => ['retireSpace'] as const;

export const getRetireSpaceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retireSpace>>, TError,RetireSpaceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof retireSpace>>, TError,RetireSpaceMutationVariables, TContext> => {

const mutationKey = getRetireSpaceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof retireSpace>>, RetireSpaceMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  retireSpace(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RetireSpaceMutationResult = NonNullable<Awaited<ReturnType<typeof retireSpace>>>

    export type RetireSpaceMutationError = unknown
    export type RetireSpaceMutationVariables = {id: string}

    /**
 * @summary Soft-retire a space
 */
export const useRetireSpace = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof retireSpace>>, TError,RetireSpaceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof retireSpace>>,
        TError,
        RetireSpaceMutationVariables,
        TContext
      > => {
      return useMutation(getRetireSpaceMutationOptions(options), queryClient);
    }

export const getGetSpaceEffectivePolicyUrl = (id: string,) => {




  return `/api/spaces/${id}/effective-policy`
}

/**
 * @summary Resolve effective booking policy for a space
 */
export const getSpaceEffectivePolicy = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<EffectivePolicy> => {

  return customFetch<EffectivePolicy>(getGetSpaceEffectivePolicyUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetSpaceEffectivePolicyQueryKey = (id: string,) => {
    return [
    `/api/spaces/${id}/effective-policy`
    ] as const;
    }


export const getGetSpaceEffectivePolicyQueryOptions = <TData = Awaited<ReturnType<typeof getSpaceEffectivePolicy>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSpaceEffectivePolicy>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSpaceEffectivePolicyQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSpaceEffectivePolicy>>> = ({ signal }) => getSpaceEffectivePolicy(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSpaceEffectivePolicy>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetSpaceEffectivePolicyQueryResult = NonNullable<Awaited<ReturnType<typeof getSpaceEffectivePolicy>>>
export type GetSpaceEffectivePolicyQueryError = unknown


export function useGetSpaceEffectivePolicy<TData = Awaited<ReturnType<typeof getSpaceEffectivePolicy>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSpaceEffectivePolicy>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSpaceEffectivePolicy>>,
          TError,
          Awaited<ReturnType<typeof getSpaceEffectivePolicy>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSpaceEffectivePolicy<TData = Awaited<ReturnType<typeof getSpaceEffectivePolicy>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSpaceEffectivePolicy>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSpaceEffectivePolicy>>,
          TError,
          Awaited<ReturnType<typeof getSpaceEffectivePolicy>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSpaceEffectivePolicy<TData = Awaited<ReturnType<typeof getSpaceEffectivePolicy>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSpaceEffectivePolicy>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Resolve effective booking policy for a space
 */

export function useGetSpaceEffectivePolicy<TData = Awaited<ReturnType<typeof getSpaceEffectivePolicy>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSpaceEffectivePolicy>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetSpaceEffectivePolicyQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListBookingPoliciesUrl = (params?: ListBookingPoliciesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/booking-policies?${stringifiedParams}` : `/api/booking-policies`
}

export const listBookingPolicies = async (params?: ListBookingPoliciesParams, options?: Parameters<typeof customFetch>[1]): Promise<BookingPolicyListResponse> => {

  return customFetch<BookingPolicyListResponse>(getListBookingPoliciesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListBookingPoliciesQueryKey = (params?: ListBookingPoliciesParams,) => {
    return [
    `/api/booking-policies`, ...(params ? [params] : [])
    ] as const;
    }


export const getListBookingPoliciesQueryOptions = <TData = Awaited<ReturnType<typeof listBookingPolicies>>, TError = unknown>(params?: ListBookingPoliciesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBookingPolicies>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListBookingPoliciesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listBookingPolicies>>> = ({ signal }) => listBookingPolicies(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listBookingPolicies>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListBookingPoliciesQueryResult = NonNullable<Awaited<ReturnType<typeof listBookingPolicies>>>
export type ListBookingPoliciesQueryError = unknown


export function useListBookingPolicies<TData = Awaited<ReturnType<typeof listBookingPolicies>>, TError = unknown>(
 params: undefined |  ListBookingPoliciesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBookingPolicies>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listBookingPolicies>>,
          TError,
          Awaited<ReturnType<typeof listBookingPolicies>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListBookingPolicies<TData = Awaited<ReturnType<typeof listBookingPolicies>>, TError = unknown>(
 params?: ListBookingPoliciesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBookingPolicies>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listBookingPolicies>>,
          TError,
          Awaited<ReturnType<typeof listBookingPolicies>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListBookingPolicies<TData = Awaited<ReturnType<typeof listBookingPolicies>>, TError = unknown>(
 params?: ListBookingPoliciesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBookingPolicies>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListBookingPolicies<TData = Awaited<ReturnType<typeof listBookingPolicies>>, TError = unknown>(
 params?: ListBookingPoliciesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBookingPolicies>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListBookingPoliciesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateBookingPolicyUrl = () => {




  return `/api/booking-policies`
}

export const createBookingPolicy = async (createBookingPolicyBody: CreateBookingPolicy, options?: Parameters<typeof customFetch>[1]): Promise<BookingPolicy> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<BookingPolicy>(getCreateBookingPolicyUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createBookingPolicyBody)
  }
);}





export const getCreateBookingPolicyMutationKey = () => ['createBookingPolicy'] as const;

export const getCreateBookingPolicyMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBookingPolicy>>, TError,CreateBookingPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createBookingPolicy>>, TError,CreateBookingPolicyMutationVariables, TContext> => {

const mutationKey = getCreateBookingPolicyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createBookingPolicy>>, CreateBookingPolicyMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createBookingPolicy(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateBookingPolicyMutationResult = NonNullable<Awaited<ReturnType<typeof createBookingPolicy>>>
    export type CreateBookingPolicyMutationBody = CreateBookingPolicy
    export type CreateBookingPolicyMutationError = unknown
    export type CreateBookingPolicyMutationVariables = {data: CreateBookingPolicy}

    export const useCreateBookingPolicy = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBookingPolicy>>, TError,CreateBookingPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createBookingPolicy>>,
        TError,
        CreateBookingPolicyMutationVariables,
        TContext
      > => {
      return useMutation(getCreateBookingPolicyMutationOptions(options), queryClient);
    }

export const getGetBookingPolicyUrl = (id: string,) => {




  return `/api/booking-policies/${id}`
}

export const getBookingPolicy = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<BookingPolicy> => {

  return customFetch<BookingPolicy>(getGetBookingPolicyUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetBookingPolicyQueryKey = (id: string,) => {
    return [
    `/api/booking-policies/${id}`
    ] as const;
    }


export const getGetBookingPolicyQueryOptions = <TData = Awaited<ReturnType<typeof getBookingPolicy>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBookingPolicy>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetBookingPolicyQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getBookingPolicy>>> = ({ signal }) => getBookingPolicy(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getBookingPolicy>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetBookingPolicyQueryResult = NonNullable<Awaited<ReturnType<typeof getBookingPolicy>>>
export type GetBookingPolicyQueryError = unknown


export function useGetBookingPolicy<TData = Awaited<ReturnType<typeof getBookingPolicy>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBookingPolicy>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBookingPolicy>>,
          TError,
          Awaited<ReturnType<typeof getBookingPolicy>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetBookingPolicy<TData = Awaited<ReturnType<typeof getBookingPolicy>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBookingPolicy>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBookingPolicy>>,
          TError,
          Awaited<ReturnType<typeof getBookingPolicy>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetBookingPolicy<TData = Awaited<ReturnType<typeof getBookingPolicy>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBookingPolicy>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetBookingPolicy<TData = Awaited<ReturnType<typeof getBookingPolicy>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBookingPolicy>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetBookingPolicyQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateBookingPolicyUrl = (id: string,) => {




  return `/api/booking-policies/${id}`
}

export const updateBookingPolicy = async (id: string,
    updateBookingPolicyBody: UpdateBookingPolicy, options?: Parameters<typeof customFetch>[1]): Promise<BookingPolicy> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<BookingPolicy>(getUpdateBookingPolicyUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateBookingPolicyBody)
  }
);}





export const getUpdateBookingPolicyMutationKey = () => ['updateBookingPolicy'] as const;

export const getUpdateBookingPolicyMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateBookingPolicy>>, TError,UpdateBookingPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateBookingPolicy>>, TError,UpdateBookingPolicyMutationVariables, TContext> => {

const mutationKey = getUpdateBookingPolicyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateBookingPolicy>>, UpdateBookingPolicyMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateBookingPolicy(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateBookingPolicyMutationResult = NonNullable<Awaited<ReturnType<typeof updateBookingPolicy>>>
    export type UpdateBookingPolicyMutationBody = UpdateBookingPolicy
    export type UpdateBookingPolicyMutationError = unknown
    export type UpdateBookingPolicyMutationVariables = {id: string;data: UpdateBookingPolicy}

    export const useUpdateBookingPolicy = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateBookingPolicy>>, TError,UpdateBookingPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateBookingPolicy>>,
        TError,
        UpdateBookingPolicyMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateBookingPolicyMutationOptions(options), queryClient);
    }

export const getDeleteBookingPolicyUrl = (id: string,) => {




  return `/api/booking-policies/${id}`
}

export const deleteBookingPolicy = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteBookingPolicyUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteBookingPolicyMutationKey = () => ['deleteBookingPolicy'] as const;

export const getDeleteBookingPolicyMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteBookingPolicy>>, TError,DeleteBookingPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteBookingPolicy>>, TError,DeleteBookingPolicyMutationVariables, TContext> => {

const mutationKey = getDeleteBookingPolicyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteBookingPolicy>>, DeleteBookingPolicyMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteBookingPolicy(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteBookingPolicyMutationResult = NonNullable<Awaited<ReturnType<typeof deleteBookingPolicy>>>

    export type DeleteBookingPolicyMutationError = unknown
    export type DeleteBookingPolicyMutationVariables = {id: string}

    export const useDeleteBookingPolicy = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteBookingPolicy>>, TError,DeleteBookingPolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteBookingPolicy>>,
        TError,
        DeleteBookingPolicyMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteBookingPolicyMutationOptions(options), queryClient);
    }

export const getListBlackoutsUrl = (params?: ListBlackoutsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/blackouts?${stringifiedParams}` : `/api/blackouts`
}

export const listBlackouts = async (params?: ListBlackoutsParams, options?: Parameters<typeof customFetch>[1]): Promise<BlackoutListResponse> => {

  return customFetch<BlackoutListResponse>(getListBlackoutsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListBlackoutsQueryKey = (params?: ListBlackoutsParams,) => {
    return [
    `/api/blackouts`, ...(params ? [params] : [])
    ] as const;
    }


export const getListBlackoutsQueryOptions = <TData = Awaited<ReturnType<typeof listBlackouts>>, TError = unknown>(params?: ListBlackoutsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBlackouts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListBlackoutsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listBlackouts>>> = ({ signal }) => listBlackouts(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listBlackouts>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListBlackoutsQueryResult = NonNullable<Awaited<ReturnType<typeof listBlackouts>>>
export type ListBlackoutsQueryError = unknown


export function useListBlackouts<TData = Awaited<ReturnType<typeof listBlackouts>>, TError = unknown>(
 params: undefined |  ListBlackoutsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBlackouts>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listBlackouts>>,
          TError,
          Awaited<ReturnType<typeof listBlackouts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListBlackouts<TData = Awaited<ReturnType<typeof listBlackouts>>, TError = unknown>(
 params?: ListBlackoutsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBlackouts>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listBlackouts>>,
          TError,
          Awaited<ReturnType<typeof listBlackouts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListBlackouts<TData = Awaited<ReturnType<typeof listBlackouts>>, TError = unknown>(
 params?: ListBlackoutsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBlackouts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useListBlackouts<TData = Awaited<ReturnType<typeof listBlackouts>>, TError = unknown>(
 params?: ListBlackoutsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBlackouts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListBlackoutsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateBlackoutUrl = () => {




  return `/api/blackouts`
}

export const createBlackout = async (createBlackoutBody: CreateBlackout, options?: Parameters<typeof customFetch>[1]): Promise<Blackout> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Blackout>(getCreateBlackoutUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createBlackoutBody)
  }
);}





export const getCreateBlackoutMutationKey = () => ['createBlackout'] as const;

export const getCreateBlackoutMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBlackout>>, TError,CreateBlackoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createBlackout>>, TError,CreateBlackoutMutationVariables, TContext> => {

const mutationKey = getCreateBlackoutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createBlackout>>, CreateBlackoutMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createBlackout(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateBlackoutMutationResult = NonNullable<Awaited<ReturnType<typeof createBlackout>>>
    export type CreateBlackoutMutationBody = CreateBlackout
    export type CreateBlackoutMutationError = unknown
    export type CreateBlackoutMutationVariables = {data: CreateBlackout}

    export const useCreateBlackout = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBlackout>>, TError,CreateBlackoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createBlackout>>,
        TError,
        CreateBlackoutMutationVariables,
        TContext
      > => {
      return useMutation(getCreateBlackoutMutationOptions(options), queryClient);
    }

export const getGetBlackoutUrl = (id: string,) => {




  return `/api/blackouts/${id}`
}

export const getBlackout = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Blackout> => {

  return customFetch<Blackout>(getGetBlackoutUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetBlackoutQueryKey = (id: string,) => {
    return [
    `/api/blackouts/${id}`
    ] as const;
    }


export const getGetBlackoutQueryOptions = <TData = Awaited<ReturnType<typeof getBlackout>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBlackout>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetBlackoutQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getBlackout>>> = ({ signal }) => getBlackout(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getBlackout>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetBlackoutQueryResult = NonNullable<Awaited<ReturnType<typeof getBlackout>>>
export type GetBlackoutQueryError = unknown


export function useGetBlackout<TData = Awaited<ReturnType<typeof getBlackout>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBlackout>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBlackout>>,
          TError,
          Awaited<ReturnType<typeof getBlackout>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetBlackout<TData = Awaited<ReturnType<typeof getBlackout>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBlackout>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBlackout>>,
          TError,
          Awaited<ReturnType<typeof getBlackout>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetBlackout<TData = Awaited<ReturnType<typeof getBlackout>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBlackout>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetBlackout<TData = Awaited<ReturnType<typeof getBlackout>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBlackout>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetBlackoutQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateBlackoutUrl = (id: string,) => {




  return `/api/blackouts/${id}`
}

export const updateBlackout = async (id: string,
    updateBlackoutBody: UpdateBlackout, options?: Parameters<typeof customFetch>[1]): Promise<Blackout> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Blackout>(getUpdateBlackoutUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateBlackoutBody)
  }
);}





export const getUpdateBlackoutMutationKey = () => ['updateBlackout'] as const;

export const getUpdateBlackoutMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateBlackout>>, TError,UpdateBlackoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateBlackout>>, TError,UpdateBlackoutMutationVariables, TContext> => {

const mutationKey = getUpdateBlackoutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateBlackout>>, UpdateBlackoutMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateBlackout(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateBlackoutMutationResult = NonNullable<Awaited<ReturnType<typeof updateBlackout>>>
    export type UpdateBlackoutMutationBody = UpdateBlackout
    export type UpdateBlackoutMutationError = unknown
    export type UpdateBlackoutMutationVariables = {id: string;data: UpdateBlackout}

    export const useUpdateBlackout = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateBlackout>>, TError,UpdateBlackoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateBlackout>>,
        TError,
        UpdateBlackoutMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateBlackoutMutationOptions(options), queryClient);
    }

export const getDeleteBlackoutUrl = (id: string,) => {




  return `/api/blackouts/${id}`
}

export const deleteBlackout = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteBlackoutUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteBlackoutMutationKey = () => ['deleteBlackout'] as const;

export const getDeleteBlackoutMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteBlackout>>, TError,DeleteBlackoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteBlackout>>, TError,DeleteBlackoutMutationVariables, TContext> => {

const mutationKey = getDeleteBlackoutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteBlackout>>, DeleteBlackoutMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteBlackout(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteBlackoutMutationResult = NonNullable<Awaited<ReturnType<typeof deleteBlackout>>>

    export type DeleteBlackoutMutationError = unknown
    export type DeleteBlackoutMutationVariables = {id: string}

    export const useDeleteBlackout = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteBlackout>>, TError,DeleteBlackoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteBlackout>>,
        TError,
        DeleteBlackoutMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteBlackoutMutationOptions(options), queryClient);
    }

export const getEvaluatePolicyUrl = () => {




  return `/api/policies/evaluate`
}

/**
 * Returns allowed=true when the window passes duration, advance, and blackout checks. Violations return 422 with a POLICY_* code.
 * @summary Evaluate a proposed booking window against PolicyEngine
 */
export const evaluatePolicy = async (evaluatePolicyRequest: EvaluatePolicyRequest, options?: Parameters<typeof customFetch>[1]): Promise<EvaluatePolicyResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EvaluatePolicyResponse>(getEvaluatePolicyUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(evaluatePolicyRequest)
  }
);}





export const getEvaluatePolicyMutationKey = () => ['evaluatePolicy'] as const;

export const getEvaluatePolicyMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof evaluatePolicy>>, TError,EvaluatePolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof evaluatePolicy>>, TError,EvaluatePolicyMutationVariables, TContext> => {

const mutationKey = getEvaluatePolicyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof evaluatePolicy>>, EvaluatePolicyMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  evaluatePolicy(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type EvaluatePolicyMutationResult = NonNullable<Awaited<ReturnType<typeof evaluatePolicy>>>
    export type EvaluatePolicyMutationBody = EvaluatePolicyRequest
    export type EvaluatePolicyMutationError = Error
    export type EvaluatePolicyMutationVariables = {data: EvaluatePolicyRequest}

    /**
 * @summary Evaluate a proposed booking window against PolicyEngine
 */
export const useEvaluatePolicy = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof evaluatePolicy>>, TError,EvaluatePolicyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof evaluatePolicy>>,
        TError,
        EvaluatePolicyMutationVariables,
        TContext
      > => {
      return useMutation(getEvaluatePolicyMutationOptions(options), queryClient);
    }

export const getPostAiChatUrl = () => {




  return `/api/ai/chat`
}

/**
 * Server-side LLM proxy. The client may only send a message and an
 * optional conversation id — model, endpoint, and credentials are
 * resolved exclusively on the server.
 * @summary Send a chat message to the app assistant
 */
export const postAiChat = async (chatRequest: ChatRequest, options?: Parameters<typeof customFetch>[1]): Promise<ChatResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ChatResponse>(getPostAiChatUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(chatRequest)
  }
);}





export const getPostAiChatMutationKey = () => ['postAiChat'] as const;

export const getPostAiChatMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postAiChat>>, TError,PostAiChatMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postAiChat>>, TError,PostAiChatMutationVariables, TContext> => {

const mutationKey = getPostAiChatMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postAiChat>>, PostAiChatMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  postAiChat(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostAiChatMutationResult = NonNullable<Awaited<ReturnType<typeof postAiChat>>>
    export type PostAiChatMutationBody = ChatRequest
    export type PostAiChatMutationError = Error
    export type PostAiChatMutationVariables = {data: ChatRequest}

    /**
 * @summary Send a chat message to the app assistant
 */
export const usePostAiChat = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postAiChat>>, TError,PostAiChatMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postAiChat>>,
        TError,
        PostAiChatMutationVariables,
        TContext
      > => {
      return useMutation(getPostAiChatMutationOptions(options), queryClient);
    }

export const getPostAiAssistantChatUrl = () => {




  return `/api/ai/assistant/chat`
}

/**
 * Natural-language booking assistant. Searches/books only via server tools.
 * Bookings are never created in this call — propose actions require
 * POST /api/ai/assistant/confirm after explicit user confirmation.
 * @summary Tool-calling booking assistant turn
 */
export const postAiAssistantChat = async (assistantChatRequest: AssistantChatRequest, options?: Parameters<typeof customFetch>[1]): Promise<AssistantChatResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AssistantChatResponse>(getPostAiAssistantChatUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(assistantChatRequest)
  }
);}





export const getPostAiAssistantChatMutationKey = () => ['postAiAssistantChat'] as const;

export const getPostAiAssistantChatMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postAiAssistantChat>>, TError,PostAiAssistantChatMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postAiAssistantChat>>, TError,PostAiAssistantChatMutationVariables, TContext> => {

const mutationKey = getPostAiAssistantChatMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postAiAssistantChat>>, PostAiAssistantChatMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  postAiAssistantChat(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostAiAssistantChatMutationResult = NonNullable<Awaited<ReturnType<typeof postAiAssistantChat>>>
    export type PostAiAssistantChatMutationBody = AssistantChatRequest
    export type PostAiAssistantChatMutationError = Error
    export type PostAiAssistantChatMutationVariables = {data: AssistantChatRequest}

    /**
 * @summary Tool-calling booking assistant turn
 */
export const usePostAiAssistantChat = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postAiAssistantChat>>, TError,PostAiAssistantChatMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postAiAssistantChat>>,
        TError,
        PostAiAssistantChatMutationVariables,
        TContext
      > => {
      return useMutation(getPostAiAssistantChatMutationOptions(options), queryClient);
    }

export const getPostAiAssistantConfirmUrl = () => {




  return `/api/ai/assistant/confirm`
}

/**
 * Consumes a short-lived confirmation token issued by the assistant tools
 * and executes the write with source=ai (create) or cancel.
 * @summary Confirm a proposed AI booking/cancel action
 */
export const postAiAssistantConfirm = async (assistantConfirmRequest: AssistantConfirmRequest, options?: Parameters<typeof customFetch>[1]): Promise<AssistantConfirmResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<AssistantConfirmResponse>(getPostAiAssistantConfirmUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(assistantConfirmRequest)
  }
);}





export const getPostAiAssistantConfirmMutationKey = () => ['postAiAssistantConfirm'] as const;

export const getPostAiAssistantConfirmMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postAiAssistantConfirm>>, TError,PostAiAssistantConfirmMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postAiAssistantConfirm>>, TError,PostAiAssistantConfirmMutationVariables, TContext> => {

const mutationKey = getPostAiAssistantConfirmMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postAiAssistantConfirm>>, PostAiAssistantConfirmMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  postAiAssistantConfirm(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostAiAssistantConfirmMutationResult = NonNullable<Awaited<ReturnType<typeof postAiAssistantConfirm>>>
    export type PostAiAssistantConfirmMutationBody = AssistantConfirmRequest
    export type PostAiAssistantConfirmMutationError = Error
    export type PostAiAssistantConfirmMutationVariables = {data: AssistantConfirmRequest}

    /**
 * @summary Confirm a proposed AI booking/cancel action
 */
export const usePostAiAssistantConfirm = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postAiAssistantConfirm>>, TError,PostAiAssistantConfirmMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postAiAssistantConfirm>>,
        TError,
        PostAiAssistantConfirmMutationVariables,
        TContext
      > => {
      return useMutation(getPostAiAssistantConfirmMutationOptions(options), queryClient);
    }

export const getListFilesUrl = () => {




  return `/api/files`
}

/**
 * @summary List stored files
 */
export const listFiles = async ( options?: Parameters<typeof customFetch>[1]): Promise<FileList> => {

  return customFetch<FileList>(getListFilesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListFilesQueryKey = () => {
    return [
    `/api/files`
    ] as const;
    }


export const getListFilesQueryOptions = <TData = Awaited<ReturnType<typeof listFiles>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listFiles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListFilesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listFiles>>> = ({ signal }) => listFiles({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listFiles>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListFilesQueryResult = NonNullable<Awaited<ReturnType<typeof listFiles>>>
export type ListFilesQueryError = Error


export function useListFiles<TData = Awaited<ReturnType<typeof listFiles>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listFiles>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFiles>>,
          TError,
          Awaited<ReturnType<typeof listFiles>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListFiles<TData = Awaited<ReturnType<typeof listFiles>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listFiles>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFiles>>,
          TError,
          Awaited<ReturnType<typeof listFiles>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListFiles<TData = Awaited<ReturnType<typeof listFiles>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listFiles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List stored files
 */

export function useListFiles<TData = Awaited<ReturnType<typeof listFiles>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listFiles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListFilesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUploadFileUrl = () => {




  return `/api/files`
}

/**
 * @summary Upload a file
 */
export const uploadFile = async (uploadFileBody: UploadFileBody, options?: Parameters<typeof customFetch>[1]): Promise<StoredFile> => {
    const formData = new FormData();
formData.append(`file`, uploadFileBody.file);

  return customFetch<StoredFile>(getUploadFileUrl(),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}





export const getUploadFileMutationKey = () => ['uploadFile'] as const;

export const getUploadFileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadFile>>, TError,UploadFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof uploadFile>>, TError,UploadFileMutationVariables, TContext> => {

const mutationKey = getUploadFileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof uploadFile>>, UploadFileMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  uploadFile(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UploadFileMutationResult = NonNullable<Awaited<ReturnType<typeof uploadFile>>>
    export type UploadFileMutationBody = UploadFileBody
    export type UploadFileMutationError = Error
    export type UploadFileMutationVariables = {data: UploadFileBody}

    /**
 * @summary Upload a file
 */
export const useUploadFile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof uploadFile>>, TError,UploadFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof uploadFile>>,
        TError,
        UploadFileMutationVariables,
        TContext
      > => {
      return useMutation(getUploadFileMutationOptions(options), queryClient);
    }

export const getDeleteFileUrl = (id: string,) => {




  return `/api/files/${id}`
}

/**
 * @summary Delete a stored file
 */
export const deleteFile = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteFileUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteFileMutationKey = () => ['deleteFile'] as const;

export const getDeleteFileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteFile>>, TError,DeleteFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteFile>>, TError,DeleteFileMutationVariables, TContext> => {

const mutationKey = getDeleteFileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteFile>>, DeleteFileMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteFile(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteFileMutationResult = NonNullable<Awaited<ReturnType<typeof deleteFile>>>

    export type DeleteFileMutationError = Error
    export type DeleteFileMutationVariables = {id: string}

    /**
 * @summary Delete a stored file
 */
export const useDeleteFile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteFile>>, TError,DeleteFileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteFile>>,
        TError,
        DeleteFileMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteFileMutationOptions(options), queryClient);
    }

export const getDownloadFileUrl = (id: string,) => {




  return `/api/files/${id}/content`
}

/**
 * @summary Download file content
 */
export const downloadFile = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadFileUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadFileQueryKey = (id: string,) => {
    return [
    `/api/files/${id}/content`
    ] as const;
    }


export const getDownloadFileQueryOptions = <TData = Awaited<ReturnType<typeof downloadFile>>, TError = Error>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadFileQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadFile>>> = ({ signal }) => downloadFile(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type DownloadFileQueryResult = NonNullable<Awaited<ReturnType<typeof downloadFile>>>
export type DownloadFileQueryError = Error


export function useDownloadFile<TData = Awaited<ReturnType<typeof downloadFile>>, TError = Error>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadFile>>,
          TError,
          Awaited<ReturnType<typeof downloadFile>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useDownloadFile<TData = Awaited<ReturnType<typeof downloadFile>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadFile>>,
          TError,
          Awaited<ReturnType<typeof downloadFile>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useDownloadFile<TData = Awaited<ReturnType<typeof downloadFile>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Download file content
 */

export function useDownloadFile<TData = Awaited<ReturnType<typeof downloadFile>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadFile>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getDownloadFileQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSearchAvailabilityUrl = (params: SearchAvailabilityParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/availability/search?${stringifiedParams}` : `/api/availability/search`
}

/**
 * Returns bookable spaces free of app conflicts, blackouts, and policy violations for the requested half-open window [start, end). Day-window semantics per FR-EU-008.
 * @summary Search spaces available for a day/window
 */
export const searchAvailability = async (params: SearchAvailabilityParams, options?: Parameters<typeof customFetch>[1]): Promise<AvailabilitySearchResponse> => {

  return customFetch<AvailabilitySearchResponse>(getSearchAvailabilityUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getSearchAvailabilityQueryKey = (params?: SearchAvailabilityParams,) => {
    return [
    `/api/availability/search`, ...(params ? [params] : [])
    ] as const;
    }


export const getSearchAvailabilityQueryOptions = <TData = Awaited<ReturnType<typeof searchAvailability>>, TError = Error>(params: SearchAvailabilityParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchAvailability>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getSearchAvailabilityQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof searchAvailability>>> = ({ signal }) => searchAvailability(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof searchAvailability>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type SearchAvailabilityQueryResult = NonNullable<Awaited<ReturnType<typeof searchAvailability>>>
export type SearchAvailabilityQueryError = Error


export function useSearchAvailability<TData = Awaited<ReturnType<typeof searchAvailability>>, TError = Error>(
 params: SearchAvailabilityParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchAvailability>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchAvailability>>,
          TError,
          Awaited<ReturnType<typeof searchAvailability>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSearchAvailability<TData = Awaited<ReturnType<typeof searchAvailability>>, TError = Error>(
 params: SearchAvailabilityParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchAvailability>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchAvailability>>,
          TError,
          Awaited<ReturnType<typeof searchAvailability>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSearchAvailability<TData = Awaited<ReturnType<typeof searchAvailability>>, TError = Error>(
 params: SearchAvailabilityParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchAvailability>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Search spaces available for a day/window
 */

export function useSearchAvailability<TData = Awaited<ReturnType<typeof searchAvailability>>, TError = Error>(
 params: SearchAvailabilityParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchAvailability>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getSearchAvailabilityQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListBookingsUrl = (params?: ListBookingsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/bookings?${stringifiedParams}` : `/api/bookings`
}

/**
 * @summary List bookings for the signed-in user
 */
export const listBookings = async (params?: ListBookingsParams, options?: Parameters<typeof customFetch>[1]): Promise<BookingListResponse> => {

  return customFetch<BookingListResponse>(getListBookingsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListBookingsQueryKey = (params?: ListBookingsParams,) => {
    return [
    `/api/bookings`, ...(params ? [params] : [])
    ] as const;
    }


export const getListBookingsQueryOptions = <TData = Awaited<ReturnType<typeof listBookings>>, TError = Error>(params?: ListBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBookings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListBookingsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listBookings>>> = ({ signal }) => listBookings(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listBookings>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListBookingsQueryResult = NonNullable<Awaited<ReturnType<typeof listBookings>>>
export type ListBookingsQueryError = Error


export function useListBookings<TData = Awaited<ReturnType<typeof listBookings>>, TError = Error>(
 params: undefined |  ListBookingsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBookings>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listBookings>>,
          TError,
          Awaited<ReturnType<typeof listBookings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListBookings<TData = Awaited<ReturnType<typeof listBookings>>, TError = Error>(
 params?: ListBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBookings>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listBookings>>,
          TError,
          Awaited<ReturnType<typeof listBookings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListBookings<TData = Awaited<ReturnType<typeof listBookings>>, TError = Error>(
 params?: ListBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBookings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List bookings for the signed-in user
 */

export function useListBookings<TData = Awaited<ReturnType<typeof listBookings>>, TError = Error>(
 params?: ListBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBookings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListBookingsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateBookingUrl = () => {




  return `/api/bookings`
}

/**
 * @summary Create a single-space booking
 */
export const createBooking = async (createBookingRequest: CreateBookingRequest, options?: Parameters<typeof customFetch>[1]): Promise<Booking> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Booking>(getCreateBookingUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createBookingRequest)
  }
);}





export const getCreateBookingMutationKey = () => ['createBooking'] as const;

export const getCreateBookingMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBooking>>, TError,CreateBookingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createBooking>>, TError,CreateBookingMutationVariables, TContext> => {

const mutationKey = getCreateBookingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createBooking>>, CreateBookingMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createBooking(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateBookingMutationResult = NonNullable<Awaited<ReturnType<typeof createBooking>>>
    export type CreateBookingMutationBody = CreateBookingRequest
    export type CreateBookingMutationError = Error
    export type CreateBookingMutationVariables = {data: CreateBookingRequest}

    /**
 * @summary Create a single-space booking
 */
export const useCreateBooking = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBooking>>, TError,CreateBookingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createBooking>>,
        TError,
        CreateBookingMutationVariables,
        TContext
      > => {
      return useMutation(getCreateBookingMutationOptions(options), queryClient);
    }

export const getGetBookingUrl = (id: string,) => {




  return `/api/bookings/${id}`
}

/**
 * @summary Get a booking by id
 */
export const getBooking = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Booking> => {

  return customFetch<Booking>(getGetBookingUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetBookingQueryKey = (id: string,) => {
    return [
    `/api/bookings/${id}`
    ] as const;
    }


export const getGetBookingQueryOptions = <TData = Awaited<ReturnType<typeof getBooking>>, TError = Error>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBooking>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetBookingQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getBooking>>> = ({ signal }) => getBooking(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getBooking>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetBookingQueryResult = NonNullable<Awaited<ReturnType<typeof getBooking>>>
export type GetBookingQueryError = Error


export function useGetBooking<TData = Awaited<ReturnType<typeof getBooking>>, TError = Error>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBooking>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBooking>>,
          TError,
          Awaited<ReturnType<typeof getBooking>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetBooking<TData = Awaited<ReturnType<typeof getBooking>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBooking>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getBooking>>,
          TError,
          Awaited<ReturnType<typeof getBooking>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetBooking<TData = Awaited<ReturnType<typeof getBooking>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBooking>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get a booking by id
 */

export function useGetBooking<TData = Awaited<ReturnType<typeof getBooking>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getBooking>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetBookingQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateBookingUrl = (id: string,) => {




  return `/api/bookings/${id}`
}

/**
 * @summary Modify an active booking window or title
 */
export const updateBooking = async (id: string,
    updateBookingRequest: UpdateBookingRequest, options?: Parameters<typeof customFetch>[1]): Promise<Booking> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Booking>(getUpdateBookingUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateBookingRequest)
  }
);}





export const getUpdateBookingMutationKey = () => ['updateBooking'] as const;

export const getUpdateBookingMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateBooking>>, TError,UpdateBookingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateBooking>>, TError,UpdateBookingMutationVariables, TContext> => {

const mutationKey = getUpdateBookingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateBooking>>, UpdateBookingMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateBooking(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateBookingMutationResult = NonNullable<Awaited<ReturnType<typeof updateBooking>>>
    export type UpdateBookingMutationBody = UpdateBookingRequest
    export type UpdateBookingMutationError = Error
    export type UpdateBookingMutationVariables = {id: string;data: UpdateBookingRequest}

    /**
 * @summary Modify an active booking window or title
 */
export const useUpdateBooking = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateBooking>>, TError,UpdateBookingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateBooking>>,
        TError,
        UpdateBookingMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateBookingMutationOptions(options), queryClient);
    }

export const getDeleteBookingHistoryUrl = (id: string,) => {




  return `/api/bookings/${id}`
}

/**
 * Permanently removes a booking that has already ended, or was cancelled
 * or auto-released. Future bookings must be cancelled first, and bookings
 * still waiting on a calendar sync are refused until it settles. The
 * deletion is recorded in the append-only audit trail.
 * @summary Delete a past or cancelled booking from history
 */
export const deleteBookingHistory = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteBookingHistoryUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteBookingHistoryMutationKey = () => ['deleteBookingHistory'] as const;

export const getDeleteBookingHistoryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteBookingHistory>>, TError,DeleteBookingHistoryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteBookingHistory>>, TError,DeleteBookingHistoryMutationVariables, TContext> => {

const mutationKey = getDeleteBookingHistoryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteBookingHistory>>, DeleteBookingHistoryMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteBookingHistory(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteBookingHistoryMutationResult = NonNullable<Awaited<ReturnType<typeof deleteBookingHistory>>>

    export type DeleteBookingHistoryMutationError = Error
    export type DeleteBookingHistoryMutationVariables = {id: string}

    /**
 * @summary Delete a past or cancelled booking from history
 */
export const useDeleteBookingHistory = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteBookingHistory>>, TError,DeleteBookingHistoryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteBookingHistory>>,
        TError,
        DeleteBookingHistoryMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteBookingHistoryMutationOptions(options), queryClient);
    }

export const getCancelBookingUrl = (id: string,) => {




  return `/api/bookings/${id}/cancel`
}

/**
 * @summary Cancel a booking (or remaining series from this occurrence)
 */
export const cancelBooking = async (id: string,
    cancelBookingRequest?: CancelBookingRequest, options?: Parameters<typeof customFetch>[1]): Promise<Booking> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Booking>(getCancelBookingUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(cancelBookingRequest)
  }
);}





export const getCancelBookingMutationKey = () => ['cancelBooking'] as const;

export const getCancelBookingMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelBooking>>, TError,CancelBookingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelBooking>>, TError,CancelBookingMutationVariables, TContext> => {

const mutationKey = getCancelBookingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelBooking>>, CancelBookingMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  cancelBooking(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelBookingMutationResult = NonNullable<Awaited<ReturnType<typeof cancelBooking>>>
    export type CancelBookingMutationBody = CancelBookingRequest | undefined
    export type CancelBookingMutationError = Error
    export type CancelBookingMutationVariables = {id: string;data?: CancelBookingRequest}

    /**
 * @summary Cancel a booking (or remaining series from this occurrence)
 */
export const useCancelBooking = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelBooking>>, TError,CancelBookingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof cancelBooking>>,
        TError,
        CancelBookingMutationVariables,
        TContext
      > => {
      return useMutation(getCancelBookingMutationOptions(options), queryClient);
    }

export const getPreviewBookingSeriesUrl = () => {




  return `/api/booking-series/preview`
}

/**
 * @summary Dry-run a recurring series and list planned vs skipped occurrences
 */
export const previewBookingSeries = async (createBookingSeriesRequest: CreateBookingSeriesRequest, options?: Parameters<typeof customFetch>[1]): Promise<BookingSeriesPreviewResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<BookingSeriesPreviewResponse>(getPreviewBookingSeriesUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createBookingSeriesRequest)
  }
);}





export const getPreviewBookingSeriesMutationKey = () => ['previewBookingSeries'] as const;

export const getPreviewBookingSeriesMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewBookingSeries>>, TError,PreviewBookingSeriesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewBookingSeries>>, TError,PreviewBookingSeriesMutationVariables, TContext> => {

const mutationKey = getPreviewBookingSeriesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewBookingSeries>>, PreviewBookingSeriesMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  previewBookingSeries(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewBookingSeriesMutationResult = NonNullable<Awaited<ReturnType<typeof previewBookingSeries>>>
    export type PreviewBookingSeriesMutationBody = CreateBookingSeriesRequest
    export type PreviewBookingSeriesMutationError = Error
    export type PreviewBookingSeriesMutationVariables = {data: CreateBookingSeriesRequest}

    /**
 * @summary Dry-run a recurring series and list planned vs skipped occurrences
 */
export const usePreviewBookingSeries = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewBookingSeries>>, TError,PreviewBookingSeriesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof previewBookingSeries>>,
        TError,
        PreviewBookingSeriesMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewBookingSeriesMutationOptions(options), queryClient);
    }

export const getCreateBookingSeriesUrl = () => {




  return `/api/booking-series`
}

/**
 * @summary Create a recurring booking series (skip conflicted occurrences)
 */
export const createBookingSeries = async (createBookingSeriesRequest: CreateBookingSeriesRequest, options?: Parameters<typeof customFetch>[1]): Promise<BookingSeriesCreateResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<BookingSeriesCreateResponse>(getCreateBookingSeriesUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createBookingSeriesRequest)
  }
);}





export const getCreateBookingSeriesMutationKey = () => ['createBookingSeries'] as const;

export const getCreateBookingSeriesMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBookingSeries>>, TError,CreateBookingSeriesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createBookingSeries>>, TError,CreateBookingSeriesMutationVariables, TContext> => {

const mutationKey = getCreateBookingSeriesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createBookingSeries>>, CreateBookingSeriesMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createBookingSeries(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateBookingSeriesMutationResult = NonNullable<Awaited<ReturnType<typeof createBookingSeries>>>
    export type CreateBookingSeriesMutationBody = CreateBookingSeriesRequest
    export type CreateBookingSeriesMutationError = Error
    export type CreateBookingSeriesMutationVariables = {data: CreateBookingSeriesRequest}

    /**
 * @summary Create a recurring booking series (skip conflicted occurrences)
 */
export const useCreateBookingSeries = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createBookingSeries>>, TError,CreateBookingSeriesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createBookingSeries>>,
        TError,
        CreateBookingSeriesMutationVariables,
        TContext
      > => {
      return useMutation(getCreateBookingSeriesMutationOptions(options), queryClient);
    }

export const getCheckInBookingUrl = (id: string,) => {




  return `/api/bookings/${id}/check-in`
}

/**
 * @summary Check in to a booking within the policy window
 */
export const checkInBooking = async (id: string,
    checkInBookingRequest?: CheckInBookingRequest, options?: Parameters<typeof customFetch>[1]): Promise<Booking> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Booking>(getCheckInBookingUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(checkInBookingRequest)
  }
);}





export const getCheckInBookingMutationKey = () => ['checkInBooking'] as const;

export const getCheckInBookingMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof checkInBooking>>, TError,CheckInBookingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof checkInBooking>>, TError,CheckInBookingMutationVariables, TContext> => {

const mutationKey = getCheckInBookingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof checkInBooking>>, CheckInBookingMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  checkInBooking(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CheckInBookingMutationResult = NonNullable<Awaited<ReturnType<typeof checkInBooking>>>
    export type CheckInBookingMutationBody = CheckInBookingRequest | undefined
    export type CheckInBookingMutationError = Error
    export type CheckInBookingMutationVariables = {id: string;data?: CheckInBookingRequest}

    /**
 * @summary Check in to a booking within the policy window
 */
export const useCheckInBooking = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof checkInBooking>>, TError,CheckInBookingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof checkInBooking>>,
        TError,
        CheckInBookingMutationVariables,
        TContext
      > => {
      return useMutation(getCheckInBookingMutationOptions(options), queryClient);
    }

export const getCheckOutBookingUrl = (id: string,) => {




  return `/api/bookings/${id}/check-out`
}

/**
 * Truncates the booking endAt to now so the remaining interval becomes
 * available to other occupants. The booking stays in its current active
 * status (e.g. checked_in) so attendance history remains honest — it is
 * not marked cancelled. Eligible when startAt <= now < endAt and the
 * status is checked_in, confirmed, or pending_check_in.
 * @summary Check out early from an in-progress booking to free remaining time
 */
export const checkOutBooking = async (id: string,
    checkOutBookingRequest?: CheckOutBookingRequest, options?: Parameters<typeof customFetch>[1]): Promise<Booking> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Booking>(getCheckOutBookingUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(checkOutBookingRequest)
  }
);}





export const getCheckOutBookingMutationKey = () => ['checkOutBooking'] as const;

export const getCheckOutBookingMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof checkOutBooking>>, TError,CheckOutBookingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof checkOutBooking>>, TError,CheckOutBookingMutationVariables, TContext> => {

const mutationKey = getCheckOutBookingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof checkOutBooking>>, CheckOutBookingMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  checkOutBooking(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CheckOutBookingMutationResult = NonNullable<Awaited<ReturnType<typeof checkOutBooking>>>
    export type CheckOutBookingMutationBody = CheckOutBookingRequest | undefined
    export type CheckOutBookingMutationError = Error
    export type CheckOutBookingMutationVariables = {id: string;data?: CheckOutBookingRequest}

    /**
 * @summary Check out early from an in-progress booking to free remaining time
 */
export const useCheckOutBooking = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof checkOutBooking>>, TError,CheckOutBookingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof checkOutBooking>>,
        TError,
        CheckOutBookingMutationVariables,
        TContext
      > => {
      return useMutation(getCheckOutBookingMutationOptions(options), queryClient);
    }

export const getTakeOverBookingUrl = (id: string,) => {




  return `/api/bookings/${id}/take-over`
}

/**
 * Any signed-in employee may claim a desk whose blocking booking is still
 * pending_check_in after checkInBy and before end. In one transaction the
 * original booking becomes auto_released and a new checked_in booking is
 * created for the caller until the original end time. The original booker
 * should use check-in instead.
 * @summary Take over an unclaimed desk after the check-in grace period
 */
export const takeOverBooking = async (id: string,
    takeOverBookingBody?: TakeOverBookingBody, options?: Parameters<typeof customFetch>[1]): Promise<Booking> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Booking>(getTakeOverBookingUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(takeOverBookingBody)
  }
);}





export const getTakeOverBookingMutationKey = () => ['takeOverBooking'] as const;

export const getTakeOverBookingMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof takeOverBooking>>, TError,TakeOverBookingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof takeOverBooking>>, TError,TakeOverBookingMutationVariables, TContext> => {

const mutationKey = getTakeOverBookingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof takeOverBooking>>, TakeOverBookingMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  takeOverBooking(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TakeOverBookingMutationResult = NonNullable<Awaited<ReturnType<typeof takeOverBooking>>>
    export type TakeOverBookingMutationBody = TakeOverBookingBody | undefined
    export type TakeOverBookingMutationError = Error
    export type TakeOverBookingMutationVariables = {id: string;data?: TakeOverBookingBody}

    /**
 * @summary Take over an unclaimed desk after the check-in grace period
 */
export const useTakeOverBooking = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof takeOverBooking>>, TError,TakeOverBookingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof takeOverBooking>>,
        TError,
        TakeOverBookingMutationVariables,
        TContext
      > => {
      return useMutation(getTakeOverBookingMutationOptions(options), queryClient);
    }

export const getListBookingHistoryUrl = (id: string,) => {




  return `/api/bookings/${id}/history`
}

/**
 * @summary Lifecycle audit history for a booking
 */
export const listBookingHistory = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<AuditEventListResponse> => {

  return customFetch<AuditEventListResponse>(getListBookingHistoryUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListBookingHistoryQueryKey = (id: string,) => {
    return [
    `/api/bookings/${id}/history`
    ] as const;
    }


export const getListBookingHistoryQueryOptions = <TData = Awaited<ReturnType<typeof listBookingHistory>>, TError = Error>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBookingHistory>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListBookingHistoryQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listBookingHistory>>> = ({ signal }) => listBookingHistory(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listBookingHistory>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListBookingHistoryQueryResult = NonNullable<Awaited<ReturnType<typeof listBookingHistory>>>
export type ListBookingHistoryQueryError = Error


export function useListBookingHistory<TData = Awaited<ReturnType<typeof listBookingHistory>>, TError = Error>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBookingHistory>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listBookingHistory>>,
          TError,
          Awaited<ReturnType<typeof listBookingHistory>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListBookingHistory<TData = Awaited<ReturnType<typeof listBookingHistory>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBookingHistory>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listBookingHistory>>,
          TError,
          Awaited<ReturnType<typeof listBookingHistory>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListBookingHistory<TData = Awaited<ReturnType<typeof listBookingHistory>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBookingHistory>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Lifecycle audit history for a booking
 */

export function useListBookingHistory<TData = Awaited<ReturnType<typeof listBookingHistory>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listBookingHistory>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListBookingHistoryQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListDirectoryUsersUrl = (params?: ListDirectoryUsersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/directory/users?${stringifiedParams}` : `/api/directory/users`
}

/**
 * @summary Directory search for book-on-behalf recipients
 */
export const listDirectoryUsers = async (params?: ListDirectoryUsersParams, options?: Parameters<typeof customFetch>[1]): Promise<DirectoryUserListResponse> => {

  return customFetch<DirectoryUserListResponse>(getListDirectoryUsersUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListDirectoryUsersQueryKey = (params?: ListDirectoryUsersParams,) => {
    return [
    `/api/directory/users`, ...(params ? [params] : [])
    ] as const;
    }


export const getListDirectoryUsersQueryOptions = <TData = Awaited<ReturnType<typeof listDirectoryUsers>>, TError = Error>(params?: ListDirectoryUsersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listDirectoryUsers>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListDirectoryUsersQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listDirectoryUsers>>> = ({ signal }) => listDirectoryUsers(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listDirectoryUsers>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListDirectoryUsersQueryResult = NonNullable<Awaited<ReturnType<typeof listDirectoryUsers>>>
export type ListDirectoryUsersQueryError = Error


export function useListDirectoryUsers<TData = Awaited<ReturnType<typeof listDirectoryUsers>>, TError = Error>(
 params: undefined |  ListDirectoryUsersParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listDirectoryUsers>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listDirectoryUsers>>,
          TError,
          Awaited<ReturnType<typeof listDirectoryUsers>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListDirectoryUsers<TData = Awaited<ReturnType<typeof listDirectoryUsers>>, TError = Error>(
 params?: ListDirectoryUsersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listDirectoryUsers>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listDirectoryUsers>>,
          TError,
          Awaited<ReturnType<typeof listDirectoryUsers>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListDirectoryUsers<TData = Awaited<ReturnType<typeof listDirectoryUsers>>, TError = Error>(
 params?: ListDirectoryUsersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listDirectoryUsers>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Directory search for book-on-behalf recipients
 */

export function useListDirectoryUsers<TData = Awaited<ReturnType<typeof listDirectoryUsers>>, TError = Error>(
 params?: ListDirectoryUsersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listDirectoryUsers>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListDirectoryUsersQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetUtilisationReportUrl = (params: GetUtilisationReportParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/reports/utilisation?${stringifiedParams}` : `/api/reports/utilisation`
}

/**
 * Aggregates booked occupancy versus capacity for the report window, grouped by site, building, and space type. Requires reports:view. Pass format=csv for a CSV download of the item rows.
 * @summary Space utilisation report
 */
export const getUtilisationReport = async (params: GetUtilisationReportParams, options?: Parameters<typeof customFetch>[1]): Promise<UtilisationReport | Blob> => {

  return customFetch<UtilisationReport | Blob>(getGetUtilisationReportUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetUtilisationReportQueryKey = (params?: GetUtilisationReportParams,) => {
    return [
    `/api/reports/utilisation`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetUtilisationReportQueryOptions = <TData = Awaited<ReturnType<typeof getUtilisationReport>>, TError = Error>(params: GetUtilisationReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUtilisationReport>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetUtilisationReportQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getUtilisationReport>>> = ({ signal }) => getUtilisationReport(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getUtilisationReport>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetUtilisationReportQueryResult = NonNullable<Awaited<ReturnType<typeof getUtilisationReport>>>
export type GetUtilisationReportQueryError = Error


export function useGetUtilisationReport<TData = Awaited<ReturnType<typeof getUtilisationReport>>, TError = Error>(
 params: GetUtilisationReportParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUtilisationReport>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUtilisationReport>>,
          TError,
          Awaited<ReturnType<typeof getUtilisationReport>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetUtilisationReport<TData = Awaited<ReturnType<typeof getUtilisationReport>>, TError = Error>(
 params: GetUtilisationReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUtilisationReport>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUtilisationReport>>,
          TError,
          Awaited<ReturnType<typeof getUtilisationReport>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetUtilisationReport<TData = Awaited<ReturnType<typeof getUtilisationReport>>, TError = Error>(
 params: GetUtilisationReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUtilisationReport>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Space utilisation report
 */

export function useGetUtilisationReport<TData = Awaited<ReturnType<typeof getUtilisationReport>>, TError = Error>(
 params: GetUtilisationReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUtilisationReport>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetUtilisationReportQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetUtilisationTimeseriesUrl = (params: GetUtilisationTimeseriesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/reports/utilisation/timeseries?${stringifiedParams}` : `/api/reports/utilisation/timeseries`
}

/**
 * Returns per-day per-site utilisation series for the report window. Requires reports:view. Pass format=csv for a CSV download.
 * @summary Utilisation trend over time
 */
export const getUtilisationTimeseries = async (params: GetUtilisationTimeseriesParams, options?: Parameters<typeof customFetch>[1]): Promise<UtilisationTimeseries | Blob> => {

  return customFetch<UtilisationTimeseries | Blob>(getGetUtilisationTimeseriesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetUtilisationTimeseriesQueryKey = (params?: GetUtilisationTimeseriesParams,) => {
    return [
    `/api/reports/utilisation/timeseries`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetUtilisationTimeseriesQueryOptions = <TData = Awaited<ReturnType<typeof getUtilisationTimeseries>>, TError = Error>(params: GetUtilisationTimeseriesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUtilisationTimeseries>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetUtilisationTimeseriesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getUtilisationTimeseries>>> = ({ signal }) => getUtilisationTimeseries(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getUtilisationTimeseries>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetUtilisationTimeseriesQueryResult = NonNullable<Awaited<ReturnType<typeof getUtilisationTimeseries>>>
export type GetUtilisationTimeseriesQueryError = Error


export function useGetUtilisationTimeseries<TData = Awaited<ReturnType<typeof getUtilisationTimeseries>>, TError = Error>(
 params: GetUtilisationTimeseriesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUtilisationTimeseries>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUtilisationTimeseries>>,
          TError,
          Awaited<ReturnType<typeof getUtilisationTimeseries>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetUtilisationTimeseries<TData = Awaited<ReturnType<typeof getUtilisationTimeseries>>, TError = Error>(
 params: GetUtilisationTimeseriesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUtilisationTimeseries>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUtilisationTimeseries>>,
          TError,
          Awaited<ReturnType<typeof getUtilisationTimeseries>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetUtilisationTimeseries<TData = Awaited<ReturnType<typeof getUtilisationTimeseries>>, TError = Error>(
 params: GetUtilisationTimeseriesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUtilisationTimeseries>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Utilisation trend over time
 */

export function useGetUtilisationTimeseries<TData = Awaited<ReturnType<typeof getUtilisationTimeseries>>, TError = Error>(
 params: GetUtilisationTimeseriesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUtilisationTimeseries>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetUtilisationTimeseriesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetNoShowReportUrl = (params: GetNoShowReportParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/reports/no-shows?${stringifiedParams}` : `/api/reports/no-shows`
}

/**
 * Lists bookings that were auto-released for missed check-in in the window. Requires reports:view. Pass format=csv for a CSV download.
 * @summary No-show (auto-released) report
 */
export const getNoShowReport = async (params: GetNoShowReportParams, options?: Parameters<typeof customFetch>[1]): Promise<NoShowReport | Blob> => {

  return customFetch<NoShowReport | Blob>(getGetNoShowReportUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetNoShowReportQueryKey = (params?: GetNoShowReportParams,) => {
    return [
    `/api/reports/no-shows`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetNoShowReportQueryOptions = <TData = Awaited<ReturnType<typeof getNoShowReport>>, TError = Error>(params: GetNoShowReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNoShowReport>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetNoShowReportQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getNoShowReport>>> = ({ signal }) => getNoShowReport(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getNoShowReport>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetNoShowReportQueryResult = NonNullable<Awaited<ReturnType<typeof getNoShowReport>>>
export type GetNoShowReportQueryError = Error


export function useGetNoShowReport<TData = Awaited<ReturnType<typeof getNoShowReport>>, TError = Error>(
 params: GetNoShowReportParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNoShowReport>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNoShowReport>>,
          TError,
          Awaited<ReturnType<typeof getNoShowReport>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetNoShowReport<TData = Awaited<ReturnType<typeof getNoShowReport>>, TError = Error>(
 params: GetNoShowReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNoShowReport>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNoShowReport>>,
          TError,
          Awaited<ReturnType<typeof getNoShowReport>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetNoShowReport<TData = Awaited<ReturnType<typeof getNoShowReport>>, TError = Error>(
 params: GetNoShowReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNoShowReport>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary No-show (auto-released) report
 */

export function useGetNoShowReport<TData = Awaited<ReturnType<typeof getNoShowReport>>, TError = Error>(
 params: GetNoShowReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNoShowReport>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetNoShowReportQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetIntentVsActualReportUrl = (params: GetIntentVsActualReportParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/reports/intent-vs-actual?${stringifiedParams}` : `/api/reports/intent-vs-actual`
}

/**
 * Compares declared office presence (intent) and bookings against check-ins (actual) per building per day. When no presence data exists, bookings are used as the intent signal. Requires reports:view.
 * @summary Intent vs actual occupancy report
 */
export const getIntentVsActualReport = async (params: GetIntentVsActualReportParams, options?: Parameters<typeof customFetch>[1]): Promise<IntentVsActualReport | Blob> => {

  return customFetch<IntentVsActualReport | Blob>(getGetIntentVsActualReportUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetIntentVsActualReportQueryKey = (params?: GetIntentVsActualReportParams,) => {
    return [
    `/api/reports/intent-vs-actual`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetIntentVsActualReportQueryOptions = <TData = Awaited<ReturnType<typeof getIntentVsActualReport>>, TError = Error>(params: GetIntentVsActualReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getIntentVsActualReport>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetIntentVsActualReportQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getIntentVsActualReport>>> = ({ signal }) => getIntentVsActualReport(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getIntentVsActualReport>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetIntentVsActualReportQueryResult = NonNullable<Awaited<ReturnType<typeof getIntentVsActualReport>>>
export type GetIntentVsActualReportQueryError = Error


export function useGetIntentVsActualReport<TData = Awaited<ReturnType<typeof getIntentVsActualReport>>, TError = Error>(
 params: GetIntentVsActualReportParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getIntentVsActualReport>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getIntentVsActualReport>>,
          TError,
          Awaited<ReturnType<typeof getIntentVsActualReport>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetIntentVsActualReport<TData = Awaited<ReturnType<typeof getIntentVsActualReport>>, TError = Error>(
 params: GetIntentVsActualReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getIntentVsActualReport>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getIntentVsActualReport>>,
          TError,
          Awaited<ReturnType<typeof getIntentVsActualReport>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetIntentVsActualReport<TData = Awaited<ReturnType<typeof getIntentVsActualReport>>, TError = Error>(
 params: GetIntentVsActualReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getIntentVsActualReport>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Intent vs actual occupancy report
 */

export function useGetIntentVsActualReport<TData = Awaited<ReturnType<typeof getIntentVsActualReport>>, TError = Error>(
 params: GetIntentVsActualReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getIntentVsActualReport>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetIntentVsActualReportQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetManagerAttendanceReportUrl = (params: GetManagerAttendanceReportParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/reports/manager-attendance?${stringifiedParams}` : `/api/reports/manager-attendance`
}

/**
 * Resolves the signed-in manager's current directory or audited local direct reports in the active entity. Site-local dates determine day boundaries. Only the selected page of authorised staff is returned or exported. Missing check-in is not proof of absence; missing checkout is estimated. Directory mode uses a browser-acquired delegated Graph credential, never a backend token exchange or application token. Local mode needs no Graph credential.
 * @summary Named work plan, booking and recorded arrival comparison for direct reports
 */
export const getManagerAttendanceReport = async (params: GetManagerAttendanceReportParams, options?: Parameters<typeof customFetch>[1]): Promise<ManagerAttendanceReport | Blob> => {

  return customFetch<ManagerAttendanceReport | Blob>(getGetManagerAttendanceReportUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetManagerAttendanceReportQueryKey = (params?: GetManagerAttendanceReportParams,) => {
    return [
    `/api/reports/manager-attendance`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetManagerAttendanceReportQueryOptions = <TData = Awaited<ReturnType<typeof getManagerAttendanceReport>>, TError = Error>(params: GetManagerAttendanceReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getManagerAttendanceReport>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetManagerAttendanceReportQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getManagerAttendanceReport>>> = ({ signal }) => getManagerAttendanceReport(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getManagerAttendanceReport>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetManagerAttendanceReportQueryResult = NonNullable<Awaited<ReturnType<typeof getManagerAttendanceReport>>>
export type GetManagerAttendanceReportQueryError = Error


export function useGetManagerAttendanceReport<TData = Awaited<ReturnType<typeof getManagerAttendanceReport>>, TError = Error>(
 params: GetManagerAttendanceReportParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getManagerAttendanceReport>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getManagerAttendanceReport>>,
          TError,
          Awaited<ReturnType<typeof getManagerAttendanceReport>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetManagerAttendanceReport<TData = Awaited<ReturnType<typeof getManagerAttendanceReport>>, TError = Error>(
 params: GetManagerAttendanceReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getManagerAttendanceReport>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getManagerAttendanceReport>>,
          TError,
          Awaited<ReturnType<typeof getManagerAttendanceReport>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetManagerAttendanceReport<TData = Awaited<ReturnType<typeof getManagerAttendanceReport>>, TError = Error>(
 params: GetManagerAttendanceReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getManagerAttendanceReport>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Named work plan, booking and recorded arrival comparison for direct reports
 */

export function useGetManagerAttendanceReport<TData = Awaited<ReturnType<typeof getManagerAttendanceReport>>, TError = Error>(
 params: GetManagerAttendanceReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getManagerAttendanceReport>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetManagerAttendanceReportQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetPeakReportUrl = (params: GetPeakReportParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/reports/peaks?${stringifiedParams}` : `/api/reports/peaks`
}

/**
 * Booking activity by weekday and hour-of-day (UTC) for the selected filters. Requires reports:view.
 * @summary Peak occupancy heatmap data
 */
export const getPeakReport = async (params: GetPeakReportParams, options?: Parameters<typeof customFetch>[1]): Promise<PeakReport | Blob> => {

  return customFetch<PeakReport | Blob>(getGetPeakReportUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetPeakReportQueryKey = (params?: GetPeakReportParams,) => {
    return [
    `/api/reports/peaks`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetPeakReportQueryOptions = <TData = Awaited<ReturnType<typeof getPeakReport>>, TError = Error>(params: GetPeakReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPeakReport>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPeakReportQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPeakReport>>> = ({ signal }) => getPeakReport(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPeakReport>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetPeakReportQueryResult = NonNullable<Awaited<ReturnType<typeof getPeakReport>>>
export type GetPeakReportQueryError = Error


export function useGetPeakReport<TData = Awaited<ReturnType<typeof getPeakReport>>, TError = Error>(
 params: GetPeakReportParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPeakReport>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPeakReport>>,
          TError,
          Awaited<ReturnType<typeof getPeakReport>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetPeakReport<TData = Awaited<ReturnType<typeof getPeakReport>>, TError = Error>(
 params: GetPeakReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPeakReport>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPeakReport>>,
          TError,
          Awaited<ReturnType<typeof getPeakReport>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetPeakReport<TData = Awaited<ReturnType<typeof getPeakReport>>, TError = Error>(
 params: GetPeakReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPeakReport>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Peak occupancy heatmap data
 */

export function useGetPeakReport<TData = Awaited<ReturnType<typeof getPeakReport>>, TError = Error>(
 params: GetPeakReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPeakReport>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetPeakReportQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetUnderutilisedReportUrl = (params: GetUnderutilisedReportParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/reports/underutilised?${stringifiedParams}` : `/api/reports/underutilised`
}

/**
 * Lists active spaces whose occupancy percentage in the window is below the threshold (default 25). Includes auto-release and never-checked-in counts. Requires reports:view.
 * @summary Underutilised spaces report
 */
export const getUnderutilisedReport = async (params: GetUnderutilisedReportParams, options?: Parameters<typeof customFetch>[1]): Promise<UnderutilisedReport | Blob> => {

  return customFetch<UnderutilisedReport | Blob>(getGetUnderutilisedReportUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetUnderutilisedReportQueryKey = (params?: GetUnderutilisedReportParams,) => {
    return [
    `/api/reports/underutilised`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetUnderutilisedReportQueryOptions = <TData = Awaited<ReturnType<typeof getUnderutilisedReport>>, TError = Error>(params: GetUnderutilisedReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUnderutilisedReport>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetUnderutilisedReportQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getUnderutilisedReport>>> = ({ signal }) => getUnderutilisedReport(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getUnderutilisedReport>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetUnderutilisedReportQueryResult = NonNullable<Awaited<ReturnType<typeof getUnderutilisedReport>>>
export type GetUnderutilisedReportQueryError = Error


export function useGetUnderutilisedReport<TData = Awaited<ReturnType<typeof getUnderutilisedReport>>, TError = Error>(
 params: GetUnderutilisedReportParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUnderutilisedReport>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUnderutilisedReport>>,
          TError,
          Awaited<ReturnType<typeof getUnderutilisedReport>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetUnderutilisedReport<TData = Awaited<ReturnType<typeof getUnderutilisedReport>>, TError = Error>(
 params: GetUnderutilisedReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUnderutilisedReport>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUnderutilisedReport>>,
          TError,
          Awaited<ReturnType<typeof getUnderutilisedReport>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetUnderutilisedReport<TData = Awaited<ReturnType<typeof getUnderutilisedReport>>, TError = Error>(
 params: GetUnderutilisedReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUnderutilisedReport>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Underutilised spaces report
 */

export function useGetUnderutilisedReport<TData = Awaited<ReturnType<typeof getUnderutilisedReport>>, TError = Error>(
 params: GetUnderutilisedReportParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUnderutilisedReport>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetUnderutilisedReportQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListAuditEventsUrl = (params?: ListAuditEventsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/audit-events?${stringifiedParams}` : `/api/audit-events`
}

/**
 * @summary List lifecycle audit events (ops)
 */
export const listAuditEvents = async (params?: ListAuditEventsParams, options?: Parameters<typeof customFetch>[1]): Promise<AuditEventListResponse> => {

  return customFetch<AuditEventListResponse>(getListAuditEventsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAuditEventsQueryKey = (params?: ListAuditEventsParams,) => {
    return [
    `/api/audit-events`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAuditEventsQueryOptions = <TData = Awaited<ReturnType<typeof listAuditEvents>>, TError = Error>(params?: ListAuditEventsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuditEvents>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAuditEventsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAuditEvents>>> = ({ signal }) => listAuditEvents(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAuditEvents>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListAuditEventsQueryResult = NonNullable<Awaited<ReturnType<typeof listAuditEvents>>>
export type ListAuditEventsQueryError = Error


export function useListAuditEvents<TData = Awaited<ReturnType<typeof listAuditEvents>>, TError = Error>(
 params: undefined |  ListAuditEventsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuditEvents>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAuditEvents>>,
          TError,
          Awaited<ReturnType<typeof listAuditEvents>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListAuditEvents<TData = Awaited<ReturnType<typeof listAuditEvents>>, TError = Error>(
 params?: ListAuditEventsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuditEvents>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAuditEvents>>,
          TError,
          Awaited<ReturnType<typeof listAuditEvents>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListAuditEvents<TData = Awaited<ReturnType<typeof listAuditEvents>>, TError = Error>(
 params?: ListAuditEventsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuditEvents>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List lifecycle audit events (ops)
 */

export function useListAuditEvents<TData = Awaited<ReturnType<typeof listAuditEvents>>, TError = Error>(
 params?: ListAuditEventsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAuditEvents>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListAuditEventsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRunAutoReleaseJobUrl = () => {




  return `/api/internal/jobs/auto-release`
}

/**
 * @summary Run no-show auto-release (ops / scheduler)
 */
export const runAutoReleaseJob = async ( options?: Parameters<typeof customFetch>[1]): Promise<AutoReleaseResult> => {

  return customFetch<AutoReleaseResult>(getRunAutoReleaseJobUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getRunAutoReleaseJobMutationKey = () => ['runAutoReleaseJob'] as const;

export const getRunAutoReleaseJobMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runAutoReleaseJob>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof runAutoReleaseJob>>, TError,void, TContext> => {

const mutationKey = getRunAutoReleaseJobMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof runAutoReleaseJob>>, void> = () => {


          return  runAutoReleaseJob(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RunAutoReleaseJobMutationResult = NonNullable<Awaited<ReturnType<typeof runAutoReleaseJob>>>

    export type RunAutoReleaseJobMutationError = Error


    /**
 * @summary Run no-show auto-release (ops / scheduler)
 */
export const useRunAutoReleaseJob = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof runAutoReleaseJob>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof runAutoReleaseJob>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getRunAutoReleaseJobMutationOptions(options), queryClient);
    }

export const getCreateItineraryUrl = () => {




  return `/api/itineraries`
}

/**
 * App booking legs are created in one transaction. After commit, room calendar reconciliation may cancel individual legs that Exchange rejects; inspect each returned booking status before presenting the result. Use for multi-resource confirmations (FR-EU-011).
 * @summary Create a multi-space itinerary in one transaction
 */
export const createItinerary = async (createItineraryRequest: CreateItineraryRequest, options?: Parameters<typeof customFetch>[1]): Promise<Itinerary> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Itinerary>(getCreateItineraryUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createItineraryRequest)
  }
);}





export const getCreateItineraryMutationKey = () => ['createItinerary'] as const;

export const getCreateItineraryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createItinerary>>, TError,CreateItineraryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createItinerary>>, TError,CreateItineraryMutationVariables, TContext> => {

const mutationKey = getCreateItineraryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createItinerary>>, CreateItineraryMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createItinerary(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateItineraryMutationResult = NonNullable<Awaited<ReturnType<typeof createItinerary>>>
    export type CreateItineraryMutationBody = CreateItineraryRequest
    export type CreateItineraryMutationError = Error
    export type CreateItineraryMutationVariables = {data: CreateItineraryRequest}

    /**
 * @summary Create a multi-space itinerary in one transaction
 */
export const useCreateItinerary = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createItinerary>>, TError,CreateItineraryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createItinerary>>,
        TError,
        CreateItineraryMutationVariables,
        TContext
      > => {
      return useMutation(getCreateItineraryMutationOptions(options), queryClient);
    }

export const getListVehiclesUrl = () => {




  return `/api/vehicles`
}

/**
 * @summary List vehicles for the signed-in user
 */
export const listVehicles = async ( options?: Parameters<typeof customFetch>[1]): Promise<VehicleListResponse> => {

  return customFetch<VehicleListResponse>(getListVehiclesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListVehiclesQueryKey = () => {
    return [
    `/api/vehicles`
    ] as const;
    }


export const getListVehiclesQueryOptions = <TData = Awaited<ReturnType<typeof listVehicles>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listVehicles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListVehiclesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listVehicles>>> = ({ signal }) => listVehicles({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listVehicles>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListVehiclesQueryResult = NonNullable<Awaited<ReturnType<typeof listVehicles>>>
export type ListVehiclesQueryError = Error


export function useListVehicles<TData = Awaited<ReturnType<typeof listVehicles>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listVehicles>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listVehicles>>,
          TError,
          Awaited<ReturnType<typeof listVehicles>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListVehicles<TData = Awaited<ReturnType<typeof listVehicles>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listVehicles>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listVehicles>>,
          TError,
          Awaited<ReturnType<typeof listVehicles>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListVehicles<TData = Awaited<ReturnType<typeof listVehicles>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listVehicles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List vehicles for the signed-in user
 */

export function useListVehicles<TData = Awaited<ReturnType<typeof listVehicles>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listVehicles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListVehiclesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateVehicleUrl = () => {




  return `/api/vehicles`
}

/**
 * @summary Register a vehicle/permit for parking bookings
 */
export const createVehicle = async (createVehicleRequest: CreateVehicleRequest, options?: Parameters<typeof customFetch>[1]): Promise<Vehicle> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Vehicle>(getCreateVehicleUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createVehicleRequest)
  }
);}





export const getCreateVehicleMutationKey = () => ['createVehicle'] as const;

export const getCreateVehicleMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createVehicle>>, TError,CreateVehicleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createVehicle>>, TError,CreateVehicleMutationVariables, TContext> => {

const mutationKey = getCreateVehicleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createVehicle>>, CreateVehicleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createVehicle(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateVehicleMutationResult = NonNullable<Awaited<ReturnType<typeof createVehicle>>>
    export type CreateVehicleMutationBody = CreateVehicleRequest
    export type CreateVehicleMutationError = Error
    export type CreateVehicleMutationVariables = {data: CreateVehicleRequest}

    /**
 * @summary Register a vehicle/permit for parking bookings
 */
export const useCreateVehicle = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createVehicle>>, TError,CreateVehicleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createVehicle>>,
        TError,
        CreateVehicleMutationVariables,
        TContext
      > => {
      return useMutation(getCreateVehicleMutationOptions(options), queryClient);
    }

export const getGetMyPresencePlanUrl = (params: GetMyPresencePlanParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/presence/me?${stringifiedParams}` : `/api/presence/me`
}

/**
 * Materialises recurring weekly defaults into concrete days for the
 * requested window. Explicit day entries override defaults.
 * @summary Get the signed-in user's work plan for a date window
 */
export const getMyPresencePlan = async (params: GetMyPresencePlanParams, options?: Parameters<typeof customFetch>[1]): Promise<PresenceWeekPlan> => {

  return customFetch<PresenceWeekPlan>(getGetMyPresencePlanUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMyPresencePlanQueryKey = (params?: GetMyPresencePlanParams,) => {
    return [
    `/api/presence/me`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetMyPresencePlanQueryOptions = <TData = Awaited<ReturnType<typeof getMyPresencePlan>>, TError = Error>(params: GetMyPresencePlanParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyPresencePlan>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMyPresencePlanQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMyPresencePlan>>> = ({ signal }) => getMyPresencePlan(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMyPresencePlan>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMyPresencePlanQueryResult = NonNullable<Awaited<ReturnType<typeof getMyPresencePlan>>>
export type GetMyPresencePlanQueryError = Error


export function useGetMyPresencePlan<TData = Awaited<ReturnType<typeof getMyPresencePlan>>, TError = Error>(
 params: GetMyPresencePlanParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyPresencePlan>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyPresencePlan>>,
          TError,
          Awaited<ReturnType<typeof getMyPresencePlan>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyPresencePlan<TData = Awaited<ReturnType<typeof getMyPresencePlan>>, TError = Error>(
 params: GetMyPresencePlanParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyPresencePlan>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyPresencePlan>>,
          TError,
          Awaited<ReturnType<typeof getMyPresencePlan>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyPresencePlan<TData = Awaited<ReturnType<typeof getMyPresencePlan>>, TError = Error>(
 params: GetMyPresencePlanParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyPresencePlan>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get the signed-in user's work plan for a date window
 */

export function useGetMyPresencePlan<TData = Awaited<ReturnType<typeof getMyPresencePlan>>, TError = Error>(
 params: GetMyPresencePlanParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyPresencePlan>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMyPresencePlanQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpsertMyPresencePlanUrl = () => {




  return `/api/presence/me`
}

/**
 * @summary Upsert explicit presence days for the signed-in user
 */
export const upsertMyPresencePlan = async (upsertPresencePlanRequest: UpsertPresencePlanRequest, options?: Parameters<typeof customFetch>[1]): Promise<UpsertPresencePlanResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<UpsertPresencePlanResponse>(getUpsertMyPresencePlanUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(upsertPresencePlanRequest)
  }
);}





export const getUpsertMyPresencePlanMutationKey = () => ['upsertMyPresencePlan'] as const;

export const getUpsertMyPresencePlanMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertMyPresencePlan>>, TError,UpsertMyPresencePlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertMyPresencePlan>>, TError,UpsertMyPresencePlanMutationVariables, TContext> => {

const mutationKey = getUpsertMyPresencePlanMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertMyPresencePlan>>, UpsertMyPresencePlanMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  upsertMyPresencePlan(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertMyPresencePlanMutationResult = NonNullable<Awaited<ReturnType<typeof upsertMyPresencePlan>>>
    export type UpsertMyPresencePlanMutationBody = UpsertPresencePlanRequest
    export type UpsertMyPresencePlanMutationError = Error
    export type UpsertMyPresencePlanMutationVariables = {data: UpsertPresencePlanRequest}

    /**
 * @summary Upsert explicit presence days for the signed-in user
 */
export const useUpsertMyPresencePlan = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertMyPresencePlan>>, TError,UpsertMyPresencePlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof upsertMyPresencePlan>>,
        TError,
        UpsertMyPresencePlanMutationVariables,
        TContext
      > => {
      return useMutation(getUpsertMyPresencePlanMutationOptions(options), queryClient);
    }

export const getSetMyPresenceDefaultsUrl = () => {




  return `/api/presence/me/defaults`
}

/**
 * @summary Replace the signed-in user's recurring weekly presence pattern
 */
export const setMyPresenceDefaults = async (setPresenceDefaultsRequest: SetPresenceDefaultsRequest, options?: Parameters<typeof customFetch>[1]): Promise<SetPresenceDefaultsResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<SetPresenceDefaultsResponse>(getSetMyPresenceDefaultsUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(setPresenceDefaultsRequest)
  }
);}





export const getSetMyPresenceDefaultsMutationKey = () => ['setMyPresenceDefaults'] as const;

export const getSetMyPresenceDefaultsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setMyPresenceDefaults>>, TError,SetMyPresenceDefaultsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setMyPresenceDefaults>>, TError,SetMyPresenceDefaultsMutationVariables, TContext> => {

const mutationKey = getSetMyPresenceDefaultsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setMyPresenceDefaults>>, SetMyPresenceDefaultsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  setMyPresenceDefaults(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetMyPresenceDefaultsMutationResult = NonNullable<Awaited<ReturnType<typeof setMyPresenceDefaults>>>
    export type SetMyPresenceDefaultsMutationBody = SetPresenceDefaultsRequest
    export type SetMyPresenceDefaultsMutationError = Error
    export type SetMyPresenceDefaultsMutationVariables = {data: SetPresenceDefaultsRequest}

    /**
 * @summary Replace the signed-in user's recurring weekly presence pattern
 */
export const useSetMyPresenceDefaults = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setMyPresenceDefaults>>, TError,SetMyPresenceDefaultsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setMyPresenceDefaults>>,
        TError,
        SetMyPresenceDefaultsMutationVariables,
        TContext
      > => {
      return useMutation(getSetMyPresenceDefaultsMutationOptions(options), queryClient);
    }

export const getGetMyPresenceSettingsUrl = () => {




  return `/api/presence/me/settings`
}

/**
 * @summary Get the signed-in user's presence privacy settings
 */
export const getMyPresenceSettings = async ( options?: Parameters<typeof customFetch>[1]): Promise<PresenceSettings> => {

  return customFetch<PresenceSettings>(getGetMyPresenceSettingsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMyPresenceSettingsQueryKey = () => {
    return [
    `/api/presence/me/settings`
    ] as const;
    }


export const getGetMyPresenceSettingsQueryOptions = <TData = Awaited<ReturnType<typeof getMyPresenceSettings>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyPresenceSettings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMyPresenceSettingsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMyPresenceSettings>>> = ({ signal }) => getMyPresenceSettings({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMyPresenceSettings>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMyPresenceSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof getMyPresenceSettings>>>
export type GetMyPresenceSettingsQueryError = Error


export function useGetMyPresenceSettings<TData = Awaited<ReturnType<typeof getMyPresenceSettings>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyPresenceSettings>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyPresenceSettings>>,
          TError,
          Awaited<ReturnType<typeof getMyPresenceSettings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyPresenceSettings<TData = Awaited<ReturnType<typeof getMyPresenceSettings>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyPresenceSettings>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyPresenceSettings>>,
          TError,
          Awaited<ReturnType<typeof getMyPresenceSettings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyPresenceSettings<TData = Awaited<ReturnType<typeof getMyPresenceSettings>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyPresenceSettings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get the signed-in user's presence privacy settings
 */

export function useGetMyPresenceSettings<TData = Awaited<ReturnType<typeof getMyPresenceSettings>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyPresenceSettings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMyPresenceSettingsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSetMyPresenceSettingsUrl = () => {




  return `/api/presence/me/settings`
}

/**
 * @summary Update the signed-in user's presence privacy settings
 */
export const setMyPresenceSettings = async (setPresenceSettingsRequest: SetPresenceSettingsRequest, options?: Parameters<typeof customFetch>[1]): Promise<PresenceSettings> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PresenceSettings>(getSetMyPresenceSettingsUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(setPresenceSettingsRequest)
  }
);}





export const getSetMyPresenceSettingsMutationKey = () => ['setMyPresenceSettings'] as const;

export const getSetMyPresenceSettingsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setMyPresenceSettings>>, TError,SetMyPresenceSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setMyPresenceSettings>>, TError,SetMyPresenceSettingsMutationVariables, TContext> => {

const mutationKey = getSetMyPresenceSettingsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setMyPresenceSettings>>, SetMyPresenceSettingsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  setMyPresenceSettings(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetMyPresenceSettingsMutationResult = NonNullable<Awaited<ReturnType<typeof setMyPresenceSettings>>>
    export type SetMyPresenceSettingsMutationBody = SetPresenceSettingsRequest
    export type SetMyPresenceSettingsMutationError = Error
    export type SetMyPresenceSettingsMutationVariables = {data: SetPresenceSettingsRequest}

    /**
 * @summary Update the signed-in user's presence privacy settings
 */
export const useSetMyPresenceSettings = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setMyPresenceSettings>>, TError,SetMyPresenceSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setMyPresenceSettings>>,
        TError,
        SetMyPresenceSettingsMutationVariables,
        TContext
      > => {
      return useMutation(getSetMyPresenceSettingsMutationOptions(options), queryClient);
    }

export const getListMyTimedPlansUrl = (params: ListMyTimedPlansParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/me/timed-plans?${stringifiedParams}` : `/api/me/timed-plans`
}

/**
 * @summary List my private timed plans in a bounded date window
 */
export const listMyTimedPlans = async (params: ListMyTimedPlansParams, options?: Parameters<typeof customFetch>[1]): Promise<TimedPlanList> => {

  return customFetch<TimedPlanList>(getListMyTimedPlansUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMyTimedPlansQueryKey = (params?: ListMyTimedPlansParams,) => {
    return [
    `/api/me/timed-plans`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMyTimedPlansQueryOptions = <TData = Awaited<ReturnType<typeof listMyTimedPlans>>, TError = Error>(params: ListMyTimedPlansParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyTimedPlans>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMyTimedPlansQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyTimedPlans>>> = ({ signal }) => listMyTimedPlans(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMyTimedPlans>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMyTimedPlansQueryResult = NonNullable<Awaited<ReturnType<typeof listMyTimedPlans>>>
export type ListMyTimedPlansQueryError = Error


export function useListMyTimedPlans<TData = Awaited<ReturnType<typeof listMyTimedPlans>>, TError = Error>(
 params: ListMyTimedPlansParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyTimedPlans>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyTimedPlans>>,
          TError,
          Awaited<ReturnType<typeof listMyTimedPlans>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyTimedPlans<TData = Awaited<ReturnType<typeof listMyTimedPlans>>, TError = Error>(
 params: ListMyTimedPlansParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyTimedPlans>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyTimedPlans>>,
          TError,
          Awaited<ReturnType<typeof listMyTimedPlans>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyTimedPlans<TData = Awaited<ReturnType<typeof listMyTimedPlans>>, TError = Error>(
 params: ListMyTimedPlansParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyTimedPlans>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List my private timed plans in a bounded date window
 */

export function useListMyTimedPlans<TData = Awaited<ReturnType<typeof listMyTimedPlans>>, TError = Error>(
 params: ListMyTimedPlansParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyTimedPlans>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMyTimedPlansQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetMyTimedPlanUrl = (date: string,) => {




  return `/api/me/timed-plans/${date}`
}

/**
 * @summary Read my private timed plan for a local date
 */
export const getMyTimedPlan = async (date: string, options?: Parameters<typeof customFetch>[1]): Promise<TimedPlan> => {

  return customFetch<TimedPlan>(getGetMyTimedPlanUrl(date),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMyTimedPlanQueryKey = (date: string,) => {
    return [
    `/api/me/timed-plans/${date}`
    ] as const;
    }


export const getGetMyTimedPlanQueryOptions = <TData = Awaited<ReturnType<typeof getMyTimedPlan>>, TError = Error>(date: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyTimedPlan>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMyTimedPlanQueryKey(date);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMyTimedPlan>>> = ({ signal }) => getMyTimedPlan(date, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: date !== null && date !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMyTimedPlan>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMyTimedPlanQueryResult = NonNullable<Awaited<ReturnType<typeof getMyTimedPlan>>>
export type GetMyTimedPlanQueryError = Error


export function useGetMyTimedPlan<TData = Awaited<ReturnType<typeof getMyTimedPlan>>, TError = Error>(
 date: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyTimedPlan>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyTimedPlan>>,
          TError,
          Awaited<ReturnType<typeof getMyTimedPlan>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyTimedPlan<TData = Awaited<ReturnType<typeof getMyTimedPlan>>, TError = Error>(
 date: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyTimedPlan>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyTimedPlan>>,
          TError,
          Awaited<ReturnType<typeof getMyTimedPlan>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyTimedPlan<TData = Awaited<ReturnType<typeof getMyTimedPlan>>, TError = Error>(
 date: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyTimedPlan>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read my private timed plan for a local date
 */

export function useGetMyTimedPlan<TData = Awaited<ReturnType<typeof getMyTimedPlan>>, TError = Error>(
 date: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyTimedPlan>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMyTimedPlanQueryOptions(date,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSaveMyTimedPlanUrl = (date: string,) => {




  return `/api/me/timed-plans/${date}`
}

/**
 * expectedRevision is 0 on creation. This foundation save records intent only; booking reconciliation is added separately.
 * @summary Save timed stops with optimistic revision and explicit personal booking links
 */
export const saveMyTimedPlan = async (date: string,
    saveTimedPlanRequest: SaveTimedPlanRequest, options?: Parameters<typeof customFetch>[1]): Promise<TimedPlan> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TimedPlan>(getSaveMyTimedPlanUrl(date),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(saveTimedPlanRequest)
  }
);}





export const getSaveMyTimedPlanMutationKey = () => ['saveMyTimedPlan'] as const;

export const getSaveMyTimedPlanMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveMyTimedPlan>>, TError,SaveMyTimedPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof saveMyTimedPlan>>, TError,SaveMyTimedPlanMutationVariables, TContext> => {

const mutationKey = getSaveMyTimedPlanMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof saveMyTimedPlan>>, SaveMyTimedPlanMutationVariables> = (props) => {
          const {date,data} = props ?? {};

          return  saveMyTimedPlan(date,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveMyTimedPlanMutationResult = NonNullable<Awaited<ReturnType<typeof saveMyTimedPlan>>>
    export type SaveMyTimedPlanMutationBody = SaveTimedPlanRequest
    export type SaveMyTimedPlanMutationError = Error
    export type SaveMyTimedPlanMutationVariables = {date: string;data: SaveTimedPlanRequest}

    /**
 * @summary Save timed stops with optimistic revision and explicit personal booking links
 */
export const useSaveMyTimedPlan = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveMyTimedPlan>>, TError,SaveMyTimedPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof saveMyTimedPlan>>,
        TError,
        SaveMyTimedPlanMutationVariables,
        TContext
      > => {
      return useMutation(getSaveMyTimedPlanMutationOptions(options), queryClient);
    }

export const getGetMyLocationContextUrl = () => {




  return `/api/me/location-context`
}

/**
 * @summary Read only my most recent personal location context
 */
export const getMyLocationContext = async ( options?: Parameters<typeof customFetch>[1]): Promise<LocationContextResponse> => {

  return customFetch<LocationContextResponse>(getGetMyLocationContextUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMyLocationContextQueryKey = () => {
    return [
    `/api/me/location-context`
    ] as const;
    }


export const getGetMyLocationContextQueryOptions = <TData = Awaited<ReturnType<typeof getMyLocationContext>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyLocationContext>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMyLocationContextQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMyLocationContext>>> = ({ signal }) => getMyLocationContext({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMyLocationContext>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMyLocationContextQueryResult = NonNullable<Awaited<ReturnType<typeof getMyLocationContext>>>
export type GetMyLocationContextQueryError = unknown


export function useGetMyLocationContext<TData = Awaited<ReturnType<typeof getMyLocationContext>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyLocationContext>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyLocationContext>>,
          TError,
          Awaited<ReturnType<typeof getMyLocationContext>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyLocationContext<TData = Awaited<ReturnType<typeof getMyLocationContext>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyLocationContext>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyLocationContext>>,
          TError,
          Awaited<ReturnType<typeof getMyLocationContext>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyLocationContext<TData = Awaited<ReturnType<typeof getMyLocationContext>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyLocationContext>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read only my most recent personal location context
 */

export function useGetMyLocationContext<TData = Awaited<ReturnType<typeof getMyLocationContext>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyLocationContext>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMyLocationContextQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPutMyLocationContextUrl = () => {




  return `/api/me/location-context`
}

/**
 * Overwrites the prior context; never changes a plan, booking or shared presence.
 * @summary Set a manually chosen or explicitly requested device location
 */
export const putMyLocationContext = async (saveLocationContextRequest: SaveLocationContextRequest, options?: Parameters<typeof customFetch>[1]): Promise<LocationContextResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<LocationContextResponse>(getPutMyLocationContextUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(saveLocationContextRequest)
  }
);}





export const getPutMyLocationContextMutationKey = () => ['putMyLocationContext'] as const;

export const getPutMyLocationContextMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putMyLocationContext>>, TError,PutMyLocationContextMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof putMyLocationContext>>, TError,PutMyLocationContextMutationVariables, TContext> => {

const mutationKey = getPutMyLocationContextMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof putMyLocationContext>>, PutMyLocationContextMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  putMyLocationContext(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PutMyLocationContextMutationResult = NonNullable<Awaited<ReturnType<typeof putMyLocationContext>>>
    export type PutMyLocationContextMutationBody = SaveLocationContextRequest
    export type PutMyLocationContextMutationError = Error
    export type PutMyLocationContextMutationVariables = {data: SaveLocationContextRequest}

    /**
 * @summary Set a manually chosen or explicitly requested device location
 */
export const usePutMyLocationContext = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putMyLocationContext>>, TError,PutMyLocationContextMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof putMyLocationContext>>,
        TError,
        PutMyLocationContextMutationVariables,
        TContext
      > => {
      return useMutation(getPutMyLocationContextMutationOptions(options), queryClient);
    }

export const getForgetMyLocationContextUrl = () => {




  return `/api/me/location-context`
}

/**
 * @summary Forget my most recent location
 */
export const forgetMyLocationContext = async ( options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getForgetMyLocationContextUrl(),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getForgetMyLocationContextMutationKey = () => ['forgetMyLocationContext'] as const;

export const getForgetMyLocationContextMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof forgetMyLocationContext>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof forgetMyLocationContext>>, TError,void, TContext> => {

const mutationKey = getForgetMyLocationContextMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof forgetMyLocationContext>>, void> = () => {


          return  forgetMyLocationContext(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ForgetMyLocationContextMutationResult = NonNullable<Awaited<ReturnType<typeof forgetMyLocationContext>>>

    export type ForgetMyLocationContextMutationError = unknown


    /**
 * @summary Forget my most recent location
 */
export const useForgetMyLocationContext = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof forgetMyLocationContext>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof forgetMyLocationContext>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getForgetMyLocationContextMutationOptions(options), queryClient);
    }

export const getListMyLocationNeedsUrl = (params?: ListMyLocationNeedsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/me/location-needs?${stringifiedParams}` : `/api/me/location-needs`
}

/**
 * @summary List my approved, pending and dismissed location needs
 */
export const listMyLocationNeeds = async (params?: ListMyLocationNeedsParams, options?: Parameters<typeof customFetch>[1]): Promise<LocationNeedList> => {

  return customFetch<LocationNeedList>(getListMyLocationNeedsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMyLocationNeedsQueryKey = (params?: ListMyLocationNeedsParams,) => {
    return [
    `/api/me/location-needs`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMyLocationNeedsQueryOptions = <TData = Awaited<ReturnType<typeof listMyLocationNeeds>>, TError = unknown>(params?: ListMyLocationNeedsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyLocationNeeds>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMyLocationNeedsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyLocationNeeds>>> = ({ signal }) => listMyLocationNeeds(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMyLocationNeeds>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMyLocationNeedsQueryResult = NonNullable<Awaited<ReturnType<typeof listMyLocationNeeds>>>
export type ListMyLocationNeedsQueryError = unknown


export function useListMyLocationNeeds<TData = Awaited<ReturnType<typeof listMyLocationNeeds>>, TError = unknown>(
 params: undefined |  ListMyLocationNeedsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyLocationNeeds>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyLocationNeeds>>,
          TError,
          Awaited<ReturnType<typeof listMyLocationNeeds>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyLocationNeeds<TData = Awaited<ReturnType<typeof listMyLocationNeeds>>, TError = unknown>(
 params?: ListMyLocationNeedsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyLocationNeeds>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyLocationNeeds>>,
          TError,
          Awaited<ReturnType<typeof listMyLocationNeeds>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyLocationNeeds<TData = Awaited<ReturnType<typeof listMyLocationNeeds>>, TError = unknown>(
 params?: ListMyLocationNeedsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyLocationNeeds>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List my approved, pending and dismissed location needs
 */

export function useListMyLocationNeeds<TData = Awaited<ReturnType<typeof listMyLocationNeeds>>, TError = unknown>(
 params?: ListMyLocationNeedsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyLocationNeeds>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMyLocationNeedsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateMyLocationNeedUrl = () => {




  return `/api/me/location-needs`
}

/**
 * @summary Record a manually approved need for one Commons site
 */
export const createMyLocationNeed = async (createLocationNeedRequest: CreateLocationNeedRequest, options?: Parameters<typeof customFetch>[1]): Promise<LocationNeed> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<LocationNeed>(getCreateMyLocationNeedUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createLocationNeedRequest)
  }
);}





export const getCreateMyLocationNeedMutationKey = () => ['createMyLocationNeed'] as const;

export const getCreateMyLocationNeedMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createMyLocationNeed>>, TError,CreateMyLocationNeedMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createMyLocationNeed>>, TError,CreateMyLocationNeedMutationVariables, TContext> => {

const mutationKey = getCreateMyLocationNeedMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createMyLocationNeed>>, CreateMyLocationNeedMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createMyLocationNeed(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateMyLocationNeedMutationResult = NonNullable<Awaited<ReturnType<typeof createMyLocationNeed>>>
    export type CreateMyLocationNeedMutationBody = CreateLocationNeedRequest
    export type CreateMyLocationNeedMutationError = Error
    export type CreateMyLocationNeedMutationVariables = {data: CreateLocationNeedRequest}

    /**
 * @summary Record a manually approved need for one Commons site
 */
export const useCreateMyLocationNeed = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createMyLocationNeed>>, TError,CreateMyLocationNeedMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createMyLocationNeed>>,
        TError,
        CreateMyLocationNeedMutationVariables,
        TContext
      > => {
      return useMutation(getCreateMyLocationNeedMutationOptions(options), queryClient);
    }

export const getSuggestMyLocationNeedsUrl = () => {




  return `/api/me/location-needs/suggestions`
}

/**
 * @summary Propose private location preferences from my booking history
 */
export const suggestMyLocationNeeds = async (suggestLocationNeedsRequest: SuggestLocationNeedsRequest, options?: Parameters<typeof customFetch>[1]): Promise<LocationNeedList> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<LocationNeedList>(getSuggestMyLocationNeedsUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(suggestLocationNeedsRequest)
  }
);}





export const getSuggestMyLocationNeedsMutationKey = () => ['suggestMyLocationNeeds'] as const;

export const getSuggestMyLocationNeedsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof suggestMyLocationNeeds>>, TError,SuggestMyLocationNeedsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof suggestMyLocationNeeds>>, TError,SuggestMyLocationNeedsMutationVariables, TContext> => {

const mutationKey = getSuggestMyLocationNeedsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof suggestMyLocationNeeds>>, SuggestMyLocationNeedsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  suggestMyLocationNeeds(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SuggestMyLocationNeedsMutationResult = NonNullable<Awaited<ReturnType<typeof suggestMyLocationNeeds>>>
    export type SuggestMyLocationNeedsMutationBody = SuggestLocationNeedsRequest
    export type SuggestMyLocationNeedsMutationError = unknown
    export type SuggestMyLocationNeedsMutationVariables = {data: SuggestLocationNeedsRequest}

    /**
 * @summary Propose private location preferences from my booking history
 */
export const useSuggestMyLocationNeeds = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof suggestMyLocationNeeds>>, TError,SuggestMyLocationNeedsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof suggestMyLocationNeeds>>,
        TError,
        SuggestMyLocationNeedsMutationVariables,
        TContext
      > => {
      return useMutation(getSuggestMyLocationNeedsMutationOptions(options), queryClient);
    }

export const getDecideMyLocationNeedsUrl = () => {




  return `/api/me/location-needs/decisions`
}

/**
 * @summary Approve or dismiss several private suggestions together
 */
export const decideMyLocationNeeds = async (decideLocationNeedsRequest: DecideLocationNeedsRequest, options?: Parameters<typeof customFetch>[1]): Promise<LocationNeedList> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<LocationNeedList>(getDecideMyLocationNeedsUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(decideLocationNeedsRequest)
  }
);}





export const getDecideMyLocationNeedsMutationKey = () => ['decideMyLocationNeeds'] as const;

export const getDecideMyLocationNeedsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideMyLocationNeeds>>, TError,DecideMyLocationNeedsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof decideMyLocationNeeds>>, TError,DecideMyLocationNeedsMutationVariables, TContext> => {

const mutationKey = getDecideMyLocationNeedsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof decideMyLocationNeeds>>, DecideMyLocationNeedsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  decideMyLocationNeeds(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DecideMyLocationNeedsMutationResult = NonNullable<Awaited<ReturnType<typeof decideMyLocationNeeds>>>
    export type DecideMyLocationNeedsMutationBody = DecideLocationNeedsRequest
    export type DecideMyLocationNeedsMutationError = unknown
    export type DecideMyLocationNeedsMutationVariables = {data: DecideLocationNeedsRequest}

    /**
 * @summary Approve or dismiss several private suggestions together
 */
export const useDecideMyLocationNeeds = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideMyLocationNeeds>>, TError,DecideMyLocationNeedsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof decideMyLocationNeeds>>,
        TError,
        DecideMyLocationNeedsMutationVariables,
        TContext
      > => {
      return useMutation(getDecideMyLocationNeedsMutationOptions(options), queryClient);
    }

export const getGetMyStopRecommendationsUrl = (date: string,
    stopId: string,) => {




  return `/api/me/timed-plans/${date}/stops/${stopId}/recommendations`
}

/**
 * @summary Currently eligible resources for my saved stop, ranked by approved preferences
 */
export const getMyStopRecommendations = async (date: string,
    stopId: string, options?: Parameters<typeof customFetch>[1]): Promise<LocationRecommendationList> => {

  return customFetch<LocationRecommendationList>(getGetMyStopRecommendationsUrl(date,stopId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMyStopRecommendationsQueryKey = (date: string,
    stopId: string,) => {
    return [
    `/api/me/timed-plans/${date}/stops/${stopId}/recommendations`
    ] as const;
    }


export const getGetMyStopRecommendationsQueryOptions = <TData = Awaited<ReturnType<typeof getMyStopRecommendations>>, TError = unknown>(date: string,
    stopId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyStopRecommendations>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMyStopRecommendationsQueryKey(date,stopId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMyStopRecommendations>>> = ({ signal }) => getMyStopRecommendations(date,stopId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: date !== null && date !== undefined && stopId !== null && stopId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMyStopRecommendations>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMyStopRecommendationsQueryResult = NonNullable<Awaited<ReturnType<typeof getMyStopRecommendations>>>
export type GetMyStopRecommendationsQueryError = unknown


export function useGetMyStopRecommendations<TData = Awaited<ReturnType<typeof getMyStopRecommendations>>, TError = unknown>(
 date: string,
    stopId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyStopRecommendations>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyStopRecommendations>>,
          TError,
          Awaited<ReturnType<typeof getMyStopRecommendations>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyStopRecommendations<TData = Awaited<ReturnType<typeof getMyStopRecommendations>>, TError = unknown>(
 date: string,
    stopId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyStopRecommendations>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyStopRecommendations>>,
          TError,
          Awaited<ReturnType<typeof getMyStopRecommendations>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyStopRecommendations<TData = Awaited<ReturnType<typeof getMyStopRecommendations>>, TError = unknown>(
 date: string,
    stopId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyStopRecommendations>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Currently eligible resources for my saved stop, ranked by approved preferences
 */

export function useGetMyStopRecommendations<TData = Awaited<ReturnType<typeof getMyStopRecommendations>>, TError = unknown>(
 date: string,
    stopId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyStopRecommendations>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMyStopRecommendationsQueryOptions(date,stopId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateMyLocationNeedUrl = (id: string,) => {




  return `/api/me/location-needs/${id}`
}

/**
 * @summary Approve, edit or dismiss my own location need
 */
export const updateMyLocationNeed = async (id: string,
    updateLocationNeedRequest: UpdateLocationNeedRequest, options?: Parameters<typeof customFetch>[1]): Promise<LocationNeed> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<LocationNeed>(getUpdateMyLocationNeedUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateLocationNeedRequest)
  }
);}





export const getUpdateMyLocationNeedMutationKey = () => ['updateMyLocationNeed'] as const;

export const getUpdateMyLocationNeedMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateMyLocationNeed>>, TError,UpdateMyLocationNeedMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateMyLocationNeed>>, TError,UpdateMyLocationNeedMutationVariables, TContext> => {

const mutationKey = getUpdateMyLocationNeedMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateMyLocationNeed>>, UpdateMyLocationNeedMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateMyLocationNeed(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateMyLocationNeedMutationResult = NonNullable<Awaited<ReturnType<typeof updateMyLocationNeed>>>
    export type UpdateMyLocationNeedMutationBody = UpdateLocationNeedRequest
    export type UpdateMyLocationNeedMutationError = Error
    export type UpdateMyLocationNeedMutationVariables = {id: string;data: UpdateLocationNeedRequest}

    /**
 * @summary Approve, edit or dismiss my own location need
 */
export const useUpdateMyLocationNeed = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateMyLocationNeed>>, TError,UpdateMyLocationNeedMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateMyLocationNeed>>,
        TError,
        UpdateMyLocationNeedMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateMyLocationNeedMutationOptions(options), queryClient);
    }

export const getForgetMyLocationNeedUrl = (id: string,) => {




  return `/api/me/location-needs/${id}`
}

/**
 * @summary Forget my own location need
 */
export const forgetMyLocationNeed = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getForgetMyLocationNeedUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getForgetMyLocationNeedMutationKey = () => ['forgetMyLocationNeed'] as const;

export const getForgetMyLocationNeedMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof forgetMyLocationNeed>>, TError,ForgetMyLocationNeedMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof forgetMyLocationNeed>>, TError,ForgetMyLocationNeedMutationVariables, TContext> => {

const mutationKey = getForgetMyLocationNeedMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof forgetMyLocationNeed>>, ForgetMyLocationNeedMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  forgetMyLocationNeed(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ForgetMyLocationNeedMutationResult = NonNullable<Awaited<ReturnType<typeof forgetMyLocationNeed>>>

    export type ForgetMyLocationNeedMutationError = unknown
    export type ForgetMyLocationNeedMutationVariables = {id: string}

    /**
 * @summary Forget my own location need
 */
export const useForgetMyLocationNeed = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof forgetMyLocationNeed>>, TError,ForgetMyLocationNeedMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof forgetMyLocationNeed>>,
        TError,
        ForgetMyLocationNeedMutationVariables,
        TContext
      > => {
      return useMutation(getForgetMyLocationNeedMutationOptions(options), queryClient);
    }

export const getGetMySearchPrefsUrl = () => {




  return `/api/me/search-prefs`
}

/**
 * Returns the last saved search preferences for the caller (site, building, space type, time-of-day window). Empty/null fields when the user has no saved context yet.
 * @summary Get the signed-in user's remembered booking search context
 */
export const getMySearchPrefs = async ( options?: Parameters<typeof customFetch>[1]): Promise<UserSearchPrefs> => {

  return customFetch<UserSearchPrefs>(getGetMySearchPrefsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMySearchPrefsQueryKey = () => {
    return [
    `/api/me/search-prefs`
    ] as const;
    }


export const getGetMySearchPrefsQueryOptions = <TData = Awaited<ReturnType<typeof getMySearchPrefs>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMySearchPrefs>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMySearchPrefsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMySearchPrefs>>> = ({ signal }) => getMySearchPrefs({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMySearchPrefs>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMySearchPrefsQueryResult = NonNullable<Awaited<ReturnType<typeof getMySearchPrefs>>>
export type GetMySearchPrefsQueryError = Error


export function useGetMySearchPrefs<TData = Awaited<ReturnType<typeof getMySearchPrefs>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMySearchPrefs>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMySearchPrefs>>,
          TError,
          Awaited<ReturnType<typeof getMySearchPrefs>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMySearchPrefs<TData = Awaited<ReturnType<typeof getMySearchPrefs>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMySearchPrefs>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMySearchPrefs>>,
          TError,
          Awaited<ReturnType<typeof getMySearchPrefs>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMySearchPrefs<TData = Awaited<ReturnType<typeof getMySearchPrefs>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMySearchPrefs>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get the signed-in user's remembered booking search context
 */

export function useGetMySearchPrefs<TData = Awaited<ReturnType<typeof getMySearchPrefs>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMySearchPrefs>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMySearchPrefsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPutMySearchPrefsUrl = () => {




  return `/api/me/search-prefs`
}

/**
 * Persists the last used booking search filters and time-of-day window for the caller. Scoped to the authenticated app user; never readable by other users.
 * @summary Upsert the signed-in user's remembered booking search context
 */
export const putMySearchPrefs = async (updateUserSearchPrefs: UpdateUserSearchPrefs, options?: Parameters<typeof customFetch>[1]): Promise<UserSearchPrefs> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<UserSearchPrefs>(getPutMySearchPrefsUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateUserSearchPrefs)
  }
);}





export const getPutMySearchPrefsMutationKey = () => ['putMySearchPrefs'] as const;

export const getPutMySearchPrefsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putMySearchPrefs>>, TError,PutMySearchPrefsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof putMySearchPrefs>>, TError,PutMySearchPrefsMutationVariables, TContext> => {

const mutationKey = getPutMySearchPrefsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof putMySearchPrefs>>, PutMySearchPrefsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  putMySearchPrefs(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PutMySearchPrefsMutationResult = NonNullable<Awaited<ReturnType<typeof putMySearchPrefs>>>
    export type PutMySearchPrefsMutationBody = UpdateUserSearchPrefs
    export type PutMySearchPrefsMutationError = Error
    export type PutMySearchPrefsMutationVariables = {data: UpdateUserSearchPrefs}

    /**
 * @summary Upsert the signed-in user's remembered booking search context
 */
export const usePutMySearchPrefs = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putMySearchPrefs>>, TError,PutMySearchPrefsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof putMySearchPrefs>>,
        TError,
        PutMySearchPrefsMutationVariables,
        TContext
      > => {
      return useMutation(getPutMySearchPrefsMutationOptions(options), queryClient);
    }

export const getGetMyWorkplaceProfileUrl = () => {




  return `/api/me/workplace-profile`
}

/**
 * @summary Get the signed-in user's entity-scoped office and neighbourhood preferences
 */
export const getMyWorkplaceProfile = async ( options?: Parameters<typeof customFetch>[1]): Promise<WorkplaceProfile> => {

  return customFetch<WorkplaceProfile>(getGetMyWorkplaceProfileUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMyWorkplaceProfileQueryKey = () => {
    return [
    `/api/me/workplace-profile`
    ] as const;
    }


export const getGetMyWorkplaceProfileQueryOptions = <TData = Awaited<ReturnType<typeof getMyWorkplaceProfile>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyWorkplaceProfile>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMyWorkplaceProfileQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMyWorkplaceProfile>>> = ({ signal }) => getMyWorkplaceProfile({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMyWorkplaceProfile>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMyWorkplaceProfileQueryResult = NonNullable<Awaited<ReturnType<typeof getMyWorkplaceProfile>>>
export type GetMyWorkplaceProfileQueryError = Error


export function useGetMyWorkplaceProfile<TData = Awaited<ReturnType<typeof getMyWorkplaceProfile>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyWorkplaceProfile>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyWorkplaceProfile>>,
          TError,
          Awaited<ReturnType<typeof getMyWorkplaceProfile>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyWorkplaceProfile<TData = Awaited<ReturnType<typeof getMyWorkplaceProfile>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyWorkplaceProfile>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyWorkplaceProfile>>,
          TError,
          Awaited<ReturnType<typeof getMyWorkplaceProfile>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyWorkplaceProfile<TData = Awaited<ReturnType<typeof getMyWorkplaceProfile>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyWorkplaceProfile>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get the signed-in user's entity-scoped office and neighbourhood preferences
 */

export function useGetMyWorkplaceProfile<TData = Awaited<ReturnType<typeof getMyWorkplaceProfile>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyWorkplaceProfile>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMyWorkplaceProfileQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPutMyWorkplaceProfileUrl = () => {




  return `/api/me/workplace-profile`
}

/**
 * @summary Save the signed-in user's default office site
 */
export const putMyWorkplaceProfile = async (putMyWorkplaceProfileBody: PutMyWorkplaceProfileBody, options?: Parameters<typeof customFetch>[1]): Promise<WorkplaceProfile> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WorkplaceProfile>(getPutMyWorkplaceProfileUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(putMyWorkplaceProfileBody)
  }
);}





export const getPutMyWorkplaceProfileMutationKey = () => ['putMyWorkplaceProfile'] as const;

export const getPutMyWorkplaceProfileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putMyWorkplaceProfile>>, TError,PutMyWorkplaceProfileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof putMyWorkplaceProfile>>, TError,PutMyWorkplaceProfileMutationVariables, TContext> => {

const mutationKey = getPutMyWorkplaceProfileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof putMyWorkplaceProfile>>, PutMyWorkplaceProfileMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  putMyWorkplaceProfile(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PutMyWorkplaceProfileMutationResult = NonNullable<Awaited<ReturnType<typeof putMyWorkplaceProfile>>>
    export type PutMyWorkplaceProfileMutationBody = PutMyWorkplaceProfileBody
    export type PutMyWorkplaceProfileMutationError = Error
    export type PutMyWorkplaceProfileMutationVariables = {data: PutMyWorkplaceProfileBody}

    /**
 * @summary Save the signed-in user's default office site
 */
export const usePutMyWorkplaceProfile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putMyWorkplaceProfile>>, TError,PutMyWorkplaceProfileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof putMyWorkplaceProfile>>,
        TError,
        PutMyWorkplaceProfileMutationVariables,
        TContext
      > => {
      return useMutation(getPutMyWorkplaceProfileMutationOptions(options), queryClient);
    }

export const getSearchWorkplaceZonesUrl = (params?: SearchWorkplaceZonesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/workplace-zones?${stringifiedParams}` : `/api/admin/workplace-zones`
}

/**
 * @summary Search active neighbourhoods in the selected entity for assignment
 */
export const searchWorkplaceZones = async (params?: SearchWorkplaceZonesParams, options?: Parameters<typeof customFetch>[1]): Promise<SearchWorkplaceZones200> => {

  return customFetch<SearchWorkplaceZones200>(getSearchWorkplaceZonesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getSearchWorkplaceZonesQueryKey = (params?: SearchWorkplaceZonesParams,) => {
    return [
    `/api/admin/workplace-zones`, ...(params ? [params] : [])
    ] as const;
    }


export const getSearchWorkplaceZonesQueryOptions = <TData = Awaited<ReturnType<typeof searchWorkplaceZones>>, TError = Error>(params?: SearchWorkplaceZonesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchWorkplaceZones>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getSearchWorkplaceZonesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof searchWorkplaceZones>>> = ({ signal }) => searchWorkplaceZones(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof searchWorkplaceZones>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type SearchWorkplaceZonesQueryResult = NonNullable<Awaited<ReturnType<typeof searchWorkplaceZones>>>
export type SearchWorkplaceZonesQueryError = Error


export function useSearchWorkplaceZones<TData = Awaited<ReturnType<typeof searchWorkplaceZones>>, TError = Error>(
 params: undefined |  SearchWorkplaceZonesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchWorkplaceZones>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchWorkplaceZones>>,
          TError,
          Awaited<ReturnType<typeof searchWorkplaceZones>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSearchWorkplaceZones<TData = Awaited<ReturnType<typeof searchWorkplaceZones>>, TError = Error>(
 params?: SearchWorkplaceZonesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchWorkplaceZones>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchWorkplaceZones>>,
          TError,
          Awaited<ReturnType<typeof searchWorkplaceZones>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSearchWorkplaceZones<TData = Awaited<ReturnType<typeof searchWorkplaceZones>>, TError = Error>(
 params?: SearchWorkplaceZonesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchWorkplaceZones>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Search active neighbourhoods in the selected entity for assignment
 */

export function useSearchWorkplaceZones<TData = Awaited<ReturnType<typeof searchWorkplaceZones>>, TError = Error>(
 params?: SearchWorkplaceZonesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchWorkplaceZones>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getSearchWorkplaceZonesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetUserStaffClassificationUrl = (userId: string,) => {




  return `/api/admin/users/${userId}/staff-classification`
}

/**
 * @summary Read an entity member's booking eligibility classification (user administrators only)
 */
export const getUserStaffClassification = async (userId: string, options?: Parameters<typeof customFetch>[1]): Promise<StaffClassificationRecord> => {

  return customFetch<StaffClassificationRecord>(getGetUserStaffClassificationUrl(userId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetUserStaffClassificationQueryKey = (userId: string,) => {
    return [
    `/api/admin/users/${userId}/staff-classification`
    ] as const;
    }


export const getGetUserStaffClassificationQueryOptions = <TData = Awaited<ReturnType<typeof getUserStaffClassification>>, TError = Error>(userId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserStaffClassification>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetUserStaffClassificationQueryKey(userId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserStaffClassification>>> = ({ signal }) => getUserStaffClassification(userId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: userId !== null && userId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getUserStaffClassification>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetUserStaffClassificationQueryResult = NonNullable<Awaited<ReturnType<typeof getUserStaffClassification>>>
export type GetUserStaffClassificationQueryError = Error


export function useGetUserStaffClassification<TData = Awaited<ReturnType<typeof getUserStaffClassification>>, TError = Error>(
 userId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserStaffClassification>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserStaffClassification>>,
          TError,
          Awaited<ReturnType<typeof getUserStaffClassification>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetUserStaffClassification<TData = Awaited<ReturnType<typeof getUserStaffClassification>>, TError = Error>(
 userId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserStaffClassification>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserStaffClassification>>,
          TError,
          Awaited<ReturnType<typeof getUserStaffClassification>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetUserStaffClassification<TData = Awaited<ReturnType<typeof getUserStaffClassification>>, TError = Error>(
 userId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserStaffClassification>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read an entity member's booking eligibility classification (user administrators only)
 */

export function useGetUserStaffClassification<TData = Awaited<ReturnType<typeof getUserStaffClassification>>, TError = Error>(
 userId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserStaffClassification>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetUserStaffClassificationQueryOptions(userId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPutUserStaffClassificationUrl = (userId: string,) => {




  return `/api/admin/users/${userId}/staff-classification`
}

/**
 * @summary Audit and save an entity member's booking eligibility classification
 */
export const putUserStaffClassification = async (userId: string,
    staffClassificationRecord: StaffClassificationRecord, options?: Parameters<typeof customFetch>[1]): Promise<StaffClassificationRecord> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<StaffClassificationRecord>(getPutUserStaffClassificationUrl(userId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(staffClassificationRecord)
  }
);}





export const getPutUserStaffClassificationMutationKey = () => ['putUserStaffClassification'] as const;

export const getPutUserStaffClassificationMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putUserStaffClassification>>, TError,PutUserStaffClassificationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof putUserStaffClassification>>, TError,PutUserStaffClassificationMutationVariables, TContext> => {

const mutationKey = getPutUserStaffClassificationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof putUserStaffClassification>>, PutUserStaffClassificationMutationVariables> = (props) => {
          const {userId,data} = props ?? {};

          return  putUserStaffClassification(userId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PutUserStaffClassificationMutationResult = NonNullable<Awaited<ReturnType<typeof putUserStaffClassification>>>
    export type PutUserStaffClassificationMutationBody = StaffClassificationRecord
    export type PutUserStaffClassificationMutationError = Error
    export type PutUserStaffClassificationMutationVariables = {userId: string;data: StaffClassificationRecord}

    /**
 * @summary Audit and save an entity member's booking eligibility classification
 */
export const usePutUserStaffClassification = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putUserStaffClassification>>, TError,PutUserStaffClassificationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof putUserStaffClassification>>,
        TError,
        PutUserStaffClassificationMutationVariables,
        TContext
      > => {
      return useMutation(getPutUserStaffClassificationMutationOptions(options), queryClient);
    }

export const getGetUserWorkplaceProfileUrl = (userId: string,) => {




  return `/api/admin/users/${userId}/workplace-profile`
}

/**
 * @summary Get an entity member's neighbourhood assignments
 */
export const getUserWorkplaceProfile = async (userId: string, options?: Parameters<typeof customFetch>[1]): Promise<WorkplaceProfile> => {

  return customFetch<WorkplaceProfile>(getGetUserWorkplaceProfileUrl(userId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetUserWorkplaceProfileQueryKey = (userId: string,) => {
    return [
    `/api/admin/users/${userId}/workplace-profile`
    ] as const;
    }


export const getGetUserWorkplaceProfileQueryOptions = <TData = Awaited<ReturnType<typeof getUserWorkplaceProfile>>, TError = Error>(userId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserWorkplaceProfile>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetUserWorkplaceProfileQueryKey(userId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserWorkplaceProfile>>> = ({ signal }) => getUserWorkplaceProfile(userId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: userId !== null && userId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getUserWorkplaceProfile>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetUserWorkplaceProfileQueryResult = NonNullable<Awaited<ReturnType<typeof getUserWorkplaceProfile>>>
export type GetUserWorkplaceProfileQueryError = Error


export function useGetUserWorkplaceProfile<TData = Awaited<ReturnType<typeof getUserWorkplaceProfile>>, TError = Error>(
 userId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserWorkplaceProfile>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserWorkplaceProfile>>,
          TError,
          Awaited<ReturnType<typeof getUserWorkplaceProfile>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetUserWorkplaceProfile<TData = Awaited<ReturnType<typeof getUserWorkplaceProfile>>, TError = Error>(
 userId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserWorkplaceProfile>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserWorkplaceProfile>>,
          TError,
          Awaited<ReturnType<typeof getUserWorkplaceProfile>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetUserWorkplaceProfile<TData = Awaited<ReturnType<typeof getUserWorkplaceProfile>>, TError = Error>(
 userId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserWorkplaceProfile>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get an entity member's neighbourhood assignments
 */

export function useGetUserWorkplaceProfile<TData = Awaited<ReturnType<typeof getUserWorkplaceProfile>>, TError = Error>(
 userId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUserWorkplaceProfile>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetUserWorkplaceProfileQueryOptions(userId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPutUserWorkplaceProfileUrl = (userId: string,) => {




  return `/api/admin/users/${userId}/workplace-profile`
}

/**
 * @summary Assign an entity member to one or more neighbourhoods
 */
export const putUserWorkplaceProfile = async (userId: string,
    putUserWorkplaceProfileBody: PutUserWorkplaceProfileBody, options?: Parameters<typeof customFetch>[1]): Promise<WorkplaceProfile> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WorkplaceProfile>(getPutUserWorkplaceProfileUrl(userId),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(putUserWorkplaceProfileBody)
  }
);}





export const getPutUserWorkplaceProfileMutationKey = () => ['putUserWorkplaceProfile'] as const;

export const getPutUserWorkplaceProfileMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putUserWorkplaceProfile>>, TError,PutUserWorkplaceProfileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof putUserWorkplaceProfile>>, TError,PutUserWorkplaceProfileMutationVariables, TContext> => {

const mutationKey = getPutUserWorkplaceProfileMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof putUserWorkplaceProfile>>, PutUserWorkplaceProfileMutationVariables> = (props) => {
          const {userId,data} = props ?? {};

          return  putUserWorkplaceProfile(userId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PutUserWorkplaceProfileMutationResult = NonNullable<Awaited<ReturnType<typeof putUserWorkplaceProfile>>>
    export type PutUserWorkplaceProfileMutationBody = PutUserWorkplaceProfileBody
    export type PutUserWorkplaceProfileMutationError = Error
    export type PutUserWorkplaceProfileMutationVariables = {userId: string;data: PutUserWorkplaceProfileBody}

    /**
 * @summary Assign an entity member to one or more neighbourhoods
 */
export const usePutUserWorkplaceProfile = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putUserWorkplaceProfile>>, TError,PutUserWorkplaceProfileMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof putUserWorkplaceProfile>>,
        TError,
        PutUserWorkplaceProfileMutationVariables,
        TContext
      > => {
      return useMutation(getPutUserWorkplaceProfileMutationOptions(options), queryClient);
    }

export const getListMyNotificationsUrl = (params?: ListMyNotificationsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/me/notifications?${stringifiedParams}` : `/api/me/notifications`
}

/**
 * @summary List the signed-in user's notifications
 */
export const listMyNotifications = async (params?: ListMyNotificationsParams, options?: Parameters<typeof customFetch>[1]): Promise<NotificationListResponse> => {

  return customFetch<NotificationListResponse>(getListMyNotificationsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMyNotificationsQueryKey = (params?: ListMyNotificationsParams,) => {
    return [
    `/api/me/notifications`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMyNotificationsQueryOptions = <TData = Awaited<ReturnType<typeof listMyNotifications>>, TError = Error>(params?: ListMyNotificationsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyNotifications>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMyNotificationsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyNotifications>>> = ({ signal }) => listMyNotifications(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMyNotifications>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMyNotificationsQueryResult = NonNullable<Awaited<ReturnType<typeof listMyNotifications>>>
export type ListMyNotificationsQueryError = Error


export function useListMyNotifications<TData = Awaited<ReturnType<typeof listMyNotifications>>, TError = Error>(
 params: undefined |  ListMyNotificationsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyNotifications>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyNotifications>>,
          TError,
          Awaited<ReturnType<typeof listMyNotifications>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyNotifications<TData = Awaited<ReturnType<typeof listMyNotifications>>, TError = Error>(
 params?: ListMyNotificationsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyNotifications>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyNotifications>>,
          TError,
          Awaited<ReturnType<typeof listMyNotifications>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyNotifications<TData = Awaited<ReturnType<typeof listMyNotifications>>, TError = Error>(
 params?: ListMyNotificationsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyNotifications>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List the signed-in user's notifications
 */

export function useListMyNotifications<TData = Awaited<ReturnType<typeof listMyNotifications>>, TError = Error>(
 params?: ListMyNotificationsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyNotifications>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMyNotificationsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetMyNotificationUnreadCountUrl = () => {




  return `/api/me/notifications/unread-count`
}

/**
 * @summary Unread notification count for the signed-in user
 */
export const getMyNotificationUnreadCount = async ( options?: Parameters<typeof customFetch>[1]): Promise<NotificationUnreadCount> => {

  return customFetch<NotificationUnreadCount>(getGetMyNotificationUnreadCountUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMyNotificationUnreadCountQueryKey = () => {
    return [
    `/api/me/notifications/unread-count`
    ] as const;
    }


export const getGetMyNotificationUnreadCountQueryOptions = <TData = Awaited<ReturnType<typeof getMyNotificationUnreadCount>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyNotificationUnreadCount>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMyNotificationUnreadCountQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMyNotificationUnreadCount>>> = ({ signal }) => getMyNotificationUnreadCount({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMyNotificationUnreadCount>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMyNotificationUnreadCountQueryResult = NonNullable<Awaited<ReturnType<typeof getMyNotificationUnreadCount>>>
export type GetMyNotificationUnreadCountQueryError = Error


export function useGetMyNotificationUnreadCount<TData = Awaited<ReturnType<typeof getMyNotificationUnreadCount>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyNotificationUnreadCount>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyNotificationUnreadCount>>,
          TError,
          Awaited<ReturnType<typeof getMyNotificationUnreadCount>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyNotificationUnreadCount<TData = Awaited<ReturnType<typeof getMyNotificationUnreadCount>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyNotificationUnreadCount>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyNotificationUnreadCount>>,
          TError,
          Awaited<ReturnType<typeof getMyNotificationUnreadCount>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyNotificationUnreadCount<TData = Awaited<ReturnType<typeof getMyNotificationUnreadCount>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyNotificationUnreadCount>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Unread notification count for the signed-in user
 */

export function useGetMyNotificationUnreadCount<TData = Awaited<ReturnType<typeof getMyNotificationUnreadCount>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyNotificationUnreadCount>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMyNotificationUnreadCountQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getMarkAllMyNotificationsReadUrl = () => {




  return `/api/me/notifications/read-all`
}

/**
 * @summary Mark all notifications as read
 */
export const markAllMyNotificationsRead = async ( options?: Parameters<typeof customFetch>[1]): Promise<MarkAllMyNotificationsRead200> => {

  return customFetch<MarkAllMyNotificationsRead200>(getMarkAllMyNotificationsReadUrl(),
  {
    ...options,
    method: 'POST'


  }
);}





export const getMarkAllMyNotificationsReadMutationKey = () => ['markAllMyNotificationsRead'] as const;

export const getMarkAllMyNotificationsReadMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markAllMyNotificationsRead>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof markAllMyNotificationsRead>>, TError,void, TContext> => {

const mutationKey = getMarkAllMyNotificationsReadMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof markAllMyNotificationsRead>>, void> = () => {


          return  markAllMyNotificationsRead(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MarkAllMyNotificationsReadMutationResult = NonNullable<Awaited<ReturnType<typeof markAllMyNotificationsRead>>>

    export type MarkAllMyNotificationsReadMutationError = Error


    /**
 * @summary Mark all notifications as read
 */
export const useMarkAllMyNotificationsRead = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markAllMyNotificationsRead>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof markAllMyNotificationsRead>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getMarkAllMyNotificationsReadMutationOptions(options), queryClient);
    }

export const getMarkMyNotificationReadUrl = (id: string,) => {




  return `/api/me/notifications/${id}/read`
}

/**
 * @summary Mark a single notification as read
 */
export const markMyNotificationRead = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<NotificationItem> => {

  return customFetch<NotificationItem>(getMarkMyNotificationReadUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getMarkMyNotificationReadMutationKey = () => ['markMyNotificationRead'] as const;

export const getMarkMyNotificationReadMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markMyNotificationRead>>, TError,MarkMyNotificationReadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof markMyNotificationRead>>, TError,MarkMyNotificationReadMutationVariables, TContext> => {

const mutationKey = getMarkMyNotificationReadMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof markMyNotificationRead>>, MarkMyNotificationReadMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  markMyNotificationRead(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type MarkMyNotificationReadMutationResult = NonNullable<Awaited<ReturnType<typeof markMyNotificationRead>>>

    export type MarkMyNotificationReadMutationError = Error
    export type MarkMyNotificationReadMutationVariables = {id: string}

    /**
 * @summary Mark a single notification as read
 */
export const useMarkMyNotificationRead = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof markMyNotificationRead>>, TError,MarkMyNotificationReadMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof markMyNotificationRead>>,
        TError,
        MarkMyNotificationReadMutationVariables,
        TContext
      > => {
      return useMutation(getMarkMyNotificationReadMutationOptions(options), queryClient);
    }

export const getGetMyNotificationPreferencesUrl = () => {




  return `/api/me/notification-preferences`
}

/**
 * @summary Get notification category and channel preferences
 */
export const getMyNotificationPreferences = async ( options?: Parameters<typeof customFetch>[1]): Promise<NotificationPreferencesResponse> => {

  return customFetch<NotificationPreferencesResponse>(getGetMyNotificationPreferencesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMyNotificationPreferencesQueryKey = () => {
    return [
    `/api/me/notification-preferences`
    ] as const;
    }


export const getGetMyNotificationPreferencesQueryOptions = <TData = Awaited<ReturnType<typeof getMyNotificationPreferences>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyNotificationPreferences>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMyNotificationPreferencesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMyNotificationPreferences>>> = ({ signal }) => getMyNotificationPreferences({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMyNotificationPreferences>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMyNotificationPreferencesQueryResult = NonNullable<Awaited<ReturnType<typeof getMyNotificationPreferences>>>
export type GetMyNotificationPreferencesQueryError = Error


export function useGetMyNotificationPreferences<TData = Awaited<ReturnType<typeof getMyNotificationPreferences>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyNotificationPreferences>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyNotificationPreferences>>,
          TError,
          Awaited<ReturnType<typeof getMyNotificationPreferences>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyNotificationPreferences<TData = Awaited<ReturnType<typeof getMyNotificationPreferences>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyNotificationPreferences>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyNotificationPreferences>>,
          TError,
          Awaited<ReturnType<typeof getMyNotificationPreferences>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyNotificationPreferences<TData = Awaited<ReturnType<typeof getMyNotificationPreferences>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyNotificationPreferences>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get notification category and channel preferences
 */

export function useGetMyNotificationPreferences<TData = Awaited<ReturnType<typeof getMyNotificationPreferences>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyNotificationPreferences>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMyNotificationPreferencesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPutMyNotificationPreferencesUrl = () => {




  return `/api/me/notification-preferences`
}

/**
 * @summary Update notification preferences
 */
export const putMyNotificationPreferences = async (updateNotificationPreferencesRequest: UpdateNotificationPreferencesRequest, options?: Parameters<typeof customFetch>[1]): Promise<NotificationPreferencesResponse> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<NotificationPreferencesResponse>(getPutMyNotificationPreferencesUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateNotificationPreferencesRequest)
  }
);}





export const getPutMyNotificationPreferencesMutationKey = () => ['putMyNotificationPreferences'] as const;

export const getPutMyNotificationPreferencesMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putMyNotificationPreferences>>, TError,PutMyNotificationPreferencesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof putMyNotificationPreferences>>, TError,PutMyNotificationPreferencesMutationVariables, TContext> => {

const mutationKey = getPutMyNotificationPreferencesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof putMyNotificationPreferences>>, PutMyNotificationPreferencesMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  putMyNotificationPreferences(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PutMyNotificationPreferencesMutationResult = NonNullable<Awaited<ReturnType<typeof putMyNotificationPreferences>>>
    export type PutMyNotificationPreferencesMutationBody = UpdateNotificationPreferencesRequest
    export type PutMyNotificationPreferencesMutationError = Error
    export type PutMyNotificationPreferencesMutationVariables = {data: UpdateNotificationPreferencesRequest}

    /**
 * @summary Update notification preferences
 */
export const usePutMyNotificationPreferences = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putMyNotificationPreferences>>, TError,PutMyNotificationPreferencesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof putMyNotificationPreferences>>,
        TError,
        PutMyNotificationPreferencesMutationVariables,
        TContext
      > => {
      return useMutation(getPutMyNotificationPreferencesMutationOptions(options), queryClient);
    }

export const getGetMyNotificationChannelsUrl = () => {




  return `/api/me/notification-channels`
}

/**
 * Surfaces whether ACS email is configured. Secrets are never returned — only a masked sender domain when email is ready.
 * @summary Email/in-app channel readiness for notifications
 */
export const getMyNotificationChannels = async ( options?: Parameters<typeof customFetch>[1]): Promise<NotificationChannelStatus> => {

  return customFetch<NotificationChannelStatus>(getGetMyNotificationChannelsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetMyNotificationChannelsQueryKey = () => {
    return [
    `/api/me/notification-channels`
    ] as const;
    }


export const getGetMyNotificationChannelsQueryOptions = <TData = Awaited<ReturnType<typeof getMyNotificationChannels>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyNotificationChannels>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMyNotificationChannelsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getMyNotificationChannels>>> = ({ signal }) => getMyNotificationChannels({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getMyNotificationChannels>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMyNotificationChannelsQueryResult = NonNullable<Awaited<ReturnType<typeof getMyNotificationChannels>>>
export type GetMyNotificationChannelsQueryError = Error


export function useGetMyNotificationChannels<TData = Awaited<ReturnType<typeof getMyNotificationChannels>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyNotificationChannels>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyNotificationChannels>>,
          TError,
          Awaited<ReturnType<typeof getMyNotificationChannels>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyNotificationChannels<TData = Awaited<ReturnType<typeof getMyNotificationChannels>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyNotificationChannels>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getMyNotificationChannels>>,
          TError,
          Awaited<ReturnType<typeof getMyNotificationChannels>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMyNotificationChannels<TData = Awaited<ReturnType<typeof getMyNotificationChannels>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyNotificationChannels>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Email/in-app channel readiness for notifications
 */

export function useGetMyNotificationChannels<TData = Awaited<ReturnType<typeof getMyNotificationChannels>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getMyNotificationChannels>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetMyNotificationChannelsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetVisitorBookingSettingsUrl = () => {




  return `/api/visitor-booking/settings`
}

/**
 * @summary Read entity visitor booking mode
 */
export const getVisitorBookingSettings = async ( options?: Parameters<typeof customFetch>[1]): Promise<VisitorBookingSettings> => {

  return customFetch<VisitorBookingSettings>(getGetVisitorBookingSettingsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetVisitorBookingSettingsQueryKey = () => {
    return [
    `/api/visitor-booking/settings`
    ] as const;
    }


export const getGetVisitorBookingSettingsQueryOptions = <TData = Awaited<ReturnType<typeof getVisitorBookingSettings>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisitorBookingSettings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetVisitorBookingSettingsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getVisitorBookingSettings>>> = ({ signal }) => getVisitorBookingSettings({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getVisitorBookingSettings>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetVisitorBookingSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof getVisitorBookingSettings>>>
export type GetVisitorBookingSettingsQueryError = unknown


export function useGetVisitorBookingSettings<TData = Awaited<ReturnType<typeof getVisitorBookingSettings>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisitorBookingSettings>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getVisitorBookingSettings>>,
          TError,
          Awaited<ReturnType<typeof getVisitorBookingSettings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetVisitorBookingSettings<TData = Awaited<ReturnType<typeof getVisitorBookingSettings>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisitorBookingSettings>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getVisitorBookingSettings>>,
          TError,
          Awaited<ReturnType<typeof getVisitorBookingSettings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetVisitorBookingSettings<TData = Awaited<ReturnType<typeof getVisitorBookingSettings>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisitorBookingSettings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read entity visitor booking mode
 */

export function useGetVisitorBookingSettings<TData = Awaited<ReturnType<typeof getVisitorBookingSettings>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisitorBookingSettings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetVisitorBookingSettingsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSetVisitorBookingSettingsUrl = () => {




  return `/api/visitor-booking/settings`
}

/**
 * @summary Administrator sets entity visitor booking mode
 */
export const setVisitorBookingSettings = async (visitorBookingSettings: VisitorBookingSettings, options?: Parameters<typeof customFetch>[1]): Promise<VisitorBookingSettings> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<VisitorBookingSettings>(getSetVisitorBookingSettingsUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(visitorBookingSettings)
  }
);}





export const getSetVisitorBookingSettingsMutationKey = () => ['setVisitorBookingSettings'] as const;

export const getSetVisitorBookingSettingsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setVisitorBookingSettings>>, TError,SetVisitorBookingSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setVisitorBookingSettings>>, TError,SetVisitorBookingSettingsMutationVariables, TContext> => {

const mutationKey = getSetVisitorBookingSettingsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setVisitorBookingSettings>>, SetVisitorBookingSettingsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  setVisitorBookingSettings(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetVisitorBookingSettingsMutationResult = NonNullable<Awaited<ReturnType<typeof setVisitorBookingSettings>>>
    export type SetVisitorBookingSettingsMutationBody = VisitorBookingSettings
    export type SetVisitorBookingSettingsMutationError = unknown
    export type SetVisitorBookingSettingsMutationVariables = {data: VisitorBookingSettings}

    /**
 * @summary Administrator sets entity visitor booking mode
 */
export const useSetVisitorBookingSettings = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setVisitorBookingSettings>>, TError,SetVisitorBookingSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setVisitorBookingSettings>>,
        TError,
        SetVisitorBookingSettingsMutationVariables,
        TContext
      > => {
      return useMutation(getSetVisitorBookingSettingsMutationOptions(options), queryClient);
    }

export const getListHostVisitorApprovalsUrl = (params?: ListHostVisitorApprovalsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/visitor-booking/approvals?${stringifiedParams}` : `/api/visitor-booking/approvals`
}

/**
 * @summary Host approval queue within the selected entity
 */
export const listHostVisitorApprovals = async (params?: ListHostVisitorApprovalsParams, options?: Parameters<typeof customFetch>[1]): Promise<VisitorApprovalList> => {

  return customFetch<VisitorApprovalList>(getListHostVisitorApprovalsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListHostVisitorApprovalsQueryKey = (params?: ListHostVisitorApprovalsParams,) => {
    return [
    `/api/visitor-booking/approvals`, ...(params ? [params] : [])
    ] as const;
    }


export const getListHostVisitorApprovalsQueryOptions = <TData = Awaited<ReturnType<typeof listHostVisitorApprovals>>, TError = unknown>(params?: ListHostVisitorApprovalsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listHostVisitorApprovals>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListHostVisitorApprovalsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listHostVisitorApprovals>>> = ({ signal }) => listHostVisitorApprovals(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listHostVisitorApprovals>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListHostVisitorApprovalsQueryResult = NonNullable<Awaited<ReturnType<typeof listHostVisitorApprovals>>>
export type ListHostVisitorApprovalsQueryError = unknown


export function useListHostVisitorApprovals<TData = Awaited<ReturnType<typeof listHostVisitorApprovals>>, TError = unknown>(
 params: undefined |  ListHostVisitorApprovalsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listHostVisitorApprovals>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listHostVisitorApprovals>>,
          TError,
          Awaited<ReturnType<typeof listHostVisitorApprovals>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListHostVisitorApprovals<TData = Awaited<ReturnType<typeof listHostVisitorApprovals>>, TError = unknown>(
 params?: ListHostVisitorApprovalsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listHostVisitorApprovals>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listHostVisitorApprovals>>,
          TError,
          Awaited<ReturnType<typeof listHostVisitorApprovals>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListHostVisitorApprovals<TData = Awaited<ReturnType<typeof listHostVisitorApprovals>>, TError = unknown>(
 params?: ListHostVisitorApprovalsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listHostVisitorApprovals>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Host approval queue within the selected entity
 */

export function useListHostVisitorApprovals<TData = Awaited<ReturnType<typeof listHostVisitorApprovals>>, TError = unknown>(
 params?: ListHostVisitorApprovalsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listHostVisitorApprovals>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListHostVisitorApprovalsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDecideVisitorApprovalUrl = (id: string,) => {




  return `/api/visitor-booking/approvals/${id}/decision`
}

/**
 * @summary Host approves or revokes a visitor request
 */
export const decideVisitorApproval = async (id: string,
    visitorApprovalDecision: VisitorApprovalDecision, options?: Parameters<typeof customFetch>[1]): Promise<VisitorBookingApproval> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<VisitorBookingApproval>(getDecideVisitorApprovalUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(visitorApprovalDecision)
  }
);}





export const getDecideVisitorApprovalMutationKey = () => ['decideVisitorApproval'] as const;

export const getDecideVisitorApprovalMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideVisitorApproval>>, TError,DecideVisitorApprovalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof decideVisitorApproval>>, TError,DecideVisitorApprovalMutationVariables, TContext> => {

const mutationKey = getDecideVisitorApprovalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof decideVisitorApproval>>, DecideVisitorApprovalMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  decideVisitorApproval(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DecideVisitorApprovalMutationResult = NonNullable<Awaited<ReturnType<typeof decideVisitorApproval>>>
    export type DecideVisitorApprovalMutationBody = VisitorApprovalDecision
    export type DecideVisitorApprovalMutationError = unknown
    export type DecideVisitorApprovalMutationVariables = {id: string;data: VisitorApprovalDecision}

    /**
 * @summary Host approves or revokes a visitor request
 */
export const useDecideVisitorApproval = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof decideVisitorApproval>>, TError,DecideVisitorApprovalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof decideVisitorApproval>>,
        TError,
        DecideVisitorApprovalMutationVariables,
        TContext
      > => {
      return useMutation(getDecideVisitorApprovalMutationOptions(options), queryClient);
    }

export const getListMyVisitorApprovalsUrl = (params?: ListMyVisitorApprovalsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/visitor-self/approvals?${stringifiedParams}` : `/api/visitor-self/approvals`
}

/**
 * @summary Signed-in visitor approval status
 */
export const listMyVisitorApprovals = async (params?: ListMyVisitorApprovalsParams, options?: Parameters<typeof customFetch>[1]): Promise<VisitorApprovalList> => {

  return customFetch<VisitorApprovalList>(getListMyVisitorApprovalsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMyVisitorApprovalsQueryKey = (params?: ListMyVisitorApprovalsParams,) => {
    return [
    `/api/visitor-self/approvals`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMyVisitorApprovalsQueryOptions = <TData = Awaited<ReturnType<typeof listMyVisitorApprovals>>, TError = unknown>(params?: ListMyVisitorApprovalsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyVisitorApprovals>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMyVisitorApprovalsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyVisitorApprovals>>> = ({ signal }) => listMyVisitorApprovals(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMyVisitorApprovals>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMyVisitorApprovalsQueryResult = NonNullable<Awaited<ReturnType<typeof listMyVisitorApprovals>>>
export type ListMyVisitorApprovalsQueryError = unknown


export function useListMyVisitorApprovals<TData = Awaited<ReturnType<typeof listMyVisitorApprovals>>, TError = unknown>(
 params: undefined |  ListMyVisitorApprovalsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyVisitorApprovals>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyVisitorApprovals>>,
          TError,
          Awaited<ReturnType<typeof listMyVisitorApprovals>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyVisitorApprovals<TData = Awaited<ReturnType<typeof listMyVisitorApprovals>>, TError = unknown>(
 params?: ListMyVisitorApprovalsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyVisitorApprovals>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyVisitorApprovals>>,
          TError,
          Awaited<ReturnType<typeof listMyVisitorApprovals>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyVisitorApprovals<TData = Awaited<ReturnType<typeof listMyVisitorApprovals>>, TError = unknown>(
 params?: ListMyVisitorApprovalsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyVisitorApprovals>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Signed-in visitor approval status
 */

export function useListMyVisitorApprovals<TData = Awaited<ReturnType<typeof listMyVisitorApprovals>>, TError = unknown>(
 params?: ListMyVisitorApprovalsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyVisitorApprovals>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMyVisitorApprovalsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getRequestVisitorApprovalUrl = () => {




  return `/api/visitor-self/approvals/request`
}

/**
 * @summary Request approval for a host-registered visit using verified sign-in
 */
export const requestVisitorApproval = async (visitorApprovalRequest: VisitorApprovalRequest, options?: Parameters<typeof customFetch>[1]): Promise<VisitorBookingApproval> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<VisitorBookingApproval>(getRequestVisitorApprovalUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(visitorApprovalRequest)
  }
);}





export const getRequestVisitorApprovalMutationKey = () => ['requestVisitorApproval'] as const;

export const getRequestVisitorApprovalMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestVisitorApproval>>, TError,RequestVisitorApprovalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof requestVisitorApproval>>, TError,RequestVisitorApprovalMutationVariables, TContext> => {

const mutationKey = getRequestVisitorApprovalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof requestVisitorApproval>>, RequestVisitorApprovalMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  requestVisitorApproval(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RequestVisitorApprovalMutationResult = NonNullable<Awaited<ReturnType<typeof requestVisitorApproval>>>
    export type RequestVisitorApprovalMutationBody = VisitorApprovalRequest
    export type RequestVisitorApprovalMutationError = unknown
    export type RequestVisitorApprovalMutationVariables = {data: VisitorApprovalRequest}

    /**
 * @summary Request approval for a host-registered visit using verified sign-in
 */
export const useRequestVisitorApproval = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof requestVisitorApproval>>, TError,RequestVisitorApprovalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof requestVisitorApproval>>,
        TError,
        RequestVisitorApprovalMutationVariables,
        TContext
      > => {
      return useMutation(getRequestVisitorApprovalMutationOptions(options), queryClient);
    }

export const getSearchVisitorAvailabilityUrl = (id: string,
    params: SearchVisitorAvailabilityParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/visitor-self/approvals/${id}/availability?${stringifiedParams}` : `/api/visitor-self/approvals/${id}/availability`
}

/**
 * @summary Search only the approved site and resource type
 */
export const searchVisitorAvailability = async (id: string,
    params: SearchVisitorAvailabilityParams, options?: Parameters<typeof customFetch>[1]): Promise<AvailabilitySearchResponse> => {

  return customFetch<AvailabilitySearchResponse>(getSearchVisitorAvailabilityUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getSearchVisitorAvailabilityQueryKey = (id: string,
    params?: SearchVisitorAvailabilityParams,) => {
    return [
    `/api/visitor-self/approvals/${id}/availability`, ...(params ? [params] : [])
    ] as const;
    }


export const getSearchVisitorAvailabilityQueryOptions = <TData = Awaited<ReturnType<typeof searchVisitorAvailability>>, TError = unknown>(id: string,
    params: SearchVisitorAvailabilityParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchVisitorAvailability>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getSearchVisitorAvailabilityQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof searchVisitorAvailability>>> = ({ signal }) => searchVisitorAvailability(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof searchVisitorAvailability>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type SearchVisitorAvailabilityQueryResult = NonNullable<Awaited<ReturnType<typeof searchVisitorAvailability>>>
export type SearchVisitorAvailabilityQueryError = unknown


export function useSearchVisitorAvailability<TData = Awaited<ReturnType<typeof searchVisitorAvailability>>, TError = unknown>(
 id: string,
    params: SearchVisitorAvailabilityParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchVisitorAvailability>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchVisitorAvailability>>,
          TError,
          Awaited<ReturnType<typeof searchVisitorAvailability>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSearchVisitorAvailability<TData = Awaited<ReturnType<typeof searchVisitorAvailability>>, TError = unknown>(
 id: string,
    params: SearchVisitorAvailabilityParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchVisitorAvailability>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchVisitorAvailability>>,
          TError,
          Awaited<ReturnType<typeof searchVisitorAvailability>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSearchVisitorAvailability<TData = Awaited<ReturnType<typeof searchVisitorAvailability>>, TError = unknown>(
 id: string,
    params: SearchVisitorAvailabilityParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchVisitorAvailability>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Search only the approved site and resource type
 */

export function useSearchVisitorAvailability<TData = Awaited<ReturnType<typeof searchVisitorAvailability>>, TError = unknown>(
 id: string,
    params: SearchVisitorAvailabilityParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchVisitorAvailability>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getSearchVisitorAvailabilityQueryOptions(id,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getBookVisitorResourceUrl = (id: string,) => {




  return `/api/visitor-self/approvals/${id}/book`
}

/**
 * @summary Create one policy-checked booking for an approved visit
 */
export const bookVisitorResource = async (id: string,
    visitorSelfBookRequest: VisitorSelfBookRequest, options?: Parameters<typeof customFetch>[1]): Promise<VisitorSelfBookingResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<VisitorSelfBookingResult>(getBookVisitorResourceUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(visitorSelfBookRequest)
  }
);}





export const getBookVisitorResourceMutationKey = () => ['bookVisitorResource'] as const;

export const getBookVisitorResourceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof bookVisitorResource>>, TError,BookVisitorResourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof bookVisitorResource>>, TError,BookVisitorResourceMutationVariables, TContext> => {

const mutationKey = getBookVisitorResourceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof bookVisitorResource>>, BookVisitorResourceMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  bookVisitorResource(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type BookVisitorResourceMutationResult = NonNullable<Awaited<ReturnType<typeof bookVisitorResource>>>
    export type BookVisitorResourceMutationBody = VisitorSelfBookRequest
    export type BookVisitorResourceMutationError = unknown
    export type BookVisitorResourceMutationVariables = {id: string;data: VisitorSelfBookRequest}

    /**
 * @summary Create one policy-checked booking for an approved visit
 */
export const useBookVisitorResource = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof bookVisitorResource>>, TError,BookVisitorResourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof bookVisitorResource>>,
        TError,
        BookVisitorResourceMutationVariables,
        TContext
      > => {
      return useMutation(getBookVisitorResourceMutationOptions(options), queryClient);
    }

export const getGetVisitorPolicyRequirementsUrl = (id: string,
    params: GetVisitorPolicyRequirementsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/visitor-self/approvals/${id}/policies-and-standards?${stringifiedParams}` : `/api/visitor-self/approvals/${id}/policies-and-standards`
}

/**
 * @summary Read the signed-in visitor's requirements within a current host approval
 */
export const getVisitorPolicyRequirements = async (id: string,
    params: GetVisitorPolicyRequirementsParams, options?: Parameters<typeof customFetch>[1]): Promise<PolicyRequirements> => {

  return customFetch<PolicyRequirements>(getGetVisitorPolicyRequirementsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetVisitorPolicyRequirementsQueryKey = (id: string,
    params?: GetVisitorPolicyRequirementsParams,) => {
    return [
    `/api/visitor-self/approvals/${id}/policies-and-standards`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetVisitorPolicyRequirementsQueryOptions = <TData = Awaited<ReturnType<typeof getVisitorPolicyRequirements>>, TError = void>(id: string,
    params: GetVisitorPolicyRequirementsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisitorPolicyRequirements>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetVisitorPolicyRequirementsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getVisitorPolicyRequirements>>> = ({ signal }) => getVisitorPolicyRequirements(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getVisitorPolicyRequirements>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetVisitorPolicyRequirementsQueryResult = NonNullable<Awaited<ReturnType<typeof getVisitorPolicyRequirements>>>
export type GetVisitorPolicyRequirementsQueryError = void


export function useGetVisitorPolicyRequirements<TData = Awaited<ReturnType<typeof getVisitorPolicyRequirements>>, TError = void>(
 id: string,
    params: GetVisitorPolicyRequirementsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisitorPolicyRequirements>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getVisitorPolicyRequirements>>,
          TError,
          Awaited<ReturnType<typeof getVisitorPolicyRequirements>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetVisitorPolicyRequirements<TData = Awaited<ReturnType<typeof getVisitorPolicyRequirements>>, TError = void>(
 id: string,
    params: GetVisitorPolicyRequirementsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisitorPolicyRequirements>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getVisitorPolicyRequirements>>,
          TError,
          Awaited<ReturnType<typeof getVisitorPolicyRequirements>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetVisitorPolicyRequirements<TData = Awaited<ReturnType<typeof getVisitorPolicyRequirements>>, TError = void>(
 id: string,
    params: GetVisitorPolicyRequirementsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisitorPolicyRequirements>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read the signed-in visitor's requirements within a current host approval
 */

export function useGetVisitorPolicyRequirements<TData = Awaited<ReturnType<typeof getVisitorPolicyRequirements>>, TError = void>(
 id: string,
    params: GetVisitorPolicyRequirementsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisitorPolicyRequirements>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetVisitorPolicyRequirementsQueryOptions(id,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getAcceptVisitorPolicyRequirementsUrl = (id: string,) => {




  return `/api/visitor-self/approvals/${id}/policies-and-standards`
}

/**
 * @summary Accept the exact current set as the verified visitor
 */
export const acceptVisitorPolicyRequirements = async (id: string,
    acceptPolicyRequirements: AcceptPolicyRequirements, options?: Parameters<typeof customFetch>[1]): Promise<PolicyRequirements> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PolicyRequirements>(getAcceptVisitorPolicyRequirementsUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(acceptPolicyRequirements)
  }
);}





export const getAcceptVisitorPolicyRequirementsMutationKey = () => ['acceptVisitorPolicyRequirements'] as const;

export const getAcceptVisitorPolicyRequirementsMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof acceptVisitorPolicyRequirements>>, TError,AcceptVisitorPolicyRequirementsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof acceptVisitorPolicyRequirements>>, TError,AcceptVisitorPolicyRequirementsMutationVariables, TContext> => {

const mutationKey = getAcceptVisitorPolicyRequirementsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof acceptVisitorPolicyRequirements>>, AcceptVisitorPolicyRequirementsMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  acceptVisitorPolicyRequirements(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AcceptVisitorPolicyRequirementsMutationResult = NonNullable<Awaited<ReturnType<typeof acceptVisitorPolicyRequirements>>>
    export type AcceptVisitorPolicyRequirementsMutationBody = AcceptPolicyRequirements
    export type AcceptVisitorPolicyRequirementsMutationError = void
    export type AcceptVisitorPolicyRequirementsMutationVariables = {id: string;data: AcceptPolicyRequirements}

    /**
 * @summary Accept the exact current set as the verified visitor
 */
export const useAcceptVisitorPolicyRequirements = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof acceptVisitorPolicyRequirements>>, TError,AcceptVisitorPolicyRequirementsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof acceptVisitorPolicyRequirements>>,
        TError,
        AcceptVisitorPolicyRequirementsMutationVariables,
        TContext
      > => {
      return useMutation(getAcceptVisitorPolicyRequirementsMutationOptions(options), queryClient);
    }

export const getGetVisitorInductionUrl = (id: string,) => {




  return `/api/visitor-self/approvals/${id}/induction`
}

/**
 * @summary Get induction checklist and status for the approved visitor site
 */
export const getVisitorInduction = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<GetVisitorInduction200> => {

  return customFetch<GetVisitorInduction200>(getGetVisitorInductionUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetVisitorInductionQueryKey = (id: string,) => {
    return [
    `/api/visitor-self/approvals/${id}/induction`
    ] as const;
    }


export const getGetVisitorInductionQueryOptions = <TData = Awaited<ReturnType<typeof getVisitorInduction>>, TError = unknown>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisitorInduction>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetVisitorInductionQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getVisitorInduction>>> = ({ signal }) => getVisitorInduction(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getVisitorInduction>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetVisitorInductionQueryResult = NonNullable<Awaited<ReturnType<typeof getVisitorInduction>>>
export type GetVisitorInductionQueryError = unknown


export function useGetVisitorInduction<TData = Awaited<ReturnType<typeof getVisitorInduction>>, TError = unknown>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisitorInduction>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getVisitorInduction>>,
          TError,
          Awaited<ReturnType<typeof getVisitorInduction>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetVisitorInduction<TData = Awaited<ReturnType<typeof getVisitorInduction>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisitorInduction>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getVisitorInduction>>,
          TError,
          Awaited<ReturnType<typeof getVisitorInduction>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetVisitorInduction<TData = Awaited<ReturnType<typeof getVisitorInduction>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisitorInduction>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get induction checklist and status for the approved visitor site
 */

export function useGetVisitorInduction<TData = Awaited<ReturnType<typeof getVisitorInduction>>, TError = unknown>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisitorInduction>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetVisitorInductionQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCompleteVisitorInductionUrl = (id: string,) => {




  return `/api/visitor-self/approvals/${id}/induction`
}

/**
 * @summary Acknowledge every item in the current approved site induction
 */
export const completeVisitorInduction = async (id: string,
    completeSiteInductionRequest: CompleteSiteInductionRequest, options?: Parameters<typeof customFetch>[1]): Promise<UserSiteInductionStatus> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<UserSiteInductionStatus>(getCompleteVisitorInductionUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(completeSiteInductionRequest)
  }
);}





export const getCompleteVisitorInductionMutationKey = () => ['completeVisitorInduction'] as const;

export const getCompleteVisitorInductionMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof completeVisitorInduction>>, TError,CompleteVisitorInductionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof completeVisitorInduction>>, TError,CompleteVisitorInductionMutationVariables, TContext> => {

const mutationKey = getCompleteVisitorInductionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof completeVisitorInduction>>, CompleteVisitorInductionMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  completeVisitorInduction(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CompleteVisitorInductionMutationResult = NonNullable<Awaited<ReturnType<typeof completeVisitorInduction>>>
    export type CompleteVisitorInductionMutationBody = CompleteSiteInductionRequest
    export type CompleteVisitorInductionMutationError = unknown
    export type CompleteVisitorInductionMutationVariables = {id: string;data: CompleteSiteInductionRequest}

    /**
 * @summary Acknowledge every item in the current approved site induction
 */
export const useCompleteVisitorInduction = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof completeVisitorInduction>>, TError,CompleteVisitorInductionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof completeVisitorInduction>>,
        TError,
        CompleteVisitorInductionMutationVariables,
        TContext
      > => {
      return useMutation(getCompleteVisitorInductionMutationOptions(options), queryClient);
    }

export const getCancelVisitorResourceUrl = (id: string,) => {




  return `/api/visitor-self/approvals/${id}/booking/cancel`
}

/**
 * @summary Visitor cancels their own approval-bound resource booking
 */
export const cancelVisitorResource = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<VisitorCancelResult> => {

  return customFetch<VisitorCancelResult>(getCancelVisitorResourceUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelVisitorResourceMutationKey = () => ['cancelVisitorResource'] as const;

export const getCancelVisitorResourceMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelVisitorResource>>, TError,CancelVisitorResourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelVisitorResource>>, TError,CancelVisitorResourceMutationVariables, TContext> => {

const mutationKey = getCancelVisitorResourceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelVisitorResource>>, CancelVisitorResourceMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  cancelVisitorResource(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelVisitorResourceMutationResult = NonNullable<Awaited<ReturnType<typeof cancelVisitorResource>>>

    export type CancelVisitorResourceMutationError = unknown
    export type CancelVisitorResourceMutationVariables = {id: string}

    /**
 * @summary Visitor cancels their own approval-bound resource booking
 */
export const useCancelVisitorResource = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelVisitorResource>>, TError,CancelVisitorResourceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof cancelVisitorResource>>,
        TError,
        CancelVisitorResourceMutationVariables,
        TContext
      > => {
      return useMutation(getCancelVisitorResourceMutationOptions(options), queryClient);
    }

export const getListVisitsUrl = (params?: ListVisitsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/visits?${stringifiedParams}` : `/api/visits`
}

/**
 * Hosts see their own visitors by default. Front-desk and admin users can list all visits; pass mine=true to scope to the caller. Use today=true for the reception board. format=csv for download (desk/admin).
 * @summary List visits (host, front desk, or admin)
 */
export const listVisits = async (params?: ListVisitsParams, options?: Parameters<typeof customFetch>[1]): Promise<VisitListResponse | string> => {

  return customFetch<VisitListResponse | string>(getListVisitsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListVisitsQueryKey = (params?: ListVisitsParams,) => {
    return [
    `/api/visits`, ...(params ? [params] : [])
    ] as const;
    }


export const getListVisitsQueryOptions = <TData = Awaited<ReturnType<typeof listVisits>>, TError = Error>(params?: ListVisitsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listVisits>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListVisitsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listVisits>>> = ({ signal }) => listVisits(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listVisits>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListVisitsQueryResult = NonNullable<Awaited<ReturnType<typeof listVisits>>>
export type ListVisitsQueryError = Error


export function useListVisits<TData = Awaited<ReturnType<typeof listVisits>>, TError = Error>(
 params: undefined |  ListVisitsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listVisits>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listVisits>>,
          TError,
          Awaited<ReturnType<typeof listVisits>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListVisits<TData = Awaited<ReturnType<typeof listVisits>>, TError = Error>(
 params?: ListVisitsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listVisits>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listVisits>>,
          TError,
          Awaited<ReturnType<typeof listVisits>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListVisits<TData = Awaited<ReturnType<typeof listVisits>>, TError = Error>(
 params?: ListVisitsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listVisits>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List visits (host, front desk, or admin)
 */

export function useListVisits<TData = Awaited<ReturnType<typeof listVisits>>, TError = Error>(
 params?: ListVisitsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listVisits>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListVisitsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateVisitUrl = () => {




  return `/api/visits`
}

/**
 * @summary Pre-register a visitor (booking-linked or standalone)
 */
export const createVisit = async (createVisitRequest: CreateVisitRequest, options?: Parameters<typeof customFetch>[1]): Promise<Visit> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Visit>(getCreateVisitUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createVisitRequest)
  }
);}





export const getCreateVisitMutationKey = () => ['createVisit'] as const;

export const getCreateVisitMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createVisit>>, TError,CreateVisitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createVisit>>, TError,CreateVisitMutationVariables, TContext> => {

const mutationKey = getCreateVisitMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createVisit>>, CreateVisitMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createVisit(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateVisitMutationResult = NonNullable<Awaited<ReturnType<typeof createVisit>>>
    export type CreateVisitMutationBody = CreateVisitRequest
    export type CreateVisitMutationError = Error
    export type CreateVisitMutationVariables = {data: CreateVisitRequest}

    /**
 * @summary Pre-register a visitor (booking-linked or standalone)
 */
export const useCreateVisit = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createVisit>>, TError,CreateVisitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createVisit>>,
        TError,
        CreateVisitMutationVariables,
        TContext
      > => {
      return useMutation(getCreateVisitMutationOptions(options), queryClient);
    }

export const getCreateWalkUpVisitUrl = () => {




  return `/api/visits/walk-up`
}

/**
 * @summary Front-desk walk-up create + check-in
 */
export const createWalkUpVisit = async (createVisitRequest: CreateVisitRequest, options?: Parameters<typeof customFetch>[1]): Promise<Visit> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Visit>(getCreateWalkUpVisitUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createVisitRequest)
  }
);}





export const getCreateWalkUpVisitMutationKey = () => ['createWalkUpVisit'] as const;

export const getCreateWalkUpVisitMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWalkUpVisit>>, TError,CreateWalkUpVisitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createWalkUpVisit>>, TError,CreateWalkUpVisitMutationVariables, TContext> => {

const mutationKey = getCreateWalkUpVisitMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createWalkUpVisit>>, CreateWalkUpVisitMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createWalkUpVisit(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateWalkUpVisitMutationResult = NonNullable<Awaited<ReturnType<typeof createWalkUpVisit>>>
    export type CreateWalkUpVisitMutationBody = CreateVisitRequest
    export type CreateWalkUpVisitMutationError = Error
    export type CreateWalkUpVisitMutationVariables = {data: CreateVisitRequest}

    /**
 * @summary Front-desk walk-up create + check-in
 */
export const useCreateWalkUpVisit = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWalkUpVisit>>, TError,CreateWalkUpVisitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createWalkUpVisit>>,
        TError,
        CreateWalkUpVisitMutationVariables,
        TContext
      > => {
      return useMutation(getCreateWalkUpVisitMutationOptions(options), queryClient);
    }

export const getUpdateVisitUrl = (id: string,) => {




  return `/api/visits/${id}`
}

/**
 * @summary Update details of an expected or checked-in visit
 */
export const updateVisit = async (id: string,
    updateVisitRequest: UpdateVisitRequest, options?: Parameters<typeof customFetch>[1]): Promise<Visit> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<Visit>(getUpdateVisitUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateVisitRequest)
  }
);}





export const getUpdateVisitMutationKey = () => ['updateVisit'] as const;

export const getUpdateVisitMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateVisit>>, TError,UpdateVisitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateVisit>>, TError,UpdateVisitMutationVariables, TContext> => {

const mutationKey = getUpdateVisitMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateVisit>>, UpdateVisitMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateVisit(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateVisitMutationResult = NonNullable<Awaited<ReturnType<typeof updateVisit>>>
    export type UpdateVisitMutationBody = UpdateVisitRequest
    export type UpdateVisitMutationError = Error
    export type UpdateVisitMutationVariables = {id: string;data: UpdateVisitRequest}

    /**
 * @summary Update details of an expected or checked-in visit
 */
export const useUpdateVisit = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateVisit>>, TError,UpdateVisitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateVisit>>,
        TError,
        UpdateVisitMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateVisitMutationOptions(options), queryClient);
    }

export const getGetVisitUrl = (id: string,) => {




  return `/api/visits/${id}`
}

/**
 * @summary Get a visit by id
 */
export const getVisit = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Visit> => {

  return customFetch<Visit>(getGetVisitUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetVisitQueryKey = (id: string,) => {
    return [
    `/api/visits/${id}`
    ] as const;
    }


export const getGetVisitQueryOptions = <TData = Awaited<ReturnType<typeof getVisit>>, TError = Error>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisit>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetVisitQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getVisit>>> = ({ signal }) => getVisit(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getVisit>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetVisitQueryResult = NonNullable<Awaited<ReturnType<typeof getVisit>>>
export type GetVisitQueryError = Error


export function useGetVisit<TData = Awaited<ReturnType<typeof getVisit>>, TError = Error>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisit>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getVisit>>,
          TError,
          Awaited<ReturnType<typeof getVisit>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetVisit<TData = Awaited<ReturnType<typeof getVisit>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisit>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getVisit>>,
          TError,
          Awaited<ReturnType<typeof getVisit>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetVisit<TData = Awaited<ReturnType<typeof getVisit>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisit>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get a visit by id
 */

export function useGetVisit<TData = Awaited<ReturnType<typeof getVisit>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getVisit>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetVisitQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCancelVisitUrl = (id: string,) => {




  return `/api/visits/${id}/cancel`
}

/**
 * @summary Cancel an expected visit
 */
export const cancelVisit = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Visit> => {

  return customFetch<Visit>(getCancelVisitUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCancelVisitMutationKey = () => ['cancelVisit'] as const;

export const getCancelVisitMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelVisit>>, TError,CancelVisitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof cancelVisit>>, TError,CancelVisitMutationVariables, TContext> => {

const mutationKey = getCancelVisitMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof cancelVisit>>, CancelVisitMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  cancelVisit(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CancelVisitMutationResult = NonNullable<Awaited<ReturnType<typeof cancelVisit>>>

    export type CancelVisitMutationError = Error
    export type CancelVisitMutationVariables = {id: string}

    /**
 * @summary Cancel an expected visit
 */
export const useCancelVisit = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof cancelVisit>>, TError,CancelVisitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof cancelVisit>>,
        TError,
        CancelVisitMutationVariables,
        TContext
      > => {
      return useMutation(getCancelVisitMutationOptions(options), queryClient);
    }

export const getCheckInVisitUrl = (id: string,) => {




  return `/api/visits/${id}/check-in`
}

/**
 * @summary Front-desk check-in (notifies host)
 */
export const checkInVisit = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Visit> => {

  return customFetch<Visit>(getCheckInVisitUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCheckInVisitMutationKey = () => ['checkInVisit'] as const;

export const getCheckInVisitMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof checkInVisit>>, TError,CheckInVisitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof checkInVisit>>, TError,CheckInVisitMutationVariables, TContext> => {

const mutationKey = getCheckInVisitMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof checkInVisit>>, CheckInVisitMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  checkInVisit(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CheckInVisitMutationResult = NonNullable<Awaited<ReturnType<typeof checkInVisit>>>

    export type CheckInVisitMutationError = Error
    export type CheckInVisitMutationVariables = {id: string}

    /**
 * @summary Front-desk check-in (notifies host)
 */
export const useCheckInVisit = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof checkInVisit>>, TError,CheckInVisitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof checkInVisit>>,
        TError,
        CheckInVisitMutationVariables,
        TContext
      > => {
      return useMutation(getCheckInVisitMutationOptions(options), queryClient);
    }

export const getCheckOutVisitUrl = (id: string,) => {




  return `/api/visits/${id}/check-out`
}

/**
 * @summary Front-desk check-out
 */
export const checkOutVisit = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Visit> => {

  return customFetch<Visit>(getCheckOutVisitUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getCheckOutVisitMutationKey = () => ['checkOutVisit'] as const;

export const getCheckOutVisitMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof checkOutVisit>>, TError,CheckOutVisitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof checkOutVisit>>, TError,CheckOutVisitMutationVariables, TContext> => {

const mutationKey = getCheckOutVisitMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof checkOutVisit>>, CheckOutVisitMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  checkOutVisit(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CheckOutVisitMutationResult = NonNullable<Awaited<ReturnType<typeof checkOutVisit>>>

    export type CheckOutVisitMutationError = Error
    export type CheckOutVisitMutationVariables = {id: string}

    /**
 * @summary Front-desk check-out
 */
export const useCheckOutVisit = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof checkOutVisit>>, TError,CheckOutVisitMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof checkOutVisit>>,
        TError,
        CheckOutVisitMutationVariables,
        TContext
      > => {
      return useMutation(getCheckOutVisitMutationOptions(options), queryClient);
    }

export const getListAdminVisitsUrl = (params?: ListAdminVisitsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/visits?${stringifiedParams}` : `/api/admin/visits`
}

/**
 * Requires visitors:admin. Defaults to full history within filters.
 * @summary Admin visitor log with optional CSV export
 */
export const listAdminVisits = async (params?: ListAdminVisitsParams, options?: Parameters<typeof customFetch>[1]): Promise<VisitListResponse | string> => {

  return customFetch<VisitListResponse | string>(getListAdminVisitsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListAdminVisitsQueryKey = (params?: ListAdminVisitsParams,) => {
    return [
    `/api/admin/visits`, ...(params ? [params] : [])
    ] as const;
    }


export const getListAdminVisitsQueryOptions = <TData = Awaited<ReturnType<typeof listAdminVisits>>, TError = Error>(params?: ListAdminVisitsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAdminVisits>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListAdminVisitsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listAdminVisits>>> = ({ signal }) => listAdminVisits(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listAdminVisits>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListAdminVisitsQueryResult = NonNullable<Awaited<ReturnType<typeof listAdminVisits>>>
export type ListAdminVisitsQueryError = Error


export function useListAdminVisits<TData = Awaited<ReturnType<typeof listAdminVisits>>, TError = Error>(
 params: undefined |  ListAdminVisitsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAdminVisits>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAdminVisits>>,
          TError,
          Awaited<ReturnType<typeof listAdminVisits>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListAdminVisits<TData = Awaited<ReturnType<typeof listAdminVisits>>, TError = Error>(
 params?: ListAdminVisitsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAdminVisits>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAdminVisits>>,
          TError,
          Awaited<ReturnType<typeof listAdminVisits>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListAdminVisits<TData = Awaited<ReturnType<typeof listAdminVisits>>, TError = Error>(
 params?: ListAdminVisitsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAdminVisits>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Admin visitor log with optional CSV export
 */

export function useListAdminVisits<TData = Awaited<ReturnType<typeof listAdminVisits>>, TError = Error>(
 params?: ListAdminVisitsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listAdminVisits>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListAdminVisitsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListMyFavouritesUrl = (params?: ListMyFavouritesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/me/favourites?${stringifiedParams}` : `/api/me/favourites`
}

/**
 * Returns favourite spaces for the caller (newest first), with optional availability annotation for a booking window. Per-user; never includes other users' favourites.
 * @summary List the signed-in user's favourite spaces
 */
export const listMyFavourites = async (params?: ListMyFavouritesParams, options?: Parameters<typeof customFetch>[1]): Promise<FavouriteListResponse> => {

  return customFetch<FavouriteListResponse>(getListMyFavouritesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMyFavouritesQueryKey = (params?: ListMyFavouritesParams,) => {
    return [
    `/api/me/favourites`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMyFavouritesQueryOptions = <TData = Awaited<ReturnType<typeof listMyFavourites>>, TError = Error>(params?: ListMyFavouritesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyFavourites>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMyFavouritesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyFavourites>>> = ({ signal }) => listMyFavourites(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMyFavourites>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMyFavouritesQueryResult = NonNullable<Awaited<ReturnType<typeof listMyFavourites>>>
export type ListMyFavouritesQueryError = Error


export function useListMyFavourites<TData = Awaited<ReturnType<typeof listMyFavourites>>, TError = Error>(
 params: undefined |  ListMyFavouritesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyFavourites>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyFavourites>>,
          TError,
          Awaited<ReturnType<typeof listMyFavourites>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyFavourites<TData = Awaited<ReturnType<typeof listMyFavourites>>, TError = Error>(
 params?: ListMyFavouritesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyFavourites>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyFavourites>>,
          TError,
          Awaited<ReturnType<typeof listMyFavourites>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyFavourites<TData = Awaited<ReturnType<typeof listMyFavourites>>, TError = Error>(
 params?: ListMyFavouritesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyFavourites>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List the signed-in user's favourite spaces
 */

export function useListMyFavourites<TData = Awaited<ReturnType<typeof listMyFavourites>>, TError = Error>(
 params?: ListMyFavouritesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyFavourites>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMyFavouritesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPutMyFavouriteUrl = (spaceId: string,) => {




  return `/api/me/favourites/${spaceId}`
}

/**
 * Favourites the given space for the caller. Idempotent when already favourited. Enforces a per-user cap (20).
 * @summary Add a space to the signed-in user's favourites
 */
export const putMyFavourite = async (spaceId: string, options?: Parameters<typeof customFetch>[1]): Promise<FavouriteItem> => {

  return customFetch<FavouriteItem>(getPutMyFavouriteUrl(spaceId),
  {
    ...options,
    method: 'PUT'


  }
);}





export const getPutMyFavouriteMutationKey = () => ['putMyFavourite'] as const;

export const getPutMyFavouriteMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putMyFavourite>>, TError,PutMyFavouriteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof putMyFavourite>>, TError,PutMyFavouriteMutationVariables, TContext> => {

const mutationKey = getPutMyFavouriteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof putMyFavourite>>, PutMyFavouriteMutationVariables> = (props) => {
          const {spaceId} = props ?? {};

          return  putMyFavourite(spaceId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PutMyFavouriteMutationResult = NonNullable<Awaited<ReturnType<typeof putMyFavourite>>>

    export type PutMyFavouriteMutationError = Error
    export type PutMyFavouriteMutationVariables = {spaceId: string}

    /**
 * @summary Add a space to the signed-in user's favourites
 */
export const usePutMyFavourite = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putMyFavourite>>, TError,PutMyFavouriteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof putMyFavourite>>,
        TError,
        PutMyFavouriteMutationVariables,
        TContext
      > => {
      return useMutation(getPutMyFavouriteMutationOptions(options), queryClient);
    }

export const getDeleteMyFavouriteUrl = (spaceId: string,) => {




  return `/api/me/favourites/${spaceId}`
}

/**
 * Idempotent — returns 204 even when the favourite was not present.
 * @summary Remove a space from the signed-in user's favourites
 */
export const deleteMyFavourite = async (spaceId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteMyFavouriteUrl(spaceId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteMyFavouriteMutationKey = () => ['deleteMyFavourite'] as const;

export const getDeleteMyFavouriteMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteMyFavourite>>, TError,DeleteMyFavouriteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteMyFavourite>>, TError,DeleteMyFavouriteMutationVariables, TContext> => {

const mutationKey = getDeleteMyFavouriteMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteMyFavourite>>, DeleteMyFavouriteMutationVariables> = (props) => {
          const {spaceId} = props ?? {};

          return  deleteMyFavourite(spaceId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteMyFavouriteMutationResult = NonNullable<Awaited<ReturnType<typeof deleteMyFavourite>>>

    export type DeleteMyFavouriteMutationError = Error
    export type DeleteMyFavouriteMutationVariables = {spaceId: string}

    /**
 * @summary Remove a space from the signed-in user's favourites
 */
export const useDeleteMyFavourite = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteMyFavourite>>, TError,DeleteMyFavouriteMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteMyFavourite>>,
        TError,
        DeleteMyFavouriteMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteMyFavouriteMutationOptions(options), queryClient);
    }

export const getListMyBookAgainUrl = (params?: ListMyBookAgainParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/me/book-again?${stringifiedParams}` : `/api/me/book-again`
}

/**
 * Returns spaces the caller booked most recently/frequently (last 90 days), one row per space, with optional availability for a chosen window. Never auto-books — powered by the Book page Book-again rail.
 * @summary Recent/frequent spaces for one-tap book-again
 */
export const listMyBookAgain = async (params?: ListMyBookAgainParams, options?: Parameters<typeof customFetch>[1]): Promise<BookAgainListResponse> => {

  return customFetch<BookAgainListResponse>(getListMyBookAgainUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMyBookAgainQueryKey = (params?: ListMyBookAgainParams,) => {
    return [
    `/api/me/book-again`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMyBookAgainQueryOptions = <TData = Awaited<ReturnType<typeof listMyBookAgain>>, TError = Error>(params?: ListMyBookAgainParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyBookAgain>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMyBookAgainQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyBookAgain>>> = ({ signal }) => listMyBookAgain(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMyBookAgain>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMyBookAgainQueryResult = NonNullable<Awaited<ReturnType<typeof listMyBookAgain>>>
export type ListMyBookAgainQueryError = Error


export function useListMyBookAgain<TData = Awaited<ReturnType<typeof listMyBookAgain>>, TError = Error>(
 params: undefined |  ListMyBookAgainParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyBookAgain>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyBookAgain>>,
          TError,
          Awaited<ReturnType<typeof listMyBookAgain>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyBookAgain<TData = Awaited<ReturnType<typeof listMyBookAgain>>, TError = Error>(
 params?: ListMyBookAgainParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyBookAgain>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyBookAgain>>,
          TError,
          Awaited<ReturnType<typeof listMyBookAgain>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyBookAgain<TData = Awaited<ReturnType<typeof listMyBookAgain>>, TError = Error>(
 params?: ListMyBookAgainParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyBookAgain>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Recent/frequent spaces for one-tap book-again
 */

export function useListMyBookAgain<TData = Awaited<ReturnType<typeof listMyBookAgain>>, TError = Error>(
 params?: ListMyBookAgainParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyBookAgain>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMyBookAgainQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListMyBookingSuggestionsUrl = (params?: ListMyBookingSuggestionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/me/booking-suggestions?${stringifiedParams}` : `/api/me/booking-suggestions`
}

/**
 * Derives patterns from the caller's own bookings (last 90 days, frequency + recency) for space × weekday × start-hour. Patterns need at least 3 matching bookings. Dismissed patterns are omitted. Suggestions never auto-book.
 * @summary Habit-based booking suggestions for the signed-in user
 */
export const listMyBookingSuggestions = async (params?: ListMyBookingSuggestionsParams, options?: Parameters<typeof customFetch>[1]): Promise<BookingSuggestionListResponse> => {

  return customFetch<BookingSuggestionListResponse>(getListMyBookingSuggestionsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMyBookingSuggestionsQueryKey = (params?: ListMyBookingSuggestionsParams,) => {
    return [
    `/api/me/booking-suggestions`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMyBookingSuggestionsQueryOptions = <TData = Awaited<ReturnType<typeof listMyBookingSuggestions>>, TError = Error>(params?: ListMyBookingSuggestionsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyBookingSuggestions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMyBookingSuggestionsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyBookingSuggestions>>> = ({ signal }) => listMyBookingSuggestions(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMyBookingSuggestions>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMyBookingSuggestionsQueryResult = NonNullable<Awaited<ReturnType<typeof listMyBookingSuggestions>>>
export type ListMyBookingSuggestionsQueryError = Error


export function useListMyBookingSuggestions<TData = Awaited<ReturnType<typeof listMyBookingSuggestions>>, TError = Error>(
 params: undefined |  ListMyBookingSuggestionsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyBookingSuggestions>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyBookingSuggestions>>,
          TError,
          Awaited<ReturnType<typeof listMyBookingSuggestions>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyBookingSuggestions<TData = Awaited<ReturnType<typeof listMyBookingSuggestions>>, TError = Error>(
 params?: ListMyBookingSuggestionsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyBookingSuggestions>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyBookingSuggestions>>,
          TError,
          Awaited<ReturnType<typeof listMyBookingSuggestions>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyBookingSuggestions<TData = Awaited<ReturnType<typeof listMyBookingSuggestions>>, TError = Error>(
 params?: ListMyBookingSuggestionsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyBookingSuggestions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Habit-based booking suggestions for the signed-in user
 */

export function useListMyBookingSuggestions<TData = Awaited<ReturnType<typeof listMyBookingSuggestions>>, TError = Error>(
 params?: ListMyBookingSuggestionsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyBookingSuggestions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMyBookingSuggestionsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDismissMyBookingSuggestionUrl = (key: string,) => {




  return `/api/me/booking-suggestions/${key}/dismiss`
}

/**
 * Hides the pattern for the signed-in user across sessions. Key is the patternKey from listMyBookingSuggestions (URL-encode before use).
 * @summary Persistently dismiss a habit suggestion pattern
 */
export const dismissMyBookingSuggestion = async (key: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDismissMyBookingSuggestionUrl(key),
  {
    ...options,
    method: 'POST'


  }
);}





export const getDismissMyBookingSuggestionMutationKey = () => ['dismissMyBookingSuggestion'] as const;

export const getDismissMyBookingSuggestionMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof dismissMyBookingSuggestion>>, TError,DismissMyBookingSuggestionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof dismissMyBookingSuggestion>>, TError,DismissMyBookingSuggestionMutationVariables, TContext> => {

const mutationKey = getDismissMyBookingSuggestionMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof dismissMyBookingSuggestion>>, DismissMyBookingSuggestionMutationVariables> = (props) => {
          const {key} = props ?? {};

          return  dismissMyBookingSuggestion(key,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DismissMyBookingSuggestionMutationResult = NonNullable<Awaited<ReturnType<typeof dismissMyBookingSuggestion>>>

    export type DismissMyBookingSuggestionMutationError = Error
    export type DismissMyBookingSuggestionMutationVariables = {key: string}

    /**
 * @summary Persistently dismiss a habit suggestion pattern
 */
export const useDismissMyBookingSuggestion = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof dismissMyBookingSuggestion>>, TError,DismissMyBookingSuggestionMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof dismissMyBookingSuggestion>>,
        TError,
        DismissMyBookingSuggestionMutationVariables,
        TContext
      > => {
      return useMutation(getDismissMyBookingSuggestionMutationOptions(options), queryClient);
    }

export const getListMyWorkgroupsUrl = () => {




  return `/api/workgroups`
}

/**
 * @summary List workgroups the signed-in user belongs to
 */
export const listMyWorkgroups = async ( options?: Parameters<typeof customFetch>[1]): Promise<WorkgroupListResponse> => {

  return customFetch<WorkgroupListResponse>(getListMyWorkgroupsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMyWorkgroupsQueryKey = () => {
    return [
    `/api/workgroups`
    ] as const;
    }


export const getListMyWorkgroupsQueryOptions = <TData = Awaited<ReturnType<typeof listMyWorkgroups>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyWorkgroups>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMyWorkgroupsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyWorkgroups>>> = ({ signal }) => listMyWorkgroups({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMyWorkgroups>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMyWorkgroupsQueryResult = NonNullable<Awaited<ReturnType<typeof listMyWorkgroups>>>
export type ListMyWorkgroupsQueryError = Error


export function useListMyWorkgroups<TData = Awaited<ReturnType<typeof listMyWorkgroups>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyWorkgroups>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyWorkgroups>>,
          TError,
          Awaited<ReturnType<typeof listMyWorkgroups>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyWorkgroups<TData = Awaited<ReturnType<typeof listMyWorkgroups>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyWorkgroups>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyWorkgroups>>,
          TError,
          Awaited<ReturnType<typeof listMyWorkgroups>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyWorkgroups<TData = Awaited<ReturnType<typeof listMyWorkgroups>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyWorkgroups>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List workgroups the signed-in user belongs to
 */

export function useListMyWorkgroups<TData = Awaited<ReturnType<typeof listMyWorkgroups>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyWorkgroups>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMyWorkgroupsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateWorkgroupUrl = () => {




  return `/api/workgroups`
}

/**
 * @summary Create a workgroup (caller becomes owner)
 */
export const createWorkgroup = async (createWorkgroupRequest: CreateWorkgroupRequest, options?: Parameters<typeof customFetch>[1]): Promise<WorkgroupDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WorkgroupDetail>(getCreateWorkgroupUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createWorkgroupRequest)
  }
);}





export const getCreateWorkgroupMutationKey = () => ['createWorkgroup'] as const;

export const getCreateWorkgroupMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWorkgroup>>, TError,CreateWorkgroupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createWorkgroup>>, TError,CreateWorkgroupMutationVariables, TContext> => {

const mutationKey = getCreateWorkgroupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createWorkgroup>>, CreateWorkgroupMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createWorkgroup(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateWorkgroupMutationResult = NonNullable<Awaited<ReturnType<typeof createWorkgroup>>>
    export type CreateWorkgroupMutationBody = CreateWorkgroupRequest
    export type CreateWorkgroupMutationError = Error
    export type CreateWorkgroupMutationVariables = {data: CreateWorkgroupRequest}

    /**
 * @summary Create a workgroup (caller becomes owner)
 */
export const useCreateWorkgroup = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWorkgroup>>, TError,CreateWorkgroupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createWorkgroup>>,
        TError,
        CreateWorkgroupMutationVariables,
        TContext
      > => {
      return useMutation(getCreateWorkgroupMutationOptions(options), queryClient);
    }

export const getGetWorkgroupUrl = (id: string,) => {




  return `/api/workgroups/${id}`
}

/**
 * @summary Get workgroup detail (members + guidance)
 */
export const getWorkgroup = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<WorkgroupDetail> => {

  return customFetch<WorkgroupDetail>(getGetWorkgroupUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWorkgroupQueryKey = (id: string,) => {
    return [
    `/api/workgroups/${id}`
    ] as const;
    }


export const getGetWorkgroupQueryOptions = <TData = Awaited<ReturnType<typeof getWorkgroup>>, TError = Error>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkgroup>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWorkgroupQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkgroup>>> = ({ signal }) => getWorkgroup(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWorkgroup>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetWorkgroupQueryResult = NonNullable<Awaited<ReturnType<typeof getWorkgroup>>>
export type GetWorkgroupQueryError = Error


export function useGetWorkgroup<TData = Awaited<ReturnType<typeof getWorkgroup>>, TError = Error>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkgroup>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkgroup>>,
          TError,
          Awaited<ReturnType<typeof getWorkgroup>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetWorkgroup<TData = Awaited<ReturnType<typeof getWorkgroup>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkgroup>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkgroup>>,
          TError,
          Awaited<ReturnType<typeof getWorkgroup>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetWorkgroup<TData = Awaited<ReturnType<typeof getWorkgroup>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkgroup>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get workgroup detail (members + guidance)
 */

export function useGetWorkgroup<TData = Awaited<ReturnType<typeof getWorkgroup>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkgroup>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetWorkgroupQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateWorkgroupUrl = (id: string,) => {




  return `/api/workgroups/${id}`
}

/**
 * @summary Update workgroup name/description (owner only)
 */
export const updateWorkgroup = async (id: string,
    updateWorkgroupRequest: UpdateWorkgroupRequest, options?: Parameters<typeof customFetch>[1]): Promise<WorkgroupDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WorkgroupDetail>(getUpdateWorkgroupUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateWorkgroupRequest)
  }
);}





export const getUpdateWorkgroupMutationKey = () => ['updateWorkgroup'] as const;

export const getUpdateWorkgroupMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWorkgroup>>, TError,UpdateWorkgroupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateWorkgroup>>, TError,UpdateWorkgroupMutationVariables, TContext> => {

const mutationKey = getUpdateWorkgroupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateWorkgroup>>, UpdateWorkgroupMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateWorkgroup(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateWorkgroupMutationResult = NonNullable<Awaited<ReturnType<typeof updateWorkgroup>>>
    export type UpdateWorkgroupMutationBody = UpdateWorkgroupRequest
    export type UpdateWorkgroupMutationError = Error
    export type UpdateWorkgroupMutationVariables = {id: string;data: UpdateWorkgroupRequest}

    /**
 * @summary Update workgroup name/description (owner only)
 */
export const useUpdateWorkgroup = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWorkgroup>>, TError,UpdateWorkgroupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateWorkgroup>>,
        TError,
        UpdateWorkgroupMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateWorkgroupMutationOptions(options), queryClient);
    }

export const getDeleteWorkgroupUrl = (id: string,) => {




  return `/api/workgroups/${id}`
}

/**
 * @summary Delete a workgroup (owner only)
 */
export const deleteWorkgroup = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<DeleteWorkgroupResponse> => {

  return customFetch<DeleteWorkgroupResponse>(getDeleteWorkgroupUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteWorkgroupMutationKey = () => ['deleteWorkgroup'] as const;

export const getDeleteWorkgroupMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteWorkgroup>>, TError,DeleteWorkgroupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteWorkgroup>>, TError,DeleteWorkgroupMutationVariables, TContext> => {

const mutationKey = getDeleteWorkgroupMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteWorkgroup>>, DeleteWorkgroupMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteWorkgroup(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteWorkgroupMutationResult = NonNullable<Awaited<ReturnType<typeof deleteWorkgroup>>>

    export type DeleteWorkgroupMutationError = Error
    export type DeleteWorkgroupMutationVariables = {id: string}

    /**
 * @summary Delete a workgroup (owner only)
 */
export const useDeleteWorkgroup = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteWorkgroup>>, TError,DeleteWorkgroupMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteWorkgroup>>,
        TError,
        DeleteWorkgroupMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteWorkgroupMutationOptions(options), queryClient);
    }

export const getAddWorkgroupMemberUrl = (id: string,) => {




  return `/api/workgroups/${id}/members`
}

/**
 * @summary Add a member from the directory (owner only)
 */
export const addWorkgroupMember = async (id: string,
    addWorkgroupMemberRequest: AddWorkgroupMemberRequest, options?: Parameters<typeof customFetch>[1]): Promise<WorkgroupDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WorkgroupDetail>(getAddWorkgroupMemberUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(addWorkgroupMemberRequest)
  }
);}





export const getAddWorkgroupMemberMutationKey = () => ['addWorkgroupMember'] as const;

export const getAddWorkgroupMemberMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addWorkgroupMember>>, TError,AddWorkgroupMemberMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof addWorkgroupMember>>, TError,AddWorkgroupMemberMutationVariables, TContext> => {

const mutationKey = getAddWorkgroupMemberMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof addWorkgroupMember>>, AddWorkgroupMemberMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  addWorkgroupMember(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AddWorkgroupMemberMutationResult = NonNullable<Awaited<ReturnType<typeof addWorkgroupMember>>>
    export type AddWorkgroupMemberMutationBody = AddWorkgroupMemberRequest
    export type AddWorkgroupMemberMutationError = Error
    export type AddWorkgroupMemberMutationVariables = {id: string;data: AddWorkgroupMemberRequest}

    /**
 * @summary Add a member from the directory (owner only)
 */
export const useAddWorkgroupMember = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addWorkgroupMember>>, TError,AddWorkgroupMemberMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof addWorkgroupMember>>,
        TError,
        AddWorkgroupMemberMutationVariables,
        TContext
      > => {
      return useMutation(getAddWorkgroupMemberMutationOptions(options), queryClient);
    }

export const getRemoveWorkgroupMemberUrl = (id: string,
    userId: string,) => {




  return `/api/workgroups/${id}/members/${userId}`
}

/**
 * @summary Remove a member (owner) or leave the group (self)
 */
export const removeWorkgroupMember = async (id: string,
    userId: string, options?: Parameters<typeof customFetch>[1]): Promise<WorkgroupDetail | LeftWorkgroupResponse> => {

  return customFetch<WorkgroupDetail | LeftWorkgroupResponse>(getRemoveWorkgroupMemberUrl(id,userId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getRemoveWorkgroupMemberMutationKey = () => ['removeWorkgroupMember'] as const;

export const getRemoveWorkgroupMemberMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeWorkgroupMember>>, TError,RemoveWorkgroupMemberMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof removeWorkgroupMember>>, TError,RemoveWorkgroupMemberMutationVariables, TContext> => {

const mutationKey = getRemoveWorkgroupMemberMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof removeWorkgroupMember>>, RemoveWorkgroupMemberMutationVariables> = (props) => {
          const {id,userId} = props ?? {};

          return  removeWorkgroupMember(id,userId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RemoveWorkgroupMemberMutationResult = NonNullable<Awaited<ReturnType<typeof removeWorkgroupMember>>>

    export type RemoveWorkgroupMemberMutationError = Error
    export type RemoveWorkgroupMemberMutationVariables = {id: string;userId: string}

    /**
 * @summary Remove a member (owner) or leave the group (self)
 */
export const useRemoveWorkgroupMember = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeWorkgroupMember>>, TError,RemoveWorkgroupMemberMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof removeWorkgroupMember>>,
        TError,
        RemoveWorkgroupMemberMutationVariables,
        TContext
      > => {
      return useMutation(getRemoveWorkgroupMemberMutationOptions(options), queryClient);
    }

export const getGetWorkgroupWeekUrl = (id: string,
    params: GetWorkgroupWeekParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workgroups/${id}/week?${stringifiedParams}` : `/api/workgroups/${id}/week`
}

/**
 * Members only. Presence is privacy-trimmed; shared membership satisfies
 * the workgroups sharing level. Private subjects remain hidden.
 * @summary Team week presence grid for a workgroup
 */
export const getWorkgroupWeek = async (id: string,
    params: GetWorkgroupWeekParams, options?: Parameters<typeof customFetch>[1]): Promise<WorkgroupWeekResponse> => {

  return customFetch<WorkgroupWeekResponse>(getGetWorkgroupWeekUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWorkgroupWeekQueryKey = (id: string,
    params?: GetWorkgroupWeekParams,) => {
    return [
    `/api/workgroups/${id}/week`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetWorkgroupWeekQueryOptions = <TData = Awaited<ReturnType<typeof getWorkgroupWeek>>, TError = Error>(id: string,
    params: GetWorkgroupWeekParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkgroupWeek>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWorkgroupWeekQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkgroupWeek>>> = ({ signal }) => getWorkgroupWeek(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWorkgroupWeek>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetWorkgroupWeekQueryResult = NonNullable<Awaited<ReturnType<typeof getWorkgroupWeek>>>
export type GetWorkgroupWeekQueryError = Error


export function useGetWorkgroupWeek<TData = Awaited<ReturnType<typeof getWorkgroupWeek>>, TError = Error>(
 id: string,
    params: GetWorkgroupWeekParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkgroupWeek>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkgroupWeek>>,
          TError,
          Awaited<ReturnType<typeof getWorkgroupWeek>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetWorkgroupWeek<TData = Awaited<ReturnType<typeof getWorkgroupWeek>>, TError = Error>(
 id: string,
    params: GetWorkgroupWeekParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkgroupWeek>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkgroupWeek>>,
          TError,
          Awaited<ReturnType<typeof getWorkgroupWeek>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetWorkgroupWeek<TData = Awaited<ReturnType<typeof getWorkgroupWeek>>, TError = Error>(
 id: string,
    params: GetWorkgroupWeekParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkgroupWeek>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Team week presence grid for a workgroup
 */

export function useGetWorkgroupWeek<TData = Awaited<ReturnType<typeof getWorkgroupWeek>>, TError = Error>(
 id: string,
    params: GetWorkgroupWeekParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkgroupWeek>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetWorkgroupWeekQueryOptions(id,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSetWorkgroupGuidanceUrl = (id: string,) => {




  return `/api/workgroups/${id}/guidance`
}

/**
 * @summary Publish recommended office days (owner only)
 */
export const setWorkgroupGuidance = async (id: string,
    setWorkgroupGuidanceRequest: SetWorkgroupGuidanceRequest, options?: Parameters<typeof customFetch>[1]): Promise<WorkgroupGuidance> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WorkgroupGuidance>(getSetWorkgroupGuidanceUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(setWorkgroupGuidanceRequest)
  }
);}





export const getSetWorkgroupGuidanceMutationKey = () => ['setWorkgroupGuidance'] as const;

export const getSetWorkgroupGuidanceMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setWorkgroupGuidance>>, TError,SetWorkgroupGuidanceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setWorkgroupGuidance>>, TError,SetWorkgroupGuidanceMutationVariables, TContext> => {

const mutationKey = getSetWorkgroupGuidanceMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setWorkgroupGuidance>>, SetWorkgroupGuidanceMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  setWorkgroupGuidance(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetWorkgroupGuidanceMutationResult = NonNullable<Awaited<ReturnType<typeof setWorkgroupGuidance>>>
    export type SetWorkgroupGuidanceMutationBody = SetWorkgroupGuidanceRequest
    export type SetWorkgroupGuidanceMutationError = Error
    export type SetWorkgroupGuidanceMutationVariables = {id: string;data: SetWorkgroupGuidanceRequest}

    /**
 * @summary Publish recommended office days (owner only)
 */
export const useSetWorkgroupGuidance = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setWorkgroupGuidance>>, TError,SetWorkgroupGuidanceMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setWorkgroupGuidance>>,
        TError,
        SetWorkgroupGuidanceMutationVariables,
        TContext
      > => {
      return useMutation(getSetWorkgroupGuidanceMutationOptions(options), queryClient);
    }

export const getGetWorkgroupBookingsUrl = (id: string,
    params: GetWorkgroupBookingsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/workgroups/${id}/bookings?${stringifiedParams}` : `/api/workgroups/${id}/bookings`
}

/**
 * Owner-only. Returns privacy-trimmed desk bookings for each team member.
 * Members with `private` sharing level are invisible regardless of team
 * membership.
 * @summary Team member desk bookings for a date range (owner only)
 */
export const getWorkgroupBookings = async (id: string,
    params: GetWorkgroupBookingsParams, options?: Parameters<typeof customFetch>[1]): Promise<TeamBookingsResponse> => {

  return customFetch<TeamBookingsResponse>(getGetWorkgroupBookingsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetWorkgroupBookingsQueryKey = (id: string,
    params?: GetWorkgroupBookingsParams,) => {
    return [
    `/api/workgroups/${id}/bookings`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetWorkgroupBookingsQueryOptions = <TData = Awaited<ReturnType<typeof getWorkgroupBookings>>, TError = Error>(id: string,
    params: GetWorkgroupBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkgroupBookings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetWorkgroupBookingsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getWorkgroupBookings>>> = ({ signal }) => getWorkgroupBookings(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getWorkgroupBookings>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetWorkgroupBookingsQueryResult = NonNullable<Awaited<ReturnType<typeof getWorkgroupBookings>>>
export type GetWorkgroupBookingsQueryError = Error


export function useGetWorkgroupBookings<TData = Awaited<ReturnType<typeof getWorkgroupBookings>>, TError = Error>(
 id: string,
    params: GetWorkgroupBookingsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkgroupBookings>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkgroupBookings>>,
          TError,
          Awaited<ReturnType<typeof getWorkgroupBookings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetWorkgroupBookings<TData = Awaited<ReturnType<typeof getWorkgroupBookings>>, TError = Error>(
 id: string,
    params: GetWorkgroupBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkgroupBookings>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getWorkgroupBookings>>,
          TError,
          Awaited<ReturnType<typeof getWorkgroupBookings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetWorkgroupBookings<TData = Awaited<ReturnType<typeof getWorkgroupBookings>>, TError = Error>(
 id: string,
    params: GetWorkgroupBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkgroupBookings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Team member desk bookings for a date range (owner only)
 */

export function useGetWorkgroupBookings<TData = Awaited<ReturnType<typeof getWorkgroupBookings>>, TError = Error>(
 id: string,
    params: GetWorkgroupBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getWorkgroupBookings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetWorkgroupBookingsQueryOptions(id,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetUsersPresenceUrl = (params: GetUsersPresenceParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/presence/users?${stringifiedParams}` : `/api/presence/users`
}

/**
 * Returns materialised presence only when the subject's sharing level
 * allows the viewer to see it. Private users are never visible to others.
 * @summary Privacy-trimmed presence for one or more users
 */
export const getUsersPresence = async (params: GetUsersPresenceParams, options?: Parameters<typeof customFetch>[1]): Promise<UsersPresenceResponse> => {

  return customFetch<UsersPresenceResponse>(getGetUsersPresenceUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetUsersPresenceQueryKey = (params?: GetUsersPresenceParams,) => {
    return [
    `/api/presence/users`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetUsersPresenceQueryOptions = <TData = Awaited<ReturnType<typeof getUsersPresence>>, TError = Error>(params: GetUsersPresenceParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsersPresence>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetUsersPresenceQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getUsersPresence>>> = ({ signal }) => getUsersPresence(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getUsersPresence>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetUsersPresenceQueryResult = NonNullable<Awaited<ReturnType<typeof getUsersPresence>>>
export type GetUsersPresenceQueryError = Error


export function useGetUsersPresence<TData = Awaited<ReturnType<typeof getUsersPresence>>, TError = Error>(
 params: GetUsersPresenceParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsersPresence>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUsersPresence>>,
          TError,
          Awaited<ReturnType<typeof getUsersPresence>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetUsersPresence<TData = Awaited<ReturnType<typeof getUsersPresence>>, TError = Error>(
 params: GetUsersPresenceParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsersPresence>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUsersPresence>>,
          TError,
          Awaited<ReturnType<typeof getUsersPresence>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetUsersPresence<TData = Awaited<ReturnType<typeof getUsersPresence>>, TError = Error>(
 params: GetUsersPresenceParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsersPresence>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Privacy-trimmed presence for one or more users
 */

export function useGetUsersPresence<TData = Awaited<ReturnType<typeof getUsersPresence>>, TError = Error>(
 params: GetUsersPresenceParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUsersPresence>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetUsersPresenceQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSearchColleaguesUrl = (params: SearchColleaguesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/colleagues?${stringifiedParams}` : `/api/colleagues`
}

/**
 * Matches directory users by name or email. Presence status and building
 * are included only when the subject's sharing level allows the viewer.
 * Private users appear in results without presence fields.
 * @summary Search people with privacy-trimmed presence and same-building flag
 */
export const searchColleagues = async (params: SearchColleaguesParams, options?: Parameters<typeof customFetch>[1]): Promise<ColleagueSearchResponse> => {

  return customFetch<ColleagueSearchResponse>(getSearchColleaguesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getSearchColleaguesQueryKey = (params?: SearchColleaguesParams,) => {
    return [
    `/api/colleagues`, ...(params ? [params] : [])
    ] as const;
    }


export const getSearchColleaguesQueryOptions = <TData = Awaited<ReturnType<typeof searchColleagues>>, TError = Error>(params: SearchColleaguesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchColleagues>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getSearchColleaguesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof searchColleagues>>> = ({ signal }) => searchColleagues(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof searchColleagues>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type SearchColleaguesQueryResult = NonNullable<Awaited<ReturnType<typeof searchColleagues>>>
export type SearchColleaguesQueryError = Error


export function useSearchColleagues<TData = Awaited<ReturnType<typeof searchColleagues>>, TError = Error>(
 params: SearchColleaguesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchColleagues>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchColleagues>>,
          TError,
          Awaited<ReturnType<typeof searchColleagues>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSearchColleagues<TData = Awaited<ReturnType<typeof searchColleagues>>, TError = Error>(
 params: SearchColleaguesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchColleagues>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchColleagues>>,
          TError,
          Awaited<ReturnType<typeof searchColleagues>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSearchColleagues<TData = Awaited<ReturnType<typeof searchColleagues>>, TError = Error>(
 params: SearchColleaguesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchColleagues>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Search people with privacy-trimmed presence and same-building flag
 */

export function useSearchColleagues<TData = Awaited<ReturnType<typeof searchColleagues>>, TError = Error>(
 params: SearchColleaguesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchColleagues>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getSearchColleaguesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetColleagueWeekUrl = (userId: string,
    params?: GetColleagueWeekParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/colleagues/${userId}/week?${stringifiedParams}` : `/api/colleagues/${userId}/week`
}

/**
 * Returns the subject's week plan only when privacy allows. Shared
 * workgroup names are always listed when membership overlaps.
 * @summary Colleague week presence, shared workgroups, and same-building flag
 */
export const getColleagueWeek = async (userId: string,
    params?: GetColleagueWeekParams, options?: Parameters<typeof customFetch>[1]): Promise<ColleagueWeekResponse> => {

  return customFetch<ColleagueWeekResponse>(getGetColleagueWeekUrl(userId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetColleagueWeekQueryKey = (userId: string,
    params?: GetColleagueWeekParams,) => {
    return [
    `/api/colleagues/${userId}/week`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetColleagueWeekQueryOptions = <TData = Awaited<ReturnType<typeof getColleagueWeek>>, TError = Error>(userId: string,
    params?: GetColleagueWeekParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getColleagueWeek>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetColleagueWeekQueryKey(userId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getColleagueWeek>>> = ({ signal }) => getColleagueWeek(userId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: userId !== null && userId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getColleagueWeek>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetColleagueWeekQueryResult = NonNullable<Awaited<ReturnType<typeof getColleagueWeek>>>
export type GetColleagueWeekQueryError = Error


export function useGetColleagueWeek<TData = Awaited<ReturnType<typeof getColleagueWeek>>, TError = Error>(
 userId: string,
    params: undefined |  GetColleagueWeekParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getColleagueWeek>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getColleagueWeek>>,
          TError,
          Awaited<ReturnType<typeof getColleagueWeek>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetColleagueWeek<TData = Awaited<ReturnType<typeof getColleagueWeek>>, TError = Error>(
 userId: string,
    params?: GetColleagueWeekParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getColleagueWeek>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getColleagueWeek>>,
          TError,
          Awaited<ReturnType<typeof getColleagueWeek>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetColleagueWeek<TData = Awaited<ReturnType<typeof getColleagueWeek>>, TError = Error>(
 userId: string,
    params?: GetColleagueWeekParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getColleagueWeek>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Colleague week presence, shared workgroups, and same-building flag
 */

export function useGetColleagueWeek<TData = Awaited<ReturnType<typeof getColleagueWeek>>, TError = Error>(
 userId: string,
    params?: GetColleagueWeekParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getColleagueWeek>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetColleagueWeekQueryOptions(userId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListVisibleColleagueDesksUrl = (params: ListVisibleColleagueDesksParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/colleagues/desk-locations?${stringifiedParams}` : `/api/colleagues/desk-locations`
}

/**
 * @summary Privacy-filtered booked desks for map and colleague views
 */
export const listVisibleColleagueDesks = async (params: ListVisibleColleagueDesksParams, options?: Parameters<typeof customFetch>[1]): Promise<VisibleColleagueDesksResponse> => {

  return customFetch<VisibleColleagueDesksResponse>(getListVisibleColleagueDesksUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListVisibleColleagueDesksQueryKey = (params?: ListVisibleColleagueDesksParams,) => {
    return [
    `/api/colleagues/desk-locations`, ...(params ? [params] : [])
    ] as const;
    }


export const getListVisibleColleagueDesksQueryOptions = <TData = Awaited<ReturnType<typeof listVisibleColleagueDesks>>, TError = Error>(params: ListVisibleColleagueDesksParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listVisibleColleagueDesks>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListVisibleColleagueDesksQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listVisibleColleagueDesks>>> = ({ signal }) => listVisibleColleagueDesks(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listVisibleColleagueDesks>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListVisibleColleagueDesksQueryResult = NonNullable<Awaited<ReturnType<typeof listVisibleColleagueDesks>>>
export type ListVisibleColleagueDesksQueryError = Error


export function useListVisibleColleagueDesks<TData = Awaited<ReturnType<typeof listVisibleColleagueDesks>>, TError = Error>(
 params: ListVisibleColleagueDesksParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listVisibleColleagueDesks>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listVisibleColleagueDesks>>,
          TError,
          Awaited<ReturnType<typeof listVisibleColleagueDesks>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListVisibleColleagueDesks<TData = Awaited<ReturnType<typeof listVisibleColleagueDesks>>, TError = Error>(
 params: ListVisibleColleagueDesksParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listVisibleColleagueDesks>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listVisibleColleagueDesks>>,
          TError,
          Awaited<ReturnType<typeof listVisibleColleagueDesks>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListVisibleColleagueDesks<TData = Awaited<ReturnType<typeof listVisibleColleagueDesks>>, TError = Error>(
 params: ListVisibleColleagueDesksParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listVisibleColleagueDesks>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Privacy-filtered booked desks for map and colleague views
 */

export function useListVisibleColleagueDesks<TData = Awaited<ReturnType<typeof listVisibleColleagueDesks>>, TError = Error>(
 params: ListVisibleColleagueDesksParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listVisibleColleagueDesks>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListVisibleColleagueDesksQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListNominationsUrl = () => {




  return `/api/colleagues/nominations`
}

/**
 * Returns the viewer's one-way nominated-colleague shortlist.
 * Nomination grants no visibility — each colleague's own sharing
 * settings decide what presence/booking data is exposed elsewhere.
 * @summary List the viewer's nominated colleagues
 */
export const listNominations = async ( options?: Parameters<typeof customFetch>[1]): Promise<NominationListResponse> => {

  return customFetch<NominationListResponse>(getListNominationsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListNominationsQueryKey = () => {
    return [
    `/api/colleagues/nominations`
    ] as const;
    }


export const getListNominationsQueryOptions = <TData = Awaited<ReturnType<typeof listNominations>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNominations>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListNominationsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listNominations>>> = ({ signal }) => listNominations({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listNominations>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListNominationsQueryResult = NonNullable<Awaited<ReturnType<typeof listNominations>>>
export type ListNominationsQueryError = Error


export function useListNominations<TData = Awaited<ReturnType<typeof listNominations>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNominations>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNominations>>,
          TError,
          Awaited<ReturnType<typeof listNominations>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNominations<TData = Awaited<ReturnType<typeof listNominations>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNominations>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNominations>>,
          TError,
          Awaited<ReturnType<typeof listNominations>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNominations<TData = Awaited<ReturnType<typeof listNominations>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNominations>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List the viewer's nominated colleagues
 */

export function useListNominations<TData = Awaited<ReturnType<typeof listNominations>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNominations>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListNominationsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getAddNominationUrl = () => {




  return `/api/colleagues/nominations`
}

/**
 * Adds a colleague to the viewer's personal shortlist. Self-nomination
 * is rejected. Idempotent — nominating an existing colleague is a no-op.
 * Nomination grants no visibility by itself.
 * @summary Nominate a colleague (one-way follow)
 */
export const addNomination = async (addNominationBody: AddNominationBody, options?: Parameters<typeof customFetch>[1]): Promise<NominationResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<NominationResult>(getAddNominationUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(addNominationBody)
  }
);}





export const getAddNominationMutationKey = () => ['addNomination'] as const;

export const getAddNominationMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addNomination>>, TError,AddNominationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof addNomination>>, TError,AddNominationMutationVariables, TContext> => {

const mutationKey = getAddNominationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof addNomination>>, AddNominationMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  addNomination(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AddNominationMutationResult = NonNullable<Awaited<ReturnType<typeof addNomination>>>
    export type AddNominationMutationBody = AddNominationBody
    export type AddNominationMutationError = Error
    export type AddNominationMutationVariables = {data: AddNominationBody}

    /**
 * @summary Nominate a colleague (one-way follow)
 */
export const useAddNomination = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof addNomination>>, TError,AddNominationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof addNomination>>,
        TError,
        AddNominationMutationVariables,
        TContext
      > => {
      return useMutation(getAddNominationMutationOptions(options), queryClient);
    }

export const getRemoveNominationUrl = (colleagueUserId: string,) => {




  return `/api/colleagues/nominations/${colleagueUserId}`
}

/**
 * Removes a colleague from the viewer's personal shortlist.
 * @summary Remove a nominated colleague
 */
export const removeNomination = async (colleagueUserId: string, options?: Parameters<typeof customFetch>[1]): Promise<NominationRemoveResult> => {

  return customFetch<NominationRemoveResult>(getRemoveNominationUrl(colleagueUserId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getRemoveNominationMutationKey = () => ['removeNomination'] as const;

export const getRemoveNominationMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeNomination>>, TError,RemoveNominationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof removeNomination>>, TError,RemoveNominationMutationVariables, TContext> => {

const mutationKey = getRemoveNominationMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof removeNomination>>, RemoveNominationMutationVariables> = (props) => {
          const {colleagueUserId} = props ?? {};

          return  removeNomination(colleagueUserId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RemoveNominationMutationResult = NonNullable<Awaited<ReturnType<typeof removeNomination>>>

    export type RemoveNominationMutationError = Error
    export type RemoveNominationMutationVariables = {colleagueUserId: string}

    /**
 * @summary Remove a nominated colleague
 */
export const useRemoveNomination = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeNomination>>, TError,RemoveNominationMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof removeNomination>>,
        TError,
        RemoveNominationMutationVariables,
        TContext
      > => {
      return useMutation(getRemoveNominationMutationOptions(options), queryClient);
    }

export const getGetNominatedColleagueBookingsUrl = (params?: GetNominatedColleagueBookingsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/colleagues/nominations/bookings?${stringifiedParams}` : `/api/colleagues/nominations/bookings`
}

/**
 * Returns booked desks/offices for the viewer's nominated colleagues
 * over a date range (cross-office by design). Each colleague's
 * visibility is gated by their own sharing settings via the same
 * canViewerSeePresence check used for presence. Private colleagues
 * appear with visible=false and an empty bookings array.
 * @summary Privacy-trimmed bookings for nominated colleagues
 */
export const getNominatedColleagueBookings = async (params?: GetNominatedColleagueBookingsParams, options?: Parameters<typeof customFetch>[1]): Promise<NominatedColleagueBookingsResponse> => {

  return customFetch<NominatedColleagueBookingsResponse>(getGetNominatedColleagueBookingsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetNominatedColleagueBookingsQueryKey = (params?: GetNominatedColleagueBookingsParams,) => {
    return [
    `/api/colleagues/nominations/bookings`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetNominatedColleagueBookingsQueryOptions = <TData = Awaited<ReturnType<typeof getNominatedColleagueBookings>>, TError = Error>(params?: GetNominatedColleagueBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNominatedColleagueBookings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetNominatedColleagueBookingsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getNominatedColleagueBookings>>> = ({ signal }) => getNominatedColleagueBookings(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getNominatedColleagueBookings>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetNominatedColleagueBookingsQueryResult = NonNullable<Awaited<ReturnType<typeof getNominatedColleagueBookings>>>
export type GetNominatedColleagueBookingsQueryError = Error


export function useGetNominatedColleagueBookings<TData = Awaited<ReturnType<typeof getNominatedColleagueBookings>>, TError = Error>(
 params: undefined |  GetNominatedColleagueBookingsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNominatedColleagueBookings>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNominatedColleagueBookings>>,
          TError,
          Awaited<ReturnType<typeof getNominatedColleagueBookings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetNominatedColleagueBookings<TData = Awaited<ReturnType<typeof getNominatedColleagueBookings>>, TError = Error>(
 params?: GetNominatedColleagueBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNominatedColleagueBookings>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNominatedColleagueBookings>>,
          TError,
          Awaited<ReturnType<typeof getNominatedColleagueBookings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetNominatedColleagueBookings<TData = Awaited<ReturnType<typeof getNominatedColleagueBookings>>, TError = Error>(
 params?: GetNominatedColleagueBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNominatedColleagueBookings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Privacy-trimmed bookings for nominated colleagues
 */

export function useGetNominatedColleagueBookings<TData = Awaited<ReturnType<typeof getNominatedColleagueBookings>>, TError = Error>(
 params?: GetNominatedColleagueBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNominatedColleagueBookings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetNominatedColleagueBookingsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListFloorBookingsUrl = (id: string,
    params: ListFloorBookingsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/floors/${id}/bookings?${stringifiedParams}` : `/api/floors/${id}/bookings`
}

/**
 * @summary Floor booking slots with privacy-filtered booker names
 */
export const listFloorBookings = async (id: string,
    params: ListFloorBookingsParams, options?: Parameters<typeof customFetch>[1]): Promise<ListFloorBookings200> => {

  return customFetch<ListFloorBookings200>(getListFloorBookingsUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListFloorBookingsQueryKey = (id: string,
    params?: ListFloorBookingsParams,) => {
    return [
    `/api/floors/${id}/bookings`, ...(params ? [params] : [])
    ] as const;
    }


export const getListFloorBookingsQueryOptions = <TData = Awaited<ReturnType<typeof listFloorBookings>>, TError = Error>(id: string,
    params: ListFloorBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listFloorBookings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListFloorBookingsQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listFloorBookings>>> = ({ signal }) => listFloorBookings(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listFloorBookings>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListFloorBookingsQueryResult = NonNullable<Awaited<ReturnType<typeof listFloorBookings>>>
export type ListFloorBookingsQueryError = Error


export function useListFloorBookings<TData = Awaited<ReturnType<typeof listFloorBookings>>, TError = Error>(
 id: string,
    params: ListFloorBookingsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listFloorBookings>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFloorBookings>>,
          TError,
          Awaited<ReturnType<typeof listFloorBookings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListFloorBookings<TData = Awaited<ReturnType<typeof listFloorBookings>>, TError = Error>(
 id: string,
    params: ListFloorBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listFloorBookings>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listFloorBookings>>,
          TError,
          Awaited<ReturnType<typeof listFloorBookings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListFloorBookings<TData = Awaited<ReturnType<typeof listFloorBookings>>, TError = Error>(
 id: string,
    params: ListFloorBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listFloorBookings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Floor booking slots with privacy-filtered booker names
 */

export function useListFloorBookings<TData = Awaited<ReturnType<typeof listFloorBookings>>, TError = Error>(
 id: string,
    params: ListFloorBookingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listFloorBookings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListFloorBookingsQueryOptions(id,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetFloorPlanMetaUrl = (id: string,) => {




  return `/api/floors/${id}/plan`
}

/**
 * @summary Floor plan metadata (no image bytes)
 */
export const getFloorPlanMeta = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<FloorPlanMeta> => {

  return customFetch<FloorPlanMeta>(getGetFloorPlanMetaUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetFloorPlanMetaQueryKey = (id: string,) => {
    return [
    `/api/floors/${id}/plan`
    ] as const;
    }


export const getGetFloorPlanMetaQueryOptions = <TData = Awaited<ReturnType<typeof getFloorPlanMeta>>, TError = Error>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorPlanMeta>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetFloorPlanMetaQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getFloorPlanMeta>>> = ({ signal }) => getFloorPlanMeta(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getFloorPlanMeta>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetFloorPlanMetaQueryResult = NonNullable<Awaited<ReturnType<typeof getFloorPlanMeta>>>
export type GetFloorPlanMetaQueryError = Error


export function useGetFloorPlanMeta<TData = Awaited<ReturnType<typeof getFloorPlanMeta>>, TError = Error>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorPlanMeta>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFloorPlanMeta>>,
          TError,
          Awaited<ReturnType<typeof getFloorPlanMeta>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetFloorPlanMeta<TData = Awaited<ReturnType<typeof getFloorPlanMeta>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorPlanMeta>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFloorPlanMeta>>,
          TError,
          Awaited<ReturnType<typeof getFloorPlanMeta>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetFloorPlanMeta<TData = Awaited<ReturnType<typeof getFloorPlanMeta>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorPlanMeta>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Floor plan metadata (no image bytes)
 */

export function useGetFloorPlanMeta<TData = Awaited<ReturnType<typeof getFloorPlanMeta>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorPlanMeta>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetFloorPlanMetaQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpsertFloorPlanUrl = (id: string,) => {




  return `/api/floors/${id}/plan`
}

/**
 * @summary Upload or replace a floor-plan image (inventory managers)
 */
export const upsertFloorPlan = async (id: string,
    upsertFloorPlanBody: UpsertFloorPlanBody, options?: Parameters<typeof customFetch>[1]): Promise<FloorPlanMeta> => {
    const formData = new FormData();
formData.append(`file`, upsertFloorPlanBody.file);
if(upsertFloorPlanBody.width !== undefined) {
 formData.append(`width`, upsertFloorPlanBody.width.toString())
 }
if(upsertFloorPlanBody.height !== undefined) {
 formData.append(`height`, upsertFloorPlanBody.height.toString())
 }

  return customFetch<FloorPlanMeta>(getUpsertFloorPlanUrl(id),
  {
    ...options,
    method: 'PUT'
    ,
    body: formData
  }
);}





export const getUpsertFloorPlanMutationKey = () => ['upsertFloorPlan'] as const;

export const getUpsertFloorPlanMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertFloorPlan>>, TError,UpsertFloorPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertFloorPlan>>, TError,UpsertFloorPlanMutationVariables, TContext> => {

const mutationKey = getUpsertFloorPlanMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertFloorPlan>>, UpsertFloorPlanMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  upsertFloorPlan(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertFloorPlanMutationResult = NonNullable<Awaited<ReturnType<typeof upsertFloorPlan>>>
    export type UpsertFloorPlanMutationBody = UpsertFloorPlanBody
    export type UpsertFloorPlanMutationError = Error
    export type UpsertFloorPlanMutationVariables = {id: string;data: UpsertFloorPlanBody}

    /**
 * @summary Upload or replace a floor-plan image (inventory managers)
 */
export const useUpsertFloorPlan = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertFloorPlan>>, TError,UpsertFloorPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof upsertFloorPlan>>,
        TError,
        UpsertFloorPlanMutationVariables,
        TContext
      > => {
      return useMutation(getUpsertFloorPlanMutationOptions(options), queryClient);
    }

export const getDeleteFloorPlanUrl = (id: string,) => {




  return `/api/floors/${id}/plan`
}

/**
 * @deprecated
 * @summary Legacy removal refused; use reviewed plan-removal preview and confirm
 */
export const deleteFloorPlan = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<DeleteFloorPlan200> => {

  return customFetch<DeleteFloorPlan200>(getDeleteFloorPlanUrl(id),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteFloorPlanMutationKey = () => ['deleteFloorPlan'] as const;

export const getDeleteFloorPlanMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteFloorPlan>>, TError,DeleteFloorPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteFloorPlan>>, TError,DeleteFloorPlanMutationVariables, TContext> => {

const mutationKey = getDeleteFloorPlanMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteFloorPlan>>, DeleteFloorPlanMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteFloorPlan(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteFloorPlanMutationResult = NonNullable<Awaited<ReturnType<typeof deleteFloorPlan>>>

    export type DeleteFloorPlanMutationError = Error
    export type DeleteFloorPlanMutationVariables = {id: string}

    /**
 * @deprecated
 * @summary Legacy removal refused; use reviewed plan-removal preview and confirm
 */
export const useDeleteFloorPlan = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteFloorPlan>>, TError,DeleteFloorPlanMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteFloorPlan>>,
        TError,
        DeleteFloorPlanMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteFloorPlanMutationOptions(options), queryClient);
    }

export const getPreviewSpacePlanRemovalUrl = (id: string,) => {




  return `/api/spaces/${id}/plan-removal/preview`
}

/**
 * @summary Review immediate closure and booking impact before removing a pin
 */
export const previewSpacePlanRemoval = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<InventoryLifecyclePreview> => {

  return customFetch<InventoryLifecyclePreview>(getPreviewSpacePlanRemovalUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPreviewSpacePlanRemovalMutationKey = () => ['previewSpacePlanRemoval'] as const;

export const getPreviewSpacePlanRemovalMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewSpacePlanRemoval>>, TError,PreviewSpacePlanRemovalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewSpacePlanRemoval>>, TError,PreviewSpacePlanRemovalMutationVariables, TContext> => {

const mutationKey = getPreviewSpacePlanRemovalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewSpacePlanRemoval>>, PreviewSpacePlanRemovalMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  previewSpacePlanRemoval(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewSpacePlanRemovalMutationResult = NonNullable<Awaited<ReturnType<typeof previewSpacePlanRemoval>>>

    export type PreviewSpacePlanRemovalMutationError = Error
    export type PreviewSpacePlanRemovalMutationVariables = {id: string}

    /**
 * @summary Review immediate closure and booking impact before removing a pin
 */
export const usePreviewSpacePlanRemoval = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewSpacePlanRemoval>>, TError,PreviewSpacePlanRemovalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof previewSpacePlanRemoval>>,
        TError,
        PreviewSpacePlanRemovalMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewSpacePlanRemovalMutationOptions(options), queryClient);
    }

export const getConfirmSpacePlanRemovalUrl = (id: string,) => {




  return `/api/spaces/${id}/plan-removal/confirm`
}

/**
 * @summary Close the space, clear its pin, cancel future bookings and notify owners atomically
 */
export const confirmSpacePlanRemoval = async (id: string,
    inventoryLifecycleConfirmation: InventoryLifecycleConfirmation, options?: Parameters<typeof customFetch>[1]): Promise<InventoryLifecycleState> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<InventoryLifecycleState>(getConfirmSpacePlanRemovalUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(inventoryLifecycleConfirmation)
  }
);}





export const getConfirmSpacePlanRemovalMutationKey = () => ['confirmSpacePlanRemoval'] as const;

export const getConfirmSpacePlanRemovalMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmSpacePlanRemoval>>, TError,ConfirmSpacePlanRemovalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmSpacePlanRemoval>>, TError,ConfirmSpacePlanRemovalMutationVariables, TContext> => {

const mutationKey = getConfirmSpacePlanRemovalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmSpacePlanRemoval>>, ConfirmSpacePlanRemovalMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  confirmSpacePlanRemoval(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmSpacePlanRemovalMutationResult = NonNullable<Awaited<ReturnType<typeof confirmSpacePlanRemoval>>>
    export type ConfirmSpacePlanRemovalMutationBody = InventoryLifecycleConfirmation
    export type ConfirmSpacePlanRemovalMutationError = Error
    export type ConfirmSpacePlanRemovalMutationVariables = {id: string;data: InventoryLifecycleConfirmation}

    /**
 * @summary Close the space, clear its pin, cancel future bookings and notify owners atomically
 */
export const useConfirmSpacePlanRemoval = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmSpacePlanRemoval>>, TError,ConfirmSpacePlanRemovalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof confirmSpacePlanRemoval>>,
        TError,
        ConfirmSpacePlanRemovalMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmSpacePlanRemovalMutationOptions(options), queryClient);
    }

export const getPreviewFloorPlanRemovalUrl = (id: string,) => {




  return `/api/floors/${id}/plan-removal/preview`
}

/**
 * @summary Review whole-floor closure and booking impact before removing the image
 */
export const previewFloorPlanRemoval = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<InventoryLifecyclePreview> => {

  return customFetch<InventoryLifecyclePreview>(getPreviewFloorPlanRemovalUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPreviewFloorPlanRemovalMutationKey = () => ['previewFloorPlanRemoval'] as const;

export const getPreviewFloorPlanRemovalMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewFloorPlanRemoval>>, TError,PreviewFloorPlanRemovalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewFloorPlanRemoval>>, TError,PreviewFloorPlanRemovalMutationVariables, TContext> => {

const mutationKey = getPreviewFloorPlanRemovalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewFloorPlanRemoval>>, PreviewFloorPlanRemovalMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  previewFloorPlanRemoval(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewFloorPlanRemovalMutationResult = NonNullable<Awaited<ReturnType<typeof previewFloorPlanRemoval>>>

    export type PreviewFloorPlanRemovalMutationError = Error
    export type PreviewFloorPlanRemovalMutationVariables = {id: string}

    /**
 * @summary Review whole-floor closure and booking impact before removing the image
 */
export const usePreviewFloorPlanRemoval = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewFloorPlanRemoval>>, TError,PreviewFloorPlanRemovalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof previewFloorPlanRemoval>>,
        TError,
        PreviewFloorPlanRemovalMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewFloorPlanRemovalMutationOptions(options), queryClient);
    }

export const getConfirmFloorPlanRemovalUrl = (id: string,) => {




  return `/api/floors/${id}/plan-removal/confirm`
}

/**
 * @summary Close the floor, delete its image, keep pins and cancel future bookings atomically
 */
export const confirmFloorPlanRemoval = async (id: string,
    inventoryLifecycleConfirmation: InventoryLifecycleConfirmation, options?: Parameters<typeof customFetch>[1]): Promise<InventoryLifecycleState> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<InventoryLifecycleState>(getConfirmFloorPlanRemovalUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(inventoryLifecycleConfirmation)
  }
);}





export const getConfirmFloorPlanRemovalMutationKey = () => ['confirmFloorPlanRemoval'] as const;

export const getConfirmFloorPlanRemovalMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmFloorPlanRemoval>>, TError,ConfirmFloorPlanRemovalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof confirmFloorPlanRemoval>>, TError,ConfirmFloorPlanRemovalMutationVariables, TContext> => {

const mutationKey = getConfirmFloorPlanRemovalMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof confirmFloorPlanRemoval>>, ConfirmFloorPlanRemovalMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  confirmFloorPlanRemoval(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ConfirmFloorPlanRemovalMutationResult = NonNullable<Awaited<ReturnType<typeof confirmFloorPlanRemoval>>>
    export type ConfirmFloorPlanRemovalMutationBody = InventoryLifecycleConfirmation
    export type ConfirmFloorPlanRemovalMutationError = Error
    export type ConfirmFloorPlanRemovalMutationVariables = {id: string;data: InventoryLifecycleConfirmation}

    /**
 * @summary Close the floor, delete its image, keep pins and cancel future bookings atomically
 */
export const useConfirmFloorPlanRemoval = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof confirmFloorPlanRemoval>>, TError,ConfirmFloorPlanRemovalMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof confirmFloorPlanRemoval>>,
        TError,
        ConfirmFloorPlanRemovalMutationVariables,
        TContext
      > => {
      return useMutation(getConfirmFloorPlanRemovalMutationOptions(options), queryClient);
    }

export const getGetFloorGeometryUrl = (id: string,) => {




  return `/api/floors/${id}/geometry`
}

/**
 * @summary Read the tenant-scoped floor geometry
 */
export const getFloorGeometry = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<FloorGeometry> => {

  return customFetch<FloorGeometry>(getGetFloorGeometryUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetFloorGeometryQueryKey = (id: string,) => {
    return [
    `/api/floors/${id}/geometry`
    ] as const;
    }


export const getGetFloorGeometryQueryOptions = <TData = Awaited<ReturnType<typeof getFloorGeometry>>, TError = Error>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorGeometry>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetFloorGeometryQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getFloorGeometry>>> = ({ signal }) => getFloorGeometry(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getFloorGeometry>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetFloorGeometryQueryResult = NonNullable<Awaited<ReturnType<typeof getFloorGeometry>>>
export type GetFloorGeometryQueryError = Error


export function useGetFloorGeometry<TData = Awaited<ReturnType<typeof getFloorGeometry>>, TError = Error>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorGeometry>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFloorGeometry>>,
          TError,
          Awaited<ReturnType<typeof getFloorGeometry>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetFloorGeometry<TData = Awaited<ReturnType<typeof getFloorGeometry>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorGeometry>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFloorGeometry>>,
          TError,
          Awaited<ReturnType<typeof getFloorGeometry>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetFloorGeometry<TData = Awaited<ReturnType<typeof getFloorGeometry>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorGeometry>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read the tenant-scoped floor geometry
 */

export function useGetFloorGeometry<TData = Awaited<ReturnType<typeof getFloorGeometry>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorGeometry>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetFloorGeometryQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateFloorGeometryUrl = (id: string,) => {




  return `/api/floors/${id}/geometry`
}

/**
 * @summary Replace floor geometry with optimistic version check
 */
export const updateFloorGeometry = async (id: string,
    updateFloorGeometryBody: UpdateFloorGeometry, options?: Parameters<typeof customFetch>[1]): Promise<FloorGeometry> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<FloorGeometry>(getUpdateFloorGeometryUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateFloorGeometryBody)
  }
);}





export const getUpdateFloorGeometryMutationKey = () => ['updateFloorGeometry'] as const;

export const getUpdateFloorGeometryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateFloorGeometry>>, TError,UpdateFloorGeometryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateFloorGeometry>>, TError,UpdateFloorGeometryMutationVariables, TContext> => {

const mutationKey = getUpdateFloorGeometryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateFloorGeometry>>, UpdateFloorGeometryMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateFloorGeometry(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateFloorGeometryMutationResult = NonNullable<Awaited<ReturnType<typeof updateFloorGeometry>>>
    export type UpdateFloorGeometryMutationBody = UpdateFloorGeometry
    export type UpdateFloorGeometryMutationError = Error
    export type UpdateFloorGeometryMutationVariables = {id: string;data: UpdateFloorGeometry}

    /**
 * @summary Replace floor geometry with optimistic version check
 */
export const useUpdateFloorGeometry = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateFloorGeometry>>, TError,UpdateFloorGeometryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateFloorGeometry>>,
        TError,
        UpdateFloorGeometryMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateFloorGeometryMutationOptions(options), queryClient);
    }

export const getGetFloorTwinStatusUrl = (id: string,
    params: GetFloorTwinStatusParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/floors/${id}/twin-status?${stringifiedParams}` : `/api/floors/${id}/twin-status`
}

/**
 * App bookings apply to the requested half-open window. Simulated physical occupancy applies only to the current instant and never decides booking eligibility. Unknown calendar data is not treated as free.
 * @summary Read separate app, calendar and simulated occupancy status
 */
export const getFloorTwinStatus = async (id: string,
    params: GetFloorTwinStatusParams, options?: Parameters<typeof customFetch>[1]): Promise<FloorTwinStatusResponse> => {

  return customFetch<FloorTwinStatusResponse>(getGetFloorTwinStatusUrl(id,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetFloorTwinStatusQueryKey = (id: string,
    params?: GetFloorTwinStatusParams,) => {
    return [
    `/api/floors/${id}/twin-status`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetFloorTwinStatusQueryOptions = <TData = Awaited<ReturnType<typeof getFloorTwinStatus>>, TError = Error>(id: string,
    params: GetFloorTwinStatusParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorTwinStatus>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetFloorTwinStatusQueryKey(id,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getFloorTwinStatus>>> = ({ signal }) => getFloorTwinStatus(id,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getFloorTwinStatus>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetFloorTwinStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getFloorTwinStatus>>>
export type GetFloorTwinStatusQueryError = Error


export function useGetFloorTwinStatus<TData = Awaited<ReturnType<typeof getFloorTwinStatus>>, TError = Error>(
 id: string,
    params: GetFloorTwinStatusParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorTwinStatus>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFloorTwinStatus>>,
          TError,
          Awaited<ReturnType<typeof getFloorTwinStatus>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetFloorTwinStatus<TData = Awaited<ReturnType<typeof getFloorTwinStatus>>, TError = Error>(
 id: string,
    params: GetFloorTwinStatusParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorTwinStatus>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFloorTwinStatus>>,
          TError,
          Awaited<ReturnType<typeof getFloorTwinStatus>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetFloorTwinStatus<TData = Awaited<ReturnType<typeof getFloorTwinStatus>>, TError = Error>(
 id: string,
    params: GetFloorTwinStatusParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorTwinStatus>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read separate app, calendar and simulated occupancy status
 */

export function useGetFloorTwinStatus<TData = Awaited<ReturnType<typeof getFloorTwinStatus>>, TError = Error>(
 id: string,
    params: GetFloorTwinStatusParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorTwinStatus>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetFloorTwinStatusQueryOptions(id,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSetFloorTwinSensorScenariosUrl = (id: string,) => {




  return `/api/floors/${id}/twin-sensors`
}

/**
 * Administrator-only process-local test control. An empty assignment list resets the floor to deterministic defaults; this never writes presence, check-ins, reports or bookings.
 * @summary Replace bounded simulated sensor scenarios for a floor
 */
export const setFloorTwinSensorScenarios = async (id: string,
    setFloorTwinSensorScenariosBody: SetFloorTwinSensorScenarios, options?: Parameters<typeof customFetch>[1]): Promise<FloorTwinSensorScenarioResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<FloorTwinSensorScenarioResult>(getSetFloorTwinSensorScenariosUrl(id),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(setFloorTwinSensorScenariosBody)
  }
);}





export const getSetFloorTwinSensorScenariosMutationKey = () => ['setFloorTwinSensorScenarios'] as const;

export const getSetFloorTwinSensorScenariosMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setFloorTwinSensorScenarios>>, TError,SetFloorTwinSensorScenariosMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof setFloorTwinSensorScenarios>>, TError,SetFloorTwinSensorScenariosMutationVariables, TContext> => {

const mutationKey = getSetFloorTwinSensorScenariosMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof setFloorTwinSensorScenarios>>, SetFloorTwinSensorScenariosMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  setFloorTwinSensorScenarios(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SetFloorTwinSensorScenariosMutationResult = NonNullable<Awaited<ReturnType<typeof setFloorTwinSensorScenarios>>>
    export type SetFloorTwinSensorScenariosMutationBody = SetFloorTwinSensorScenarios
    export type SetFloorTwinSensorScenariosMutationError = Error
    export type SetFloorTwinSensorScenariosMutationVariables = {id: string;data: SetFloorTwinSensorScenarios}

    /**
 * @summary Replace bounded simulated sensor scenarios for a floor
 */
export const useSetFloorTwinSensorScenarios = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof setFloorTwinSensorScenarios>>, TError,SetFloorTwinSensorScenariosMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof setFloorTwinSensorScenarios>>,
        TError,
        SetFloorTwinSensorScenariosMutationVariables,
        TContext
      > => {
      return useMutation(getSetFloorTwinSensorScenariosMutationOptions(options), queryClient);
    }

export const getGetFloorPlanContentUrl = (id: string,) => {




  return `/api/floors/${id}/plan/content`
}

/**
 * @summary Download floor-plan image bytes
 */
export const getFloorPlanContent = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getGetFloorPlanContentUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetFloorPlanContentQueryKey = (id: string,) => {
    return [
    `/api/floors/${id}/plan/content`
    ] as const;
    }


export const getGetFloorPlanContentQueryOptions = <TData = Awaited<ReturnType<typeof getFloorPlanContent>>, TError = Error>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorPlanContent>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetFloorPlanContentQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getFloorPlanContent>>> = ({ signal }) => getFloorPlanContent(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getFloorPlanContent>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetFloorPlanContentQueryResult = NonNullable<Awaited<ReturnType<typeof getFloorPlanContent>>>
export type GetFloorPlanContentQueryError = Error


export function useGetFloorPlanContent<TData = Awaited<ReturnType<typeof getFloorPlanContent>>, TError = Error>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorPlanContent>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFloorPlanContent>>,
          TError,
          Awaited<ReturnType<typeof getFloorPlanContent>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetFloorPlanContent<TData = Awaited<ReturnType<typeof getFloorPlanContent>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorPlanContent>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getFloorPlanContent>>,
          TError,
          Awaited<ReturnType<typeof getFloorPlanContent>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetFloorPlanContent<TData = Awaited<ReturnType<typeof getFloorPlanContent>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorPlanContent>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Download floor-plan image bytes
 */

export function useGetFloorPlanContent<TData = Awaited<ReturnType<typeof getFloorPlanContent>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getFloorPlanContent>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetFloorPlanContentQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetInsightsOverviewUrl = (params?: GetInsightsOverviewParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/insights/overview?${stringifiedParams}` : `/api/admin/insights/overview`
}

/**
 * Returns utilisation trends, underuse/overbook flags, no-show hotspots, and a 4-week demand forecast for admins.
 * @summary AI insights overview
 */
export const getInsightsOverview = async (params?: GetInsightsOverviewParams, options?: Parameters<typeof customFetch>[1]): Promise<InsightsOverview> => {

  return customFetch<InsightsOverview>(getGetInsightsOverviewUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetInsightsOverviewQueryKey = (params?: GetInsightsOverviewParams,) => {
    return [
    `/api/admin/insights/overview`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetInsightsOverviewQueryOptions = <TData = Awaited<ReturnType<typeof getInsightsOverview>>, TError = Error>(params?: GetInsightsOverviewParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getInsightsOverview>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetInsightsOverviewQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getInsightsOverview>>> = ({ signal }) => getInsightsOverview(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getInsightsOverview>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetInsightsOverviewQueryResult = NonNullable<Awaited<ReturnType<typeof getInsightsOverview>>>
export type GetInsightsOverviewQueryError = Error


export function useGetInsightsOverview<TData = Awaited<ReturnType<typeof getInsightsOverview>>, TError = Error>(
 params: undefined |  GetInsightsOverviewParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getInsightsOverview>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getInsightsOverview>>,
          TError,
          Awaited<ReturnType<typeof getInsightsOverview>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetInsightsOverview<TData = Awaited<ReturnType<typeof getInsightsOverview>>, TError = Error>(
 params?: GetInsightsOverviewParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getInsightsOverview>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getInsightsOverview>>,
          TError,
          Awaited<ReturnType<typeof getInsightsOverview>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetInsightsOverview<TData = Awaited<ReturnType<typeof getInsightsOverview>>, TError = Error>(
 params?: GetInsightsOverviewParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getInsightsOverview>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary AI insights overview
 */

export function useGetInsightsOverview<TData = Awaited<ReturnType<typeof getInsightsOverview>>, TError = Error>(
 params?: GetInsightsOverviewParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getInsightsOverview>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetInsightsOverviewQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGenerateWeeklySummaryUrl = (params?: GenerateWeeklySummaryParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/insights/summary?${stringifiedParams}` : `/api/admin/insights/summary`
}

/**
 * Generates a labeled LLM weekly workplace summary from the computed metrics. Degrades to a heuristic fallback when the LLM is unavailable.
 * @summary Generate LLM weekly summary
 */
export const generateWeeklySummary = async (params?: GenerateWeeklySummaryParams, options?: Parameters<typeof customFetch>[1]): Promise<WeeklySummaryResult> => {

  return customFetch<WeeklySummaryResult>(getGenerateWeeklySummaryUrl(params),
  {
    ...options,
    method: 'POST'


  }
);}





export const getGenerateWeeklySummaryMutationKey = () => ['generateWeeklySummary'] as const;

export const getGenerateWeeklySummaryMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateWeeklySummary>>, TError,GenerateWeeklySummaryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof generateWeeklySummary>>, TError,GenerateWeeklySummaryMutationVariables, TContext> => {

const mutationKey = getGenerateWeeklySummaryMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof generateWeeklySummary>>, GenerateWeeklySummaryMutationVariables> = (props) => {
          const {params} = props ?? {};

          return  generateWeeklySummary(params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GenerateWeeklySummaryMutationResult = NonNullable<Awaited<ReturnType<typeof generateWeeklySummary>>>

    export type GenerateWeeklySummaryMutationError = Error
    export type GenerateWeeklySummaryMutationVariables = {params?: GenerateWeeklySummaryParams}

    /**
 * @summary Generate LLM weekly summary
 */
export const useGenerateWeeklySummary = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof generateWeeklySummary>>, TError,GenerateWeeklySummaryMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof generateWeeklySummary>>,
        TError,
        GenerateWeeklySummaryMutationVariables,
        TContext
      > => {
      return useMutation(getGenerateWeeklySummaryMutationOptions(options), queryClient);
    }

export const getExportInventoryCsvUrl = () => {




  return `/api/admin/inventory/export`
}

/**
 * Requires inventory management permission in the selected entity. Never exports another entity.
 * UTF-8 space-row CSV; an empty entity returns the same headers and no example rows.
 * Existing hierarchy and catalogues are referenced, never provisioned.
 * Identity: spaceId (keep for updates; blank for additions).
 * Location/type references: siteId, buildingId, floorId, zoneId, spaceTypeId.
 * Context only: siteCode, siteName, buildingCode, buildingName, floorName, zoneName,
 * typeCode, typeName, amenityCodes, amenityNames. Names do not rename referenced records.
 * Editable: spaceCode, spaceName, capacity, mode, sotOverride, assignedUserId,
 * mapX, mapY, mapSnapEnabled, tags, attributes, amenityIds.
 * Status is exported for context; changes require the existing lifecycle review.
 * tags and amenityIds are JSON arrays; attributes is a JSON object or null.
 * Null scalar fields are empty cells. Formula-sensitive strings are apostrophe-escaped
 * reversibly, including original leading apostrophes.
 * Source-prefixed attribute keys and source geometry are excluded and must be preserved
 * on updates. External integration references, images, layouts, lifecycle metadata,
 * bookings, ownership, timestamps and hierarchy configuration are never exported.
 * See docs/requirements/inventory-space-csv.md for field and compatibility semantics.
 * @summary Export selected-entity inventory as CSV
 */
export const exportInventoryCsv = async ( options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getExportInventoryCsvUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getExportInventoryCsvQueryKey = () => {
    return [
    `/api/admin/inventory/export`
    ] as const;
    }


export const getExportInventoryCsvQueryOptions = <TData = Awaited<ReturnType<typeof exportInventoryCsv>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportInventoryCsv>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getExportInventoryCsvQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof exportInventoryCsv>>> = ({ signal }) => exportInventoryCsv({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof exportInventoryCsv>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ExportInventoryCsvQueryResult = NonNullable<Awaited<ReturnType<typeof exportInventoryCsv>>>
export type ExportInventoryCsvQueryError = Error


export function useExportInventoryCsv<TData = Awaited<ReturnType<typeof exportInventoryCsv>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportInventoryCsv>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportInventoryCsv>>,
          TError,
          Awaited<ReturnType<typeof exportInventoryCsv>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useExportInventoryCsv<TData = Awaited<ReturnType<typeof exportInventoryCsv>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportInventoryCsv>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportInventoryCsv>>,
          TError,
          Awaited<ReturnType<typeof exportInventoryCsv>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useExportInventoryCsv<TData = Awaited<ReturnType<typeof exportInventoryCsv>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportInventoryCsv>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Export selected-entity inventory as CSV
 */

export function useExportInventoryCsv<TData = Awaited<ReturnType<typeof exportInventoryCsv>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportInventoryCsv>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getExportInventoryCsvQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getDownloadInventoryAddSpacesTemplateUrl = () => {




  return `/api/admin/inventory/import/template`
}

/**
 * Requires inventory management permission and a selected entity.
 * Returns headers only: siteCode, buildingCode, floorName, zoneName, typeCode,
 * spaceCode, spaceName. Fill these with existing selected-entity location/type
 * references and the new space code/name. No IDs are required.
 * New spaces receive generated IDs and default to capacity 1, reservable, active.
 * A matching existing zone and space code updates that space instead; preview
 * always shows additions, updates and conflicts before applying.
 * Locations and types are never created by import.
 * @summary Download a CSV template for adding spaces
 */
export const downloadInventoryAddSpacesTemplate = async ( options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadInventoryAddSpacesTemplateUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadInventoryAddSpacesTemplateQueryKey = () => {
    return [
    `/api/admin/inventory/import/template`
    ] as const;
    }


export const getDownloadInventoryAddSpacesTemplateQueryOptions = <TData = Awaited<ReturnType<typeof downloadInventoryAddSpacesTemplate>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadInventoryAddSpacesTemplate>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadInventoryAddSpacesTemplateQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadInventoryAddSpacesTemplate>>> = ({ signal }) => downloadInventoryAddSpacesTemplate({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadInventoryAddSpacesTemplate>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type DownloadInventoryAddSpacesTemplateQueryResult = NonNullable<Awaited<ReturnType<typeof downloadInventoryAddSpacesTemplate>>>
export type DownloadInventoryAddSpacesTemplateQueryError = Error


export function useDownloadInventoryAddSpacesTemplate<TData = Awaited<ReturnType<typeof downloadInventoryAddSpacesTemplate>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadInventoryAddSpacesTemplate>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadInventoryAddSpacesTemplate>>,
          TError,
          Awaited<ReturnType<typeof downloadInventoryAddSpacesTemplate>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useDownloadInventoryAddSpacesTemplate<TData = Awaited<ReturnType<typeof downloadInventoryAddSpacesTemplate>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadInventoryAddSpacesTemplate>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadInventoryAddSpacesTemplate>>,
          TError,
          Awaited<ReturnType<typeof downloadInventoryAddSpacesTemplate>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useDownloadInventoryAddSpacesTemplate<TData = Awaited<ReturnType<typeof downloadInventoryAddSpacesTemplate>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadInventoryAddSpacesTemplate>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Download a CSV template for adding spaces
 */

export function useDownloadInventoryAddSpacesTemplate<TData = Awaited<ReturnType<typeof downloadInventoryAddSpacesTemplate>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadInventoryAddSpacesTemplate>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getDownloadInventoryAddSpacesTemplateQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPreviewInventoryImportUrl = () => {




  return `/api/admin/inventory/import/preview`
}

/**
 * Stable space IDs update originals; blank IDs use unique existing zone/code matching or create.
 * References resolve only within the selected entity. Omitted rows stay untouched.
 * Preview classifies all rows; returns at most limit rows starting at offset.
 * Malformed/duplicate headers fail validation. Booking/lifecycle conflicts are row-level blocks.
 * @summary Validate selected-entity CSV import (preview)
 */
export const previewInventoryImport = async (previewInventoryImportBody: PreviewInventoryImportBody, options?: Parameters<typeof customFetch>[1]): Promise<InventoryImportPreview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<InventoryImportPreview>(getPreviewInventoryImportUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(previewInventoryImportBody)
  }
);}





export const getPreviewInventoryImportMutationKey = () => ['previewInventoryImport'] as const;

export const getPreviewInventoryImportMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewInventoryImport>>, TError,PreviewInventoryImportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewInventoryImport>>, TError,PreviewInventoryImportMutationVariables, TContext> => {

const mutationKey = getPreviewInventoryImportMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewInventoryImport>>, PreviewInventoryImportMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  previewInventoryImport(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewInventoryImportMutationResult = NonNullable<Awaited<ReturnType<typeof previewInventoryImport>>>
    export type PreviewInventoryImportMutationBody = PreviewInventoryImportBody
    export type PreviewInventoryImportMutationError = Error
    export type PreviewInventoryImportMutationVariables = {data: PreviewInventoryImportBody}

    /**
 * @summary Validate selected-entity CSV import (preview)
 */
export const usePreviewInventoryImport = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewInventoryImport>>, TError,PreviewInventoryImportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof previewInventoryImport>>,
        TError,
        PreviewInventoryImportMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewInventoryImportMutationOptions(options), queryClient);
    }

export const getCommitInventoryImportUrl = () => {




  return `/api/admin/inventory/import/commit`
}

/**
 * Re-validates the CSV against the selected entity server-side and commits valid rows.
 * Never trusts a client-supplied preview object or resolves codes from another entity.
 * Each successful row change retains an actor-bound entity audit event.
 * Each row rechecks current inventory, bookings and administrator authority under the common
 * lifecycle/booking lock. Blocked and unchanged rows perform no space or booking writes.
 * Response rows/errors are paginated; reportId retrieves remaining results without replaying writes.
 * @summary Commit validated CSV import
 */
export const commitInventoryImport = async (commitInventoryImportBody: CommitInventoryImportBody, options?: Parameters<typeof customFetch>[1]): Promise<InventoryImportCommitResult> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<InventoryImportCommitResult>(getCommitInventoryImportUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(commitInventoryImportBody)
  }
);}





export const getCommitInventoryImportMutationKey = () => ['commitInventoryImport'] as const;

export const getCommitInventoryImportMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof commitInventoryImport>>, TError,CommitInventoryImportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof commitInventoryImport>>, TError,CommitInventoryImportMutationVariables, TContext> => {

const mutationKey = getCommitInventoryImportMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof commitInventoryImport>>, CommitInventoryImportMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  commitInventoryImport(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CommitInventoryImportMutationResult = NonNullable<Awaited<ReturnType<typeof commitInventoryImport>>>
    export type CommitInventoryImportMutationBody = CommitInventoryImportBody
    export type CommitInventoryImportMutationError = Error
    export type CommitInventoryImportMutationVariables = {data: CommitInventoryImportBody}

    /**
 * @summary Commit validated CSV import
 */
export const useCommitInventoryImport = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof commitInventoryImport>>, TError,CommitInventoryImportMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof commitInventoryImport>>,
        TError,
        CommitInventoryImportMutationVariables,
        TContext
      > => {
      return useMutation(getCommitInventoryImportMutationOptions(options), queryClient);
    }

export const getGetInventoryImportResultUrl = (reportId: string,
    params?: GetInventoryImportResultParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/inventory/import/results/${reportId}?${stringifiedParams}` : `/api/admin/inventory/import/results/${reportId}`
}

/**
 * Actor/entity-bound report retained for 15 minutes in the serving process. Missing or expired reports return 404; never replay commit to paginate.
 * @summary Read one page of an import result without replaying writes
 */
export const getInventoryImportResult = async (reportId: string,
    params?: GetInventoryImportResultParams, options?: Parameters<typeof customFetch>[1]): Promise<InventoryImportCommitResult> => {

  return customFetch<InventoryImportCommitResult>(getGetInventoryImportResultUrl(reportId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetInventoryImportResultQueryKey = (reportId: string,
    params?: GetInventoryImportResultParams,) => {
    return [
    `/api/admin/inventory/import/results/${reportId}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetInventoryImportResultQueryOptions = <TData = Awaited<ReturnType<typeof getInventoryImportResult>>, TError = Error>(reportId: string,
    params?: GetInventoryImportResultParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getInventoryImportResult>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetInventoryImportResultQueryKey(reportId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getInventoryImportResult>>> = ({ signal }) => getInventoryImportResult(reportId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: reportId !== null && reportId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getInventoryImportResult>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetInventoryImportResultQueryResult = NonNullable<Awaited<ReturnType<typeof getInventoryImportResult>>>
export type GetInventoryImportResultQueryError = Error


export function useGetInventoryImportResult<TData = Awaited<ReturnType<typeof getInventoryImportResult>>, TError = Error>(
 reportId: string,
    params: undefined |  GetInventoryImportResultParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getInventoryImportResult>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getInventoryImportResult>>,
          TError,
          Awaited<ReturnType<typeof getInventoryImportResult>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetInventoryImportResult<TData = Awaited<ReturnType<typeof getInventoryImportResult>>, TError = Error>(
 reportId: string,
    params?: GetInventoryImportResultParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getInventoryImportResult>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getInventoryImportResult>>,
          TError,
          Awaited<ReturnType<typeof getInventoryImportResult>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetInventoryImportResult<TData = Awaited<ReturnType<typeof getInventoryImportResult>>, TError = Error>(
 reportId: string,
    params?: GetInventoryImportResultParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getInventoryImportResult>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read one page of an import result without replaying writes
 */

export function useGetInventoryImportResult<TData = Awaited<ReturnType<typeof getInventoryImportResult>>, TError = Error>(
 reportId: string,
    params?: GetInventoryImportResultParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getInventoryImportResult>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetInventoryImportResultQueryOptions(reportId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getExportBookingsCsvUrl = (params: ExportBookingsCsvParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/bookings/export?${stringifiedParams}` : `/api/admin/bookings/export`
}

/**
 * @summary Export bookings/utilisation as CSV
 */
export const exportBookingsCsv = async (params: ExportBookingsCsvParams, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getExportBookingsCsvUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getExportBookingsCsvQueryKey = (params?: ExportBookingsCsvParams,) => {
    return [
    `/api/admin/bookings/export`, ...(params ? [params] : [])
    ] as const;
    }


export const getExportBookingsCsvQueryOptions = <TData = Awaited<ReturnType<typeof exportBookingsCsv>>, TError = Error>(params: ExportBookingsCsvParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportBookingsCsv>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getExportBookingsCsvQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof exportBookingsCsv>>> = ({ signal }) => exportBookingsCsv(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof exportBookingsCsv>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ExportBookingsCsvQueryResult = NonNullable<Awaited<ReturnType<typeof exportBookingsCsv>>>
export type ExportBookingsCsvQueryError = Error


export function useExportBookingsCsv<TData = Awaited<ReturnType<typeof exportBookingsCsv>>, TError = Error>(
 params: ExportBookingsCsvParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportBookingsCsv>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportBookingsCsv>>,
          TError,
          Awaited<ReturnType<typeof exportBookingsCsv>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useExportBookingsCsv<TData = Awaited<ReturnType<typeof exportBookingsCsv>>, TError = Error>(
 params: ExportBookingsCsvParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportBookingsCsv>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof exportBookingsCsv>>,
          TError,
          Awaited<ReturnType<typeof exportBookingsCsv>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useExportBookingsCsv<TData = Awaited<ReturnType<typeof exportBookingsCsv>>, TError = Error>(
 params: ExportBookingsCsvParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportBookingsCsv>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Export bookings/utilisation as CSV
 */

export function useExportBookingsCsv<TData = Awaited<ReturnType<typeof exportBookingsCsv>>, TError = Error>(
 params: ExportBookingsCsvParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof exportBookingsCsv>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getExportBookingsCsvQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetApiPlatformAdminTenantsUrl = (params?: GetApiPlatformAdminTenantsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform-admin/tenants?${stringifiedParams}` : `/api/platform-admin/tenants`
}

/**
 * @summary List tenants (platform admin only)
 */
export const getApiPlatformAdminTenants = async (params?: GetApiPlatformAdminTenantsParams, options?: Parameters<typeof customFetch>[1]): Promise<GetApiPlatformAdminTenants200> => {

  return customFetch<GetApiPlatformAdminTenants200>(getGetApiPlatformAdminTenantsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiPlatformAdminTenantsQueryKey = (params?: GetApiPlatformAdminTenantsParams,) => {
    return [
    `/api/platform-admin/tenants`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetApiPlatformAdminTenantsQueryOptions = <TData = Awaited<ReturnType<typeof getApiPlatformAdminTenants>>, TError = Error>(params?: GetApiPlatformAdminTenantsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenants>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiPlatformAdminTenantsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiPlatformAdminTenants>>> = ({ signal }) => getApiPlatformAdminTenants(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenants>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiPlatformAdminTenantsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiPlatformAdminTenants>>>
export type GetApiPlatformAdminTenantsQueryError = Error


export function useGetApiPlatformAdminTenants<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenants>>, TError = Error>(
 params: undefined |  GetApiPlatformAdminTenantsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenants>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminTenants>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminTenants>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminTenants<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenants>>, TError = Error>(
 params?: GetApiPlatformAdminTenantsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenants>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminTenants>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminTenants>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminTenants<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenants>>, TError = Error>(
 params?: GetApiPlatformAdminTenantsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenants>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List tenants (platform admin only)
 */

export function useGetApiPlatformAdminTenants<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenants>>, TError = Error>(
 params?: GetApiPlatformAdminTenantsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenants>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiPlatformAdminTenantsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPostApiPlatformAdminTenantsUrl = () => {




  return `/api/platform-admin/tenants`
}

/**
 * @summary Create a new tenant (platform admin only)
 */
export const postApiPlatformAdminTenants = async (postApiPlatformAdminTenantsBody: PostApiPlatformAdminTenantsBody, options?: Parameters<typeof customFetch>[1]): Promise<TenantDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenantDetail>(getPostApiPlatformAdminTenantsUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(postApiPlatformAdminTenantsBody)
  }
);}





export const getPostApiPlatformAdminTenantsMutationKey = () => ['postApiPlatformAdminTenants'] as const;

export const getPostApiPlatformAdminTenantsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminTenants>>, TError,PostApiPlatformAdminTenantsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminTenants>>, TError,PostApiPlatformAdminTenantsMutationVariables, TContext> => {

const mutationKey = getPostApiPlatformAdminTenantsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiPlatformAdminTenants>>, PostApiPlatformAdminTenantsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  postApiPlatformAdminTenants(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostApiPlatformAdminTenantsMutationResult = NonNullable<Awaited<ReturnType<typeof postApiPlatformAdminTenants>>>
    export type PostApiPlatformAdminTenantsMutationBody = PostApiPlatformAdminTenantsBody
    export type PostApiPlatformAdminTenantsMutationError = Error
    export type PostApiPlatformAdminTenantsMutationVariables = {data: PostApiPlatformAdminTenantsBody}

    /**
 * @summary Create a new tenant (platform admin only)
 */
export const usePostApiPlatformAdminTenants = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminTenants>>, TError,PostApiPlatformAdminTenantsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postApiPlatformAdminTenants>>,
        TError,
        PostApiPlatformAdminTenantsMutationVariables,
        TContext
      > => {
      return useMutation(getPostApiPlatformAdminTenantsMutationOptions(options), queryClient);
    }

export const getGetApiPlatformAdminTenantsTenantIdUrl = (tenantId: string,) => {




  return `/api/platform-admin/tenants/${tenantId}`
}

/**
 * @summary Read a governance tenant
 */
export const getApiPlatformAdminTenantsTenantId = async (tenantId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenantDetail> => {

  return customFetch<TenantDetail>(getGetApiPlatformAdminTenantsTenantIdUrl(tenantId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiPlatformAdminTenantsTenantIdQueryKey = (tenantId: string,) => {
    return [
    `/api/platform-admin/tenants/${tenantId}`
    ] as const;
    }


export const getGetApiPlatformAdminTenantsTenantIdQueryOptions = <TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantId>>, TError = unknown>(tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantId>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiPlatformAdminTenantsTenantIdQueryKey(tenantId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantId>>> = ({ signal }) => getApiPlatformAdminTenantsTenantId(tenantId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantId !== null && tenantId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantId>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiPlatformAdminTenantsTenantIdQueryResult = NonNullable<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantId>>>
export type GetApiPlatformAdminTenantsTenantIdQueryError = unknown


export function useGetApiPlatformAdminTenantsTenantId<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantId>>, TError = unknown>(
 tenantId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantId>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantId>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantId>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminTenantsTenantId<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantId>>, TError = unknown>(
 tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantId>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantId>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantId>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminTenantsTenantId<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantId>>, TError = unknown>(
 tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantId>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read a governance tenant
 */

export function useGetApiPlatformAdminTenantsTenantId<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantId>>, TError = unknown>(
 tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantId>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiPlatformAdminTenantsTenantIdQueryOptions(tenantId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPatchApiPlatformAdminTenantsTenantIdUrl = (tenantId: string,) => {




  return `/api/platform-admin/tenants/${tenantId}`
}

/**
 * @summary Rename an active governance tenant (platform admin only)
 */
export const patchApiPlatformAdminTenantsTenantId = async (tenantId: string,
    patchApiPlatformAdminTenantsTenantIdBody: PatchApiPlatformAdminTenantsTenantIdBody, options?: Parameters<typeof customFetch>[1]): Promise<TenantDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenantDetail>(getPatchApiPlatformAdminTenantsTenantIdUrl(tenantId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(patchApiPlatformAdminTenantsTenantIdBody)
  }
);}





export const getPatchApiPlatformAdminTenantsTenantIdMutationKey = () => ['patchApiPlatformAdminTenantsTenantId'] as const;

export const getPatchApiPlatformAdminTenantsTenantIdMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantId>>, TError,PatchApiPlatformAdminTenantsTenantIdMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantId>>, TError,PatchApiPlatformAdminTenantsTenantIdMutationVariables, TContext> => {

const mutationKey = getPatchApiPlatformAdminTenantsTenantIdMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantId>>, PatchApiPlatformAdminTenantsTenantIdMutationVariables> = (props) => {
          const {tenantId,data} = props ?? {};

          return  patchApiPlatformAdminTenantsTenantId(tenantId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchApiPlatformAdminTenantsTenantIdMutationResult = NonNullable<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantId>>>
    export type PatchApiPlatformAdminTenantsTenantIdMutationBody = PatchApiPlatformAdminTenantsTenantIdBody
    export type PatchApiPlatformAdminTenantsTenantIdMutationError = Error
    export type PatchApiPlatformAdminTenantsTenantIdMutationVariables = {tenantId: string;data: PatchApiPlatformAdminTenantsTenantIdBody}

    /**
 * @summary Rename an active governance tenant (platform admin only)
 */
export const usePatchApiPlatformAdminTenantsTenantId = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantId>>, TError,PatchApiPlatformAdminTenantsTenantIdMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantId>>,
        TError,
        PatchApiPlatformAdminTenantsTenantIdMutationVariables,
        TContext
      > => {
      return useMutation(getPatchApiPlatformAdminTenantsTenantIdMutationOptions(options), queryClient);
    }

export const getPatchApiPlatformAdminTenantsTenantIdSlugUrl = (tenantId: string,) => {




  return `/api/platform-admin/tenants/${tenantId}/slug`
}

/**
 * Changes the parent tenant alias only. Its GUID base remains stable; entity addresses are unchanged.
 * @summary Change an active governance tenant friendly alias (platform admin only)
 */
export const patchApiPlatformAdminTenantsTenantIdSlug = async (tenantId: string,
    patchApiPlatformAdminTenantsTenantIdSlugBody: PatchApiPlatformAdminTenantsTenantIdSlugBody, options?: Parameters<typeof customFetch>[1]): Promise<TenantDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenantDetail>(getPatchApiPlatformAdminTenantsTenantIdSlugUrl(tenantId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(patchApiPlatformAdminTenantsTenantIdSlugBody)
  }
);}





export const getPatchApiPlatformAdminTenantsTenantIdSlugMutationKey = () => ['patchApiPlatformAdminTenantsTenantIdSlug'] as const;

export const getPatchApiPlatformAdminTenantsTenantIdSlugMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdSlug>>, TError,PatchApiPlatformAdminTenantsTenantIdSlugMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdSlug>>, TError,PatchApiPlatformAdminTenantsTenantIdSlugMutationVariables, TContext> => {

const mutationKey = getPatchApiPlatformAdminTenantsTenantIdSlugMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdSlug>>, PatchApiPlatformAdminTenantsTenantIdSlugMutationVariables> = (props) => {
          const {tenantId,data} = props ?? {};

          return  patchApiPlatformAdminTenantsTenantIdSlug(tenantId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchApiPlatformAdminTenantsTenantIdSlugMutationResult = NonNullable<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdSlug>>>
    export type PatchApiPlatformAdminTenantsTenantIdSlugMutationBody = PatchApiPlatformAdminTenantsTenantIdSlugBody
    export type PatchApiPlatformAdminTenantsTenantIdSlugMutationError = Error
    export type PatchApiPlatformAdminTenantsTenantIdSlugMutationVariables = {tenantId: string;data: PatchApiPlatformAdminTenantsTenantIdSlugBody}

    /**
 * @summary Change an active governance tenant friendly alias (platform admin only)
 */
export const usePatchApiPlatformAdminTenantsTenantIdSlug = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdSlug>>, TError,PatchApiPlatformAdminTenantsTenantIdSlugMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdSlug>>,
        TError,
        PatchApiPlatformAdminTenantsTenantIdSlugMutationVariables,
        TContext
      > => {
      return useMutation(getPatchApiPlatformAdminTenantsTenantIdSlugMutationOptions(options), queryClient);
    }

export const getPatchApiPlatformAdminTenantsTenantIdIdentityUrl = (tenantId: string,) => {




  return `/api/platform-admin/tenants/${tenantId}/identity`
}

/**
 * Changes the parent and sole entity names and friendly aliases. Their GUID base addresses remain stable; prior aliases stop resolving.
 * @summary Rename an active tenant and its sole entity together (platform admin only)
 */
export const patchApiPlatformAdminTenantsTenantIdIdentity = async (tenantId: string,
    patchApiPlatformAdminTenantsTenantIdIdentityBody: PatchApiPlatformAdminTenantsTenantIdIdentityBody, options?: Parameters<typeof customFetch>[1]): Promise<TenantDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenantDetail>(getPatchApiPlatformAdminTenantsTenantIdIdentityUrl(tenantId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(patchApiPlatformAdminTenantsTenantIdIdentityBody)
  }
);}





export const getPatchApiPlatformAdminTenantsTenantIdIdentityMutationKey = () => ['patchApiPlatformAdminTenantsTenantIdIdentity'] as const;

export const getPatchApiPlatformAdminTenantsTenantIdIdentityMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdIdentity>>, TError,PatchApiPlatformAdminTenantsTenantIdIdentityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdIdentity>>, TError,PatchApiPlatformAdminTenantsTenantIdIdentityMutationVariables, TContext> => {

const mutationKey = getPatchApiPlatformAdminTenantsTenantIdIdentityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdIdentity>>, PatchApiPlatformAdminTenantsTenantIdIdentityMutationVariables> = (props) => {
          const {tenantId,data} = props ?? {};

          return  patchApiPlatformAdminTenantsTenantIdIdentity(tenantId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchApiPlatformAdminTenantsTenantIdIdentityMutationResult = NonNullable<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdIdentity>>>
    export type PatchApiPlatformAdminTenantsTenantIdIdentityMutationBody = PatchApiPlatformAdminTenantsTenantIdIdentityBody
    export type PatchApiPlatformAdminTenantsTenantIdIdentityMutationError = Error
    export type PatchApiPlatformAdminTenantsTenantIdIdentityMutationVariables = {tenantId: string;data: PatchApiPlatformAdminTenantsTenantIdIdentityBody}

    /**
 * @summary Rename an active tenant and its sole entity together (platform admin only)
 */
export const usePatchApiPlatformAdminTenantsTenantIdIdentity = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdIdentity>>, TError,PatchApiPlatformAdminTenantsTenantIdIdentityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdIdentity>>,
        TError,
        PatchApiPlatformAdminTenantsTenantIdIdentityMutationVariables,
        TContext
      > => {
      return useMutation(getPatchApiPlatformAdminTenantsTenantIdIdentityMutationOptions(options), queryClient);
    }

export const getGetApiPlatformAdminHomeWebpartCatalogueUrl = () => {




  return `/api/platform-admin/home-webpart-catalogue`
}

/**
 * @summary Read homepage catalogue
 */
export const getApiPlatformAdminHomeWebpartCatalogue = async ( options?: Parameters<typeof customFetch>[1]): Promise<HomeWebpartCatalogue> => {

  return customFetch<HomeWebpartCatalogue>(getGetApiPlatformAdminHomeWebpartCatalogueUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiPlatformAdminHomeWebpartCatalogueQueryKey = () => {
    return [
    `/api/platform-admin/home-webpart-catalogue`
    ] as const;
    }


export const getGetApiPlatformAdminHomeWebpartCatalogueQueryOptions = <TData = Awaited<ReturnType<typeof getApiPlatformAdminHomeWebpartCatalogue>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminHomeWebpartCatalogue>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiPlatformAdminHomeWebpartCatalogueQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiPlatformAdminHomeWebpartCatalogue>>> = ({ signal }) => getApiPlatformAdminHomeWebpartCatalogue({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminHomeWebpartCatalogue>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiPlatformAdminHomeWebpartCatalogueQueryResult = NonNullable<Awaited<ReturnType<typeof getApiPlatformAdminHomeWebpartCatalogue>>>
export type GetApiPlatformAdminHomeWebpartCatalogueQueryError = Error


export function useGetApiPlatformAdminHomeWebpartCatalogue<TData = Awaited<ReturnType<typeof getApiPlatformAdminHomeWebpartCatalogue>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminHomeWebpartCatalogue>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminHomeWebpartCatalogue>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminHomeWebpartCatalogue>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminHomeWebpartCatalogue<TData = Awaited<ReturnType<typeof getApiPlatformAdminHomeWebpartCatalogue>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminHomeWebpartCatalogue>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminHomeWebpartCatalogue>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminHomeWebpartCatalogue>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminHomeWebpartCatalogue<TData = Awaited<ReturnType<typeof getApiPlatformAdminHomeWebpartCatalogue>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminHomeWebpartCatalogue>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read homepage catalogue
 */

export function useGetApiPlatformAdminHomeWebpartCatalogue<TData = Awaited<ReturnType<typeof getApiPlatformAdminHomeWebpartCatalogue>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminHomeWebpartCatalogue>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiPlatformAdminHomeWebpartCatalogueQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPutApiPlatformAdminHomeWebpartCatalogueUrl = () => {




  return `/api/platform-admin/home-webpart-catalogue`
}

/**
 * @summary Publish or reset homepage catalogue
 */
export const putApiPlatformAdminHomeWebpartCatalogue = async (saveHomeWebpartCatalogue: SaveHomeWebpartCatalogue, options?: Parameters<typeof customFetch>[1]): Promise<HomeWebpartCatalogue> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<HomeWebpartCatalogue>(getPutApiPlatformAdminHomeWebpartCatalogueUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(saveHomeWebpartCatalogue)
  }
);}





export const getPutApiPlatformAdminHomeWebpartCatalogueMutationKey = () => ['putApiPlatformAdminHomeWebpartCatalogue'] as const;

export const getPutApiPlatformAdminHomeWebpartCatalogueMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putApiPlatformAdminHomeWebpartCatalogue>>, TError,PutApiPlatformAdminHomeWebpartCatalogueMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof putApiPlatformAdminHomeWebpartCatalogue>>, TError,PutApiPlatformAdminHomeWebpartCatalogueMutationVariables, TContext> => {

const mutationKey = getPutApiPlatformAdminHomeWebpartCatalogueMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof putApiPlatformAdminHomeWebpartCatalogue>>, PutApiPlatformAdminHomeWebpartCatalogueMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  putApiPlatformAdminHomeWebpartCatalogue(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PutApiPlatformAdminHomeWebpartCatalogueMutationResult = NonNullable<Awaited<ReturnType<typeof putApiPlatformAdminHomeWebpartCatalogue>>>
    export type PutApiPlatformAdminHomeWebpartCatalogueMutationBody = SaveHomeWebpartCatalogue
    export type PutApiPlatformAdminHomeWebpartCatalogueMutationError = Error
    export type PutApiPlatformAdminHomeWebpartCatalogueMutationVariables = {data: SaveHomeWebpartCatalogue}

    /**
 * @summary Publish or reset homepage catalogue
 */
export const usePutApiPlatformAdminHomeWebpartCatalogue = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putApiPlatformAdminHomeWebpartCatalogue>>, TError,PutApiPlatformAdminHomeWebpartCatalogueMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof putApiPlatformAdminHomeWebpartCatalogue>>,
        TError,
        PutApiPlatformAdminHomeWebpartCatalogueMutationVariables,
        TContext
      > => {
      return useMutation(getPutApiPlatformAdminHomeWebpartCatalogueMutationOptions(options), queryClient);
    }

export const getGetApiPlatformAdminHomeWebpartsUrl = () => {




  return `/api/platform-admin/home-webparts`
}

/**
 * @summary Read homepage configuration
 */
export const getApiPlatformAdminHomeWebparts = async ( options?: Parameters<typeof customFetch>[1]): Promise<HomeWebpartPage> => {

  return customFetch<HomeWebpartPage>(getGetApiPlatformAdminHomeWebpartsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiPlatformAdminHomeWebpartsQueryKey = () => {
    return [
    `/api/platform-admin/home-webparts`
    ] as const;
    }


export const getGetApiPlatformAdminHomeWebpartsQueryOptions = <TData = Awaited<ReturnType<typeof getApiPlatformAdminHomeWebparts>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminHomeWebparts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiPlatformAdminHomeWebpartsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiPlatformAdminHomeWebparts>>> = ({ signal }) => getApiPlatformAdminHomeWebparts({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminHomeWebparts>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiPlatformAdminHomeWebpartsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiPlatformAdminHomeWebparts>>>
export type GetApiPlatformAdminHomeWebpartsQueryError = Error


export function useGetApiPlatformAdminHomeWebparts<TData = Awaited<ReturnType<typeof getApiPlatformAdminHomeWebparts>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminHomeWebparts>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminHomeWebparts>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminHomeWebparts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminHomeWebparts<TData = Awaited<ReturnType<typeof getApiPlatformAdminHomeWebparts>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminHomeWebparts>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminHomeWebparts>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminHomeWebparts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminHomeWebparts<TData = Awaited<ReturnType<typeof getApiPlatformAdminHomeWebparts>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminHomeWebparts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read homepage configuration
 */

export function useGetApiPlatformAdminHomeWebparts<TData = Awaited<ReturnType<typeof getApiPlatformAdminHomeWebparts>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminHomeWebparts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiPlatformAdminHomeWebpartsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPutApiPlatformAdminHomeWebpartsUrl = () => {




  return `/api/platform-admin/home-webparts`
}

/**
 * @summary Publish or reset homepage configuration
 */
export const putApiPlatformAdminHomeWebparts = async (saveHomeWebpartPage: SaveHomeWebpartPage, options?: Parameters<typeof customFetch>[1]): Promise<HomeWebpartPage> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<HomeWebpartPage>(getPutApiPlatformAdminHomeWebpartsUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(saveHomeWebpartPage)
  }
);}





export const getPutApiPlatformAdminHomeWebpartsMutationKey = () => ['putApiPlatformAdminHomeWebparts'] as const;

export const getPutApiPlatformAdminHomeWebpartsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putApiPlatformAdminHomeWebparts>>, TError,PutApiPlatformAdminHomeWebpartsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof putApiPlatformAdminHomeWebparts>>, TError,PutApiPlatformAdminHomeWebpartsMutationVariables, TContext> => {

const mutationKey = getPutApiPlatformAdminHomeWebpartsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof putApiPlatformAdminHomeWebparts>>, PutApiPlatformAdminHomeWebpartsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  putApiPlatformAdminHomeWebparts(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PutApiPlatformAdminHomeWebpartsMutationResult = NonNullable<Awaited<ReturnType<typeof putApiPlatformAdminHomeWebparts>>>
    export type PutApiPlatformAdminHomeWebpartsMutationBody = SaveHomeWebpartPage
    export type PutApiPlatformAdminHomeWebpartsMutationError = Error
    export type PutApiPlatformAdminHomeWebpartsMutationVariables = {data: SaveHomeWebpartPage}

    /**
 * @summary Publish or reset homepage configuration
 */
export const usePutApiPlatformAdminHomeWebparts = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putApiPlatformAdminHomeWebparts>>, TError,PutApiPlatformAdminHomeWebpartsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof putApiPlatformAdminHomeWebparts>>,
        TError,
        PutApiPlatformAdminHomeWebpartsMutationVariables,
        TContext
      > => {
      return useMutation(getPutApiPlatformAdminHomeWebpartsMutationOptions(options), queryClient);
    }

export const getGetHomeWebpartOptionsUrl = (tenantSlug: string,
    params: GetHomeWebpartOptionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/home-webpart-options?${stringifiedParams}` : `/api/tenants/${tenantSlug}/home-webpart-options`
}

/**
 * @summary Read paginated entity-owned homepage targets
 */
export const getHomeWebpartOptions = async (tenantSlug: string,
    params: GetHomeWebpartOptionsParams, options?: Parameters<typeof customFetch>[1]): Promise<GetHomeWebpartOptions200> => {

  return customFetch<GetHomeWebpartOptions200>(getGetHomeWebpartOptionsUrl(tenantSlug,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetHomeWebpartOptionsQueryKey = (tenantSlug: string,
    params?: GetHomeWebpartOptionsParams,) => {
    return [
    `/api/tenants/${tenantSlug}/home-webpart-options`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetHomeWebpartOptionsQueryOptions = <TData = Awaited<ReturnType<typeof getHomeWebpartOptions>>, TError = Error>(tenantSlug: string,
    params: GetHomeWebpartOptionsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHomeWebpartOptions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetHomeWebpartOptionsQueryKey(tenantSlug,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getHomeWebpartOptions>>> = ({ signal }) => getHomeWebpartOptions(tenantSlug,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getHomeWebpartOptions>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetHomeWebpartOptionsQueryResult = NonNullable<Awaited<ReturnType<typeof getHomeWebpartOptions>>>
export type GetHomeWebpartOptionsQueryError = Error


export function useGetHomeWebpartOptions<TData = Awaited<ReturnType<typeof getHomeWebpartOptions>>, TError = Error>(
 tenantSlug: string,
    params: GetHomeWebpartOptionsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHomeWebpartOptions>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHomeWebpartOptions>>,
          TError,
          Awaited<ReturnType<typeof getHomeWebpartOptions>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetHomeWebpartOptions<TData = Awaited<ReturnType<typeof getHomeWebpartOptions>>, TError = Error>(
 tenantSlug: string,
    params: GetHomeWebpartOptionsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHomeWebpartOptions>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getHomeWebpartOptions>>,
          TError,
          Awaited<ReturnType<typeof getHomeWebpartOptions>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetHomeWebpartOptions<TData = Awaited<ReturnType<typeof getHomeWebpartOptions>>, TError = Error>(
 tenantSlug: string,
    params: GetHomeWebpartOptionsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHomeWebpartOptions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read paginated entity-owned homepage targets
 */

export function useGetHomeWebpartOptions<TData = Awaited<ReturnType<typeof getHomeWebpartOptions>>, TError = Error>(
 tenantSlug: string,
    params: GetHomeWebpartOptionsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getHomeWebpartOptions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetHomeWebpartOptionsQueryOptions(tenantSlug,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetApiTenantsTenantSlugHomeWebpartsUrl = (tenantSlug: string,
    params?: GetApiTenantsTenantSlugHomeWebpartsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/home-webparts?${stringifiedParams}` : `/api/tenants/${tenantSlug}/home-webparts`
}

/**
 * @summary Read homepage configuration
 */
export const getApiTenantsTenantSlugHomeWebparts = async (tenantSlug: string,
    params?: GetApiTenantsTenantSlugHomeWebpartsParams, options?: Parameters<typeof customFetch>[1]): Promise<HomeWebpartPage> => {

  return customFetch<HomeWebpartPage>(getGetApiTenantsTenantSlugHomeWebpartsUrl(tenantSlug,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiTenantsTenantSlugHomeWebpartsQueryKey = (tenantSlug: string,
    params?: GetApiTenantsTenantSlugHomeWebpartsParams,) => {
    return [
    `/api/tenants/${tenantSlug}/home-webparts`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetApiTenantsTenantSlugHomeWebpartsQueryOptions = <TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugHomeWebparts>>, TError = Error>(tenantSlug: string,
    params?: GetApiTenantsTenantSlugHomeWebpartsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugHomeWebparts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiTenantsTenantSlugHomeWebpartsQueryKey(tenantSlug,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiTenantsTenantSlugHomeWebparts>>> = ({ signal }) => getApiTenantsTenantSlugHomeWebparts(tenantSlug,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugHomeWebparts>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiTenantsTenantSlugHomeWebpartsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiTenantsTenantSlugHomeWebparts>>>
export type GetApiTenantsTenantSlugHomeWebpartsQueryError = Error


export function useGetApiTenantsTenantSlugHomeWebparts<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugHomeWebparts>>, TError = Error>(
 tenantSlug: string,
    params: undefined |  GetApiTenantsTenantSlugHomeWebpartsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugHomeWebparts>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugHomeWebparts>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugHomeWebparts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugHomeWebparts<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugHomeWebparts>>, TError = Error>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugHomeWebpartsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugHomeWebparts>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugHomeWebparts>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugHomeWebparts>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugHomeWebparts<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugHomeWebparts>>, TError = Error>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugHomeWebpartsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugHomeWebparts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read homepage configuration
 */

export function useGetApiTenantsTenantSlugHomeWebparts<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugHomeWebparts>>, TError = Error>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugHomeWebpartsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugHomeWebparts>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiTenantsTenantSlugHomeWebpartsQueryOptions(tenantSlug,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPutApiTenantsTenantSlugHomeWebpartsUrl = (tenantSlug: string,
    params?: PutApiTenantsTenantSlugHomeWebpartsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/home-webparts?${stringifiedParams}` : `/api/tenants/${tenantSlug}/home-webparts`
}

/**
 * @summary Publish or reset homepage configuration
 */
export const putApiTenantsTenantSlugHomeWebparts = async (tenantSlug: string,
    saveHomeWebpartPage: SaveHomeWebpartPage,
    params?: PutApiTenantsTenantSlugHomeWebpartsParams, options?: Parameters<typeof customFetch>[1]): Promise<HomeWebpartPage> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<HomeWebpartPage>(getPutApiTenantsTenantSlugHomeWebpartsUrl(tenantSlug,params),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(saveHomeWebpartPage)
  }
);}





export const getPutApiTenantsTenantSlugHomeWebpartsMutationKey = () => ['putApiTenantsTenantSlugHomeWebparts'] as const;

export const getPutApiTenantsTenantSlugHomeWebpartsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putApiTenantsTenantSlugHomeWebparts>>, TError,PutApiTenantsTenantSlugHomeWebpartsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof putApiTenantsTenantSlugHomeWebparts>>, TError,PutApiTenantsTenantSlugHomeWebpartsMutationVariables, TContext> => {

const mutationKey = getPutApiTenantsTenantSlugHomeWebpartsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof putApiTenantsTenantSlugHomeWebparts>>, PutApiTenantsTenantSlugHomeWebpartsMutationVariables> = (props) => {
          const {tenantSlug,data,params} = props ?? {};

          return  putApiTenantsTenantSlugHomeWebparts(tenantSlug,data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PutApiTenantsTenantSlugHomeWebpartsMutationResult = NonNullable<Awaited<ReturnType<typeof putApiTenantsTenantSlugHomeWebparts>>>
    export type PutApiTenantsTenantSlugHomeWebpartsMutationBody = SaveHomeWebpartPage
    export type PutApiTenantsTenantSlugHomeWebpartsMutationError = Error
    export type PutApiTenantsTenantSlugHomeWebpartsMutationVariables = {tenantSlug: string;data: SaveHomeWebpartPage;params?: PutApiTenantsTenantSlugHomeWebpartsParams}

    /**
 * @summary Publish or reset homepage configuration
 */
export const usePutApiTenantsTenantSlugHomeWebparts = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putApiTenantsTenantSlugHomeWebparts>>, TError,PutApiTenantsTenantSlugHomeWebpartsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof putApiTenantsTenantSlugHomeWebparts>>,
        TError,
        PutApiTenantsTenantSlugHomeWebpartsMutationVariables,
        TContext
      > => {
      return useMutation(getPutApiTenantsTenantSlugHomeWebpartsMutationOptions(options), queryClient);
    }

export const getGetApiPlatformAdminHomeLayoutUrl = () => {




  return `/api/platform-admin/home-layout`
}

/**
 * @summary Read the platform Home layout default
 */
export const getApiPlatformAdminHomeLayout = async ( options?: Parameters<typeof customFetch>[1]): Promise<PlatformHomeLayout> => {

  return customFetch<PlatformHomeLayout>(getGetApiPlatformAdminHomeLayoutUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiPlatformAdminHomeLayoutQueryKey = () => {
    return [
    `/api/platform-admin/home-layout`
    ] as const;
    }


export const getGetApiPlatformAdminHomeLayoutQueryOptions = <TData = Awaited<ReturnType<typeof getApiPlatformAdminHomeLayout>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminHomeLayout>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiPlatformAdminHomeLayoutQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiPlatformAdminHomeLayout>>> = ({ signal }) => getApiPlatformAdminHomeLayout({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminHomeLayout>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiPlatformAdminHomeLayoutQueryResult = NonNullable<Awaited<ReturnType<typeof getApiPlatformAdminHomeLayout>>>
export type GetApiPlatformAdminHomeLayoutQueryError = Error


export function useGetApiPlatformAdminHomeLayout<TData = Awaited<ReturnType<typeof getApiPlatformAdminHomeLayout>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminHomeLayout>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminHomeLayout>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminHomeLayout>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminHomeLayout<TData = Awaited<ReturnType<typeof getApiPlatformAdminHomeLayout>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminHomeLayout>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminHomeLayout>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminHomeLayout>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminHomeLayout<TData = Awaited<ReturnType<typeof getApiPlatformAdminHomeLayout>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminHomeLayout>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read the platform Home layout default
 */

export function useGetApiPlatformAdminHomeLayout<TData = Awaited<ReturnType<typeof getApiPlatformAdminHomeLayout>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminHomeLayout>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiPlatformAdminHomeLayoutQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPutApiPlatformAdminHomeLayoutUrl = () => {




  return `/api/platform-admin/home-layout`
}

/**
 * @summary Replace the platform Home layout default
 */
export const putApiPlatformAdminHomeLayout = async (homeLayout: HomeLayout, options?: Parameters<typeof customFetch>[1]): Promise<PlatformHomeLayout> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PlatformHomeLayout>(getPutApiPlatformAdminHomeLayoutUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(homeLayout)
  }
);}





export const getPutApiPlatformAdminHomeLayoutMutationKey = () => ['putApiPlatformAdminHomeLayout'] as const;

export const getPutApiPlatformAdminHomeLayoutMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putApiPlatformAdminHomeLayout>>, TError,PutApiPlatformAdminHomeLayoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof putApiPlatformAdminHomeLayout>>, TError,PutApiPlatformAdminHomeLayoutMutationVariables, TContext> => {

const mutationKey = getPutApiPlatformAdminHomeLayoutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof putApiPlatformAdminHomeLayout>>, PutApiPlatformAdminHomeLayoutMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  putApiPlatformAdminHomeLayout(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PutApiPlatformAdminHomeLayoutMutationResult = NonNullable<Awaited<ReturnType<typeof putApiPlatformAdminHomeLayout>>>
    export type PutApiPlatformAdminHomeLayoutMutationBody = HomeLayout
    export type PutApiPlatformAdminHomeLayoutMutationError = Error
    export type PutApiPlatformAdminHomeLayoutMutationVariables = {data: HomeLayout}

    /**
 * @summary Replace the platform Home layout default
 */
export const usePutApiPlatformAdminHomeLayout = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putApiPlatformAdminHomeLayout>>, TError,PutApiPlatformAdminHomeLayoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof putApiPlatformAdminHomeLayout>>,
        TError,
        PutApiPlatformAdminHomeLayoutMutationVariables,
        TContext
      > => {
      return useMutation(getPutApiPlatformAdminHomeLayoutMutationOptions(options), queryClient);
    }

export const getDeleteApiPlatformAdminHomeLayoutUrl = () => {




  return `/api/platform-admin/home-layout`
}

/**
 * @summary Reset the platform Home layout to the built-in default
 */
export const deleteApiPlatformAdminHomeLayout = async ( options?: Parameters<typeof customFetch>[1]): Promise<PlatformHomeLayout> => {

  return customFetch<PlatformHomeLayout>(getDeleteApiPlatformAdminHomeLayoutUrl(),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteApiPlatformAdminHomeLayoutMutationKey = () => ['deleteApiPlatformAdminHomeLayout'] as const;

export const getDeleteApiPlatformAdminHomeLayoutMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteApiPlatformAdminHomeLayout>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteApiPlatformAdminHomeLayout>>, TError,void, TContext> => {

const mutationKey = getDeleteApiPlatformAdminHomeLayoutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteApiPlatformAdminHomeLayout>>, void> = () => {


          return  deleteApiPlatformAdminHomeLayout(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteApiPlatformAdminHomeLayoutMutationResult = NonNullable<Awaited<ReturnType<typeof deleteApiPlatformAdminHomeLayout>>>

    export type DeleteApiPlatformAdminHomeLayoutMutationError = Error


    /**
 * @summary Reset the platform Home layout to the built-in default
 */
export const useDeleteApiPlatformAdminHomeLayout = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteApiPlatformAdminHomeLayout>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteApiPlatformAdminHomeLayout>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getDeleteApiPlatformAdminHomeLayoutMutationOptions(options), queryClient);
    }

export const getGetApiPlatformAdminNavigationLayoutUrl = () => {




  return `/api/platform-admin/navigation-layout`
}

/**
 * @summary Read the platform navigation layout default
 */
export const getApiPlatformAdminNavigationLayout = async ( options?: Parameters<typeof customFetch>[1]): Promise<PlatformNavigationLayout> => {

  return customFetch<PlatformNavigationLayout>(getGetApiPlatformAdminNavigationLayoutUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiPlatformAdminNavigationLayoutQueryKey = () => {
    return [
    `/api/platform-admin/navigation-layout`
    ] as const;
    }


export const getGetApiPlatformAdminNavigationLayoutQueryOptions = <TData = Awaited<ReturnType<typeof getApiPlatformAdminNavigationLayout>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminNavigationLayout>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiPlatformAdminNavigationLayoutQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiPlatformAdminNavigationLayout>>> = ({ signal }) => getApiPlatformAdminNavigationLayout({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminNavigationLayout>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiPlatformAdminNavigationLayoutQueryResult = NonNullable<Awaited<ReturnType<typeof getApiPlatformAdminNavigationLayout>>>
export type GetApiPlatformAdminNavigationLayoutQueryError = Error


export function useGetApiPlatformAdminNavigationLayout<TData = Awaited<ReturnType<typeof getApiPlatformAdminNavigationLayout>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminNavigationLayout>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminNavigationLayout>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminNavigationLayout>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminNavigationLayout<TData = Awaited<ReturnType<typeof getApiPlatformAdminNavigationLayout>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminNavigationLayout>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminNavigationLayout>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminNavigationLayout>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminNavigationLayout<TData = Awaited<ReturnType<typeof getApiPlatformAdminNavigationLayout>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminNavigationLayout>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read the platform navigation layout default
 */

export function useGetApiPlatformAdminNavigationLayout<TData = Awaited<ReturnType<typeof getApiPlatformAdminNavigationLayout>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminNavigationLayout>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiPlatformAdminNavigationLayoutQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPutApiPlatformAdminNavigationLayoutUrl = () => {




  return `/api/platform-admin/navigation-layout`
}

/**
 * @summary Replace the platform navigation layout default
 */
export const putApiPlatformAdminNavigationLayout = async (navigationLayout: NavigationLayout, options?: Parameters<typeof customFetch>[1]): Promise<PlatformNavigationLayout> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PlatformNavigationLayout>(getPutApiPlatformAdminNavigationLayoutUrl(),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(navigationLayout)
  }
);}





export const getPutApiPlatformAdminNavigationLayoutMutationKey = () => ['putApiPlatformAdminNavigationLayout'] as const;

export const getPutApiPlatformAdminNavigationLayoutMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putApiPlatformAdminNavigationLayout>>, TError,PutApiPlatformAdminNavigationLayoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof putApiPlatformAdminNavigationLayout>>, TError,PutApiPlatformAdminNavigationLayoutMutationVariables, TContext> => {

const mutationKey = getPutApiPlatformAdminNavigationLayoutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof putApiPlatformAdminNavigationLayout>>, PutApiPlatformAdminNavigationLayoutMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  putApiPlatformAdminNavigationLayout(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PutApiPlatformAdminNavigationLayoutMutationResult = NonNullable<Awaited<ReturnType<typeof putApiPlatformAdminNavigationLayout>>>
    export type PutApiPlatformAdminNavigationLayoutMutationBody = NavigationLayout
    export type PutApiPlatformAdminNavigationLayoutMutationError = Error
    export type PutApiPlatformAdminNavigationLayoutMutationVariables = {data: NavigationLayout}

    /**
 * @summary Replace the platform navigation layout default
 */
export const usePutApiPlatformAdminNavigationLayout = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putApiPlatformAdminNavigationLayout>>, TError,PutApiPlatformAdminNavigationLayoutMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof putApiPlatformAdminNavigationLayout>>,
        TError,
        PutApiPlatformAdminNavigationLayoutMutationVariables,
        TContext
      > => {
      return useMutation(getPutApiPlatformAdminNavigationLayoutMutationOptions(options), queryClient);
    }

export const getDeleteApiPlatformAdminNavigationLayoutUrl = () => {




  return `/api/platform-admin/navigation-layout`
}

/**
 * @summary Reset the platform navigation layout to the built-in default
 */
export const deleteApiPlatformAdminNavigationLayout = async ( options?: Parameters<typeof customFetch>[1]): Promise<PlatformNavigationLayout> => {

  return customFetch<PlatformNavigationLayout>(getDeleteApiPlatformAdminNavigationLayoutUrl(),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteApiPlatformAdminNavigationLayoutMutationKey = () => ['deleteApiPlatformAdminNavigationLayout'] as const;

export const getDeleteApiPlatformAdminNavigationLayoutMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteApiPlatformAdminNavigationLayout>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteApiPlatformAdminNavigationLayout>>, TError,void, TContext> => {

const mutationKey = getDeleteApiPlatformAdminNavigationLayoutMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteApiPlatformAdminNavigationLayout>>, void> = () => {


          return  deleteApiPlatformAdminNavigationLayout(requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteApiPlatformAdminNavigationLayoutMutationResult = NonNullable<Awaited<ReturnType<typeof deleteApiPlatformAdminNavigationLayout>>>

    export type DeleteApiPlatformAdminNavigationLayoutMutationError = Error


    /**
 * @summary Reset the platform navigation layout to the built-in default
 */
export const useDeleteApiPlatformAdminNavigationLayout = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteApiPlatformAdminNavigationLayout>>, TError,void, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteApiPlatformAdminNavigationLayout>>,
        TError,
        void,
        TContext
      > => {
      return useMutation(getDeleteApiPlatformAdminNavigationLayoutMutationOptions(options), queryClient);
    }

export const getGetApiPlatformAdminTenantsTenantIdSettingsUrl = (tenantId: string,) => {




  return `/api/platform-admin/tenants/${tenantId}/settings`
}

/**
 * @summary Read governance tenant settings
 */
export const getApiPlatformAdminTenantsTenantIdSettings = async (tenantId: string, options?: Parameters<typeof customFetch>[1]): Promise<GetApiPlatformAdminTenantsTenantIdSettings200> => {

  return customFetch<GetApiPlatformAdminTenantsTenantIdSettings200>(getGetApiPlatformAdminTenantsTenantIdSettingsUrl(tenantId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiPlatformAdminTenantsTenantIdSettingsQueryKey = (tenantId: string,) => {
    return [
    `/api/platform-admin/tenants/${tenantId}/settings`
    ] as const;
    }


export const getGetApiPlatformAdminTenantsTenantIdSettingsQueryOptions = <TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdSettings>>, TError = unknown>(tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdSettings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiPlatformAdminTenantsTenantIdSettingsQueryKey(tenantId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdSettings>>> = ({ signal }) => getApiPlatformAdminTenantsTenantIdSettings(tenantId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantId !== null && tenantId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdSettings>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiPlatformAdminTenantsTenantIdSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdSettings>>>
export type GetApiPlatformAdminTenantsTenantIdSettingsQueryError = unknown


export function useGetApiPlatformAdminTenantsTenantIdSettings<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdSettings>>, TError = unknown>(
 tenantId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdSettings>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdSettings>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdSettings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminTenantsTenantIdSettings<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdSettings>>, TError = unknown>(
 tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdSettings>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdSettings>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdSettings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminTenantsTenantIdSettings<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdSettings>>, TError = unknown>(
 tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdSettings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read governance tenant settings
 */

export function useGetApiPlatformAdminTenantsTenantIdSettings<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdSettings>>, TError = unknown>(
 tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdSettings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiPlatformAdminTenantsTenantIdSettingsQueryOptions(tenantId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPatchApiPlatformAdminTenantsTenantIdSettingsUrl = (tenantId: string,) => {




  return `/api/platform-admin/tenants/${tenantId}/settings`
}

/**
 * @summary Replace governance tenant settings
 */
export const patchApiPlatformAdminTenantsTenantIdSettings = async (tenantId: string,
    patchApiPlatformAdminTenantsTenantIdSettingsBody: PatchApiPlatformAdminTenantsTenantIdSettingsBody, options?: Parameters<typeof customFetch>[1]): Promise<PatchApiPlatformAdminTenantsTenantIdSettings200> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PatchApiPlatformAdminTenantsTenantIdSettings200>(getPatchApiPlatformAdminTenantsTenantIdSettingsUrl(tenantId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(patchApiPlatformAdminTenantsTenantIdSettingsBody)
  }
);}





export const getPatchApiPlatformAdminTenantsTenantIdSettingsMutationKey = () => ['patchApiPlatformAdminTenantsTenantIdSettings'] as const;

export const getPatchApiPlatformAdminTenantsTenantIdSettingsMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdSettings>>, TError,PatchApiPlatformAdminTenantsTenantIdSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdSettings>>, TError,PatchApiPlatformAdminTenantsTenantIdSettingsMutationVariables, TContext> => {

const mutationKey = getPatchApiPlatformAdminTenantsTenantIdSettingsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdSettings>>, PatchApiPlatformAdminTenantsTenantIdSettingsMutationVariables> = (props) => {
          const {tenantId,data} = props ?? {};

          return  patchApiPlatformAdminTenantsTenantIdSettings(tenantId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchApiPlatformAdminTenantsTenantIdSettingsMutationResult = NonNullable<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdSettings>>>
    export type PatchApiPlatformAdminTenantsTenantIdSettingsMutationBody = PatchApiPlatformAdminTenantsTenantIdSettingsBody
    export type PatchApiPlatformAdminTenantsTenantIdSettingsMutationError = unknown
    export type PatchApiPlatformAdminTenantsTenantIdSettingsMutationVariables = {tenantId: string;data: PatchApiPlatformAdminTenantsTenantIdSettingsBody}

    /**
 * @summary Replace governance tenant settings
 */
export const usePatchApiPlatformAdminTenantsTenantIdSettings = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdSettings>>, TError,PatchApiPlatformAdminTenantsTenantIdSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdSettings>>,
        TError,
        PatchApiPlatformAdminTenantsTenantIdSettingsMutationVariables,
        TContext
      > => {
      return useMutation(getPatchApiPlatformAdminTenantsTenantIdSettingsMutationOptions(options), queryClient);
    }

export const getGetApiPlatformAdminTenantsTenantIdEntitiesUrl = (tenantId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform-admin/tenants/${tenantId}/entities?${stringifiedParams}` : `/api/platform-admin/tenants/${tenantId}/entities`
}

/**
 * @summary List entities in a governance tenant
 */
export const getApiPlatformAdminTenantsTenantIdEntities = async (tenantId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesParams, options?: Parameters<typeof customFetch>[1]): Promise<EntityPage> => {

  return customFetch<EntityPage>(getGetApiPlatformAdminTenantsTenantIdEntitiesUrl(tenantId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiPlatformAdminTenantsTenantIdEntitiesQueryKey = (tenantId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesParams,) => {
    return [
    `/api/platform-admin/tenants/${tenantId}/entities`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetApiPlatformAdminTenantsTenantIdEntitiesQueryOptions = <TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntities>>, TError = Error>(tenantId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntities>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiPlatformAdminTenantsTenantIdEntitiesQueryKey(tenantId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntities>>> = ({ signal }) => getApiPlatformAdminTenantsTenantIdEntities(tenantId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantId !== null && tenantId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntities>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiPlatformAdminTenantsTenantIdEntitiesQueryResult = NonNullable<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntities>>>
export type GetApiPlatformAdminTenantsTenantIdEntitiesQueryError = Error


export function useGetApiPlatformAdminTenantsTenantIdEntities<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntities>>, TError = Error>(
 tenantId: string,
    params: undefined |  GetApiPlatformAdminTenantsTenantIdEntitiesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntities>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntities>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntities>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminTenantsTenantIdEntities<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntities>>, TError = Error>(
 tenantId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntities>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntities>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntities>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminTenantsTenantIdEntities<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntities>>, TError = Error>(
 tenantId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntities>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List entities in a governance tenant
 */

export function useGetApiPlatformAdminTenantsTenantIdEntities<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntities>>, TError = Error>(
 tenantId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntities>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiPlatformAdminTenantsTenantIdEntitiesQueryOptions(tenantId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasUrl = (tenantId: string,
    entityId: string,) => {




  return `/api/platform-admin/tenants/${tenantId}/entities/${entityId}/alias`
}

/**
 * The GUID base address and entity identity remain stable. The prior alias stops resolving.
 * @summary Change an entity friendly alias (platform admin only)
 */
export const patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAlias = async (tenantId: string,
    entityId: string,
    patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasBody: PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasBody, options?: Parameters<typeof customFetch>[1]): Promise<EntityDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EntityDetail>(getPatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasUrl(tenantId,entityId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasBody)
  }
);}





export const getPatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasMutationKey = () => ['patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAlias'] as const;

export const getPatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAlias>>, TError,PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAlias>>, TError,PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasMutationVariables, TContext> => {

const mutationKey = getPatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAlias>>, PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasMutationVariables> = (props) => {
          const {tenantId,entityId,data} = props ?? {};

          return  patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAlias(tenantId,entityId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasMutationResult = NonNullable<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAlias>>>
    export type PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasMutationBody = PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasBody
    export type PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasMutationError = Error
    export type PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasMutationVariables = {tenantId: string;entityId: string;data: PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasBody}

    /**
 * @summary Change an entity friendly alias (platform admin only)
 */
export const usePatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAlias = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAlias>>, TError,PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAlias>>,
        TError,
        PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasMutationVariables,
        TContext
      > => {
      return useMutation(getPatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdAliasMutationOptions(options), queryClient);
    }

export const getGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRolesUrl = (tenantId: string,
    entityId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRolesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform-admin/tenants/${tenantId}/entities/${entityId}/roles?${stringifiedParams}` : `/api/platform-admin/tenants/${tenantId}/entities/${entityId}/roles`
}

/**
 * @summary List selectable roles for an entity (platform admin only)
 */
export const getApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles = async (tenantId: string,
    entityId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRolesParams, options?: Parameters<typeof customFetch>[1]): Promise<GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles200> => {

  return customFetch<GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles200>(getGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRolesUrl(tenantId,entityId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRolesQueryKey = (tenantId: string,
    entityId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRolesParams,) => {
    return [
    `/api/platform-admin/tenants/${tenantId}/entities/${entityId}/roles`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRolesQueryOptions = <TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles>>, TError = Error>(tenantId: string,
    entityId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRolesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRolesQueryKey(tenantId,entityId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles>>> = ({ signal }) => getApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles(tenantId,entityId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantId !== null && tenantId !== undefined && entityId !== null && entityId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRolesQueryResult = NonNullable<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles>>>
export type GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRolesQueryError = Error


export function useGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles>>, TError = Error>(
 tenantId: string,
    entityId: string,
    params: undefined |  GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRolesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles>>, TError = Error>(
 tenantId: string,
    entityId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRolesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles>>, TError = Error>(
 tenantId: string,
    entityId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRolesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List selectable roles for an entity (platform admin only)
 */

export function useGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles>>, TError = Error>(
 tenantId: string,
    entityId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRolesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdRoles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdRolesQueryOptions(tenantId,entityId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidatesUrl = (tenantId: string,
    entityId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidatesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform-admin/tenants/${tenantId}/entities/${entityId}/member-candidates?${stringifiedParams}` : `/api/platform-admin/tenants/${tenantId}/entities/${entityId}/member-candidates`
}

/**
 * @summary Find registered users for a platform entity access grant
 */
export const getApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates = async (tenantId: string,
    entityId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidatesParams, options?: Parameters<typeof customFetch>[1]): Promise<MemberCandidatePage> => {

  return customFetch<MemberCandidatePage>(getGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidatesUrl(tenantId,entityId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidatesQueryKey = (tenantId: string,
    entityId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidatesParams,) => {
    return [
    `/api/platform-admin/tenants/${tenantId}/entities/${entityId}/member-candidates`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidatesQueryOptions = <TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates>>, TError = Error>(tenantId: string,
    entityId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidatesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidatesQueryKey(tenantId,entityId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates>>> = ({ signal }) => getApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates(tenantId,entityId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantId !== null && tenantId !== undefined && entityId !== null && entityId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidatesQueryResult = NonNullable<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates>>>
export type GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidatesQueryError = Error


export function useGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates>>, TError = Error>(
 tenantId: string,
    entityId: string,
    params: undefined |  GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidatesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates>>, TError = Error>(
 tenantId: string,
    entityId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidatesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates>>, TError = Error>(
 tenantId: string,
    entityId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidatesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Find registered users for a platform entity access grant
 */

export function useGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates>>, TError = Error>(
 tenantId: string,
    entityId: string,
    params?: GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidatesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidates>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdMemberCandidatesQueryOptions(tenantId,entityId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersUrl = (tenantId: string,
    entityId: string,) => {




  return `/api/platform-admin/tenants/${tenantId}/entities/${entityId}/members`
}

/**
 * Adds explicit entity access, synchronizes parent membership, and records a platform audit event. Selected roles are added to any existing roles.
 * @summary Grant entity membership and roles to an existing app user
 */
export const postApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembers = async (tenantId: string,
    entityId: string,
    postApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersBody: PostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersBody, options?: Parameters<typeof customFetch>[1]): Promise<TenantMember> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenantMember>(getPostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersUrl(tenantId,entityId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(postApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersBody)
  }
);}





export const getPostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersMutationKey = () => ['postApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembers'] as const;

export const getPostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembers>>, TError,PostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembers>>, TError,PostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersMutationVariables, TContext> => {

const mutationKey = getPostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembers>>, PostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersMutationVariables> = (props) => {
          const {tenantId,entityId,data} = props ?? {};

          return  postApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembers(tenantId,entityId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersMutationResult = NonNullable<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembers>>>
    export type PostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersMutationBody = PostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersBody
    export type PostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersMutationError = Error
    export type PostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersMutationVariables = {tenantId: string;entityId: string;data: PostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersBody}

    /**
 * @summary Grant entity membership and roles to an existing app user
 */
export const usePostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembers = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembers>>, TError,PostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembers>>,
        TError,
        PostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersMutationVariables,
        TContext
      > => {
      return useMutation(getPostApiPlatformAdminTenantsTenantIdEntitiesEntityIdMembersMutationOptions(options), queryClient);
    }

export const getPostApiPlatformAdminTenantsTenantIdSuspendUrl = (tenantId: string,) => {




  return `/api/platform-admin/tenants/${tenantId}/suspend`
}

/**
 * @summary Suspend an active tenant (platform admin only)
 */
export const postApiPlatformAdminTenantsTenantIdSuspend = async (tenantId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenantDetail> => {

  return customFetch<TenantDetail>(getPostApiPlatformAdminTenantsTenantIdSuspendUrl(tenantId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPostApiPlatformAdminTenantsTenantIdSuspendMutationKey = () => ['postApiPlatformAdminTenantsTenantIdSuspend'] as const;

export const getPostApiPlatformAdminTenantsTenantIdSuspendMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdSuspend>>, TError,PostApiPlatformAdminTenantsTenantIdSuspendMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdSuspend>>, TError,PostApiPlatformAdminTenantsTenantIdSuspendMutationVariables, TContext> => {

const mutationKey = getPostApiPlatformAdminTenantsTenantIdSuspendMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdSuspend>>, PostApiPlatformAdminTenantsTenantIdSuspendMutationVariables> = (props) => {
          const {tenantId} = props ?? {};

          return  postApiPlatformAdminTenantsTenantIdSuspend(tenantId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostApiPlatformAdminTenantsTenantIdSuspendMutationResult = NonNullable<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdSuspend>>>

    export type PostApiPlatformAdminTenantsTenantIdSuspendMutationError = Error
    export type PostApiPlatformAdminTenantsTenantIdSuspendMutationVariables = {tenantId: string}

    /**
 * @summary Suspend an active tenant (platform admin only)
 */
export const usePostApiPlatformAdminTenantsTenantIdSuspend = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdSuspend>>, TError,PostApiPlatformAdminTenantsTenantIdSuspendMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdSuspend>>,
        TError,
        PostApiPlatformAdminTenantsTenantIdSuspendMutationVariables,
        TContext
      > => {
      return useMutation(getPostApiPlatformAdminTenantsTenantIdSuspendMutationOptions(options), queryClient);
    }

export const getPostApiPlatformAdminTenantsTenantIdRestoreUrl = (tenantId: string,) => {




  return `/api/platform-admin/tenants/${tenantId}/restore`
}

/**
 * Reactivates the tenant, clears its suspension and archive timestamps, and restores access under existing permissions.
 * @summary Restore a suspended or archived tenant (platform admin only)
 */
export const postApiPlatformAdminTenantsTenantIdRestore = async (tenantId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenantDetail> => {

  return customFetch<TenantDetail>(getPostApiPlatformAdminTenantsTenantIdRestoreUrl(tenantId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPostApiPlatformAdminTenantsTenantIdRestoreMutationKey = () => ['postApiPlatformAdminTenantsTenantIdRestore'] as const;

export const getPostApiPlatformAdminTenantsTenantIdRestoreMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdRestore>>, TError,PostApiPlatformAdminTenantsTenantIdRestoreMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdRestore>>, TError,PostApiPlatformAdminTenantsTenantIdRestoreMutationVariables, TContext> => {

const mutationKey = getPostApiPlatformAdminTenantsTenantIdRestoreMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdRestore>>, PostApiPlatformAdminTenantsTenantIdRestoreMutationVariables> = (props) => {
          const {tenantId} = props ?? {};

          return  postApiPlatformAdminTenantsTenantIdRestore(tenantId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostApiPlatformAdminTenantsTenantIdRestoreMutationResult = NonNullable<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdRestore>>>

    export type PostApiPlatformAdminTenantsTenantIdRestoreMutationError = Error
    export type PostApiPlatformAdminTenantsTenantIdRestoreMutationVariables = {tenantId: string}

    /**
 * @summary Restore a suspended or archived tenant (platform admin only)
 */
export const usePostApiPlatformAdminTenantsTenantIdRestore = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdRestore>>, TError,PostApiPlatformAdminTenantsTenantIdRestoreMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdRestore>>,
        TError,
        PostApiPlatformAdminTenantsTenantIdRestoreMutationVariables,
        TContext
      > => {
      return useMutation(getPostApiPlatformAdminTenantsTenantIdRestoreMutationOptions(options), queryClient);
    }

export const getPostApiPlatformAdminTenantsTenantIdArchiveUrl = (tenantId: string,) => {




  return `/api/platform-admin/tenants/${tenantId}/archive`
}

/**
 * @summary Archive a suspended tenant (platform admin only)
 */
export const postApiPlatformAdminTenantsTenantIdArchive = async (tenantId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenantDetail> => {

  return customFetch<TenantDetail>(getPostApiPlatformAdminTenantsTenantIdArchiveUrl(tenantId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPostApiPlatformAdminTenantsTenantIdArchiveMutationKey = () => ['postApiPlatformAdminTenantsTenantIdArchive'] as const;

export const getPostApiPlatformAdminTenantsTenantIdArchiveMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdArchive>>, TError,PostApiPlatformAdminTenantsTenantIdArchiveMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdArchive>>, TError,PostApiPlatformAdminTenantsTenantIdArchiveMutationVariables, TContext> => {

const mutationKey = getPostApiPlatformAdminTenantsTenantIdArchiveMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdArchive>>, PostApiPlatformAdminTenantsTenantIdArchiveMutationVariables> = (props) => {
          const {tenantId} = props ?? {};

          return  postApiPlatformAdminTenantsTenantIdArchive(tenantId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostApiPlatformAdminTenantsTenantIdArchiveMutationResult = NonNullable<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdArchive>>>

    export type PostApiPlatformAdminTenantsTenantIdArchiveMutationError = Error
    export type PostApiPlatformAdminTenantsTenantIdArchiveMutationVariables = {tenantId: string}

    /**
 * @summary Archive a suspended tenant (platform admin only)
 */
export const usePostApiPlatformAdminTenantsTenantIdArchive = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdArchive>>, TError,PostApiPlatformAdminTenantsTenantIdArchiveMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postApiPlatformAdminTenantsTenantIdArchive>>,
        TError,
        PostApiPlatformAdminTenantsTenantIdArchiveMutationVariables,
        TContext
      > => {
      return useMutation(getPostApiPlatformAdminTenantsTenantIdArchiveMutationOptions(options), queryClient);
    }

export const getGetApiPlatformAdminTenantsTenantIdDeletionPreviewUrl = (tenantId: string,) => {




  return `/api/platform-admin/tenants/${tenantId}/deletion-preview`
}

/**
 * Read-only impact preview. No deletion endpoint is available until immediate erasure from live data, backups and external copies can be verified.
 * @summary Preview tenant deletion impact and governance blockers
 */
export const getApiPlatformAdminTenantsTenantIdDeletionPreview = async (tenantId: string, options?: Parameters<typeof customFetch>[1]): Promise<TenantDeletionPreview> => {

  return customFetch<TenantDeletionPreview>(getGetApiPlatformAdminTenantsTenantIdDeletionPreviewUrl(tenantId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiPlatformAdminTenantsTenantIdDeletionPreviewQueryKey = (tenantId: string,) => {
    return [
    `/api/platform-admin/tenants/${tenantId}/deletion-preview`
    ] as const;
    }


export const getGetApiPlatformAdminTenantsTenantIdDeletionPreviewQueryOptions = <TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdDeletionPreview>>, TError = Error>(tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdDeletionPreview>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiPlatformAdminTenantsTenantIdDeletionPreviewQueryKey(tenantId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdDeletionPreview>>> = ({ signal }) => getApiPlatformAdminTenantsTenantIdDeletionPreview(tenantId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantId !== null && tenantId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdDeletionPreview>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiPlatformAdminTenantsTenantIdDeletionPreviewQueryResult = NonNullable<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdDeletionPreview>>>
export type GetApiPlatformAdminTenantsTenantIdDeletionPreviewQueryError = Error


export function useGetApiPlatformAdminTenantsTenantIdDeletionPreview<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdDeletionPreview>>, TError = Error>(
 tenantId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdDeletionPreview>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdDeletionPreview>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdDeletionPreview>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminTenantsTenantIdDeletionPreview<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdDeletionPreview>>, TError = Error>(
 tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdDeletionPreview>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdDeletionPreview>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdDeletionPreview>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminTenantsTenantIdDeletionPreview<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdDeletionPreview>>, TError = Error>(
 tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdDeletionPreview>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Preview tenant deletion impact and governance blockers
 */

export function useGetApiPlatformAdminTenantsTenantIdDeletionPreview<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdDeletionPreview>>, TError = Error>(
 tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdDeletionPreview>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiPlatformAdminTenantsTenantIdDeletionPreviewQueryOptions(tenantId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetApiPlatformAdminAdminsUrl = () => {




  return `/api/platform-admin/admins`
}

/**
 * @summary List platform admins (platform admin only)
 */
export const getApiPlatformAdminAdmins = async ( options?: Parameters<typeof customFetch>[1]): Promise<GetApiPlatformAdminAdmins200> => {

  return customFetch<GetApiPlatformAdminAdmins200>(getGetApiPlatformAdminAdminsUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiPlatformAdminAdminsQueryKey = () => {
    return [
    `/api/platform-admin/admins`
    ] as const;
    }


export const getGetApiPlatformAdminAdminsQueryOptions = <TData = Awaited<ReturnType<typeof getApiPlatformAdminAdmins>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminAdmins>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiPlatformAdminAdminsQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiPlatformAdminAdmins>>> = ({ signal }) => getApiPlatformAdminAdmins({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminAdmins>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiPlatformAdminAdminsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiPlatformAdminAdmins>>>
export type GetApiPlatformAdminAdminsQueryError = Error


export function useGetApiPlatformAdminAdmins<TData = Awaited<ReturnType<typeof getApiPlatformAdminAdmins>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminAdmins>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminAdmins>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminAdmins>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminAdmins<TData = Awaited<ReturnType<typeof getApiPlatformAdminAdmins>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminAdmins>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminAdmins>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminAdmins>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminAdmins<TData = Awaited<ReturnType<typeof getApiPlatformAdminAdmins>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminAdmins>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List platform admins (platform admin only)
 */

export function useGetApiPlatformAdminAdmins<TData = Awaited<ReturnType<typeof getApiPlatformAdminAdmins>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminAdmins>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiPlatformAdminAdminsQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPostApiPlatformAdminAdminsUrl = () => {




  return `/api/platform-admin/admins`
}

/**
 * @summary Add a platform admin (platform admin only)
 */
export const postApiPlatformAdminAdmins = async (postApiPlatformAdminAdminsBody: PostApiPlatformAdminAdminsBody, options?: Parameters<typeof customFetch>[1]): Promise<PlatformAdmin> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PlatformAdmin>(getPostApiPlatformAdminAdminsUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(postApiPlatformAdminAdminsBody)
  }
);}





export const getPostApiPlatformAdminAdminsMutationKey = () => ['postApiPlatformAdminAdmins'] as const;

export const getPostApiPlatformAdminAdminsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminAdmins>>, TError,PostApiPlatformAdminAdminsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminAdmins>>, TError,PostApiPlatformAdminAdminsMutationVariables, TContext> => {

const mutationKey = getPostApiPlatformAdminAdminsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiPlatformAdminAdmins>>, PostApiPlatformAdminAdminsMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  postApiPlatformAdminAdmins(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostApiPlatformAdminAdminsMutationResult = NonNullable<Awaited<ReturnType<typeof postApiPlatformAdminAdmins>>>
    export type PostApiPlatformAdminAdminsMutationBody = PostApiPlatformAdminAdminsBody
    export type PostApiPlatformAdminAdminsMutationError = Error
    export type PostApiPlatformAdminAdminsMutationVariables = {data: PostApiPlatformAdminAdminsBody}

    /**
 * @summary Add a platform admin (platform admin only)
 */
export const usePostApiPlatformAdminAdmins = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminAdmins>>, TError,PostApiPlatformAdminAdminsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postApiPlatformAdminAdmins>>,
        TError,
        PostApiPlatformAdminAdminsMutationVariables,
        TContext
      > => {
      return useMutation(getPostApiPlatformAdminAdminsMutationOptions(options), queryClient);
    }

export const getDeleteApiPlatformAdminAdminsUserIdUrl = (userId: string,) => {




  return `/api/platform-admin/admins/${userId}`
}

/**
 * @summary Remove a platform admin (platform admin only)
 */
export const deleteApiPlatformAdminAdminsUserId = async (userId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteApiPlatformAdminAdminsUserIdUrl(userId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteApiPlatformAdminAdminsUserIdMutationKey = () => ['deleteApiPlatformAdminAdminsUserId'] as const;

export const getDeleteApiPlatformAdminAdminsUserIdMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteApiPlatformAdminAdminsUserId>>, TError,DeleteApiPlatformAdminAdminsUserIdMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteApiPlatformAdminAdminsUserId>>, TError,DeleteApiPlatformAdminAdminsUserIdMutationVariables, TContext> => {

const mutationKey = getDeleteApiPlatformAdminAdminsUserIdMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteApiPlatformAdminAdminsUserId>>, DeleteApiPlatformAdminAdminsUserIdMutationVariables> = (props) => {
          const {userId} = props ?? {};

          return  deleteApiPlatformAdminAdminsUserId(userId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteApiPlatformAdminAdminsUserIdMutationResult = NonNullable<Awaited<ReturnType<typeof deleteApiPlatformAdminAdminsUserId>>>

    export type DeleteApiPlatformAdminAdminsUserIdMutationError = Error
    export type DeleteApiPlatformAdminAdminsUserIdMutationVariables = {userId: string}

    /**
 * @summary Remove a platform admin (platform admin only)
 */
export const useDeleteApiPlatformAdminAdminsUserId = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteApiPlatformAdminAdminsUserId>>, TError,DeleteApiPlatformAdminAdminsUserIdMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteApiPlatformAdminAdminsUserId>>,
        TError,
        DeleteApiPlatformAdminAdminsUserIdMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteApiPlatformAdminAdminsUserIdMutationOptions(options), queryClient);
    }

export const getPostApiInvitesAcceptUrl = () => {




  return `/api/invites/accept`
}

/**
 * @summary Accept a tenant invite by token (signed-in user)
 */
export const postApiInvitesAccept = async (postApiInvitesAcceptBody: PostApiInvitesAcceptBody, options?: Parameters<typeof customFetch>[1]): Promise<PostApiInvitesAccept200> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PostApiInvitesAccept200>(getPostApiInvitesAcceptUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(postApiInvitesAcceptBody)
  }
);}





export const getPostApiInvitesAcceptMutationKey = () => ['postApiInvitesAccept'] as const;

export const getPostApiInvitesAcceptMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiInvitesAccept>>, TError,PostApiInvitesAcceptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postApiInvitesAccept>>, TError,PostApiInvitesAcceptMutationVariables, TContext> => {

const mutationKey = getPostApiInvitesAcceptMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiInvitesAccept>>, PostApiInvitesAcceptMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  postApiInvitesAccept(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostApiInvitesAcceptMutationResult = NonNullable<Awaited<ReturnType<typeof postApiInvitesAccept>>>
    export type PostApiInvitesAcceptMutationBody = PostApiInvitesAcceptBody
    export type PostApiInvitesAcceptMutationError = Error
    export type PostApiInvitesAcceptMutationVariables = {data: PostApiInvitesAcceptBody}

    /**
 * @summary Accept a tenant invite by token (signed-in user)
 */
export const usePostApiInvitesAccept = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiInvitesAccept>>, TError,PostApiInvitesAcceptMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postApiInvitesAccept>>,
        TError,
        PostApiInvitesAcceptMutationVariables,
        TContext
      > => {
      return useMutation(getPostApiInvitesAcceptMutationOptions(options), queryClient);
    }

export const getGetApiTenantsTenantSlugEntityUrl = (tenantSlug: string,) => {




  return `/api/tenants/${tenantSlug}/entity`
}

/**
 * @summary Read the selected entity profile
 */
export const getApiTenantsTenantSlugEntity = async (tenantSlug: string, options?: Parameters<typeof customFetch>[1]): Promise<EntityDetail> => {

  return customFetch<EntityDetail>(getGetApiTenantsTenantSlugEntityUrl(tenantSlug),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiTenantsTenantSlugEntityQueryKey = (tenantSlug: string,) => {
    return [
    `/api/tenants/${tenantSlug}/entity`
    ] as const;
    }


export const getGetApiTenantsTenantSlugEntityQueryOptions = <TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugEntity>>, TError = Error>(tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugEntity>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiTenantsTenantSlugEntityQueryKey(tenantSlug);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiTenantsTenantSlugEntity>>> = ({ signal }) => getApiTenantsTenantSlugEntity(tenantSlug, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugEntity>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiTenantsTenantSlugEntityQueryResult = NonNullable<Awaited<ReturnType<typeof getApiTenantsTenantSlugEntity>>>
export type GetApiTenantsTenantSlugEntityQueryError = Error


export function useGetApiTenantsTenantSlugEntity<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugEntity>>, TError = Error>(
 tenantSlug: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugEntity>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugEntity>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugEntity>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugEntity<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugEntity>>, TError = Error>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugEntity>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugEntity>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugEntity>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugEntity<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugEntity>>, TError = Error>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugEntity>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read the selected entity profile
 */

export function useGetApiTenantsTenantSlugEntity<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugEntity>>, TError = Error>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugEntity>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiTenantsTenantSlugEntityQueryOptions(tenantSlug,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPatchApiTenantsTenantSlugEntityUrl = (tenantSlug: string,) => {




  return `/api/tenants/${tenantSlug}/entity`
}

/**
 * @summary Update the selected entity profile
 */
export const patchApiTenantsTenantSlugEntity = async (tenantSlug: string,
    entityProfileUpdate: EntityProfileUpdate, options?: Parameters<typeof customFetch>[1]): Promise<EntityDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EntityDetail>(getPatchApiTenantsTenantSlugEntityUrl(tenantSlug),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(entityProfileUpdate)
  }
);}





export const getPatchApiTenantsTenantSlugEntityMutationKey = () => ['patchApiTenantsTenantSlugEntity'] as const;

export const getPatchApiTenantsTenantSlugEntityMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugEntity>>, TError,PatchApiTenantsTenantSlugEntityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugEntity>>, TError,PatchApiTenantsTenantSlugEntityMutationVariables, TContext> => {

const mutationKey = getPatchApiTenantsTenantSlugEntityMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchApiTenantsTenantSlugEntity>>, PatchApiTenantsTenantSlugEntityMutationVariables> = (props) => {
          const {tenantSlug,data} = props ?? {};

          return  patchApiTenantsTenantSlugEntity(tenantSlug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchApiTenantsTenantSlugEntityMutationResult = NonNullable<Awaited<ReturnType<typeof patchApiTenantsTenantSlugEntity>>>
    export type PatchApiTenantsTenantSlugEntityMutationBody = EntityProfileUpdate
    export type PatchApiTenantsTenantSlugEntityMutationError = Error
    export type PatchApiTenantsTenantSlugEntityMutationVariables = {tenantSlug: string;data: EntityProfileUpdate}

    /**
 * @summary Update the selected entity profile
 */
export const usePatchApiTenantsTenantSlugEntity = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugEntity>>, TError,PatchApiTenantsTenantSlugEntityMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchApiTenantsTenantSlugEntity>>,
        TError,
        PatchApiTenantsTenantSlugEntityMutationVariables,
        TContext
      > => {
      return useMutation(getPatchApiTenantsTenantSlugEntityMutationOptions(options), queryClient);
    }

export const getGetApiTenantsTenantSlugEntitiesUrl = (tenantSlug: string,
    params?: GetApiTenantsTenantSlugEntitiesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/entities?${stringifiedParams}` : `/api/tenants/${tenantSlug}/entities`
}

/**
 * @summary List granted entities in the selected governance tenant
 */
export const getApiTenantsTenantSlugEntities = async (tenantSlug: string,
    params?: GetApiTenantsTenantSlugEntitiesParams, options?: Parameters<typeof customFetch>[1]): Promise<EntityPage> => {

  return customFetch<EntityPage>(getGetApiTenantsTenantSlugEntitiesUrl(tenantSlug,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiTenantsTenantSlugEntitiesQueryKey = (tenantSlug: string,
    params?: GetApiTenantsTenantSlugEntitiesParams,) => {
    return [
    `/api/tenants/${tenantSlug}/entities`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetApiTenantsTenantSlugEntitiesQueryOptions = <TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugEntities>>, TError = unknown>(tenantSlug: string,
    params?: GetApiTenantsTenantSlugEntitiesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugEntities>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiTenantsTenantSlugEntitiesQueryKey(tenantSlug,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiTenantsTenantSlugEntities>>> = ({ signal }) => getApiTenantsTenantSlugEntities(tenantSlug,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugEntities>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiTenantsTenantSlugEntitiesQueryResult = NonNullable<Awaited<ReturnType<typeof getApiTenantsTenantSlugEntities>>>
export type GetApiTenantsTenantSlugEntitiesQueryError = unknown


export function useGetApiTenantsTenantSlugEntities<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugEntities>>, TError = unknown>(
 tenantSlug: string,
    params: undefined |  GetApiTenantsTenantSlugEntitiesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugEntities>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugEntities>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugEntities>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugEntities<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugEntities>>, TError = unknown>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugEntitiesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugEntities>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugEntities>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugEntities>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugEntities<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugEntities>>, TError = unknown>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugEntitiesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugEntities>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List granted entities in the selected governance tenant
 */

export function useGetApiTenantsTenantSlugEntities<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugEntities>>, TError = unknown>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugEntitiesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugEntities>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiTenantsTenantSlugEntitiesQueryOptions(tenantSlug,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPostApiTenantsTenantSlugEntitiesUrl = (tenantSlug: string,) => {




  return `/api/tenants/${tenantSlug}/entities`
}

/**
 * @summary Create an entity in the selected governance tenant
 */
export const postApiTenantsTenantSlugEntities = async (tenantSlug: string,
    postApiTenantsTenantSlugEntitiesBody: PostApiTenantsTenantSlugEntitiesBody, options?: Parameters<typeof customFetch>[1]): Promise<EntityDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EntityDetail>(getPostApiTenantsTenantSlugEntitiesUrl(tenantSlug),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(postApiTenantsTenantSlugEntitiesBody)
  }
);}





export const getPostApiTenantsTenantSlugEntitiesMutationKey = () => ['postApiTenantsTenantSlugEntities'] as const;

export const getPostApiTenantsTenantSlugEntitiesMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugEntities>>, TError,PostApiTenantsTenantSlugEntitiesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugEntities>>, TError,PostApiTenantsTenantSlugEntitiesMutationVariables, TContext> => {

const mutationKey = getPostApiTenantsTenantSlugEntitiesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiTenantsTenantSlugEntities>>, PostApiTenantsTenantSlugEntitiesMutationVariables> = (props) => {
          const {tenantSlug,data} = props ?? {};

          return  postApiTenantsTenantSlugEntities(tenantSlug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostApiTenantsTenantSlugEntitiesMutationResult = NonNullable<Awaited<ReturnType<typeof postApiTenantsTenantSlugEntities>>>
    export type PostApiTenantsTenantSlugEntitiesMutationBody = PostApiTenantsTenantSlugEntitiesBody
    export type PostApiTenantsTenantSlugEntitiesMutationError = Error
    export type PostApiTenantsTenantSlugEntitiesMutationVariables = {tenantSlug: string;data: PostApiTenantsTenantSlugEntitiesBody}

    /**
 * @summary Create an entity in the selected governance tenant
 */
export const usePostApiTenantsTenantSlugEntities = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugEntities>>, TError,PostApiTenantsTenantSlugEntitiesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postApiTenantsTenantSlugEntities>>,
        TError,
        PostApiTenantsTenantSlugEntitiesMutationVariables,
        TContext
      > => {
      return useMutation(getPostApiTenantsTenantSlugEntitiesMutationOptions(options), queryClient);
    }

export const getPostApiTenantsTenantSlugEntityLifecycleUrl = (tenantSlug: string,) => {




  return `/api/tenants/${tenantSlug}/entity/lifecycle`
}

/**
 * @summary Suspend or restore the selected entity
 */
export const postApiTenantsTenantSlugEntityLifecycle = async (tenantSlug: string,
    postApiTenantsTenantSlugEntityLifecycleBody: PostApiTenantsTenantSlugEntityLifecycleBody, options?: Parameters<typeof customFetch>[1]): Promise<EntityDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EntityDetail>(getPostApiTenantsTenantSlugEntityLifecycleUrl(tenantSlug),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(postApiTenantsTenantSlugEntityLifecycleBody)
  }
);}





export const getPostApiTenantsTenantSlugEntityLifecycleMutationKey = () => ['postApiTenantsTenantSlugEntityLifecycle'] as const;

export const getPostApiTenantsTenantSlugEntityLifecycleMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugEntityLifecycle>>, TError,PostApiTenantsTenantSlugEntityLifecycleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugEntityLifecycle>>, TError,PostApiTenantsTenantSlugEntityLifecycleMutationVariables, TContext> => {

const mutationKey = getPostApiTenantsTenantSlugEntityLifecycleMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiTenantsTenantSlugEntityLifecycle>>, PostApiTenantsTenantSlugEntityLifecycleMutationVariables> = (props) => {
          const {tenantSlug,data} = props ?? {};

          return  postApiTenantsTenantSlugEntityLifecycle(tenantSlug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostApiTenantsTenantSlugEntityLifecycleMutationResult = NonNullable<Awaited<ReturnType<typeof postApiTenantsTenantSlugEntityLifecycle>>>
    export type PostApiTenantsTenantSlugEntityLifecycleMutationBody = PostApiTenantsTenantSlugEntityLifecycleBody
    export type PostApiTenantsTenantSlugEntityLifecycleMutationError = unknown
    export type PostApiTenantsTenantSlugEntityLifecycleMutationVariables = {tenantSlug: string;data: PostApiTenantsTenantSlugEntityLifecycleBody}

    /**
 * @summary Suspend or restore the selected entity
 */
export const usePostApiTenantsTenantSlugEntityLifecycle = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugEntityLifecycle>>, TError,PostApiTenantsTenantSlugEntityLifecycleMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postApiTenantsTenantSlugEntityLifecycle>>,
        TError,
        PostApiTenantsTenantSlugEntityLifecycleMutationVariables,
        TContext
      > => {
      return useMutation(getPostApiTenantsTenantSlugEntityLifecycleMutationOptions(options), queryClient);
    }

export const getGetApiTenantsTenantSlugMemberCandidatesUrl = (tenantSlug: string,
    params?: GetApiTenantsTenantSlugMemberCandidatesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/member-candidates?${stringifiedParams}` : `/api/tenants/${tenantSlug}/member-candidates`
}

/**
 * Requires existing entity member-management permission. Effective platform administrators may select any registered user; others may select only active members of this entity's parent tenant.
 * @summary Find eligible registered users for entity membership
 */
export const getApiTenantsTenantSlugMemberCandidates = async (tenantSlug: string,
    params?: GetApiTenantsTenantSlugMemberCandidatesParams, options?: Parameters<typeof customFetch>[1]): Promise<MemberCandidatePage> => {

  return customFetch<MemberCandidatePage>(getGetApiTenantsTenantSlugMemberCandidatesUrl(tenantSlug,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiTenantsTenantSlugMemberCandidatesQueryKey = (tenantSlug: string,
    params?: GetApiTenantsTenantSlugMemberCandidatesParams,) => {
    return [
    `/api/tenants/${tenantSlug}/member-candidates`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetApiTenantsTenantSlugMemberCandidatesQueryOptions = <TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugMemberCandidates>>, TError = Error>(tenantSlug: string,
    params?: GetApiTenantsTenantSlugMemberCandidatesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugMemberCandidates>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiTenantsTenantSlugMemberCandidatesQueryKey(tenantSlug,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiTenantsTenantSlugMemberCandidates>>> = ({ signal }) => getApiTenantsTenantSlugMemberCandidates(tenantSlug,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugMemberCandidates>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiTenantsTenantSlugMemberCandidatesQueryResult = NonNullable<Awaited<ReturnType<typeof getApiTenantsTenantSlugMemberCandidates>>>
export type GetApiTenantsTenantSlugMemberCandidatesQueryError = Error


export function useGetApiTenantsTenantSlugMemberCandidates<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugMemberCandidates>>, TError = Error>(
 tenantSlug: string,
    params: undefined |  GetApiTenantsTenantSlugMemberCandidatesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugMemberCandidates>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugMemberCandidates>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugMemberCandidates>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugMemberCandidates<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugMemberCandidates>>, TError = Error>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugMemberCandidatesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugMemberCandidates>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugMemberCandidates>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugMemberCandidates>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugMemberCandidates<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugMemberCandidates>>, TError = Error>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugMemberCandidatesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugMemberCandidates>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Find eligible registered users for entity membership
 */

export function useGetApiTenantsTenantSlugMemberCandidates<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugMemberCandidates>>, TError = Error>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugMemberCandidatesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugMemberCandidates>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiTenantsTenantSlugMemberCandidatesQueryOptions(tenantSlug,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetOrganisationGroupAccessStatusUrl = (tenantSlug: string,) => {




  return `/api/tenants/${tenantSlug}/group-access/status`
}

/**
 * @summary Read administrator Microsoft group setup status
 */
export const getOrganisationGroupAccessStatus = async (tenantSlug: string, options?: Parameters<typeof customFetch>[1]): Promise<OrganisationGroupAccessStatus> => {

  return customFetch<OrganisationGroupAccessStatus>(getGetOrganisationGroupAccessStatusUrl(tenantSlug),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetOrganisationGroupAccessStatusQueryKey = (tenantSlug: string,) => {
    return [
    `/api/tenants/${tenantSlug}/group-access/status`
    ] as const;
    }


export const getGetOrganisationGroupAccessStatusQueryOptions = <TData = Awaited<ReturnType<typeof getOrganisationGroupAccessStatus>>, TError = Error>(tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationGroupAccessStatus>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetOrganisationGroupAccessStatusQueryKey(tenantSlug);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getOrganisationGroupAccessStatus>>> = ({ signal }) => getOrganisationGroupAccessStatus(tenantSlug, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getOrganisationGroupAccessStatus>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetOrganisationGroupAccessStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getOrganisationGroupAccessStatus>>>
export type GetOrganisationGroupAccessStatusQueryError = Error


export function useGetOrganisationGroupAccessStatus<TData = Awaited<ReturnType<typeof getOrganisationGroupAccessStatus>>, TError = Error>(
 tenantSlug: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationGroupAccessStatus>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganisationGroupAccessStatus>>,
          TError,
          Awaited<ReturnType<typeof getOrganisationGroupAccessStatus>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetOrganisationGroupAccessStatus<TData = Awaited<ReturnType<typeof getOrganisationGroupAccessStatus>>, TError = Error>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationGroupAccessStatus>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganisationGroupAccessStatus>>,
          TError,
          Awaited<ReturnType<typeof getOrganisationGroupAccessStatus>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetOrganisationGroupAccessStatus<TData = Awaited<ReturnType<typeof getOrganisationGroupAccessStatus>>, TError = Error>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationGroupAccessStatus>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read administrator Microsoft group setup status
 */

export function useGetOrganisationGroupAccessStatus<TData = Awaited<ReturnType<typeof getOrganisationGroupAccessStatus>>, TError = Error>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationGroupAccessStatus>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetOrganisationGroupAccessStatusQueryOptions(tenantSlug,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSearchOrganisationDirectoryGroupsUrl = (tenantSlug: string,
    params: SearchOrganisationDirectoryGroupsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/directory-groups?${stringifiedParams}` : `/api/tenants/${tenantSlug}/directory-groups`
}

/**
 * Display-name prefix search. Signed continuation cursors bind actor, organisation, directory, prefix and page size, expire after ten minutes and on server restart, and allow at most 100 pages. Hidden groups are visible as unusable; unsupported group types are omitted.
 * @summary Search real groups in the configured sign-in directory as an administrator
 */
export const searchOrganisationDirectoryGroups = async (tenantSlug: string,
    params: SearchOrganisationDirectoryGroupsParams, options?: Parameters<typeof customFetch>[1]): Promise<OrganisationDirectoryGroupPage> => {

  return customFetch<OrganisationDirectoryGroupPage>(getSearchOrganisationDirectoryGroupsUrl(tenantSlug,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getSearchOrganisationDirectoryGroupsQueryKey = (tenantSlug: string,
    params?: SearchOrganisationDirectoryGroupsParams,) => {
    return [
    `/api/tenants/${tenantSlug}/directory-groups`, ...(params ? [params] : [])
    ] as const;
    }


export const getSearchOrganisationDirectoryGroupsQueryOptions = <TData = Awaited<ReturnType<typeof searchOrganisationDirectoryGroups>>, TError = Error>(tenantSlug: string,
    params: SearchOrganisationDirectoryGroupsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchOrganisationDirectoryGroups>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getSearchOrganisationDirectoryGroupsQueryKey(tenantSlug,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof searchOrganisationDirectoryGroups>>> = ({ signal }) => searchOrganisationDirectoryGroups(tenantSlug,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof searchOrganisationDirectoryGroups>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type SearchOrganisationDirectoryGroupsQueryResult = NonNullable<Awaited<ReturnType<typeof searchOrganisationDirectoryGroups>>>
export type SearchOrganisationDirectoryGroupsQueryError = Error


export function useSearchOrganisationDirectoryGroups<TData = Awaited<ReturnType<typeof searchOrganisationDirectoryGroups>>, TError = Error>(
 tenantSlug: string,
    params: SearchOrganisationDirectoryGroupsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchOrganisationDirectoryGroups>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchOrganisationDirectoryGroups>>,
          TError,
          Awaited<ReturnType<typeof searchOrganisationDirectoryGroups>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSearchOrganisationDirectoryGroups<TData = Awaited<ReturnType<typeof searchOrganisationDirectoryGroups>>, TError = Error>(
 tenantSlug: string,
    params: SearchOrganisationDirectoryGroupsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchOrganisationDirectoryGroups>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof searchOrganisationDirectoryGroups>>,
          TError,
          Awaited<ReturnType<typeof searchOrganisationDirectoryGroups>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useSearchOrganisationDirectoryGroups<TData = Awaited<ReturnType<typeof searchOrganisationDirectoryGroups>>, TError = Error>(
 tenantSlug: string,
    params: SearchOrganisationDirectoryGroupsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchOrganisationDirectoryGroups>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Search real groups in the configured sign-in directory as an administrator
 */

export function useSearchOrganisationDirectoryGroups<TData = Awaited<ReturnType<typeof searchOrganisationDirectoryGroups>>, TError = Error>(
 tenantSlug: string,
    params: SearchOrganisationDirectoryGroupsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof searchOrganisationDirectoryGroups>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getSearchOrganisationDirectoryGroupsQueryOptions(tenantSlug,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetOrganisationDirectoryGroupUrl = (tenantSlug: string,
    groupId: string,) => {




  return `/api/tenants/${tenantSlug}/directory-groups/${groupId}`
}

/**
 * @summary Read and validate one real directory group as an administrator
 */
export const getOrganisationDirectoryGroup = async (tenantSlug: string,
    groupId: string, options?: Parameters<typeof customFetch>[1]): Promise<OrganisationDirectoryGroup> => {

  return customFetch<OrganisationDirectoryGroup>(getGetOrganisationDirectoryGroupUrl(tenantSlug,groupId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetOrganisationDirectoryGroupQueryKey = (tenantSlug: string,
    groupId: string,) => {
    return [
    `/api/tenants/${tenantSlug}/directory-groups/${groupId}`
    ] as const;
    }


export const getGetOrganisationDirectoryGroupQueryOptions = <TData = Awaited<ReturnType<typeof getOrganisationDirectoryGroup>>, TError = Error>(tenantSlug: string,
    groupId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationDirectoryGroup>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetOrganisationDirectoryGroupQueryKey(tenantSlug,groupId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getOrganisationDirectoryGroup>>> = ({ signal }) => getOrganisationDirectoryGroup(tenantSlug,groupId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined && groupId !== null && groupId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getOrganisationDirectoryGroup>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetOrganisationDirectoryGroupQueryResult = NonNullable<Awaited<ReturnType<typeof getOrganisationDirectoryGroup>>>
export type GetOrganisationDirectoryGroupQueryError = Error


export function useGetOrganisationDirectoryGroup<TData = Awaited<ReturnType<typeof getOrganisationDirectoryGroup>>, TError = Error>(
 tenantSlug: string,
    groupId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationDirectoryGroup>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganisationDirectoryGroup>>,
          TError,
          Awaited<ReturnType<typeof getOrganisationDirectoryGroup>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetOrganisationDirectoryGroup<TData = Awaited<ReturnType<typeof getOrganisationDirectoryGroup>>, TError = Error>(
 tenantSlug: string,
    groupId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationDirectoryGroup>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganisationDirectoryGroup>>,
          TError,
          Awaited<ReturnType<typeof getOrganisationDirectoryGroup>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetOrganisationDirectoryGroup<TData = Awaited<ReturnType<typeof getOrganisationDirectoryGroup>>, TError = Error>(
 tenantSlug: string,
    groupId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationDirectoryGroup>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read and validate one real directory group as an administrator
 */

export function useGetOrganisationDirectoryGroup<TData = Awaited<ReturnType<typeof getOrganisationDirectoryGroup>>, TError = Error>(
 tenantSlug: string,
    groupId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationDirectoryGroup>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetOrganisationDirectoryGroupQueryOptions(tenantSlug,groupId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListOrganisationGroupRolesUrl = (tenantSlug: string,
    params?: ListOrganisationGroupRolesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/group-access/roles?${stringifiedParams}` : `/api/tenants/${tenantSlug}/group-access/roles`
}

/**
 * @summary Page existing organisation roles available to group administrators
 */
export const listOrganisationGroupRoles = async (tenantSlug: string,
    params?: ListOrganisationGroupRolesParams, options?: Parameters<typeof customFetch>[1]): Promise<ListOrganisationGroupRoles200> => {

  return customFetch<ListOrganisationGroupRoles200>(getListOrganisationGroupRolesUrl(tenantSlug,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListOrganisationGroupRolesQueryKey = (tenantSlug: string,
    params?: ListOrganisationGroupRolesParams,) => {
    return [
    `/api/tenants/${tenantSlug}/group-access/roles`, ...(params ? [params] : [])
    ] as const;
    }


export const getListOrganisationGroupRolesQueryOptions = <TData = Awaited<ReturnType<typeof listOrganisationGroupRoles>>, TError = void>(tenantSlug: string,
    params?: ListOrganisationGroupRolesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOrganisationGroupRoles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListOrganisationGroupRolesQueryKey(tenantSlug,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listOrganisationGroupRoles>>> = ({ signal }) => listOrganisationGroupRoles(tenantSlug,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listOrganisationGroupRoles>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListOrganisationGroupRolesQueryResult = NonNullable<Awaited<ReturnType<typeof listOrganisationGroupRoles>>>
export type ListOrganisationGroupRolesQueryError = void


export function useListOrganisationGroupRoles<TData = Awaited<ReturnType<typeof listOrganisationGroupRoles>>, TError = void>(
 tenantSlug: string,
    params: undefined |  ListOrganisationGroupRolesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOrganisationGroupRoles>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listOrganisationGroupRoles>>,
          TError,
          Awaited<ReturnType<typeof listOrganisationGroupRoles>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListOrganisationGroupRoles<TData = Awaited<ReturnType<typeof listOrganisationGroupRoles>>, TError = void>(
 tenantSlug: string,
    params?: ListOrganisationGroupRolesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOrganisationGroupRoles>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listOrganisationGroupRoles>>,
          TError,
          Awaited<ReturnType<typeof listOrganisationGroupRoles>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListOrganisationGroupRoles<TData = Awaited<ReturnType<typeof listOrganisationGroupRoles>>, TError = void>(
 tenantSlug: string,
    params?: ListOrganisationGroupRolesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOrganisationGroupRoles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Page existing organisation roles available to group administrators
 */

export function useListOrganisationGroupRoles<TData = Awaited<ReturnType<typeof listOrganisationGroupRoles>>, TError = void>(
 tenantSlug: string,
    params?: ListOrganisationGroupRolesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOrganisationGroupRoles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListOrganisationGroupRolesQueryOptions(tenantSlug,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListOrganisationGroupBindingsUrl = (tenantSlug: string,
    params?: ListOrganisationGroupBindingsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/group-bindings?${stringifiedParams}` : `/api/tenants/${tenantSlug}/group-bindings`
}

/**
 * @summary List organisation group role bindings as an administrator
 */
export const listOrganisationGroupBindings = async (tenantSlug: string,
    params?: ListOrganisationGroupBindingsParams, options?: Parameters<typeof customFetch>[1]): Promise<OrganisationGroupBindingPage> => {

  return customFetch<OrganisationGroupBindingPage>(getListOrganisationGroupBindingsUrl(tenantSlug,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListOrganisationGroupBindingsQueryKey = (tenantSlug: string,
    params?: ListOrganisationGroupBindingsParams,) => {
    return [
    `/api/tenants/${tenantSlug}/group-bindings`, ...(params ? [params] : [])
    ] as const;
    }


export const getListOrganisationGroupBindingsQueryOptions = <TData = Awaited<ReturnType<typeof listOrganisationGroupBindings>>, TError = Error>(tenantSlug: string,
    params?: ListOrganisationGroupBindingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOrganisationGroupBindings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListOrganisationGroupBindingsQueryKey(tenantSlug,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listOrganisationGroupBindings>>> = ({ signal }) => listOrganisationGroupBindings(tenantSlug,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listOrganisationGroupBindings>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListOrganisationGroupBindingsQueryResult = NonNullable<Awaited<ReturnType<typeof listOrganisationGroupBindings>>>
export type ListOrganisationGroupBindingsQueryError = Error


export function useListOrganisationGroupBindings<TData = Awaited<ReturnType<typeof listOrganisationGroupBindings>>, TError = Error>(
 tenantSlug: string,
    params: undefined |  ListOrganisationGroupBindingsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOrganisationGroupBindings>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listOrganisationGroupBindings>>,
          TError,
          Awaited<ReturnType<typeof listOrganisationGroupBindings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListOrganisationGroupBindings<TData = Awaited<ReturnType<typeof listOrganisationGroupBindings>>, TError = Error>(
 tenantSlug: string,
    params?: ListOrganisationGroupBindingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOrganisationGroupBindings>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listOrganisationGroupBindings>>,
          TError,
          Awaited<ReturnType<typeof listOrganisationGroupBindings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListOrganisationGroupBindings<TData = Awaited<ReturnType<typeof listOrganisationGroupBindings>>, TError = Error>(
 tenantSlug: string,
    params?: ListOrganisationGroupBindingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOrganisationGroupBindings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List organisation group role bindings as an administrator
 */

export function useListOrganisationGroupBindings<TData = Awaited<ReturnType<typeof listOrganisationGroupBindings>>, TError = Error>(
 tenantSlug: string,
    params?: ListOrganisationGroupBindingsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOrganisationGroupBindings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListOrganisationGroupBindingsQueryOptions(tenantSlug,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateOrganisationGroupBindingUrl = (tenantSlug: string,) => {




  return `/api/tenants/${tenantSlug}/group-bindings`
}

/**
 * @summary Bind a verified directory group to allowed existing organisation roles
 */
export const createOrganisationGroupBinding = async (tenantSlug: string,
    createOrganisationGroupBindingBody: CreateOrganisationGroupBinding, options?: Parameters<typeof customFetch>[1]): Promise<OrganisationGroupBinding> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OrganisationGroupBinding>(getCreateOrganisationGroupBindingUrl(tenantSlug),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createOrganisationGroupBindingBody)
  }
);}





export const getCreateOrganisationGroupBindingMutationKey = () => ['createOrganisationGroupBinding'] as const;

export const getCreateOrganisationGroupBindingMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createOrganisationGroupBinding>>, TError,CreateOrganisationGroupBindingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createOrganisationGroupBinding>>, TError,CreateOrganisationGroupBindingMutationVariables, TContext> => {

const mutationKey = getCreateOrganisationGroupBindingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createOrganisationGroupBinding>>, CreateOrganisationGroupBindingMutationVariables> = (props) => {
          const {tenantSlug,data} = props ?? {};

          return  createOrganisationGroupBinding(tenantSlug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateOrganisationGroupBindingMutationResult = NonNullable<Awaited<ReturnType<typeof createOrganisationGroupBinding>>>
    export type CreateOrganisationGroupBindingMutationBody = CreateOrganisationGroupBinding
    export type CreateOrganisationGroupBindingMutationError = Error
    export type CreateOrganisationGroupBindingMutationVariables = {tenantSlug: string;data: CreateOrganisationGroupBinding}

    /**
 * @summary Bind a verified directory group to allowed existing organisation roles
 */
export const useCreateOrganisationGroupBinding = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createOrganisationGroupBinding>>, TError,CreateOrganisationGroupBindingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createOrganisationGroupBinding>>,
        TError,
        CreateOrganisationGroupBindingMutationVariables,
        TContext
      > => {
      return useMutation(getCreateOrganisationGroupBindingMutationOptions(options), queryClient);
    }

export const getGetOrganisationGroupBindingUrl = (tenantSlug: string,
    bindingId: string,) => {




  return `/api/tenants/${tenantSlug}/group-bindings/${bindingId}`
}

/**
 * @summary Read an organisation group binding
 */
export const getOrganisationGroupBinding = async (tenantSlug: string,
    bindingId: string, options?: Parameters<typeof customFetch>[1]): Promise<OrganisationGroupBinding> => {

  return customFetch<OrganisationGroupBinding>(getGetOrganisationGroupBindingUrl(tenantSlug,bindingId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetOrganisationGroupBindingQueryKey = (tenantSlug: string,
    bindingId: string,) => {
    return [
    `/api/tenants/${tenantSlug}/group-bindings/${bindingId}`
    ] as const;
    }


export const getGetOrganisationGroupBindingQueryOptions = <TData = Awaited<ReturnType<typeof getOrganisationGroupBinding>>, TError = Error>(tenantSlug: string,
    bindingId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationGroupBinding>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetOrganisationGroupBindingQueryKey(tenantSlug,bindingId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getOrganisationGroupBinding>>> = ({ signal }) => getOrganisationGroupBinding(tenantSlug,bindingId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined && bindingId !== null && bindingId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getOrganisationGroupBinding>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetOrganisationGroupBindingQueryResult = NonNullable<Awaited<ReturnType<typeof getOrganisationGroupBinding>>>
export type GetOrganisationGroupBindingQueryError = Error


export function useGetOrganisationGroupBinding<TData = Awaited<ReturnType<typeof getOrganisationGroupBinding>>, TError = Error>(
 tenantSlug: string,
    bindingId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationGroupBinding>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganisationGroupBinding>>,
          TError,
          Awaited<ReturnType<typeof getOrganisationGroupBinding>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetOrganisationGroupBinding<TData = Awaited<ReturnType<typeof getOrganisationGroupBinding>>, TError = Error>(
 tenantSlug: string,
    bindingId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationGroupBinding>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganisationGroupBinding>>,
          TError,
          Awaited<ReturnType<typeof getOrganisationGroupBinding>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetOrganisationGroupBinding<TData = Awaited<ReturnType<typeof getOrganisationGroupBinding>>, TError = Error>(
 tenantSlug: string,
    bindingId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationGroupBinding>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read an organisation group binding
 */

export function useGetOrganisationGroupBinding<TData = Awaited<ReturnType<typeof getOrganisationGroupBinding>>, TError = Error>(
 tenantSlug: string,
    bindingId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationGroupBinding>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetOrganisationGroupBindingQueryOptions(tenantSlug,bindingId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getUpdateOrganisationGroupBindingUrl = (tenantSlug: string,
    bindingId: string,) => {




  return `/api/tenants/${tenantSlug}/group-bindings/${bindingId}`
}

/**
 * @summary Update group roles or enabled state using the current binding revision
 */
export const updateOrganisationGroupBinding = async (tenantSlug: string,
    bindingId: string,
    updateOrganisationGroupBindingBody: UpdateOrganisationGroupBinding, options?: Parameters<typeof customFetch>[1]): Promise<OrganisationGroupBinding> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OrganisationGroupBinding>(getUpdateOrganisationGroupBindingUrl(tenantSlug,bindingId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateOrganisationGroupBindingBody)
  }
);}





export const getUpdateOrganisationGroupBindingMutationKey = () => ['updateOrganisationGroupBinding'] as const;

export const getUpdateOrganisationGroupBindingMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateOrganisationGroupBinding>>, TError,UpdateOrganisationGroupBindingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateOrganisationGroupBinding>>, TError,UpdateOrganisationGroupBindingMutationVariables, TContext> => {

const mutationKey = getUpdateOrganisationGroupBindingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateOrganisationGroupBinding>>, UpdateOrganisationGroupBindingMutationVariables> = (props) => {
          const {tenantSlug,bindingId,data} = props ?? {};

          return  updateOrganisationGroupBinding(tenantSlug,bindingId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateOrganisationGroupBindingMutationResult = NonNullable<Awaited<ReturnType<typeof updateOrganisationGroupBinding>>>
    export type UpdateOrganisationGroupBindingMutationBody = UpdateOrganisationGroupBinding
    export type UpdateOrganisationGroupBindingMutationError = Error
    export type UpdateOrganisationGroupBindingMutationVariables = {tenantSlug: string;bindingId: string;data: UpdateOrganisationGroupBinding}

    /**
 * @summary Update group roles or enabled state using the current binding revision
 */
export const useUpdateOrganisationGroupBinding = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateOrganisationGroupBinding>>, TError,UpdateOrganisationGroupBindingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateOrganisationGroupBinding>>,
        TError,
        UpdateOrganisationGroupBindingMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateOrganisationGroupBindingMutationOptions(options), queryClient);
    }

export const getRemoveOrganisationGroupBindingUrl = (tenantSlug: string,
    bindingId: string,
    params: RemoveOrganisationGroupBindingParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/group-bindings/${bindingId}?${stringifiedParams}` : `/api/tenants/${tenantSlug}/group-bindings/${bindingId}`
}

/**
 * @summary Remove only this group access source
 */
export const removeOrganisationGroupBinding = async (tenantSlug: string,
    bindingId: string,
    params: RemoveOrganisationGroupBindingParams, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getRemoveOrganisationGroupBindingUrl(tenantSlug,bindingId,params),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getRemoveOrganisationGroupBindingMutationKey = () => ['removeOrganisationGroupBinding'] as const;

export const getRemoveOrganisationGroupBindingMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeOrganisationGroupBinding>>, TError,RemoveOrganisationGroupBindingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof removeOrganisationGroupBinding>>, TError,RemoveOrganisationGroupBindingMutationVariables, TContext> => {

const mutationKey = getRemoveOrganisationGroupBindingMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof removeOrganisationGroupBinding>>, RemoveOrganisationGroupBindingMutationVariables> = (props) => {
          const {tenantSlug,bindingId,params} = props ?? {};

          return  removeOrganisationGroupBinding(tenantSlug,bindingId,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type RemoveOrganisationGroupBindingMutationResult = NonNullable<Awaited<ReturnType<typeof removeOrganisationGroupBinding>>>

    export type RemoveOrganisationGroupBindingMutationError = Error
    export type RemoveOrganisationGroupBindingMutationVariables = {tenantSlug: string;bindingId: string;params: RemoveOrganisationGroupBindingParams}

    /**
 * @summary Remove only this group access source
 */
export const useRemoveOrganisationGroupBinding = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof removeOrganisationGroupBinding>>, TError,RemoveOrganisationGroupBindingMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof removeOrganisationGroupBinding>>,
        TError,
        RemoveOrganisationGroupBindingMutationVariables,
        TContext
      > => {
      return useMutation(getRemoveOrganisationGroupBindingMutationOptions(options), queryClient);
    }

export const getGetOrganisationMemberAccessSourcesUrl = (tenantSlug: string,
    memberId: string,
    params?: GetOrganisationMemberAccessSourcesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/members/${memberId}/access-sources?${stringifiedParams}` : `/api/tenants/${tenantSlug}/members/${memberId}/access-sources`
}

/**
 * @summary Read current access sources for this member as an administrator
 */
export const getOrganisationMemberAccessSources = async (tenantSlug: string,
    memberId: string,
    params?: GetOrganisationMemberAccessSourcesParams, options?: Parameters<typeof customFetch>[1]): Promise<OrganisationMemberAccessSources> => {

  return customFetch<OrganisationMemberAccessSources>(getGetOrganisationMemberAccessSourcesUrl(tenantSlug,memberId,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetOrganisationMemberAccessSourcesQueryKey = (tenantSlug: string,
    memberId: string,
    params?: GetOrganisationMemberAccessSourcesParams,) => {
    return [
    `/api/tenants/${tenantSlug}/members/${memberId}/access-sources`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetOrganisationMemberAccessSourcesQueryOptions = <TData = Awaited<ReturnType<typeof getOrganisationMemberAccessSources>>, TError = Error>(tenantSlug: string,
    memberId: string,
    params?: GetOrganisationMemberAccessSourcesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationMemberAccessSources>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetOrganisationMemberAccessSourcesQueryKey(tenantSlug,memberId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getOrganisationMemberAccessSources>>> = ({ signal }) => getOrganisationMemberAccessSources(tenantSlug,memberId,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined && memberId !== null && memberId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getOrganisationMemberAccessSources>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetOrganisationMemberAccessSourcesQueryResult = NonNullable<Awaited<ReturnType<typeof getOrganisationMemberAccessSources>>>
export type GetOrganisationMemberAccessSourcesQueryError = Error


export function useGetOrganisationMemberAccessSources<TData = Awaited<ReturnType<typeof getOrganisationMemberAccessSources>>, TError = Error>(
 tenantSlug: string,
    memberId: string,
    params: undefined |  GetOrganisationMemberAccessSourcesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationMemberAccessSources>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganisationMemberAccessSources>>,
          TError,
          Awaited<ReturnType<typeof getOrganisationMemberAccessSources>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetOrganisationMemberAccessSources<TData = Awaited<ReturnType<typeof getOrganisationMemberAccessSources>>, TError = Error>(
 tenantSlug: string,
    memberId: string,
    params?: GetOrganisationMemberAccessSourcesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationMemberAccessSources>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganisationMemberAccessSources>>,
          TError,
          Awaited<ReturnType<typeof getOrganisationMemberAccessSources>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetOrganisationMemberAccessSources<TData = Awaited<ReturnType<typeof getOrganisationMemberAccessSources>>, TError = Error>(
 tenantSlug: string,
    memberId: string,
    params?: GetOrganisationMemberAccessSourcesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationMemberAccessSources>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read current access sources for this member as an administrator
 */

export function useGetOrganisationMemberAccessSources<TData = Awaited<ReturnType<typeof getOrganisationMemberAccessSources>>, TError = Error>(
 tenantSlug: string,
    memberId: string,
    params?: GetOrganisationMemberAccessSourcesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOrganisationMemberAccessSources>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetOrganisationMemberAccessSourcesQueryOptions(tenantSlug,memberId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetApiTenantsTenantSlugMembersUrl = (tenantSlug: string,
    params?: GetApiTenantsTenantSlugMembersParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/members?${stringifiedParams}` : `/api/tenants/${tenantSlug}/members`
}

/**
 * @summary List tenant members
 */
export const getApiTenantsTenantSlugMembers = async (tenantSlug: string,
    params?: GetApiTenantsTenantSlugMembersParams, options?: Parameters<typeof customFetch>[1]): Promise<GetApiTenantsTenantSlugMembers200> => {

  return customFetch<GetApiTenantsTenantSlugMembers200>(getGetApiTenantsTenantSlugMembersUrl(tenantSlug,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiTenantsTenantSlugMembersQueryKey = (tenantSlug: string,
    params?: GetApiTenantsTenantSlugMembersParams,) => {
    return [
    `/api/tenants/${tenantSlug}/members`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetApiTenantsTenantSlugMembersQueryOptions = <TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugMembers>>, TError = Error>(tenantSlug: string,
    params?: GetApiTenantsTenantSlugMembersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugMembers>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiTenantsTenantSlugMembersQueryKey(tenantSlug,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiTenantsTenantSlugMembers>>> = ({ signal }) => getApiTenantsTenantSlugMembers(tenantSlug,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugMembers>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiTenantsTenantSlugMembersQueryResult = NonNullable<Awaited<ReturnType<typeof getApiTenantsTenantSlugMembers>>>
export type GetApiTenantsTenantSlugMembersQueryError = Error


export function useGetApiTenantsTenantSlugMembers<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugMembers>>, TError = Error>(
 tenantSlug: string,
    params: undefined |  GetApiTenantsTenantSlugMembersParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugMembers>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugMembers>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugMembers>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugMembers<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugMembers>>, TError = Error>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugMembersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugMembers>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugMembers>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugMembers>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugMembers<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugMembers>>, TError = Error>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugMembersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugMembers>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List tenant members
 */

export function useGetApiTenantsTenantSlugMembers<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugMembers>>, TError = Error>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugMembersParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugMembers>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiTenantsTenantSlugMembersQueryOptions(tenantSlug,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPostApiTenantsTenantSlugMembersUrl = (tenantSlug: string,) => {




  return `/api/tenants/${tenantSlug}/members`
}

/**
 * @summary Add an eligible registered member to the entity (by email)
 */
export const postApiTenantsTenantSlugMembers = async (tenantSlug: string,
    postApiTenantsTenantSlugMembersBody: PostApiTenantsTenantSlugMembersBody, options?: Parameters<typeof customFetch>[1]): Promise<TenantMember> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenantMember>(getPostApiTenantsTenantSlugMembersUrl(tenantSlug),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(postApiTenantsTenantSlugMembersBody)
  }
);}





export const getPostApiTenantsTenantSlugMembersMutationKey = () => ['postApiTenantsTenantSlugMembers'] as const;

export const getPostApiTenantsTenantSlugMembersMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugMembers>>, TError,PostApiTenantsTenantSlugMembersMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugMembers>>, TError,PostApiTenantsTenantSlugMembersMutationVariables, TContext> => {

const mutationKey = getPostApiTenantsTenantSlugMembersMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiTenantsTenantSlugMembers>>, PostApiTenantsTenantSlugMembersMutationVariables> = (props) => {
          const {tenantSlug,data} = props ?? {};

          return  postApiTenantsTenantSlugMembers(tenantSlug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostApiTenantsTenantSlugMembersMutationResult = NonNullable<Awaited<ReturnType<typeof postApiTenantsTenantSlugMembers>>>
    export type PostApiTenantsTenantSlugMembersMutationBody = PostApiTenantsTenantSlugMembersBody
    export type PostApiTenantsTenantSlugMembersMutationError = Error
    export type PostApiTenantsTenantSlugMembersMutationVariables = {tenantSlug: string;data: PostApiTenantsTenantSlugMembersBody}

    /**
 * @summary Add an eligible registered member to the entity (by email)
 */
export const usePostApiTenantsTenantSlugMembers = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugMembers>>, TError,PostApiTenantsTenantSlugMembersMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postApiTenantsTenantSlugMembers>>,
        TError,
        PostApiTenantsTenantSlugMembersMutationVariables,
        TContext
      > => {
      return useMutation(getPostApiTenantsTenantSlugMembersMutationOptions(options), queryClient);
    }

export const getPatchApiTenantsTenantSlugMembersMemberIdUrl = (tenantSlug: string,
    memberId: string,) => {




  return `/api/tenants/${tenantSlug}/members/${memberId}`
}

/**
 * @summary Update member roles
 */
export const patchApiTenantsTenantSlugMembersMemberId = async (tenantSlug: string,
    memberId: string,
    patchApiTenantsTenantSlugMembersMemberIdBody: PatchApiTenantsTenantSlugMembersMemberIdBody, options?: Parameters<typeof customFetch>[1]): Promise<TenantMember> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenantMember>(getPatchApiTenantsTenantSlugMembersMemberIdUrl(tenantSlug,memberId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(patchApiTenantsTenantSlugMembersMemberIdBody)
  }
);}





export const getPatchApiTenantsTenantSlugMembersMemberIdMutationKey = () => ['patchApiTenantsTenantSlugMembersMemberId'] as const;

export const getPatchApiTenantsTenantSlugMembersMemberIdMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugMembersMemberId>>, TError,PatchApiTenantsTenantSlugMembersMemberIdMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugMembersMemberId>>, TError,PatchApiTenantsTenantSlugMembersMemberIdMutationVariables, TContext> => {

const mutationKey = getPatchApiTenantsTenantSlugMembersMemberIdMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchApiTenantsTenantSlugMembersMemberId>>, PatchApiTenantsTenantSlugMembersMemberIdMutationVariables> = (props) => {
          const {tenantSlug,memberId,data} = props ?? {};

          return  patchApiTenantsTenantSlugMembersMemberId(tenantSlug,memberId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchApiTenantsTenantSlugMembersMemberIdMutationResult = NonNullable<Awaited<ReturnType<typeof patchApiTenantsTenantSlugMembersMemberId>>>
    export type PatchApiTenantsTenantSlugMembersMemberIdMutationBody = PatchApiTenantsTenantSlugMembersMemberIdBody
    export type PatchApiTenantsTenantSlugMembersMemberIdMutationError = Error
    export type PatchApiTenantsTenantSlugMembersMemberIdMutationVariables = {tenantSlug: string;memberId: string;data: PatchApiTenantsTenantSlugMembersMemberIdBody}

    /**
 * @summary Update member roles
 */
export const usePatchApiTenantsTenantSlugMembersMemberId = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugMembersMemberId>>, TError,PatchApiTenantsTenantSlugMembersMemberIdMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchApiTenantsTenantSlugMembersMemberId>>,
        TError,
        PatchApiTenantsTenantSlugMembersMemberIdMutationVariables,
        TContext
      > => {
      return useMutation(getPatchApiTenantsTenantSlugMembersMemberIdMutationOptions(options), queryClient);
    }

export const getDeleteApiTenantsTenantSlugMembersMemberIdUrl = (tenantSlug: string,
    memberId: string,) => {




  return `/api/tenants/${tenantSlug}/members/${memberId}`
}

/**
 * @summary Remove a member from the tenant
 */
export const deleteApiTenantsTenantSlugMembersMemberId = async (tenantSlug: string,
    memberId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getDeleteApiTenantsTenantSlugMembersMemberIdUrl(tenantSlug,memberId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteApiTenantsTenantSlugMembersMemberIdMutationKey = () => ['deleteApiTenantsTenantSlugMembersMemberId'] as const;

export const getDeleteApiTenantsTenantSlugMembersMemberIdMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteApiTenantsTenantSlugMembersMemberId>>, TError,DeleteApiTenantsTenantSlugMembersMemberIdMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteApiTenantsTenantSlugMembersMemberId>>, TError,DeleteApiTenantsTenantSlugMembersMemberIdMutationVariables, TContext> => {

const mutationKey = getDeleteApiTenantsTenantSlugMembersMemberIdMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteApiTenantsTenantSlugMembersMemberId>>, DeleteApiTenantsTenantSlugMembersMemberIdMutationVariables> = (props) => {
          const {tenantSlug,memberId} = props ?? {};

          return  deleteApiTenantsTenantSlugMembersMemberId(tenantSlug,memberId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteApiTenantsTenantSlugMembersMemberIdMutationResult = NonNullable<Awaited<ReturnType<typeof deleteApiTenantsTenantSlugMembersMemberId>>>

    export type DeleteApiTenantsTenantSlugMembersMemberIdMutationError = Error
    export type DeleteApiTenantsTenantSlugMembersMemberIdMutationVariables = {tenantSlug: string;memberId: string}

    /**
 * @summary Remove a member from the tenant
 */
export const useDeleteApiTenantsTenantSlugMembersMemberId = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteApiTenantsTenantSlugMembersMemberId>>, TError,DeleteApiTenantsTenantSlugMembersMemberIdMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteApiTenantsTenantSlugMembersMemberId>>,
        TError,
        DeleteApiTenantsTenantSlugMembersMemberIdMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteApiTenantsTenantSlugMembersMemberIdMutationOptions(options), queryClient);
    }

export const getGetApiTenantsTenantSlugInvitesUrl = (tenantSlug: string,
    params?: GetApiTenantsTenantSlugInvitesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/invites?${stringifiedParams}` : `/api/tenants/${tenantSlug}/invites`
}

/**
 * @summary List tenant invites
 */
export const getApiTenantsTenantSlugInvites = async (tenantSlug: string,
    params?: GetApiTenantsTenantSlugInvitesParams, options?: Parameters<typeof customFetch>[1]): Promise<GetApiTenantsTenantSlugInvites200> => {

  return customFetch<GetApiTenantsTenantSlugInvites200>(getGetApiTenantsTenantSlugInvitesUrl(tenantSlug,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiTenantsTenantSlugInvitesQueryKey = (tenantSlug: string,
    params?: GetApiTenantsTenantSlugInvitesParams,) => {
    return [
    `/api/tenants/${tenantSlug}/invites`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetApiTenantsTenantSlugInvitesQueryOptions = <TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugInvites>>, TError = Error>(tenantSlug: string,
    params?: GetApiTenantsTenantSlugInvitesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugInvites>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiTenantsTenantSlugInvitesQueryKey(tenantSlug,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiTenantsTenantSlugInvites>>> = ({ signal }) => getApiTenantsTenantSlugInvites(tenantSlug,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugInvites>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiTenantsTenantSlugInvitesQueryResult = NonNullable<Awaited<ReturnType<typeof getApiTenantsTenantSlugInvites>>>
export type GetApiTenantsTenantSlugInvitesQueryError = Error


export function useGetApiTenantsTenantSlugInvites<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugInvites>>, TError = Error>(
 tenantSlug: string,
    params: undefined |  GetApiTenantsTenantSlugInvitesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugInvites>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugInvites>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugInvites>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugInvites<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugInvites>>, TError = Error>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugInvitesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugInvites>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugInvites>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugInvites>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugInvites<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugInvites>>, TError = Error>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugInvitesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugInvites>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List tenant invites
 */

export function useGetApiTenantsTenantSlugInvites<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugInvites>>, TError = Error>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugInvitesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugInvites>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiTenantsTenantSlugInvitesQueryOptions(tenantSlug,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPostApiTenantsTenantSlugInvitesUrl = (tenantSlug: string,) => {




  return `/api/tenants/${tenantSlug}/invites`
}

/**
 * @summary Create a tenant invite
 */
export const postApiTenantsTenantSlugInvites = async (tenantSlug: string,
    postApiTenantsTenantSlugInvitesBody: PostApiTenantsTenantSlugInvitesBody, options?: Parameters<typeof customFetch>[1]): Promise<PostApiTenantsTenantSlugInvites201> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PostApiTenantsTenantSlugInvites201>(getPostApiTenantsTenantSlugInvitesUrl(tenantSlug),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(postApiTenantsTenantSlugInvitesBody)
  }
);}





export const getPostApiTenantsTenantSlugInvitesMutationKey = () => ['postApiTenantsTenantSlugInvites'] as const;

export const getPostApiTenantsTenantSlugInvitesMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugInvites>>, TError,PostApiTenantsTenantSlugInvitesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugInvites>>, TError,PostApiTenantsTenantSlugInvitesMutationVariables, TContext> => {

const mutationKey = getPostApiTenantsTenantSlugInvitesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiTenantsTenantSlugInvites>>, PostApiTenantsTenantSlugInvitesMutationVariables> = (props) => {
          const {tenantSlug,data} = props ?? {};

          return  postApiTenantsTenantSlugInvites(tenantSlug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostApiTenantsTenantSlugInvitesMutationResult = NonNullable<Awaited<ReturnType<typeof postApiTenantsTenantSlugInvites>>>
    export type PostApiTenantsTenantSlugInvitesMutationBody = PostApiTenantsTenantSlugInvitesBody
    export type PostApiTenantsTenantSlugInvitesMutationError = Error
    export type PostApiTenantsTenantSlugInvitesMutationVariables = {tenantSlug: string;data: PostApiTenantsTenantSlugInvitesBody}

    /**
 * @summary Create a tenant invite
 */
export const usePostApiTenantsTenantSlugInvites = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugInvites>>, TError,PostApiTenantsTenantSlugInvitesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postApiTenantsTenantSlugInvites>>,
        TError,
        PostApiTenantsTenantSlugInvitesMutationVariables,
        TContext
      > => {
      return useMutation(getPostApiTenantsTenantSlugInvitesMutationOptions(options), queryClient);
    }

export const getPostApiTenantsTenantSlugInvitesInviteIdRevokeUrl = (tenantSlug: string,
    inviteId: string,) => {




  return `/api/tenants/${tenantSlug}/invites/${inviteId}/revoke`
}

/**
 * @summary Revoke a pending invite
 */
export const postApiTenantsTenantSlugInvitesInviteIdRevoke = async (tenantSlug: string,
    inviteId: string, options?: Parameters<typeof customFetch>[1]): Promise<void> => {

  return customFetch<void>(getPostApiTenantsTenantSlugInvitesInviteIdRevokeUrl(tenantSlug,inviteId),
  {
    ...options,
    method: 'POST'


  }
);}





export const getPostApiTenantsTenantSlugInvitesInviteIdRevokeMutationKey = () => ['postApiTenantsTenantSlugInvitesInviteIdRevoke'] as const;

export const getPostApiTenantsTenantSlugInvitesInviteIdRevokeMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugInvitesInviteIdRevoke>>, TError,PostApiTenantsTenantSlugInvitesInviteIdRevokeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugInvitesInviteIdRevoke>>, TError,PostApiTenantsTenantSlugInvitesInviteIdRevokeMutationVariables, TContext> => {

const mutationKey = getPostApiTenantsTenantSlugInvitesInviteIdRevokeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiTenantsTenantSlugInvitesInviteIdRevoke>>, PostApiTenantsTenantSlugInvitesInviteIdRevokeMutationVariables> = (props) => {
          const {tenantSlug,inviteId} = props ?? {};

          return  postApiTenantsTenantSlugInvitesInviteIdRevoke(tenantSlug,inviteId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostApiTenantsTenantSlugInvitesInviteIdRevokeMutationResult = NonNullable<Awaited<ReturnType<typeof postApiTenantsTenantSlugInvitesInviteIdRevoke>>>

    export type PostApiTenantsTenantSlugInvitesInviteIdRevokeMutationError = Error
    export type PostApiTenantsTenantSlugInvitesInviteIdRevokeMutationVariables = {tenantSlug: string;inviteId: string}

    /**
 * @summary Revoke a pending invite
 */
export const usePostApiTenantsTenantSlugInvitesInviteIdRevoke = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugInvitesInviteIdRevoke>>, TError,PostApiTenantsTenantSlugInvitesInviteIdRevokeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postApiTenantsTenantSlugInvitesInviteIdRevoke>>,
        TError,
        PostApiTenantsTenantSlugInvitesInviteIdRevokeMutationVariables,
        TContext
      > => {
      return useMutation(getPostApiTenantsTenantSlugInvitesInviteIdRevokeMutationOptions(options), queryClient);
    }

export const getGetApiTenantsTenantSlugRolesUrl = (tenantSlug: string,) => {




  return `/api/tenants/${tenantSlug}/roles`
}

/**
 * @summary List tenant roles (system + custom)
 */
export const getApiTenantsTenantSlugRoles = async (tenantSlug: string, options?: Parameters<typeof customFetch>[1]): Promise<GetApiTenantsTenantSlugRoles200> => {

  return customFetch<GetApiTenantsTenantSlugRoles200>(getGetApiTenantsTenantSlugRolesUrl(tenantSlug),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiTenantsTenantSlugRolesQueryKey = (tenantSlug: string,) => {
    return [
    `/api/tenants/${tenantSlug}/roles`
    ] as const;
    }


export const getGetApiTenantsTenantSlugRolesQueryOptions = <TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugRoles>>, TError = Error>(tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugRoles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiTenantsTenantSlugRolesQueryKey(tenantSlug);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiTenantsTenantSlugRoles>>> = ({ signal }) => getApiTenantsTenantSlugRoles(tenantSlug, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugRoles>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiTenantsTenantSlugRolesQueryResult = NonNullable<Awaited<ReturnType<typeof getApiTenantsTenantSlugRoles>>>
export type GetApiTenantsTenantSlugRolesQueryError = Error


export function useGetApiTenantsTenantSlugRoles<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugRoles>>, TError = Error>(
 tenantSlug: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugRoles>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugRoles>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugRoles>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugRoles<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugRoles>>, TError = Error>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugRoles>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugRoles>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugRoles>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugRoles<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugRoles>>, TError = Error>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugRoles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List tenant roles (system + custom)
 */

export function useGetApiTenantsTenantSlugRoles<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugRoles>>, TError = Error>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugRoles>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiTenantsTenantSlugRolesQueryOptions(tenantSlug,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPostApiTenantsTenantSlugRolesUrl = (tenantSlug: string,) => {




  return `/api/tenants/${tenantSlug}/roles`
}

/**
 * @summary Create a custom tenant role (tenant.roles.manage)
 */
export const postApiTenantsTenantSlugRoles = async (tenantSlug: string,
    postApiTenantsTenantSlugRolesBody: PostApiTenantsTenantSlugRolesBody, options?: Parameters<typeof customFetch>[1]): Promise<TenantRoleDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenantRoleDetail>(getPostApiTenantsTenantSlugRolesUrl(tenantSlug),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(postApiTenantsTenantSlugRolesBody)
  }
);}





export const getPostApiTenantsTenantSlugRolesMutationKey = () => ['postApiTenantsTenantSlugRoles'] as const;

export const getPostApiTenantsTenantSlugRolesMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugRoles>>, TError,PostApiTenantsTenantSlugRolesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugRoles>>, TError,PostApiTenantsTenantSlugRolesMutationVariables, TContext> => {

const mutationKey = getPostApiTenantsTenantSlugRolesMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiTenantsTenantSlugRoles>>, PostApiTenantsTenantSlugRolesMutationVariables> = (props) => {
          const {tenantSlug,data} = props ?? {};

          return  postApiTenantsTenantSlugRoles(tenantSlug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostApiTenantsTenantSlugRolesMutationResult = NonNullable<Awaited<ReturnType<typeof postApiTenantsTenantSlugRoles>>>
    export type PostApiTenantsTenantSlugRolesMutationBody = PostApiTenantsTenantSlugRolesBody
    export type PostApiTenantsTenantSlugRolesMutationError = Error
    export type PostApiTenantsTenantSlugRolesMutationVariables = {tenantSlug: string;data: PostApiTenantsTenantSlugRolesBody}

    /**
 * @summary Create a custom tenant role (tenant.roles.manage)
 */
export const usePostApiTenantsTenantSlugRoles = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugRoles>>, TError,PostApiTenantsTenantSlugRolesMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postApiTenantsTenantSlugRoles>>,
        TError,
        PostApiTenantsTenantSlugRolesMutationVariables,
        TContext
      > => {
      return useMutation(getPostApiTenantsTenantSlugRolesMutationOptions(options), queryClient);
    }

export const getGetApiPlatformAdminFeaturesUrl = () => {




  return `/api/platform-admin/features`
}

/**
 * @summary Read global platform module settings
 */
export const getApiPlatformAdminFeatures = async ( options?: Parameters<typeof customFetch>[1]): Promise<FeatureSettingsList> => {

  return customFetch<FeatureSettingsList>(getGetApiPlatformAdminFeaturesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiPlatformAdminFeaturesQueryKey = () => {
    return [
    `/api/platform-admin/features`
    ] as const;
    }


export const getGetApiPlatformAdminFeaturesQueryOptions = <TData = Awaited<ReturnType<typeof getApiPlatformAdminFeatures>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminFeatures>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiPlatformAdminFeaturesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiPlatformAdminFeatures>>> = ({ signal }) => getApiPlatformAdminFeatures({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminFeatures>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiPlatformAdminFeaturesQueryResult = NonNullable<Awaited<ReturnType<typeof getApiPlatformAdminFeatures>>>
export type GetApiPlatformAdminFeaturesQueryError = Error


export function useGetApiPlatformAdminFeatures<TData = Awaited<ReturnType<typeof getApiPlatformAdminFeatures>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminFeatures>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminFeatures>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminFeatures>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminFeatures<TData = Awaited<ReturnType<typeof getApiPlatformAdminFeatures>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminFeatures>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminFeatures>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminFeatures>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminFeatures<TData = Awaited<ReturnType<typeof getApiPlatformAdminFeatures>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminFeatures>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read global platform module settings
 */

export function useGetApiPlatformAdminFeatures<TData = Awaited<ReturnType<typeof getApiPlatformAdminFeatures>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminFeatures>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiPlatformAdminFeaturesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPatchApiPlatformAdminFeaturesModuleKeyUrl = (moduleKey: 'booking' | 'workplace_planning' | 'visitors' | 'staffed_services' | 'maps_rooms' | 'reporting' | 'ai_tools',) => {




  return `/api/platform-admin/features/${moduleKey}`
}

/**
 * @summary Set a global platform module switch for all tenants
 */
export const patchApiPlatformAdminFeaturesModuleKey = async (moduleKey: 'booking' | 'workplace_planning' | 'visitors' | 'staffed_services' | 'maps_rooms' | 'reporting' | 'ai_tools',
    featureEnabledUpdate: FeatureEnabledUpdate, options?: Parameters<typeof customFetch>[1]): Promise<FeatureSetting> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<FeatureSetting>(getPatchApiPlatformAdminFeaturesModuleKeyUrl(moduleKey),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(featureEnabledUpdate)
  }
);}





export const getPatchApiPlatformAdminFeaturesModuleKeyMutationKey = () => ['patchApiPlatformAdminFeaturesModuleKey'] as const;

export const getPatchApiPlatformAdminFeaturesModuleKeyMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminFeaturesModuleKey>>, TError,PatchApiPlatformAdminFeaturesModuleKeyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminFeaturesModuleKey>>, TError,PatchApiPlatformAdminFeaturesModuleKeyMutationVariables, TContext> => {

const mutationKey = getPatchApiPlatformAdminFeaturesModuleKeyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchApiPlatformAdminFeaturesModuleKey>>, PatchApiPlatformAdminFeaturesModuleKeyMutationVariables> = (props) => {
          const {moduleKey,data} = props ?? {};

          return  patchApiPlatformAdminFeaturesModuleKey(moduleKey,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchApiPlatformAdminFeaturesModuleKeyMutationResult = NonNullable<Awaited<ReturnType<typeof patchApiPlatformAdminFeaturesModuleKey>>>
    export type PatchApiPlatformAdminFeaturesModuleKeyMutationBody = FeatureEnabledUpdate
    export type PatchApiPlatformAdminFeaturesModuleKeyMutationError = Error
    export type PatchApiPlatformAdminFeaturesModuleKeyMutationVariables = {moduleKey: 'booking' | 'workplace_planning' | 'visitors' | 'staffed_services' | 'maps_rooms' | 'reporting' | 'ai_tools';data: FeatureEnabledUpdate}

    /**
 * @summary Set a global platform module switch for all tenants
 */
export const usePatchApiPlatformAdminFeaturesModuleKey = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminFeaturesModuleKey>>, TError,PatchApiPlatformAdminFeaturesModuleKeyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchApiPlatformAdminFeaturesModuleKey>>,
        TError,
        PatchApiPlatformAdminFeaturesModuleKeyMutationVariables,
        TContext
      > => {
      return useMutation(getPatchApiPlatformAdminFeaturesModuleKeyMutationOptions(options), queryClient);
    }

export const getGetApiPlatformAdminTenantsTenantIdParentFeaturesUrl = (tenantId: string,) => {




  return `/api/platform-admin/tenants/${tenantId}/parent-features`
}

/**
 * @summary Read parent feature settings as a platform administrator
 */
export const getApiPlatformAdminTenantsTenantIdParentFeatures = async (tenantId: string, options?: Parameters<typeof customFetch>[1]): Promise<FeatureSettingsList> => {

  return customFetch<FeatureSettingsList>(getGetApiPlatformAdminTenantsTenantIdParentFeaturesUrl(tenantId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiPlatformAdminTenantsTenantIdParentFeaturesQueryKey = (tenantId: string,) => {
    return [
    `/api/platform-admin/tenants/${tenantId}/parent-features`
    ] as const;
    }


export const getGetApiPlatformAdminTenantsTenantIdParentFeaturesQueryOptions = <TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdParentFeatures>>, TError = Error>(tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdParentFeatures>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiPlatformAdminTenantsTenantIdParentFeaturesQueryKey(tenantId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdParentFeatures>>> = ({ signal }) => getApiPlatformAdminTenantsTenantIdParentFeatures(tenantId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantId !== null && tenantId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdParentFeatures>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiPlatformAdminTenantsTenantIdParentFeaturesQueryResult = NonNullable<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdParentFeatures>>>
export type GetApiPlatformAdminTenantsTenantIdParentFeaturesQueryError = Error


export function useGetApiPlatformAdminTenantsTenantIdParentFeatures<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdParentFeatures>>, TError = Error>(
 tenantId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdParentFeatures>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdParentFeatures>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdParentFeatures>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminTenantsTenantIdParentFeatures<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdParentFeatures>>, TError = Error>(
 tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdParentFeatures>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdParentFeatures>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdParentFeatures>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminTenantsTenantIdParentFeatures<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdParentFeatures>>, TError = Error>(
 tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdParentFeatures>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read parent feature settings as a platform administrator
 */

export function useGetApiPlatformAdminTenantsTenantIdParentFeatures<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdParentFeatures>>, TError = Error>(
 tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdParentFeatures>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiPlatformAdminTenantsTenantIdParentFeaturesQueryOptions(tenantId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPatchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKeyUrl = (tenantId: string,
    moduleKey: 'booking' | 'workplace_planning' | 'visitors' | 'staffed_services' | 'maps_rooms' | 'reporting' | 'ai_tools',) => {




  return `/api/platform-admin/tenants/${tenantId}/parent-features/${moduleKey}`
}

/**
 * @summary Set a parent feature switch as a platform administrator
 */
export const patchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKey = async (tenantId: string,
    moduleKey: 'booking' | 'workplace_planning' | 'visitors' | 'staffed_services' | 'maps_rooms' | 'reporting' | 'ai_tools',
    featureEnabledUpdate: FeatureEnabledUpdate, options?: Parameters<typeof customFetch>[1]): Promise<FeatureSetting> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<FeatureSetting>(getPatchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKeyUrl(tenantId,moduleKey),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(featureEnabledUpdate)
  }
);}





export const getPatchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKeyMutationKey = () => ['patchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKey'] as const;

export const getPatchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKeyMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKey>>, TError,PatchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKeyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKey>>, TError,PatchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKeyMutationVariables, TContext> => {

const mutationKey = getPatchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKeyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKey>>, PatchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKeyMutationVariables> = (props) => {
          const {tenantId,moduleKey,data} = props ?? {};

          return  patchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKey(tenantId,moduleKey,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKeyMutationResult = NonNullable<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKey>>>
    export type PatchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKeyMutationBody = FeatureEnabledUpdate
    export type PatchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKeyMutationError = Error
    export type PatchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKeyMutationVariables = {tenantId: string;moduleKey: 'booking' | 'workplace_planning' | 'visitors' | 'staffed_services' | 'maps_rooms' | 'reporting' | 'ai_tools';data: FeatureEnabledUpdate}

    /**
 * @summary Set a parent feature switch as a platform administrator
 */
export const usePatchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKey = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKey>>, TError,PatchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKeyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKey>>,
        TError,
        PatchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKeyMutationVariables,
        TContext
      > => {
      return useMutation(getPatchApiPlatformAdminTenantsTenantIdParentFeaturesModuleKeyMutationOptions(options), queryClient);
    }

export const getGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesUrl = (tenantId: string,
    entityId: string,) => {




  return `/api/platform-admin/tenants/${tenantId}/entities/${entityId}/features`
}

/**
 * @summary Read effective entity features as a platform administrator
 */
export const getApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures = async (tenantId: string,
    entityId: string, options?: Parameters<typeof customFetch>[1]): Promise<EffectiveFeaturesList> => {

  return customFetch<EffectiveFeaturesList>(getGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesUrl(tenantId,entityId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesQueryKey = (tenantId: string,
    entityId: string,) => {
    return [
    `/api/platform-admin/tenants/${tenantId}/entities/${entityId}/features`
    ] as const;
    }


export const getGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesQueryOptions = <TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures>>, TError = Error>(tenantId: string,
    entityId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesQueryKey(tenantId,entityId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures>>> = ({ signal }) => getApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures(tenantId,entityId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantId !== null && tenantId !== undefined && entityId !== null && entityId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesQueryResult = NonNullable<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures>>>
export type GetApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesQueryError = Error


export function useGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures>>, TError = Error>(
 tenantId: string,
    entityId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures>>, TError = Error>(
 tenantId: string,
    entityId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures>>, TError = Error>(
 tenantId: string,
    entityId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read effective entity features as a platform administrator
 */

export function useGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures<TData = Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures>>, TError = Error>(
 tenantId: string,
    entityId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeatures>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesQueryOptions(tenantId,entityId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKeyUrl = (tenantId: string,
    entityId: string,
    moduleKey: 'booking' | 'workplace_planning' | 'visitors' | 'staffed_services' | 'maps_rooms' | 'reporting' | 'ai_tools',) => {




  return `/api/platform-admin/tenants/${tenantId}/entities/${entityId}/features/${moduleKey}`
}

/**
 * @summary Set or reset an entity feature opt-out as a platform administrator
 */
export const patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKey = async (tenantId: string,
    entityId: string,
    moduleKey: 'booking' | 'workplace_planning' | 'visitors' | 'staffed_services' | 'maps_rooms' | 'reporting' | 'ai_tools',
    entityFeatureUpdate: EntityFeatureUpdate, options?: Parameters<typeof customFetch>[1]): Promise<EffectiveFeature> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EffectiveFeature>(getPatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKeyUrl(tenantId,entityId,moduleKey),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(entityFeatureUpdate)
  }
);}





export const getPatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKeyMutationKey = () => ['patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKey'] as const;

export const getPatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKeyMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKey>>, TError,PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKeyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKey>>, TError,PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKeyMutationVariables, TContext> => {

const mutationKey = getPatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKeyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKey>>, PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKeyMutationVariables> = (props) => {
          const {tenantId,entityId,moduleKey,data} = props ?? {};

          return  patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKey(tenantId,entityId,moduleKey,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKeyMutationResult = NonNullable<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKey>>>
    export type PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKeyMutationBody = EntityFeatureUpdate
    export type PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKeyMutationError = Error
    export type PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKeyMutationVariables = {tenantId: string;entityId: string;moduleKey: 'booking' | 'workplace_planning' | 'visitors' | 'staffed_services' | 'maps_rooms' | 'reporting' | 'ai_tools';data: EntityFeatureUpdate}

    /**
 * @summary Set or reset an entity feature opt-out as a platform administrator
 */
export const usePatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKey = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKey>>, TError,PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKeyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKey>>,
        TError,
        PatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKeyMutationVariables,
        TContext
      > => {
      return useMutation(getPatchApiPlatformAdminTenantsTenantIdEntitiesEntityIdFeaturesModuleKeyMutationOptions(options), queryClient);
    }

export const getGetApiTenantAdminTenantsTenantIdFeaturesUrl = (tenantId: string,) => {




  return `/api/tenant-admin/tenants/${tenantId}/features`
}

/**
 * @summary Read parent tenant feature settings
 */
export const getApiTenantAdminTenantsTenantIdFeatures = async (tenantId: string, options?: Parameters<typeof customFetch>[1]): Promise<FeatureSettingsList> => {

  return customFetch<FeatureSettingsList>(getGetApiTenantAdminTenantsTenantIdFeaturesUrl(tenantId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiTenantAdminTenantsTenantIdFeaturesQueryKey = (tenantId: string,) => {
    return [
    `/api/tenant-admin/tenants/${tenantId}/features`
    ] as const;
    }


export const getGetApiTenantAdminTenantsTenantIdFeaturesQueryOptions = <TData = Awaited<ReturnType<typeof getApiTenantAdminTenantsTenantIdFeatures>>, TError = Error>(tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantAdminTenantsTenantIdFeatures>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiTenantAdminTenantsTenantIdFeaturesQueryKey(tenantId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiTenantAdminTenantsTenantIdFeatures>>> = ({ signal }) => getApiTenantAdminTenantsTenantIdFeatures(tenantId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantId !== null && tenantId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiTenantAdminTenantsTenantIdFeatures>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiTenantAdminTenantsTenantIdFeaturesQueryResult = NonNullable<Awaited<ReturnType<typeof getApiTenantAdminTenantsTenantIdFeatures>>>
export type GetApiTenantAdminTenantsTenantIdFeaturesQueryError = Error


export function useGetApiTenantAdminTenantsTenantIdFeatures<TData = Awaited<ReturnType<typeof getApiTenantAdminTenantsTenantIdFeatures>>, TError = Error>(
 tenantId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantAdminTenantsTenantIdFeatures>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantAdminTenantsTenantIdFeatures>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantAdminTenantsTenantIdFeatures>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantAdminTenantsTenantIdFeatures<TData = Awaited<ReturnType<typeof getApiTenantAdminTenantsTenantIdFeatures>>, TError = Error>(
 tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantAdminTenantsTenantIdFeatures>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantAdminTenantsTenantIdFeatures>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantAdminTenantsTenantIdFeatures>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantAdminTenantsTenantIdFeatures<TData = Awaited<ReturnType<typeof getApiTenantAdminTenantsTenantIdFeatures>>, TError = Error>(
 tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantAdminTenantsTenantIdFeatures>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read parent tenant feature settings
 */

export function useGetApiTenantAdminTenantsTenantIdFeatures<TData = Awaited<ReturnType<typeof getApiTenantAdminTenantsTenantIdFeatures>>, TError = Error>(
 tenantId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantAdminTenantsTenantIdFeatures>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiTenantAdminTenantsTenantIdFeaturesQueryOptions(tenantId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPatchApiTenantAdminTenantsTenantIdFeaturesModuleKeyUrl = (tenantId: string,
    moduleKey: 'booking' | 'workplace_planning' | 'visitors' | 'staffed_services' | 'maps_rooms' | 'reporting' | 'ai_tools',) => {




  return `/api/tenant-admin/tenants/${tenantId}/features/${moduleKey}`
}

/**
 * @summary Set a parent tenant feature switch
 */
export const patchApiTenantAdminTenantsTenantIdFeaturesModuleKey = async (tenantId: string,
    moduleKey: 'booking' | 'workplace_planning' | 'visitors' | 'staffed_services' | 'maps_rooms' | 'reporting' | 'ai_tools',
    featureEnabledUpdate: FeatureEnabledUpdate, options?: Parameters<typeof customFetch>[1]): Promise<FeatureSetting> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<FeatureSetting>(getPatchApiTenantAdminTenantsTenantIdFeaturesModuleKeyUrl(tenantId,moduleKey),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(featureEnabledUpdate)
  }
);}





export const getPatchApiTenantAdminTenantsTenantIdFeaturesModuleKeyMutationKey = () => ['patchApiTenantAdminTenantsTenantIdFeaturesModuleKey'] as const;

export const getPatchApiTenantAdminTenantsTenantIdFeaturesModuleKeyMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantAdminTenantsTenantIdFeaturesModuleKey>>, TError,PatchApiTenantAdminTenantsTenantIdFeaturesModuleKeyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantAdminTenantsTenantIdFeaturesModuleKey>>, TError,PatchApiTenantAdminTenantsTenantIdFeaturesModuleKeyMutationVariables, TContext> => {

const mutationKey = getPatchApiTenantAdminTenantsTenantIdFeaturesModuleKeyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchApiTenantAdminTenantsTenantIdFeaturesModuleKey>>, PatchApiTenantAdminTenantsTenantIdFeaturesModuleKeyMutationVariables> = (props) => {
          const {tenantId,moduleKey,data} = props ?? {};

          return  patchApiTenantAdminTenantsTenantIdFeaturesModuleKey(tenantId,moduleKey,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchApiTenantAdminTenantsTenantIdFeaturesModuleKeyMutationResult = NonNullable<Awaited<ReturnType<typeof patchApiTenantAdminTenantsTenantIdFeaturesModuleKey>>>
    export type PatchApiTenantAdminTenantsTenantIdFeaturesModuleKeyMutationBody = FeatureEnabledUpdate
    export type PatchApiTenantAdminTenantsTenantIdFeaturesModuleKeyMutationError = Error
    export type PatchApiTenantAdminTenantsTenantIdFeaturesModuleKeyMutationVariables = {tenantId: string;moduleKey: 'booking' | 'workplace_planning' | 'visitors' | 'staffed_services' | 'maps_rooms' | 'reporting' | 'ai_tools';data: FeatureEnabledUpdate}

    /**
 * @summary Set a parent tenant feature switch
 */
export const usePatchApiTenantAdminTenantsTenantIdFeaturesModuleKey = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantAdminTenantsTenantIdFeaturesModuleKey>>, TError,PatchApiTenantAdminTenantsTenantIdFeaturesModuleKeyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchApiTenantAdminTenantsTenantIdFeaturesModuleKey>>,
        TError,
        PatchApiTenantAdminTenantsTenantIdFeaturesModuleKeyMutationVariables,
        TContext
      > => {
      return useMutation(getPatchApiTenantAdminTenantsTenantIdFeaturesModuleKeyMutationOptions(options), queryClient);
    }

export const getGetApiTenantsTenantSlugFeaturesUrl = (tenantSlug: string,) => {




  return `/api/tenants/${tenantSlug}/features`
}

/**
 * @summary Read effective features for the selected entity
 */
export const getApiTenantsTenantSlugFeatures = async (tenantSlug: string, options?: Parameters<typeof customFetch>[1]): Promise<EffectiveFeaturesList> => {

  return customFetch<EffectiveFeaturesList>(getGetApiTenantsTenantSlugFeaturesUrl(tenantSlug),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiTenantsTenantSlugFeaturesQueryKey = (tenantSlug: string,) => {
    return [
    `/api/tenants/${tenantSlug}/features`
    ] as const;
    }


export const getGetApiTenantsTenantSlugFeaturesQueryOptions = <TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugFeatures>>, TError = Error>(tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugFeatures>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiTenantsTenantSlugFeaturesQueryKey(tenantSlug);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiTenantsTenantSlugFeatures>>> = ({ signal }) => getApiTenantsTenantSlugFeatures(tenantSlug, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugFeatures>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiTenantsTenantSlugFeaturesQueryResult = NonNullable<Awaited<ReturnType<typeof getApiTenantsTenantSlugFeatures>>>
export type GetApiTenantsTenantSlugFeaturesQueryError = Error


export function useGetApiTenantsTenantSlugFeatures<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugFeatures>>, TError = Error>(
 tenantSlug: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugFeatures>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugFeatures>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugFeatures>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugFeatures<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugFeatures>>, TError = Error>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugFeatures>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugFeatures>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugFeatures>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugFeatures<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugFeatures>>, TError = Error>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugFeatures>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read effective features for the selected entity
 */

export function useGetApiTenantsTenantSlugFeatures<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugFeatures>>, TError = Error>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugFeatures>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiTenantsTenantSlugFeaturesQueryOptions(tenantSlug,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPatchApiTenantsTenantSlugFeaturesModuleKeyUrl = (tenantSlug: string,
    moduleKey: 'booking' | 'workplace_planning' | 'visitors' | 'staffed_services' | 'maps_rooms' | 'reporting' | 'ai_tools',) => {




  return `/api/tenants/${tenantSlug}/features/${moduleKey}`
}

/**
 * @summary Set or reset an entity off override
 */
export const patchApiTenantsTenantSlugFeaturesModuleKey = async (tenantSlug: string,
    moduleKey: 'booking' | 'workplace_planning' | 'visitors' | 'staffed_services' | 'maps_rooms' | 'reporting' | 'ai_tools',
    entityFeatureUpdate: EntityFeatureUpdate, options?: Parameters<typeof customFetch>[1]): Promise<EffectiveFeature> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EffectiveFeature>(getPatchApiTenantsTenantSlugFeaturesModuleKeyUrl(tenantSlug,moduleKey),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(entityFeatureUpdate)
  }
);}





export const getPatchApiTenantsTenantSlugFeaturesModuleKeyMutationKey = () => ['patchApiTenantsTenantSlugFeaturesModuleKey'] as const;

export const getPatchApiTenantsTenantSlugFeaturesModuleKeyMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugFeaturesModuleKey>>, TError,PatchApiTenantsTenantSlugFeaturesModuleKeyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugFeaturesModuleKey>>, TError,PatchApiTenantsTenantSlugFeaturesModuleKeyMutationVariables, TContext> => {

const mutationKey = getPatchApiTenantsTenantSlugFeaturesModuleKeyMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchApiTenantsTenantSlugFeaturesModuleKey>>, PatchApiTenantsTenantSlugFeaturesModuleKeyMutationVariables> = (props) => {
          const {tenantSlug,moduleKey,data} = props ?? {};

          return  patchApiTenantsTenantSlugFeaturesModuleKey(tenantSlug,moduleKey,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchApiTenantsTenantSlugFeaturesModuleKeyMutationResult = NonNullable<Awaited<ReturnType<typeof patchApiTenantsTenantSlugFeaturesModuleKey>>>
    export type PatchApiTenantsTenantSlugFeaturesModuleKeyMutationBody = EntityFeatureUpdate
    export type PatchApiTenantsTenantSlugFeaturesModuleKeyMutationError = Error
    export type PatchApiTenantsTenantSlugFeaturesModuleKeyMutationVariables = {tenantSlug: string;moduleKey: 'booking' | 'workplace_planning' | 'visitors' | 'staffed_services' | 'maps_rooms' | 'reporting' | 'ai_tools';data: EntityFeatureUpdate}

    /**
 * @summary Set or reset an entity off override
 */
export const usePatchApiTenantsTenantSlugFeaturesModuleKey = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugFeaturesModuleKey>>, TError,PatchApiTenantsTenantSlugFeaturesModuleKeyMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchApiTenantsTenantSlugFeaturesModuleKey>>,
        TError,
        PatchApiTenantsTenantSlugFeaturesModuleKeyMutationVariables,
        TContext
      > => {
      return useMutation(getPatchApiTenantsTenantSlugFeaturesModuleKeyMutationOptions(options), queryClient);
    }

export const getGetApiTenantsTenantSlugSettingsUrl = (tenantSlug: string,) => {




  return `/api/tenants/${tenantSlug}/settings`
}

/**
 * @summary Read tenant settings
 */
export const getApiTenantsTenantSlugSettings = async (tenantSlug: string, options?: Parameters<typeof customFetch>[1]): Promise<GetApiTenantsTenantSlugSettings200> => {

  return customFetch<GetApiTenantsTenantSlugSettings200>(getGetApiTenantsTenantSlugSettingsUrl(tenantSlug),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiTenantsTenantSlugSettingsQueryKey = (tenantSlug: string,) => {
    return [
    `/api/tenants/${tenantSlug}/settings`
    ] as const;
    }


export const getGetApiTenantsTenantSlugSettingsQueryOptions = <TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugSettings>>, TError = Error>(tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugSettings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiTenantsTenantSlugSettingsQueryKey(tenantSlug);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiTenantsTenantSlugSettings>>> = ({ signal }) => getApiTenantsTenantSlugSettings(tenantSlug, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugSettings>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiTenantsTenantSlugSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiTenantsTenantSlugSettings>>>
export type GetApiTenantsTenantSlugSettingsQueryError = Error


export function useGetApiTenantsTenantSlugSettings<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugSettings>>, TError = Error>(
 tenantSlug: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugSettings>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugSettings>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugSettings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugSettings<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugSettings>>, TError = Error>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugSettings>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugSettings>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugSettings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugSettings<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugSettings>>, TError = Error>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugSettings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read tenant settings
 */

export function useGetApiTenantsTenantSlugSettings<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugSettings>>, TError = Error>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugSettings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiTenantsTenantSlugSettingsQueryOptions(tenantSlug,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPatchApiTenantsTenantSlugSettingsUrl = (tenantSlug: string,) => {




  return `/api/tenants/${tenantSlug}/settings`
}

/**
 * @summary Update tenant settings (shallow merge)
 */
export const patchApiTenantsTenantSlugSettings = async (tenantSlug: string,
    patchApiTenantsTenantSlugSettingsBody: PatchApiTenantsTenantSlugSettingsBody, options?: Parameters<typeof customFetch>[1]): Promise<PatchApiTenantsTenantSlugSettings200> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<PatchApiTenantsTenantSlugSettings200>(getPatchApiTenantsTenantSlugSettingsUrl(tenantSlug),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(patchApiTenantsTenantSlugSettingsBody)
  }
);}





export const getPatchApiTenantsTenantSlugSettingsMutationKey = () => ['patchApiTenantsTenantSlugSettings'] as const;

export const getPatchApiTenantsTenantSlugSettingsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugSettings>>, TError,PatchApiTenantsTenantSlugSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugSettings>>, TError,PatchApiTenantsTenantSlugSettingsMutationVariables, TContext> => {

const mutationKey = getPatchApiTenantsTenantSlugSettingsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchApiTenantsTenantSlugSettings>>, PatchApiTenantsTenantSlugSettingsMutationVariables> = (props) => {
          const {tenantSlug,data} = props ?? {};

          return  patchApiTenantsTenantSlugSettings(tenantSlug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchApiTenantsTenantSlugSettingsMutationResult = NonNullable<Awaited<ReturnType<typeof patchApiTenantsTenantSlugSettings>>>
    export type PatchApiTenantsTenantSlugSettingsMutationBody = PatchApiTenantsTenantSlugSettingsBody
    export type PatchApiTenantsTenantSlugSettingsMutationError = Error
    export type PatchApiTenantsTenantSlugSettingsMutationVariables = {tenantSlug: string;data: PatchApiTenantsTenantSlugSettingsBody}

    /**
 * @summary Update tenant settings (shallow merge)
 */
export const usePatchApiTenantsTenantSlugSettings = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugSettings>>, TError,PatchApiTenantsTenantSlugSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchApiTenantsTenantSlugSettings>>,
        TError,
        PatchApiTenantsTenantSlugSettingsMutationVariables,
        TContext
      > => {
      return useMutation(getPatchApiTenantsTenantSlugSettingsMutationOptions(options), queryClient);
    }

export const getGetApiTenantsTenantSlugWorkplaceOptionsUrl = (tenantSlug: string,) => {




  return `/api/tenants/${tenantSlug}/workplace-options`
}

/**
 * @summary Read effective workplace options for the selected entity
 */
export const getApiTenantsTenantSlugWorkplaceOptions = async (tenantSlug: string, options?: Parameters<typeof customFetch>[1]): Promise<WorkplaceOptionsSettings> => {

  return customFetch<WorkplaceOptionsSettings>(getGetApiTenantsTenantSlugWorkplaceOptionsUrl(tenantSlug),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiTenantsTenantSlugWorkplaceOptionsQueryKey = (tenantSlug: string,) => {
    return [
    `/api/tenants/${tenantSlug}/workplace-options`
    ] as const;
    }


export const getGetApiTenantsTenantSlugWorkplaceOptionsQueryOptions = <TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugWorkplaceOptions>>, TError = unknown>(tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugWorkplaceOptions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiTenantsTenantSlugWorkplaceOptionsQueryKey(tenantSlug);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiTenantsTenantSlugWorkplaceOptions>>> = ({ signal }) => getApiTenantsTenantSlugWorkplaceOptions(tenantSlug, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugWorkplaceOptions>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiTenantsTenantSlugWorkplaceOptionsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiTenantsTenantSlugWorkplaceOptions>>>
export type GetApiTenantsTenantSlugWorkplaceOptionsQueryError = unknown


export function useGetApiTenantsTenantSlugWorkplaceOptions<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugWorkplaceOptions>>, TError = unknown>(
 tenantSlug: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugWorkplaceOptions>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugWorkplaceOptions>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugWorkplaceOptions>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugWorkplaceOptions<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugWorkplaceOptions>>, TError = unknown>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugWorkplaceOptions>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugWorkplaceOptions>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugWorkplaceOptions>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugWorkplaceOptions<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugWorkplaceOptions>>, TError = unknown>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugWorkplaceOptions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read effective workplace options for the selected entity
 */

export function useGetApiTenantsTenantSlugWorkplaceOptions<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugWorkplaceOptions>>, TError = unknown>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugWorkplaceOptions>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiTenantsTenantSlugWorkplaceOptionsQueryOptions(tenantSlug,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPatchApiTenantsTenantSlugWorkplaceOptionsUrl = (tenantSlug: string,) => {




  return `/api/tenants/${tenantSlug}/workplace-options`
}

/**
 * @summary Update tenant values or permitted entity overrides for workplace options
 */
export const patchApiTenantsTenantSlugWorkplaceOptions = async (tenantSlug: string,
    workplaceOptionsPatch: WorkplaceOptionsPatch, options?: Parameters<typeof customFetch>[1]): Promise<WorkplaceOptionsSettings> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<WorkplaceOptionsSettings>(getPatchApiTenantsTenantSlugWorkplaceOptionsUrl(tenantSlug),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(workplaceOptionsPatch)
  }
);}





export const getPatchApiTenantsTenantSlugWorkplaceOptionsMutationKey = () => ['patchApiTenantsTenantSlugWorkplaceOptions'] as const;

export const getPatchApiTenantsTenantSlugWorkplaceOptionsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugWorkplaceOptions>>, TError,PatchApiTenantsTenantSlugWorkplaceOptionsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugWorkplaceOptions>>, TError,PatchApiTenantsTenantSlugWorkplaceOptionsMutationVariables, TContext> => {

const mutationKey = getPatchApiTenantsTenantSlugWorkplaceOptionsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchApiTenantsTenantSlugWorkplaceOptions>>, PatchApiTenantsTenantSlugWorkplaceOptionsMutationVariables> = (props) => {
          const {tenantSlug,data} = props ?? {};

          return  patchApiTenantsTenantSlugWorkplaceOptions(tenantSlug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchApiTenantsTenantSlugWorkplaceOptionsMutationResult = NonNullable<Awaited<ReturnType<typeof patchApiTenantsTenantSlugWorkplaceOptions>>>
    export type PatchApiTenantsTenantSlugWorkplaceOptionsMutationBody = WorkplaceOptionsPatch
    export type PatchApiTenantsTenantSlugWorkplaceOptionsMutationError = Error
    export type PatchApiTenantsTenantSlugWorkplaceOptionsMutationVariables = {tenantSlug: string;data: WorkplaceOptionsPatch}

    /**
 * @summary Update tenant values or permitted entity overrides for workplace options
 */
export const usePatchApiTenantsTenantSlugWorkplaceOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugWorkplaceOptions>>, TError,PatchApiTenantsTenantSlugWorkplaceOptionsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchApiTenantsTenantSlugWorkplaceOptions>>,
        TError,
        PatchApiTenantsTenantSlugWorkplaceOptionsMutationVariables,
        TContext
      > => {
      return useMutation(getPatchApiTenantsTenantSlugWorkplaceOptionsMutationOptions(options), queryClient);
    }

export const getGetApiTenantsTenantSlugCustomerSettingsUrl = (tenantSlug: string,) => {




  return `/api/tenants/${tenantSlug}/customer-settings`
}

/**
 * @summary Read effective customer settings for the selected entity
 */
export const getApiTenantsTenantSlugCustomerSettings = async (tenantSlug: string, options?: Parameters<typeof customFetch>[1]): Promise<CustomerSettings> => {

  return customFetch<CustomerSettings>(getGetApiTenantsTenantSlugCustomerSettingsUrl(tenantSlug),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiTenantsTenantSlugCustomerSettingsQueryKey = (tenantSlug: string,) => {
    return [
    `/api/tenants/${tenantSlug}/customer-settings`
    ] as const;
    }


export const getGetApiTenantsTenantSlugCustomerSettingsQueryOptions = <TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugCustomerSettings>>, TError = unknown>(tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugCustomerSettings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiTenantsTenantSlugCustomerSettingsQueryKey(tenantSlug);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiTenantsTenantSlugCustomerSettings>>> = ({ signal }) => getApiTenantsTenantSlugCustomerSettings(tenantSlug, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugCustomerSettings>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiTenantsTenantSlugCustomerSettingsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiTenantsTenantSlugCustomerSettings>>>
export type GetApiTenantsTenantSlugCustomerSettingsQueryError = unknown


export function useGetApiTenantsTenantSlugCustomerSettings<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugCustomerSettings>>, TError = unknown>(
 tenantSlug: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugCustomerSettings>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugCustomerSettings>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugCustomerSettings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugCustomerSettings<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugCustomerSettings>>, TError = unknown>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugCustomerSettings>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugCustomerSettings>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugCustomerSettings>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugCustomerSettings<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugCustomerSettings>>, TError = unknown>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugCustomerSettings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read effective customer settings for the selected entity
 */

export function useGetApiTenantsTenantSlugCustomerSettings<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugCustomerSettings>>, TError = unknown>(
 tenantSlug: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugCustomerSettings>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiTenantsTenantSlugCustomerSettingsQueryOptions(tenantSlug,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPatchApiTenantsTenantSlugCustomerSettingsUrl = (tenantSlug: string,) => {




  return `/api/tenants/${tenantSlug}/customer-settings`
}

/**
 * @summary Update tenant defaults or permitted entity overrides
 */
export const patchApiTenantsTenantSlugCustomerSettings = async (tenantSlug: string,
    customerSettingsPatch: CustomerSettingsPatch, options?: Parameters<typeof customFetch>[1]): Promise<CustomerSettings> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CustomerSettings>(getPatchApiTenantsTenantSlugCustomerSettingsUrl(tenantSlug),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(customerSettingsPatch)
  }
);}





export const getPatchApiTenantsTenantSlugCustomerSettingsMutationKey = () => ['patchApiTenantsTenantSlugCustomerSettings'] as const;

export const getPatchApiTenantsTenantSlugCustomerSettingsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugCustomerSettings>>, TError,PatchApiTenantsTenantSlugCustomerSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugCustomerSettings>>, TError,PatchApiTenantsTenantSlugCustomerSettingsMutationVariables, TContext> => {

const mutationKey = getPatchApiTenantsTenantSlugCustomerSettingsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchApiTenantsTenantSlugCustomerSettings>>, PatchApiTenantsTenantSlugCustomerSettingsMutationVariables> = (props) => {
          const {tenantSlug,data} = props ?? {};

          return  patchApiTenantsTenantSlugCustomerSettings(tenantSlug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchApiTenantsTenantSlugCustomerSettingsMutationResult = NonNullable<Awaited<ReturnType<typeof patchApiTenantsTenantSlugCustomerSettings>>>
    export type PatchApiTenantsTenantSlugCustomerSettingsMutationBody = CustomerSettingsPatch
    export type PatchApiTenantsTenantSlugCustomerSettingsMutationError = Error
    export type PatchApiTenantsTenantSlugCustomerSettingsMutationVariables = {tenantSlug: string;data: CustomerSettingsPatch}

    /**
 * @summary Update tenant defaults or permitted entity overrides
 */
export const usePatchApiTenantsTenantSlugCustomerSettings = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugCustomerSettings>>, TError,PatchApiTenantsTenantSlugCustomerSettingsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchApiTenantsTenantSlugCustomerSettings>>,
        TError,
        PatchApiTenantsTenantSlugCustomerSettingsMutationVariables,
        TContext
      > => {
      return useMutation(getPatchApiTenantsTenantSlugCustomerSettingsMutationOptions(options), queryClient);
    }

export const getPutApiTenantsTenantSlugCustomerLogoUrl = (tenantSlug: string,) => {




  return `/api/tenants/${tenantSlug}/customer-logo`
}

/**
 * @summary Upload or replace a validated customer logo
 */
export const putApiTenantsTenantSlugCustomerLogo = async (tenantSlug: string,
    putApiTenantsTenantSlugCustomerLogoBody: PutApiTenantsTenantSlugCustomerLogoBody, options?: Parameters<typeof customFetch>[1]): Promise<CustomerSettings> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CustomerSettings>(getPutApiTenantsTenantSlugCustomerLogoUrl(tenantSlug),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(putApiTenantsTenantSlugCustomerLogoBody)
  }
);}





export const getPutApiTenantsTenantSlugCustomerLogoMutationKey = () => ['putApiTenantsTenantSlugCustomerLogo'] as const;

export const getPutApiTenantsTenantSlugCustomerLogoMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putApiTenantsTenantSlugCustomerLogo>>, TError,PutApiTenantsTenantSlugCustomerLogoMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof putApiTenantsTenantSlugCustomerLogo>>, TError,PutApiTenantsTenantSlugCustomerLogoMutationVariables, TContext> => {

const mutationKey = getPutApiTenantsTenantSlugCustomerLogoMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof putApiTenantsTenantSlugCustomerLogo>>, PutApiTenantsTenantSlugCustomerLogoMutationVariables> = (props) => {
          const {tenantSlug,data} = props ?? {};

          return  putApiTenantsTenantSlugCustomerLogo(tenantSlug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PutApiTenantsTenantSlugCustomerLogoMutationResult = NonNullable<Awaited<ReturnType<typeof putApiTenantsTenantSlugCustomerLogo>>>
    export type PutApiTenantsTenantSlugCustomerLogoMutationBody = PutApiTenantsTenantSlugCustomerLogoBody
    export type PutApiTenantsTenantSlugCustomerLogoMutationError = unknown
    export type PutApiTenantsTenantSlugCustomerLogoMutationVariables = {tenantSlug: string;data: PutApiTenantsTenantSlugCustomerLogoBody}

    /**
 * @summary Upload or replace a validated customer logo
 */
export const usePutApiTenantsTenantSlugCustomerLogo = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof putApiTenantsTenantSlugCustomerLogo>>, TError,PutApiTenantsTenantSlugCustomerLogoMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof putApiTenantsTenantSlugCustomerLogo>>,
        TError,
        PutApiTenantsTenantSlugCustomerLogoMutationVariables,
        TContext
      > => {
      return useMutation(getPutApiTenantsTenantSlugCustomerLogoMutationOptions(options), queryClient);
    }

export const getDeleteApiTenantsTenantSlugCustomerLogoUrl = (tenantSlug: string,
    params: DeleteApiTenantsTenantSlugCustomerLogoParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/customer-logo?${stringifiedParams}` : `/api/tenants/${tenantSlug}/customer-logo`
}

/**
 * @summary Remove a customer logo default or entity override
 */
export const deleteApiTenantsTenantSlugCustomerLogo = async (tenantSlug: string,
    params: DeleteApiTenantsTenantSlugCustomerLogoParams, options?: Parameters<typeof customFetch>[1]): Promise<CustomerSettings> => {

  return customFetch<CustomerSettings>(getDeleteApiTenantsTenantSlugCustomerLogoUrl(tenantSlug,params),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteApiTenantsTenantSlugCustomerLogoMutationKey = () => ['deleteApiTenantsTenantSlugCustomerLogo'] as const;

export const getDeleteApiTenantsTenantSlugCustomerLogoMutationOptions = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteApiTenantsTenantSlugCustomerLogo>>, TError,DeleteApiTenantsTenantSlugCustomerLogoMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteApiTenantsTenantSlugCustomerLogo>>, TError,DeleteApiTenantsTenantSlugCustomerLogoMutationVariables, TContext> => {

const mutationKey = getDeleteApiTenantsTenantSlugCustomerLogoMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteApiTenantsTenantSlugCustomerLogo>>, DeleteApiTenantsTenantSlugCustomerLogoMutationVariables> = (props) => {
          const {tenantSlug,params} = props ?? {};

          return  deleteApiTenantsTenantSlugCustomerLogo(tenantSlug,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteApiTenantsTenantSlugCustomerLogoMutationResult = NonNullable<Awaited<ReturnType<typeof deleteApiTenantsTenantSlugCustomerLogo>>>

    export type DeleteApiTenantsTenantSlugCustomerLogoMutationError = unknown
    export type DeleteApiTenantsTenantSlugCustomerLogoMutationVariables = {tenantSlug: string;params: DeleteApiTenantsTenantSlugCustomerLogoParams}

    /**
 * @summary Remove a customer logo default or entity override
 */
export const useDeleteApiTenantsTenantSlugCustomerLogo = <TError = unknown,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteApiTenantsTenantSlugCustomerLogo>>, TError,DeleteApiTenantsTenantSlugCustomerLogoMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteApiTenantsTenantSlugCustomerLogo>>,
        TError,
        DeleteApiTenantsTenantSlugCustomerLogoMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteApiTenantsTenantSlugCustomerLogoMutationOptions(options), queryClient);
    }

export const getGetApiTenantsTenantSlugAuditUrl = (tenantSlug: string,
    params?: GetApiTenantsTenantSlugAuditParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/audit?${stringifiedParams}` : `/api/tenants/${tenantSlug}/audit`
}

/**
 * @summary List tenant audit events
 */
export const getApiTenantsTenantSlugAudit = async (tenantSlug: string,
    params?: GetApiTenantsTenantSlugAuditParams, options?: Parameters<typeof customFetch>[1]): Promise<GetApiTenantsTenantSlugAudit200> => {

  return customFetch<GetApiTenantsTenantSlugAudit200>(getGetApiTenantsTenantSlugAuditUrl(tenantSlug,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiTenantsTenantSlugAuditQueryKey = (tenantSlug: string,
    params?: GetApiTenantsTenantSlugAuditParams,) => {
    return [
    `/api/tenants/${tenantSlug}/audit`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetApiTenantsTenantSlugAuditQueryOptions = <TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugAudit>>, TError = Error>(tenantSlug: string,
    params?: GetApiTenantsTenantSlugAuditParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugAudit>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiTenantsTenantSlugAuditQueryKey(tenantSlug,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiTenantsTenantSlugAudit>>> = ({ signal }) => getApiTenantsTenantSlugAudit(tenantSlug,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugAudit>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiTenantsTenantSlugAuditQueryResult = NonNullable<Awaited<ReturnType<typeof getApiTenantsTenantSlugAudit>>>
export type GetApiTenantsTenantSlugAuditQueryError = Error


export function useGetApiTenantsTenantSlugAudit<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugAudit>>, TError = Error>(
 tenantSlug: string,
    params: undefined |  GetApiTenantsTenantSlugAuditParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugAudit>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugAudit>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugAudit>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugAudit<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugAudit>>, TError = Error>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugAuditParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugAudit>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugAudit>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugAudit>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugAudit<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugAudit>>, TError = Error>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugAuditParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugAudit>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List tenant audit events
 */

export function useGetApiTenantsTenantSlugAudit<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugAudit>>, TError = Error>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugAuditParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugAudit>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiTenantsTenantSlugAuditQueryOptions(tenantSlug,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetApiTenantsTenantSlugDirectorySearchUrl = (tenantSlug: string,
    params: GetApiTenantsTenantSlugDirectorySearchParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/directory/search?${stringifiedParams}` : `/api/tenants/${tenantSlug}/directory/search`
}

/**
 * Delegated Microsoft Graph user search (displayName/mail typeahead) for invite and member-picker flows. Returns `{ available: false }` when Graph consent/token is unavailable so the UI can fall back to manual email entry without a hard failure.
 * @summary Search Entra directory people for invites
 */
export const getApiTenantsTenantSlugDirectorySearch = async (tenantSlug: string,
    params: GetApiTenantsTenantSlugDirectorySearchParams, options?: Parameters<typeof customFetch>[1]): Promise<DirectorySearchResult> => {

  return customFetch<DirectorySearchResult>(getGetApiTenantsTenantSlugDirectorySearchUrl(tenantSlug,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiTenantsTenantSlugDirectorySearchQueryKey = (tenantSlug: string,
    params?: GetApiTenantsTenantSlugDirectorySearchParams,) => {
    return [
    `/api/tenants/${tenantSlug}/directory/search`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetApiTenantsTenantSlugDirectorySearchQueryOptions = <TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugDirectorySearch>>, TError = Error>(tenantSlug: string,
    params: GetApiTenantsTenantSlugDirectorySearchParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugDirectorySearch>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiTenantsTenantSlugDirectorySearchQueryKey(tenantSlug,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiTenantsTenantSlugDirectorySearch>>> = ({ signal }) => getApiTenantsTenantSlugDirectorySearch(tenantSlug,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugDirectorySearch>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiTenantsTenantSlugDirectorySearchQueryResult = NonNullable<Awaited<ReturnType<typeof getApiTenantsTenantSlugDirectorySearch>>>
export type GetApiTenantsTenantSlugDirectorySearchQueryError = Error


export function useGetApiTenantsTenantSlugDirectorySearch<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugDirectorySearch>>, TError = Error>(
 tenantSlug: string,
    params: GetApiTenantsTenantSlugDirectorySearchParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugDirectorySearch>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugDirectorySearch>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugDirectorySearch>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugDirectorySearch<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugDirectorySearch>>, TError = Error>(
 tenantSlug: string,
    params: GetApiTenantsTenantSlugDirectorySearchParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugDirectorySearch>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugDirectorySearch>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugDirectorySearch>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugDirectorySearch<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugDirectorySearch>>, TError = Error>(
 tenantSlug: string,
    params: GetApiTenantsTenantSlugDirectorySearchParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugDirectorySearch>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Search Entra directory people for invites
 */

export function useGetApiTenantsTenantSlugDirectorySearch<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugDirectorySearch>>, TError = Error>(
 tenantSlug: string,
    params: GetApiTenantsTenantSlugDirectorySearchParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugDirectorySearch>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiTenantsTenantSlugDirectorySearchQueryOptions(tenantSlug,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPatchApiTenantsTenantSlugRolesRoleIdUrl = (tenantSlug: string,
    roleId: string,) => {




  return `/api/tenants/${tenantSlug}/roles/${roleId}`
}

/**
 * @summary Update a custom tenant role (tenant.roles.manage)
 */
export const patchApiTenantsTenantSlugRolesRoleId = async (tenantSlug: string,
    roleId: string,
    patchApiTenantsTenantSlugRolesRoleIdBody: PatchApiTenantsTenantSlugRolesRoleIdBody, options?: Parameters<typeof customFetch>[1]): Promise<TenantRoleDetail> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<TenantRoleDetail>(getPatchApiTenantsTenantSlugRolesRoleIdUrl(tenantSlug,roleId),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(patchApiTenantsTenantSlugRolesRoleIdBody)
  }
);}





export const getPatchApiTenantsTenantSlugRolesRoleIdMutationKey = () => ['patchApiTenantsTenantSlugRolesRoleId'] as const;

export const getPatchApiTenantsTenantSlugRolesRoleIdMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugRolesRoleId>>, TError,PatchApiTenantsTenantSlugRolesRoleIdMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugRolesRoleId>>, TError,PatchApiTenantsTenantSlugRolesRoleIdMutationVariables, TContext> => {

const mutationKey = getPatchApiTenantsTenantSlugRolesRoleIdMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchApiTenantsTenantSlugRolesRoleId>>, PatchApiTenantsTenantSlugRolesRoleIdMutationVariables> = (props) => {
          const {tenantSlug,roleId,data} = props ?? {};

          return  patchApiTenantsTenantSlugRolesRoleId(tenantSlug,roleId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchApiTenantsTenantSlugRolesRoleIdMutationResult = NonNullable<Awaited<ReturnType<typeof patchApiTenantsTenantSlugRolesRoleId>>>
    export type PatchApiTenantsTenantSlugRolesRoleIdMutationBody = PatchApiTenantsTenantSlugRolesRoleIdBody
    export type PatchApiTenantsTenantSlugRolesRoleIdMutationError = Error
    export type PatchApiTenantsTenantSlugRolesRoleIdMutationVariables = {tenantSlug: string;roleId: string;data: PatchApiTenantsTenantSlugRolesRoleIdBody}

    /**
 * @summary Update a custom tenant role (tenant.roles.manage)
 */
export const usePatchApiTenantsTenantSlugRolesRoleId = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchApiTenantsTenantSlugRolesRoleId>>, TError,PatchApiTenantsTenantSlugRolesRoleIdMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchApiTenantsTenantSlugRolesRoleId>>,
        TError,
        PatchApiTenantsTenantSlugRolesRoleIdMutationVariables,
        TContext
      > => {
      return useMutation(getPatchApiTenantsTenantSlugRolesRoleIdMutationOptions(options), queryClient);
    }

export const getDeleteApiTenantsTenantSlugRolesRoleIdUrl = (tenantSlug: string,
    roleId: string,) => {




  return `/api/tenants/${tenantSlug}/roles/${roleId}`
}

/**
 * @summary Delete a custom tenant role (tenant.roles.manage)
 */
export const deleteApiTenantsTenantSlugRolesRoleId = async (tenantSlug: string,
    roleId: string, options?: Parameters<typeof customFetch>[1]): Promise<RoleDeleteResult> => {

  return customFetch<RoleDeleteResult>(getDeleteApiTenantsTenantSlugRolesRoleIdUrl(tenantSlug,roleId),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getDeleteApiTenantsTenantSlugRolesRoleIdMutationKey = () => ['deleteApiTenantsTenantSlugRolesRoleId'] as const;

export const getDeleteApiTenantsTenantSlugRolesRoleIdMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteApiTenantsTenantSlugRolesRoleId>>, TError,DeleteApiTenantsTenantSlugRolesRoleIdMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteApiTenantsTenantSlugRolesRoleId>>, TError,DeleteApiTenantsTenantSlugRolesRoleIdMutationVariables, TContext> => {

const mutationKey = getDeleteApiTenantsTenantSlugRolesRoleIdMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteApiTenantsTenantSlugRolesRoleId>>, DeleteApiTenantsTenantSlugRolesRoleIdMutationVariables> = (props) => {
          const {tenantSlug,roleId} = props ?? {};

          return  deleteApiTenantsTenantSlugRolesRoleId(tenantSlug,roleId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteApiTenantsTenantSlugRolesRoleIdMutationResult = NonNullable<Awaited<ReturnType<typeof deleteApiTenantsTenantSlugRolesRoleId>>>

    export type DeleteApiTenantsTenantSlugRolesRoleIdMutationError = Error
    export type DeleteApiTenantsTenantSlugRolesRoleIdMutationVariables = {tenantSlug: string;roleId: string}

    /**
 * @summary Delete a custom tenant role (tenant.roles.manage)
 */
export const useDeleteApiTenantsTenantSlugRolesRoleId = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteApiTenantsTenantSlugRolesRoleId>>, TError,DeleteApiTenantsTenantSlugRolesRoleIdMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteApiTenantsTenantSlugRolesRoleId>>,
        TError,
        DeleteApiTenantsTenantSlugRolesRoleIdMutationVariables,
        TContext
      > => {
      return useMutation(getDeleteApiTenantsTenantSlugRolesRoleIdMutationOptions(options), queryClient);
    }

export const getGetApiTenantsTenantSlugElevationsUrl = (tenantSlug: string,
    params?: GetApiTenantsTenantSlugElevationsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/elevations?${stringifiedParams}` : `/api/tenants/${tenantSlug}/elevations`
}

/**
 * @summary List elevation requests (tenant.elevation.request)
 */
export const getApiTenantsTenantSlugElevations = async (tenantSlug: string,
    params?: GetApiTenantsTenantSlugElevationsParams, options?: Parameters<typeof customFetch>[1]): Promise<ElevationRequestList> => {

  return customFetch<ElevationRequestList>(getGetApiTenantsTenantSlugElevationsUrl(tenantSlug,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiTenantsTenantSlugElevationsQueryKey = (tenantSlug: string,
    params?: GetApiTenantsTenantSlugElevationsParams,) => {
    return [
    `/api/tenants/${tenantSlug}/elevations`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetApiTenantsTenantSlugElevationsQueryOptions = <TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugElevations>>, TError = Error>(tenantSlug: string,
    params?: GetApiTenantsTenantSlugElevationsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugElevations>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiTenantsTenantSlugElevationsQueryKey(tenantSlug,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiTenantsTenantSlugElevations>>> = ({ signal }) => getApiTenantsTenantSlugElevations(tenantSlug,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugElevations>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiTenantsTenantSlugElevationsQueryResult = NonNullable<Awaited<ReturnType<typeof getApiTenantsTenantSlugElevations>>>
export type GetApiTenantsTenantSlugElevationsQueryError = Error


export function useGetApiTenantsTenantSlugElevations<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugElevations>>, TError = Error>(
 tenantSlug: string,
    params: undefined |  GetApiTenantsTenantSlugElevationsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugElevations>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugElevations>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugElevations>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugElevations<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugElevations>>, TError = Error>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugElevationsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugElevations>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugElevations>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugElevations>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugElevations<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugElevations>>, TError = Error>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugElevationsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugElevations>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List elevation requests (tenant.elevation.request)
 */

export function useGetApiTenantsTenantSlugElevations<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugElevations>>, TError = Error>(
 tenantSlug: string,
    params?: GetApiTenantsTenantSlugElevationsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugElevations>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiTenantsTenantSlugElevationsQueryOptions(tenantSlug,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPostApiTenantsTenantSlugElevationsUrl = (tenantSlug: string,) => {




  return `/api/tenants/${tenantSlug}/elevations`
}

/**
 * @summary Request a JIT elevation (tenant.elevation.request)
 */
export const postApiTenantsTenantSlugElevations = async (tenantSlug: string,
    postApiTenantsTenantSlugElevationsBody: PostApiTenantsTenantSlugElevationsBody, options?: Parameters<typeof customFetch>[1]): Promise<ElevationRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ElevationRequest>(getPostApiTenantsTenantSlugElevationsUrl(tenantSlug),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(postApiTenantsTenantSlugElevationsBody)
  }
);}





export const getPostApiTenantsTenantSlugElevationsMutationKey = () => ['postApiTenantsTenantSlugElevations'] as const;

export const getPostApiTenantsTenantSlugElevationsMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugElevations>>, TError,PostApiTenantsTenantSlugElevationsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugElevations>>, TError,PostApiTenantsTenantSlugElevationsMutationVariables, TContext> => {

const mutationKey = getPostApiTenantsTenantSlugElevationsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiTenantsTenantSlugElevations>>, PostApiTenantsTenantSlugElevationsMutationVariables> = (props) => {
          const {tenantSlug,data} = props ?? {};

          return  postApiTenantsTenantSlugElevations(tenantSlug,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostApiTenantsTenantSlugElevationsMutationResult = NonNullable<Awaited<ReturnType<typeof postApiTenantsTenantSlugElevations>>>
    export type PostApiTenantsTenantSlugElevationsMutationBody = PostApiTenantsTenantSlugElevationsBody
    export type PostApiTenantsTenantSlugElevationsMutationError = Error
    export type PostApiTenantsTenantSlugElevationsMutationVariables = {tenantSlug: string;data: PostApiTenantsTenantSlugElevationsBody}

    /**
 * @summary Request a JIT elevation (tenant.elevation.request)
 */
export const usePostApiTenantsTenantSlugElevations = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugElevations>>, TError,PostApiTenantsTenantSlugElevationsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postApiTenantsTenantSlugElevations>>,
        TError,
        PostApiTenantsTenantSlugElevationsMutationVariables,
        TContext
      > => {
      return useMutation(getPostApiTenantsTenantSlugElevationsMutationOptions(options), queryClient);
    }

export const getGetApiTenantsTenantSlugElevationsElevationIdUrl = (tenantSlug: string,
    elevationId: string,) => {




  return `/api/tenants/${tenantSlug}/elevations/${elevationId}`
}

/**
 * @summary Get a single elevation request (tenant.elevation.request)
 */
export const getApiTenantsTenantSlugElevationsElevationId = async (tenantSlug: string,
    elevationId: string, options?: Parameters<typeof customFetch>[1]): Promise<ElevationRequest> => {

  return customFetch<ElevationRequest>(getGetApiTenantsTenantSlugElevationsElevationIdUrl(tenantSlug,elevationId),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiTenantsTenantSlugElevationsElevationIdQueryKey = (tenantSlug: string,
    elevationId: string,) => {
    return [
    `/api/tenants/${tenantSlug}/elevations/${elevationId}`
    ] as const;
    }


export const getGetApiTenantsTenantSlugElevationsElevationIdQueryOptions = <TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugElevationsElevationId>>, TError = Error>(tenantSlug: string,
    elevationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugElevationsElevationId>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiTenantsTenantSlugElevationsElevationIdQueryKey(tenantSlug,elevationId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiTenantsTenantSlugElevationsElevationId>>> = ({ signal }) => getApiTenantsTenantSlugElevationsElevationId(tenantSlug,elevationId, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined && elevationId !== null && elevationId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugElevationsElevationId>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiTenantsTenantSlugElevationsElevationIdQueryResult = NonNullable<Awaited<ReturnType<typeof getApiTenantsTenantSlugElevationsElevationId>>>
export type GetApiTenantsTenantSlugElevationsElevationIdQueryError = Error


export function useGetApiTenantsTenantSlugElevationsElevationId<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugElevationsElevationId>>, TError = Error>(
 tenantSlug: string,
    elevationId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugElevationsElevationId>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugElevationsElevationId>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugElevationsElevationId>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugElevationsElevationId<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugElevationsElevationId>>, TError = Error>(
 tenantSlug: string,
    elevationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugElevationsElevationId>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTenantsTenantSlugElevationsElevationId>>,
          TError,
          Awaited<ReturnType<typeof getApiTenantsTenantSlugElevationsElevationId>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiTenantsTenantSlugElevationsElevationId<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugElevationsElevationId>>, TError = Error>(
 tenantSlug: string,
    elevationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugElevationsElevationId>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get a single elevation request (tenant.elevation.request)
 */

export function useGetApiTenantsTenantSlugElevationsElevationId<TData = Awaited<ReturnType<typeof getApiTenantsTenantSlugElevationsElevationId>>, TError = Error>(
 tenantSlug: string,
    elevationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiTenantsTenantSlugElevationsElevationId>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiTenantsTenantSlugElevationsElevationIdQueryOptions(tenantSlug,elevationId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPostApiTenantsTenantSlugElevationsElevationIdResolveUrl = (tenantSlug: string,
    elevationId: string,) => {




  return `/api/tenants/${tenantSlug}/elevations/${elevationId}/resolve`
}

/**
 * @summary Approve or deny an elevation request (tenant.elevation.approve)
 */
export const postApiTenantsTenantSlugElevationsElevationIdResolve = async (tenantSlug: string,
    elevationId: string,
    postApiTenantsTenantSlugElevationsElevationIdResolveBody: PostApiTenantsTenantSlugElevationsElevationIdResolveBody, options?: Parameters<typeof customFetch>[1]): Promise<ElevationRequest> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<ElevationRequest>(getPostApiTenantsTenantSlugElevationsElevationIdResolveUrl(tenantSlug,elevationId),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(postApiTenantsTenantSlugElevationsElevationIdResolveBody)
  }
);}





export const getPostApiTenantsTenantSlugElevationsElevationIdResolveMutationKey = () => ['postApiTenantsTenantSlugElevationsElevationIdResolve'] as const;

export const getPostApiTenantsTenantSlugElevationsElevationIdResolveMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugElevationsElevationIdResolve>>, TError,PostApiTenantsTenantSlugElevationsElevationIdResolveMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugElevationsElevationIdResolve>>, TError,PostApiTenantsTenantSlugElevationsElevationIdResolveMutationVariables, TContext> => {

const mutationKey = getPostApiTenantsTenantSlugElevationsElevationIdResolveMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiTenantsTenantSlugElevationsElevationIdResolve>>, PostApiTenantsTenantSlugElevationsElevationIdResolveMutationVariables> = (props) => {
          const {tenantSlug,elevationId,data} = props ?? {};

          return  postApiTenantsTenantSlugElevationsElevationIdResolve(tenantSlug,elevationId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostApiTenantsTenantSlugElevationsElevationIdResolveMutationResult = NonNullable<Awaited<ReturnType<typeof postApiTenantsTenantSlugElevationsElevationIdResolve>>>
    export type PostApiTenantsTenantSlugElevationsElevationIdResolveMutationBody = PostApiTenantsTenantSlugElevationsElevationIdResolveBody
    export type PostApiTenantsTenantSlugElevationsElevationIdResolveMutationError = Error
    export type PostApiTenantsTenantSlugElevationsElevationIdResolveMutationVariables = {tenantSlug: string;elevationId: string;data: PostApiTenantsTenantSlugElevationsElevationIdResolveBody}

    /**
 * @summary Approve or deny an elevation request (tenant.elevation.approve)
 */
export const usePostApiTenantsTenantSlugElevationsElevationIdResolve = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiTenantsTenantSlugElevationsElevationIdResolve>>, TError,PostApiTenantsTenantSlugElevationsElevationIdResolveMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postApiTenantsTenantSlugElevationsElevationIdResolve>>,
        TError,
        PostApiTenantsTenantSlugElevationsElevationIdResolveMutationVariables,
        TContext
      > => {
      return useMutation(getPostApiTenantsTenantSlugElevationsElevationIdResolveMutationOptions(options), queryClient);
    }

export const getPostApiPlatformAdminOverrideUrl = () => {




  return `/api/platform-admin/override`
}

/**
 * @summary Start a break-glass override session into a tenant (platform admin only)
 */
export const postApiPlatformAdminOverride = async (postApiPlatformAdminOverrideBody: PostApiPlatformAdminOverrideBody, options?: Parameters<typeof customFetch>[1]): Promise<OverrideSession> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OverrideSession>(getPostApiPlatformAdminOverrideUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(postApiPlatformAdminOverrideBody)
  }
);}





export const getPostApiPlatformAdminOverrideMutationKey = () => ['postApiPlatformAdminOverride'] as const;

export const getPostApiPlatformAdminOverrideMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminOverride>>, TError,PostApiPlatformAdminOverrideMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminOverride>>, TError,PostApiPlatformAdminOverrideMutationVariables, TContext> => {

const mutationKey = getPostApiPlatformAdminOverrideMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiPlatformAdminOverride>>, PostApiPlatformAdminOverrideMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  postApiPlatformAdminOverride(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostApiPlatformAdminOverrideMutationResult = NonNullable<Awaited<ReturnType<typeof postApiPlatformAdminOverride>>>
    export type PostApiPlatformAdminOverrideMutationBody = PostApiPlatformAdminOverrideBody
    export type PostApiPlatformAdminOverrideMutationError = Error
    export type PostApiPlatformAdminOverrideMutationVariables = {data: PostApiPlatformAdminOverrideBody}

    /**
 * @summary Start a break-glass override session into a tenant (platform admin only)
 */
export const usePostApiPlatformAdminOverride = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminOverride>>, TError,PostApiPlatformAdminOverrideMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postApiPlatformAdminOverride>>,
        TError,
        PostApiPlatformAdminOverrideMutationVariables,
        TContext
      > => {
      return useMutation(getPostApiPlatformAdminOverrideMutationOptions(options), queryClient);
    }

export const getGetApiPlatformAdminOverrideUrl = (params?: GetApiPlatformAdminOverrideParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform-admin/override?${stringifiedParams}` : `/api/platform-admin/override`
}

/**
 * @summary List override sessions for the current platform admin
 */
export const getApiPlatformAdminOverride = async (params?: GetApiPlatformAdminOverrideParams, options?: Parameters<typeof customFetch>[1]): Promise<OverrideSessionList> => {

  return customFetch<OverrideSessionList>(getGetApiPlatformAdminOverrideUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiPlatformAdminOverrideQueryKey = (params?: GetApiPlatformAdminOverrideParams,) => {
    return [
    `/api/platform-admin/override`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetApiPlatformAdminOverrideQueryOptions = <TData = Awaited<ReturnType<typeof getApiPlatformAdminOverride>>, TError = Error>(params?: GetApiPlatformAdminOverrideParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminOverride>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiPlatformAdminOverrideQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiPlatformAdminOverride>>> = ({ signal }) => getApiPlatformAdminOverride(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminOverride>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiPlatformAdminOverrideQueryResult = NonNullable<Awaited<ReturnType<typeof getApiPlatformAdminOverride>>>
export type GetApiPlatformAdminOverrideQueryError = Error


export function useGetApiPlatformAdminOverride<TData = Awaited<ReturnType<typeof getApiPlatformAdminOverride>>, TError = Error>(
 params: undefined |  GetApiPlatformAdminOverrideParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminOverride>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminOverride>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminOverride>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminOverride<TData = Awaited<ReturnType<typeof getApiPlatformAdminOverride>>, TError = Error>(
 params?: GetApiPlatformAdminOverrideParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminOverride>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminOverride>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminOverride>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminOverride<TData = Awaited<ReturnType<typeof getApiPlatformAdminOverride>>, TError = Error>(
 params?: GetApiPlatformAdminOverrideParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminOverride>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List override sessions for the current platform admin
 */

export function useGetApiPlatformAdminOverride<TData = Awaited<ReturnType<typeof getApiPlatformAdminOverride>>, TError = Error>(
 params?: GetApiPlatformAdminOverrideParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminOverride>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiPlatformAdminOverrideQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getPostApiPlatformAdminOverrideEndUrl = () => {




  return `/api/platform-admin/override/end`
}

/**
 * @summary End an active override session (platform admin only)
 */
export const postApiPlatformAdminOverrideEnd = async (postApiPlatformAdminOverrideEndBody: PostApiPlatformAdminOverrideEndBody, options?: Parameters<typeof customFetch>[1]): Promise<OverrideSession> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<OverrideSession>(getPostApiPlatformAdminOverrideEndUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(postApiPlatformAdminOverrideEndBody)
  }
);}





export const getPostApiPlatformAdminOverrideEndMutationKey = () => ['postApiPlatformAdminOverrideEnd'] as const;

export const getPostApiPlatformAdminOverrideEndMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminOverrideEnd>>, TError,PostApiPlatformAdminOverrideEndMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminOverrideEnd>>, TError,PostApiPlatformAdminOverrideEndMutationVariables, TContext> => {

const mutationKey = getPostApiPlatformAdminOverrideEndMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof postApiPlatformAdminOverrideEnd>>, PostApiPlatformAdminOverrideEndMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  postApiPlatformAdminOverrideEnd(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PostApiPlatformAdminOverrideEndMutationResult = NonNullable<Awaited<ReturnType<typeof postApiPlatformAdminOverrideEnd>>>
    export type PostApiPlatformAdminOverrideEndMutationBody = PostApiPlatformAdminOverrideEndBody
    export type PostApiPlatformAdminOverrideEndMutationError = Error
    export type PostApiPlatformAdminOverrideEndMutationVariables = {data: PostApiPlatformAdminOverrideEndBody}

    /**
 * @summary End an active override session (platform admin only)
 */
export const usePostApiPlatformAdminOverrideEnd = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof postApiPlatformAdminOverrideEnd>>, TError,PostApiPlatformAdminOverrideEndMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof postApiPlatformAdminOverrideEnd>>,
        TError,
        PostApiPlatformAdminOverrideEndMutationVariables,
        TContext
      > => {
      return useMutation(getPostApiPlatformAdminOverrideEndMutationOptions(options), queryClient);
    }

export const getGetApiPlatformAdminGovernanceReadinessUrl = () => {




  return `/api/platform-admin/governance-readiness`
}

/**
 * Both privileged operations remain unavailable until their separate custody and complete aggregate adapters are configured.
 * @summary Read audit custody and entity transfer readiness
 */
export const getApiPlatformAdminGovernanceReadiness = async ( options?: Parameters<typeof customFetch>[1]): Promise<GovernanceReadiness> => {

  return customFetch<GovernanceReadiness>(getGetApiPlatformAdminGovernanceReadinessUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiPlatformAdminGovernanceReadinessQueryKey = () => {
    return [
    `/api/platform-admin/governance-readiness`
    ] as const;
    }


export const getGetApiPlatformAdminGovernanceReadinessQueryOptions = <TData = Awaited<ReturnType<typeof getApiPlatformAdminGovernanceReadiness>>, TError = Error>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminGovernanceReadiness>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiPlatformAdminGovernanceReadinessQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiPlatformAdminGovernanceReadiness>>> = ({ signal }) => getApiPlatformAdminGovernanceReadiness({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminGovernanceReadiness>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiPlatformAdminGovernanceReadinessQueryResult = NonNullable<Awaited<ReturnType<typeof getApiPlatformAdminGovernanceReadiness>>>
export type GetApiPlatformAdminGovernanceReadinessQueryError = Error


export function useGetApiPlatformAdminGovernanceReadiness<TData = Awaited<ReturnType<typeof getApiPlatformAdminGovernanceReadiness>>, TError = Error>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminGovernanceReadiness>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminGovernanceReadiness>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminGovernanceReadiness>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminGovernanceReadiness<TData = Awaited<ReturnType<typeof getApiPlatformAdminGovernanceReadiness>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminGovernanceReadiness>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminGovernanceReadiness>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminGovernanceReadiness>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminGovernanceReadiness<TData = Awaited<ReturnType<typeof getApiPlatformAdminGovernanceReadiness>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminGovernanceReadiness>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read audit custody and entity transfer readiness
 */

export function useGetApiPlatformAdminGovernanceReadiness<TData = Awaited<ReturnType<typeof getApiPlatformAdminGovernanceReadiness>>, TError = Error>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminGovernanceReadiness>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiPlatformAdminGovernanceReadinessQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getGetApiPlatformAdminAuditUrl = (params?: GetApiPlatformAdminAuditParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/platform-admin/audit?${stringifiedParams}` : `/api/platform-admin/audit`
}

/**
 * @summary Query platform-plane audit events (platform admin only)
 */
export const getApiPlatformAdminAudit = async (params?: GetApiPlatformAdminAuditParams, options?: Parameters<typeof customFetch>[1]): Promise<TenancyAuditEventList> => {

  return customFetch<TenancyAuditEventList>(getGetApiPlatformAdminAuditUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetApiPlatformAdminAuditQueryKey = (params?: GetApiPlatformAdminAuditParams,) => {
    return [
    `/api/platform-admin/audit`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetApiPlatformAdminAuditQueryOptions = <TData = Awaited<ReturnType<typeof getApiPlatformAdminAudit>>, TError = Error>(params?: GetApiPlatformAdminAuditParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminAudit>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetApiPlatformAdminAuditQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiPlatformAdminAudit>>> = ({ signal }) => getApiPlatformAdminAudit(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminAudit>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetApiPlatformAdminAuditQueryResult = NonNullable<Awaited<ReturnType<typeof getApiPlatformAdminAudit>>>
export type GetApiPlatformAdminAuditQueryError = Error


export function useGetApiPlatformAdminAudit<TData = Awaited<ReturnType<typeof getApiPlatformAdminAudit>>, TError = Error>(
 params: undefined |  GetApiPlatformAdminAuditParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminAudit>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminAudit>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminAudit>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminAudit<TData = Awaited<ReturnType<typeof getApiPlatformAdminAudit>>, TError = Error>(
 params?: GetApiPlatformAdminAuditParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminAudit>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiPlatformAdminAudit>>,
          TError,
          Awaited<ReturnType<typeof getApiPlatformAdminAudit>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetApiPlatformAdminAudit<TData = Awaited<ReturnType<typeof getApiPlatformAdminAudit>>, TError = Error>(
 params?: GetApiPlatformAdminAuditParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminAudit>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Query platform-plane audit events (platform admin only)
 */

export function useGetApiPlatformAdminAudit<TData = Awaited<ReturnType<typeof getApiPlatformAdminAudit>>, TError = Error>(
 params?: GetApiPlatformAdminAuditParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getApiPlatformAdminAudit>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetApiPlatformAdminAuditQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListCredentialTypesUrl = (params?: ListCredentialTypesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/credential-types?${stringifiedParams}` : `/api/credential-types`
}

/**
 * @summary List selected-entity credential types; employees see enabled types only
 */
export const listCredentialTypes = async (params?: ListCredentialTypesParams, options?: Parameters<typeof customFetch>[1]): Promise<CredentialTypeList> => {

  return customFetch<CredentialTypeList>(getListCredentialTypesUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCredentialTypesQueryKey = (params?: ListCredentialTypesParams,) => {
    return [
    `/api/credential-types`, ...(params ? [params] : [])
    ] as const;
    }


export const getListCredentialTypesQueryOptions = <TData = Awaited<ReturnType<typeof listCredentialTypes>>, TError = Error>(params?: ListCredentialTypesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listCredentialTypes>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCredentialTypesQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCredentialTypes>>> = ({ signal }) => listCredentialTypes(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCredentialTypes>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListCredentialTypesQueryResult = NonNullable<Awaited<ReturnType<typeof listCredentialTypes>>>
export type ListCredentialTypesQueryError = Error


export function useListCredentialTypes<TData = Awaited<ReturnType<typeof listCredentialTypes>>, TError = Error>(
 params: undefined |  ListCredentialTypesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listCredentialTypes>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCredentialTypes>>,
          TError,
          Awaited<ReturnType<typeof listCredentialTypes>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListCredentialTypes<TData = Awaited<ReturnType<typeof listCredentialTypes>>, TError = Error>(
 params?: ListCredentialTypesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listCredentialTypes>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCredentialTypes>>,
          TError,
          Awaited<ReturnType<typeof listCredentialTypes>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListCredentialTypes<TData = Awaited<ReturnType<typeof listCredentialTypes>>, TError = Error>(
 params?: ListCredentialTypesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listCredentialTypes>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List selected-entity credential types; employees see enabled types only
 */

export function useListCredentialTypes<TData = Awaited<ReturnType<typeof listCredentialTypes>>, TError = Error>(
 params?: ListCredentialTypesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listCredentialTypes>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListCredentialTypesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getCreateCredentialTypeUrl = () => {




  return `/api/credential-types`
}

/**
 * @summary Create a credential type with policy management permission
 */
export const createCredentialType = async (createCredentialTypeRequest: CreateCredentialTypeRequest, options?: Parameters<typeof customFetch>[1]): Promise<CredentialType> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CredentialType>(getCreateCredentialTypeUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(createCredentialTypeRequest)
  }
);}





export const getCreateCredentialTypeMutationKey = () => ['createCredentialType'] as const;

export const getCreateCredentialTypeMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCredentialType>>, TError,CreateCredentialTypeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof createCredentialType>>, TError,CreateCredentialTypeMutationVariables, TContext> => {

const mutationKey = getCreateCredentialTypeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createCredentialType>>, CreateCredentialTypeMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createCredentialType(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateCredentialTypeMutationResult = NonNullable<Awaited<ReturnType<typeof createCredentialType>>>
    export type CreateCredentialTypeMutationBody = CreateCredentialTypeRequest
    export type CreateCredentialTypeMutationError = Error
    export type CreateCredentialTypeMutationVariables = {data: CreateCredentialTypeRequest}

    /**
 * @summary Create a credential type with policy management permission
 */
export const useCreateCredentialType = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createCredentialType>>, TError,CreateCredentialTypeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createCredentialType>>,
        TError,
        CreateCredentialTypeMutationVariables,
        TContext
      > => {
      return useMutation(getCreateCredentialTypeMutationOptions(options), queryClient);
    }

export const getUpdateCredentialTypeUrl = (id: string,) => {




  return `/api/credential-types/${id}`
}

/**
 * @summary Update a selected-entity credential type
 */
export const updateCredentialType = async (id: string,
    updateCredentialTypeRequest: UpdateCredentialTypeRequest, options?: Parameters<typeof customFetch>[1]): Promise<CredentialType> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<CredentialType>(getUpdateCredentialTypeUrl(id),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(updateCredentialTypeRequest)
  }
);}





export const getUpdateCredentialTypeMutationKey = () => ['updateCredentialType'] as const;

export const getUpdateCredentialTypeMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCredentialType>>, TError,UpdateCredentialTypeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateCredentialType>>, TError,UpdateCredentialTypeMutationVariables, TContext> => {

const mutationKey = getUpdateCredentialTypeMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateCredentialType>>, UpdateCredentialTypeMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  updateCredentialType(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateCredentialTypeMutationResult = NonNullable<Awaited<ReturnType<typeof updateCredentialType>>>
    export type UpdateCredentialTypeMutationBody = UpdateCredentialTypeRequest
    export type UpdateCredentialTypeMutationError = Error
    export type UpdateCredentialTypeMutationVariables = {id: string;data: UpdateCredentialTypeRequest}

    /**
 * @summary Update a selected-entity credential type
 */
export const useUpdateCredentialType = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateCredentialType>>, TError,UpdateCredentialTypeMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateCredentialType>>,
        TError,
        UpdateCredentialTypeMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateCredentialTypeMutationOptions(options), queryClient);
    }

export const getListMyCredentialsUrl = (params?: ListMyCredentialsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/credentials/me?${stringifiedParams}` : `/api/credentials/me`
}

/**
 * @summary List the signed-in user's selected-entity submissions without document bytes
 */
export const listMyCredentials = async (params?: ListMyCredentialsParams, options?: Parameters<typeof customFetch>[1]): Promise<UserCredentialList> => {

  return customFetch<UserCredentialList>(getListMyCredentialsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListMyCredentialsQueryKey = (params?: ListMyCredentialsParams,) => {
    return [
    `/api/credentials/me`, ...(params ? [params] : [])
    ] as const;
    }


export const getListMyCredentialsQueryOptions = <TData = Awaited<ReturnType<typeof listMyCredentials>>, TError = Error>(params?: ListMyCredentialsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyCredentials>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListMyCredentialsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listMyCredentials>>> = ({ signal }) => listMyCredentials(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listMyCredentials>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListMyCredentialsQueryResult = NonNullable<Awaited<ReturnType<typeof listMyCredentials>>>
export type ListMyCredentialsQueryError = Error


export function useListMyCredentials<TData = Awaited<ReturnType<typeof listMyCredentials>>, TError = Error>(
 params: undefined |  ListMyCredentialsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyCredentials>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyCredentials>>,
          TError,
          Awaited<ReturnType<typeof listMyCredentials>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyCredentials<TData = Awaited<ReturnType<typeof listMyCredentials>>, TError = Error>(
 params?: ListMyCredentialsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyCredentials>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listMyCredentials>>,
          TError,
          Awaited<ReturnType<typeof listMyCredentials>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListMyCredentials<TData = Awaited<ReturnType<typeof listMyCredentials>>, TError = Error>(
 params?: ListMyCredentialsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyCredentials>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List the signed-in user's selected-entity submissions without document bytes
 */

export function useListMyCredentials<TData = Awaited<ReturnType<typeof listMyCredentials>>, TError = Error>(
 params?: ListMyCredentialsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listMyCredentials>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListMyCredentialsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSubmitMyCredentialUrl = () => {




  return `/api/credentials/me`
}

/**
 * @summary Submit a selected-entity credential with one PDF, JPEG or PNG document (maximum 10 MB)
 */
export const submitMyCredential = async (submitCredentialRequest: SubmitCredentialRequest, options?: Parameters<typeof customFetch>[1]): Promise<UserCredential> => {
    const formData = new FormData();
formData.append(`credentialTypeId`, submitCredentialRequest.credentialTypeId);
formData.append(`referenceNumber`, submitCredentialRequest.referenceNumber);
if(submitCredentialRequest.expiresAt !== undefined && submitCredentialRequest.expiresAt !== null) {
 formData.append(`expiresAt`, submitCredentialRequest.expiresAt);
 }
formData.append(`document`, submitCredentialRequest.document);

  return customFetch<UserCredential>(getSubmitMyCredentialUrl(),
  {
    ...options,
    method: 'POST'
    ,
    body: formData
  }
);}





export const getSubmitMyCredentialMutationKey = () => ['submitMyCredential'] as const;

export const getSubmitMyCredentialMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitMyCredential>>, TError,SubmitMyCredentialMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof submitMyCredential>>, TError,SubmitMyCredentialMutationVariables, TContext> => {

const mutationKey = getSubmitMyCredentialMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof submitMyCredential>>, SubmitMyCredentialMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  submitMyCredential(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SubmitMyCredentialMutationResult = NonNullable<Awaited<ReturnType<typeof submitMyCredential>>>
    export type SubmitMyCredentialMutationBody = SubmitCredentialRequest
    export type SubmitMyCredentialMutationError = Error
    export type SubmitMyCredentialMutationVariables = {data: SubmitCredentialRequest}

    /**
 * @summary Submit a selected-entity credential with one PDF, JPEG or PNG document (maximum 10 MB)
 */
export const useSubmitMyCredential = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof submitMyCredential>>, TError,SubmitMyCredentialMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof submitMyCredential>>,
        TError,
        SubmitMyCredentialMutationVariables,
        TContext
      > => {
      return useMutation(getSubmitMyCredentialMutationOptions(options), queryClient);
    }

export const getDownloadCredentialDocumentUrl = (id: string,) => {




  return `/api/credentials/${id}/document`
}

/**
 * @summary Download own document or review an entity document with policy management permission
 */
export const downloadCredentialDocument = async (id: string, options?: Parameters<typeof customFetch>[1]): Promise<Blob> => {

  return customFetch<Blob>(getDownloadCredentialDocumentUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getDownloadCredentialDocumentQueryKey = (id: string,) => {
    return [
    `/api/credentials/${id}/document`
    ] as const;
    }


export const getDownloadCredentialDocumentQueryOptions = <TData = Awaited<ReturnType<typeof downloadCredentialDocument>>, TError = Error>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadCredentialDocument>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getDownloadCredentialDocumentQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof downloadCredentialDocument>>> = ({ signal }) => downloadCredentialDocument(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof downloadCredentialDocument>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type DownloadCredentialDocumentQueryResult = NonNullable<Awaited<ReturnType<typeof downloadCredentialDocument>>>
export type DownloadCredentialDocumentQueryError = Error


export function useDownloadCredentialDocument<TData = Awaited<ReturnType<typeof downloadCredentialDocument>>, TError = Error>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadCredentialDocument>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadCredentialDocument>>,
          TError,
          Awaited<ReturnType<typeof downloadCredentialDocument>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useDownloadCredentialDocument<TData = Awaited<ReturnType<typeof downloadCredentialDocument>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadCredentialDocument>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof downloadCredentialDocument>>,
          TError,
          Awaited<ReturnType<typeof downloadCredentialDocument>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useDownloadCredentialDocument<TData = Awaited<ReturnType<typeof downloadCredentialDocument>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadCredentialDocument>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Download own document or review an entity document with policy management permission
 */

export function useDownloadCredentialDocument<TData = Awaited<ReturnType<typeof downloadCredentialDocument>>, TError = Error>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof downloadCredentialDocument>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getDownloadCredentialDocumentQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getListCredentialReviewsUrl = (params?: ListCredentialReviewsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/admin/credentials?${stringifiedParams}` : `/api/admin/credentials`
}

/**
 * @summary List selected-entity submissions with policy management permission
 */
export const listCredentialReviews = async (params?: ListCredentialReviewsParams, options?: Parameters<typeof customFetch>[1]): Promise<UserCredentialList> => {

  return customFetch<UserCredentialList>(getListCredentialReviewsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListCredentialReviewsQueryKey = (params?: ListCredentialReviewsParams,) => {
    return [
    `/api/admin/credentials`, ...(params ? [params] : [])
    ] as const;
    }


export const getListCredentialReviewsQueryOptions = <TData = Awaited<ReturnType<typeof listCredentialReviews>>, TError = Error>(params?: ListCredentialReviewsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listCredentialReviews>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListCredentialReviewsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listCredentialReviews>>> = ({ signal }) => listCredentialReviews(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listCredentialReviews>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListCredentialReviewsQueryResult = NonNullable<Awaited<ReturnType<typeof listCredentialReviews>>>
export type ListCredentialReviewsQueryError = Error


export function useListCredentialReviews<TData = Awaited<ReturnType<typeof listCredentialReviews>>, TError = Error>(
 params: undefined |  ListCredentialReviewsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listCredentialReviews>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCredentialReviews>>,
          TError,
          Awaited<ReturnType<typeof listCredentialReviews>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListCredentialReviews<TData = Awaited<ReturnType<typeof listCredentialReviews>>, TError = Error>(
 params?: ListCredentialReviewsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listCredentialReviews>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCredentialReviews>>,
          TError,
          Awaited<ReturnType<typeof listCredentialReviews>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListCredentialReviews<TData = Awaited<ReturnType<typeof listCredentialReviews>>, TError = Error>(
 params?: ListCredentialReviewsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listCredentialReviews>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List selected-entity submissions with policy management permission
 */

export function useListCredentialReviews<TData = Awaited<ReturnType<typeof listCredentialReviews>>, TError = Error>(
 params?: ListCredentialReviewsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listCredentialReviews>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListCredentialReviewsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getReviewCredentialUrl = (id: string,) => {




  return `/api/admin/credentials/${id}/review`
}

/**
 * @summary Approve or reject a pending entity submission; rejection requires a note
 */
export const reviewCredential = async (id: string,
    reviewCredentialRequest: ReviewCredentialRequest, options?: Parameters<typeof customFetch>[1]): Promise<UserCredential> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<UserCredential>(getReviewCredentialUrl(id),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewCredentialRequest)
  }
);}





export const getReviewCredentialMutationKey = () => ['reviewCredential'] as const;

export const getReviewCredentialMutationOptions = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewCredential>>, TError,ReviewCredentialMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof reviewCredential>>, TError,ReviewCredentialMutationVariables, TContext> => {

const mutationKey = getReviewCredentialMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof reviewCredential>>, ReviewCredentialMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  reviewCredential(id,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReviewCredentialMutationResult = NonNullable<Awaited<ReturnType<typeof reviewCredential>>>
    export type ReviewCredentialMutationBody = ReviewCredentialRequest
    export type ReviewCredentialMutationError = Error
    export type ReviewCredentialMutationVariables = {id: string;data: ReviewCredentialRequest}

    /**
 * @summary Approve or reject a pending entity submission; rejection requires a note
 */
export const useReviewCredential = <TError = Error,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof reviewCredential>>, TError,ReviewCredentialMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof reviewCredential>>,
        TError,
        ReviewCredentialMutationVariables,
        TContext
      > => {
      return useMutation(getReviewCredentialMutationOptions(options), queryClient);
    }

export const getListPlatformEmailTemplatesUrl = () => {




  return `/api/platform/email-templates`
}

/**
 * @summary Read all customer email defaults (platform administrator only)
 */
export const listPlatformEmailTemplates = async ( options?: Parameters<typeof customFetch>[1]): Promise<EmailTemplateList> => {

  return customFetch<EmailTemplateList>(getListPlatformEmailTemplatesUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListPlatformEmailTemplatesQueryKey = () => {
    return [
    `/api/platform/email-templates`
    ] as const;
    }


export const getListPlatformEmailTemplatesQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformEmailTemplates>>, TError = void>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPlatformEmailTemplates>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformEmailTemplatesQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformEmailTemplates>>> = ({ signal }) => listPlatformEmailTemplates({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformEmailTemplates>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListPlatformEmailTemplatesQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformEmailTemplates>>>
export type ListPlatformEmailTemplatesQueryError = void


export function useListPlatformEmailTemplates<TData = Awaited<ReturnType<typeof listPlatformEmailTemplates>>, TError = void>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPlatformEmailTemplates>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPlatformEmailTemplates>>,
          TError,
          Awaited<ReturnType<typeof listPlatformEmailTemplates>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListPlatformEmailTemplates<TData = Awaited<ReturnType<typeof listPlatformEmailTemplates>>, TError = void>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPlatformEmailTemplates>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPlatformEmailTemplates>>,
          TError,
          Awaited<ReturnType<typeof listPlatformEmailTemplates>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListPlatformEmailTemplates<TData = Awaited<ReturnType<typeof listPlatformEmailTemplates>>, TError = void>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPlatformEmailTemplates>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read all customer email defaults (platform administrator only)
 */

export function useListPlatformEmailTemplates<TData = Awaited<ReturnType<typeof listPlatformEmailTemplates>>, TError = void>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPlatformEmailTemplates>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListPlatformEmailTemplatesQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSavePlatformEmailTemplateUrl = (type: 'booking_confirmed' | 'booking_modified' | 'booking_cancelled' | 'booking_auto_released' | 'booking_reminder' | 'check_in_prompt' | 'auto_release_warning' | 'resource_unavailable' | 'visitor_arrival' | 'visitor_approval',) => {




  return `/api/platform/email-templates/${type}`
}

/**
 * @summary Save platform default customer email wording
 */
export const savePlatformEmailTemplate = async (type: 'booking_confirmed' | 'booking_modified' | 'booking_cancelled' | 'booking_auto_released' | 'booking_reminder' | 'check_in_prompt' | 'auto_release_warning' | 'resource_unavailable' | 'visitor_arrival' | 'visitor_approval',
    emailTemplateContent: EmailTemplateContent, options?: Parameters<typeof customFetch>[1]): Promise<EmailTemplate> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EmailTemplate>(getSavePlatformEmailTemplateUrl(type),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(emailTemplateContent)
  }
);}





export const getSavePlatformEmailTemplateMutationKey = () => ['savePlatformEmailTemplate'] as const;

export const getSavePlatformEmailTemplateMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof savePlatformEmailTemplate>>, TError,SavePlatformEmailTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof savePlatformEmailTemplate>>, TError,SavePlatformEmailTemplateMutationVariables, TContext> => {

const mutationKey = getSavePlatformEmailTemplateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof savePlatformEmailTemplate>>, SavePlatformEmailTemplateMutationVariables> = (props) => {
          const {type,data} = props ?? {};

          return  savePlatformEmailTemplate(type,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SavePlatformEmailTemplateMutationResult = NonNullable<Awaited<ReturnType<typeof savePlatformEmailTemplate>>>
    export type SavePlatformEmailTemplateMutationBody = EmailTemplateContent
    export type SavePlatformEmailTemplateMutationError = void
    export type SavePlatformEmailTemplateMutationVariables = {type: 'booking_confirmed' | 'booking_modified' | 'booking_cancelled' | 'booking_auto_released' | 'booking_reminder' | 'check_in_prompt' | 'auto_release_warning' | 'resource_unavailable' | 'visitor_arrival' | 'visitor_approval';data: EmailTemplateContent}

    /**
 * @summary Save platform default customer email wording
 */
export const useSavePlatformEmailTemplate = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof savePlatformEmailTemplate>>, TError,SavePlatformEmailTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof savePlatformEmailTemplate>>,
        TError,
        SavePlatformEmailTemplateMutationVariables,
        TContext
      > => {
      return useMutation(getSavePlatformEmailTemplateMutationOptions(options), queryClient);
    }

export const getResetPlatformEmailTemplateUrl = (type: 'booking_confirmed' | 'booking_modified' | 'booking_cancelled' | 'booking_auto_released' | 'booking_reminder' | 'check_in_prompt' | 'auto_release_warning' | 'resource_unavailable' | 'visitor_arrival' | 'visitor_approval',) => {




  return `/api/platform/email-templates/${type}`
}

/**
 * @summary Reset platform customer email wording to the built-in default
 */
export const resetPlatformEmailTemplate = async (type: 'booking_confirmed' | 'booking_modified' | 'booking_cancelled' | 'booking_auto_released' | 'booking_reminder' | 'check_in_prompt' | 'auto_release_warning' | 'resource_unavailable' | 'visitor_arrival' | 'visitor_approval', options?: Parameters<typeof customFetch>[1]): Promise<EmailTemplate> => {

  return customFetch<EmailTemplate>(getResetPlatformEmailTemplateUrl(type),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getResetPlatformEmailTemplateMutationKey = () => ['resetPlatformEmailTemplate'] as const;

export const getResetPlatformEmailTemplateMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resetPlatformEmailTemplate>>, TError,ResetPlatformEmailTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof resetPlatformEmailTemplate>>, TError,ResetPlatformEmailTemplateMutationVariables, TContext> => {

const mutationKey = getResetPlatformEmailTemplateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof resetPlatformEmailTemplate>>, ResetPlatformEmailTemplateMutationVariables> = (props) => {
          const {type} = props ?? {};

          return  resetPlatformEmailTemplate(type,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResetPlatformEmailTemplateMutationResult = NonNullable<Awaited<ReturnType<typeof resetPlatformEmailTemplate>>>

    export type ResetPlatformEmailTemplateMutationError = void
    export type ResetPlatformEmailTemplateMutationVariables = {type: 'booking_confirmed' | 'booking_modified' | 'booking_cancelled' | 'booking_auto_released' | 'booking_reminder' | 'check_in_prompt' | 'auto_release_warning' | 'resource_unavailable' | 'visitor_arrival' | 'visitor_approval'}

    /**
 * @summary Reset platform customer email wording to the built-in default
 */
export const useResetPlatformEmailTemplate = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resetPlatformEmailTemplate>>, TError,ResetPlatformEmailTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof resetPlatformEmailTemplate>>,
        TError,
        ResetPlatformEmailTemplateMutationVariables,
        TContext
      > => {
      return useMutation(getResetPlatformEmailTemplateMutationOptions(options), queryClient);
    }

export const getPreviewPlatformEmailTemplateUrl = () => {




  return `/api/platform/email-templates/preview`
}

/**
 * @summary Render customer email wording using sample details without sending
 */
export const previewPlatformEmailTemplate = async (emailTemplatePreviewRequest: EmailTemplatePreviewRequest, options?: Parameters<typeof customFetch>[1]): Promise<EmailTemplatePreview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EmailTemplatePreview>(getPreviewPlatformEmailTemplateUrl(),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(emailTemplatePreviewRequest)
  }
);}





export const getPreviewPlatformEmailTemplateMutationKey = () => ['previewPlatformEmailTemplate'] as const;

export const getPreviewPlatformEmailTemplateMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewPlatformEmailTemplate>>, TError,PreviewPlatformEmailTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewPlatformEmailTemplate>>, TError,PreviewPlatformEmailTemplateMutationVariables, TContext> => {

const mutationKey = getPreviewPlatformEmailTemplateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewPlatformEmailTemplate>>, PreviewPlatformEmailTemplateMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  previewPlatformEmailTemplate(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewPlatformEmailTemplateMutationResult = NonNullable<Awaited<ReturnType<typeof previewPlatformEmailTemplate>>>
    export type PreviewPlatformEmailTemplateMutationBody = EmailTemplatePreviewRequest
    export type PreviewPlatformEmailTemplateMutationError = void
    export type PreviewPlatformEmailTemplateMutationVariables = {data: EmailTemplatePreviewRequest}

    /**
 * @summary Render customer email wording using sample details without sending
 */
export const usePreviewPlatformEmailTemplate = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewPlatformEmailTemplate>>, TError,PreviewPlatformEmailTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof previewPlatformEmailTemplate>>,
        TError,
        PreviewPlatformEmailTemplateMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewPlatformEmailTemplateMutationOptions(options), queryClient);
    }

export const getListTenantEmailTemplatesUrl = (tenantSlug: string,
    params?: ListTenantEmailTemplatesParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/email-templates?${stringifiedParams}` : `/api/tenants/${tenantSlug}/email-templates`
}

/**
 * Scope defaults to entity. Parent tenant scope requires explicit tenant administrator authority.
 * @summary Read effective customer emails for an entity or its parent tenant
 */
export const listTenantEmailTemplates = async (tenantSlug: string,
    params?: ListTenantEmailTemplatesParams, options?: Parameters<typeof customFetch>[1]): Promise<EmailTemplateList> => {

  return customFetch<EmailTemplateList>(getListTenantEmailTemplatesUrl(tenantSlug,params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getListTenantEmailTemplatesQueryKey = (tenantSlug: string,
    params?: ListTenantEmailTemplatesParams,) => {
    return [
    `/api/tenants/${tenantSlug}/email-templates`, ...(params ? [params] : [])
    ] as const;
    }


export const getListTenantEmailTemplatesQueryOptions = <TData = Awaited<ReturnType<typeof listTenantEmailTemplates>>, TError = void>(tenantSlug: string,
    params?: ListTenantEmailTemplatesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTenantEmailTemplates>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListTenantEmailTemplatesQueryKey(tenantSlug,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listTenantEmailTemplates>>> = ({ signal }) => listTenantEmailTemplates(tenantSlug,params, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: tenantSlug !== null && tenantSlug !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listTenantEmailTemplates>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListTenantEmailTemplatesQueryResult = NonNullable<Awaited<ReturnType<typeof listTenantEmailTemplates>>>
export type ListTenantEmailTemplatesQueryError = void


export function useListTenantEmailTemplates<TData = Awaited<ReturnType<typeof listTenantEmailTemplates>>, TError = void>(
 tenantSlug: string,
    params: undefined |  ListTenantEmailTemplatesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTenantEmailTemplates>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTenantEmailTemplates>>,
          TError,
          Awaited<ReturnType<typeof listTenantEmailTemplates>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTenantEmailTemplates<TData = Awaited<ReturnType<typeof listTenantEmailTemplates>>, TError = void>(
 tenantSlug: string,
    params?: ListTenantEmailTemplatesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTenantEmailTemplates>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listTenantEmailTemplates>>,
          TError,
          Awaited<ReturnType<typeof listTenantEmailTemplates>>
        > , 'initialData'
      >, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListTenantEmailTemplates<TData = Awaited<ReturnType<typeof listTenantEmailTemplates>>, TError = void>(
 tenantSlug: string,
    params?: ListTenantEmailTemplatesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTenantEmailTemplates>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Read effective customer emails for an entity or its parent tenant
 */

export function useListTenantEmailTemplates<TData = Awaited<ReturnType<typeof listTenantEmailTemplates>>, TError = void>(
 tenantSlug: string,
    params?: ListTenantEmailTemplatesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listTenantEmailTemplates>>, TError, TData>>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListTenantEmailTemplatesQueryOptions(tenantSlug,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}







export const getSaveTenantEmailTemplateUrl = (tenantSlug: string,
    type: 'booking_confirmed' | 'booking_modified' | 'booking_cancelled' | 'booking_auto_released' | 'booking_reminder' | 'check_in_prompt' | 'auto_release_warning' | 'resource_unavailable' | 'visitor_arrival' | 'visitor_approval',
    params?: SaveTenantEmailTemplateParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/email-templates/${type}?${stringifiedParams}` : `/api/tenants/${tenantSlug}/email-templates/${type}`
}

/**
 * @summary Save tenant wording or an unlocked entity override
 */
export const saveTenantEmailTemplate = async (tenantSlug: string,
    type: 'booking_confirmed' | 'booking_modified' | 'booking_cancelled' | 'booking_auto_released' | 'booking_reminder' | 'check_in_prompt' | 'auto_release_warning' | 'resource_unavailable' | 'visitor_arrival' | 'visitor_approval',
    emailTemplateContent: EmailTemplateContent,
    params?: SaveTenantEmailTemplateParams, options?: Parameters<typeof customFetch>[1]): Promise<EmailTemplate> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EmailTemplate>(getSaveTenantEmailTemplateUrl(tenantSlug,type,params),
  {
    ...options,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(emailTemplateContent)
  }
);}





export const getSaveTenantEmailTemplateMutationKey = () => ['saveTenantEmailTemplate'] as const;

export const getSaveTenantEmailTemplateMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveTenantEmailTemplate>>, TError,SaveTenantEmailTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof saveTenantEmailTemplate>>, TError,SaveTenantEmailTemplateMutationVariables, TContext> => {

const mutationKey = getSaveTenantEmailTemplateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof saveTenantEmailTemplate>>, SaveTenantEmailTemplateMutationVariables> = (props) => {
          const {tenantSlug,type,data,params} = props ?? {};

          return  saveTenantEmailTemplate(tenantSlug,type,data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SaveTenantEmailTemplateMutationResult = NonNullable<Awaited<ReturnType<typeof saveTenantEmailTemplate>>>
    export type SaveTenantEmailTemplateMutationBody = EmailTemplateContent
    export type SaveTenantEmailTemplateMutationError = void
    export type SaveTenantEmailTemplateMutationVariables = {tenantSlug: string;type: 'booking_confirmed' | 'booking_modified' | 'booking_cancelled' | 'booking_auto_released' | 'booking_reminder' | 'check_in_prompt' | 'auto_release_warning' | 'resource_unavailable' | 'visitor_arrival' | 'visitor_approval';data: EmailTemplateContent;params?: SaveTenantEmailTemplateParams}

    /**
 * @summary Save tenant wording or an unlocked entity override
 */
export const useSaveTenantEmailTemplate = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof saveTenantEmailTemplate>>, TError,SaveTenantEmailTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof saveTenantEmailTemplate>>,
        TError,
        SaveTenantEmailTemplateMutationVariables,
        TContext
      > => {
      return useMutation(getSaveTenantEmailTemplateMutationOptions(options), queryClient);
    }

export const getResetTenantEmailTemplateUrl = (tenantSlug: string,
    type: 'booking_confirmed' | 'booking_modified' | 'booking_cancelled' | 'booking_auto_released' | 'booking_reminder' | 'check_in_prompt' | 'auto_release_warning' | 'resource_unavailable' | 'visitor_arrival' | 'visitor_approval',
    params?: ResetTenantEmailTemplateParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/email-templates/${type}?${stringifiedParams}` : `/api/tenants/${tenantSlug}/email-templates/${type}`
}

/**
 * @summary Remove an override and inherit the parent wording
 */
export const resetTenantEmailTemplate = async (tenantSlug: string,
    type: 'booking_confirmed' | 'booking_modified' | 'booking_cancelled' | 'booking_auto_released' | 'booking_reminder' | 'check_in_prompt' | 'auto_release_warning' | 'resource_unavailable' | 'visitor_arrival' | 'visitor_approval',
    params?: ResetTenantEmailTemplateParams, options?: Parameters<typeof customFetch>[1]): Promise<EmailTemplate> => {

  return customFetch<EmailTemplate>(getResetTenantEmailTemplateUrl(tenantSlug,type,params),
  {
    ...options,
    method: 'DELETE'


  }
);}





export const getResetTenantEmailTemplateMutationKey = () => ['resetTenantEmailTemplate'] as const;

export const getResetTenantEmailTemplateMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resetTenantEmailTemplate>>, TError,ResetTenantEmailTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof resetTenantEmailTemplate>>, TError,ResetTenantEmailTemplateMutationVariables, TContext> => {

const mutationKey = getResetTenantEmailTemplateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof resetTenantEmailTemplate>>, ResetTenantEmailTemplateMutationVariables> = (props) => {
          const {tenantSlug,type,params} = props ?? {};

          return  resetTenantEmailTemplate(tenantSlug,type,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ResetTenantEmailTemplateMutationResult = NonNullable<Awaited<ReturnType<typeof resetTenantEmailTemplate>>>

    export type ResetTenantEmailTemplateMutationError = void
    export type ResetTenantEmailTemplateMutationVariables = {tenantSlug: string;type: 'booking_confirmed' | 'booking_modified' | 'booking_cancelled' | 'booking_auto_released' | 'booking_reminder' | 'check_in_prompt' | 'auto_release_warning' | 'resource_unavailable' | 'visitor_arrival' | 'visitor_approval';params?: ResetTenantEmailTemplateParams}

    /**
 * @summary Remove an override and inherit the parent wording
 */
export const useResetTenantEmailTemplate = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof resetTenantEmailTemplate>>, TError,ResetTenantEmailTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof resetTenantEmailTemplate>>,
        TError,
        ResetTenantEmailTemplateMutationVariables,
        TContext
      > => {
      return useMutation(getResetTenantEmailTemplateMutationOptions(options), queryClient);
    }

export const getUpdateEmailTemplatePermissionsUrl = (tenantSlug: string,
    params?: UpdateEmailTemplatePermissionsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/email-templates/permissions?${stringifiedParams}` : `/api/tenants/${tenantSlug}/email-templates/permissions`
}

/**
 * @summary Allow or lock entity customisation of individual emails (tenant scope only)
 */
export const updateEmailTemplatePermissions = async (tenantSlug: string,
    emailTemplatePermissions: EmailTemplatePermissions,
    params?: UpdateEmailTemplatePermissionsParams, options?: Parameters<typeof customFetch>[1]): Promise<EmailTemplateList> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EmailTemplateList>(getUpdateEmailTemplatePermissionsUrl(tenantSlug,params),
  {
    ...options,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(emailTemplatePermissions)
  }
);}





export const getUpdateEmailTemplatePermissionsMutationKey = () => ['updateEmailTemplatePermissions'] as const;

export const getUpdateEmailTemplatePermissionsMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEmailTemplatePermissions>>, TError,UpdateEmailTemplatePermissionsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof updateEmailTemplatePermissions>>, TError,UpdateEmailTemplatePermissionsMutationVariables, TContext> => {

const mutationKey = getUpdateEmailTemplatePermissionsMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateEmailTemplatePermissions>>, UpdateEmailTemplatePermissionsMutationVariables> = (props) => {
          const {tenantSlug,data,params} = props ?? {};

          return  updateEmailTemplatePermissions(tenantSlug,data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateEmailTemplatePermissionsMutationResult = NonNullable<Awaited<ReturnType<typeof updateEmailTemplatePermissions>>>
    export type UpdateEmailTemplatePermissionsMutationBody = EmailTemplatePermissions
    export type UpdateEmailTemplatePermissionsMutationError = void
    export type UpdateEmailTemplatePermissionsMutationVariables = {tenantSlug: string;data: EmailTemplatePermissions;params?: UpdateEmailTemplatePermissionsParams}

    /**
 * @summary Allow or lock entity customisation of individual emails (tenant scope only)
 */
export const useUpdateEmailTemplatePermissions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEmailTemplatePermissions>>, TError,UpdateEmailTemplatePermissionsMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateEmailTemplatePermissions>>,
        TError,
        UpdateEmailTemplatePermissionsMutationVariables,
        TContext
      > => {
      return useMutation(getUpdateEmailTemplatePermissionsMutationOptions(options), queryClient);
    }

export const getPreviewTenantEmailTemplateUrl = (tenantSlug: string,
    params?: PreviewTenantEmailTemplateParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/tenants/${tenantSlug}/email-templates/preview?${stringifiedParams}` : `/api/tenants/${tenantSlug}/email-templates/preview`
}

/**
 * @summary Render customer email wording with sample details without saving or sending
 */
export const previewTenantEmailTemplate = async (tenantSlug: string,
    emailTemplatePreviewRequest: EmailTemplatePreviewRequest,
    params?: PreviewTenantEmailTemplateParams, options?: Parameters<typeof customFetch>[1]): Promise<EmailTemplatePreview> => {

    const getHeaders = (h?: NonNullable<RequestInit['headers']>): Record<string, string | readonly string[]> => {
    if (!h) return {};
    if (h instanceof Headers) return Object.fromEntries(h.entries());
    if (Symbol.iterator in h) {
      return Object.fromEntries(
        Array.from(h as Iterable<Iterable<string>>, (entry) => Array.from(entry) as [string, string]),
      );
    }
    const headers: Record<string, string | readonly string[]> = {};
    for (const [name, value] of Object.entries<string | readonly string[] | undefined>(h)) {
      if (value !== undefined) headers[name] = value;
    }
    return headers;
  };
return customFetch<EmailTemplatePreview>(getPreviewTenantEmailTemplateUrl(tenantSlug,params),
  {
    ...options,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getHeaders(options?.headers) },
    body: JSON.stringify(emailTemplatePreviewRequest)
  }
);}





export const getPreviewTenantEmailTemplateMutationKey = () => ['previewTenantEmailTemplate'] as const;

export const getPreviewTenantEmailTemplateMutationOptions = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewTenantEmailTemplate>>, TError,PreviewTenantEmailTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof previewTenantEmailTemplate>>, TError,PreviewTenantEmailTemplateMutationVariables, TContext> => {

const mutationKey = getPreviewTenantEmailTemplateMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof previewTenantEmailTemplate>>, PreviewTenantEmailTemplateMutationVariables> = (props) => {
          const {tenantSlug,data,params} = props ?? {};

          return  previewTenantEmailTemplate(tenantSlug,data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PreviewTenantEmailTemplateMutationResult = NonNullable<Awaited<ReturnType<typeof previewTenantEmailTemplate>>>
    export type PreviewTenantEmailTemplateMutationBody = EmailTemplatePreviewRequest
    export type PreviewTenantEmailTemplateMutationError = void
    export type PreviewTenantEmailTemplateMutationVariables = {tenantSlug: string;data: EmailTemplatePreviewRequest;params?: PreviewTenantEmailTemplateParams}

    /**
 * @summary Render customer email wording with sample details without saving or sending
 */
export const usePreviewTenantEmailTemplate = <TError = void,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof previewTenantEmailTemplate>>, TError,PreviewTenantEmailTemplateMutationVariables, TContext>, request?: SecondParameter<typeof customFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof previewTenantEmailTemplate>>,
        TError,
        PreviewTenantEmailTemplateMutationVariables,
        TContext
      > => {
      return useMutation(getPreviewTenantEmailTemplateMutationOptions(options), queryClient);
    }
